import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { useContextualQueryState } from '../utils/routeUtils';
import {
    ArrowUpDown,
    ArrowLeft,
    ArrowRight,
    ArrowUp,
    ArrowDown,
    GripVertical,
    List,
    Kanban,
    Download,
    Plus,
    MoreHorizontal,
    Mail,
    Phone,
    MessageSquare,
    Shield,
    MapPin,
    ChevronDown,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Eye,
    ArrowRightLeft,
    X,
    Trash2,
    Edit2,
    Calendar,
    DollarSign,
    CreditCard,
    User,
    CheckCircle,
    Building,
    Building2,
    LayoutDashboard,
    Users,
    Briefcase,
    UserPlus,
    FileText,
    ExternalLink,
    Paperclip,
    CheckSquare,
    Globe,
    Search,
    Filter,
    Tag,
    Smartphone,
    Info,
    Clock,
    AlertCircle,
    Check,
    Bell,
    Loader2
} from 'lucide-react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import './Pipeline.css';

// Navigation Tabs for Left Sub-Sidebar (Deals, Contacts, Companies, Tasks, Reminders)
const SIDEBAR_TABS = [
    { id: 'deals', label: 'Deals', icon: Briefcase },
    { id: 'contacts', label: 'Contacts', icon: Users },
    { id: 'companies', label: 'Companies', icon: Building2 },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare },
    { id: 'reminders', label: 'Reminders', icon: Bell }
];

const TASK_PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];
const TASK_STATUS_OPTIONS = ['Pending', 'In Progress', 'Completed'];
const TASK_TYPE_OPTIONS = ['Message', 'Email', 'Meeting', 'Follow-up', 'Review', 'To-Do'];

const INITIAL_SAMPLE_TASKS = [];

// Country codes with flags, max digits & placeholder formatting
const COUNTRY_CODES = [
    { code: '+1', country: 'United States', flag: '🇺🇸', minDigits: 10, maxDigits: 10, formatPlaceholder: '(555) 000-0000' },
    { code: '+91', country: 'India', flag: '🇮🇳', minDigits: 10, maxDigits: 10, formatPlaceholder: '98765 43210' },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧', minDigits: 10, maxDigits: 11, formatPlaceholder: '7911 123456' },
    { code: '+61', country: 'Australia', flag: '🇦🇺', minDigits: 9, maxDigits: 10, formatPlaceholder: '412 345 678' },
    { code: '+49', country: 'Germany', flag: '🇩🇪', minDigits: 10, maxDigits: 11, formatPlaceholder: '151 12345678' },
    { code: '+971', country: 'UAE', flag: '🇦🇪', minDigits: 9, maxDigits: 10, formatPlaceholder: '050 123 4567' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬', minDigits: 8, maxDigits: 8, formatPlaceholder: '8123 4567' },
    { code: '+33', country: 'France', flag: '🇫🇷', minDigits: 9, maxDigits: 10, formatPlaceholder: '6 12 34 56 78' },
    { code: '+81', country: 'Japan', flag: '🇯🇵', minDigits: 10, maxDigits: 11, formatPlaceholder: '90 1234 5678' },
    { code: '+86', country: 'China', flag: '🇨🇳', minDigits: 11, maxDigits: 11, formatPlaceholder: '138 0000 0000' },
    { code: '+55', country: 'Brazil', flag: '🇧🇷', minDigits: 10, maxDigits: 11, formatPlaceholder: '11 91234-5678' }
];

const CONTACT_SOURCE_OPTIONS = [];
const LEAD_STAGE_OPTIONS = [];
const LEAD_STATUS_OPTIONS = [];

// Default Contact Pipeline Stages for Columns
const DEFAULT_CONTACT_STAGES = [
    { id: 'New Lead', name: 'New Lead', badgeClass: 'new', createdAt: '2026-10-17T00:00:00+05:30' }
];

// Initial High-Fidelity Pre-seeded Contacts
const INITIAL_SAMPLE_CONTACTS = [];

const INDUSTRY_OPTIONS = [];

// Country to States/Regions Mapping Dictionary
const COUNTRY_STATE_MAP = {
    'United States': [
        'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
        'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana',
        'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts',
        'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
        'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota',
        'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
        'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
        'West Virginia', 'Wisconsin', 'Wyoming'
    ],
    'India': [
        'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
        'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
        'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
        'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
        'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Chandigarh'
    ],
    'United Kingdom': [
        'England', 'Scotland', 'Wales', 'Northern Ireland', 'Greater London', 'West Midlands', 'Greater Manchester', 'Yorkshire'
    ],
    'Australia': [
        'New South Wales', 'Victoria', 'Queensland', 'Western Australia', 'South Australia', 'Tasmania', 'Australian Capital Territory', 'Northern Territory'
    ],
    'Germany': [
        'Bavaria', 'Berlin', 'Baden-Württemberg', 'Brandenburg', 'Bremen', 'Hamburg', 'Hesse', 'Lower Saxony', 'North Rhine-Westphalia', 'Saxony'
    ],
    'United Arab Emirates': [
        'Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'
    ],
    'Singapore': [
        'Central Region', 'East Region', 'North Region', 'North-East Region', 'West Region'
    ],
    'France': [
        'Île-de-France', 'Auvergne-Rhône-Alpes', 'Nouvelle-Aquitaine', 'Occitanie', 'Provence-Alpes-Côte d\'Azur', 'Hauts-de-France', 'Grand Est'
    ],
    'Canada': [
        'Ontario', 'Quebec', 'British Columbia', 'Alberta', 'Manitoba', 'Saskatchewan', 'Nova Scotia', 'New Brunswick', 'Newfoundland and Labrador'
    ],
    'Netherlands': [
        'North Holland', 'South Holland', 'Utrecht', 'North Brabant', 'Gelderland', 'Overijssel', 'Limburg'
    ]
};

const COUNTRY_OPTIONS = Object.keys(COUNTRY_STATE_MAP);

const INITIAL_SAMPLE_COMPANIES = [];

// Date formatting helper for DD-MM-YYYY
const formatDateDDMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    const str = String(dateStr).trim();
    if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
        const [year, month, day] = str.slice(0, 10).split('-');
        return `${day}-${month}-${year}`;
    }
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

// Helper to parse follow-up scheduled date and time into timestamp
const parseFollowUpDateTime = (dateStr, timeStr) => {
    if (!dateStr) return null;
    try {
        let year, month, day;
        const str = String(dateStr).trim();
        const isoMatch = str.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
        const splitMatch = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);

        if (isoMatch) {
            year = parseInt(isoMatch[1], 10);
            month = parseInt(isoMatch[2], 10);
            day = parseInt(isoMatch[3], 10);
        } else if (splitMatch) {
            const p1 = parseInt(splitMatch[1], 10);
            const p2 = parseInt(splitMatch[2], 10);
            year = parseInt(splitMatch[3], 10);

            if (p1 > 12) {
                day = p1;
                month = p2;
            } else if (p2 > 12) {
                month = p1;
                day = p2;
            } else {
                day = p1;
                month = p2;
            }
        } else {
            const d = new Date(str);
            if (isNaN(d.getTime())) return null;
            year = d.getFullYear();
            month = d.getMonth() + 1;
            day = d.getDate();
        }

        let hours = 10;
        let minutes = 0;

        if (timeStr && typeof timeStr === 'string' && timeStr.trim()) {
            const clean = timeStr.trim();
            const ampmMatch = clean.match(/^(\d{1,2})[:.](\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
            if (ampmMatch) {
                let h = parseInt(ampmMatch[1], 10);
                const m = parseInt(ampmMatch[2], 10);
                const period = ampmMatch[3] ? ampmMatch[3].toUpperCase() : null;

                if (period === 'PM' && h !== 12) {
                    h += 12;
                } else if (period === 'AM' && h === 12) {
                    h = 0;
                }
                hours = h;
                minutes = m;
            }
        }

        const yStr = String(year).padStart(4, '0');
        const mStr = String(month).padStart(2, '0');
        const dStr = String(day).padStart(2, '0');
        const hStr = String(hours).padStart(2, '0');
        const minStr = String(minutes).padStart(2, '0');

        // Aligned to Indian Standard Time (Asia/Kolkata, UTC+05:30)
        const isoTarget = `${yStr}-${mStr}-${dStr}T${hStr}:${minStr}:00+05:30`;
        const targetDate = new Date(isoTarget);

        return isNaN(targetDate.getTime()) ? null : targetDate;
    } catch (e) {
        return null;
    }
};

// Returns true if the scheduled follow-up time has already passed
const isFollowUpTimePassed = (act) => {
    if (!act) return false;
    const dateVal = act.followUpDate || act.date;
    if (!dateVal) return false;
    const targetDate = parseFollowUpDateTime(dateVal, act.followUpTime || act.time);
    if (!targetDate) return false;
    return targetDate.getTime() <= Date.now();
};

// Checks if current user is permitted to edit this follow-up
const canUserEditFollowUp = (act, currentUser, isSuperAdminOrHead = false) => {
    if (!act || act.type !== 'followup') return false;

    if (isSuperAdminOrHead) return true;
    if (!currentUser) return false;

    const currentUserId = (currentUser._id || currentUser.id || '').toString();
    const actCreatorId = (act.createdBy?._id || act.createdBy || '').toString();
    if (currentUserId && actCreatorId && currentUserId === actCreatorId) return true;

    const currentFullName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim().toLowerCase();
    const currentEmail = (currentUser.email || '').trim().toLowerCase();
    const currentOfficialEmail = (currentUser.officialEmail || '').trim().toLowerCase();
    const author = (act.author || '').trim().toLowerCase();

    if (currentFullName && author === currentFullName) return true;
    if (currentEmail && author === currentEmail) return true;
    if (currentOfficialEmail && author === currentOfficialEmail) return true;

    // Linked / assigned member permissions
    const memberId = (act.memberId || act.assignedTo?._id || act.assignedTo || act.member || '').toString();
    if (currentUserId && memberId && currentUserId === memberId) return true;

    const memberName = (act.memberName || act.assignedToName || '').trim().toLowerCase();
    if (currentFullName && memberName && memberName === currentFullName) return true;
    if (currentEmail && memberName === currentEmail) return true;
    if (currentOfficialEmail && memberName === currentOfficialEmail) return true;

    return false;
};

// Checks if current user is permitted to view an activity (specifically follow-ups)
// Admin, Sales Head, and Sales Manager can view ALL follow-ups.
// Sales Executives can view follow-ups they personally created or where they are the assigned/linked member.
const canUserViewActivity = (act, currentUser, isSuperAdminOrHead = false) => {
    if (!act) return false;
    // Non-followup activities (deal status events, payments, notes, tasks) are part of standard audit trail
    if (act.type !== 'followup') return true;

    // Admin, Sales Head, Sales Manager have full company visibility
    if (isSuperAdminOrHead) return true;
    if (!currentUser) return false;

    const currentUserId = (currentUser._id || currentUser.id || '').toString();
    const actCreatorId = (act.createdBy?._id || act.createdBy || '').toString();
    if (currentUserId && actCreatorId && currentUserId === actCreatorId) return true;

    const currentFullName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim().toLowerCase();
    const currentEmail = (currentUser.email || '').trim().toLowerCase();
    const currentOfficialEmail = (currentUser.officialEmail || '').trim().toLowerCase();
    const author = (act.author || '').trim().toLowerCase();

    if (currentFullName && author === currentFullName) return true;
    if (currentEmail && author === currentEmail) return true;
    if (currentOfficialEmail && author === currentOfficialEmail) return true;

    // Linked / assigned member permissions
    const memberId = (act.memberId || act.assignedTo?._id || act.assignedTo || act.member || '').toString();
    if (currentUserId && memberId && currentUserId === memberId) return true;

    const memberName = (act.memberName || act.assignedToName || '').trim().toLowerCase();
    if (currentFullName && memberName && memberName === currentFullName) return true;
    if (currentEmail && memberName === currentEmail) return true;
    if (currentOfficialEmail && memberName === currentOfficialEmail) return true;

    return false;
};

// Global Phone Formatter (+CC 000-000-0000 format)
const formatPhoneNumber = (rawPhone, defaultCountryCode = '+91') => {
    if (!rawPhone || typeof rawPhone !== 'string') return '';
    let str = rawPhone.trim();
    if (!str || str === '-' || str === '—') return '—';

    // Determine country code
    let cc = '';
    if (defaultCountryCode && typeof defaultCountryCode === 'string') {
        const cleanDefCC = defaultCountryCode.trim();
        cc = cleanDefCC.startsWith('+') ? cleanDefCC : `+${cleanDefCC}`;
    }

    // If rawPhone starts with +, extract CC
    if (str.startsWith('+')) {
        const matched = (COUNTRY_CODES || []).find(c => str.startsWith(c.code));
        if (matched) {
            cc = matched.code;
            str = str.slice(matched.code.length);
        } else {
            const m = str.match(/^(\+\d{1,4})\s*(.*)$/);
            if (m) {
                cc = m[1];
                str = m[2];
            }
        }
    }

    if (!cc) cc = '+91';

    // Extract only digits from remaining phone string
    let digits = str.replace(/\D/g, '');

    // If digits still contain duplicate country code digits (e.g. "919874666799" when cc is "+91")
    const ccNum = cc.replace(/\D/g, '');
    if (ccNum && digits.startsWith(ccNum) && digits.length > 10) {
        digits = digits.slice(ccNum.length);
    }

    if (!digits) return str;

    let formatted = digits;
    if (digits.length === 10) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 11 && digits.startsWith('0')) {
        const withoutZero = digits.slice(1);
        formatted = `${withoutZero.slice(0, 3)}-${withoutZero.slice(3, 6)}-${withoutZero.slice(6)}`;
    } else if (digits.length === 9) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 8) {
        formatted = `${digits.slice(0, 4)}-${digits.slice(4)}`;
    } else if (digits.length > 10) {
        const last4 = digits.slice(-4);
        const mid3 = digits.slice(-7, -4);
        const firstPart = digits.slice(0, -7);
        formatted = `${firstPart}-${mid3}-${last4}`;
    }

    return `${cc} ${formatted}`;
};

// Normalize raw currency symbols/strings to standard 3-letter currency code
const normalizeCurrencyCode = (rawCurrency) => {
    if (!rawCurrency || typeof rawCurrency !== 'string') return 'INR';
    const c = rawCurrency.trim().toUpperCase();
    if (c === '₹' || c === 'INR' || c === 'RS' || c === 'RS.') return 'INR';
    if (c === '$' || c === 'USD' || c === 'US$') return 'USD';
    if (c === '€' || c === 'EUR') return 'EUR';
    if (c === '£' || c === 'GBP') return 'GBP';
    if (c === 'A$' || c === 'AUD') return 'AUD';
    if (c === 'C$' || c === 'CAD') return 'CAD';
    if (c === '¥' || c === 'JPY' || c === 'CNY') return 'JPY';
    if (c === 'CHF') return 'CHF';
    if (c === 'AED') return 'AED';
    if (c === 'SGD') return 'SGD';
    if (/^[A-Z]{3}$/.test(c)) return c;
    return 'USD';
};

// Robust numerical parser for deal values
const parseNumericDealValue = (rawVal) => {
    if (typeof rawVal === 'number') return isNaN(rawVal) ? 0 : rawVal;
    if (!rawVal) return 0;
    const cleaned = String(rawVal).replace(/[^0-9.-]+/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
};

const getFutureDateDDMMYYYY = (daysAhead = 14) => {
    const d = new Date();
    d.setDate(d.getDate() + daysAhead);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

// Robust date parser for deal dates & expected close dates
const parseDealDate = (dateVal) => {
    if (!dateVal) return null;
    if (dateVal instanceof Date) return isNaN(dateVal.getTime()) ? null : dateVal;
    const str = String(dateVal).trim();
    if (!str) return null;
    if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(str)) {
        const parts = str.split(/[-/]/);
        const d = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
        return isNaN(d.getTime()) ? null : d;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
        const parts = str.slice(0, 10).split('-');
        const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
};

// Date & Time formatting helper with Indian Standard Time (IST) timezone
const formatISTDateTime = (dateVal) => {
    if (!dateVal) return '—';
    try {
        const str = String(dateVal).trim();
        let d = new Date(dateVal);
        let hasTime = true;

        // Check if format is pure date like DD-MM-YYYY or DD/MM/YYYY
        if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(str)) {
            const parts = str.split(/[-/]/);
            d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
            hasTime = false;
        } else if (isNaN(d.getTime())) {
            const parts = str.split(/[-/]/);
            if (parts.length >= 3) {
                d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
            }
        }

        if (isNaN(d.getTime())) return str;

        const datePart = d.toLocaleDateString('en-IN', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '-');

        if (!hasTime) {
            return `${datePart} (IST)`;
        }

        const timePart = d.toLocaleTimeString('en-IN', {
            timeZone: 'Asia/Kolkata',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });

        return `${datePart}, ${timePart} IST`;
    } catch (e) {
        return String(dateVal);
    }
};

// Activity timestamp formatting helper: DD-MM-YYYY, hh:mm AM/PM IST
const formatActivityTimestamp = (act) => {
    if (!act) return '—';
    try {
        const dtSource = act.createdAt || act.date || new Date();
        let d = new Date(dtSource);
        if (isNaN(d.getTime()) && typeof dtSource === 'string') {
            if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(dtSource.trim())) {
                const parts = dtSource.trim().split(/[-/]/);
                d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
            }
        }

        if (!isNaN(d.getTime())) {
            const datePart = d.toLocaleDateString('en-IN', {
                timeZone: 'Asia/Kolkata',
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }).replace(/\//g, '-');

            let timePart = '';
            if (act.time && /\d{1,2}:\d{2}/.test(act.time)) {
                const trimmed = act.time.trim();
                if (/am|pm/i.test(trimmed)) {
                    timePart = trimmed.toUpperCase();
                } else {
                    const [hStr, mStr] = trimmed.split(':');
                    const h = parseInt(hStr, 10);
                    const m = parseInt(mStr, 10);
                    if (!isNaN(h) && !isNaN(m)) {
                        const period = h >= 12 ? 'PM' : 'AM';
                        const h12 = h % 12 === 0 ? 12 : h % 12;
                        timePart = `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
                    } else {
                        timePart = trimmed.toUpperCase();
                    }
                }
            } else {
                timePart = d.toLocaleTimeString('en-IN', {
                    timeZone: 'Asia/Kolkata',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                }).toUpperCase();
            }

            return `${datePart}, ${timePart} IST`;
        }
    } catch (e) { }

    const dateFallback = formatDateDDMMYYYY(act.date || new Date());
    let timeFallback = '12:00 PM';
    if (act.time && /\d{1,2}:\d{2}/.test(act.time)) {
        const trimmed = act.time.trim();
        if (/am|pm/i.test(trimmed)) {
            timeFallback = trimmed.toUpperCase();
        } else {
            const [hStr, mStr] = trimmed.split(':');
            const h = parseInt(hStr, 10);
            const m = parseInt(mStr, 10);
            if (!isNaN(h) && !isNaN(m)) {
                const period = h >= 12 ? 'PM' : 'AM';
                const h12 = h % 12 === 0 ? 12 : h % 12;
                timeFallback = `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
            }
        }
    }
    return `${dateFallback}, ${timeFallback} IST`;
};

// Robust activity timestamp parser for sorting (latest first)
const parseActivityTimestamp = (act) => {
    if (!act) return 0;
    if (act.timestamp && typeof act.timestamp === 'number' && act.timestamp > 0) {
        return act.timestamp;
    }

    // 1. If createdAt is a full ISO 8601 string (e.g. 2026-09-04T13:56:00.000Z)
    if (act.createdAt && typeof act.createdAt === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(act.createdAt)) {
        const t = new Date(act.createdAt).getTime();
        if (!isNaN(t)) return t;
    }

    // 2. Parse DD-MM-YYYY or DD/MM/YYYY with time
    const dtSource = act.date || act.createdAt;
    const timeSource = act.time;

    if (dtSource) {
        if (dtSource instanceof Date) return dtSource.getTime();
        const str = String(dtSource).trim();

        // Match DD-MM-YYYY or DD/MM/YYYY with optional inline time
        const match = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:[,\s]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?/i);
        if (match) {
            const day = parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const year = parseInt(match[3], 10);
            let hours = match[4] ? parseInt(match[4], 10) : 12;
            let mins = match[5] ? parseInt(match[5], 10) : 0;
            let secs = match[6] ? parseInt(match[6], 10) : 0;
            let ampm = match[7] ? match[7].toUpperCase() : null;

            if (timeSource && /\d{1,2}:\d{2}/.test(timeSource)) {
                const tMatch = timeSource.trim().match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i);
                if (tMatch) {
                    hours = parseInt(tMatch[1], 10);
                    mins = parseInt(tMatch[2], 10);
                    secs = tMatch[3] ? parseInt(tMatch[3], 10) : 0;
                    ampm = tMatch[4] ? tMatch[4].toUpperCase() : ampm;
                }
            }

            if (ampm === 'PM' && hours < 12) hours += 12;
            if (ampm === 'AM' && hours === 12) hours = 0;

            const dt = new Date(year, month, day, hours, mins, secs);
            if (!isNaN(dt.getTime())) return dt.getTime();
        }

        const t = new Date(str).getTime();
        if (!isNaN(t)) return t;
    }

    return 0;
};

// Relative time formatter for activities (e.g., '10 mins ago', '2 hours ago', 'Yesterday', '07-09-2026')
const formatRelativeActivityTime = (act) => {
    if (!act) return '';
    const ts = parseActivityTimestamp(act);
    if (!ts) {
        return act.time ? `${act.date ? `${act.date}, ` : ''}${act.time}` : 'Recently';
    }
    const now = Date.now();
    const diffMs = now - ts;
    if (diffMs < 0) {
        return formatActivityTimestamp(act);
    }
    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'min' : 'mins'} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} ${Math.floor(diffDays / 7) === 1 ? 'week' : 'weeks'} ago`;
    return formatActivityTimestamp(act);
};

const INITIAL_SAMPLE_DEALS = [];

// Real-time domain validation helper
const isValidCompanyDomain = (domain) => {
    if (!domain || !domain.trim()) return true;
    const clean = domain.trim().toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0]
        .split('?')[0]
        .split('#')[0];

    // Valid domain format: hostname with at least 1 period, valid labels, and valid TLD (2+ chars)
    const domainRegex = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*\.[a-z]{2,63}$/i;
    return domainRegex.test(clean);
};

const DEFAULT_PIPELINES = [
    { id: 'Lost', name: 'Lost', badgeClass: 'lost', createdAt: '2026-10-17T00:00:00+05:30' },
    { id: 'New Lead', name: 'New Lead', badgeClass: 'new', createdAt: '2026-10-17T00:00:00+05:30' },
    { id: 'Initial', name: 'Initial', badgeClass: 'new', createdAt: '2026-10-17T00:00:00+05:30' },
    { id: 'Sales Pipeline', name: 'Sales Pipeline', badgeClass: 'hot', createdAt: '2026-09-01T00:00:00+05:30' },
    { id: 'Enterprise Pipeline', name: 'Enterprise Pipeline', badgeClass: 'cold', createdAt: '2026-09-01T00:00:00+05:30' },
    { id: 'Lead Conversion Pipeline', name: 'Lead Conversion Pipeline', badgeClass: 'won', createdAt: '2026-09-01T00:00:00+05:30' },
    { id: 'Renewal & Retention Pipeline', name: 'Renewal & Retention Pipeline', badgeClass: 'old', createdAt: '2026-09-01T00:00:00+05:30' },
    { id: 'Partnership Pipeline', name: 'Partnership Pipeline', badgeClass: 'custom', createdAt: '2026-09-01T00:00:00+05:30' }
];

const DEFAULT_STAGES = [
    { id: 'Lost', name: 'Lost', badgeClass: 'lost', createdAt: '2026-10-17T00:00:00+05:30' },
    { id: 'New Lead', name: 'New Lead', badgeClass: 'new', createdAt: '2026-10-17T00:00:00+05:30' },
    { id: 'Initial', name: 'Initial', badgeClass: 'new', createdAt: '2026-10-17T00:00:00+05:30' },
    { id: 'Hot', name: 'Hot', badgeClass: 'hot', createdAt: '2026-09-01T00:00:00+05:30' },
    { id: 'Old', name: 'Old', badgeClass: 'old', createdAt: '2026-09-01T00:00:00+05:30' },
    { id: 'Cold', name: 'Cold', badgeClass: 'cold', createdAt: '2026-09-01T00:00:00+05:30' },
    { id: 'Won', name: 'Won', badgeClass: 'won', createdAt: '2026-10-17T00:00:00+05:30' }
];

const PIPELINE_OPTIONS = [
    'Lost',
    'New Lead',
    'Initial',
    'Sales Pipeline',
    'Enterprise Pipeline',
    'Lead Conversion Pipeline',
    'Renewal & Retention Pipeline',
    'Partnership Pipeline'
];

const DEAL_TYPE_OPTIONS = [];

const DEFAULT_DEAL_ACTIVITIES = {};

const CURRENCY_OPTIONS = [
    { code: 'USD', symbol: '$', label: '$ (USD)' },
    { code: 'INR', symbol: '₹', label: '₹ (INR)' },
    { code: 'EUR', symbol: '€', label: '€ (EUR)' },
    { code: 'GBP', symbol: '£', label: '£ (GBP)' },
    { code: 'AED', symbol: 'AED', label: 'AED (د.إ)' },
    { code: 'CAD', symbol: 'CAD $', label: 'CAD ($)' },
    { code: 'AUD', symbol: 'AUD $', label: 'AUD ($)' },
    { code: 'JPY', symbol: '¥', label: '¥ (JPY)' }
];

const FILTER_ITEMS = [
    { id: 'open', label: 'Open deals' },
    { id: 'my', label: 'My deals' },
    { id: 'recent', label: 'Recent deals' },
    { id: 'territory', label: 'Territory deals' },
    { id: 'imported', label: 'Recently imported' },
    { id: 'lost', label: 'Lost deals' },
    { id: 'won', label: 'Won deals' },
    { id: 'hot', label: 'Hot deals' },
    { id: 'cold', label: 'Cold deals' },
    { id: 'closing_week', label: 'Closing this week' },
    { id: 'monthly_sales', label: "This month's sales" },
    { id: 'recycle_bin', label: 'Recycle bin' },
    { id: 'all', label: 'All deals' }
];

// Reusable Custom / Creatable Combobox & Select Component (with Portal Floating Dropdown)
function CreatableSelect({
    value,
    onChange,
    options,
    onCreateOption,
    onDeleteOption,
    placeholder,
    className = '',
    required = false,
    isSearchable = true,
    isCreatable = true,
    isDeleteable = true,
    storageKey = '',
    placement = 'auto' // 'auto' | 'bottom' | 'top'
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(value || '');
    const [isTyping, setIsTyping] = useState(false);
    const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 200, placement: 'bottom', transform: 'none' });
    const wrapperRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const dropdownRef = React.useRef(null);

    // Persistent custom added options for CreatableSelect
    const [customCreatedOptions, setCustomCreatedOptions] = useState(() => {
        if (!storageKey) return [];
        try {
            const saved = localStorage.getItem(`hrms_custom_${storageKey}`);
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });

    // Persistent deleted options for CreatableSelect
    const [deletedSelectOptions, setDeletedSelectOptions] = useState(() => {
        if (!storageKey) return [];
        try {
            const saved = localStorage.getItem(`hrms_deleted_${storageKey}`);
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });

    const saveCustomOptions = (newList) => {
        setCustomCreatedOptions(newList);
        if (storageKey) {
            try {
                localStorage.setItem(`hrms_custom_${storageKey}`, JSON.stringify(newList));
            } catch (e) { }
        }
    };

    const saveDeletedOptions = (newList) => {
        setDeletedSelectOptions(newList);
        if (storageKey) {
            try {
                localStorage.setItem(`hrms_deleted_${storageKey}`, JSON.stringify(newList));
            } catch (e) { }
        }
    };

    // Calculate floating portal coordinates
    const calculateCoords = () => {
        if (!wrapperRef.current) return null;
        const rect = wrapperRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Check if space below is tight (< 230px) and place upwards if more room above
        const placeTop = placement === 'top' || (placement === 'auto' && spaceBelow < 230 && spaceAbove > spaceBelow);
        const availableSpace = placeTop ? spaceAbove - 20 : spaceBelow - 20;
        const calculatedMaxHeight = Math.min(220, Math.max(120, availableSpace));

        return {
            top: placeTop ? Math.max(10, rect.top - 6) : rect.bottom + 6,
            left: rect.left,
            width: Math.max(rect.width, 180),
            placement: placeTop ? 'top' : 'bottom',
            transform: placeTop ? 'translateY(-100%)' : 'none',
            maxHeight: `${calculatedMaxHeight}px`
        };
    };

    const updateCoords = () => {
        const nextCoords = calculateCoords();
        if (nextCoords) {
            setDropdownCoords(nextCoords);
        }
    };

    const openDropdown = () => {
        const nextCoords = calculateCoords();
        if (nextCoords) {
            setDropdownCoords(nextCoords);
        }
        setIsOpen(true);
        setIsTyping(false);
    };

    useEffect(() => {
        if (isOpen) {
            updateCoords();
            window.addEventListener('resize', updateCoords);
            window.addEventListener('scroll', updateCoords, true);
            return () => {
                window.removeEventListener('resize', updateCoords);
                window.removeEventListener('scroll', updateCoords, true);
            };
        }
    }, [isOpen]);

    // Normalize options to { id, label } with custom additions and deleted exclusions
    const normalizedOptions = useMemo(() => {
        const map = new Map();
        const deletedSet = new Set(deletedSelectOptions.map(d => (typeof d === 'string' ? d : d.id || d.label || '').toLowerCase()));

        // Base options
        (options || []).forEach(opt => {
            let id, label;
            if (typeof opt === 'string') {
                id = opt;
                label = opt;
            } else {
                id = opt.id ?? opt.name ?? opt.value ?? opt.label;
                label = opt.label ?? opt.name ?? opt.id ?? opt.value;
            }
            if (!id) return;
            if (deletedSet.has(String(id).toLowerCase()) || deletedSet.has(String(label).toLowerCase())) return;
            map.set(String(id).toLowerCase(), { id: String(id), label: String(label) });
        });

        // Custom options
        (customCreatedOptions || []).forEach(opt => {
            let id, label;
            if (typeof opt === 'string') {
                id = opt;
                label = opt;
            } else {
                id = opt.id ?? opt.label;
                label = opt.label ?? opt.id;
            }
            if (!id) return;
            const lower = String(id).toLowerCase();
            if (deletedSet.has(lower) || deletedSet.has(String(label).toLowerCase())) return;
            if (!map.has(lower)) {
                map.set(lower, { id: String(id), label: String(label) });
            }
        });

        return Array.from(map.values());
    }, [options, customCreatedOptions, deletedSelectOptions]);

    // Sync searchTerm when value changes externally
    useEffect(() => {
        const valLower = (value || '').trim().toLowerCase();
        const matched = normalizedOptions.find(o =>
            o.id === value ||
            o.label === value ||
            (valLower && o.id.toLowerCase() === valLower) ||
            (valLower && o.label.toLowerCase() === valLower) ||
            (valLower && o.label.toLowerCase().includes(valLower))
        );
        setSearchTerm(matched ? matched.label : (value || ''));
        setIsTyping(false);
    }, [value, normalizedOptions]);

    // Filter options: when opened, display ALL options unless user is actively typing a search
    const filteredOptions = useMemo(() => {
        if (!isSearchable || !isTyping || !searchTerm.trim() || searchTerm.trim().toLowerCase() === (value || '').trim().toLowerCase()) {
            return normalizedOptions;
        }
        return normalizedOptions.filter(opt =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase().trim())
        );
    }, [normalizedOptions, searchTerm, isSearchable, isTyping, value]);

    // Check if current search term exact matches any existing option
    const exactMatch = useMemo(() => {
        if (!isCreatable || !searchTerm.trim()) return true;
        return normalizedOptions.some(
            opt => opt.label.toLowerCase() === searchTerm.toLowerCase().trim()
        );
    }, [normalizedOptions, searchTerm, isCreatable]);

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target)
            ) {
                setIsOpen(false);
                setIsTyping(false);
                setSearchTerm(value || '');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, value]);

    const handleSelectOrAdd = (val) => {
        const trimmed = (val || '').trim();
        if (!trimmed) return;

        const lower = trimmed.toLowerCase();

        // If it was in deleted list, restore it
        if (deletedSelectOptions.some(d => (typeof d === 'string' ? d : d.id || d.label || '').toLowerCase() === lower)) {
            const nextDel = deletedSelectOptions.filter(d => (typeof d === 'string' ? d : d.id || d.label || '').toLowerCase() !== lower);
            saveDeletedOptions(nextDel);
        }

        const existing = normalizedOptions.find(
            opt => opt.label.toLowerCase() === lower
        );

        if (existing) {
            onChange(existing.id);
            setSearchTerm(existing.label);
        } else if (isCreatable) {
            // Save to custom options
            const nextCustom = [...customCreatedOptions.filter(c => (typeof c === 'string' ? c : c.id || c.label || '').toLowerCase() !== lower), { id: trimmed, label: trimmed }];
            saveCustomOptions(nextCustom);

            if (onCreateOption) {
                onCreateOption(trimmed);
            }
            onChange(trimmed);
            setSearchTerm(trimmed);
        }
        setIsTyping(false);
        setIsOpen(false);
    };

    const handleDeleteOption = (optToDelete, e) => {
        if (e) e.stopPropagation();
        const optId = (optToDelete.id || optToDelete.label || '').toLowerCase();
        const optLabel = (optToDelete.label || optToDelete.id || '').toLowerCase();
        if (!optId && !optLabel) return;

        // Remove from custom options
        const nextCustom = customCreatedOptions.filter(c => {
            const cId = (typeof c === 'string' ? c : c.id || c.label || '').toLowerCase();
            return cId !== optId && cId !== optLabel;
        });
        saveCustomOptions(nextCustom);

        // Add to deleted options
        if (!deletedSelectOptions.some(d => {
            const dId = (typeof d === 'string' ? d : d.id || d.label || '').toLowerCase();
            return dId === optId || dId === optLabel;
        })) {
            const nextDel = [...deletedSelectOptions, optToDelete.id || optToDelete.label];
            saveDeletedOptions(nextDel);
        }

        if (onDeleteOption) {
            onDeleteOption(optToDelete);
        }

        // If current value was this option, clear it
        if ((value || '').toLowerCase() === optId || (value || '').toLowerCase() === optLabel) {
            onChange('');
            setSearchTerm('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredOptions.length > 0) {
                handleSelectOrAdd(filteredOptions[0].label);
            } else if (isCreatable && searchTerm.trim()) {
                handleSelectOrAdd(searchTerm.trim());
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
            setIsTyping(false);
        } else if (e.key === 'ArrowDown' && !isOpen) {
            openDropdown();
        }
    };

    const handleBoxClick = () => {
        if (isOpen) {
            setIsOpen(false);
        } else {
            openDropdown();
            if (inputRef.current) {
                inputRef.current.select();
            }
        }
    };

    const dropdownMenu = isOpen && (
        <div
            ref={dropdownRef}
            className={`pipeline-creatable-dropdown pipeline-portal-dropdown ${dropdownCoords.placement === 'top' ? 'placement-top' : 'placement-bottom'}`}
            style={{
                position: 'fixed',
                top: `${dropdownCoords.top}px`,
                left: `${dropdownCoords.left}px`,
                width: `${dropdownCoords.width}px`,
                maxHeight: dropdownCoords.maxHeight || '220px',
                transform: dropdownCoords.transform,
                zIndex: 999999
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="pipeline-creatable-options-list">
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((opt) => {
                        const isSelected = (value || '').toLowerCase() === opt.id.toLowerCase();
                        return (
                            <div
                                key={opt.id}
                                className={`pipeline-creatable-option ${isSelected ? 'selected' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectOrAdd(opt.label);
                                }}
                            >
                                <span className="pipeline-creatable-opt-text">{opt.label}</span>
                                {isSelected && <CheckCircle size={14} className="pipeline-creatable-opt-check" />}
                                {isDeleteable && (
                                    <button
                                        type="button"
                                        className="pipeline-creatable-opt-delete-btn"
                                        onClick={(e) => handleDeleteOption(opt, e)}
                                        title={`Remove "${opt.label}" from options`}
                                    >
                                        <X size={12} />
                                    </button>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className="pipeline-creatable-no-match">
                        {isCreatable ? 'Type to add new option...' : 'No options available'}
                    </div>
                )}
            </div>

            {isCreatable && isTyping && !exactMatch && searchTerm.trim() && (
                <div
                    className="pipeline-creatable-add-new-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSelectOrAdd(searchTerm.trim());
                    }}
                >
                    <Plus size={14} />
                    <span>Add "<strong>{searchTerm.trim()}</strong>" as new</span>
                </div>
            )}
        </div>
    );

    return (
        <div className={`pipeline-creatable-select-wrapper ${isOpen ? 'is-open' : ''} ${className}`} ref={wrapperRef}>
            <div
                className={`pipeline-creatable-input-box ${isOpen ? 'is-focused' : ''}`}
                onClick={handleBoxClick}
                style={{ cursor: isSearchable ? 'text' : 'pointer' }}
            >
                <input
                    ref={inputRef}
                    type="text"
                    className="pipeline-creatable-input"
                    placeholder={placeholder}
                    value={(() => {
                        const valLower = (value || '').trim().toLowerCase();
                        const matched = normalizedOptions.find(o =>
                            o.id === value ||
                            o.label === value ||
                            (valLower && o.id.toLowerCase() === valLower) ||
                            (valLower && o.label.toLowerCase() === valLower) ||
                            (valLower && o.label.toLowerCase().includes(valLower))
                        );
                        const displayLabel = matched ? matched.label : (value || '');
                        return isSearchable ? (isTyping ? searchTerm : (displayLabel || searchTerm)) : displayLabel;
                    })()}
                    readOnly={!isSearchable}
                    style={{ cursor: isSearchable ? 'text' : 'pointer', userSelect: isSearchable ? 'auto' : 'none' }}
                    required={required}
                    onChange={(e) => {
                        if (isSearchable) {
                            setSearchTerm(e.target.value);
                            setIsTyping(true);
                            if (!isOpen) openDropdown();
                        }
                    }}
                    onFocus={(e) => {
                        if (isSearchable && !isOpen) {
                            openDropdown();
                            e.target.select();
                        }
                    }}
                    onKeyDown={handleKeyDown}
                />
                <button
                    type="button"
                    className="pipeline-creatable-chevron-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (isOpen) {
                            setIsOpen(false);
                        } else {
                            openDropdown();
                        }
                    }}
                    tabIndex={-1}
                    title="Toggle options"
                >
                    <ChevronDown size={15} className={`pipeline-chevron-icon ${isOpen ? 'open' : ''}`} />
                </button>
            </div>

            {isOpen && createPortal(dropdownMenu, document.body)}
        </div>
    );
}

// Reusable Multi-Select Contacts & Emails Combobox with Badges, Persistent Additions & Remove (X) button
function MultiSelectContactEmails({
    values = [],
    onChange,
    options = [],
    placeholder = "Select contact emails...",
    className = "",
    isCreatable = true,
    storageKey = "hrms_company_owner_contacts"
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 260, placement: 'bottom', transform: 'none' });
    const wrapperRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Persistent custom added options
    const [customOptions, setCustomOptions] = useState(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });

    // Persistent deleted / hidden options
    const [deletedOptions, setDeletedOptions] = useState(() => {
        try {
            const saved = localStorage.getItem(`${storageKey}_deleted`);
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });

    const saveCustomOptions = (newList) => {
        setCustomOptions(newList);
        try {
            localStorage.setItem(storageKey, JSON.stringify(newList));
        } catch (e) { }
    };

    const saveDeletedOptions = (newList) => {
        setDeletedOptions(newList);
        try {
            localStorage.setItem(`${storageKey}_deleted`, JSON.stringify(newList));
        } catch (e) { }
    };

    const selectedArray = useMemo(() => {
        if (Array.isArray(values)) return values.filter(Boolean);
        if (typeof values === 'string' && values.trim()) {
            return values.split(',').map(s => s.trim()).filter(Boolean);
        }
        return [];
    }, [values]);

    // Combine passed options + customOptions, excluding deletedOptions
    const allMergedOptions = useMemo(() => {
        const map = new Map();
        const deletedSet = new Set(deletedOptions.map(d => (typeof d === 'string' ? d : d.email || d.id || '').toLowerCase()));

        // Process base options
        (options || []).forEach(opt => {
            const email = (typeof opt === 'string' ? opt : opt.email || opt.id || '').trim();
            if (!email) return;
            const lower = email.toLowerCase();
            if (deletedSet.has(lower)) return;
            map.set(lower, typeof opt === 'string' ? { id: opt, email: opt, name: opt.split('@')[0], company: '' } : opt);
        });

        // Process custom options
        (customOptions || []).forEach(opt => {
            const email = (typeof opt === 'string' ? opt : opt.email || opt.id || '').trim();
            if (!email) return;
            const lower = email.toLowerCase();
            if (deletedSet.has(lower)) return;
            if (!map.has(lower)) {
                map.set(lower, typeof opt === 'string' ? { id: opt, email: opt, name: opt.split('@')[0], company: 'Custom' } : opt);
            }
        });

        return Array.from(map.values());
    }, [options, customOptions, deletedOptions]);

    const calculateCoords = () => {
        if (!wrapperRef.current) return null;
        const rect = wrapperRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        const placeTop = spaceBelow < 260 && spaceAbove > spaceBelow;
        const availableSpace = placeTop ? spaceAbove - 20 : spaceBelow - 20;
        const calculatedMaxHeight = Math.min(270, Math.max(140, availableSpace));

        return {
            top: placeTop ? Math.max(10, rect.top - 6) : rect.bottom + 6,
            left: rect.left,
            width: Math.max(rect.width, 300),
            placement: placeTop ? 'top' : 'bottom',
            transform: placeTop ? 'translateY(-100%)' : 'none',
            maxHeight: `${calculatedMaxHeight}px`
        };
    };

    const updateCoords = () => {
        const nextCoords = calculateCoords();
        if (nextCoords) setDropdownCoords(nextCoords);
    };

    const openDropdown = () => {
        const nextCoords = calculateCoords();
        if (nextCoords) setDropdownCoords(nextCoords);
        setIsOpen(true);
        setTimeout(() => {
            if (searchInputRef.current) searchInputRef.current.focus();
        }, 50);
    };

    useEffect(() => {
        if (isOpen) {
            updateCoords();
            window.addEventListener('resize', updateCoords);
            window.addEventListener('scroll', updateCoords, true);
            return () => {
                window.removeEventListener('resize', updateCoords);
                window.removeEventListener('scroll', updateCoords, true);
            };
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target)
            ) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleToggleOption = (email) => {
        if (!email) return;
        const cleanEmail = email.trim();
        const isSelected = selectedArray.some(e => e.toLowerCase() === cleanEmail.toLowerCase());
        let next;
        if (isSelected) {
            next = selectedArray.filter(e => e.toLowerCase() !== cleanEmail.toLowerCase());
        } else {
            next = [...selectedArray, cleanEmail];
        }
        if (onChange) onChange(next);
    };

    const handleAddNewOption = (newEmail) => {
        if (!newEmail || !newEmail.trim()) return;
        const cleanEmail = newEmail.trim();
        const lower = cleanEmail.toLowerCase();

        // If it was in deleted list, restore it
        if (deletedOptions.some(d => (typeof d === 'string' ? d : d.email || d.id || '').toLowerCase() === lower)) {
            const nextDeleted = deletedOptions.filter(d => (typeof d === 'string' ? d : d.email || d.id || '').toLowerCase() !== lower);
            saveDeletedOptions(nextDeleted);
        }

        // Add to custom options if not already present
        const existing = allMergedOptions.find(o => (o.email || o.id || '').toLowerCase() === lower);
        if (!existing) {
            const newOptObj = {
                id: cleanEmail,
                email: cleanEmail,
                name: cleanEmail.includes('@') ? cleanEmail.split('@')[0] : cleanEmail,
                company: '',
                type: 'Custom'
            };
            const nextCustom = [...customOptions.filter(c => (c.email || c.id || '').toLowerCase() !== lower), newOptObj];
            saveCustomOptions(nextCustom);
        }

        // Also select this newly added option
        if (!selectedArray.some(e => e.toLowerCase() === lower)) {
            if (onChange) onChange([...selectedArray, cleanEmail]);
        }
        setSearchTerm('');
    };

    const handleDeleteOptionFromList = (optToDelete, e) => {
        if (e) e.stopPropagation();
        const emailToDelete = (typeof optToDelete === 'string' ? optToDelete : optToDelete.email || optToDelete.id || '').toLowerCase();
        if (!emailToDelete) return;

        // 1. Remove from custom options
        const nextCustom = customOptions.filter(c => (c.email || c.id || '').toLowerCase() !== emailToDelete);
        saveCustomOptions(nextCustom);

        // 2. Add to deleted options list
        if (!deletedOptions.some(d => (typeof d === 'string' ? d : d.email || d.id || '').toLowerCase() === emailToDelete)) {
            const nextDeleted = [...deletedOptions, emailToDelete];
            saveDeletedOptions(nextDeleted);
        }

        // 3. If currently selected in values, unselect it
        if (selectedArray.some(e => e.toLowerCase() === emailToDelete)) {
            const nextSelected = selectedArray.filter(e => e.toLowerCase() !== emailToDelete);
            if (onChange) onChange(nextSelected);
        }
    };

    const handleRemoveBadge = (emailToRemove, e) => {
        if (e) e.stopPropagation();
        const next = selectedArray.filter(e => e.toLowerCase() !== emailToRemove.toLowerCase());
        if (onChange) onChange(next);
    };

    const filteredOptions = useMemo(() => {
        if (!searchTerm.trim()) return allMergedOptions;
        const term = searchTerm.toLowerCase().trim();
        return allMergedOptions.filter(opt => {
            const email = (opt.email || opt.id || '').toLowerCase();
            const name = (opt.name || opt.label || '').toLowerCase();
            const company = (opt.company || '').toLowerCase();
            return email.includes(term) || name.includes(term) || company.includes(term);
        });
    }, [allMergedOptions, searchTerm]);

    const exactMatch = useMemo(() => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase().trim();
        return allMergedOptions.some(opt => (opt.email || opt.id || '').toLowerCase() === term);
    }, [allMergedOptions, searchTerm]);

    const firstEmail = selectedArray[0] || '';
    const firstMatchedOpt = allMergedOptions.find(o => (o.email || o.id || '').toLowerCase() === firstEmail.toLowerCase());
    const firstDisplayLabel = firstMatchedOpt
        ? (firstMatchedOpt.name ? `${firstMatchedOpt.name} (${firstEmail})` : firstEmail)
        : firstEmail;

    const dropdownMenu = isOpen && (
        <div
            ref={dropdownRef}
            className={`pipeline-multiselect-dropdown pipeline-portal-dropdown ${dropdownCoords.placement === 'top' ? 'placement-top' : 'placement-bottom'}`}
            style={{
                position: 'fixed',
                top: `${dropdownCoords.top}px`,
                left: `${dropdownCoords.left}px`,
                width: `${dropdownCoords.width}px`,
                maxHeight: dropdownCoords.maxHeight || '270px',
                transform: dropdownCoords.transform,
                zIndex: 999999
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="pipeline-multiselect-search-box" onClick={e => e.stopPropagation()}>
                <Search size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />
                <input
                    ref={searchInputRef}
                    type="text"
                    className="pipeline-multiselect-search-input"
                    placeholder="Search contacts by name or email..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (searchTerm.trim()) {
                                handleAddNewOption(searchTerm.trim());
                            }
                        } else if (e.key === 'Escape') {
                            setIsOpen(false);
                            setSearchTerm('');
                        }
                    }}
                    onClick={e => e.stopPropagation()}
                />
                {searchTerm && (
                    <button
                        type="button"
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px', color: '#94a3b8', display: 'flex', alignItems: 'center' }}
                        onClick={() => setSearchTerm('')}
                    >
                        <X size={12} />
                    </button>
                )}
            </div>

            <div className="pipeline-multiselect-dropdown-header">
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    CRM Contacts ({filteredOptions.length})
                </span>
                {selectedArray.length > 0 && (
                    <button
                        type="button"
                        className="pipeline-multiselect-clear-all"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onChange) onChange([]);
                        }}
                    >
                        Clear all ({selectedArray.length})
                    </button>
                )}
            </div>

            <div className="pipeline-multiselect-options-list" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((opt) => {
                        const email = opt.email || opt.id || '';
                        const isSelected = selectedArray.some(e => e.toLowerCase() === email.toLowerCase());
                        const name = opt.name || opt.label || email.split('@')[0];
                        const initials = name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'C';

                        return (
                            <div
                                key={email}
                                className={`pipeline-multiselect-option ${isSelected ? 'selected' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleOption(email);
                                }}
                            >
                                <div className="pipeline-multiselect-opt-left">
                                    <div className={`pipeline-multiselect-avatar ${isSelected ? 'active' : ''}`}>
                                        {initials}
                                    </div>
                                    <div className="pipeline-multiselect-opt-details">
                                        <span className="pipeline-multiselect-opt-name">
                                            {name}
                                            {opt.company && opt.company !== 'Direct Client' && (
                                                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500, marginLeft: '6px' }}>({opt.company})</span>
                                            )}
                                        </span>
                                        <span className="pipeline-multiselect-opt-email">{email}</span>
                                    </div>
                                </div>

                                <div className="pipeline-multiselect-opt-actions">
                                    <div className={`pipeline-multiselect-checkbox ${isSelected ? 'checked' : ''}`}>
                                        {isSelected && <Check size={12} strokeWidth={3} />}
                                    </div>
                                    <button
                                        type="button"
                                        className="pipeline-multiselect-opt-delete-btn"
                                        onClick={(e) => handleDeleteOptionFromList(opt, e)}
                                        title={`Delete "${email}" from list`}
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="pipeline-creatable-no-match" style={{ padding: '14px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                        No contacts found matching "{searchTerm}"
                    </div>
                )}
            </div>

            {isCreatable && searchTerm.trim() && !exactMatch && (
                <div
                    className="pipeline-creatable-add-new-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleAddNewOption(searchTerm.trim());
                    }}
                    style={{ borderTop: '1px solid #f1f5f9', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', background: '#f8fafc', color: '#0284c7', fontSize: '13px', fontWeight: 600 }}
                >
                    <Plus size={14} />
                    <span>Add "<strong>{searchTerm.trim()}</strong>" as contact email</span>
                </div>
            )}
        </div>
    );

    return (
        <div className={`pipeline-multiselect-wrapper ${isOpen ? 'is-open' : ''} ${className}`} ref={wrapperRef}>
            <div
                className={`pipeline-multiselect-box ${isOpen ? 'is-focused' : ''}`}
                onClick={() => {
                    if (isOpen) {
                        setIsOpen(false);
                    } else {
                        openDropdown();
                    }
                }}
            >
                {selectedArray.length === 0 ? (
                    <span className="pipeline-multiselect-placeholder">{placeholder}</span>
                ) : (
                    <div className="pipeline-multiselect-selection-row">
                        <span className="pipeline-multiselect-badge" title={firstEmail}>
                            <Mail size={12} className="pipeline-multiselect-badge-icon" />
                            <span className="pipeline-multiselect-badge-text">{firstDisplayLabel}</span>
                            <button
                                type="button"
                                className="pipeline-multiselect-badge-remove"
                                onClick={(e) => handleRemoveBadge(firstEmail, e)}
                                title={`Remove ${firstEmail}`}
                            >
                                <X size={11} />
                            </button>
                        </span>

                        {selectedArray.length > 1 && (
                            <span
                                className="pipeline-multiselect-count-badge"
                                title={selectedArray.slice(1).map(em => {
                                    const m = allMergedOptions.find(o => (o.email || o.id || '').toLowerCase() === em.toLowerCase());
                                    return m ? (m.name ? `${m.name} (${em})` : em) : em;
                                }).join(', ')}
                            >
                                +{selectedArray.length - 1}
                            </span>
                        )}
                    </div>
                )}

                <button
                    type="button"
                    className="pipeline-creatable-chevron-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (isOpen) {
                            setIsOpen(false);
                        } else {
                            openDropdown();
                        }
                    }}
                    tabIndex={-1}
                    title="Toggle contacts"
                >
                    <ChevronDown size={15} className={`pipeline-chevron-icon ${isOpen ? 'open' : ''}`} />
                </button>
            </div>

            {isOpen && createPortal(dropdownMenu, document.body)}
        </div>
    );
}

// Multi-Select Component for Contacts strictly associated with a Deal
function MultiSelectDealContacts({
    contacts = [],
    selectedIds = [],
    onChange,
    placeholder = "Select associated contacts..."
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 280, placement: 'bottom', transform: 'none' });
    const wrapperRef = useRef(null);
    const dropdownRef = useRef(null);

    const calculateCoords = () => {
        if (!wrapperRef.current) return null;
        const rect = wrapperRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        const placeTop = spaceBelow < 240 && spaceAbove > spaceBelow;
        const availableSpace = placeTop ? spaceAbove - 20 : spaceBelow - 20;
        const calculatedMaxHeight = Math.min(260, Math.max(130, availableSpace));

        return {
            top: placeTop ? Math.max(10, rect.top - 6) : rect.bottom + 6,
            left: rect.left,
            width: Math.max(rect.width, 280),
            placement: placeTop ? 'top' : 'bottom',
            transform: placeTop ? 'translateY(-100%)' : 'none',
            maxHeight: `${calculatedMaxHeight}px`
        };
    };

    const updateCoords = () => {
        const nextCoords = calculateCoords();
        if (nextCoords) setDropdownCoords(nextCoords);
    };

    const openDropdown = () => {
        const nextCoords = calculateCoords();
        if (nextCoords) setDropdownCoords(nextCoords);
        setIsOpen(true);
    };

    useEffect(() => {
        if (isOpen) {
            updateCoords();
            window.addEventListener('resize', updateCoords);
            window.addEventListener('scroll', updateCoords, true);
            return () => {
                window.removeEventListener('resize', updateCoords);
                window.removeEventListener('scroll', updateCoords, true);
            };
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target)
            ) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleToggle = (id) => {
        const isSelected = selectedIds.includes(id);
        let next;
        if (isSelected) {
            next = selectedIds.filter(item => item !== id);
        } else {
            next = [...selectedIds, id];
        }
        onChange(next);
    };

    const allSelected = contacts.length > 0 && selectedIds.length === contacts.length;

    const filteredContacts = useMemo(() => {
        if (!searchTerm.trim()) return contacts;
        const q = searchTerm.toLowerCase();
        return contacts.filter(c => {
            const name = `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase();
            const email = (c.email || '').toLowerCase();
            const company = (c.company || '').toLowerCase();
            return name.includes(q) || email.includes(q) || company.includes(q);
        });
    }, [contacts, searchTerm]);

    const dropdownMenu = (
        <div
            ref={dropdownRef}
            style={{
                position: 'fixed',
                top: `${dropdownCoords.top}px`,
                left: `${dropdownCoords.left}px`,
                width: `${dropdownCoords.width}px`,
                maxHeight: dropdownCoords.maxHeight || '240px',
                transform: dropdownCoords.transform,
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)',
                zIndex: 999999,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Search Filter if more than 4 contacts */}
            {contacts.length > 4 && (
                <div style={{ padding: '8px 10px 6px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Search size={13} style={{ color: '#94a3b8' }} />
                    <input
                        type="text"
                        placeholder="Filter deal contacts..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '12.5px', color: '#1e293b' }}
                    />
                    {searchTerm && (
                        <button type="button" onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#94a3b8' }}>
                            <X size={12} />
                        </button>
                    )}
                </div>
            )}

            {/* Header with Select All / Deselect All */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Deal Contacts ({contacts.length})
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onChange(allSelected ? [] : contacts.map(c => c._id));
                        }}
                        style={{ background: 'none', border: 'none', color: '#0284c7', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                    >
                        {allSelected ? 'Deselect All' : 'Select All'}
                    </button>
                </div>
            </div>

            {/* Options List */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '4px' }}>
                {filteredContacts.map(c => {
                    const isChecked = selectedIds.includes(c._id);
                    const name = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || c.company || 'Contact';
                    return (
                        <div
                            key={c._id}
                            onClick={() => handleToggle(c._id)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '7px 10px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                backgroundColor: isChecked ? '#f0f9ff' : 'transparent',
                                transition: 'background-color 0.1s ease'
                            }}
                            onMouseEnter={e => { if (!isChecked) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                            onMouseLeave={e => { if (!isChecked) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                            <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => { }}
                                style={{ width: '15px', height: '15px', accentColor: '#0284c7', cursor: 'pointer', flexShrink: 0 }}
                            />
                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                                <span style={{ fontSize: '13px', fontWeight: isChecked ? 600 : 500, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {name}
                                </span>
                                {c.email && (
                                    <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {c.email}
                                    </span>
                                )}
                            </div>
                            {c.leadStage && (
                                <span className="pipeline-stage-badge new" style={{ fontSize: '10px', padding: '1px 6px', flexShrink: 0 }}>
                                    {c.leadStage}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            <div
                onClick={() => {
                    if (isOpen) setIsOpen(false);
                    else openDropdown();
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '4px 10px',
                    backgroundColor: '#ffffff',
                    border: isOpen ? '1.5px solid #0284c7' : '1.5px solid #cbd5e1',
                    borderRadius: '8px',
                    minHeight: '38px',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    gap: '6px'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', flex: 1, overflow: 'hidden' }}>
                    {selectedIds.length === 0 ? (
                        <span style={{ fontSize: '13px', color: '#94a3b8' }}>{placeholder}</span>
                    ) : selectedIds.length <= 2 ? (
                        selectedIds.map(id => {
                            const c = contacts.find(item => item._id === id);
                            const label = c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email : id;
                            return (
                                <span
                                    key={id}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        backgroundColor: '#e0f2fe',
                                        color: '#0369a1',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        padding: '2px 7px',
                                        borderRadius: '5px'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggle(id);
                                    }}
                                >
                                    {label}
                                    <X size={11} style={{ cursor: 'pointer' }} />
                                </span>
                            );
                        })
                    ) : (
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            backgroundColor: '#e0f2fe',
                            color: '#0369a1',
                            fontSize: '12.5px',
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: '5px'
                        }}>
                            <CheckCircle size={13} />
                            {selectedIds.length} of {contacts.length} contacts selected
                        </span>
                    )}
                </div>

                <ChevronDown size={15} style={{ color: '#64748b', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease', flexShrink: 0 }} />
            </div>

            {isOpen && createPortal(dropdownMenu, document.body)}
        </div>
    );
}

// Multi-Select Component for Deals strictly associated with a Contact
function MultiSelectContactDeals({
    deals = [],
    selectedIds = [],
    onChange,
    placeholder = "Select associated deals..."
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 280, placement: 'bottom', transform: 'none' });
    const wrapperRef = useRef(null);
    const dropdownRef = useRef(null);

    const calculateCoords = () => {
        if (!wrapperRef.current) return null;
        const rect = wrapperRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        const placeTop = spaceBelow < 240 && spaceAbove > spaceBelow;
        const availableSpace = placeTop ? spaceAbove - 20 : spaceBelow - 20;
        const calculatedMaxHeight = Math.min(260, Math.max(130, availableSpace));

        return {
            top: placeTop ? Math.max(10, rect.top - 6) : rect.bottom + 6,
            left: rect.left,
            width: Math.max(rect.width, 280),
            placement: placeTop ? 'top' : 'bottom',
            transform: placeTop ? 'translateY(-100%)' : 'none',
            maxHeight: `${calculatedMaxHeight}px`
        };
    };

    const updateCoords = () => {
        const nextCoords = calculateCoords();
        if (nextCoords) setDropdownCoords(nextCoords);
    };

    const openDropdown = () => {
        const nextCoords = calculateCoords();
        if (nextCoords) setDropdownCoords(nextCoords);
        setIsOpen(true);
    };

    useEffect(() => {
        if (isOpen) {
            updateCoords();
            window.addEventListener('resize', updateCoords);
            window.addEventListener('scroll', updateCoords, true);
            return () => {
                window.removeEventListener('resize', updateCoords);
                window.removeEventListener('scroll', updateCoords, true);
            };
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target)
            ) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleToggle = (id) => {
        const isSelected = selectedIds.includes(id);
        let next;
        if (isSelected) {
            next = selectedIds.filter(item => item !== id);
        } else {
            next = [...selectedIds, id];
        }
        onChange(next);
    };

    const allSelected = deals.length > 0 && selectedIds.length === deals.length;

    const filteredDeals = useMemo(() => {
        if (!searchTerm.trim()) return deals;
        const q = searchTerm.toLowerCase();
        return deals.filter(d => {
            const name = (d.dealName || d.clientName || '').toLowerCase();
            const val = (d.dealValue || '').toString();
            const owner = (d.salesOwner || '').toLowerCase();
            return name.includes(q) || val.includes(q) || owner.includes(q);
        });
    }, [deals, searchTerm]);

    const dropdownMenu = (
        <div
            ref={dropdownRef}
            style={{
                position: 'fixed',
                top: `${dropdownCoords.top}px`,
                left: `${dropdownCoords.left}px`,
                width: `${dropdownCoords.width}px`,
                maxHeight: dropdownCoords.maxHeight || '240px',
                transform: dropdownCoords.transform,
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)',
                zIndex: 999999,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Search Filter if more than 4 deals */}
            {deals.length > 4 && (
                <div style={{ padding: '8px 10px 6px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Search size={13} style={{ color: '#94a3b8' }} />
                    <input
                        type="text"
                        placeholder="Filter contact deals..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '12.5px', color: '#1e293b' }}
                    />
                    {searchTerm && (
                        <button type="button" onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#94a3b8' }}>
                            <X size={12} />
                        </button>
                    )}
                </div>
            )}

            {/* Header with Select All / Deselect All */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Associated Deals ({deals.length})
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onChange(allSelected ? [] : deals.map(d => d._id));
                        }}
                        style={{ background: 'none', border: 'none', color: '#0284c7', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                    >
                        {allSelected ? 'Deselect All' : 'Select All'}
                    </button>
                </div>
            </div>

            {/* Options List */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '4px' }}>
                {filteredDeals.map(d => {
                    const isChecked = selectedIds.includes(d._id);
                    const name = d.dealName || d.clientName || 'Deal';
                    return (
                        <div
                            key={d._id}
                            onClick={() => handleToggle(d._id)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '7px 10px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                backgroundColor: isChecked ? '#f0f9ff' : 'transparent',
                                transition: 'background-color 0.1s ease'
                            }}
                            onMouseEnter={e => { if (!isChecked) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                            onMouseLeave={e => { if (!isChecked) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                            <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => { }}
                                style={{ width: '15px', height: '15px', accentColor: '#0284c7', cursor: 'pointer', flexShrink: 0 }}
                            />
                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                                <span style={{ fontSize: '13px', fontWeight: isChecked ? 600 : 500, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {name}
                                </span>
                                <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 600 }}>
                                    {d.currency || '$'}{Number(d.dealValue || 0).toLocaleString()}
                                </span>
                            </div>
                            <span className={`pipeline-stage-badge ${(d.stage || 'new').toLowerCase()}`} style={{ fontSize: '10px', padding: '1px 6px', flexShrink: 0 }}>
                                {d.stage || 'Deal'}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            <div
                onClick={() => {
                    if (isOpen) setIsOpen(false);
                    else openDropdown();
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '4px 10px',
                    backgroundColor: '#ffffff',
                    border: isOpen ? '1.5px solid #0284c7' : '1.5px solid #cbd5e1',
                    borderRadius: '8px',
                    minHeight: '38px',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    gap: '6px'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', flex: 1, overflow: 'hidden' }}>
                    {selectedIds.length === 0 ? (
                        <span style={{ fontSize: '13px', color: '#94a3b8' }}>{placeholder}</span>
                    ) : selectedIds.length <= 2 ? (
                        selectedIds.map(id => {
                            const d = deals.find(item => item._id === id);
                            const label = d ? (d.dealName || d.clientName) : id;
                            return (
                                <span
                                    key={id}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        backgroundColor: '#e0f2fe',
                                        color: '#0369a1',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        padding: '2px 7px',
                                        borderRadius: '5px'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggle(id);
                                    }}
                                >
                                    {label}
                                    <X size={11} style={{ cursor: 'pointer' }} />
                                </span>
                            );
                        })
                    ) : (
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            backgroundColor: '#e0f2fe',
                            color: '#0369a1',
                            fontSize: '12.5px',
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: '5px'
                        }}>
                            <CheckCircle size={13} />
                            {selectedIds.length} of {deals.length} deals selected
                        </span>
                    )}
                </div>

                <ChevronDown size={15} style={{ color: '#64748b', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease', flexShrink: 0 }} />
            </div>

            {isOpen && createPortal(dropdownMenu, document.body)}
        </div>
    );
}

// Interactive Pill Shape Badge for Single or Multiple Company Owners
function CompanyOwnerDisplay({ owner, fallback = '—', className = '' }) {
    const [showTooltip, setShowTooltip] = useState(false);
    const pillRef = useRef(null);
    const tooltipRef = useRef(null);

    const owners = useMemo(() => {
        if (Array.isArray(owner)) {
            return owner.map(s => String(s || '').trim()).filter(Boolean);
        }
        if (typeof owner === 'string' && owner.trim()) {
            return owner.split(',').map(s => s.trim()).filter(Boolean);
        }
        if (fallback && fallback !== '—' && fallback !== '-') {
            return [fallback];
        }
        return [];
    }, [owner, fallback]);

    useEffect(() => {
        if (!showTooltip) return;
        const handleClickOutside = (e) => {
            if (
                pillRef.current &&
                !pillRef.current.contains(e.target) &&
                tooltipRef.current &&
                !tooltipRef.current.contains(e.target)
            ) {
                setShowTooltip(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showTooltip]);

    if (owners.length === 0) {
        return <span style={{ color: '#94a3b8' }}>—</span>;
    }

    // Single Owner: Blue text with Mail icon
    if (owners.length === 1) {
        return (
            <span
                className={`pipeline-company-single-owner ${className}`}
                style={{
                    color: '#0284c7',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    wordBreak: 'break-all',
                    fontSize: '12.5px'
                }}
                title={`Company Owner: ${owners[0]}`}
            >
                <Mail size={12} style={{ color: '#0284c7', flexShrink: 0 }} />
                <span>{owners[0]}</span>
            </span>
        );
    }

    // Multiple Owners: Pill shape badge with interactive dropdown/tooltip
    const firstOwner = owners[0];
    const allOwnersTitle = `Associated Company Owners (${owners.length}):\n${owners.map((o, i) => `${i + 1}. ${o}`).join('\n')}`;

    return (
        <div
            ref={pillRef}
            style={{ position: 'relative', display: 'inline-block' }}
            className={`pipeline-company-owner-pill-container ${className}`}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            <div
                className="pipeline-company-owner-pill-badge"
                onClick={(e) => {
                    e.stopPropagation();
                    setShowTooltip(prev => !prev);
                }}
                title={allOwnersTitle}
                style={{
                    background: '#e0f2fe',
                    color: '#0284c7',
                    border: '1px solid #bae6fd',
                    borderRadius: '999px',
                    padding: '2px 9px',
                    fontSize: '11px',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    userSelect: 'none'
                }}
            >
                <Mail size={11} style={{ flexShrink: 0 }} />
                <span style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {firstOwner}
                </span>
                <span
                    style={{
                        background: '#0284c7',
                        color: '#ffffff',
                        borderRadius: '999px',
                        padding: '1px 6px',
                        fontSize: '9.5px',
                        fontWeight: 700,
                        lineHeight: 1.2
                    }}
                >
                    +{owners.length - 1}
                </span>
            </div>

            {showTooltip && (
                <div
                    ref={tooltipRef}
                    className="pipeline-company-owner-popover"
                    style={{
                        position: 'absolute',
                        bottom: 'calc(100% + 6px)',
                        right: 0,
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '10px',
                        boxShadow: '0 10px 25px -3px rgba(15, 23, 42, 0.2), 0 4px 10px -2px rgba(15, 23, 42, 0.1)',
                        padding: '10px',
                        zIndex: 99999,
                        minWidth: '220px',
                        maxWidth: '300px',
                        animation: 'pipelineModalFade 0.15s ease-out'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '6px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            Company Owners ({owners.length})
                        </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '160px', overflowY: 'auto' }}>
                        {owners.map((email, idx) => (
                            <div
                                key={idx}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '11.5px',
                                    color: '#0284c7',
                                    fontWeight: 600,
                                    padding: '4px 6px',
                                    borderRadius: '6px',
                                    background: '#f8fafc',
                                    wordBreak: 'break-all'
                                }}
                            >
                                <Mail size={11} style={{ flexShrink: 0 }} />
                                <span>{email}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// Floating Portal Status Dropdown for Table Rows
function TableStatusPill({ status, onSelectStatus }) {
    const isPaid = (status || '').toLowerCase() === 'paid';
    const [isOpen, setIsOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0, transform: 'none' });
    const btnRef = useRef(null);

    const updateCoords = () => {
        if (btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - rect.bottom;
            const placeTop = spaceBelow < 180 && rect.top > 180;
            setCoords({
                top: placeTop ? Math.max(10, rect.top - 4) : rect.bottom + 4,
                left: rect.left,
                transform: placeTop ? 'translateY(-100%)' : 'none'
            });
        }
    };

    useEffect(() => {
        if (isOpen) {
            updateCoords();
            const handleClick = (e) => {
                if (btnRef.current && !btnRef.current.contains(e.target)) {
                    setIsOpen(false);
                }
            };
            window.addEventListener('click', handleClick);
            window.addEventListener('scroll', updateCoords, true);
            window.addEventListener('resize', updateCoords);
            return () => {
                window.removeEventListener('click', handleClick);
                window.removeEventListener('scroll', updateCoords, true);
                window.removeEventListener('resize', updateCoords);
            };
        }
    }, [isOpen]);

    if (isPaid) {
        return <span className="pipeline-milestone-status-badge paid">Paid</span>;
    }

    const curStatus = status || 'Pending';

    const menu = isOpen && (
        <div
            className="pipeline-status-pill-menu"
            style={{
                position: 'fixed',
                top: `${coords.top}px`,
                left: `${coords.left}px`,
                transform: coords.transform,
                zIndex: 999999
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {['Paid', 'Pending', 'Partial', 'Overdue', 'Cancelled'].map(opt => (
                <div
                    key={opt}
                    className={`pipeline-status-pill-option ${opt.toLowerCase()} ${curStatus.toLowerCase() === opt.toLowerCase() ? 'selected' : ''}`}
                    onClick={() => {
                        onSelectStatus(opt);
                        setIsOpen(false);
                    }}
                >
                    <span className={`pipeline-status-dot ${opt.toLowerCase()}`} />
                    <span>{opt}</span>
                    {curStatus.toLowerCase() === opt.toLowerCase() && (
                        <CheckCircle size={12} style={{ marginLeft: 'auto', color: '#0284c7' }} />
                    )}
                </div>
            ))}
        </div>
    );

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                className={`pipeline-milestone-status-badge ${curStatus.toLowerCase()} interactive`}
                onClick={(e) => {
                    e.stopPropagation();
                    updateCoords();
                    setIsOpen(prev => !prev);
                }}
            >
                <span>{curStatus}</span>
                <ChevronDown size={11} className="pipeline-status-chevron" />
            </button>
            {isOpen && createPortal(menu, document.body)}
        </>
    );
}

// Cutoff timestamp: 17 OCT 2026 00:00:00 IST
const DATA_CUTOFF_DATE = new Date('2026-10-17T00:00:00+05:30');

// Safe item date extractor
const getItemCreationDate = (item) => {
    if (!item) return new Date(0);
    const raw = item.createdAt || item.createdTime || item.date || item.addedOn || item.createdDate || item.dueDate;
    if (!raw) {
        if (item._id && (item._id.toString().startsWith('task-') || item.title)) {
            return new Date();
        }
        return new Date(0);
    }
    let d = new Date(raw);
    if (isNaN(d.getTime()) && typeof raw === 'string') {
        const parts = raw.split(/[-/]/);
        if (parts.length === 3 && parts[2].length === 4) {
            d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        }
    }
    return isNaN(d.getTime()) ? (item.title ? new Date() : new Date(0)) : d;
};

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// MemberDropdown — fully custom styled dropdown for Assign / Link Member
// Uses position:fixed portal so it is never clipped by overflow:hidden parents.
// ---------------------------------------------------------------------------
function MemberDropdown({ options, value, onChange, placeholder = '-- No Member Linked --' }) {
    const [open, setOpen] = useState(false);
    const [dropStyle, setDropStyle] = useState({});
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    const selectedOpt = options.find(o => String(o.id) === String(value)) || null;

    const computePosition = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const PANEL_MAX_H = 280;
        const MARGIN = 6;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        // Flip upward if not enough space below but enough above
        const flipUp = spaceBelow < PANEL_MAX_H + MARGIN && spaceAbove > PANEL_MAX_H + MARGIN;

        setDropStyle({
            position: 'fixed',
            left: rect.left,
            width: rect.width,
            minWidth: Math.max(rect.width, 220),
            zIndex: 999999,
            ...(flipUp
                ? { bottom: window.innerHeight - rect.top + MARGIN }
                : { top: rect.bottom + MARGIN }),
        });
    };

    const openDropdown = () => {
        computePosition();
        setOpen(true);
    };

    // Reposition on scroll/resize while open
    useEffect(() => {
        if (!open) return;
        const reposition = () => computePosition();
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [open]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target) &&
                panelRef.current && !panelRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const panel = open ? createPortal(
        <div
            ref={panelRef}
            style={{
                ...dropStyle,
                background: '#fff',
                border: '1.5px solid #e2e8f0',
                borderRadius: '10px',
                boxShadow: '0 12px 40px -6px rgba(15,23,42,0.20), 0 2px 8px -2px rgba(15,23,42,0.08)',
                overflow: 'hidden',
                maxHeight: '280px',
                overflowY: 'auto',
                animation: 'memberDropAnim 0.14s ease',
            }}
        >
            <style>{`
        @keyframes memberDropAnim {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .mdd-item { transition: background 0.1s; }
        .mdd-item:hover { background: #f1f5f9 !important; }
        .mdd-item.selected:hover { background: #dbeafe !important; }
      `}</style>

            {/* No member / clear option */}
            <div
                className="mdd-item"
                onMouseDown={e => { e.preventDefault(); onChange('', null); setOpen(false); }}
                style={{
                    padding: '10px 14px',
                    fontSize: '13px',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f1f5f9',
                    background: !value ? '#f8fafc' : '#fff',
                }}
            >
                {placeholder}
            </div>

            {/* Member rows */}
            {options.map(opt => {
                const isSelected = String(opt.id) === String(value);
                return (
                    <div
                        key={opt.id}
                        className={`mdd-item${isSelected ? ' selected' : ''}`}
                        onMouseDown={e => { e.preventDefault(); onChange(opt.id, opt); setOpen(false); }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '9px 14px',
                            cursor: 'pointer',
                            background: isSelected ? '#eff6ff' : '#fff',
                            borderBottom: '1px solid #f8fafc',
                        }}
                    >
                        {/* Star or spacer */}
                        {opt.isDealOwner
                            ? <span style={{ color: '#f59e0b', fontSize: '15px', lineHeight: 1, flexShrink: 0 }}>★</span>
                            : <span style={{ width: '15px', flexShrink: 0 }} />
                        }
                        {/* Name */}
                        <span style={{
                            flex: 1,
                            fontSize: '13.5px',
                            color: '#1e293b',
                            fontWeight: opt.isDealOwner ? 600 : 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                            {opt.name}
                        </span>
                        {/* Role badge */}
                        {(opt.isDealOwner || opt.role) && (
                            <span style={{
                                fontSize: '11px',
                                color: opt.isDealOwner ? '#1d4ed8' : '#64748b',
                                background: opt.isDealOwner ? '#dbeafe' : '#f1f5f9',
                                padding: '2px 8px',
                                borderRadius: '5px',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                fontWeight: 500,
                            }}>
                                {opt.isDealOwner ? 'Deal Owner' : opt.role}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>,
        document.body
    ) : null;

    return (
        <div style={{ position: 'relative' }}>
            {/* Trigger */}
            <div
                ref={triggerRef}
                onClick={() => open ? setOpen(false) : openDropdown()}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    height: '34px',
                    padding: '0 28px 0 10px',
                    border: `1.5px solid ${open ? '#3b82f6' : '#cbd5e1'}`,
                    borderRadius: '7px',
                    background: open ? '#fafcff' : '#fff',
                    fontSize: '13px',
                    color: '#1e293b',
                    fontWeight: selectedOpt?.isDealOwner ? 600 : 400,
                    cursor: 'pointer',
                    userSelect: 'none',
                    boxSizing: 'border-box',
                    width: '100%',
                    outline: open ? '3px solid rgba(59,130,246,0.15)' : 'none',
                    transition: 'border-color 0.15s, outline 0.15s, background 0.15s',
                    position: 'relative',
                }}
            >
                {selectedOpt ? (
                    <>
                        {selectedOpt.isDealOwner && (
                            <span style={{ color: '#f59e0b', fontSize: '14px', lineHeight: 1, flexShrink: 0 }}>★</span>
                        )}
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {selectedOpt.name}
                        </span>
                        {selectedOpt.isDealOwner ? (
                            <span style={{ fontSize: '11px', color: '#1d4ed8', background: '#dbeafe', padding: '1px 6px', borderRadius: '4px', whiteSpace: 'nowrap', flexShrink: 0 }}>Deal Owner</span>
                        ) : selectedOpt.role ? (
                            <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px', whiteSpace: 'nowrap', flexShrink: 0 }}>{selectedOpt.role}</span>
                        ) : null}
                    </>
                ) : (
                    <span style={{ color: '#94a3b8' }}>{placeholder}</span>
                )}
                <span style={{
                    position: 'absolute', right: '9px', top: '50%',
                    transform: `translateY(-50%) rotate(${open ? '180deg' : '0deg'})`,
                    color: open ? '#3b82f6' : '#94a3b8',
                    pointerEvents: 'none', fontSize: '9px',
                    transition: 'transform 0.15s, color 0.15s',
                }}>▼</span>
            </div>
            {panel}
        </div>
    );
}


function Pipeline() {
    const { user, hasPermission, setActiveToast } = useAuth();

    const roleName = useMemo(() => {
        return (user?.role?.name || user?.role || '').toString().toLowerCase().trim();
    }, [user]);

    const roleNormalized = useMemo(() => {
        return roleName.replace(/\s+/g, ' ');
    }, [roleName]);

    const isSalesHead = useMemo(() => {
        return (
            roleNormalized.includes('sales head') ||
            roleNormalized === 'saleshead' ||
            roleNormalized === 'head of sales'
        );
    }, [roleNormalized]);

    const isSalesManager = useMemo(() => {
        return (
            roleNormalized.includes('sales manager') ||
            roleNormalized.includes('salesmanager') ||
            roleNormalized === 'manager of sales'
        );
    }, [roleNormalized]);

    const isAdmin = useMemo(() => {
        const des = (user?.designation || '').toString().toLowerCase().trim();
        const dept = (user?.department || '').toString().toLowerCase().trim();
        const em = (user?.email || '').toString().toLowerCase().trim();
        return (
            roleNormalized === 'admin' ||
            roleNormalized.includes('admin') ||
            roleNormalized.includes('superadmin') ||
            roleNormalized.includes('director') ||
            des.includes('admin') ||
            des.includes('director') ||
            dept === 'administration' ||
            em.startsWith('admin@') ||
            user?.role?.name === 'Admin' ||
            user?.role?.name === 'Super Admin' ||
            user?.role?.name === 'Director'
        );
    }, [roleNormalized, user]);

    const hasFullAccess = isAdmin || isSalesHead || isSalesManager;

    // Module actions permissions
    const canRead = useMemo(() => {
        return hasFullAccess || hasPermission('pipeline', 'read') || hasPermission('deals', 'read') || hasPermission('contacts', 'read') || hasPermission('companies', 'read') || hasPermission('tasks', 'read');
    }, [hasFullAccess, hasPermission]);

    const canCreate = useMemo(() => {
        return isAdmin || hasFullAccess || hasPermission('pipeline', 'create') || hasPermission('deals', 'create') || hasPermission('contacts', 'create') || hasPermission('companies', 'create') || hasPermission('tasks', 'create');
    }, [isAdmin, hasFullAccess, hasPermission]);

    const canUpdate = useMemo(() => {
        return isAdmin || hasFullAccess || hasPermission('pipeline', 'update') || hasPermission('deals', 'update') || hasPermission('contacts', 'update') || hasPermission('companies', 'update') || hasPermission('tasks', 'update');
    }, [isAdmin, hasFullAccess, hasPermission]);

    const canDelete = useMemo(() => {
        return isAdmin || hasFullAccess || hasPermission('pipeline', 'delete') || hasPermission('deals', 'delete') || hasPermission('contacts', 'delete') || hasPermission('companies', 'delete') || hasPermission('tasks', 'delete');
    }, [isAdmin, hasFullAccess, hasPermission]);

    // Admin Data View Mode ('new' | 'old' | 'all') - defaults to 'new' (hiding old historical pipeline data)
    const [adminDataViewMode, setAdminDataViewMode] = useState(() => {
        try {
            const saved = localStorage.getItem('admin_crm_data_view_mode');
            if (saved && ['all', 'new', 'old'].includes(saved.toLowerCase())) {
                return saved.toLowerCase();
            }
        } catch (e) { }
        return 'new';
    });

    const handleSetAdminDataViewMode = useCallback((mode) => {
        const clean = (mode || 'new').toLowerCase();
        setAdminDataViewMode(clean);
        try {
            localStorage.setItem('admin_crm_data_view_mode', clean);
        } catch (e) { }
    }, []);

    const [deals, setDeals] = useState([]);
    const [contacts, setContacts] = useState([]);

    // Ownership verification for data isolation
    const isItemOwnedByUser = useCallback((item, type = 'deal') => {
        if (!item) return false;

        const itemDate = getItemCreationDate(item);

        // Admin Data View Mode controls what data the admin views:
        // 'new' => only data from 17 OCT 2026 onwards (itemDate >= DATA_CUTOFF_DATE)
        // 'old' => only data before 17 OCT 2026 (itemDate < DATA_CUTOFF_DATE)
        // 'all' => all data (both old and new)
        if (isAdmin) {
            if (adminDataViewMode === 'new') {
                return itemDate >= DATA_CUTOFF_DATE;
            }
            if (adminDataViewMode === 'old') {
                return itemDate < DATA_CUTOFF_DATE;
            }
            return true; // 'all' mode
        }

        if (!user) return false;

        // For non-admin roles (Sales Head, Sales Manager, Executives), old pipeline items before 17 OCT 2026 are strictly hidden
        if (itemDate < DATA_CUTOFF_DATE) return false;

        if (hasFullAccess) return true;

        const userId = (user._id || user.id || '').toString();
        const userFullName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
        const userEmail = (user.email || '').trim().toLowerCase();
        const userOfficialEmail = (user.officialEmail || '').trim().toLowerCase();

        const assignedId = (item.assignedTo?._id || item.assignedTo || '').toString();
        const createdId = (item.createdBy?._id || item.createdBy || '').toString();

        if (userId && (assignedId === userId || createdId === userId)) return true;

        if (type === 'deal') {
            const salesOwner = (item.salesOwner || '').trim().toLowerCase();
            const addedBy = (item.addedBy || '').trim().toLowerCase();
            const email = (item.email || '').trim().toLowerCase();
            if (userFullName && (salesOwner === userFullName || addedBy === userFullName)) return true;
            if (userEmail && (salesOwner === userEmail || addedBy === userEmail || email === userEmail)) return true;
            if (userOfficialEmail && (salesOwner === userOfficialEmail || email === userOfficialEmail)) return true;
        } else if (type === 'contact') {
            const owner = (item.owner || '').trim().toLowerCase();
            const addedBy = (item.addedBy || '').trim().toLowerCase();
            const email = (item.email || '').trim().toLowerCase();
            if (userFullName && (owner === userFullName || addedBy === userFullName)) return true;
            if (userEmail && (owner === userEmail || addedBy === userEmail || email === userEmail)) return true;
            if (userOfficialEmail && (owner === userOfficialEmail || email === userOfficialEmail)) return true;
        } else if (type === 'company') {
            const owner = (item.owner || '').trim().toLowerCase();
            if (userFullName && owner === userFullName) return true;
            if (userEmail && owner === userEmail) return true;
        } else if (type === 'task') {
            const assignee = (item.assignee || '').trim().toLowerCase();
            if (userFullName && (assignee === userFullName || assignee === 'admin system')) return true;
            if (userEmail && assignee === userEmail) return true;
            if (userOfficialEmail && assignee === userOfficialEmail) return true;
            if (userId && (assignedId === userId || createdId === userId)) return true;

            // Universal Tasks: check if task is linked to a deal owned by or assigned to user
            if (item.associatedDealId) {
                const deal = deals.find(d => String(d._id) === String(item.associatedDealId));
                if (deal && isItemOwnedByUser(deal, 'deal')) return true;
            }
            // Check if task is linked to a contact owned by or assigned to user
            if (item.associatedContactId) {
                const contact = contacts.find(c => String(c._id) === String(item.associatedContactId));
                if (contact && isItemOwnedByUser(contact, 'contact')) return true;
            }

            if (hasFullAccess) return true;
        }

        return false;
    }, [user, hasFullAccess, isAdmin, adminDataViewMode, deals, contacts]);

    const RETIRED_LEGACY_TEMPLATES = useMemo(() => [
        'sales pipeline',
        'enterprise pipeline',
        'lead conversion pipeline',
        'renewal & retention pipeline',
        'partnership pipeline',
        'marketing qualified (mql)',
        'marketing qualified',
        'sales qualified (sql)',
        'sales qualified',
        'opportunity stage'
    ], []);

    // Stage cutoff filter:
    // Non-admins (Sales Head, Sales Manager, Sales Executives / Sales Role) strictly only see stages created on or after 17 Oct 2026.
    // Admins see all stages (or filtered by adminDataViewMode if 'new' or 'old').
    const isStageCreatedOnOrAfterCutoff = useCallback((item, type = 'deal') => {
        if (!item) return false;

        const name = (typeof item === 'string' ? item : (item?.name || item?.id || '')).toLowerCase().trim();
        if (!name) return false;

        // 1. Strictly hide retired legacy template pipelines
        if (RETIRED_LEGACY_TEMPLATES.includes(name)) {
            return false;
        }

        // 2. Core active stages in the post-cutoff system must always be visible
        if (name === 'lost' || name === 'won' || name === 'new lead' || name === 'initial') {
            return true;
        }

        // Explicit createdAt timestamp check
        if (item?.createdAt) {
            const itemCreatedAt = new Date(item.createdAt);
            if (isAdmin) {
                if (adminDataViewMode === 'old') return itemCreatedAt < DATA_CUTOFF_DATE;
                if (adminDataViewMode === 'new') return itemCreatedAt >= DATA_CUTOFF_DATE;
                return true; // 'all' mode
            }
            // Non-admins see everything created from 17 Oct 2026
            return itemCreatedAt >= DATA_CUTOFF_DATE;
        }

        if (isAdmin) {
            if (adminDataViewMode === 'old') {
                if (type === 'deal') {
                    return deals.some(d => getItemCreationDate(d) < DATA_CUTOFF_DATE && ((d.pipeline || '').toLowerCase().trim() === name || (d.stage || '').toLowerCase().trim() === name));
                } else {
                    return contacts.some(c => getItemCreationDate(c) < DATA_CUTOFF_DATE && (c.leadStage || '').toLowerCase().trim() === name);
                }
            }
            if (adminDataViewMode === 'new') {
                return true;
            }
            return true; // 'all' mode: admin sees all
        }

        // For non-admin roles (Sales Head, Sales Manager, Sales Role / Executives):
        // All other stages configured or created from 17 Oct 2026 onwards MUST be shown to ALL sales roles,
        // does not matter if they currently have deals in those stages or not!
        return true;
    }, [isAdmin, adminDataViewMode, RETIRED_LEGACY_TEMPLATES, deals, contacts, getItemCreationDate]);

    const [isLoading, setIsLoading] = useState(true);
    const [pipelines, setPipelines] = useState(() => {
        try {
            const deleted = JSON.parse(localStorage.getItem('crm_deleted_pipelines') || '[]');
            const saved = localStorage.getItem('crm_pipelines');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    let filtered = parsed
                        .filter(p => !deleted.includes((p.name || p.id || '').toLowerCase()))
                        .map((p, idx) => ({
                            ...p,
                            id: p.id || p.name || `pipeline-${idx}`,
                            name: p.name || p.id || `Stage ${idx + 1}`
                        }));
                    const seen = new Set();
                    filtered = filtered.filter(p => {
                        const key = (p.name || p.id || '').toLowerCase().trim();
                        if (!key || seen.has(key)) return false;
                        seen.add(key);
                        return true;
                    });
                    if (!filtered.some(p => (p.name || p.id || '').toLowerCase() === 'lost') && !deleted.includes('lost')) {
                        filtered = [{ id: 'Lost', name: 'Lost', badgeClass: 'lost', createdAt: '2026-10-17T00:00:00+05:30' }, ...filtered];
                    }
                    if (!filtered.some(p => (p.name || p.id || '').toLowerCase() === 'new lead') && !deleted.includes('new lead')) {
                        filtered = [{ id: 'New Lead', name: 'New Lead', badgeClass: 'new', createdAt: '2026-10-17T00:00:00+05:30' }, ...filtered];
                    }

                    if (!filtered.some(p => (p.name || p.id || '').toLowerCase() === 'initial') && !deleted.includes('initial')) {
                        filtered = [...filtered, { id: 'Initial', name: 'Initial', badgeClass: 'new', createdAt: '2026-10-17T00:00:00+05:30' }];
                    }
                    return filtered;
                }
            }
            return DEFAULT_PIPELINES.filter(p => !deleted.includes((p.name || p.id || '').toLowerCase()));
        } catch (e) { }
        return DEFAULT_PIPELINES;
    });
    const [stages, setStages] = useState(() => {
        try {
            const deleted = JSON.parse(localStorage.getItem('crm_deleted_pipelines') || localStorage.getItem('crm_deleted_stages') || '[]');
            const saved = localStorage.getItem('crm_stages');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    let list = parsed.filter(s => !deleted.includes((s.name || s.id || '').toLowerCase()));
                    if (!list.some(s => (s.name || s.id || '').toLowerCase() === 'lost') && !deleted.includes('lost')) {
                        list = [{ id: 'Lost', name: 'Lost', badgeClass: 'lost', createdAt: '2026-10-17T00:00:00+05:30' }, ...list];
                    }
                    if (!list.some(s => (s.name || s.id || '').toLowerCase() === 'new lead') && !deleted.includes('new lead')) {
                        list = [{ id: 'New Lead', name: 'New Lead', badgeClass: 'new', createdAt: '2026-10-17T00:00:00+05:30' }, ...list];
                    }
                    if (!list.some(s => (s.name || s.id || '').toLowerCase() === 'initial') && !deleted.includes('initial')) {
                        list = [...list, { id: 'Initial', name: 'Initial', badgeClass: 'new', createdAt: '2026-10-17T00:00:00+05:30' }];
                    }
                    return list;
                }
            }
        } catch (e) { }
        return DEFAULT_STAGES;
    });
    const [dealTypes, setDealTypes] = useState(DEAL_TYPE_OPTIONS);
    const [pipelineOptions, setPipelineOptions] = useState(() => {
        try {
            const deleted = JSON.parse(localStorage.getItem('crm_deleted_pipelines') || '[]');
            const saved = localStorage.getItem('crm_pipeline_options');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    let list = parsed.filter(p => !deleted.includes(String(p).toLowerCase()));
                    if (!list.some(p => p.toLowerCase() === 'lost') && !deleted.includes('lost')) {
                        list = ['Lost', ...list];
                    }
                    if (!list.some(p => p.toLowerCase() === 'new lead') && !deleted.includes('new lead')) {
                        list = ['New Lead', ...list];
                    }

                    if (!list.some(p => p.toLowerCase() === 'initial') && !deleted.includes('initial')) {
                        list = [...list, 'Initial'];
                    }
                    return list;
                }
            }
            return PIPELINE_OPTIONS.filter(p => !deleted.includes(String(p).toLowerCase()));
        } catch (e) { }
        return PIPELINE_OPTIONS;
    });
    const [customOwners, setCustomOwners] = useState([]);
    const [employees, setEmployees] = useState([]);

    // Dynamic Deal & Contact Owner list (Only active employees with Sales role)
    const ownerOptions = useMemo(() => {
        const list = new Set();

        (employees || []).forEach(emp => {
            if (!emp) return;
            // Must be Active
            if (emp.isActive === false || emp.status === 'Inactive' || emp.status === 'inactive') {
                return;
            }

            const roleName = (typeof emp.role === 'string' ? emp.role : (emp.role?.name || '')).toLowerCase().trim();
            const department = (emp.department || '').toLowerCase().trim();
            const designation = (emp.designation || '').toLowerCase().trim();

            const isSales =
                roleName === 'sales' ||
                roleName.includes('sales') ||
                roleName.includes('business development') ||
                roleName.includes('bde') ||
                department === 'sales' ||
                department.includes('sales') ||
                designation.includes('sales') ||
                designation.includes('bde');

            if (isSales) {
                const name = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
                if (name) list.add(name);
            }
        });

        return Array.from(list);
    }, [employees]);
    const [showAddPipelineInput, setShowAddPipelineInput] = useState(false);
    const [newPipelineName, setNewPipelineName] = useState('');
    const [editingPipelineId, setEditingPipelineId] = useState(null);
    const [editingPipelineName, setEditingPipelineName] = useState('');
    const [editingStageModal, setEditingStageModal] = useState(null); // { type: 'pipeline' | 'contactStage', id, name, originalName, index }
    const [{ tab: activeTab, view: viewMode, detailTab: detailActiveTab, stage: selectedStageFilter, dealId: queryDealId, contactId: queryContactId }, setQueryState] = useContextualQueryState({
        tab: 'deals',
        view: 'kanban',
        detailTab: 'all',
        stage: 'all',
        dealId: '',
        contactId: '',
    });

    const setActiveTab = useCallback((tab) => {
        if (tab === 'contacts' && !isAdmin) {
            setQueryState({ tab: 'deals' });
            return;
        }
        setQueryState({ tab });
    }, [isAdmin, setQueryState]);
    const setViewMode = useCallback((view) => setQueryState({ view }), [setQueryState]);
    const setDetailActiveTab = useCallback((detailTab) => setQueryState({ detailTab }), [setQueryState]);
    const setSelectedStageFilter = useCallback((stage) => setQueryState({ stage }), [setQueryState]);

    // Restrict contacts tab to admin only; automatically redirect non-admin users to deals
    useEffect(() => {
        if (!isAdmin && activeTab === 'contacts') {
            setActiveTab('deals');
        }
    }, [isAdmin, activeTab, setActiveTab]);

    const visibleSidebarTabs = useMemo(() => {
        return SIDEBAR_TABS.filter(tab => {
            if (tab.id === 'contacts') {
                return Boolean(isAdmin);
            }
            return true;
        });
    }, [isAdmin]);

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [modalStage, setModalStage] = useState('New Lead');
    const [activeMenuId, setActiveMenuId] = useState(null);
    const [moveSubmenuDealId, setMoveSubmenuDealId] = useState(null);
    const [viewingDeal, setViewingDeal] = useState(null);
    const [editingDeal, setEditingDeal] = useState(null);
    const [hoveredActivityDealId, setHoveredActivityDealId] = useState(null);
    const [hoveredActivityContactId, setHoveredActivityContactId] = useState(null);
    const [popoverCoords, setPopoverCoords] = useState({ top: 0, left: 0, placement: 'right', arrowTop: 20 });
    const hoverTimeoutRef = React.useRef(null);
    const lastContactScrollTopRef = useRef(0);
    const lastDealScrollTopRef = useRef(0);
    const [draggedDealId, setDraggedDealId] = useState(null);
    const [dragOverColumnId, setDragOverColumnId] = useState(null);
    const [selectedKanbanDealIds, setSelectedKanbanDealIds] = useState([]);
    const [isBulkMoveDropdownOpen, setIsBulkMoveDropdownOpen] = useState(false);
    const cardClickTimerRef = useRef(null);

    // Clear kanban multi-selection when tab or view mode changes
    useEffect(() => {
        setSelectedKanbanDealIds([]);
        setIsBulkMoveDropdownOpen(false);
    }, [activeTab, viewMode]);

    // Live currency exchange rates to INR from official Frankfurter v2 API
    const [exchangeRatesToINR, setExchangeRatesToINR] = useState({ INR: 1 });

    // Auto-fetch latest live exchange rates from Frankfurter v2 API (no hardcoding, no API key required)
    useEffect(() => {
        if (!deals || deals.length === 0) return;

        const uniqueCurrencies = Array.from(
            new Set(
                deals
                    .map(d => normalizeCurrencyCode(d.currency))
                    .filter(code => code && code !== 'INR')
            )
        );

        const neededCurrencies = uniqueCurrencies.filter(code => exchangeRatesToINR[code] === undefined);
        if (neededCurrencies.length === 0) return;

        let isMounted = true;

        neededCurrencies.forEach(async (baseCode) => {
            try {
                const response = await fetch(`https://api.frankfurter.dev/v2/rate/${encodeURIComponent(baseCode)}/INR`);
                if (response.ok) {
                    const data = await response.json();
                    if (data && typeof data.rate === 'number' && isMounted) {
                        setExchangeRatesToINR(prev => ({
                            ...prev,
                            [baseCode]: data.rate
                        }));
                    }
                }
            } catch (err) {
                console.warn(`Frankfurter v2 exchange rate fetch failed for ${baseCode}/INR:`, err.message);
            }
        });

        return () => {
            isMounted = false;
        };
    }, [deals, exchangeRatesToINR]);

    // Convert deal value from its native currency to INR using latest Frankfurter rates
    const getDealValueInINR = useCallback((deal) => {
        if (!deal) return 0;
        const rawVal = parseNumericDealValue(deal.dealValue);
        const baseCode = normalizeCurrencyCode(deal.currency);
        if (baseCode === 'INR') return rawVal;
        const rate = exchangeRatesToINR[baseCode];
        if (typeof rate === 'number' && rate > 0) {
            return rawVal * rate;
        }
        return rawVal;
    }, [exchangeRatesToINR]);

    const hoveredDeal = useMemo(() => {
        return deals.find(d => d._id === hoveredActivityDealId) || null;
    }, [deals, hoveredActivityDealId]);

    // Detailed Deal 2-Section Page State
    const [detailedDealId, setDetailedDealId] = useState(() => queryDealId || null);

    useEffect(() => {
        if (queryDealId && queryDealId !== detailedDealId) {
            setDetailedDealId(queryDealId);
        }
    }, [queryDealId]);

    useEffect(() => {
        if (queryContactId && queryContactId !== detailedContactId) {
            setDetailedContactId(queryContactId);
        }
    }, [queryContactId]);
    const [dealActivities, setDealActivities] = useState({});

    const [newActivityType, setNewActivityType] = useState('note'); // 'note' | 'email' | 'message' | 'call'
    const [newActivityContent, setNewActivityContent] = useState('');
    const [newActivitySubject, setNewActivitySubject] = useState('');
    const [isComposingActivity, setIsComposingActivity] = useState(false);

    // Dedicated Follow-up Tab State
    const [isComposingFollowUp, setIsComposingFollowUp] = useState(false);
    const [followUpFormData, setFollowUpFormData] = useState({
        title: '',
        memberId: '',
        memberName: '',
        channel: 'Call', // 'Call' | 'Meeting' | 'WhatsApp' | 'Email' | 'Demo' | 'Visit'
        status: 'Scheduled', // 'Scheduled' | 'Completed' | 'Follow-up Needed' | 'Rescheduled' | 'Missed'
        followUpDate: new Date().toISOString().slice(0, 10),
        followUpTime: '11:00',
        nextFollowUpDate: '',
        remarks: ''
    });
    const [isSubmittingFollowUp, setIsSubmittingFollowUp] = useState(false);
    const [editingFollowUp, setEditingFollowUp] = useState(null);
    const [editFollowUpForm, setEditFollowUpForm] = useState({
        title: '',
        memberId: '',
        memberName: '',
        channel: 'Call',
        status: 'Scheduled',
        followUpDate: '',
        followUpTime: '11:00',
        nextFollowUpDate: '',
        remarks: ''
    });
    const [isSavingEditFollowUp, setIsSavingEditFollowUp] = useState(false);
    const [deletingFollowUpId, setDeletingFollowUpId] = useState(null);

    const detailedDeal = useMemo(() => {
        if (!detailedDealId) return null;
        return deals.find(d => d._id === detailedDealId) || null;
    }, [deals, detailedDealId]);

    const [showDealRequirementModal, setShowDealRequirementModal] = useState(false);
    const [isEditingRequirement, setIsEditingRequirement] = useState(false);
    const [editingRequirementText, setEditingRequirementText] = useState('');
    const [isSavingRequirement, setIsSavingRequirement] = useState(false);


    const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
    const currencyRef = useRef(null);
    const [dealSearchQuery, setDealSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'value_high', 'value_low', 'name_asc', 'name_desc', 'date_asc', 'date_desc'
    const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
    const [ownerFilter, setOwnerFilter] = useState('all');
    const [isOwnerDropdownOpen, setIsOwnerDropdownOpen] = useState(false);
    const [ownerSearchQuery, setOwnerSearchQuery] = useState('');
    const [dealStatusFilter, setDealStatusFilter] = useState('all');
    const [isDealStatusDropdownOpen, setIsDealStatusDropdownOpen] = useState(false);
    const [dealOriginFilter, setDealOriginFilter] = useState('all'); // 'all', 'Domestic', 'International'
    const [isDealOriginDropdownOpen, setIsDealOriginDropdownOpen] = useState(false);
    const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'this_week', 'this_month', 'next_30_days', 'this_quarter', 'this_year', 'custom'
    const [customDateStart, setCustomDateStart] = useState('');
    const [customDateEnd, setCustomDateEnd] = useState('');
    const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);

    // Toolbar & Column 3-dots Menu States
    const [isDealsToolbarMenuOpen, setIsDealsToolbarMenuOpen] = useState(false);
    const [isContactsToolbarMenuOpen, setIsContactsToolbarMenuOpen] = useState(false);
    const [isCompaniesToolbarMenuOpen, setIsCompaniesToolbarMenuOpen] = useState(false);
    const [isTasksToolbarMenuOpen, setIsTasksToolbarMenuOpen] = useState(false);
    const [activeColMenuId, setActiveColMenuId] = useState(null);
    const [colMoveSubmenuStageId, setColMoveSubmenuStageId] = useState(null);
    const [colMoveDealSubmenuStageId, setColMoveDealSubmenuStageId] = useState(null);
    const [isTopBulkMoveDropdownOpen, setIsTopBulkMoveDropdownOpen] = useState(false);
    const [isTopDealBulkMoveDropdownOpen, setIsTopDealBulkMoveDropdownOpen] = useState(false);
    const [isDealDetailsMenuOpen, setIsDealDetailsMenuOpen] = useState(false);
    const [isContactDetailsMenuOpen, setIsContactDetailsMenuOpen] = useState(false);
    const [isAssocContactMenuOpen, setIsAssocContactMenuOpen] = useState(false);
    const [isPipelineDropdownOpen, setIsPipelineDropdownOpen] = useState(false);
    const [isStageDropdownOpen, setIsStageDropdownOpen] = useState(false);
    const [isContactLeadStageDropdownOpen, setIsContactLeadStageDropdownOpen] = useState(false);
    const [isContactLeadStatusDropdownOpen, setIsContactLeadStatusDropdownOpen] = useState(false);
    const [isDealerPopoverOpen, setIsDealerPopoverOpen] = useState(false);
    const [isDealerDropdownOpen, setIsDealerDropdownOpen] = useState(false);
    const [dealerSearchQuery, setDealerSearchQuery] = useState('');
    const [dealerPopoverCoords, setDealerPopoverCoords] = useState({ top: 0, left: 0 });
    const dealerPopoverTimeoutRef = useRef(null);
    const [isContactAddedByPopoverOpen, setIsContactAddedByPopoverOpen] = useState(false);
    const [contactAddedByPopoverCoords, setContactAddedByPopoverCoords] = useState({ top: 0, left: 0 });
    const contactAddedByPopoverTimeoutRef = useRef(null);
    const [isDealAddedByPopoverOpen, setIsDealAddedByPopoverOpen] = useState(false);
    const [dealAddedByPopoverCoords, setDealAddedByPopoverCoords] = useState({ top: 0, left: 0 });
    const dealAddedByPopoverTimeoutRef = useRef(null);
    const [isMilestonePopoverOpen, setIsMilestonePopoverOpen] = useState(false);
    const [contactAccordionState, setContactAccordionState] = useState({ contact: true, project: false, payment: false, deals: true, company: true });
    const [collapsedDeals, setCollapsedDeals] = useState({});
    const [collapsedCompanies, setCollapsedCompanies] = useState({});
    const [dealAccordionState, setDealAccordionState] = useState({ deal: true, project: false, payment: false });

    // Always expand Deal Details by default whenever a deal is opened
    useEffect(() => {
        if (detailedDealId) {
            setDealAccordionState({ deal: true, project: false, payment: false });
            lastDealScrollTopRef.current = 0;
        }
    }, [detailedDealId]);

    // Payment Records Modal State
    const [showMilestoneModal, setShowMilestoneModal] = useState(false);
    const [milestoneModalDealId, setMilestoneModalDealId] = useState(null);
    const [previewAttachment, setPreviewAttachment] = useState(null);
    const [paymentRecords, setPaymentRecords] = useState(() => {
        try {
            const saved = localStorage.getItem('crm_payment_records');
            if (saved) return JSON.parse(saved);
        } catch (e) { }
        return {};
    });

    useEffect(() => {
        try {
            localStorage.setItem('crm_payment_records', JSON.stringify(paymentRecords));
        } catch (e) { }
    }, [paymentRecords]);
    const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);
    const [newPaymentData, setNewPaymentData] = useState({
        clientName: '', projectName: '', paidAmount: '',
        paymentMode: 'Bank Transfer', paymentStatus: 'Paid',
        paymentDate: '', attachmentName: ''
    });
    const DEFAULT_PAYMENT_MODES = ['Paypal', 'Bank Transfer', 'Cash', 'UPI', 'QR Code'];
    const [customPaymentModes, setCustomPaymentModes] = useState([]);
    const [newCustomMode, setNewCustomMode] = useState('');
    const [showAddModeInput, setShowAddModeInput] = useState(false);
    const [attachmentFile, setAttachmentFile] = useState(null);
    const [milestoneStatuses, setMilestoneStatuses] = useState({});
    const [activeStatusDropdownId, setActiveStatusDropdownId] = useState(null);

    // Close milestone status pill dropdown on click outside
    useEffect(() => {
        if (!activeStatusDropdownId) return;
        const handleStatusOutsideClick = () => setActiveStatusDropdownId(null);
        document.addEventListener('click', handleStatusOutsideClick);
        return () => document.removeEventListener('click', handleStatusOutsideClick);
    }, [activeStatusDropdownId]);

    // Add Pipeline & Stage Modal States
    const [showAddPipelineModal, setShowAddPipelineModal] = useState(false);
    const [addPipelineModalName, setAddPipelineModalName] = useState('');
    const [showAddStageModal, setShowAddStageModal] = useState(false);
    const [addStageModalName, setAddStageModalName] = useState('');
    const [showReorderStagesModal, setShowReorderStagesModal] = useState(false);
    const [reorderStagesList, setReorderStagesList] = useState([]);
    const [savingStageOrder, setSavingStageOrder] = useState(false);
    const [draggedStageColIndex, setDraggedStageColIndex] = useState(null);
    const [dragOverStageColIndex, setDragOverStageColIndex] = useState(null);

    // Contacts Management State
    const [contactStages, setContactStages] = useState(() => {
        if (!isAdmin) return [];
        const legacyDummyNames = ['marketing qualified (mql)', 'marketing qualified', 'sales qualified (sql)', 'sales qualified', 'opportunity', 'opportunity stage', 'customer', 'retention & customer'];
        try {
            const deleted = JSON.parse(localStorage.getItem('crm_deleted_stages') || '[]');
            const saved = localStorage.getItem('crm_contact_stages');
            if (saved !== null) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed)) {
                    return parsed.filter(s =>
                        !deleted.includes((s.name || s.id || '').toLowerCase()) &&
                        !legacyDummyNames.includes((s.name || s.id || '').toLowerCase())
                    );
                }
            }
            return DEFAULT_CONTACT_STAGES.filter(s => !deleted.includes((s.name || s.id || '').toLowerCase()));
        } catch (e) { }
        return DEFAULT_CONTACT_STAGES;
    });

    const effectiveContactStages = useMemo(() => {
        let deleted = [];
        try {
            deleted = JSON.parse(localStorage.getItem('crm_deleted_stages') || '[]')
                .filter(d => typeof d === 'string' && d.trim().length > 0)
                .map(d => d.toLowerCase().trim());
        } catch (e) { }

        let list = (contactStages || [])
            .filter(s => !deleted.includes((s.name || s.id || '').toLowerCase().trim()))
            .map((s, idx) => ({
                ...s,
                id: s.id || s.name || `contact-stage-${idx}`,
                name: s.name || s.id || `Stage ${idx + 1}`
            }));
        const existingNames = new Set(list.map(s => (s.name || s.id || '').toLowerCase().trim()));

        // Dynamically include any stage present on existing contacts ONLY if NOT deleted
        contacts.forEach(c => {
            const cDate = getItemCreationDate(c);
            if (!isAdmin && cDate < DATA_CUTOFF_DATE) return;
            const cStage = (c.leadStage || '').trim();
            if (cStage && !existingNames.has(cStage.toLowerCase()) && !deleted.includes(cStage.toLowerCase())) {
                existingNames.add(cStage.toLowerCase());
                list.push({
                    id: cStage,
                    name: cStage,
                    badgeClass: 'new',
                    isCustom: true
                });
            }
        });

        if (!isAdmin) {
            list = list.filter(s => isStageCreatedOnOrAfterCutoff(s, 'contact'));
            if (list.length === 0) {
                list = [{ id: 'New Lead', name: 'New Lead', badgeClass: 'new', createdAt: DATA_CUTOFF_DATE.toISOString() }];
            }
        } else if (adminDataViewMode === 'new') {
            list = list.filter(s => isStageCreatedOnOrAfterCutoff(s, 'contact'));
            if (list.length === 0) {
                list = [{ id: 'New Lead', name: 'New Lead', badgeClass: 'new', createdAt: DATA_CUTOFF_DATE.toISOString() }];
            }
        } else if (adminDataViewMode === 'old') {
            list = list.filter(s => isStageCreatedOnOrAfterCutoff(s, 'contact'));
        }

        return list;
    }, [contactStages, contacts, isAdmin, adminDataViewMode, isStageCreatedOnOrAfterCutoff]);
    const [draggedContactId, setDraggedContactId] = useState(null);
    const [dragOverContactColumnId, setDragOverContactColumnId] = useState(null);
    const [editingContactStageId, setEditingContactStageId] = useState(null);
    const [editingContactStageName, setEditingContactStageName] = useState('');
    const [showAddContactStageInput, setShowAddContactStageInput] = useState(false);
    const [newContactStageName, setNewContactStageName] = useState('');

    const [contactSources, setContactSources] = useState(() => {
        try {
            const saved = localStorage.getItem('crm_contact_sources');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed)) return parsed;
            }
        } catch (e) { }
        return CONTACT_SOURCE_OPTIONS;
    });
    const [leadStages, setLeadStages] = useState(() => {
        try {
            const deleted = JSON.parse(localStorage.getItem('crm_deleted_stages') || '[]');
            const saved = localStorage.getItem('crm_lead_stages');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed)) {
                    return parsed.filter(s => !deleted.includes(String(s).toLowerCase()));
                }
            }
        } catch (e) { }
        return LEAD_STAGE_OPTIONS;
    });
    const [leadStatuses, setLeadStatuses] = useState(() => {
        try {
            const saved = localStorage.getItem('crm_lead_statuses');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed)) return parsed;
            }
        } catch (e) { }
        return LEAD_STATUS_OPTIONS;
    });

    const [contactSearchQuery, setContactSearchQuery] = useState('');
    const [contactStageFilter, setContactStageFilter] = useState('all');
    const [contactStatusFilter, setContactStatusFilter] = useState('all');
    const [contactOriginFilter, setContactOriginFilter] = useState('all'); // 'all', 'Domestic', 'International'
    const [contactSourceFilter, setContactSourceFilter] = useState('all');
    const [contactOwnerFilter, setContactOwnerFilter] = useState('all');
    const [contactDateFilter, setContactDateFilter] = useState('all');
    const [customContactDateStart, setCustomContactDateStart] = useState('');
    const [customContactDateEnd, setCustomContactDateEnd] = useState('');
    const [contactSortBy, setContactSortBy] = useState('newest'); // 'newest', 'oldest', 'name_asc', 'name_desc', 'company', 'stage', 'status'
    const [contactViewMode, setContactViewMode] = useState('kanban'); // 'kanban' | 'table'
    const [selectedKanbanContactIds, setSelectedKanbanContactIds] = useState([]);
    const [isBulkMoveContactStageDropdownOpen, setIsBulkMoveContactStageDropdownOpen] = useState(false);
    const contactClickTimerRef = useRef(null);

    // Clear contacts multi-selection when tab or view mode changes
    useEffect(() => {
        setSelectedKanbanContactIds([]);
        setIsBulkMoveContactStageDropdownOpen(false);
    }, [activeTab, contactViewMode]);

    const [isContactSortDropdownOpen, setIsContactSortDropdownOpen] = useState(false);
    const [isContactOwnerDropdownOpen, setIsContactOwnerDropdownOpen] = useState(false);
    const [isContactStatusDropdownOpen, setIsContactStatusDropdownOpen] = useState(false);
    const [isContactOriginDropdownOpen, setIsContactOriginDropdownOpen] = useState(false);
    const [isContactDateDropdownOpen, setIsContactDateDropdownOpen] = useState(false);
    const [contactOwnerSearchQuery, setContactOwnerSearchQuery] = useState('');

    const [detailedContactId, setDetailedContactId] = useState(null);

    // Close all toolbar filter dropdowns on click outside
    useEffect(() => {
        const handleToolbarOutsideClick = () => {
            setIsSortDropdownOpen(false);
            setIsOwnerDropdownOpen(false);
            setIsDealStatusDropdownOpen(false);
            setIsDealOriginDropdownOpen(false);
            setIsDateDropdownOpen(false);
            setIsContactSortDropdownOpen(false);
            setIsContactOwnerDropdownOpen(false);
            setIsContactStatusDropdownOpen(false);
            setIsContactOriginDropdownOpen(false);
            setIsContactDateDropdownOpen(false);
            setIsCompanySortDropdownOpen(false);
            setIsCompanyIndustryDropdownOpen(false);
            setIsCompanyOwnerDropdownOpen(false);
            setIsCompanyDateDropdownOpen(false);
        };
        document.addEventListener('click', handleToolbarOutsideClick);
        return () => document.removeEventListener('click', handleToolbarOutsideClick);
    }, []);

    // Always expand Contact Details by default whenever a contact is opened
    useEffect(() => {
        if (detailedContactId) {
            setContactAccordionState({ contact: true, project: false, payment: false, deals: true, company: true });
            setCollapsedDeals({});
            setCollapsedCompanies({});
            lastContactScrollTopRef.current = 0;
        }
    }, [detailedContactId]);

    // Helper to safely parse any currency/numeric string
    const parsePaymentAmount = useCallback((val) => {
        if (typeof val === 'number') return isNaN(val) ? 0 : val;
        if (!val) return 0;
        const clean = val.toString().replace(/[^0-9.]/g, '');
        return Number(clean) || 0;
    }, []);

    // Helper to get all matching payment records for a Deal
    const getDealPaymentRecords = useCallback((deal, pRecords = paymentRecords) => {
        if (!deal) return [];
        const dId = deal._id ? String(deal._id) : '';
        const dName = (deal.dealName || deal.clientName || '').toLowerCase().trim();
        const dEmail = (deal.email || deal.clientEmail || '').toLowerCase().trim();

        // 1. Direct key matches
        const direct = (dId && pRecords[dId]) ? pRecords[dId] : [];

        // 2. Scan all records across all keys
        const allList = Object.values(pRecords || {}).flat().filter(Boolean);
        const matched = allList.filter(r => {
            if (!r) return false;
            if (dId && r.dealId && String(r.dealId) === dId) return true;
            if (dEmail && (r.clientEmail || r.clientName || '').toLowerCase().includes(dEmail)) return true;
            if (dName) {
                const recClient = (r.clientName || '').toLowerCase();
                const recProj = (r.projectName || '').toLowerCase();
                if (recClient.includes(dName) || dName.includes(recClient) || recProj.includes(dName)) return true;
            }
            return false;
        });

        // Deduplicate
        const seen = new Set();
        return [...direct, ...matched].filter(r => {
            const key = r._id || r.id || `${r.srNo}_${r.paidAmount}_${r.paymentDate}_${r.clientName}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [paymentRecords]);

    // Helper to get all matching payment records for a Contact
    const getContactPaymentRecords = useCallback((contact, allDeals = deals, pRecords = paymentRecords) => {
        if (!contact) return [];
        const cId = contact._id ? String(contact._id) : '';
        const cEmail = (contact.email || '').toLowerCase().trim();
        const cName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim().toLowerCase();
        const cCompany = (contact.company || '').toLowerCase().trim();

        const matchedDeal = allDeals.find(d =>
            (d.contactId && String(d.contactId) === cId) ||
            (d.clientName && contact.firstName && (
                d.clientName.toLowerCase().includes(contact.firstName.toLowerCase()) ||
                contact.firstName.toLowerCase().includes(d.clientName.toLowerCase())
            )) ||
            (d.clientName && contact.company && d.clientName.toLowerCase().includes(contact.company.toLowerCase()))
        );

        const direct = [
            ...(cId && pRecords[cId] ? pRecords[cId] : []),
            ...(cId && pRecords[`contact_${cId}`] ? pRecords[`contact_${cId}`] : []),
            ...(matchedDeal && matchedDeal._id && pRecords[matchedDeal._id] ? pRecords[matchedDeal._id] : [])
        ];

        const allList = Object.values(pRecords || {}).flat().filter(Boolean);
        const matched = allList.filter(r => {
            if (!r) return false;
            if (cId && r.contactId && String(r.contactId) === cId) return true;
            if (matchedDeal && matchedDeal._id && r.dealId && String(r.dealId) === String(matchedDeal._id)) return true;
            if (cEmail && (r.clientEmail || r.clientName || '').toLowerCase().includes(cEmail)) return true;
            if (cName && (r.clientName || '').toLowerCase().includes(cName)) return true;
            if (cCompany && (r.clientName || r.projectName || '').toLowerCase().includes(cCompany)) return true;
            return false;
        });

        const seen = new Set();
        return [...direct, ...matched].filter(r => {
            const key = r._id || r.id || `${r.srNo}_${r.paidAmount}_${r.paymentDate}_${r.clientName}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [paymentRecords, deals]);

    const logActivity = useCallback(async (targetId, activityData, isContact = false) => {
        if (!targetId) return;
        const isContactEntity = isContact || (typeof targetId === 'string' && (targetId.startsWith('contact_') || targetId.startsWith('cont-'))) || (Array.isArray(contacts) && contacts.some(c => c._id === targetId));
        const authorName = activityData.author || (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';
        const now = new Date();
        const formattedDate = formatDateDDMMYYYY(now);
        const formattedTime = now.toLocaleTimeString('en-IN', {
            timeZone: 'Asia/Kolkata',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        }).toUpperCase();

        const newActivity = {
            ...activityData,
            id: activityData.id || `act-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
            type: activityData.type || 'activity',
            title: activityData.title || 'Activity Update',
            content: activityData.content || activityData.remarks || '',
            remarks: activityData.remarks || activityData.content || '',
            author: authorName,
            createdBy: activityData.createdBy || user?._id || user?.id || null,
            time: (activityData.time || formattedTime).toUpperCase(),
            date: activityData.date || formattedDate,
            createdAt: activityData.createdAt || now.toISOString(),
            timestamp: Date.now()
        };

        // 1. Update local dealActivities state
        setDealActivities(prev => {
            const currentList = prev[targetId] || [];
            return {
                ...prev,
                [targetId]: [newActivity, ...currentList].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
            };
        });

        // 2. Update entities in state and sync with backend
        if (isContactEntity) {
            const cleanContactId = targetId.toString().replace('contact_', '');
            setContacts(prev => prev.map(c => {
                if (c._id === cleanContactId || c._id === targetId) {
                    const acts = Array.isArray(c.activities) ? c.activities : [];
                    return { ...c, activities: [newActivity, ...acts].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)) };
                }
                return c;
            }));
            try {
                if (cleanContactId && !cleanContactId.startsWith('contact-')) {
                    const res = await api.post(`/pipeline-contacts/${cleanContactId}/activities`, newActivity);
                    if (res?.data?._id) {
                        const saved = res.data;
                        setContacts(prev => prev.map(c => {
                            if (c._id === cleanContactId || c._id === targetId) {
                                const acts = (c.activities || []).map(a => (a.id === newActivity.id ? { ...saved, id: saved._id } : a));
                                return { ...c, activities: acts };
                            }
                            return c;
                        }));
                        setDealActivities(prev => {
                            const currentList = prev[targetId] || [];
                            return {
                                ...prev,
                                [targetId]: currentList.map(a => (a.id === newActivity.id ? { ...saved, id: saved._id } : a))
                            };
                        });
                    }
                }
            } catch (err) {
                console.warn('API sync contact activity:', err.message);
            }
        } else {
            setDeals(prev => prev.map(d => {
                if (d._id === targetId) {
                    const acts = Array.isArray(d.activities) ? d.activities : [];
                    return { ...d, activities: [newActivity, ...acts].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)) };
                }
                return d;
            }));
            try {
                if (targetId && !targetId.toString().startsWith('deal-sample-') && !targetId.toString().startsWith('deal-')) {
                    const res = await api.post(`/deals/${targetId}/activities`, newActivity);
                    if (res?.data?._id) {
                        const saved = res.data;
                        setDeals(prev => prev.map(d => {
                            if (d._id === targetId) {
                                const acts = (d.activities || []).map(a => (a.id === newActivity.id ? { ...saved, id: saved._id } : a));
                                return { ...d, activities: acts };
                            }
                            return d;
                        }));
                        setDealActivities(prev => {
                            const currentList = prev[targetId] || [];
                            return {
                                ...prev,
                                [targetId]: currentList.map(a => (a.id === newActivity.id ? { ...saved, id: saved._id } : a))
                            };
                        });
                    }
                }
            } catch (err) {
                console.warn('API sync deal activity:', err.message);
            }
        }

        return newActivity;
    }, [contacts, deals, user]);

    // Initial Backend Data Fetching
    useEffect(() => {
        let isMounted = true;
        // Clear legacy mock/static localStorage caches so only live database records are used
        try {
            localStorage.removeItem('crm_deals');
            localStorage.removeItem('crm_contacts');
            localStorage.removeItem('crm_deal_activities');
            if (!isAdmin) {
                localStorage.removeItem('crm_pipelines');
                localStorage.removeItem('crm_stages');
                localStorage.removeItem('crm_contact_stages');
                localStorage.removeItem('crm_lead_stages');
                localStorage.removeItem('crm_pipeline_options');
            }
        } catch (e) { }

        const fetchAllData = async () => {
            setIsLoading(true);
            const startTime = Date.now();
            let loadedDeals = [];
            let initialContacts = [];
            try {
                const dealsRes = await api.get('/deals');
                if (isMounted) {
                    const apiDeals = (dealsRes.data && Array.isArray(dealsRes.data)) ? dealsRes.data : [];
                    loadedDeals = apiDeals;
                    setDeals(apiDeals);
                }
            } catch (err) {
                console.warn('Deals API load:', err.message);
                if (isMounted) setDeals([]);
            }

            try {
                const contactsRes = await api.get('/pipeline-contacts');
                if (isMounted) {
                    const apiContacts = (contactsRes.data && Array.isArray(contactsRes.data)) ? contactsRes.data : [];
                    initialContacts = apiContacts;
                    setContacts(apiContacts);
                }
            } catch (err) {
                console.warn('Pipeline contacts API load:', err.message);
                if (isMounted) setContacts([]);
            }

            try {
                const compRes = await api.get('/pipeline-companies');
                if (isMounted && compRes.data && Array.isArray(compRes.data)) {
                    setCompanies(compRes.data);
                }
            } catch (err) {
                console.warn('Pipeline companies API load:', err.message);
            }

            try {
                const tasksRes = await api.get('/pipeline-tasks');
                if (isMounted && tasksRes.data && Array.isArray(tasksRes.data)) {
                    setTasks(tasksRes.data);
                }
            } catch (err) {
                console.warn('Pipeline tasks API load:', err.message);
            }

            try {
                const empRes = await api.get('/employees');
                if (isMounted && empRes.data && Array.isArray(empRes.data)) {
                    setEmployees(empRes.data);
                }
            } catch (err) {
                console.warn('Employees API load:', err.message);
                try {
                    const listRes = await api.get('/employees/list');
                    if (isMounted && listRes.data && Array.isArray(listRes.data)) {
                        setEmployees(listRes.data);
                    }
                } catch (err2) {
                    console.warn('Employees list fallback load:', err2.message);
                }
            }

            try {
                const payRes = await api.get('/payment-records');
                if (isMounted && payRes.data && Array.isArray(payRes.data)) {
                    const grouped = {};
                    payRes.data.forEach(r => {
                        const key = r.dealId || r.contactId || 'general';
                        if (!grouped[key]) grouped[key] = [];
                        grouped[key].push(r);
                    });
                    setPaymentRecords(grouped);
                }
            } catch (err) {
                console.warn('Payment records API load:', err.message);
            }

            try {
                const configRes = await api.get('/pipeline-configs');
                if (isMounted && configRes.data) {
                    const cfg = configRes.data;
                    const deletedStages = Array.isArray(cfg.deletedStages) ? cfg.deletedStages : [];
                    const deletedPipelines = Array.isArray(cfg.deletedPipelines) ? cfg.deletedPipelines : [];
                    if (deletedStages.length > 0) {
                        try { localStorage.setItem('crm_deleted_stages', JSON.stringify(deletedStages)); } catch (e) { }
                    }
                    if (deletedPipelines.length > 0) {
                        try { localStorage.setItem('crm_deleted_pipelines', JSON.stringify(deletedPipelines)); } catch (e) { }
                    }
                    const legacyDummyNames = ['marketing qualified (mql)', 'marketing qualified', 'sales qualified (sql)', 'sales qualified', 'opportunity', 'opportunity stage', 'customer', 'retention & customer'];
                    if (Array.isArray(cfg.contactStages)) {
                        const cleanedContactStages = cfg.contactStages.filter(s =>
                            !deletedStages.includes((s.name || s.id || '').toLowerCase()) &&
                            !legacyDummyNames.includes((s.name || s.id || '').toLowerCase())
                        );
                        setContactStages(cleanedContactStages);
                        try { localStorage.setItem('crm_contact_stages', JSON.stringify(cleanedContactStages)); } catch (e) { }
                    }
                    if (Array.isArray(cfg.leadStages) && cfg.leadStages.length > 0) {
                        const cleanedLeadStages = cfg.leadStages.filter(s => !deletedStages.includes(String(s).toLowerCase()));
                        setLeadStages(cleanedLeadStages);
                        try { localStorage.setItem('crm_lead_stages', JSON.stringify(cleanedLeadStages)); } catch (e) { }
                    }
                    if (Array.isArray(cfg.pipelines) && cfg.pipelines.length > 0) {
                        const seen = new Set();
                        let cleanedPipelines = cfg.pipelines
                            .filter(p => !deletedPipelines.includes((p.name || p.id || '').toLowerCase()))
                            .map((p, idx) => ({
                                ...p,
                                id: p.id || p.name || `pipeline-${idx}`,
                                name: p.name || p.id || `Stage ${idx + 1}`
                            }))
                            .filter(p => {
                                const key = (p.name || p.id || '').toLowerCase().trim();
                                if (!key || seen.has(key)) return false;
                                seen.add(key);
                                return true;
                            });
                        if (!cleanedPipelines.some(p => (p.name || p.id || '').toLowerCase() === 'lost') && !deletedPipelines.includes('lost')) {
                            cleanedPipelines = [{ id: 'Lost', name: 'Lost', badgeClass: 'lost', createdAt: '2026-10-17T00:00:00+05:30' }, ...cleanedPipelines];
                        }
                        if (!cleanedPipelines.some(p => (p.name || p.id || '').toLowerCase() === 'new lead') && !deletedPipelines.includes('new lead')) {
                            cleanedPipelines = [{ id: 'New Lead', name: 'New Lead', badgeClass: 'new', createdAt: '2026-10-17T00:00:00+05:30' }, ...cleanedPipelines];
                        }

                        if (!cleanedPipelines.some(p => (p.name || p.id || '').toLowerCase() === 'initial') && !deletedPipelines.includes('initial')) {
                            cleanedPipelines = [...cleanedPipelines, { id: 'Initial', name: 'Initial', badgeClass: 'new', createdAt: '2026-10-17T00:00:00+05:30' }];
                        }
                        setPipelines(cleanedPipelines);
                        try { localStorage.setItem('crm_pipelines', JSON.stringify(cleanedPipelines)); } catch (e) { }
                    }
                    if (Array.isArray(cfg.stages) && cfg.stages.length > 0) {
                        let cleanedStages = [...cfg.stages];
                        if (!cleanedStages.some(s => (s.name || s.id || '').toLowerCase() === 'lost')) {
                            cleanedStages = [{ id: 'Lost', name: 'Lost', badgeClass: 'lost', createdAt: '2026-10-17T00:00:00+05:30' }, ...cleanedStages];
                        }
                        if (!cleanedStages.some(s => (s.name || s.id || '').toLowerCase() === 'new lead')) {
                            cleanedStages = [{ id: 'New Lead', name: 'New Lead', badgeClass: 'new', createdAt: '2026-10-17T00:00:00+05:30' }, ...cleanedStages];
                        }

                        if (!cleanedStages.some(s => (s.name || s.id || '').toLowerCase() === 'initial')) {
                            cleanedStages = [...cleanedStages, { id: 'Initial', name: 'Initial', badgeClass: 'new', createdAt: '2026-10-17T00:00:00+05:30' }];
                        }
                        setStages(cleanedStages);
                        try { localStorage.setItem('crm_stages', JSON.stringify(cleanedStages)); } catch (e) { }
                    }
                    if (Array.isArray(cfg.pipelineOptions) && cfg.pipelineOptions.length > 0) {
                        let cleanedPipelineOptions = cfg.pipelineOptions.filter(p => !deletedPipelines.includes(String(p).toLowerCase()));
                        if (!cleanedPipelineOptions.some(p => p.toLowerCase() === 'lost') && !deletedPipelines.includes('lost')) {
                            cleanedPipelineOptions = ['Lost', ...cleanedPipelineOptions];
                        }
                        if (!cleanedPipelineOptions.some(p => p.toLowerCase() === 'new lead') && !deletedPipelines.includes('new lead')) {
                            cleanedPipelineOptions = ['New Lead', ...cleanedPipelineOptions];
                        }

                        if (!cleanedPipelineOptions.some(p => p.toLowerCase() === 'initial') && !deletedPipelines.includes('initial')) {
                            cleanedPipelineOptions = [...cleanedPipelineOptions, 'Initial'];
                        }
                        setPipelineOptions(cleanedPipelineOptions);
                        try { localStorage.setItem('crm_pipeline_options', JSON.stringify(cleanedPipelineOptions)); } catch (e) { }
                    }

                    // Dynamically discover any pipelines and stages from loaded deals
                    if (loadedDeals && loadedDeals.length > 0) {
                        const dealPipelines = Array.from(new Set(loadedDeals.map(d => d.pipeline).filter(Boolean)));
                        setPipelines(prev => {
                            const current = prev || [];
                            const names = new Set(current.map(p => (p.name || p.id || '').toLowerCase()));
                            const toAdd = dealPipelines.filter(dp => !names.has(dp.toLowerCase())).map(dp => {
                                const matchedDeal = loadedDeals.find(d => (d.pipeline || '').toLowerCase() === dp.toLowerCase());
                                const dDate = matchedDeal ? getItemCreationDate(matchedDeal) : null;
                                const isPostCutoff = dDate && dDate >= DATA_CUTOFF_DATE;
                                return {
                                    id: dp,
                                    name: dp,
                                    createdAt: isPostCutoff ? (matchedDeal.createdAt || new Date().toISOString()) : '2026-09-01T00:00:00+05:30'
                                };
                            });
                            return toAdd.length > 0 ? [...current, ...toAdd] : current;
                        });
                        setPipelineOptions(prev => {
                            const current = prev || [];
                            const lower = new Set(current.map(p => String(p).toLowerCase()));
                            const toAdd = dealPipelines.filter(dp => !lower.has(dp.toLowerCase()));
                            return toAdd.length > 0 ? [...current, ...toAdd] : current;
                        });

                        const dealStages = Array.from(new Set(loadedDeals.map(d => d.stage).filter(Boolean)));
                        setStages(prev => {
                            const current = prev || [];
                            const names = new Set(current.map(s => (s.name || s.id || '').toLowerCase()));
                            const toAdd = dealStages.filter(ds => !names.has(ds.toLowerCase())).map(ds => {
                                const matchedDeal = loadedDeals.find(d => (d.stage || '').toLowerCase() === ds.toLowerCase());
                                const dDate = matchedDeal ? getItemCreationDate(matchedDeal) : null;
                                const isPostCutoff = dDate && dDate >= DATA_CUTOFF_DATE;
                                return {
                                    id: ds,
                                    name: ds,
                                    badgeClass: 'new',
                                    isCustom: true,
                                    createdAt: isPostCutoff ? (matchedDeal.createdAt || new Date().toISOString()) : '2026-09-01T00:00:00+05:30'
                                };
                            });
                            return toAdd.length > 0 ? [...current, ...toAdd] : current;
                        });
                    }

                    // Dynamically discover any contact stages from loaded contacts
                    if (initialContacts && initialContacts.length > 0) {
                        const contactLeadStages = Array.from(new Set(initialContacts.map(c => c.leadStage).filter(Boolean)));
                        setContactStages(prev => {
                            const current = prev || [];
                            const names = new Set(current.map(s => (s.name || s.id || '').toLowerCase()));
                            const toAdd = contactLeadStages
                                .filter(st => !names.has(st.toLowerCase()) && !deletedStages.includes(st.toLowerCase()))
                                .map((st, i) => {
                                    const matchedContact = initialContacts.find(c => (c.leadStage || '').toLowerCase() === st.toLowerCase());
                                    const cDate = matchedContact ? getItemCreationDate(matchedContact) : null;
                                    const isPostCutoff = cDate && cDate >= DATA_CUTOFF_DATE;
                                    return {
                                        id: st,
                                        name: st,
                                        badgeClass: ['new', 'hot', 'cold', 'won', 'custom'][i % 5],
                                        createdAt: isPostCutoff ? (matchedContact.createdAt || new Date().toISOString()) : '2026-09-01T00:00:00+05:30'
                                    };
                                });
                            return toAdd.length > 0 ? [...current, ...toAdd] : current;
                        });
                        setLeadStages(prev => {
                            const current = prev || [];
                            const lower = new Set(current.map(s => String(s).toLowerCase()));
                            const toAdd = contactLeadStages.filter(st => !lower.has(st.toLowerCase()) && !deletedStages.includes(st.toLowerCase()));
                            return toAdd.length > 0 ? [...current, ...toAdd] : current;
                        });
                    }
                }
            } catch (err) {
                console.warn('Pipeline configs API load:', err.message);
            } finally {
                const elapsed = Date.now() - startTime;
                const delay = Math.max(0, 450 - elapsed);
                setTimeout(() => {
                    if (isMounted) setIsLoading(false);
                }, delay);
            }
        };

        fetchAllData();
        return () => { isMounted = false; };
    }, []);


    const detailedContact = useMemo(() => {
        return contacts.find(c => c._id === detailedContactId) || null;
    }, [contacts, detailedContactId]);

    const hoveredContact = useMemo(() => {
        return (contacts || []).find(c => c && c._id === hoveredActivityContactId) || null;
    }, [contacts, hoveredActivityContactId]);


    const [showCreateContactModal, setShowCreateContactModal] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [viewingContact, setViewingContact] = useState(null);
    const [activeContactMenuId, setActiveContactMenuId] = useState(null);
    const [moveSubmenuContactId, setMoveSubmenuContactId] = useState(null);
    const [activeContactStatusDropdownId, setActiveContactStatusDropdownId] = useState(null);

    const [isCountryCodeOpen, setIsCountryCodeOpen] = useState(false);
    const countryCodeRef = useRef(null);
    const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]);

    const [isDealCountryCodeOpen, setIsDealCountryCodeOpen] = useState(false);
    const dealCountryCodeRef = useRef(null);
    const [dealSelectedCountry, setDealSelectedCountry] = useState(COUNTRY_CODES[0]);

    const [contactFormData, setContactFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        countryCode: '+1',
        phone: '',
        origin: 'Domestic',
        contactSource: '',
        leadStage: '',
        leadStatus: '',
        owner: 'Artiflow',
        company: ''
    });

    // Dynamic list of all contact owners (from sales team & existing contacts)
    const allContactOwners = useMemo(() => {
        const ownerMap = new Map();
        (ownerOptions || []).forEach(name => {
            if (name && name.trim()) {
                ownerMap.set(name.trim().toLowerCase(), name.trim());
            }
        });
        (contacts || []).forEach(c => {
            if (c.owner && c.owner.trim()) {
                const key = c.owner.trim().toLowerCase();
                if (!ownerMap.has(key)) {
                    ownerMap.set(key, c.owner.trim());
                }
            }
        });
        return Array.from(ownerMap.values()).sort((a, b) => a.localeCompare(b));
    }, [ownerOptions, contacts]);

    // Contact counts per owner for badge counts
    const contactOwnerCounts = useMemo(() => {
        const counts = {};
        (contacts || []).forEach(c => {
            if (!isItemOwnedByUser(c, 'contact')) return;
            const o = (c.owner || 'Unassigned').trim();
            counts[o] = (counts[o] || 0) + 1;
            counts[o.toLowerCase()] = (counts[o.toLowerCase()] || 0) + 1;
        });
        return counts;
    }, [contacts, isItemOwnedByUser]);

    const contactSortLabel = useMemo(() => {
        switch (contactSortBy) {
            case 'name_asc': return 'Name: A - Z';
            case 'name_desc': return 'Name: Z - A';
            case 'company': return 'Company: A - Z';
            case 'stage': return 'Lead Stage';
            case 'status': return 'Lead Status';
            case 'oldest': return 'Oldest first';
            default: return 'Newest first';
        }
    }, [contactSortBy]);

    const contactDateFilterLabel = useMemo(() => {
        switch (contactDateFilter) {
            case 'today': return 'Date: Today';
            case 'this_week': return 'Date: This week';
            case 'this_month': return 'Date: This month';
            case 'next_30_days': return 'Date: Next 30 days';
            case 'this_quarter': return 'Date: This quarter';
            case 'this_year': return 'Date: This year';
            case 'custom':
                if (customContactDateStart && customContactDateEnd) {
                    return `${formatDateDDMMYYYY(customContactDateStart)} to ${formatDateDDMMYYYY(customContactDateEnd)}`;
                }
                if (customContactDateStart) return `From ${formatDateDDMMYYYY(customContactDateStart)}`;
                if (customContactDateEnd) return `Until ${formatDateDDMMYYYY(customContactDateEnd)}`;
                return 'Date: Custom';
            default:
                return 'All dates';
        }
    }, [contactDateFilter, customContactDateStart, customContactDateEnd]);

    const contactOriginCounts = useMemo(() => {
        const counts = { all: 0, domestic: 0, international: 0 };
        (contacts || []).forEach(c => {
            if (!isItemOwnedByUser(c, 'contact')) return;
            counts.all++;
            const origin = (c.origin || 'Domestic').toLowerCase();
            if (origin === 'international') {
                counts.international++;
            } else {
                counts.domestic++;
            }
        });
        return counts;
    }, [contacts, isItemOwnedByUser]);

    const isAnyContactFilterActive = useMemo(() => {
        return (
            contactSearchQuery.trim() !== '' ||
            contactOwnerFilter !== 'all' ||
            contactStatusFilter !== 'all' ||
            contactOriginFilter !== 'all' ||
            contactStageFilter !== 'all' ||
            contactDateFilter !== 'all' ||
            contactSortBy !== 'newest'
        );
    }, [contactSearchQuery, contactOwnerFilter, contactStatusFilter, contactOriginFilter, contactStageFilter, contactDateFilter, contactSortBy]);

    const handleResetContactFilters = () => {
        setContactSearchQuery('');
        setContactOwnerFilter('all');
        setContactStatusFilter('all');
        setContactOriginFilter('all');
        setContactStageFilter('all');
        setContactDateFilter('all');
        setCustomContactDateStart('');
        setCustomContactDateEnd('');
        setContactSortBy('newest');
        setIsContactSortDropdownOpen(false);
        setIsContactOwnerDropdownOpen(false);
        setIsContactStatusDropdownOpen(false);
        setIsContactOriginDropdownOpen(false);
        setIsContactDateDropdownOpen(false);
        setContactOwnerSearchQuery('');
    };

    // Filtered Contacts Memo
    const filteredContacts = useMemo(() => {
        return contacts
            .filter(contact => {
                if (!isItemOwnedByUser(contact, 'contact')) {
                    return false;
                }

                if (contactSearchQuery.trim()) {
                    const q = contactSearchQuery.toLowerCase();
                    const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.toLowerCase();
                    const email = (contact.email || '').toLowerCase();
                    const company = (contact.company || '').toLowerCase();
                    const phone = (contact.phone || '').toLowerCase();
                    if (!fullName.includes(q) && !email.includes(q) && !company.includes(q) && !phone.includes(q)) {
                        return false;
                    }
                }

                if (contactOwnerFilter !== 'all') {
                    const cOwner = (contact.owner || '').trim().toLowerCase();
                    const fOwner = contactOwnerFilter.trim().toLowerCase();
                    if (cOwner !== fOwner && !cOwner.includes(fOwner)) {
                        return false;
                    }
                }

                if (contactStageFilter !== 'all') {
                    const filterLower = contactStageFilter.toLowerCase();
                    const contactStageLower = (contact.leadStage || '').toLowerCase();
                    const matchedStage = effectiveContactStages.find(s => (s.name || '').toLowerCase() === filterLower || (s.id || '').toLowerCase() === filterLower);
                    if (matchedStage) {
                        const matchesStage = contactStageLower === (matchedStage.name || '').toLowerCase() || contactStageLower === (matchedStage.id || '').toLowerCase();
                        if (!matchesStage) return false;
                    } else if (contactStageLower !== filterLower) {
                        return false;
                    }
                }

                if (contactStatusFilter !== 'all' && (contact.leadStatus || '').toLowerCase() !== contactStatusFilter.toLowerCase()) {
                    return false;
                }

                if (contactOriginFilter !== 'all') {
                    const cOrig = (contact.origin || 'Domestic').toLowerCase();
                    if (cOrig !== contactOriginFilter.toLowerCase()) return false;
                }

                if (contactSourceFilter !== 'all' && (contact.contactSource || '').toLowerCase() !== contactSourceFilter.toLowerCase()) {
                    return false;
                }

                if (contactDateFilter !== 'all') {
                    const contactDate = parseDealDate(contact.createdAt) || parseDealDate(contact.createdDate) || parseDealDate(contact.date);
                    if (!contactDate) return false;
                    const now = new Date();
                    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

                    if (contactDateFilter === 'today') {
                        if (contactDate < startOfToday || contactDate > endOfToday) return false;
                    } else if (contactDateFilter === 'this_week') {
                        const firstDayOfWeek = new Date(startOfToday);
                        const day = firstDayOfWeek.getDay();
                        const diff = firstDayOfWeek.getDate() - day + (day === 0 ? -6 : 1);
                        firstDayOfWeek.setDate(diff);
                        const lastDayOfWeek = new Date(firstDayOfWeek);
                        lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
                        lastDayOfWeek.setHours(23, 59, 59, 999);
                        if (contactDate < firstDayOfWeek || contactDate > lastDayOfWeek) return false;
                    } else if (contactDateFilter === 'this_month') {
                        if (contactDate.getMonth() !== now.getMonth() || contactDate.getFullYear() !== now.getFullYear()) return false;
                    } else if (contactDateFilter === 'next_30_days') {
                        const in30Days = new Date(startOfToday);
                        in30Days.setDate(in30Days.getDate() + 30);
                        in30Days.setHours(23, 59, 59, 999);
                        if (contactDate < startOfToday || contactDate > in30Days) return false;
                    } else if (contactDateFilter === 'this_quarter') {
                        const currentQuarter = Math.floor(now.getMonth() / 3);
                        const contactQuarter = Math.floor(contactDate.getMonth() / 3);
                        if (contactQuarter !== currentQuarter || contactDate.getFullYear() !== now.getFullYear()) return false;
                    } else if (contactDateFilter === 'this_year') {
                        if (contactDate.getFullYear() !== now.getFullYear()) return false;
                    } else if (contactDateFilter === 'custom') {
                        if (customContactDateStart) {
                            const start = new Date(customContactDateStart);
                            start.setHours(0, 0, 0, 0);
                            if (contactDate < start) return false;
                        }
                        if (customContactDateEnd) {
                            const end = new Date(customContactDateEnd);
                            end.setHours(23, 59, 59, 999);
                            if (contactDate > end) return false;
                        }
                    }
                }

                return true;
            })
            .sort((a, b) => {
                if (contactSortBy === 'name_asc') {
                    const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || '';
                    const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.email || '';
                    return nameA.localeCompare(nameB);
                }
                if (contactSortBy === 'name_desc') {
                    const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || '';
                    const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.email || '';
                    return nameB.localeCompare(nameA);
                }
                if (contactSortBy === 'company') {
                    return (a.company || '').localeCompare(b.company || '');
                }
                if (contactSortBy === 'stage') {
                    return (a.leadStage || '').localeCompare(b.leadStage || '');
                }
                if (contactSortBy === 'status') {
                    return (a.leadStatus || '').localeCompare(b.leadStatus || '');
                }
                if (contactSortBy === 'oldest') {
                    const da = parseDealDate(a.createdAt) || parseDealDate(a.createdDate) || parseDealDate(a.date) || new Date(0);
                    const db = parseDealDate(b.createdAt) || parseDealDate(b.createdDate) || parseDealDate(b.date) || new Date(0);
                    return da - db;
                }
                // newest
                const da = parseDealDate(a.createdAt) || parseDealDate(a.createdDate) || parseDealDate(a.date) || new Date(0);
                const db = parseDealDate(b.createdAt) || parseDealDate(b.createdDate) || parseDealDate(b.date) || new Date(0);
                return db - da;
            });
    }, [contacts, contactSearchQuery, contactOwnerFilter, contactStageFilter, contactStatusFilter, contactOriginFilter, contactSourceFilter, contactDateFilter, customContactDateStart, customContactDateEnd, contactSortBy, effectiveContactStages, isItemOwnedByUser]);

    // Companies Management State
    const [companies, setCompanies] = useState(INITIAL_SAMPLE_COMPANIES);
    const [industryOptions, setIndustryOptions] = useState(() => {
        try {
            const saved = localStorage.getItem('crm_industry_options');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed)) return parsed;
            }
        } catch (e) { }
        return INDUSTRY_OPTIONS;
    });
    const [countryListOptions, setCountryListOptions] = useState(COUNTRY_OPTIONS);
    const [companySearchQuery, setCompanySearchQuery] = useState('');
    const [companyIndustryFilter, setCompanyIndustryFilter] = useState('all');
    const [isCompanyIndustryDropdownOpen, setIsCompanyIndustryDropdownOpen] = useState(false);
    const [companyOwnerFilter, setCompanyOwnerFilter] = useState('all');
    const [isCompanyOwnerDropdownOpen, setIsCompanyOwnerDropdownOpen] = useState(false);
    const [companyOwnerSearchQuery, setCompanyOwnerSearchQuery] = useState('');
    const [companyDateFilter, setCompanyDateFilter] = useState('all');
    const [customCompanyDateStart, setCustomCompanyDateStart] = useState('');
    const [customCompanyDateEnd, setCustomCompanyDateEnd] = useState('');
    const [isCompanyDateDropdownOpen, setIsCompanyDateDropdownOpen] = useState(false);
    const [companySortBy, setCompanySortBy] = useState('newest'); // 'newest', 'oldest', 'name_asc', 'name_desc', 'industry', 'country'
    const [isCompanySortDropdownOpen, setIsCompanySortDropdownOpen] = useState(false);
    const [companyViewMode, setCompanyViewMode] = useState('grid'); // 'grid' | 'list'
    const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
    const [isBulkCompanyIndustryDropdownOpen, setIsBulkCompanyIndustryDropdownOpen] = useState(false);
    const [isBulkCompanyOwnerDropdownOpen, setIsBulkCompanyOwnerDropdownOpen] = useState(false);
    const companyClickTimerRef = useRef(null);

    // Clear companies multi-selection when tab or view mode changes
    useEffect(() => {
        setSelectedCompanyIds([]);
        setIsBulkCompanyIndustryDropdownOpen(false);
        setIsBulkCompanyOwnerDropdownOpen(false);
    }, [activeTab, companyViewMode]);

    const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [viewingCompany, setViewingCompany] = useState(null);
    const [activeCompanyMenuId, setActiveCompanyMenuId] = useState(null);

    // Dynamic list of all company owners (from sales team & existing companies)
    const allCompanyOwners = useMemo(() => {
        const ownerMap = new Map();
        (ownerOptions || []).forEach(name => {
            if (name && name.trim()) {
                ownerMap.set(name.trim().toLowerCase(), name.trim());
            }
        });
        (companies || []).forEach(c => {
            const ownerVal = Array.isArray(c.owner) ? c.owner.join(', ') : (c.owner || '');
            if (ownerVal && ownerVal.trim()) {
                ownerVal.split(',').map(s => s.trim()).filter(Boolean).forEach(o => {
                    const key = o.toLowerCase();
                    if (!ownerMap.has(key)) {
                        ownerMap.set(key, o);
                    }
                });
            }
        });
        return Array.from(ownerMap.values()).sort((a, b) => a.localeCompare(b));
    }, [ownerOptions, companies]);

    // Company counts per owner for badge counts
    const companyOwnerCounts = useMemo(() => {
        const counts = {};
        (companies || []).forEach(c => {
            if (!isItemOwnedByUser(c, 'company')) return;
            const ownerVal = Array.isArray(c.owner) ? c.owner.join(', ') : (c.owner || 'Unassigned');
            ownerVal.split(',').map(s => s.trim()).filter(Boolean).forEach(o => {
                counts[o] = (counts[o] || 0) + 1;
                counts[o.toLowerCase()] = (counts[o.toLowerCase()] || 0) + 1;
            });
        });
        return counts;
    }, [companies, isItemOwnedByUser]);

    // Company counts per industry for badge counts
    const companyIndustryCounts = useMemo(() => {
        const counts = {};
        (companies || []).forEach(c => {
            if (!isItemOwnedByUser(c, 'company')) return;
            const ind = (c.industry || 'Other').trim();
            counts[ind] = (counts[ind] || 0) + 1;
            counts[ind.toLowerCase()] = (counts[ind.toLowerCase()] || 0) + 1;
        });
        return counts;
    }, [companies, isItemOwnedByUser]);

    const companySortLabel = useMemo(() => {
        switch (companySortBy) {
            case 'name_asc': return 'Name: A - Z';
            case 'name_desc': return 'Name: Z - A';
            case 'industry': return 'Industry: A - Z';
            case 'country': return 'Country: A - Z';
            case 'oldest': return 'Oldest first';
            default: return 'Newest first';
        }
    }, [companySortBy]);

    const companyDateFilterLabel = useMemo(() => {
        switch (companyDateFilter) {
            case 'today': return 'Date: Today';
            case 'this_week': return 'Date: This week';
            case 'this_month': return 'Date: This month';
            case 'next_30_days': return 'Date: Next 30 days';
            case 'this_quarter': return 'Date: This quarter';
            case 'this_year': return 'Date: This year';
            case 'custom':
                if (customCompanyDateStart && customCompanyDateEnd) {
                    return `${formatDateDDMMYYYY(customCompanyDateStart)} to ${formatDateDDMMYYYY(customCompanyDateEnd)}`;
                }
                if (customCompanyDateStart) return `From ${formatDateDDMMYYYY(customCompanyDateStart)}`;
                if (customCompanyDateEnd) return `Until ${formatDateDDMMYYYY(customCompanyDateEnd)}`;
                return 'Date: Custom';
            default:
                return 'All dates';
        }
    }, [companyDateFilter, customCompanyDateStart, customCompanyDateEnd]);

    const isAnyCompanyFilterActive = useMemo(() => {
        return (
            companySearchQuery.trim() !== '' ||
            companyIndustryFilter !== 'all' ||
            companyOwnerFilter !== 'all' ||
            companyDateFilter !== 'all' ||
            companySortBy !== 'newest'
        );
    }, [companySearchQuery, companyIndustryFilter, companyOwnerFilter, companyDateFilter, companySortBy]);

    const handleResetCompanyFilters = () => {
        setCompanySearchQuery('');
        setCompanyIndustryFilter('all');
        setCompanyOwnerFilter('all');
        setCompanyDateFilter('all');
        setCustomCompanyDateStart('');
        setCustomCompanyDateEnd('');
        setCompanySortBy('newest');
        setIsCompanySortDropdownOpen(false);
        setIsCompanyIndustryDropdownOpen(false);
        setIsCompanyOwnerDropdownOpen(false);
        setIsCompanyDateDropdownOpen(false);
        setCompanyOwnerSearchQuery('');
    };

    // Ensure non-full access users (e.g. Sales role) do not have lingering owner filters active
    useEffect(() => {
        if (!hasFullAccess) {
            if (ownerFilter !== 'all') setOwnerFilter('all');
            if (contactOwnerFilter !== 'all') setContactOwnerFilter('all');
            if (companyOwnerFilter !== 'all') setCompanyOwnerFilter('all');
        }
    }, [hasFullAccess, ownerFilter, contactOwnerFilter, companyOwnerFilter]);

    // Tasks Management State
    const [tasks, setTasks] = useState(INITIAL_SAMPLE_TASKS);
    const [taskSearchQuery, setTaskSearchQuery] = useState('');
    const [taskStatusFilter, setTaskStatusFilter] = useState('all');
    const [taskPriorityFilter, setTaskPriorityFilter] = useState('all');
    const [taskTypeFilter, setTaskTypeFilter] = useState('all');
    const [taskSortBy, setTaskSortBy] = useState('dueDate_asc');
    const [taskViewMode, setTaskViewMode] = useState('list'); // 'list' | 'kanban'
    const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [taskLockedContext, setTaskLockedContext] = useState({ dealId: null, contactId: null });
    const [isAssignCustom, setIsAssignCustom] = useState(false);
    const [taskFormData, setTaskFormData] = useState({
        title: '',
        description: '',
        dueDate: '',
        priority: 'Medium',
        status: 'Pending',
        type: 'Follow-up',
        assignee: 'Artiflow',
        associatedDealId: '',
        associatedDealIds: [],
        associatedContactId: '',
        associatedContactIds: []
    });

    // Reminders Management State
    const [reminderSearchQuery, setReminderSearchQuery] = useState('');
    const [reminderStatusFilter, setReminderStatusFilter] = useState('all');
    const [reminderChannelFilter, setReminderChannelFilter] = useState('all');
    const [reminderTimingFilter, setReminderTimingFilter] = useState('all'); // 'all' | 'today' | 'upcoming' | 'overdue'
    const [reminderSortBy, setReminderSortBy] = useState('date_asc');
    const [reminderViewMode, setReminderViewMode] = useState('list'); // 'list' | 'kanban'
    const [isRemindersToolbarMenuOpen, setIsRemindersToolbarMenuOpen] = useState(false);
    const [showCreateReminderModal, setShowCreateReminderModal] = useState(false);
    const [editingReminder, setEditingReminder] = useState(null);
    const [isSubmittingReminder, setIsSubmittingReminder] = useState(false);
    const [deletingReminderId, setDeletingReminderId] = useState(null);
    const [togglingReminderId, setTogglingReminderId] = useState(null);
    const [reminderFormData, setReminderFormData] = useState({
        targetType: 'deal',
        targetId: '',
        memberId: '',
        memberName: '',
        title: '',
        channel: 'Call',
        status: 'Scheduled',
        followUpDate: new Date().toISOString().slice(0, 10),
        followUpTime: '11:00 AM',
        nextFollowUpDate: '',
        remarks: ''
    });

    // Dynamic Follow-up Member Options tailored to user role:
    // - Sales Head / Sales Manager / Admin: Can select Deal Owner (featured at top) or any sales team member.
    // - Sales Person (Executive): Can ONLY select their Sales Head or Sales Manager.
    // - Admin: Can select any member.
    const getFollowUpMemberOptions = useCallback((targetType, targetId) => {
        const isSalesPerson = !isAdmin && !isSalesHead && !isSalesManager;
        const salesHeadsAndManagers = [];
        const allSalesMembers = [];

        (employees || []).forEach(emp => {
            if (!emp) return;
            if (emp.isActive === false || emp.status === 'Inactive' || emp.status === 'inactive') return;

            const rName = (typeof emp.role === 'string' ? emp.role : (emp.role?.name || '')).toLowerCase().trim();
            const des = (emp.designation || '').toLowerCase().trim();
            const dept = (emp.department || '').toLowerCase().trim();

            const isHead = rName.includes('sales head') || rName === 'saleshead' || rName === 'head of sales' || des.includes('sales head') || des.includes('head of sales');
            const isMgr = !isHead && (rName.includes('sales manager') || rName.includes('salesmanager') || rName === 'manager of sales' || des.includes('sales manager'));
            const isSales = isHead || isMgr || rName.includes('sales') || rName.includes('business development') || rName.includes('bde') || dept.includes('sales') || des.includes('sales') || des.includes('bde');

            // Strictly only include employees with Sales, Sales Head, or Sales Manager roles
            if (!isSales) return;

            const empName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.name || emp.email;
            let roleBadge = 'Sales';
            if (isHead) {
                roleBadge = 'Sales Head';
            } else if (isMgr) {
                roleBadge = 'Sales Manager';
            } else {
                roleBadge = emp.designation || 'Sales';
            }

            const empOption = {
                id: String(emp._id || emp.id),
                name: empName,
                email: emp.email || emp.officialEmail || '',
                role: roleBadge,
                isHead,
                isMgr
            };

            if (isHead || isMgr) {
                salesHeadsAndManagers.push(empOption);
            }
            allSalesMembers.push(empOption);
        });

        // 1. For Sales Person: Strictly restricted to Sales Head & Sales Manager
        if (isSalesPerson) {
            return salesHeadsAndManagers.map(m => ({
                id: m.id,
                name: m.name,
                label: `${m.name} (${m.role})`
            }));
        }

        // 2. For Sales Head, Sales Manager, Admin: Resolve Deal Owner / Contact Owner
        let ownerOption = null;
        if (targetType === 'deal') {
            const targetDeal = deals.find(d => String(d._id) === String(targetId)) || detailedDeal;
            const dOwnerName = (targetDeal?.salesOwner || targetDeal?.addedBy || '').trim();
            if (dOwnerName) {
                const matchedEmp = (employees || []).find(emp => {
                    const eName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                    const eEmail = (emp.email || '').trim().toLowerCase();
                    const eOffEmail = (emp.officialEmail || '').trim().toLowerCase();
                    const target = dOwnerName.toLowerCase();
                    return (eName && eName === target) || (eEmail && eEmail === target) || (eOffEmail && eOffEmail === target);
                });
                if (matchedEmp) {
                    ownerOption = {
                        id: String(matchedEmp._id || matchedEmp.id),
                        name: `${matchedEmp.firstName || ''} ${matchedEmp.lastName || ''}`.trim() || dOwnerName,
                        label: `⭐ ${dOwnerName} (Deal Owner)`,
                        isDealOwner: true
                    };
                } else {
                    ownerOption = {
                        id: `name_${dOwnerName}`,
                        name: dOwnerName,
                        label: `⭐ ${dOwnerName} (Deal Owner)`,
                        isDealOwner: true
                    };
                }
            }
        } else if (targetType === 'contact') {
            const targetContact = contacts.find(c => String(c._id) === String(targetId)) || detailedContact;
            const cOwnerName = (targetContact?.owner || targetContact?.addedBy || '').trim();
            if (cOwnerName) {
                const matchedEmp = (employees || []).find(emp => {
                    const eName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                    const eEmail = (emp.email || '').trim().toLowerCase();
                    const eOffEmail = (emp.officialEmail || '').trim().toLowerCase();
                    const target = cOwnerName.toLowerCase();
                    return (eName && eName === target) || (eEmail && eEmail === target) || (eOffEmail && eOffEmail === target);
                });
                if (matchedEmp) {
                    ownerOption = {
                        id: String(matchedEmp._id || matchedEmp.id),
                        name: `${matchedEmp.firstName || ''} ${matchedEmp.lastName || ''}`.trim() || cOwnerName,
                        label: `⭐ ${cOwnerName} (Contact Owner)`,
                        isDealOwner: true
                    };
                } else {
                    ownerOption = {
                        id: `name_${cOwnerName}`,
                        name: cOwnerName,
                        label: `⭐ ${cOwnerName} (Contact Owner)`,
                        isDealOwner: true
                    };
                }
            }
        }

        const options = [];
        const addedIds = new Set();

        if (ownerOption) {
            options.push(ownerOption);
            addedIds.add(String(ownerOption.id));
            addedIds.add(ownerOption.name.toLowerCase());
        }

        allSalesMembers.forEach(m => {
            if (!addedIds.has(String(m.id)) && !addedIds.has(m.name.toLowerCase())) {
                options.push({
                    id: m.id,
                    name: m.name,
                    label: `${m.name} (${m.role})`
                });
                addedIds.add(String(m.id));
                addedIds.add(m.name.toLowerCase());
            }
        });

        return options;
    }, [employees, isAdmin, isSalesHead, isSalesManager, deals, contacts, detailedDeal, detailedContact]);

    // Helper to extract strictly the contacts associated with a given deal
    const getDealAssociatedContacts = (targetDealIdOrObj) => {
        if (!targetDealIdOrObj) return [];
        const targetDeal = typeof targetDealIdOrObj === 'object'
            ? targetDealIdOrObj
            : (deals.find(d => d._id === targetDealIdOrObj) || detailedDeal);
        if (!targetDeal) return [];

        const matched = [];
        const seen = new Set();
        const dealComp = (targetDeal.associatedCompany || targetDeal.clientName || '').trim().toLowerCase();

        // 1. Direct ID
        if (targetDeal.associatedContactId || targetDeal.contactId) {
            const dId = targetDeal.associatedContactId || targetDeal.contactId;
            const found = contacts.find(c => c._id === dId);
            if (found && !seen.has(found._id)) {
                matched.push(found);
                seen.add(found._id);
            }
        }

        // 2. Deal raw emails/names
        const rawTokens = [targetDeal.associatedContact, targetDeal.email, targetDeal.clientEmail, targetDeal.contact]
            .filter(Boolean)
            .flatMap(s => typeof s === 'string' ? s.split(',') : [s])
            .map(s => String(s).trim().toLowerCase())
            .filter(Boolean);

        // 3. Match from CRM contacts
        contacts.forEach(c => {
            const cEmail = (c.email || '').trim().toLowerCase();
            const cName = `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase();
            const cComp = (c.company || '').trim().toLowerCase();

            const isTokenMatch = rawTokens.some(t => t === cEmail || t === cName || (cEmail && t.includes(cEmail)));
            const isCompanyMatch = dealComp && cComp && (cComp === dealComp || cComp.includes(dealComp) || dealComp.includes(cComp)) && cComp !== 'direct client';

            if ((isTokenMatch || isCompanyMatch) && !seen.has(c._id)) {
                matched.push(c);
                seen.add(c._id);
            }
        });

        // 4. If raw email not in contacts db, add synthetic entry
        rawTokens.forEach(token => {
            const alreadyHas = matched.some(c => (c.email || '').toLowerCase() === token || `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase() === token);
            if (!alreadyHas && token) {
                const synId = `raw_${token}`;
                if (!seen.has(synId)) {
                    matched.push({
                        _id: synId,
                        firstName: token.includes('@') ? token.split('@')[0] : token,
                        lastName: '',
                        email: token.includes('@') ? token : '',
                        company: targetDeal.associatedCompany || targetDeal.clientName || ''
                    });
                    seen.add(synId);
                }
            }
        });

        return matched;
    };

    // Helper to extract strictly the deals associated with a given contact
    const getContactAssociatedDeals = (targetContactIdOrObj) => {
        if (!targetContactIdOrObj) return [];
        const targetContact = typeof targetContactIdOrObj === 'object'
            ? targetContactIdOrObj
            : (contacts.find(c => c._id === targetContactIdOrObj) || detailedContact);
        if (!targetContact) return [];

        const cEmail = (targetContact.email || '').trim().toLowerCase();
        const cFullName = `${targetContact.firstName || ''} ${targetContact.lastName || ''}`.trim().toLowerCase();
        const cPhoneClean = (targetContact.phone || '').replace(/\D/g, '');
        const cCompany = (targetContact.company || '').trim().toLowerCase();

        const found = deals.filter(d => {
            if (!d) return false;
            const dAssocContact = (d.associatedContact || '').trim().toLowerCase();
            const dEmail = (d.email || '').trim().toLowerCase();
            const dClientName = (d.clientName || '').trim().toLowerCase();
            const dDealName = (d.dealName || '').trim().toLowerCase();
            const dPhoneClean = (d.phone || '').replace(/\D/g, '');
            const dCompany = (d.associatedCompany || '').trim().toLowerCase();

            const matchesDirectId = (Array.isArray(targetContact.associatedDeals) && targetContact.associatedDeals.includes(d._id)) ||
                d.associatedContactId === targetContact._id || d.contactId === targetContact._id;
            const matchesEmail = cEmail && (dAssocContact === cEmail || dEmail === cEmail || dAssocContact.includes(cEmail) || dEmail.includes(cEmail));
            const matchesName = cFullName && (dClientName === cFullName || dAssocContact === cFullName || dDealName.includes(cFullName) || dClientName.includes(cFullName) || cFullName.includes(dClientName));
            const matchesPhone = cPhoneClean && cPhoneClean.length >= 7 && dPhoneClean && (dPhoneClean.endsWith(cPhoneClean) || cPhoneClean.endsWith(dPhoneClean));
            const matchesCompany = cCompany && cCompany !== 'direct client' && dCompany && (dCompany === cCompany || dCompany.includes(cCompany) || cCompany.includes(dCompany));

            return matchesDirectId || matchesEmail || matchesName || matchesPhone || matchesCompany;
        });

        const seen = new Set();
        return found.filter(d => {
            if (seen.has(d._id)) return false;
            seen.add(d._id);
            return true;
        });
    };

    const userAccessibleTasks = useMemo(() => {
        return tasks.filter(t => isItemOwnedByUser(t, 'task'));
    }, [tasks, isItemOwnedByUser]);

    const filteredTasks = useMemo(() => {
        return userAccessibleTasks
            .filter(t => {
                if (taskSearchQuery.trim()) {
                    const q = taskSearchQuery.toLowerCase();
                    const title = (t.title || '').toLowerCase();
                    const desc = (t.description || '').toLowerCase();
                    const deal = (t.associatedDealName || '').toLowerCase();
                    const contact = (t.associatedContactName || '').toLowerCase();
                    const assignee = (t.assignee || '').toLowerCase();
                    if (!title.includes(q) && !desc.includes(q) && !deal.includes(q) && !contact.includes(q) && !assignee.includes(q)) {
                        return false;
                    }
                }
                if (taskStatusFilter !== 'all') {
                    if (taskStatusFilter === 'Completed' && !t.completed && t.status !== 'Completed') return false;
                    if (taskStatusFilter === 'Pending' && (t.completed || t.status === 'Completed' || t.status === 'In Progress')) return false;
                    if (taskStatusFilter === 'In Progress' && (t.completed || t.status !== 'In Progress')) return false;
                }
                if (taskPriorityFilter !== 'all' && (t.priority || '').toLowerCase() !== taskPriorityFilter.toLowerCase()) {
                    return false;
                }
                if (taskTypeFilter !== 'all' && (t.type || '').toLowerCase() !== taskTypeFilter.toLowerCase()) {
                    return false;
                }
                return true;
            })
            .sort((a, b) => {
                if (taskSortBy === 'dueDate_asc') {
                    return (a.dueDate || '9999').localeCompare(b.dueDate || '9999');
                }
                if (taskSortBy === 'dueDate_desc') {
                    return (b.dueDate || '0000').localeCompare(a.dueDate || '0000');
                }
                if (taskSortBy === 'priority') {
                    const pOrder = { High: 1, Medium: 2, Low: 3 };
                    return (pOrder[a.priority] || 4) - (pOrder[b.priority] || 4);
                }
                return 0;
            });
    }, [userAccessibleTasks, taskSearchQuery, taskStatusFilter, taskPriorityFilter, taskTypeFilter, taskSortBy]);

    const dealSpecificTasks = useMemo(() => {
        if (!detailedDeal) return [];
        const dealIdStr = String(detailedDeal._id);
        const dealNameLower = (detailedDeal.dealName || '').toLowerCase().trim();
        const clientNameLower = (detailedDeal.clientName || '').toLowerCase().trim();
        return tasks.filter(t => {
            if (t.associatedDealId && String(t.associatedDealId) === dealIdStr) return true;
            if (Array.isArray(t.associatedDealIds) && t.associatedDealIds.some(id => String(id) === dealIdStr)) return true;
            const assocNameLower = (t.associatedDealName || '').toLowerCase().trim();
            if (assocNameLower && (assocNameLower === dealNameLower || assocNameLower === clientNameLower)) return true;
            return false;
        });
    }, [detailedDeal, tasks]);

    const contactSpecificTasks = useMemo(() => {
        if (!detailedContact) return [];
        const contactIdStr = String(detailedContact._id);
        const contactFullName = `${detailedContact.firstName || ''} ${detailedContact.lastName || ''}`.trim().toLowerCase();
        const contactEmail = (detailedContact.email || '').toLowerCase().trim();
        return tasks.filter(t => {
            if (t.associatedContactId && String(t.associatedContactId) === contactIdStr) return true;
            if (Array.isArray(t.associatedContactIds) && t.associatedContactIds.some(id => String(id) === contactIdStr)) return true;
            const assocName = (t.associatedContactName || '').toLowerCase().trim();
            if (assocName && (assocName === contactFullName || (contactEmail && assocName === contactEmail))) return true;
            return false;
        });
    }, [detailedContact, tasks]);

    // Date & Time normalization helpers for follow-ups and reminders
    const normYMD = useCallback((d) => {
        if (!d) return '';
        const s = String(d).trim();
        const ddmmyyyy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
        if (ddmmyyyy) {
            return `${ddmmyyyy[3]}-${ddmmyyyy[2].padStart(2, '0')}-${ddmmyyyy[1].padStart(2, '0')}`;
        }
        const yyyymmdd = s.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
        if (yyyymmdd) {
            return `${yyyymmdd[1]}-${yyyymmdd[2].padStart(2, '0')}-${yyyymmdd[3].padStart(2, '0')}`;
        }
        return s;
    }, []);

    const normTimeStr = useCallback((t) => {
        if (!t) return '';
        return String(t).trim().toLowerCase().replace(/\s+/g, '').replace(/^0/, '');
    }, []);

    // Reminders / Follow-ups Computation
    const isReminderOverdue = useCallback((r) => {
        if (!r.followUpDate || r.status === 'Completed' || r.status === 'Cancelled') return false;
        const now = new Date();
        const todayYMD = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const dateStr = normYMD(r.followUpDate);
        return dateStr < todayYMD;
    }, [normYMD]);

    const isReminderToday = useCallback((r) => {
        if (!r.followUpDate) return false;
        const now = new Date();
        const todayYMD = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const dateStr = normYMD(r.followUpDate);
        return dateStr === todayYMD;
    }, [normYMD]);

    const allReminders = useMemo(() => {
        const list = [];
        const seen = new Set();
        const seenSignatures = new Set();

        const userId = (user?._id || user?.id || '').toString();
        const userFullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim().toLowerCase();
        const userEmail = (user?.email || '').trim().toLowerCase();
        const userOfficialEmail = (user?.officialEmail || '').trim().toLowerCase();
        const username = (user?.username || '').trim().toLowerCase();

        // Check if a reminder/activity belongs to the user
        // Admin sees ALL reminders.
        // Regular users see ONLY their own reminders:
        //  - Reminders they personally created (act.createdBy === userId || act.author === user)
        //  - Reminders assigned to them (act.assignedTo === userId || act.assignedToName === user)
        //  - Reminders on deals/contacts assigned to or owned by them (entity.assignedTo === userId || entity.salesOwner/owner === user)
        const isReminderBelongingToUser = (act, entity, entityType) => {
            if (isAdmin) return true;
            if (!user) return false;

            // 1. Created by this user
            const actCreatedById = (act?.createdBy?._id || act?.createdBy || '').toString();
            if (userId && actCreatedById && actCreatedById === userId) return true;

            // 2. Author matches this user
            const author = (act?.author || '').trim().toLowerCase();
            if (userFullName && author === userFullName) return true;
            if (userEmail && author === userEmail) return true;
            if (userOfficialEmail && author === userOfficialEmail) return true;
            if (username && author === username) return true;

            // 3. Reminder explicitly assigned to this user / selected member
            const actAssignedId = (act?.memberId || act?.assignedTo?._id || act?.assignedTo || act?.member || '').toString();
            if (userId && actAssignedId && actAssignedId === userId) return true;
            const actAssignedName = (act?.memberName || act?.assignedToName || act?.assignee || '').trim().toLowerCase();
            if (userFullName && actAssignedName === userFullName) return true;
            if (userEmail && actAssignedName === userEmail) return true;
            if (userOfficialEmail && actAssignedName === userOfficialEmail) return true;
            if (username && actAssignedName === username) return true;

            // 4. Deal or Contact is assigned to or owned by this user
            if (entity) {
                const entityAssignedId = (entity.assignedTo?._id || entity.assignedTo || '').toString();
                const entityCreatedId = (entity.createdBy?._id || entity.createdBy || '').toString();
                if (userId && (entityAssignedId === userId || entityCreatedId === userId)) return true;

                if (entityType === 'deal') {
                    const salesOwner = (entity.salesOwner || '').trim().toLowerCase();
                    const addedBy = (entity.addedBy || '').trim().toLowerCase();
                    const email = (entity.email || '').trim().toLowerCase();
                    if (userFullName && (salesOwner === userFullName || addedBy === userFullName)) return true;
                    if (userEmail && (salesOwner === userEmail || addedBy === userEmail || email === userEmail)) return true;
                    if (userOfficialEmail && (salesOwner === userOfficialEmail || email === userOfficialEmail)) return true;
                } else if (entityType === 'contact') {
                    const owner = (entity.owner || '').trim().toLowerCase();
                    const addedBy = (entity.addedBy || '').trim().toLowerCase();
                    const email = (entity.email || '').trim().toLowerCase();
                    if (userFullName && (owner === userFullName || addedBy === userFullName)) return true;
                    if (userEmail && (owner === userEmail || addedBy === userEmail || email === userEmail)) return true;
                    if (userOfficialEmail && (owner === userOfficialEmail || email === userOfficialEmail)) return true;
                }
            }

            return false;
        };

        // From Deals
        deals.forEach(d => {
            const dDate = getItemCreationDate(d);
            if (isAdmin) {
                if (adminDataViewMode === 'new' && dDate < DATA_CUTOFF_DATE) return;
                if (adminDataViewMode === 'old' && dDate >= DATA_CUTOFF_DATE) return;
            } else {
                if (dDate < DATA_CUTOFF_DATE) return;
            }

            const backend = Array.isArray(d.activities) ? d.activities : [];
            const custom = (dealActivities[d._id] || []).filter(cAct => {
                const cId = cAct._id ? String(cAct._id) : (cAct.id ? String(cAct.id) : '');
                if (cId && backend.some(b => String(b._id || b.id || '') === cId)) return false;
                const cNormChannel = (cAct.channel || '').trim().toLowerCase();
                const cNormDate = normYMD(cAct.followUpDate || cAct.date);
                const cNormTime = normTimeStr(cAct.followUpTime || cAct.time);
                const cNormTitle = (cAct.title || '').trim().toLowerCase();
                const cNormRemarks = (cAct.remarks || cAct.content || '').trim().toLowerCase();
                if (backend.some(b => {
                    const bId = String(b._id || b.id || '');
                    if (cId && bId && (bId === cId || bId.includes(cId) || cId.includes(bId))) return true;
                    const bNormChannel = (b.channel || '').trim().toLowerCase();
                    const bNormDate = normYMD(b.followUpDate || b.date);
                    const bNormTime = normTimeStr(b.followUpTime || b.time);
                    const bNormTitle = (b.title || '').trim().toLowerCase();
                    const bNormRemarks = (b.remarks || b.content || '').trim().toLowerCase();
                    if (cNormChannel === bNormChannel) {
                        if (cNormDate && bNormDate && cNormDate === bNormDate && cNormTime === bNormTime) return true;
                        if (cNormRemarks && bNormRemarks && cNormRemarks === bNormRemarks) return true;
                        if (cNormTitle && bNormTitle && cNormTitle === bNormTitle) return true;
                    }
                    return false;
                })) {
                    return false;
                }
                return true;
            });
            const combined = [...backend, ...custom];

            // Resolve associated contact details for this deal
            const assocContact = contacts.find(c =>
                (c._id && d.associatedContactId && String(c._id) === String(d.associatedContactId)) ||
                (d.associatedContact && `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase() === d.associatedContact.trim().toLowerCase()) ||
                (d.clientName && `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase() === d.clientName.trim().toLowerCase()) ||
                (c.email && d.email && c.email.toLowerCase() === d.email.toLowerCase())
            );

            const contactName = assocContact
                ? `${assocContact.firstName || ''} ${assocContact.lastName || ''}`.trim()
                : (d.associatedContact || d.clientName || '');
            const contactEmail = assocContact?.email || d.email || d.clientEmail || d.contactEmail || '';
            const contactPhone = assocContact?.phone || assocContact?.mobile || d.phone || d.clientPhone || d.contactPhone || '';
            const contactId = assocContact?._id || d.associatedContactId || null;

            combined.forEach(act => {
                if (!act || act.type !== 'followup') return;
                if (!isReminderBelongingToUser(act, d, 'deal')) return;

                const idKey = act._id ? String(act._id) : (act.id ? String(act.id) : '');
                const normTitle = (act.title || '').trim().toLowerCase();
                const normChannel = (act.channel || '').trim().toLowerCase();
                const normRemarks = (act.remarks || act.content || '').trim().toLowerCase();
                const actNormDate = normYMD(act.followUpDate || act.date);
                const actNormTime = normTimeStr(act.followUpTime || act.time);

                // When title or remarks exist, collapse any duplicate or updated copies for the same deal touchpoint
                const signature = (normTitle || normRemarks)
                    ? `deal-${d._id}-${normTitle}-${normChannel}-${normRemarks}`
                    : `deal-${d._id}-${normChannel}-${actNormDate}-${actNormTime}`;

                if ((idKey && seen.has(idKey)) || seenSignatures.has(signature)) return;
                if (idKey) seen.add(idKey);
                seenSignatures.add(signature);

                list.push({
                    ...act,
                    _id: act._id || act.id || idKey || `deal-act-${d._id}-${signature}`,
                    id: act.id || act._id || idKey || `deal-act-${d._id}-${signature}`,
                    entityType: 'deal',
                    entityId: d._id,
                    entityName: d.dealName || d.clientName || 'Untitled Deal',
                    dealName: d.dealName || d.clientName || '',
                    clientName: d.clientName || '',
                    dealStage: d.stage || '',
                    owner: d.salesOwner || d.addedBy || '',
                    contactName: contactName,
                    contactEmail: contactEmail,
                    contactPhone: contactPhone,
                    contactId: contactId,
                    status: act.status || 'Scheduled',
                    channel: act.channel || 'Call',
                    followUpDate: act.followUpDate || act.date || '',
                    followUpTime: act.followUpTime || act.time || '',
                    nextFollowUpDate: act.nextFollowUpDate || '',
                    remarks: act.remarks || act.content || '',
                    author: act.author || d.salesOwner || 'Admin System',
                    memberId: act.memberId || act.assignedTo?._id || act.assignedTo || '',
                    memberName: act.memberName || act.assignedToName || '',
                    assignedTo: act.assignedTo || act.memberId || '',
                    assignedToName: act.assignedToName || act.memberName || ''
                });
            });
        });

        // From Contacts
        contacts.forEach(c => {
            const cDate = getItemCreationDate(c);
            if (isAdmin) {
                if (adminDataViewMode === 'new' && cDate < DATA_CUTOFF_DATE) return;
                if (adminDataViewMode === 'old' && cDate >= DATA_CUTOFF_DATE) return;
            } else {
                if (cDate < DATA_CUTOFF_DATE) return;
            }

            const backend = Array.isArray(c.activities) ? c.activities : [];
            const custom = (dealActivities[c._id] || []).filter(cAct => {
                const cId = cAct._id ? String(cAct._id) : (cAct.id ? String(cAct.id) : '');
                if (cId && backend.some(b => String(b._id || b.id || '') === cId)) return false;
                const cNormChannel = (cAct.channel || '').trim().toLowerCase();
                const cNormDate = normYMD(cAct.followUpDate || cAct.date);
                const cNormTime = normTimeStr(cAct.followUpTime || cAct.time);
                const cNormTitle = (cAct.title || '').trim().toLowerCase();
                const cNormRemarks = (cAct.remarks || cAct.content || '').trim().toLowerCase();
                if (backend.some(b => {
                    const bId = String(b._id || b.id || '');
                    if (cId && bId && (bId === cId || bId.includes(cId) || cId.includes(bId))) return true;
                    const bNormChannel = (b.channel || '').trim().toLowerCase();
                    const bNormDate = normYMD(b.followUpDate || b.date);
                    const bNormTime = normTimeStr(b.followUpTime || b.time);
                    const bNormTitle = (b.title || '').trim().toLowerCase();
                    const bNormRemarks = (b.remarks || b.content || '').trim().toLowerCase();
                    if (cNormChannel === bNormChannel) {
                        if (cNormDate && bNormDate && cNormDate === bNormDate && cNormTime === bNormTime) return true;
                        if (cNormRemarks && bNormRemarks && cNormRemarks === bNormRemarks) return true;
                        if (cNormTitle && bNormTitle && cNormTitle === bNormTitle) return true;
                    }
                    return false;
                })) {
                    return false;
                }
                return true;
            });
            const combined = [...backend, ...custom];

            const contactName = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Untitled Contact';
            const contactEmail = c.email || '';
            const contactPhone = c.phone || c.mobile || '';

            combined.forEach(act => {
                if (!act || act.type !== 'followup') return;
                if (!isReminderBelongingToUser(act, c, 'contact')) return;

                const idKey = act._id ? String(act._id) : (act.id ? String(act.id) : '');
                const normTitle = (act.title || '').trim().toLowerCase();
                const normChannel = (act.channel || '').trim().toLowerCase();
                const normRemarks = (act.remarks || act.content || '').trim().toLowerCase();
                const actNormDate = normYMD(act.followUpDate || act.date);
                const actNormTime = normTimeStr(act.followUpTime || act.time);

                const signature = (normTitle || normRemarks)
                    ? `contact-${c._id}-${normTitle}-${normChannel}-${normRemarks}`
                    : `contact-${c._id}-${normChannel}-${actNormDate}-${actNormTime}`;

                if ((idKey && seen.has(idKey)) || seenSignatures.has(signature)) return;
                if (idKey) seen.add(idKey);
                seenSignatures.add(signature);

                list.push({
                    ...act,
                    _id: act._id || act.id || idKey || `contact-act-${c._id}-${signature}`,
                    id: act.id || act._id || idKey || `contact-act-${c._id}-${signature}`,
                    entityType: 'contact',
                    entityId: c._id,
                    entityName: contactName,
                    contactName: contactName,
                    contactStage: c.leadStage || '',
                    owner: c.owner || c.addedBy || '',
                    contactEmail: contactEmail,
                    contactPhone: contactPhone,
                    contactId: c._id,
                    status: act.status || 'Scheduled',
                    channel: act.channel || 'Call',
                    followUpDate: act.followUpDate || act.date || '',
                    followUpTime: act.followUpTime || act.time || '',
                    nextFollowUpDate: act.nextFollowUpDate || '',
                    remarks: act.remarks || act.content || '',
                    author: act.author || c.owner || 'Admin System',
                    memberId: act.memberId || act.assignedTo?._id || act.assignedTo || '',
                    memberName: act.memberName || act.assignedToName || '',
                    assignedTo: act.assignedTo || act.memberId || '',
                    assignedToName: act.assignedToName || act.memberName || ''
                });
            });
        });

        return list;
    }, [deals, contacts, dealActivities, isAdmin, user, adminDataViewMode, normYMD, normTimeStr]);

    const activeRemindersCount = useMemo(() => {
        return allReminders.length;
    }, [allReminders]);

    const notifiedRemindersRef = useRef(new Set());

    // Real-time follow-up due alert: checks every 5 seconds so notifications pop up instantly at the scheduled minute
    useEffect(() => {
        const checkDueReminders = () => {
            if (!Array.isArray(allReminders) || allReminders.length === 0 || !setActiveToast) return;

            const now = new Date();
            const todayStr = formatDateDDMMYYYY(now);
            const nowH = now.getHours();
            const nowM = now.getMinutes();
            const nowTotalMin = nowH * 60 + nowM;

            allReminders.forEach(reminder => {
                if (reminder.status === 'Completed' || reminder.status === 'Missed') return;
                const rDate = reminder.followUpDate ? formatDateDDMMYYYY(reminder.followUpDate) : '';
                if (rDate !== todayStr) return;

                const rTimeStr = reminder.followUpTime || '';
                if (!rTimeStr) return;

                const ampmMatch = rTimeStr.match(/^(\d{1,2})[:.](\d{2})\s*(AM|PM)?$/i);
                if (!ampmMatch) return;

                let rH = parseInt(ampmMatch[1], 10);
                const rM = parseInt(ampmMatch[2], 10);
                const period = ampmMatch[3] ? ampmMatch[3].toUpperCase() : null;
                if (period === 'PM' && rH !== 12) rH += 12;
                if (period === 'AM' && rH === 12) rH = 0;

                const rTotalMin = rH * 60 + rM;

                // If reminder is due right now (within current minute or up to 2 min overdue)
                if (nowTotalMin >= rTotalMin && nowTotalMin <= rTotalMin + 2) {
                    const reminderKey = `${reminder._id || reminder.id}-${todayStr}-${rTimeStr}`;
                    if (notifiedRemindersRef.current.has(reminderKey)) return;
                    notifiedRemindersRef.current.add(reminderKey);

                    const entityName = reminder.dealName || reminder.clientName || reminder.contactName || 'Client';
                    const channel = reminder.channel || 'Follow-up';

                    setActiveToast({
                        _id: `due-toast-${Date.now()}`,
                        type: 'Follow-up',
                        title: `Follow-up Due: ${channel} with ${entityName}`,
                        message: `Your scheduled ${channel} follow-up for "${entityName}" is due now (${reminder.followUpTime}). Purpose: ${reminder.title || 'Touchpoint'}${reminder.remarks ? ` | Notes: "${reminder.remarks}"` : ''}`,
                        link: (reminder.entityType === 'contact' && isAdmin)
                            ? `/pipeline/crm/overview?ref=sidebar&tab=contacts&view=list&detailTab=followups&stage=all`
                            : `/pipeline/crm/overview?ref=sidebar&tab=deals&view=kanban&detailTab=followups&stage=all`
                    });

                    // Play notification audio chime
                    try {
                        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0 || navigator.userAgent.toUpperCase().indexOf('MAC') >= 0;
                        const soundUrl = isMac ? '/sounds/that-was-quick-606.m4r' : '/sounds/that-was-quick-606.mp3';
                        const audio = new Audio(soundUrl);
                        audio.play().catch(() => { });
                    } catch (e) { }

                    // Native Desktop Notification
                    if ("Notification" in window && Notification.permission === "granted") {
                        try {
                            new Notification(`Follow-up Due: ${channel} with ${entityName}`, {
                                body: `Scheduled touchpoint is due now (${reminder.followUpTime}).`
                            });
                        } catch (e) { }
                    }
                }
            });
        };

        checkDueReminders();
        const interval = setInterval(checkDueReminders, 5000);
        return () => clearInterval(interval);
    }, [allReminders, setActiveToast]);

    const filteredReminders = useMemo(() => {
        return allReminders
            .filter(r => {
                if (reminderSearchQuery.trim()) {
                    const q = reminderSearchQuery.toLowerCase();
                    const title = (r.title || '').toLowerCase();
                    const remarks = (r.remarks || '').toLowerCase();
                    const entity = (r.entityName || '').toLowerCase();
                    const channel = (r.channel || '').toLowerCase();
                    const author = (r.author || '').toLowerCase();
                    const dateStr = (r.followUpDate || '').toLowerCase();
                    if (!title.includes(q) && !remarks.includes(q) && !entity.includes(q) && !channel.includes(q) && !author.includes(q) && !dateStr.includes(q)) {
                        return false;
                    }
                }
                if (reminderStatusFilter !== 'all') {
                    if (r.status !== reminderStatusFilter) return false;
                }
                if (reminderChannelFilter !== 'all') {
                    if ((r.channel || '').toLowerCase() !== reminderChannelFilter.toLowerCase()) return false;
                }
                if (reminderTimingFilter !== 'all') {
                    if (reminderTimingFilter === 'today' && !isReminderToday(r)) return false;
                    if (reminderTimingFilter === 'overdue' && !isReminderOverdue(r)) return false;
                    if (reminderTimingFilter === 'upcoming') {
                        if (r.status === 'Completed' || isReminderOverdue(r)) return false;
                    }
                }
                return true;
            })
            .sort((a, b) => {
                const da = a.followUpDate || '9999';
                const db = b.followUpDate || '9999';
                if (reminderSortBy === 'date_asc') {
                    return da.localeCompare(db);
                }
                return db.localeCompare(da);
            });
    }, [allReminders, reminderSearchQuery, reminderStatusFilter, reminderChannelFilter, reminderTimingFilter, reminderSortBy, isReminderToday, isReminderOverdue]);

    const currentDealActivities = useMemo(() => {
        if (!detailedDeal) return [];
        const custom = dealActivities[detailedDeal._id] || [];
        const backendActivities = Array.isArray(detailedDeal.activities) ? detailedDeal.activities : [];

        // Synthesize tasks associated with this deal into activity timeline items
        const taskActs = dealSpecificTasks.map(t => {
            const taskDateStr = t.dueDate || t.createdAt || new Date();
            let taskTime = '12:00 PM';
            try {
                const dt = new Date(t.createdAt || new Date());
                if (!isNaN(dt.getTime())) {
                    taskTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                }
            } catch (e) { }
            return {
                id: `task-act-${t._id}`,
                type: 'task',
                author: t.assignee || detailedDeal.salesOwner || 'Sales Specialist',
                title: `Task: ${t.title}`,
                content: `${t.completed ? 'Completed' : (t.status || 'Pending')} task • Due: ${t.dueDate ? formatDateDDMMYYYY(t.dueDate) : 'No due date'} • Priority: ${t.priority || 'Medium'}${t.description ? ` • ${t.description}` : ''}`,
                time: taskTime,
                date: formatDateDDMMYYYY(taskDateStr),
                createdAt: t.createdAt || new Date()
            };
        });

        // Baseline system events for this deal if not already present
        const baseActs = [];
        const hasDealCreated = [...custom, ...backendActivities].some(a => a.type === 'deal' && (a.title === 'Deal Created' || (a.title && a.title.toLowerCase().includes('created'))));
        if (!hasDealCreated) {
            const createDateStr = detailedDeal.createdAt || detailedDeal.createdDate || detailedDeal.date || new Date();
            const formattedDate = formatDateDDMMYYYY(createDateStr);
            let formattedTime = '01:05 PM';
            try {
                const dt = new Date(createDateStr);
                if (!isNaN(dt.getTime())) {
                    formattedTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                }
            } catch (e) { }

            baseActs.push({
                id: `deal-created-${detailedDeal._id}`,
                type: 'deal',
                title: 'Deal Created',
                author: detailedDeal.addedBy || 'Admin System',
                content: `Created deal "${detailedDeal.dealName || detailedDeal.clientName}" in stage "${detailedDeal.stage || 'New'}" with value ${detailedDeal.currency || '₹'} ${Number(detailedDeal.dealValue || 0).toLocaleString()}. Assigned to ${detailedDeal.salesOwner || 'Sales Specialist'}.`,
                time: formattedTime,
                date: formattedDate,
                createdAt: createDateStr
            });
        }

        // Baseline contact association if present and not already logged
        const contactEmail = detailedDeal.associatedContact || detailedDeal.email;
        const hasContactLinked = [...custom, ...backendActivities].some(a => a.type === 'contact' || (a.title && a.title.toLowerCase().includes('contact')));
        if (contactEmail && !hasContactLinked) {
            const linkDateStr = detailedDeal.createdAt || new Date();
            let linkTime = '01:05 PM';
            try {
                const dt = new Date(linkDateStr);
                if (!isNaN(dt.getTime())) {
                    linkTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                }
            } catch (e) { }

            baseActs.push({
                id: `contact-linked-${detailedDeal._id}`,
                type: 'contact',
                title: 'Associated Contact Linked',
                author: detailedDeal.addedBy || 'Admin System',
                content: `Linked contact ${contactEmail} with this deal.`,
                time: linkTime,
                date: formatDateDDMMYYYY(linkDateStr),
                createdAt: linkDateStr
            });
        }

        // Baseline company association if present and not already logged
        const companyName = detailedDeal.associatedCompany || (detailedDeal.company && detailedDeal.company !== 'Direct Client' ? detailedDeal.company : '');
        const hasCompanyLinked = [...custom, ...backendActivities].some(a => a.type === 'company' || (a.title && a.title.toLowerCase().includes('company')));
        if (companyName && !hasCompanyLinked) {
            const linkDateStr = detailedDeal.createdAt || new Date();
            let linkTime = '01:05 PM';
            try {
                const dt = new Date(linkDateStr);
                if (!isNaN(dt.getTime())) {
                    linkTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                }
            } catch (e) { }

            baseActs.push({
                id: `company-linked-${detailedDeal._id}`,
                type: 'company',
                title: 'Associated Company Linked',
                author: detailedDeal.addedBy || 'Admin System',
                content: `Linked company "${companyName}" with this deal.`,
                time: linkTime,
                date: formatDateDDMMYYYY(linkDateStr),
                createdAt: linkDateStr
            });
        }

        // Synthesize payment records associated with this deal into activity timeline items
        const paymentActs = getDealPaymentRecords(detailedDeal).map((p, pIdx) => {
            const pDateStr = p.recordedOnDate || p.paymentDate || p.createdAt || new Date();
            let pTime = p.recordedOnTime || '12:00 PM';
            try {
                if (!p.recordedOnTime && p.createdAt) {
                    const dt = new Date(p.createdAt);
                    if (!isNaN(dt.getTime())) {
                        pTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                    }
                }
            } catch (e) { }
            const pMode = p.paymentMode || 'Bank Transfer';
            const pStatus = p.paymentStatus || 'Paid';
            const pAmount = p.paidAmount || '₹0';
            const authorName = (p.createdBy && (p.createdBy.firstName || p.createdBy.name))
                ? `${p.createdBy.firstName || ''} ${p.createdBy.lastName || ''}`.trim()
                : (detailedDeal.addedBy || detailedDeal.salesOwner || (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System');

            return {
                id: `payment-act-${p._id || p.id || pIdx}`,
                type: 'payment',
                author: authorName,
                title: 'Payment Recorded',
                content: `Payment of ${pAmount} recorded via ${pMode}. Status: ${pStatus}.${p.attachmentName ? ` (Attachment: ${p.attachmentName})` : ''}`,
                time: pTime,
                date: p.paymentDate || (typeof pDateStr === 'string' && pDateStr.includes('-') ? pDateStr : formatDateDDMMYYYY(pDateStr)),
                createdAt: p.createdAt || new Date(),
                attachmentName: p.attachmentName || '',
                attachmentUrl: p.attachmentUrl || ''
            };
        });

        // Synthesize notes from detailedDeal.notes or detailedDeal.relatedLead?.notes if not already in activities
        const rawDealNotes = (detailedDeal.notes || detailedDeal.relatedLead?.notes || '').trim();
        const leadNoteActs = [];
        if (rawDealNotes) {
            const pattern = /\[(\d{1,2}\s+[A-Za-z]{3}\s+\d{4}(?:\s+\d{1,2}:\d{2}(?:\s*(?:AM|PM|am|pm))?)?)\s*-\s*([^\]]+)\]:\s*([\s\S]*?)(?=(?:\[\d{1,2}\s+[A-Za-z]{3}\s+\d{4})|$)/g;
            let match;
            let matchedAny = false;
            while ((match = pattern.exec(rawDealNotes)) !== null) {
                matchedAny = true;
                const timeStr = match[1].trim();
                const noteAuthor = match[2].trim() || 'Admin System';
                const noteContent = match[3].trim();
                if (noteContent && !backendActivities.some(b => (b.content || '').trim() === noteContent.trim())) {
                    const d = new Date(timeStr);
                    const isValidDate = !isNaN(d.getTime());
                    leadNoteActs.push({
                        id: `lead-note-${detailedDeal._id}-${leadNoteActs.length}`,
                        type: 'note',
                        title: 'Lead Note',
                        content: noteContent,
                        author: noteAuthor,
                        time: isValidDate ? d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : timeStr,
                        date: isValidDate ? formatDateDDMMYYYY(d) : timeStr.slice(0, 11),
                        createdAt: isValidDate ? d : new Date()
                    });
                }
            }
            if (!matchedAny && rawDealNotes.trim() && !rawDealNotes.startsWith('Moved from') && !rawDealNotes.startsWith('Lead updated')) {
                if (!backendActivities.some(b => (b.content || '').trim() === rawDealNotes.trim())) {
                    leadNoteActs.push({
                        id: `lead-note-${detailedDeal._id}-single`,
                        type: 'note',
                        title: 'Lead Note',
                        content: rawDealNotes.trim(),
                        author: detailedDeal.addedBy || 'Admin System',
                        time: '12:00 PM',
                        date: formatDateDDMMYYYY(detailedDeal.createdAt || new Date()),
                        createdAt: detailedDeal.createdAt || new Date()
                    });
                }
            }
        }

        // Also check detailedDeal.relatedLead?.history for any notes
        if (Array.isArray(detailedDeal.relatedLead?.history)) {
            detailedDeal.relatedLead.history.forEach((h, hIdx) => {
                if (h && (h.action === 'Note Added' || h.action === 'Note') && h.details) {
                    const content = String(h.details).trim();
                    if (content && !backendActivities.some(b => (b.content || '').trim() === content) && !leadNoteActs.some(a => a.content === content)) {
                        const hDate = h.date ? new Date(h.date) : new Date();
                        const author = (h.by && (h.by.firstName ? `${h.by.firstName} ${h.by.lastName || ''}`.trim() : h.by.name)) || 'Admin System';
                        leadNoteActs.push({
                            id: `lead-hist-note-${detailedDeal._id}-${hIdx}`,
                            type: 'note',
                            title: 'Lead Note',
                            content,
                            author,
                            time: hDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
                            date: formatDateDDMMYYYY(hDate),
                            createdAt: hDate
                        });
                    }
                }
            });
        }

        const all = [...custom, ...backendActivities, ...taskActs, ...paymentActs, ...baseActs, ...leadNoteActs];
        const seen = new Set();
        const unique = all.filter(a => {
            if (!a) return false;
            // Role-based visibility isolation:
            // Sales Executives can ONLY see follow-ups they personally created.
            // Admin, Sales Head, and Sales Manager can see ALL follow-ups.
            if (!canUserViewActivity(a, user, hasFullAccess)) return false;

            const contentKey = a.type === 'note'
                ? `note-${(a.content || '').trim()}`
                : `${a.type || ''}-${(a.title || '').trim()}-${(a.content || a.remarks || '').trim()}`;
            if (seen.has(contentKey)) return false;
            seen.add(contentKey);
            return true;
        });

        const typePriority = {
            payment: 11,
            task: 10,
            deal: 9,
            followup: 8.5,
            note: 8,
            email: 7,
            message: 6,
            company: 5,
            activity: 4,
            contact: 1
        };

        return unique.sort((a, b) => {
            const ta = parseActivityTimestamp(a);
            const tb = parseActivityTimestamp(b);
            if (tb !== ta) {
                return tb - ta;
            }
            const pA = typePriority[a.type] || (a.title && a.title.includes('Company') ? 5 : 4);
            const pB = typePriority[b.type] || (b.title && b.title.includes('Company') ? 5 : 4);
            return pB - pA;
        });
    }, [detailedDeal, dealActivities, dealSpecificTasks, getDealPaymentRecords, user, hasFullAccess]);

    const matchedDealsForContact = useMemo(() => {
        if (!detailedContact) return [];
        const cEmail = (detailedContact.email || '').trim().toLowerCase();
        const cFullName = `${detailedContact.firstName || ''} ${detailedContact.lastName || ''}`.trim().toLowerCase();
        const cPhoneClean = (detailedContact.phone || '').replace(/\D/g, '');

        const found = deals.filter(d => {
            if (!d) return false;
            const dAssocContact = (d.associatedContact || '').trim().toLowerCase();
            const dEmail = (d.email || '').trim().toLowerCase();
            const dClientName = (d.clientName || '').trim().toLowerCase();
            const dDealName = (d.dealName || '').trim().toLowerCase();
            const dPhoneClean = (d.phone || '').replace(/\D/g, '');

            const matchesEmail = cEmail && (dAssocContact === cEmail || dEmail === cEmail || dAssocContact.includes(cEmail) || dEmail.includes(cEmail));
            const matchesName = cFullName && (dClientName === cFullName || dAssocContact === cFullName || dDealName.includes(cFullName) || dClientName.includes(cFullName) || cFullName.includes(dClientName));
            const matchesPhone = cPhoneClean && cPhoneClean.length >= 7 && dPhoneClean && (dPhoneClean.endsWith(cPhoneClean) || cPhoneClean.endsWith(dPhoneClean));
            const matchesDirectId = Array.isArray(detailedContact.associatedDeals) && detailedContact.associatedDeals.includes(d._id);

            return matchesEmail || matchesName || matchesPhone || matchesDirectId;
        });

        const seen = new Set();
        return found.filter(d => {
            if (seen.has(d._id)) return false;
            seen.add(d._id);
            return true;
        });
    }, [deals, detailedContact]);

    const matchedDealForContact = useMemo(() => {
        return matchedDealsForContact[0] || null;
    }, [matchedDealsForContact]);

    const currentContactActivities = useMemo(() => {
        if (!detailedContact) return [];
        const custom = dealActivities[detailedContact._id] || [];
        const backendActivities = Array.isArray(detailedContact.activities) ? detailedContact.activities : [];

        // Synthesize contact-specific tasks into activity timeline items
        const taskActs = contactSpecificTasks.map(t => {
            const taskDateStr = t.dueDate || t.createdAt || new Date();
            let taskTime = '12:00 PM';
            try {
                const dt = new Date(t.createdAt || new Date());
                if (!isNaN(dt.getTime())) {
                    taskTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                }
            } catch (e) { }
            return {
                id: `task-contact-act-${t._id}`,
                type: 'task',
                author: t.assignee || detailedContact.owner || 'Sales Specialist',
                title: `Task: ${t.title}`,
                content: `${t.completed ? 'Completed' : (t.status || 'Pending')} task • Due: ${t.dueDate ? formatDateDDMMYYYY(t.dueDate) : 'No due date'} • Priority: ${t.priority || 'Medium'}${t.description ? ` • ${t.description}` : ''}`,
                time: taskTime,
                date: formatDateDDMMYYYY(taskDateStr),
                createdAt: t.createdAt || new Date()
            };
        });

        // Baseline contact created event
        const baseActs = [];
        const hasContactCreated = [...custom, ...backendActivities].some(a => a.type === 'contact' && (a.title === 'Contact Created' || (a.title && a.title.toLowerCase().includes('created'))));
        if (!hasContactCreated) {
            const createDateStr = detailedContact.createdAt || detailedContact.createdDate || new Date();
            let formattedTime = '01:05 PM';
            try {
                const dt = new Date(createDateStr);
                if (!isNaN(dt.getTime())) {
                    formattedTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                }
            } catch (e) { }

            baseActs.push({
                id: `contact-created-${detailedContact._id}`,
                type: 'contact',
                title: 'Contact Created',
                author: detailedContact.addedBy || detailedContact.owner || 'Admin System',
                content: `Contact "${detailedContact.firstName} ${detailedContact.lastName}" created with status "${detailedContact.leadStatus || 'New'}" in stage "${detailedContact.leadStage || 'Lead'}".`,
                time: formattedTime,
                date: formatDateDDMMYYYY(createDateStr),
                createdAt: createDateStr,
                timestamp: parseActivityTimestamp({ date: formatDateDDMMYYYY(createDateStr), time: formattedTime, createdAt: createDateStr })
            });
        }

        const isRealCompany = (companies || []).some(c => c && c.name && c.name.trim().toLowerCase() === detailedContact.company.trim().toLowerCase());
        if (detailedContact.company && detailedContact.company !== 'Direct Client' && isRealCompany) {
            const hasCompanyLinked = [...custom, ...backendActivities].some(a => a.type === 'company' || (a.title && a.title.toLowerCase().includes('company')));
            if (!hasCompanyLinked) {
                const linkDateStr = detailedContact.createdAt || new Date();
                let linkTime = '01:05 PM';
                try {
                    const dt = new Date(linkDateStr);
                    if (!isNaN(dt.getTime())) {
                        linkTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                    }
                } catch (e) { }

                baseActs.push({
                    id: `company-contact-linked-${detailedContact._id}`,
                    type: 'company',
                    title: 'Company Registered & Linked',
                    author: detailedContact.addedBy || detailedContact.owner || 'Admin System',
                    content: `Company "${detailedContact.company}" registered and linked with contact.`,
                    time: linkTime,
                    date: formatDateDDMMYYYY(linkDateStr),
                    createdAt: linkDateStr,
                    timestamp: parseActivityTimestamp({ date: formatDateDDMMYYYY(linkDateStr), time: linkTime, createdAt: linkDateStr }) + 1000
                });
            }
        }

        // Baseline associated deals linked / created events
        (matchedDealsForContact || []).forEach(deal => {
            const dealTitle = deal.dealName || deal.clientName || 'Associated Deal';
            const hasDealAct = [...custom, ...backendActivities].some(a =>
                a.type === 'deal' &&
                (a.id === `deal-contact-linked-${deal._id}` || (a.content && a.content.includes(dealTitle)))
            );
            if (!hasDealAct) {
                const dealDateStr = deal.createdAt || deal.createdDate || deal.date || detailedContact.createdAt || new Date();
                let dealTime = '01:10 PM';
                try {
                    const dt = new Date(dealDateStr);
                    if (!isNaN(dt.getTime())) {
                        dealTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                    }
                } catch (e) { }

                baseActs.push({
                    id: `deal-contact-linked-${deal._id}`,
                    type: 'deal',
                    title: 'Deal Registered & Linked',
                    author: deal.salesOwner || deal.addedBy || detailedContact.addedBy || detailedContact.owner || 'Sales Specialist',
                    content: `Deal "${dealTitle}" (${deal.currency || '₹'}${Number(deal.dealValue || 0).toLocaleString()} • ${deal.stage || 'New'}) registered and linked with contact.`,
                    time: dealTime,
                    date: formatDateDDMMYYYY(dealDateStr),
                    createdAt: dealDateStr,
                    timestamp: parseActivityTimestamp({ date: formatDateDDMMYYYY(dealDateStr), time: dealTime, createdAt: dealDateStr }) + 2000
                });
            }
        });

        // Synthesize payment records associated with this contact into activity timeline items
        const paymentActs = getContactPaymentRecords(detailedContact).map((p, pIdx) => {
            const pDateStr = p.recordedOnDate || p.paymentDate || p.createdAt || new Date();
            let pTime = p.recordedOnTime || '12:00 PM';
            try {
                if (!p.recordedOnTime && p.createdAt) {
                    const dt = new Date(p.createdAt);
                    if (!isNaN(dt.getTime())) {
                        pTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                    }
                }
            } catch (e) { }
            const pMode = p.paymentMode || 'Bank Transfer';
            const pStatus = p.paymentStatus || 'Paid';
            const pAmount = p.paidAmount || '₹0';
            const authorName = (p.createdBy && (p.createdBy.firstName || p.createdBy.name))
                ? `${p.createdBy.firstName || ''} ${p.createdBy.lastName || ''}`.trim()
                : (detailedContact.addedBy || detailedContact.owner || (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System');

            return {
                id: `payment-contact-act-${p._id || p.id || pIdx}`,
                type: 'payment',
                author: authorName,
                title: 'Payment Recorded',
                content: `Payment of ${pAmount} recorded via ${pMode}. Status: ${pStatus}.${p.attachmentName ? ` (Attachment: ${p.attachmentName})` : ''}`,
                time: pTime,
                date: p.paymentDate || (typeof pDateStr === 'string' && pDateStr.includes('-') ? pDateStr : formatDateDDMMYYYY(pDateStr)),
                createdAt: p.createdAt || new Date(),
                attachmentName: p.attachmentName || '',
                attachmentUrl: p.attachmentUrl || ''
            };
        });

        const all = [...custom, ...backendActivities, ...taskActs, ...paymentActs, ...baseActs];
        const seen = new Set();
        const unique = all.filter(a => {
            if (!a) return false;
            // Role-based visibility isolation:
            // Sales Executives can ONLY see follow-ups they personally created.
            // Admin, Sales Head, and Sales Manager can see ALL follow-ups.
            if (!canUserViewActivity(a, user, hasFullAccess)) return false;

            const contentKey = `${a.type || ''}-${(a.title || '').trim()}-${(a.content || a.remarks || '').trim()}-${(a.date || '').trim()}-${(a.time || '').trim()}`;
            if (seen.has(contentKey)) return false;
            seen.add(contentKey);
            return true;
        });

        const typePriority = {
            payment: 11,
            task: 10,
            deal: 9,
            followup: 8.5,
            note: 8,
            email: 7,
            message: 6,
            company: 5,
            activity: 4,
            contact: 1
        };

        return unique.sort((a, b) => {
            const ta = parseActivityTimestamp(a);
            const tb = parseActivityTimestamp(b);
            if (tb !== ta) {
                return tb - ta;
            }
            const pA = typePriority[a.type] || (a.title && a.title.includes('Company') ? 5 : 4);
            const pB = typePriority[b.type] || (b.title && b.title.includes('Company') ? 5 : 4);
            return pB - pA;
        });
    }, [detailedContact, dealActivities, contactSpecificTasks, matchedDealsForContact, getContactPaymentRecords, user, hasFullAccess]);

    // Dynamic activities for hovered deal / contact in Activity Popover
    const hoveredActivities = useMemo(() => {
        if (hoveredDeal) {
            const custom = dealActivities[hoveredDeal._id] || [];
            const backendActivities = Array.isArray(hoveredDeal.activities) ? hoveredDeal.activities : [];
            const dealTasks = tasks.filter(
                t =>
                    t.associatedDealId === hoveredDeal._id ||
                    (t.associatedDealName && (
                        t.associatedDealName.toLowerCase() === (hoveredDeal.dealName || '').toLowerCase() ||
                        t.associatedDealName.toLowerCase() === (hoveredDeal.clientName || '').toLowerCase()
                    ))
            );

            const taskActs = dealTasks.map(t => {
                const taskDateStr = t.dueDate || t.createdAt || new Date();
                let taskTime = '12:00 PM';
                try {
                    const dt = new Date(t.createdAt || new Date());
                    if (!isNaN(dt.getTime())) {
                        taskTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                    }
                } catch (e) { }
                return {
                    id: `task-act-${t._id}`,
                    type: 'task',
                    author: t.assignee || hoveredDeal.salesOwner || 'Sales Specialist',
                    title: `Task: ${t.title}`,
                    content: `${t.completed ? 'Completed' : (t.status || 'Pending')} task • Priority: ${t.priority || 'Medium'}${t.description ? ` • ${t.description}` : ''}`,
                    time: taskTime,
                    date: formatDateDDMMYYYY(taskDateStr),
                    createdAt: t.createdAt || new Date()
                };
            });

            const baseActs = [];
            const hasDealCreated = [...custom, ...backendActivities].some(a => a.type === 'deal' && (a.title === 'Deal Created' || (a.title && a.title.toLowerCase().includes('created'))));
            if (!hasDealCreated) {
                const createDateStr = hoveredDeal.createdAt || hoveredDeal.createdDate || hoveredDeal.date || new Date();
                const formattedDate = formatDateDDMMYYYY(createDateStr);
                let formattedTime = '01:05 PM';
                try {
                    const dt = new Date(createDateStr);
                    if (!isNaN(dt.getTime())) {
                        formattedTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                    }
                } catch (e) { }
                baseActs.push({
                    id: `deal-created-${hoveredDeal._id}`,
                    type: 'deal',
                    title: 'Deal Created',
                    author: hoveredDeal.addedBy || hoveredDeal.salesOwner || 'Admin System',
                    content: `Created deal "${hoveredDeal.dealName || hoveredDeal.clientName}" in stage "${hoveredDeal.stage || 'New'}" with value ${hoveredDeal.currency || '₹'} ${Number(hoveredDeal.dealValue || 0).toLocaleString()}.`,
                    time: formattedTime,
                    date: formattedDate,
                    createdAt: createDateStr
                });
            }

            const contactEmail = hoveredDeal.associatedContact || hoveredDeal.email;
            const hasContactLinked = [...custom, ...backendActivities].some(a => a.type === 'contact' || (a.title && a.title.toLowerCase().includes('contact')));
            if (contactEmail && !hasContactLinked) {
                const linkDateStr = hoveredDeal.createdAt || new Date();
                let linkTime = '01:05 PM';
                try {
                    const dt = new Date(linkDateStr);
                    if (!isNaN(dt.getTime())) {
                        linkTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                    }
                } catch (e) { }
                baseActs.push({
                    id: `contact-linked-${hoveredDeal._id}`,
                    type: 'contact',
                    title: 'Associated Contact Linked',
                    author: hoveredDeal.addedBy || 'Admin System',
                    content: `Linked contact ${contactEmail} with this deal.`,
                    time: linkTime,
                    date: formatDateDDMMYYYY(linkDateStr),
                    createdAt: linkDateStr
                });
            }

            const companyName = hoveredDeal.associatedCompany || (hoveredDeal.company && hoveredDeal.company !== 'Direct Client' ? hoveredDeal.company : '');
            const hasCompanyLinked = [...custom, ...backendActivities].some(a => a.type === 'company' || (a.title && a.title.toLowerCase().includes('company')));
            if (companyName && !hasCompanyLinked) {
                const linkDateStr = hoveredDeal.createdAt || new Date();
                let linkTime = '01:05 PM';
                try {
                    const dt = new Date(linkDateStr);
                    if (!isNaN(dt.getTime())) {
                        linkTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                    }
                } catch (e) { }
                baseActs.push({
                    id: `company-linked-${hoveredDeal._id}`,
                    type: 'company',
                    title: 'Associated Company Linked',
                    author: hoveredDeal.addedBy || 'Admin System',
                    content: `Linked company "${companyName}" with this deal.`,
                    time: linkTime,
                    date: formatDateDDMMYYYY(linkDateStr),
                    createdAt: linkDateStr
                });
            }

            // Synthesize payment records associated with this deal into activity timeline items
            const paymentActs = getDealPaymentRecords(hoveredDeal).map((p, pIdx) => {
                const pDateStr = p.recordedOnDate || p.paymentDate || p.createdAt || new Date();
                let pTime = p.recordedOnTime || '12:00 PM';
                try {
                    if (!p.recordedOnTime && p.createdAt) {
                        const dt = new Date(p.createdAt);
                        if (!isNaN(dt.getTime())) {
                            pTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                        }
                    }
                } catch (e) { }
                const pMode = p.paymentMode || 'Bank Transfer';
                const pStatus = p.paymentStatus || 'Paid';
                const pAmount = p.paidAmount || '₹0';
                const authorName = (p.createdBy && (p.createdBy.firstName || p.createdBy.name))
                    ? `${p.createdBy.firstName || ''} ${p.createdBy.lastName || ''}`.trim()
                    : (hoveredDeal.addedBy || hoveredDeal.salesOwner || (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System');

                return {
                    id: `payment-act-${p._id || p.id || pIdx}`,
                    type: 'payment',
                    author: authorName,
                    title: 'Payment Recorded',
                    content: `Payment of ${pAmount} recorded via ${pMode}. Status: ${pStatus}.${p.attachmentName ? ` (Attachment: ${p.attachmentName})` : ''}`,
                    time: pTime,
                    date: p.paymentDate || (typeof pDateStr === 'string' && pDateStr.includes('-') ? pDateStr : formatDateDDMMYYYY(pDateStr)),
                    createdAt: p.createdAt || new Date(),
                    attachmentName: p.attachmentName || '',
                    attachmentUrl: p.attachmentUrl || ''
                };
            });

            const all = [...custom, ...backendActivities, ...taskActs, ...paymentActs, ...baseActs];
            const seen = new Set();
            const unique = all.filter(a => {
                if (!a) return false;
                if (!canUserViewActivity(a, user, hasFullAccess)) return false;
                const contentKey = `${a.type || ''}-${(a.title || '').trim()}-${(a.content || '').trim()}-${(a.date || '').trim()}-${(a.time || '').trim()}`;
                if (seen.has(contentKey)) return false;
                seen.add(contentKey);
                return true;
            });

            const typePriority = {
                payment: 11,
                task: 10,
                deal: 9,
                note: 8,
                email: 7,
                message: 6,
                company: 5,
                activity: 4,
                contact: 1
            };

            return unique.sort((a, b) => {
                const ta = parseActivityTimestamp(a);
                const tb = parseActivityTimestamp(b);
                if (tb !== ta) {
                    return tb - ta;
                }
                const pA = typePriority[a.type] || (a.title && a.title.includes('Company') ? 5 : 4);
                const pB = typePriority[b.type] || (b.title && b.title.includes('Company') ? 5 : 4);
                return pB - pA;
            });
        }

        if (hoveredContact) {
            const custom = dealActivities[hoveredContact._id] || [];
            const backendActivities = Array.isArray(hoveredContact.activities) ? hoveredContact.activities : [];
            const contactFullName = `${hoveredContact.firstName || ''} ${hoveredContact.lastName || ''}`.trim().toLowerCase();
            const contactTasks = tasks.filter(
                t =>
                    t.associatedContactId === hoveredContact._id ||
                    (t.associatedContactName && t.associatedContactName.toLowerCase() === contactFullName)
            );

            const taskActs = contactTasks.map(t => {
                const taskDateStr = t.dueDate || t.createdAt || new Date();
                let taskTime = '12:00 PM';
                try {
                    const dt = new Date(t.createdAt || new Date());
                    if (!isNaN(dt.getTime())) {
                        taskTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                    }
                } catch (e) { }
                return {
                    id: `task-contact-act-${t._id}`,
                    type: 'task',
                    author: t.assignee || hoveredContact.owner || 'Sales Specialist',
                    title: `Task: ${t.title}`,
                    content: `${t.completed ? 'Completed' : (t.status || 'Pending')} task • Priority: ${t.priority || 'Medium'}${t.description ? ` • ${t.description}` : ''}`,
                    time: taskTime,
                    date: formatDateDDMMYYYY(taskDateStr),
                    createdAt: t.createdAt || new Date()
                };
            });

            const baseActs = [];
            const hasContactCreated = [...custom, ...backendActivities].some(a => a.type === 'contact' && (a.title === 'Contact Created' || (a.title && a.title.toLowerCase().includes('created'))));
            if (!hasContactCreated) {
                const createDateStr = hoveredContact.createdAt || hoveredContact.createdDate || new Date();
                let formattedTime = '01:05 PM';
                try {
                    const dt = new Date(createDateStr);
                    if (!isNaN(dt.getTime())) {
                        formattedTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                    }
                } catch (e) { }
                baseActs.push({
                    id: `contact-created-${hoveredContact._id}`,
                    type: 'contact',
                    title: 'Contact Created',
                    author: hoveredContact.addedBy || hoveredContact.owner || 'Admin System',
                    content: `Contact "${hoveredContact.firstName} ${hoveredContact.lastName}" created with status "${hoveredContact.leadStatus || 'New'}" in stage "${hoveredContact.leadStage || 'Lead'}".`,
                    time: formattedTime,
                    date: formatDateDDMMYYYY(createDateStr),
                    createdAt: createDateStr,
                    timestamp: parseActivityTimestamp({ date: formatDateDDMMYYYY(createDateStr), time: formattedTime, createdAt: createDateStr })
                });
            }

            const isRealCompanyHover = (companies || []).some(c => c && c.name && c.name.trim().toLowerCase() === hoveredContact.company.trim().toLowerCase());
            if (hoveredContact.company && hoveredContact.company !== 'Direct Client' && isRealCompanyHover) {
                const hasCompanyLinked = [...custom, ...backendActivities].some(a => a.type === 'company' || (a.title && a.title.toLowerCase().includes('company')));
                if (!hasCompanyLinked) {
                    const linkDateStr = hoveredContact.createdAt || new Date();
                    let linkTime = '01:05 PM';
                    try {
                        const dt = new Date(linkDateStr);
                        if (!isNaN(dt.getTime())) {
                            linkTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                        }
                    } catch (e) { }
                    baseActs.push({
                        id: `company-contact-linked-${hoveredContact._id}`,
                        type: 'company',
                        title: 'Company Registered & Linked',
                        author: hoveredContact.addedBy || hoveredContact.owner || 'Admin System',
                        content: `Company "${hoveredContact.company}" registered and linked with contact.`,
                        time: linkTime,
                        date: formatDateDDMMYYYY(linkDateStr),
                        createdAt: linkDateStr,
                        timestamp: parseActivityTimestamp({ date: formatDateDDMMYYYY(linkDateStr), time: linkTime, createdAt: linkDateStr }) + 1000
                    });
                }
            }

            // Associated Deals
            const cEmail = (hoveredContact.email || '').trim().toLowerCase();
            const cPhoneClean = (hoveredContact.phone || '').replace(/\D/g, '');
            const matchedDeals = deals.filter(d => {
                if (!d) return false;
                const dAssoc = (d.associatedContact || '').trim().toLowerCase();
                const dEmail = (d.email || '').trim().toLowerCase();
                const dClient = (d.clientName || '').trim().toLowerCase();
                const dPhone = (d.phone || '').replace(/\D/g, '');
                const matchesEmail = cEmail && (dAssoc === cEmail || dEmail === cEmail || dAssoc.includes(cEmail) || dEmail.includes(cEmail));
                const matchesName = contactFullName && (dClient === contactFullName || dAssoc === contactFullName || dClient.includes(contactFullName) || contactFullName.includes(dClient));
                const matchesPhone = cPhoneClean && cPhoneClean.length >= 7 && dPhone && (dPhone.endsWith(cPhoneClean) || cPhoneClean.endsWith(dPhone));
                const matchesDirect = Array.isArray(hoveredContact.associatedDeals) && hoveredContact.associatedDeals.includes(d._id);
                return matchesEmail || matchesName || matchesPhone || matchesDirect;
            });

            matchedDeals.forEach(deal => {
                const dealTitle = deal.dealName || deal.clientName || 'Associated Deal';
                const hasDealAct = [...custom, ...backendActivities].some(a =>
                    a.type === 'deal' && (a.id === `deal-contact-linked-${deal._id}` || (a.content && a.content.includes(dealTitle)))
                );
                if (!hasDealAct) {
                    const dealDateStr = deal.createdAt || deal.createdDate || deal.date || hoveredContact.createdAt || new Date();
                    let dealTime = '01:10 PM';
                    try {
                        const dt = new Date(dealDateStr);
                        if (!isNaN(dt.getTime())) {
                            dealTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                        }
                    } catch (e) { }
                    baseActs.push({
                        id: `deal-contact-linked-${deal._id}`,
                        type: 'deal',
                        title: 'Deal Registered & Linked',
                        author: deal.salesOwner || deal.addedBy || hoveredContact.addedBy || 'Sales Specialist',
                        content: `Deal "${dealTitle}" (${deal.currency || '₹'}${Number(deal.dealValue || 0).toLocaleString()} • ${deal.stage || 'New'}) registered and linked with contact.`,
                        time: dealTime,
                        date: formatDateDDMMYYYY(dealDateStr),
                        createdAt: dealDateStr,
                        timestamp: parseActivityTimestamp({ date: formatDateDDMMYYYY(dealDateStr), time: dealTime, createdAt: dealDateStr }) + 2000
                    });
                }
            });

            // Synthesize payment records associated with this contact into activity timeline items
            const paymentActs = getContactPaymentRecords(hoveredContact).map((p, pIdx) => {
                const pDateStr = p.recordedOnDate || p.paymentDate || p.createdAt || new Date();
                let pTime = p.recordedOnTime || '12:00 PM';
                try {
                    if (!p.recordedOnTime && p.createdAt) {
                        const dt = new Date(p.createdAt);
                        if (!isNaN(dt.getTime())) {
                            pTime = dt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
                        }
                    }
                } catch (e) { }
                const pMode = p.paymentMode || 'Bank Transfer';
                const pStatus = p.paymentStatus || 'Paid';
                const pAmount = p.paidAmount || '₹0';
                const authorName = (p.createdBy && (p.createdBy.firstName || p.createdBy.name))
                    ? `${p.createdBy.firstName || ''} ${p.createdBy.lastName || ''}`.trim()
                    : (hoveredContact.addedBy || hoveredContact.owner || (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System');

                return {
                    id: `payment-contact-act-${p._id || p.id || pIdx}`,
                    type: 'payment',
                    author: authorName,
                    title: 'Payment Recorded',
                    content: `Payment of ${pAmount} recorded via ${pMode}. Status: ${pStatus}.${p.attachmentName ? ` (Attachment: ${p.attachmentName})` : ''}`,
                    time: pTime,
                    date: p.paymentDate || (typeof pDateStr === 'string' && pDateStr.includes('-') ? pDateStr : formatDateDDMMYYYY(pDateStr)),
                    createdAt: p.createdAt || new Date(),
                    attachmentName: p.attachmentName || '',
                    attachmentUrl: p.attachmentUrl || ''
                };
            });

            const all = [...custom, ...backendActivities, ...taskActs, ...paymentActs, ...baseActs];
            const seen = new Set();
            const unique = all.filter(a => {
                if (!a) return false;
                if (!canUserViewActivity(a, user, hasFullAccess)) return false;
                const contentKey = `${a.type || ''}-${(a.title || '').trim()}-${(a.content || '').trim()}-${(a.date || '').trim()}-${(a.time || '').trim()}`;
                if (seen.has(contentKey)) return false;
                seen.add(contentKey);
                return true;
            });

            const typePriority = {
                payment: 11,
                task: 10,
                deal: 9,
                note: 8,
                email: 7,
                message: 6,
                company: 5,
                activity: 4,
                contact: 1
            };

            return unique.sort((a, b) => {
                const ta = parseActivityTimestamp(a);
                const tb = parseActivityTimestamp(b);
                if (tb !== ta) {
                    return tb - ta;
                }
                const pA = typePriority[a.type] || (a.title && a.title.includes('Company') ? 5 : 4);
                const pB = typePriority[b.type] || (b.title && b.title.includes('Company') ? 5 : 4);
                return pB - pA;
            });
        }

        return [];
    }, [hoveredDeal, hoveredContact, dealActivities, tasks, deals, getDealPaymentRecords, getContactPaymentRecords, user, hasFullAccess]);

    // Upcoming next step tasks for hovered item
    const hoveredUpcomingTasks = useMemo(() => {
        if (hoveredDeal) {
            return tasks.filter(
                t =>
                    !t.completed &&
                    (t.associatedDealId === hoveredDeal._id ||
                        (t.associatedDealName && (
                            t.associatedDealName.toLowerCase() === (hoveredDeal.dealName || '').toLowerCase() ||
                            t.associatedDealName.toLowerCase() === (hoveredDeal.clientName || '').toLowerCase()
                        )))
            );
        }
        if (hoveredContact) {
            const contactFullName = `${hoveredContact.firstName || ''} ${hoveredContact.lastName || ''}`.trim().toLowerCase();
            return tasks.filter(
                t =>
                    !t.completed &&
                    (t.associatedContactId === hoveredContact._id ||
                        (t.associatedContactName && t.associatedContactName.toLowerCase() === contactFullName))
            );
        }
        return [];
    }, [hoveredDeal, hoveredContact, tasks]);

    // Helper to extract the phone number for a deal from direct fields, relations, or associated CRM contacts
    const getDealContactPhone = useCallback((deal) => {
        if (!deal) return '';
        // 1. Direct deal phone fields
        if (deal.phone && String(deal.phone).trim()) return String(deal.phone).trim();
        if (deal.contactPhone && String(deal.contactPhone).trim()) return String(deal.contactPhone).trim();
        if (deal.phoneNumber && String(deal.phoneNumber).trim()) return String(deal.phoneNumber).trim();
        if (deal.clientPhone && String(deal.clientPhone).trim()) return String(deal.clientPhone).trim();

        // 2. Populated lead relations
        if (deal.relatedLead && typeof deal.relatedLead === 'object') {
            const lp = deal.relatedLead.phone || deal.relatedLead.contactNumber;
            if (lp && String(lp).trim()) return String(lp).trim();
        }
        if (deal.relatedMetaLead && typeof deal.relatedMetaLead === 'object') {
            const mp = deal.relatedMetaLead.phone;
            if (mp && String(mp).trim()) return String(mp).trim();
        }

        // 3. Matched contact in contacts list
        const targetEmail = (deal.associatedContact || deal.email || '').trim().toLowerCase();
        const targetName = (deal.clientName || deal.dealName || '').trim().toLowerCase();
        const targetAssoc = (deal.associatedContact || '').trim().toLowerCase();

        const matchedContact = (contacts || []).find(c => {
            if (!c) return false;
            if (deal.contactId && String(c._id) === String(deal.contactId)) return true;
            if (deal.associatedContactId && String(c._id) === String(deal.associatedContactId)) return true;
            const cEmail = (c.email || '').trim().toLowerCase();
            if (targetEmail && cEmail && cEmail === targetEmail) return true;
            if (targetAssoc && cEmail && cEmail === targetAssoc) return true;
            const cFullName = `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase();
            if (targetName && cFullName && cFullName === targetName) return true;
            if (targetAssoc && cFullName && cFullName === targetAssoc) return true;
            return false;
        });

        if (matchedContact && matchedContact.phone && String(matchedContact.phone).trim()) {
            const cCode = matchedContact.countryCode ? `${matchedContact.countryCode} ` : '';
            const rawP = String(matchedContact.phone).trim();
            return rawP.startsWith('+') ? rawP : `${cCode}${rawP}`.trim();
        }

        return '';
    }, [contacts]);

    const matchedContactForDeal = useMemo(() => {
        if (!detailedDeal) return null;
        const directMatch = contacts.find(
            c =>
                (c.email && detailedDeal.associatedContact && c.email.toLowerCase() === detailedDeal.associatedContact.toLowerCase()) ||
                (c.email && detailedDeal.email && c.email.toLowerCase() === detailedDeal.email.toLowerCase()) ||
                (`${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase() === (detailedDeal.clientName || '').toLowerCase()) ||
                (detailedDeal.associatedContact && `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase() === detailedDeal.associatedContact.toLowerCase())
        );
        if (directMatch) return directMatch;
        const contactEmail = (detailedDeal.associatedContact || detailedDeal.email || '').trim();
        const clientName = (detailedDeal.clientName || '').trim();
        if (!contactEmail && !clientName) return null;
        let fName = 'Contact';
        let lName = '';
        if (clientName && !clientName.includes('@')) {
            const parts = clientName.split(/\s+/);
            fName = parts[0] || 'Contact';
            lName = parts.slice(1).join(' ') || '';
        } else if (contactEmail && contactEmail.includes('@')) {
            const userPart = contactEmail.split('@')[0];
            const parts = userPart.split(/[._-]/);
            fName = parts[0] ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1) : 'Contact';
            lName = parts.slice(1).join(' ') || '';
        }
        const phoneVal = (getDealContactPhone(detailedDeal) || detailedDeal.phone || '').trim();
        let synCC = '+91';
        let synPhone = phoneVal;
        if (phoneVal.startsWith('+')) {
            synCC = '';
            synPhone = phoneVal;
        } else if (detailedDeal.countryCode) {
            synCC = detailedDeal.countryCode;
        }
        return {
            _id: `contact-deal-${detailedDeal._id || 'temp'}`,
            firstName: fName,
            lastName: lName,
            email: (contactEmail && contactEmail.includes('@')) ? contactEmail : (detailedDeal.email || `${fName.toLowerCase()}@example.com`),
            countryCode: synCC,
            phone: synPhone,
            contactSource: 'Direct Reference',
            leadStage: 'Lead',
            leadStatus: 'New',
            owner: detailedDeal.salesOwner || 'Admin System',
            company: detailedDeal.associatedCompany || ''
        };
    }, [contacts, detailedDeal, getDealContactPhone]);

    const matchedCompanyForDeal = useMemo(() => {
        if (!detailedDeal) return null;
        const targetComp = detailedDeal.associatedCompany || detailedDeal.company || (matchedContactForDeal ? matchedContactForDeal.company : '');
        if (!targetComp || targetComp === 'Direct Client') return null;
        return companies.find(c => c.name && c.name.toLowerCase() === targetComp.toLowerCase()) || null;
    }, [companies, detailedDeal, matchedContactForDeal]);

    const dealDisplayLocation = useMemo(() => {
        if (!detailedDeal) return '-';
        if (matchedCompanyForDeal?.city || matchedCompanyForDeal?.state || matchedCompanyForDeal?.country) {
            return [matchedCompanyForDeal.city, matchedCompanyForDeal.state, matchedCompanyForDeal.country].filter(Boolean).join(', ');
        }
        const loc = (detailedDeal.location || '').trim();
        if (loc && loc !== 'Pembroke Pines' && loc !== 'Pune, India' && loc !== 'solar panel comp') {
            return loc;
        }
        return '-';
    }, [detailedDeal, matchedCompanyForDeal]);

    const getDealCompany = useCallback((deal) => {
        if (!deal) return '';
        if (deal.associatedCompany && deal.associatedCompany !== 'Direct Client') return deal.associatedCompany;
        if (deal.company && deal.company !== 'Direct Client') return deal.company;
        if (deal.companyName && deal.companyName !== 'Direct Client') return deal.companyName;

        const targetEmail = (deal.associatedContact || deal.email || '').trim().toLowerCase();
        const targetName = (deal.clientName || '').trim().toLowerCase();
        const matchedContact = (contacts || []).find(c => {
            if (deal.contactId && c._id === deal.contactId) return true;
            const ce = (c.email || '').trim().toLowerCase();
            if (targetEmail && ce && ce === targetEmail) return true;
            const cn = `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase();
            return cn && (cn === targetEmail || cn === targetName);
        });
        if (matchedContact?.company && matchedContact.company !== 'Direct Client') {
            return matchedContact.company;
        }

        if (deal.companyId) {
            const comp = (companies || []).find(c => c._id === deal.companyId);
            if (comp?.name && comp.name !== 'Direct Client') return comp.name;
        }
        return '';
    }, [contacts, companies]);

    const getDealContactOrigin = useCallback((deal) => {
        if (!deal) return null;
        if (deal.origin) return deal.origin;
        const targetEmail = (deal.associatedContact || deal.email || '').trim().toLowerCase();
        const targetPhone = (deal.phone || '').replace(/\D/g, '');
        const targetName = (deal.clientName || '').trim().toLowerCase();
        const targetAssoc = (deal.associatedContact || '').trim().toLowerCase();

        const matchedContact = (contacts || []).find(c => {
            if (!c) return false;
            if (deal.contactId && c._id === deal.contactId) return true;
            const cEmail = (c.email || '').trim().toLowerCase();
            if (targetEmail && cEmail && cEmail === targetEmail) return true;
            if (targetAssoc && cEmail && cEmail === targetAssoc) return true;
            const cFullName = `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase();
            if (targetName && cFullName && cFullName === targetName) return true;
            if (targetAssoc && cFullName && cFullName === targetAssoc) return true;
            const cPhone = (c.phone || '').replace(/\D/g, '');
            if (targetPhone && cPhone && targetPhone.length >= 7 && (targetPhone.includes(cPhone) || cPhone.includes(targetPhone))) return true;
            return false;
        });

        if (matchedContact) {
            return matchedContact.origin || 'Domestic';
        }
        return null;
    }, [contacts]);

    // Professional Skeleton Shimmer Helpers
    const renderKanbanSkeleton = (colCount = 4, isContact = false) => (
        <div className="pipeline-kanban-board">
            {Array.from({ length: colCount }).map((_, colIdx) => (
                <div key={colIdx} className="pipeline-skeleton-column">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 10, borderBottom: '1px solid #e2e8f0' }}>
                        <div className="pipeline-skeleton" style={{ width: 110, height: 18, borderRadius: 6 }} />
                        <div className="pipeline-skeleton" style={{ width: 26, height: 20, borderRadius: 12 }} />
                    </div>
                    {[1, 2, 3].map(cardIdx => (
                        <div key={cardIdx} className="pipeline-skeleton-card">
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                <div className="pipeline-skeleton" style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0 }} />
                                <div style={{ flex: 1 }}>
                                    <div className="pipeline-skeleton" style={{ width: '75%', height: 14, borderRadius: 4, marginBottom: 6 }} />
                                    <div className="pipeline-skeleton" style={{ width: '55%', height: 11, borderRadius: 4, marginBottom: isContact ? 6 : 0 }} />
                                    {isContact && <div className="pipeline-skeleton" style={{ width: '40%', height: 14, borderRadius: 999 }} />}
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div className="pipeline-skeleton" style={{ width: 60, height: 11, borderRadius: 4 }} />
                                    <div className="pipeline-skeleton" style={{ width: 55, height: 12, borderRadius: 4 }} />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div className="pipeline-skeleton" style={{ width: 80, height: 11, borderRadius: 4 }} />
                                    <div className="pipeline-skeleton" style={{ width: 70, height: 11, borderRadius: 4 }} />
                                </div>
                                {!isContact && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <div className="pipeline-skeleton" style={{ width: 65, height: 11, borderRadius: 4 }} />
                                        <div className="pipeline-skeleton" style={{ width: 90, height: 11, borderRadius: 4 }} />
                                    </div>
                                )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <div className="pipeline-skeleton" style={{ width: 22, height: 22, borderRadius: '50%' }} />
                                    <div className="pipeline-skeleton" style={{ width: 22, height: 22, borderRadius: '50%' }} />
                                </div>
                                <div className="pipeline-skeleton" style={{ width: 72, height: 22, borderRadius: 6 }} />
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );

    const renderTableSkeleton = (rowCount = 7) => (
        <div className="pipeline-table-view">
            <div className="pipeline-table-card">
                {Array.from({ length: rowCount }).map((_, r) => (
                    <div key={r} className="pipeline-skeleton-table-row">
                        <div className="pipeline-skeleton" style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0 }} />
                        <div className="pipeline-skeleton" style={{ width: 140, height: 14, borderRadius: 4 }} />
                        <div className="pipeline-skeleton" style={{ width: 120, height: 14, borderRadius: 4 }} />
                        <div className="pipeline-skeleton" style={{ width: 90, height: 14, borderRadius: 4 }} />
                        <div className="pipeline-skeleton" style={{ width: 80, height: 14, borderRadius: 4 }} />
                        <div className="pipeline-skeleton" style={{ width: 100, height: 14, borderRadius: 4 }} />
                        <div className="pipeline-skeleton" style={{ width: 90, height: 22, borderRadius: 12 }} />
                        <div className="pipeline-skeleton" style={{ width: 60, height: 22, borderRadius: 12 }} />
                    </div>
                ))}
            </div>
        </div>
    );

    const renderCompanyGridSkeleton = (cardCount = 8) => (
        <div className="pipeline-company-body" style={{ padding: '20px 24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                {Array.from({ length: cardCount }).map((_, i) => (
                    <div key={i} className="pipeline-skeleton-grid-card">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div className="pipeline-skeleton" style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                                <div className="pipeline-skeleton" style={{ width: '70%', height: 15, borderRadius: 4, marginBottom: 6 }} />
                                <div className="pipeline-skeleton" style={{ width: '50%', height: 12, borderRadius: 4 }} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <div className="pipeline-skeleton" style={{ width: 50, height: 11, borderRadius: 4 }} />
                                <div className="pipeline-skeleton" style={{ width: 80, height: 11, borderRadius: 4 }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <div className="pipeline-skeleton" style={{ width: 60, height: 11, borderRadius: 4 }} />
                                <div className="pipeline-skeleton" style={{ width: 100, height: 11, borderRadius: 4 }} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                            <div className="pipeline-skeleton" style={{ width: 70, height: 20, borderRadius: 6 }} />
                            <div className="pipeline-skeleton" style={{ width: 24, height: 24, borderRadius: '50%' }} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderTaskListSkeleton = (rowCount = 7) => (
        <div className="pipeline-tasks-container" style={{ padding: '20px 24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Array.from({ length: rowCount }).map((_, i) => (
                    <div key={i} className="pipeline-skeleton-card" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                            <div className="pipeline-skeleton" style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                                <div className="pipeline-skeleton" style={{ width: `${40 + (i % 3) * 15}%`, height: 14, borderRadius: 4, marginBottom: 6 }} />
                                <div className="pipeline-skeleton" style={{ width: `${60 + (i % 2) * 20}%`, height: 11, borderRadius: 4 }} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            <div className="pipeline-skeleton" style={{ width: 90, height: 12, borderRadius: 4 }} />
                            <div className="pipeline-skeleton" style={{ width: 60, height: 20, borderRadius: 999 }} />
                            <div className="pipeline-skeleton" style={{ width: 70, height: 20, borderRadius: 999 }} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderDetailSkeleton = () => (
        <div className="pipeline-detail-view-container">
            <div className="pipeline-detail-top-nav">
                <div className="pipeline-detail-nav-left">
                    <div className="pipeline-skeleton" style={{ width: 130, height: 36, borderRadius: 8 }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div className="pipeline-skeleton" style={{ width: 40, height: 40, borderRadius: 10 }} />
                        <div>
                            <div className="pipeline-skeleton" style={{ width: 180, height: 20, borderRadius: 4, marginBottom: 6 }} />
                            <div className="pipeline-skeleton" style={{ width: 220, height: 13, borderRadius: 4 }} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="pipeline-detail-split-body three-sections">
                <div className="pipeline-detail-col-deal">
                    <div className="pipeline-detail-card-panel">
                        <div className="pipeline-skeleton" style={{ width: 140, height: 20, borderRadius: 4, marginBottom: 16 }} />
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                <div className="pipeline-skeleton" style={{ width: 90, height: 14, borderRadius: 4 }} />
                                <div className="pipeline-skeleton" style={{ width: 120, height: 14, borderRadius: 4 }} />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="pipeline-detail-col-activities">
                    <div className="pipeline-detail-actions-card">
                        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} className="pipeline-skeleton" style={{ width: 85, height: 32, borderRadius: 8 }} />
                            ))}
                        </div>
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="pipeline-skeleton-card" style={{ marginBottom: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div className="pipeline-skeleton" style={{ width: 36, height: 36, borderRadius: 10 }} />
                                    <div style={{ flex: 1 }}>
                                        <div className="pipeline-skeleton" style={{ width: 140, height: 14, borderRadius: 4, marginBottom: 6 }} />
                                        <div className="pipeline-skeleton" style={{ width: 200, height: 12, borderRadius: 4 }} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="pipeline-detail-col-assoc">
                    <div className="pipeline-detail-card-panel">
                        <div className="pipeline-skeleton" style={{ width: 150, height: 20, borderRadius: 4, marginBottom: 16 }} />
                        {[1, 2, 3].map(i => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                <div className="pipeline-skeleton" style={{ width: 80, height: 14, borderRadius: 4 }} />
                                <div className="pipeline-skeleton" style={{ width: 110, height: 14, borderRadius: 4 }} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const matchedOwnerEmployee = useMemo(() => {
        if (!detailedDeal) return null;
        const ownerName = (detailedDeal.salesOwner || '').trim().toLowerCase();

        // 1. Priority 1: Match by deal owner name (salesOwner) in employees list
        if (employees && employees.length > 0 && ownerName) {
            // 1a. Exact full name / email match
            const matchByName = employees.find(emp => {
                if (!emp) return false;
                const empFullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                const empName = (emp.name || '').trim().toLowerCase();
                const empEmail = (emp.email || '').trim().toLowerCase();
                const empOfficialEmail = (emp.officialEmail || '').trim().toLowerCase();
                return (
                    empFullName === ownerName ||
                    empName === ownerName ||
                    empEmail === ownerName ||
                    empOfficialEmail === ownerName
                );
            });
            if (matchByName) return matchByName;

            // 1b. Split name match (first name + last name)
            const ownerParts = ownerName.split(/\s+/).filter(Boolean);
            if (ownerParts.length >= 2) {
                const matchSplit = employees.find(emp => {
                    if (!emp) return false;
                    const f = (emp.firstName || '').trim().toLowerCase();
                    const l = (emp.lastName || '').trim().toLowerCase();
                    return f === ownerParts[0] && l === ownerParts[1];
                });
                if (matchSplit) return matchSplit;
            }

            // 1c. Partial / Substring match in employees
            const matchPartial = employees.find(emp => {
                if (!emp) return false;
                const empFullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                const f = (emp.firstName || '').trim().toLowerCase();
                const empEmail = (emp.email || '').trim().toLowerCase();
                return (
                    (empFullName && (empFullName.includes(ownerName) || ownerName.includes(empFullName))) ||
                    (f && f.length >= 3 && ownerName.includes(f)) ||
                    (empEmail && ownerParts[0] && empEmail.startsWith(ownerParts[0]))
                );
            });
            if (matchPartial) return matchPartial;
        }

        // 2. Priority 2: Check detailedDeal.assignedTo ONLY IF it actually matches ownerName or ownerName is empty
        if (detailedDeal.assignedTo) {
            if (typeof detailedDeal.assignedTo === 'object' && detailedDeal.assignedTo !== null) {
                const assignedFullName = `${detailedDeal.assignedTo.firstName || ''} ${detailedDeal.assignedTo.lastName || ''}`.trim().toLowerCase();
                const assignedEmail = (detailedDeal.assignedTo.email || '').trim().toLowerCase();
                const matchesOwner = !ownerName || assignedFullName === ownerName || assignedEmail === ownerName ||
                    (assignedFullName && (assignedFullName.includes(ownerName) || ownerName.includes(assignedFullName)));

                if (matchesOwner) {
                    const matchInEmp = (employees || []).find(e =>
                        (e._id && detailedDeal.assignedTo._id && e._id.toString() === detailedDeal.assignedTo._id.toString()) ||
                        (e.email && detailedDeal.assignedTo.email && e.email.toLowerCase() === detailedDeal.assignedTo.email.toLowerCase())
                    );
                    if (matchInEmp) return { ...detailedDeal.assignedTo, ...matchInEmp };
                    return detailedDeal.assignedTo;
                }
            } else if (typeof detailedDeal.assignedTo === 'string' && employees && employees.length > 0) {
                const matchById = employees.find(e => e._id && e._id.toString() === detailedDeal.assignedTo.toString());
                if (matchById) {
                    const empFullName = `${matchById.firstName || ''} ${matchById.lastName || ''}`.trim().toLowerCase();
                    if (!ownerName || empFullName === ownerName || empFullName.includes(ownerName) || ownerName.includes(empFullName)) {
                        return matchById;
                    }
                }
            }
        }

        // 3. Priority 3: Match with logged-in user ONLY IF user's actual name matches ownerName
        if (user && ownerName) {
            const currentUserName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
            const userName = (user.name || '').trim().toLowerCase();
            const userEmail = (user.email || '').trim().toLowerCase();
            if (
                (currentUserName && currentUserName === ownerName) ||
                (userName && userName === ownerName) ||
                (userEmail && userEmail === ownerName)
            ) {
                return user;
            }
        }

        return null;
    }, [detailedDeal, employees, user]);

    const getAddedByEmployee = (name) => {
        if (!name) return null;
        const cleanName = name.trim().toLowerCase();
        if (employees && employees.length > 0) {
            const matchByName = employees.find(emp => {
                if (!emp) return false;
                const empFullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                const empName = (emp.name || '').trim().toLowerCase();
                const empEmail = (emp.email || '').trim().toLowerCase();
                const empOfficialEmail = (emp.officialEmail || '').trim().toLowerCase();
                return (
                    empFullName === cleanName ||
                    empName === cleanName ||
                    empEmail === cleanName ||
                    empOfficialEmail === cleanName
                );
            });
            if (matchByName) return matchByName;

            const parts = cleanName.split(/\s+/).filter(Boolean);
            if (parts.length >= 2) {
                const matchSplit = employees.find(emp => {
                    if (!emp) return false;
                    const f = (emp.firstName || '').trim().toLowerCase();
                    const l = (emp.lastName || '').trim().toLowerCase();
                    return f === parts[0] && l === parts[1];
                });
                if (matchSplit) return matchSplit;
            }

            const matchPartial = employees.find(emp => {
                if (!emp) return false;
                const empFullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                const f = (emp.firstName || '').trim().toLowerCase();
                return (
                    (empFullName && (empFullName.includes(cleanName) || cleanName.includes(empFullName))) ||
                    (f && f.length >= 3 && cleanName.includes(f))
                );
            });
            if (matchPartial) return matchPartial;
        }

        if (user) {
            const currentUserName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
            const userName = (user.name || '').trim().toLowerCase();
            const userEmail = (user.email || '').trim().toLowerCase();
            if (
                (currentUserName && currentUserName === cleanName) ||
                (userName && userName === cleanName) ||
                (userEmail && userEmail === cleanName)
            ) {
                return user;
            }
        }

        return null;
    };

    const matchedCompaniesForContact = useMemo(() => {
        if (!detailedContact || !companies || companies.length === 0) return [];
        const results = [];
        const seen = new Set();

        const addCompanyByName = (compName, sourceLabel = '') => {
            if (!compName || compName === 'Direct Client' || compName.trim() === '' || compName.trim() === '-') return;
            const cleanName = compName.trim();
            const lower = cleanName.toLowerCase();
            if (seen.has(lower)) return;

            const found = companies.find(c => c && c.name && c.name.trim().toLowerCase() === lower);
            if (found) {
                seen.add(lower);
                results.push({ ...found, sourceLabel: sourceLabel || 'Associated' });
            }
        };

        // 1. Direct company on contact
        if (detailedContact.company && detailedContact.company !== 'Direct Client') {
            const fullCompany = detailedContact.company.trim();
            const directFound = companies.find(c => c && c.name && c.name.trim().toLowerCase() === fullCompany.toLowerCase());
            if (directFound) {
                addCompanyByName(fullCompany, 'Direct Company');
            } else if (fullCompany.includes(',')) {
                const parts = fullCompany.split(',').map(s => s.trim()).filter(Boolean);
                parts.forEach(p => addCompanyByName(p, 'Direct Company'));
            }
        }

        // 2. Companies from all associated deals
        matchedDealsForContact.forEach(deal => {
            const dealComp = deal.associatedCompany || deal.company;
            if (dealComp && dealComp !== 'Direct Client') {
                const dealTitle = deal.dealName || deal.clientName || 'Deal';
                addCompanyByName(dealComp, `Via Deal: ${dealTitle}`);
            }
        });

        // 3. Domain match from contact email
        if (detailedContact.email && detailedContact.email.includes('@')) {
            const emailDomain = detailedContact.email.split('@')[1]?.toLowerCase();
            if (emailDomain && !['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'example.com'].includes(emailDomain)) {
                companies.forEach(c => {
                    if (!c || !c.domain) return;
                    const cleanDom = c.domain.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
                    if (cleanDom === emailDomain) {
                        addCompanyByName(c.name, 'Domain Match');
                    }
                });
            }
        }

        return results;
    }, [companies, detailedContact, matchedDealsForContact]);

    const matchedCompanyForContact = useMemo(() => {
        return matchedCompaniesForContact[0] || null;
    }, [matchedCompaniesForContact]);

    // Dynamically sync options from existing contacts, companies, deals (placed after state declarations)
    useEffect(() => {
        if (contacts && contacts.length > 0) {
            let deletedStages = [];
            try {
                deletedStages = JSON.parse(localStorage.getItem('crm_deleted_stages') || '[]');
            } catch (e) { }

            const existingSources = Array.from(new Set(contacts.map(c => c.contactSource).filter(Boolean)));
            const existingStages = Array.from(new Set(contacts.map(c => c.leadStage).filter(Boolean)))
                .filter(s => !deletedStages.includes(s.toLowerCase()));
            const existingStatuses = Array.from(new Set(contacts.map(c => c.leadStatus).filter(Boolean)));

            if (existingSources.length > 0) {
                setContactSources(prev => Array.from(new Set([...prev, ...existingSources])));
            }
            if (existingStages.length > 0) {
                setLeadStages(prev => Array.from(new Set([...prev, ...existingStages])));
                setContactStages(prev => {
                    const current = prev || [];
                    const names = new Set(current.map(s => (s.name || s.id || '').toLowerCase()));
                    const toAdd = existingStages
                        .filter(st => !names.has(st.toLowerCase()))
                        .map((st, i) => ({
                            id: st,
                            name: st,
                            badgeClass: ['new', 'hot', 'cold', 'won', 'custom'][i % 5],
                            isCustom: true
                        }));
                    return toAdd.length > 0 ? [...current, ...toAdd] : current;
                });
            }
            if (existingStatuses.length > 0) {
                setLeadStatuses(prev => Array.from(new Set([...prev, ...existingStatuses])));
            }
        }
    }, [contacts]);

    useEffect(() => {
        if (companies && companies.length > 0) {
            const existingIndustries = Array.from(new Set(companies.map(c => c.industry).filter(Boolean)));
            if (existingIndustries.length > 0) {
                setIndustryOptions(prev => Array.from(new Set([...prev, ...existingIndustries])));
            }
        }
    }, [companies]);

    useEffect(() => {
        if (deals && deals.length > 0) {
            const existingTypes = Array.from(new Set(deals.map(d => d.dealType).filter(Boolean)));
            if (existingTypes.length > 0) {
                setDealTypes(prev => Array.from(new Set([...prev, ...existingTypes])));
            }
        }
    }, [deals]);

    // Persist pipeline configurations across page refreshes
    useEffect(() => {
        try {
            localStorage.setItem('crm_pipelines', JSON.stringify(pipelines));
        } catch (e) { }
    }, [pipelines]);

    useEffect(() => {
        try {
            localStorage.setItem('crm_pipeline_options', JSON.stringify(pipelineOptions));
        } catch (e) { }
    }, [pipelineOptions]);

    useEffect(() => {
        try {
            localStorage.setItem('crm_stages', JSON.stringify(stages));
        } catch (e) { }
    }, [stages]);

    useEffect(() => {
        try {
            localStorage.setItem('crm_contact_stages', JSON.stringify(contactStages));
        } catch (e) { }
    }, [contactStages]);

    useEffect(() => {
        try {
            localStorage.setItem('crm_lead_stages', JSON.stringify(leadStages));
        } catch (e) { }
    }, [leadStages]);

    useEffect(() => {
        try {
            localStorage.setItem('crm_contact_sources', JSON.stringify(contactSources));
        } catch (e) { }
    }, [contactSources]);

    useEffect(() => {
        try {
            localStorage.setItem('crm_lead_statuses', JSON.stringify(leadStatuses));
        } catch (e) { }
    }, [leadStatuses]);

    useEffect(() => {
        try {
            localStorage.setItem('crm_industry_options', JSON.stringify(industryOptions));
        } catch (e) { }
    }, [industryOptions]);

    const contactStageOptions = useMemo(() => {
        const stageNames = (effectiveContactStages || []).map(s => typeof s === 'string' ? s : (s.name || s.id)).filter(Boolean);
        const fromLeadStages = (leadStages || []).filter(st => isStageCreatedOnOrAfterCutoff(st, 'contact')).filter(Boolean);
        const fromContacts = (contacts || []).filter(c => !isAdmin ? getItemCreationDate(c) >= DATA_CUTOFF_DATE : true).map(c => c.leadStage).filter(st => isStageCreatedOnOrAfterCutoff(st, 'contact')).filter(Boolean);
        const combined = Array.from(new Set([...stageNames, ...fromLeadStages, ...fromContacts]));
        return combined.length > 0 ? combined : ['New Lead'];
    }, [effectiveContactStages, leadStages, contacts, isStageCreatedOnOrAfterCutoff, isAdmin]);


    const handleToggleTaskComplete = async (taskId, e) => {
        if (e) e.stopPropagation();
        const task = tasks.find(t => String(t._id) === String(taskId));
        if (!task) return;
        const nextCompleted = !task.completed;
        const nextStatus = nextCompleted ? 'Completed' : 'Pending';

        // Optimistically update React state immediately for instant feedback
        setTasks(prev =>
            prev.map(t => (String(t._id) === String(taskId) ? { ...t, completed: nextCompleted, status: nextStatus } : t))
        );

        try {
            if (taskId && !taskId.toString().startsWith('task-')) {
                await api.put(`/pipeline-tasks/${taskId}`, { completed: nextCompleted, status: nextStatus });
            }
        } catch (err) {
            console.warn('Toggle task API sync:', err.message);
        }

        if (task.associatedDealId) {
            logActivity(task.associatedDealId, {
                type: 'task',
                title: nextCompleted ? 'Task Completed' : 'Task Reopened',
                content: `Marked task "${task.title}" as ${nextCompleted ? 'Completed' : 'Pending'}.`
            });
        }
        if (task.associatedContactId) {
            logActivity(task.associatedContactId, {
                type: 'task',
                title: nextCompleted ? 'Task Completed' : 'Task Reopened',
                content: `Marked task "${task.title}" as ${nextCompleted ? 'Completed' : 'Pending'}.`
            }, true);
        }
    };

    const handleUpdateTaskStatus = async (taskId, nextStatus) => {
        const isCompleted = nextStatus === 'Completed';
        setTasks(prev =>
            prev.map(t => (String(t._id) === String(taskId) ? { ...t, status: nextStatus, completed: isCompleted } : t))
        );

        try {
            if (taskId && !taskId.toString().startsWith('task-')) {
                await api.put(`/pipeline-tasks/${taskId}`, { status: nextStatus, completed: isCompleted });
            }
        } catch (err) {
            console.warn('Update task status API sync:', err.message);
        }
    };

    const handleOpenCreateTask = (presetDealId = '', presetContactId = '') => {
        setEditingTask(null);
        setIsAssignCustom(false);

        const effectiveDealId = presetDealId || detailedDealId || (detailedDeal ? detailedDeal._id : '');
        const matchedDeal = deals.find(d => String(d._id) === String(effectiveDealId));

        const effectiveContactId = presetContactId || detailedContactId || (detailedContact ? detailedContact._id : '');
        const matchedContact = contacts.find(c => String(c._id) === String(effectiveContactId));

        // Get strictly the associated contacts of this deal (if deal is primary)
        const dealAssocContacts = effectiveDealId ? getDealAssociatedContacts(matchedDeal || effectiveDealId) : [];

        // Get strictly the associated deals of this contact (if contact is primary)
        const contactAssocDeals = effectiveContactId ? getContactAssociatedDeals(matchedContact || effectiveContactId) : [];

        let initialContactIds = [];
        if (effectiveContactId) {
            initialContactIds = [effectiveContactId];
        } else if (dealAssocContacts.length > 0) {
            initialContactIds = dealAssocContacts.map(c => c._id);
        }

        let initialDealIds = [];
        if (effectiveDealId) {
            initialDealIds = [effectiveDealId];
        } else if (contactAssocDeals.length > 0) {
            initialDealIds = contactAssocDeals.map(d => d._id);
        }

        setTaskLockedContext({
            dealId: effectiveDealId || null,
            contactId: presetContactId || detailedContactId || (detailedContact ? detailedContact._id : null)
        });

        const defaultAssignee = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || (matchedDeal ? matchedDeal.salesOwner : (detailedDeal ? detailedDeal.salesOwner : '')) || 'Admin System';

        setTaskFormData({
            title: '',
            description: '',
            dueDate: new Date(Date.now() + 86400000 * 2).toISOString().slice(0, 10),
            priority: 'Medium',
            status: 'Pending',
            type: 'Follow-up',
            assignee: defaultAssignee,
            associatedDealId: initialDealIds[0] || effectiveDealId || '',
            associatedDealIds: initialDealIds,
            associatedContactId: initialContactIds[0] || '',
            associatedContactIds: initialContactIds
        });
        setShowCreateTaskModal(true);
    };

    const handleOpenEditTask = (task, e) => {
        if (e) e.stopPropagation();
        setEditingTask(task);
        setTaskLockedContext({
            dealId: task.associatedDealId || (detailedDealId ? detailedDealId : null),
            contactId: task.associatedContactId || (detailedContactId ? detailedContactId : null)
        });
        const currentUser = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';
        const hasCustomAssignee = Boolean(task.assignee && task.assignee !== currentUser && task.assignee !== 'Artiflow' && task.assignee !== 'Admin System');
        setIsAssignCustom(hasCustomAssignee);

        const contactIds = task.associatedContactIds && Array.isArray(task.associatedContactIds) && task.associatedContactIds.length > 0
            ? task.associatedContactIds
            : (task.associatedContactId ? [task.associatedContactId] : []);

        const dealIds = task.associatedDealIds && Array.isArray(task.associatedDealIds) && task.associatedDealIds.length > 0
            ? task.associatedDealIds
            : (task.associatedDealId ? [task.associatedDealId] : []);

        setTaskFormData({
            title: task.title || '',
            description: task.description || '',
            dueDate: task.dueDate || '',
            priority: task.priority || 'Medium',
            status: task.status || (task.completed ? 'Completed' : 'Pending'),
            type: task.type || 'Follow-up',
            assignee: task.assignee || currentUser,
            associatedDealId: task.associatedDealId || (dealIds[0] || ''),
            associatedDealIds: dealIds,
            associatedContactId: task.associatedContactId || (contactIds[0] || ''),
            associatedContactIds: contactIds
        });
        setShowCreateTaskModal(true);
    };

    const handleDeleteTask = async (taskId, e) => {
        if (e) e.stopPropagation();
        if (!canDelete) {
            alert('You do not have permission to delete tasks.');
            return;
        }
        const taskToDelete = tasks.find(t => String(t._id) === String(taskId));
        if (window.confirm('Are you sure you want to delete this task?')) {
            setTasks(prev => prev.filter(t => String(t._id) !== String(taskId)));
            try {
                if (taskId && !taskId.toString().startsWith('task-')) {
                    await api.delete(`/pipeline-tasks/${taskId}`);
                }
            } catch (err) {
                console.warn('Delete task API sync:', err.message);
            }

            if (taskToDelete && taskToDelete.associatedDealId) {
                logActivity(taskToDelete.associatedDealId, {
                    type: 'task',
                    title: 'Task Deleted',
                    content: `Deleted task "${taskToDelete.title}".`
                });
            }
            if (taskToDelete && taskToDelete.associatedContactId) {
                logActivity(taskToDelete.associatedContactId, {
                    type: 'task',
                    title: 'Task Deleted',
                    content: `Deleted task "${taskToDelete.title}".`
                }, true);
            }
        }
    };

    const handleTaskSubmit = async (e) => {
        e.preventDefault();
        if (!taskFormData.title.trim()) {
            alert('Please enter a task title');
            return;
        }

        const effectiveDealId = taskFormData.associatedDealId || (detailedDeal ? detailedDeal._id : '');
        const linkedDeal = deals.find(d => d._id === effectiveDealId);

        let selectedContactIds = taskFormData.associatedContactIds && taskFormData.associatedContactIds.length > 0
            ? taskFormData.associatedContactIds
            : (taskFormData.associatedContactId ? [taskFormData.associatedContactId] : []);

        let selectedDealIds = taskFormData.associatedDealIds && taskFormData.associatedDealIds.length > 0
            ? taskFormData.associatedDealIds
            : (taskFormData.associatedDealId ? [taskFormData.associatedDealId] : []);

        if (selectedContactIds.length === 0 && effectiveDealId) {
            const dealAssoc = getDealAssociatedContacts(linkedDeal || effectiveDealId);
            if (dealAssoc.length > 0) {
                selectedContactIds = dealAssoc.map(c => c._id);
            }
        }

        if (selectedDealIds.length === 0 && selectedContactIds[0]) {
            const contactAssoc = getContactAssociatedDeals(selectedContactIds[0]);
            if (contactAssoc.length > 0) {
                selectedDealIds = contactAssoc.map(d => d._id);
            }
        }

        const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';

        if (editingTask) {
            const primaryCId = selectedContactIds[0] || '';
            const primaryDId = selectedDealIds[0] || effectiveDealId || '';
            const primaryContact = contacts.find(c => c._id === primaryCId);
            const primaryDeal = deals.find(d => d._id === primaryDId);

            const payload = {
                title: taskFormData.title.trim(),
                description: taskFormData.description.trim(),
                dueDate: taskFormData.dueDate,
                priority: taskFormData.priority,
                status: taskFormData.status,
                completed: taskFormData.status === 'Completed',
                type: taskFormData.type,
                assignee: taskFormData.assignee || authorName,
                associatedDealId: primaryDId || '',
                associatedDealIds: selectedDealIds,
                associatedDealName: primaryDeal ? (primaryDeal.dealName || primaryDeal.clientName) : '',
                associatedContactId: (primaryCId && !primaryCId.startsWith('raw_')) ? primaryCId : '',
                associatedContactIds: selectedContactIds,
                associatedContactName: primaryContact ? `${primaryContact.firstName || ''} ${primaryContact.lastName || ''}`.trim() : ''
            };

            try {
                if (editingTask._id && !editingTask._id.toString().startsWith('task-')) {
                    const res = await api.put(`/pipeline-tasks/${editingTask._id}`, payload);
                    if (res.data) {
                        setTasks(prev => prev.map(t => (String(t._id) === String(editingTask._id) ? res.data : t)));
                    }
                }
            } catch (err) {
                console.warn('Update task API error:', err.message);
            }

            setTasks(prev =>
                prev.map(t =>
                    String(t._id) === String(editingTask._id) ? { ...t, ...payload } : t
                )
            );

            if (payload.associatedDealId) {
                logActivity(payload.associatedDealId, {
                    type: 'task',
                    title: 'Task Updated',
                    content: `Updated task: "${payload.title}" (Due: ${payload.dueDate ? formatDateDDMMYYYY(payload.dueDate) : 'No due date'}, Priority: ${payload.priority}, Assigned to: ${payload.assignee}).${payload.description ? ` Details: ${payload.description}` : ''}`,
                    author: authorName
                });
            }
            for (const cId of selectedContactIds) {
                if (cId && !cId.startsWith('raw_')) {
                    logActivity(cId, {
                        type: 'task',
                        title: 'Task Updated',
                        content: `Updated task: "${payload.title}" (Due: ${payload.dueDate ? formatDateDDMMYYYY(payload.dueDate) : 'No due date'}, Priority: ${payload.priority}, Assigned to: ${payload.assignee}).`,
                        author: authorName
                    }, true);
                }
            }
        } else {
            const newTasksList = [];

            // Determine multiple creation: multiple deals vs multiple contacts
            if (selectedDealIds.length > 1 && selectedContactIds.length <= 1) {
                const contactId = selectedContactIds[0] || '';
                const contactObj = contacts.find(c => c._id === contactId) || (contactId ? { firstName: contactId.replace('raw_', ''), lastName: '', email: contactId.replace('raw_', '') } : null);
                const contactDisplayName = contactObj ? `${contactObj.firstName || ''} ${contactObj.lastName || ''}`.trim() || contactObj.email || contactObj.company : '';

                for (const dId of selectedDealIds) {
                    const currentDeal = deals.find(d => String(d._id) === String(dId));
                    const dealTitle = currentDeal ? (currentDeal.dealName || currentDeal.clientName) : '';

                    const payload = {
                        title: taskFormData.title.trim(),
                        description: taskFormData.description.trim(),
                        dueDate: taskFormData.dueDate,
                        priority: taskFormData.priority,
                        status: taskFormData.status,
                        completed: taskFormData.status === 'Completed',
                        type: taskFormData.type,
                        assignee: taskFormData.assignee || authorName,
                        associatedDealId: dId,
                        associatedDealIds: [dId],
                        associatedDealName: dealTitle,
                        associatedContactId: (contactId && !contactId.startsWith('raw_')) ? contactId : '',
                        associatedContactIds: [contactId].filter(Boolean),
                        associatedContactName: contactDisplayName,
                        createdAt: new Date().toISOString()
                    };

                    let savedTask = null;
                    try {
                        const res = await api.post('/pipeline-tasks', payload);
                        if (res.data) {
                            savedTask = res.data;
                            newTasksList.push(res.data);
                        }
                    } catch (err) {
                        console.warn('Create task API error:', err.message);
                    }

                    if (!savedTask) {
                        const newTask = {
                            _id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
                            ...payload,
                            createdAt: new Date().toISOString(),
                            createdDate: formatDateDDMMYYYY(new Date())
                        };
                        newTasksList.push(newTask);
                    }

                    logActivity(dId, {
                        type: 'task',
                        title: 'Task Created',
                        content: `Created task: "${payload.title}" (Due: ${payload.dueDate ? formatDateDDMMYYYY(payload.dueDate) : 'No due date'}, Priority: ${payload.priority}, Assigned to: ${payload.assignee}).`,
                        author: authorName
                    });
                }

                if (contactId && !contactId.startsWith('raw_')) {
                    logActivity(contactId, {
                        type: 'task',
                        title: `${selectedDealIds.length} Tasks Created`,
                        content: `Created tasks for ${selectedDealIds.length} associated deals: "${taskFormData.title.trim()}".`,
                        author: authorName
                    }, true);
                }
            } else {
                // Targets: multiple contacts (or 1 deal + 1 contact)
                const targets = selectedContactIds.length > 0 ? selectedContactIds : [''];
                const primaryDealId = selectedDealIds[0] || effectiveDealId || '';
                const currentDeal = deals.find(d => String(d._id) === String(primaryDealId));
                const dealTitle = currentDeal ? (currentDeal.dealName || currentDeal.clientName) : '';

                for (const cId of targets) {
                    const contactObj = contacts.find(c => c._id === cId) || (cId ? { firstName: cId.replace('raw_', ''), lastName: '', email: cId.replace('raw_', '') } : null);
                    const contactDisplayName = contactObj ? `${contactObj.firstName || ''} ${contactObj.lastName || ''}`.trim() || contactObj.email || contactObj.company : '';

                    const payload = {
                        title: taskFormData.title.trim(),
                        description: taskFormData.description.trim(),
                        dueDate: taskFormData.dueDate,
                        priority: taskFormData.priority,
                        status: taskFormData.status,
                        completed: taskFormData.status === 'Completed',
                        type: taskFormData.type,
                        assignee: taskFormData.assignee || authorName,
                        associatedDealId: primaryDealId,
                        associatedDealIds: [primaryDealId].filter(Boolean),
                        associatedDealName: dealTitle,
                        associatedContactId: (cId && !cId.startsWith('raw_')) ? cId : '',
                        associatedContactIds: [cId].filter(Boolean),
                        associatedContactName: contactDisplayName,
                        createdAt: new Date().toISOString()
                    };

                    let savedTask = null;
                    try {
                        const res = await api.post('/pipeline-tasks', payload);
                        if (res.data) {
                            savedTask = res.data;
                            newTasksList.push(res.data);
                        }
                    } catch (err) {
                        console.warn('Create task API error:', err.message);
                    }

                    if (!savedTask) {
                        const newTask = {
                            _id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
                            ...payload,
                            createdAt: new Date().toISOString(),
                            createdDate: formatDateDDMMYYYY(new Date())
                        };
                        newTasksList.push(newTask);
                    }

                    if (payload.associatedContactId) {
                        logActivity(payload.associatedContactId, {
                            type: 'task',
                            title: 'Task Created',
                            content: `Created new task: "${payload.title}" (Due: ${payload.dueDate ? formatDateDDMMYYYY(payload.dueDate) : 'No due date'}, Priority: ${payload.priority}, Assigned to: ${payload.assignee}).`,
                            author: authorName
                        }, true);
                    }
                }

                if (primaryDealId) {
                    logActivity(primaryDealId, {
                        type: 'task',
                        title: targets.length > 1 ? `${targets.length} Tasks Created` : 'Task Created',
                        content: targets.length > 1
                            ? `Created tasks for ${targets.length} associated contacts: "${taskFormData.title.trim()}" (Due: ${taskFormData.dueDate ? formatDateDDMMYYYY(taskFormData.dueDate) : 'No due date'}).`
                            : `Created new task: "${taskFormData.title.trim()}" (Due: ${taskFormData.dueDate ? formatDateDDMMYYYY(taskFormData.dueDate) : 'No due date'}, Priority: ${taskFormData.priority}, Assigned to: ${taskFormData.assignee}).${taskFormData.description ? ` Details: ${taskFormData.description}` : ''}`,
                        author: authorName
                    });
                }
            }

            setTasks(prev => [...newTasksList, ...prev]);
        }

        setShowCreateTaskModal(false);
        setEditingTask(null);
    };

    const handleAddActivity = async (e) => {
        e.preventDefault();
        if (!newActivityContent.trim()) return;

        const isContactAct = activeTab === 'contacts' || (!detailedDealId && !!detailedContactId);
        const targetId = isContactAct ? (detailedContact ? detailedContact._id : null) : (detailedDeal ? detailedDeal._id : (detailedContact ? detailedContact._id : null));
        if (!targetId) return;

        const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';

        const titleDefault = newActivityType === 'note'
            ? 'Internal Note'
            : newActivityType === 'email'
                ? (newActivitySubject.trim() || 'Email Logged')
                : 'Message Logged';

        await logActivity(targetId, {
            type: newActivityType,
            title: titleDefault,
            content: newActivityContent.trim(),
            author: authorName
        }, isContactAct);

        setNewActivityContent('');
        setNewActivitySubject('');
        setIsComposingActivity(false);
    };

    const getFollowUpChannelIcon = (channel, size = 16) => {
        const ch = (channel || 'call').toLowerCase();
        if (ch.includes('call') || ch.includes('phone')) return <Phone size={size} />;
        if (ch.includes('meeting')) return <Users size={size} />;
        if (ch.includes('whatsapp') || ch.includes('message')) return <MessageSquare size={size} />;
        if (ch.includes('email') || ch.includes('mail')) return <Mail size={size} />;
        if (ch.includes('demo')) return <Briefcase size={size} />;
        if (ch.includes('visit')) return <Building2 size={size} />;
        return <Phone size={size} />;
    };

    const handleAddFollowUp = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const isContactAct = activeTab === 'contacts' || (!detailedDealId && !!detailedContactId);
        const targetId = isContactAct ? (detailedContact ? detailedContact._id : null) : (detailedDeal ? detailedDeal._id : (detailedContact ? detailedContact._id : null));
        if (!targetId) return;

        if (!followUpFormData.remarks || !followUpFormData.remarks.trim()) {
            alert('Please enter remarks for this follow-up.');
            return;
        }

        setIsSubmittingFollowUp(true);
        try {
            const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';
            const authorRoleName = user?.role?.name || user?.role || (roleName ? roleName.charAt(0).toUpperCase() + roleName.slice(1) : 'Sales Executive');

            const titleDefault = followUpFormData.title.trim() || `${followUpFormData.channel} Follow-up`;
            const now = new Date();
            const formattedDate = formatDateDDMMYYYY(now);
            const formattedTime = now.toLocaleTimeString('en-IN', {
                timeZone: 'Asia/Kolkata',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            }).toUpperCase();

            const formatTime12H = (val) => {
                if (!val) return '';
                if (/am|pm/i.test(val)) return val;
                const [h, m] = val.split(':');
                let hours = parseInt(h, 10);
                if (isNaN(hours)) return val;
                const ampm = hours >= 12 ? 'PM' : 'AM';
                hours = hours % 12;
                hours = hours ? hours : 12;
                const hh = hours < 10 ? `0${hours}` : `${hours}`;
                return `${hh}:${m || '00'} ${ampm}`;
            };

            const followUpActivity = {
                id: `followup-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
                type: 'followup',
                title: titleDefault,
                channel: followUpFormData.channel,
                status: followUpFormData.status,
                followUpDate: followUpFormData.followUpDate,
                followUpTime: formatTime12H(followUpFormData.followUpTime),
                nextFollowUpDate: followUpFormData.nextFollowUpDate || '',
                remarks: followUpFormData.remarks.trim(),
                content: followUpFormData.remarks.trim(),
                memberId: followUpFormData.memberId || '',
                memberName: followUpFormData.memberName || '',
                assignedTo: followUpFormData.memberId || '',
                assignedToName: followUpFormData.memberName || '',
                member: followUpFormData.memberId || '',
                author: authorName,
                authorRole: authorRoleName,
                createdBy: user?._id || user?.id || null,
                time: formattedTime,
                date: formattedDate,
                createdAt: now.toISOString(),
                timestamp: Date.now()
            };

            await logActivity(targetId, followUpActivity, isContactAct);

            setFollowUpFormData({
                title: '',
                memberId: '',
                memberName: '',
                channel: 'Call',
                status: 'Scheduled',
                followUpDate: new Date().toISOString().slice(0, 10),
                followUpTime: '11:00',
                nextFollowUpDate: '',
                remarks: ''
            });
            setIsComposingFollowUp(false);
        } catch (err) {
            console.error('Error logging follow-up:', err);
        } finally {
            setIsSubmittingFollowUp(false);
        }
    };

    const handleUpdateFollowUpStatus = async (actId, newStatus) => {
        const isContactAct = activeTab === 'contacts' || (!detailedDealId && !!detailedContactId);
        const targetId = isContactAct ? (detailedContact ? detailedContact._id : null) : (detailedDeal ? detailedDeal._id : (detailedContact ? detailedContact._id : null));
        if (!targetId) return;

        setDealActivities(prev => {
            const currentList = prev[targetId] || [];
            return {
                ...prev,
                [targetId]: currentList.map(a => (a.id === actId || a._id === actId) ? { ...a, status: newStatus } : a)
            };
        });

        if (isContactAct) {
            setContacts(prev => prev.map(c => {
                if (c._id === targetId) {
                    const acts = (c.activities || []).map(a => (a.id === actId || a._id === actId) ? { ...a, status: newStatus } : a);
                    return { ...c, activities: acts };
                }
                return c;
            }));
        } else {
            setDeals(prev => prev.map(d => {
                if (d._id === targetId) {
                    const acts = (d.activities || []).map(a => (a.id === actId || a._id === actId) ? { ...a, status: newStatus } : a);
                    return { ...d, activities: acts };
                }
                return d;
            }));
        }

        try {
            if (!isContactAct && targetId && !targetId.toString().startsWith('deal-sample-') && !targetId.toString().startsWith('deal-')) {
                await api.patch(`/deals/${targetId}/activities/${actId}`, { status: newStatus });
            }
        } catch (err) {
            console.warn('API update follow-up status error:', err.message);
        }
    };

    const handleDeleteFollowUp = async (actId) => {
        if (deletingFollowUpId === actId) return;
        if (!window.confirm('Are you sure you want to delete this follow-up?')) return;
        setDeletingFollowUpId(actId);
        try {
            const isContactAct = activeTab === 'contacts' || (!detailedDealId && !!detailedContactId);
            const targetId = isContactAct ? (detailedContact ? detailedContact._id : null) : (detailedDeal ? detailedDeal._id : (detailedContact ? detailedContact._id : null));
            if (!targetId) return;

            setDealActivities(prev => {
                const currentList = prev[targetId] || [];
                return {
                    ...prev,
                    [targetId]: currentList.filter(a => a.id !== actId && a._id !== actId)
                };
            });

            if (isContactAct) {
                setContacts(prev => prev.map(c => {
                    if (c._id === targetId) {
                        const acts = (c.activities || []).filter(a => a.id !== actId && a._id !== actId);
                        return { ...c, activities: acts };
                    }
                    return c;
                }));
            } else {
                setDeals(prev => prev.map(d => {
                    if (d._id === targetId) {
                        const acts = (d.activities || []).filter(a => a.id !== actId && a._id !== actId);
                        return { ...d, activities: acts };
                    }
                    return d;
                }));
            }

            if (!isContactAct && targetId && !targetId.toString().startsWith('deal-sample-') && !targetId.toString().startsWith('deal-')) {
                await api.delete(`/deals/${targetId}/activities/${actId}`);
            } else if (isContactAct && targetId && !targetId.toString().startsWith('contact-')) {
                await api.delete(`/pipeline-contacts/${targetId}/activities/${actId}`);
            }
        } catch (err) {
            console.warn('API delete follow-up error:', err.message);
        } finally {
            setDeletingFollowUpId(null);
        }
    };

    const handleToggleReminderStatus = async (reminder, e) => {
        if (e) e.stopPropagation();
        if (!reminder) return;
        const actId = reminder._id || reminder.id;
        if (togglingReminderId === actId) return;

        setTogglingReminderId(actId);
        const targetId = reminder.entityId;
        const isContactAct = reminder.entityType === 'contact';
        const nextStatus = reminder.status === 'Completed' ? 'Scheduled' : 'Completed';

        // Optimistically update React state immediately for instant feedback
        setDealActivities(prev => {
            const currentList = prev[targetId] || [];
            return {
                ...prev,
                [targetId]: currentList.map(a => (String(a.id) === String(actId) || String(a._id) === String(actId)) ? { ...a, status: nextStatus } : a)
            };
        });

        if (isContactAct) {
            setContacts(prev => prev.map(c => {
                if (String(c._id) === String(targetId)) {
                    const acts = (c.activities || []).map(a => (String(a.id) === String(actId) || String(a._id) === String(actId)) ? { ...a, status: nextStatus } : a);
                    return { ...c, activities: acts };
                }
                return c;
            }));
        } else {
            setDeals(prev => prev.map(d => {
                if (String(d._id) === String(targetId)) {
                    const acts = (d.activities || []).map(a => (String(a.id) === String(actId) || String(a._id) === String(actId)) ? { ...a, status: nextStatus } : a);
                    return { ...d, activities: acts };
                }
                return d;
            }));
        }

        try {
            if (isContactAct && targetId && !targetId.toString().startsWith('contact-')) {
                await api.patch(`/pipeline-contacts/${targetId}/activities/${actId}`, { status: nextStatus });
            } else if (!isContactAct && targetId && !targetId.toString().startsWith('deal-sample-') && !targetId.toString().startsWith('deal-')) {
                await api.patch(`/deals/${targetId}/activities/${actId}`, { status: nextStatus });
            }
        } catch (err) {
            console.warn('API update reminder status error:', err.message);
        } finally {
            setTogglingReminderId(null);
        }
    };

    const handleDeleteReminder = async (reminder, e) => {
        if (e) e.stopPropagation();
        if (!canDelete) {
            alert('You do not have permission to delete reminders.');
            return;
        }
        const actId = reminder._id || reminder.id;
        if (deletingReminderId === actId) return;

        if (!window.confirm('Are you sure you want to delete this follow-up reminder?')) return;
        setDeletingReminderId(actId);

        const targetId = reminder.entityId;
        const isContactAct = reminder.entityType === 'contact';

        setDealActivities(prev => {
            const currentList = prev[targetId] || [];
            return {
                ...prev,
                [targetId]: currentList.filter(a => String(a.id) !== String(actId) && String(a._id) !== String(actId))
            };
        });

        if (isContactAct) {
            setContacts(prev => prev.map(c => {
                if (String(c._id) === String(targetId)) {
                    const acts = (c.activities || []).filter(a => String(a.id) !== String(actId) && String(a._id) !== String(actId));
                    return { ...c, activities: acts };
                }
                return c;
            }));
        } else {
            setDeals(prev => prev.map(d => {
                if (String(d._id) === String(targetId)) {
                    const acts = (d.activities || []).filter(a => String(a.id) !== String(actId) && String(a._id) !== String(actId));
                    return { ...d, activities: acts };
                }
                return d;
            }));
        }

        try {
            if (isContactAct && targetId && !targetId.toString().startsWith('contact-')) {
                await api.delete(`/pipeline-contacts/${targetId}/activities/${actId}`);
            } else if (!isContactAct && targetId && !targetId.toString().startsWith('deal-sample-') && !targetId.toString().startsWith('deal-')) {
                await api.delete(`/deals/${targetId}/activities/${actId}`);
            }
        } catch (err) {
            console.warn('API delete reminder error:', err.message);
        } finally {
            setDeletingReminderId(null);
        }
    };

    const handleOpenCreateReminder = (presetType = 'deal', presetId = '') => {
        setEditingReminder(null);
        const initialTargetId = presetId || (presetType === 'deal' ? (deals[0]?._id || '') : (contacts[0]?._id || ''));

        // Auto-select deal/contact owner if Sales Head / Sales Manager / Admin
        let initialMemberId = '';
        let initialMemberName = '';
        if (presetType === 'deal' && initialTargetId && (isSalesHead || isSalesManager || isAdmin)) {
            const targetDeal = deals.find(d => String(d._id) === String(initialTargetId));
            const dOwnerName = (targetDeal?.salesOwner || targetDeal?.addedBy || '').trim();
            if (dOwnerName) {
                const matchedEmp = (employees || []).find(emp => {
                    const eName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                    const eEmail = (emp.email || '').trim().toLowerCase();
                    const eOffEmail = (emp.officialEmail || '').trim().toLowerCase();
                    const target = dOwnerName.toLowerCase();
                    return (eName && eName === target) || (eEmail && eEmail === target) || (eOffEmail && eOffEmail === target);
                });
                if (matchedEmp) {
                    initialMemberId = String(matchedEmp._id || matchedEmp.id);
                    initialMemberName = `${matchedEmp.firstName || ''} ${matchedEmp.lastName || ''}`.trim() || dOwnerName;
                } else {
                    initialMemberId = `name_${dOwnerName}`;
                    initialMemberName = dOwnerName;
                }
            }
        } else if (presetType === 'contact' && initialTargetId && (isSalesHead || isSalesManager || isAdmin)) {
            const targetContact = contacts.find(c => String(c._id) === String(initialTargetId));
            const cOwnerName = (targetContact?.owner || targetContact?.addedBy || '').trim();
            if (cOwnerName) {
                const matchedEmp = (employees || []).find(emp => {
                    const eName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                    const eEmail = (emp.email || '').trim().toLowerCase();
                    const eOffEmail = (emp.officialEmail || '').trim().toLowerCase();
                    const target = cOwnerName.toLowerCase();
                    return (eName && eName === target) || (eEmail && eEmail === target) || (eOffEmail && eOffEmail === target);
                });
                if (matchedEmp) {
                    initialMemberId = String(matchedEmp._id || matchedEmp.id);
                    initialMemberName = `${matchedEmp.firstName || ''} ${matchedEmp.lastName || ''}`.trim() || cOwnerName;
                } else {
                    initialMemberId = `name_${cOwnerName}`;
                    initialMemberName = cOwnerName;
                }
            }
        }

        setReminderFormData({
            targetType: presetType,
            targetId: initialTargetId,
            memberId: initialMemberId,
            memberName: initialMemberName,
            title: '',
            channel: 'Call',
            status: 'Scheduled',
            followUpDate: new Date().toISOString().slice(0, 10),
            followUpTime: '11:00 AM',
            nextFollowUpDate: '',
            remarks: ''
        });
        setShowCreateReminderModal(true);
    };

    const handleOpenEditReminder = (reminder, e) => {
        if (e) e.stopPropagation();
        setEditingReminder(reminder);

        let dateStr = reminder.followUpDate || reminder.date || new Date().toISOString().slice(0, 10);
        const ddmmyyyy = String(dateStr).match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
        if (ddmmyyyy) {
            dateStr = `${ddmmyyyy[3]}-${ddmmyyyy[2].padStart(2, '0')}-${ddmmyyyy[1].padStart(2, '0')}`;
        }

        let nextDateStr = reminder.nextFollowUpDate || '';
        if (nextDateStr) {
            const nextMatch = String(nextDateStr).match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
            if (nextMatch) {
                nextDateStr = `${nextMatch[3]}-${nextMatch[2].padStart(2, '0')}-${nextMatch[1].padStart(2, '0')}`;
            }
        }

        setReminderFormData({
            targetType: reminder.entityType,
            targetId: reminder.entityId,
            memberId: reminder.memberId || reminder.assignedTo?._id || reminder.assignedTo || '',
            memberName: reminder.memberName || reminder.assignedToName || '',
            title: reminder.title || '',
            channel: reminder.channel || 'Call',
            status: reminder.status || 'Scheduled',
            followUpDate: dateStr,
            followUpTime: reminder.followUpTime || '11:00 AM',
            nextFollowUpDate: nextDateStr,
            remarks: reminder.remarks || reminder.content || ''
        });
        setShowCreateReminderModal(true);
    };

    const handleSubmitReminder = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (isSubmittingReminder) return;

        if (!reminderFormData.targetId) {
            alert('Please select an associated Deal or Contact.');
            return;
        }
        if (!reminderFormData.remarks || !reminderFormData.remarks.trim()) {
            alert('Please enter remarks for this follow-up reminder.');
            return;
        }

        setIsSubmittingReminder(true);
        try {
            const isContactAct = reminderFormData.targetType === 'contact';
            const targetId = reminderFormData.targetId;
            const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';
            const authorRoleName = user?.role?.name || user?.role || (roleName ? roleName.charAt(0).toUpperCase() + roleName.slice(1) : 'Sales Executive');

            const formatTime12H = (val) => {
                if (!val) return '';
                if (/am|pm/i.test(val)) return val;
                const [h, m] = val.split(':');
                let hours = parseInt(h, 10);
                if (isNaN(hours)) return val;
                const ampm = hours >= 12 ? 'PM' : 'AM';
                hours = hours % 12;
                hours = hours ? hours : 12;
                const hh = hours < 10 ? `0${hours}` : `${hours}`;
                return `${hh}:${m || '00'} ${ampm}`;
            };

            if (editingReminder) {
                const actId = String(editingReminder._id || editingReminder.id || '');
                const altActId = String(editingReminder.id || editingReminder._id || '');
                const origNormDate = normYMD(editingReminder.followUpDate || editingReminder.date);
                const origNormTime = normTimeStr(editingReminder.followUpTime || editingReminder.time);
                const origChannel = (editingReminder.channel || '').trim().toLowerCase();
                const origTitle = (editingReminder.title || '').trim().toLowerCase();
                const origRemarks = (editingReminder.remarks || editingReminder.content || '').trim().toLowerCase();

                const updatePayload = {
                    title: reminderFormData.title.trim() || `${reminderFormData.channel} Follow-up`,
                    channel: reminderFormData.channel,
                    status: reminderFormData.status,
                    followUpDate: reminderFormData.followUpDate,
                    followUpTime: formatTime12H(reminderFormData.followUpTime),
                    nextFollowUpDate: reminderFormData.nextFollowUpDate || '',
                    remarks: reminderFormData.remarks.trim(),
                    content: reminderFormData.remarks.trim(),
                    memberId: reminderFormData.memberId || '',
                    memberName: reminderFormData.memberName || '',
                    assignedTo: reminderFormData.memberId || '',
                    assignedToName: reminderFormData.memberName || '',
                    member: reminderFormData.memberId || ''
                };

                const isMatchingAct = (a) => {
                    if (!a) return false;
                    const aId = String(a._id || a.id || '');
                    const aAltId = String(a.id || a._id || '');
                    if (actId && (aId === actId || aAltId === actId || aId === altActId || aAltId === altActId)) return true;
                    const aChannel = (a.channel || '').trim().toLowerCase();
                    const aNormDate = normYMD(a.followUpDate || a.date);
                    const aNormTime = normTimeStr(a.followUpTime || a.time);
                    const aTitle = (a.title || '').trim().toLowerCase();
                    const aRemarks = (a.remarks || a.content || '').trim().toLowerCase();
                    if (aChannel === origChannel) {
                        if (aNormDate && origNormDate && aNormDate === origNormDate && (aNormTime === origNormTime || aTitle === origTitle)) return true;
                        if (origRemarks && aRemarks && aRemarks === origRemarks) return true;
                        if (origTitle && aTitle && aTitle === origTitle) return true;
                    }
                    return false;
                };

                // Synchronously resolve the target entity and real MongoDB activity _id BEFORE state updates
                const currentTargetObj = isContactAct
                    ? contacts.find(c => String(c._id) === String(targetId))
                    : deals.find(d => String(d._id) === String(targetId));
                const existingActivities = currentTargetObj?.activities || [];
                const matchedAct = existingActivities.find(isMatchingAct);
                const realMongoId = matchedAct?._id
                    ? String(matchedAct._id)
                    : (actId && !actId.startsWith('deal-act-') && !actId.startsWith('contact-act-') && !actId.startsWith('followup-') ? actId : null);

                // 1. Update entities in state
                if (isContactAct) {
                    setContacts(prev => prev.map(c => {
                        if (String(c._id) === String(targetId)) {
                            const acts = (c.activities || []).map(a => isMatchingAct(a) ? { ...a, ...updatePayload } : a);
                            return { ...c, activities: acts };
                        }
                        return c;
                    }));
                } else {
                    setDeals(prev => prev.map(d => {
                        if (String(d._id) === String(targetId)) {
                            const acts = (d.activities || []).map(a => isMatchingAct(a) ? { ...a, ...updatePayload } : a);
                            return { ...d, activities: acts };
                        }
                        return d;
                    }));
                }

                // 2. Remove matching activity from dealActivities local state to avoid duplicate ghost entries
                setDealActivities(prev => {
                    const currentList = prev[targetId] || [];
                    return {
                        ...prev,
                        [targetId]: currentList.filter(a => !isMatchingAct(a))
                    };
                });

                // 3. Sync update with backend
                try {
                    const patchId = realMongoId || actId;
                    if (patchId && !patchId.startsWith('deal-act-') && !patchId.startsWith('contact-act-') && !patchId.startsWith('followup-')) {
                        if (isContactAct && targetId && !targetId.toString().startsWith('contact-')) {
                            await api.patch(`/pipeline-contacts/${targetId}/activities/${patchId}`, updatePayload);
                        } else if (!isContactAct && targetId && !targetId.toString().startsWith('deal-sample-') && !targetId.toString().startsWith('deal-')) {
                            await api.patch(`/deals/${targetId}/activities/${patchId}`, updatePayload);
                        }
                    }
                } catch (err) {
                    console.warn('API edit reminder error:', err.message);
                }
            } else {
                const now = new Date();
                const formattedDate = formatDateDDMMYYYY(now);
                const formattedTime = now.toLocaleTimeString('en-IN', {
                    timeZone: 'Asia/Kolkata',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                }).toUpperCase();

                const newAct = {
                    id: `followup-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
                    type: 'followup',
                    title: reminderFormData.title.trim() || `${reminderFormData.channel} Follow-up`,
                    channel: reminderFormData.channel,
                    status: reminderFormData.status,
                    followUpDate: reminderFormData.followUpDate,
                    followUpTime: formatTime12H(reminderFormData.followUpTime),
                    nextFollowUpDate: reminderFormData.nextFollowUpDate || '',
                    remarks: reminderFormData.remarks.trim(),
                    content: reminderFormData.remarks.trim(),
                    memberId: reminderFormData.memberId || '',
                    memberName: reminderFormData.memberName || '',
                    assignedTo: reminderFormData.memberId || '',
                    assignedToName: reminderFormData.memberName || '',
                    member: reminderFormData.memberId || '',
                    author: authorName,
                    authorRole: authorRoleName,
                    createdBy: user?._id || user?.id || null,
                    time: formattedTime,
                    date: formattedDate,
                    createdAt: now.toISOString(),
                    timestamp: Date.now()
                };

                await logActivity(targetId, newAct, isContactAct);
            }

            setShowCreateReminderModal(false);
            setEditingReminder(null);
        } finally {
            setIsSubmittingReminder(false);
        }
    };

    const handleExportReminders = () => {
        if (!filteredReminders.length) {
            alert('No reminders available to export.');
            return;
        }
        const dataToExport = filteredReminders.map(r => ({
            'Entity Type': r.entityType === 'deal' ? 'Deal' : 'Contact',
            'Entity Name': r.entityName || '',
            'Title / Purpose': r.title || '',
            'Channel': r.channel || '',
            'Status': r.status || 'Scheduled',
            'Scheduled Date': r.followUpDate ? formatDateDDMMYYYY(r.followUpDate) : '',
            'Scheduled Time': r.followUpTime || '',
            'Next Follow-up Date': r.nextFollowUpDate ? formatDateDDMMYYYY(r.nextFollowUpDate) : '',
            'Remarks': r.remarks || r.content || '',
            'Created By': r.author || '',
            'Contact Email': r.contactEmail || '',
            'Contact Phone': r.contactPhone || ''
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Reminders & Follow-ups');
        XLSX.writeFile(workbook, `Reminders_Followups_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const convertTimeTo24H = (timeStr) => {
        if (!timeStr) return '11:00';
        const clean = String(timeStr).trim();
        if (/^\d{2}:\d{2}$/.test(clean)) return clean;
        const match = clean.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
        if (!match) return '11:00';
        let h = parseInt(match[1], 10);
        const m = match[2];
        const period = match[3] ? match[3].toUpperCase() : null;
        if (period === 'PM' && h !== 12) h += 12;
        if (period === 'AM' && h === 12) h = 0;
        return `${String(h).padStart(2, '0')}:${m}`;
    };

    const handleOpenEditFollowUp = (act, entityType = null, entityId = null) => {
        if (!act) return;

        const determinedEntityType = entityType || (activeTab === 'contacts' || (!detailedDealId && !!detailedContactId) ? 'contact' : 'deal');
        const determinedEntityId = entityId || (determinedEntityType === 'contact' ? (detailedContact ? detailedContact._id : null) : (detailedDeal ? detailedDeal._id : null));

        let dateStr = act.followUpDate || act.date || new Date().toISOString().slice(0, 10);
        const ddmmyyyy = String(dateStr).match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
        if (ddmmyyyy) {
            dateStr = `${ddmmyyyy[3]}-${ddmmyyyy[2].padStart(2, '0')}-${ddmmyyyy[1].padStart(2, '0')}`;
        }

        setEditingFollowUp({
            ...act,
            _targetEntityType: determinedEntityType,
            _targetEntityId: determinedEntityId
        });

        setEditFollowUpForm({
            id: act.id || act._id,
            title: act.title || '',
            memberId: act.memberId || act.assignedTo?._id || act.assignedTo || '',
            memberName: act.memberName || act.assignedToName || '',
            channel: act.channel || 'Call',
            status: act.status || 'Scheduled',
            followUpDate: dateStr,
            followUpTime: convertTimeTo24H(act.followUpTime),
            nextFollowUpDate: act.nextFollowUpDate || '',
            remarks: act.remarks || act.content || ''
        });
    };

    const handleSaveEditFollowUp = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!editingFollowUp) return;

        if (!editFollowUpForm.remarks || !editFollowUpForm.remarks.trim()) {
            alert('Please enter remarks for this follow-up.');
            return;
        }

        // Allow users to freely set/update follow-up date and time without blocking past timestamps

        const isContactAct = editingFollowUp._targetEntityType === 'contact' || activeTab === 'contacts' || (!detailedDealId && !!detailedContactId);
        const targetId = editingFollowUp._targetEntityId || (isContactAct ? (detailedContact ? detailedContact._id : null) : (detailedDeal ? detailedDeal._id : (detailedContact ? detailedContact._id : null)));
        if (!targetId) return;

        const actId = editingFollowUp.id || editingFollowUp._id;

        const formatTime12H = (val) => {
            if (!val) return '';
            if (/am|pm/i.test(val)) return val;
            const [h, m] = val.split(':');
            let hours = parseInt(h, 10);
            if (isNaN(hours)) return val;
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            const hh = hours < 10 ? `0${hours}` : `${hours}`;
            return `${hh}:${m || '00'} ${ampm}`;
        };

        const formattedTime12 = formatTime12H(editFollowUpForm.followUpTime);
        const updatedPayload = {
            title: editFollowUpForm.title.trim() || `${editFollowUpForm.channel} Follow-up`,
            channel: editFollowUpForm.channel,
            status: editFollowUpForm.status,
            followUpDate: editFollowUpForm.followUpDate,
            followUpTime: formattedTime12,
            nextFollowUpDate: editFollowUpForm.nextFollowUpDate || '',
            remarks: editFollowUpForm.remarks.trim(),
            content: editFollowUpForm.remarks.trim(),
            memberId: editFollowUpForm.memberId || '',
            memberName: editFollowUpForm.memberName || '',
            assignedTo: editFollowUpForm.memberId || '',
            assignedToName: editFollowUpForm.memberName || '',
            member: editFollowUpForm.memberId || ''
        };

        setIsSavingEditFollowUp(true);

        try {
            const actId = String(editingFollowUp.id || editingFollowUp._id || '');
            const altActId = String(editingFollowUp._id || editingFollowUp.id || '');
            const origNormDate = normYMD(editingFollowUp.followUpDate || editingFollowUp.date);
            const origNormTime = normTimeStr(editingFollowUp.followUpTime || editingFollowUp.time);
            const origChannel = (editingFollowUp.channel || '').trim().toLowerCase();
            const origTitle = (editingFollowUp.title || '').trim().toLowerCase();
            const origRemarks = (editingFollowUp.remarks || editingFollowUp.content || '').trim().toLowerCase();

            const isMatchingAct = (a) => {
                if (!a) return false;
                const aId = String(a._id || a.id || '');
                const aAltId = String(a.id || a._id || '');
                if (actId && (aId === actId || aAltId === actId || aId === altActId || aAltId === altActId)) return true;
                const aChannel = (a.channel || '').trim().toLowerCase();
                const aNormDate = normYMD(a.followUpDate || a.date);
                const aNormTime = normTimeStr(a.followUpTime || a.time);
                const aTitle = (a.title || '').trim().toLowerCase();
                const aRemarks = (a.remarks || a.content || '').trim().toLowerCase();
                if (aChannel === origChannel) {
                    if (aNormDate && origNormDate && aNormDate === origNormDate && (aNormTime === origNormTime || aTitle === origTitle)) return true;
                    if (origRemarks && aRemarks && aRemarks === origRemarks) return true;
                    if (origTitle && aTitle && aTitle === origTitle) return true;
                }
                return false;
            };

            // Synchronously resolve the target entity and real MongoDB activity _id BEFORE state updates
            const currentTargetObj = isContactAct
                ? contacts.find(c => String(c._id) === String(targetId))
                : deals.find(d => String(d._id) === String(targetId));
            const existingActivities = currentTargetObj?.activities || [];
            const matchedAct = existingActivities.find(isMatchingAct);
            const realMongoId = matchedAct?._id
                ? String(matchedAct._id)
                : (actId && !actId.startsWith('deal-act-') && !actId.startsWith('contact-act-') && !actId.startsWith('followup-') ? actId : null);

            // 1. Update entities in state
            if (isContactAct) {
                setContacts(prev => prev.map(c => {
                    if (String(c._id) === String(targetId)) {
                        const acts = (c.activities || []).map(a => isMatchingAct(a) ? { ...a, ...updatedPayload } : a);
                        return { ...c, activities: acts };
                    }
                    return c;
                }));
            } else {
                setDeals(prev => prev.map(d => {
                    if (String(d._id) === String(targetId)) {
                        const acts = (d.activities || []).map(a => isMatchingAct(a) ? { ...a, ...updatedPayload } : a);
                        return { ...d, activities: acts };
                    }
                    return d;
                }));
            }

            // 2. Remove matching activity from dealActivities local state to avoid duplicate ghost entries
            setDealActivities(prev => {
                const currentList = prev[targetId] || [];
                return {
                    ...prev,
                    [targetId]: currentList.filter(a => !isMatchingAct(a))
                };
            });

            // 3. Sync update with backend
            try {
                const patchId = realMongoId || actId;
                if (patchId && !patchId.startsWith('deal-act-') && !patchId.startsWith('contact-act-') && !patchId.startsWith('followup-')) {
                    if (isContactAct && targetId && !targetId.toString().startsWith('contact-')) {
                        await api.patch(`/pipeline-contacts/${targetId}/activities/${patchId}`, updatedPayload);
                    } else if (!isContactAct && targetId && !targetId.toString().startsWith('deal-sample-') && !targetId.toString().startsWith('deal-')) {
                        await api.patch(`/deals/${targetId}/activities/${patchId}`, updatedPayload);
                    }
                }
            } catch (err) {
                console.warn('API edit follow-up error:', err.message);
            }

            setEditingFollowUp(null);
        } catch (err) {
            console.error('Error saving follow-up edit:', err);
            alert(err.response?.data?.message || 'Failed to update follow-up. Please try again.');
        } finally {
            setIsSavingEditFollowUp(false);
        }
    };

    const [companyFormData, setCompanyFormData] = useState({
        name: '',
        domain: '',
        owner: [],
        industry: '',
        country: '',
        state: '',
        city: '',
        postalCode: ''
    });

    const [showValidDomainMsg, setShowValidDomainMsg] = useState(false);

    useEffect(() => {
        if (companyFormData.domain && companyFormData.domain.trim() && isValidCompanyDomain(companyFormData.domain)) {
            setShowValidDomainMsg(true);
            const timer = setTimeout(() => {
                setShowValidDomainMsg(false);
            }, 2000);
            return () => clearTimeout(timer);
        } else {
            setShowValidDomainMsg(false);
        }
    }, [companyFormData.domain]);

    // Dynamic States/Regions Filtered by Selected Country
    const availableCompanyStates = useMemo(() => {
        if (!companyFormData.country) return [];
        return COUNTRY_STATE_MAP[companyFormData.country] || [];
    }, [companyFormData.country]);

    // Filtered Companies Memo
    const filteredCompanies = useMemo(() => {
        return companies
            .filter(company => {
                if (!isItemOwnedByUser(company, 'company')) {
                    return false;
                }

                if (companySearchQuery.trim()) {
                    const q = companySearchQuery.toLowerCase();
                    const name = (company.name || '').toLowerCase();
                    const domain = (company.domain || '').toLowerCase();
                    const industry = (company.industry || '').toLowerCase();
                    const city = (company.city || '').toLowerCase();
                    const state = (company.state || '').toLowerCase();
                    const country = (company.country || '').toLowerCase();
                    const ownerVal = Array.isArray(company.owner) ? company.owner.join(' ').toLowerCase() : (company.owner || '').toLowerCase();
                    if (
                        !name.includes(q) &&
                        !domain.includes(q) &&
                        !industry.includes(q) &&
                        !city.includes(q) &&
                        !state.includes(q) &&
                        !country.includes(q) &&
                        !ownerVal.includes(q)
                    ) {
                        return false;
                    }
                }

                if (companyIndustryFilter !== 'all') {
                    const ind = (company.industry || '').toLowerCase();
                    if (ind !== companyIndustryFilter.toLowerCase()) {
                        return false;
                    }
                }

                if (companyOwnerFilter !== 'all') {
                    const fOwner = companyOwnerFilter.trim().toLowerCase();
                    const cOwner = (Array.isArray(company.owner) ? company.owner.join(' ') : (company.owner || '')).trim().toLowerCase();
                    if (!cOwner.includes(fOwner) && cOwner !== fOwner) {
                        return false;
                    }
                }

                if (companyDateFilter !== 'all') {
                    const compDate = parseDealDate(company.createdAt) || parseDealDate(company.createdDate) || parseDealDate(company.date);
                    if (!compDate) return false;
                    const now = new Date();
                    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

                    if (companyDateFilter === 'today') {
                        if (compDate < startOfToday || compDate > endOfToday) return false;
                    } else if (companyDateFilter === 'this_week') {
                        const firstDayOfWeek = new Date(startOfToday);
                        const day = firstDayOfWeek.getDay();
                        const diff = firstDayOfWeek.getDate() - day + (day === 0 ? -6 : 1);
                        firstDayOfWeek.setDate(diff);
                        const lastDayOfWeek = new Date(firstDayOfWeek);
                        lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
                        lastDayOfWeek.setHours(23, 59, 59, 999);
                        if (compDate < firstDayOfWeek || compDate > lastDayOfWeek) return false;
                    } else if (companyDateFilter === 'this_month') {
                        if (compDate.getMonth() !== now.getMonth() || compDate.getFullYear() !== now.getFullYear()) return false;
                    } else if (companyDateFilter === 'next_30_days') {
                        const in30Days = new Date(startOfToday);
                        in30Days.setDate(in30Days.getDate() + 30);
                        in30Days.setHours(23, 59, 59, 999);
                        if (compDate < startOfToday || compDate > in30Days) return false;
                    } else if (companyDateFilter === 'this_quarter') {
                        const currentQuarter = Math.floor(now.getMonth() / 3);
                        const compQuarter = Math.floor(compDate.getMonth() / 3);
                        if (compQuarter !== currentQuarter || compDate.getFullYear() !== now.getFullYear()) return false;
                    } else if (companyDateFilter === 'this_year') {
                        if (compDate.getFullYear() !== now.getFullYear()) return false;
                    } else if (companyDateFilter === 'custom') {
                        if (customCompanyDateStart) {
                            const start = new Date(customCompanyDateStart);
                            start.setHours(0, 0, 0, 0);
                            if (compDate < start) return false;
                        }
                        if (customCompanyDateEnd) {
                            const end = new Date(customCompanyDateEnd);
                            end.setHours(23, 59, 59, 999);
                            if (compDate > end) return false;
                        }
                    }
                }

                return true;
            })
            .sort((a, b) => {
                if (companySortBy === 'name_asc') {
                    return (a.name || '').localeCompare(b.name || '');
                }
                if (companySortBy === 'name_desc') {
                    return (b.name || '').localeCompare(a.name || '');
                }
                if (companySortBy === 'industry') {
                    return (a.industry || '').localeCompare(b.industry || '');
                }
                if (companySortBy === 'country') {
                    return (a.country || '').localeCompare(b.country || '');
                }
                if (companySortBy === 'oldest') {
                    const da = parseDealDate(a.createdAt) || parseDealDate(a.createdDate) || parseDealDate(a.date) || new Date(0);
                    const db = parseDealDate(b.createdAt) || parseDealDate(b.createdDate) || parseDealDate(b.date) || new Date(0);
                    return da - db;
                }
                // newest
                const da = parseDealDate(a.createdAt) || parseDealDate(a.createdDate) || parseDealDate(a.date) || new Date(0);
                const db = parseDealDate(b.createdAt) || parseDealDate(b.createdDate) || parseDealDate(b.date) || new Date(0);
                return db - da;
            });
    }, [companies, companySearchQuery, companyIndustryFilter, companyOwnerFilter, companyDateFilter, customCompanyDateStart, customCompanyDateEnd, companySortBy, isItemOwnedByUser]);

    // Associated Contact and Company matching for Detailed Deal View
    const matchedContact = useMemo(() => {
        if (!detailedDeal) return null;
        return (
            contacts.find(
                c =>
                    c.email === detailedDeal.associatedContact ||
                    c.email === detailedDeal.email ||
                    `${c.firstName} ${c.lastName}`.trim().toLowerCase() === (detailedDeal.clientName || '').toLowerCase()
            ) || {
                firstName: (detailedDeal.clientName || 'Client').split(' ')[0] || 'Client',
                lastName: (detailedDeal.clientName || '').split(' ').slice(1).join(' ') || '',
                email: detailedDeal.associatedContact || detailedDeal.email || 'contact@example.com',
                phone: detailedDeal.phone || '+91 98765 43210',
                countryCode: '+91',
                leadStage: 'Lead',
                leadStatus: 'Active',
                contactSource: 'Direct Reference',
                owner: detailedDeal.salesOwner || (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Tejaswi Yadav') || 'Tejaswi Yadav'
            }
        );
    }, [detailedDeal, contacts]);

    const matchedCompany = useMemo(() => {
        if (!detailedDeal) return null;
        return (
            companies.find(
                comp =>
                    comp.name === detailedDeal.associatedCompany ||
                    comp._id === detailedDeal.associatedCompany ||
                    comp.name.toLowerCase() === (detailedDeal.company || '').toLowerCase()
            ) || {
                name: detailedDeal.associatedCompany || 'Bhoomi Tech Enterprises',
                domain: 'bhoomi.com',
                owner: detailedDeal.salesOwner || 'Sahil Singh',
                industry: 'Software & Technology',
                country: 'India',
                state: 'Maharashtra',
                city: detailedDeal.location || 'Pune',
                postalCode: '411001'
            }
        );
    }, [detailedDeal, companies]);

    const handleAddPipeline = async (customName) => {
        if (!hasFullAccess) {
            alert('Only Admin, Sales Head, and Sales Manager can add new stages.');
            return;
        }
        const trimmed = (typeof customName === 'string' ? customName : (addPipelineModalName || newPipelineName)).trim();
        if (!trimmed) return;
        if (pipelines.some(p => p.name.toLowerCase() === trimmed.toLowerCase())) {
            alert('A pipeline with this name already exists');
            return;
        }
        const badgeClasses = ['new', 'hot', 'cold', 'won', 'old', 'custom'];
        const randomBadge = badgeClasses[pipelines.length % badgeClasses.length];
        const newP = { id: trimmed, name: trimmed, badgeClass: randomBadge, createdAt: new Date().toISOString() };
        const nextPipelines = [...pipelines, newP];
        const nextOptions = pipelineOptions.includes(trimmed) ? pipelineOptions : [...pipelineOptions, trimmed];

        let deletedPipelines = [];
        try {
            deletedPipelines = JSON.parse(localStorage.getItem('crm_deleted_pipelines') || '[]');
            deletedPipelines = deletedPipelines.filter(d => d !== trimmed.toLowerCase());
            localStorage.setItem('crm_deleted_pipelines', JSON.stringify(deletedPipelines));
        } catch (e) { }

        setPipelines(nextPipelines);
        setPipelineOptions(nextOptions);
        setNewPipelineName('');
        setAddPipelineModalName('');
        setShowAddPipelineModal(false);
        setShowAddPipelineInput(false);

        try {
            localStorage.setItem('crm_pipelines', JSON.stringify(nextPipelines));
            localStorage.setItem('crm_pipeline_options', JSON.stringify(nextOptions));
            await api.post('/pipeline-configs/save', {
                pipelines: nextPipelines,
                pipelineOptions: nextOptions,
                deletedPipelines
            });
        } catch (err) {
            console.warn('Save pipeline to backend error:', err.message);
        }
    };

    const handleStartEditPipeline = (pipeline, index, e) => {
        if (e) e.stopPropagation();
        if (!hasFullAccess) {
            alert('Only Admin, Sales Head, and Sales Manager can edit stage names.');
            return;
        }
        const key = `pipeline-col-${index}`;
        setEditingPipelineId(key);
        setEditingPipelineName(pipeline.name || '');
        setActiveColMenuId(null);
    };

    const handleSaveEditPipeline = async (pipelineId, targetIndex, overrideName, overrideOldName) => {
        if (!hasFullAccess) {
            alert('Only Admin, Sales Head, and Sales Manager can edit stage names.');
            setEditingPipelineId(null);
            return;
        }
        const trimmed = (typeof overrideName === 'string' ? overrideName : editingPipelineName).trim();
        if (!trimmed) {
            setEditingPipelineId(null);
            return;
        }
        const target = (typeof targetIndex === 'number' && targetIndex >= 0 && pipelines[targetIndex])
            ? pipelines[targetIndex]
            : pipelines.find((p, idx) => (p._id && p._id === pipelineId) || (`pipeline-col-${idx}` === pipelineId));

        const oldName = overrideOldName || (target ? target.name : pipelineId);
        const oldId = target ? (target.id || target._id) : pipelineId;

        const nextPipelines = pipelines.map((p, idx) => {
            const isMatch = (typeof targetIndex === 'number' && targetIndex >= 0)
                ? idx === targetIndex
                : ((p._id && p._id === pipelineId) || (`pipeline-col-${idx}` === pipelineId));
            return isMatch ? { ...p, name: trimmed, id: trimmed } : p;
        });
        const nextOptions = pipelineOptions.map(p => (p === oldName ? trimmed : p));
        if (!nextOptions.includes(trimmed)) nextOptions.push(trimmed);

        const nextStages = (stages || []).map(s => ((s.id === oldName || s.name === oldName || s.id === oldId) ? { ...s, name: trimmed, id: trimmed } : s));
        if (!nextStages.some(s => (s.name || s.id || '').toLowerCase() === trimmed.toLowerCase())) {
            nextStages.push({ id: trimmed, name: trimmed, badgeClass: 'custom', createdAt: new Date().toISOString() });
        }
        setPipelines(nextPipelines);
        setPipelineOptions(nextOptions);
        setStages(nextStages);
        setDeals(prev => prev.map(d => (d.pipeline === oldName || d.pipeline === oldId || d.stage === oldName || d.stage === oldId ? { ...d, pipeline: trimmed, stage: trimmed } : d)));
        setEditingPipelineId(null);
        setEditingPipelineName('');

        try {
            localStorage.setItem('crm_pipelines', JSON.stringify(nextPipelines));
            localStorage.setItem('crm_pipeline_options', JSON.stringify(nextOptions));
            localStorage.setItem('crm_stages', JSON.stringify(nextStages));

            const affected = deals.filter(d => d.pipeline === oldName || d.pipeline === oldId || d.stage === oldName || d.stage === oldId);
            for (const d of affected) {
                if (d._id && !d._id.toString().startsWith('deal-')) {
                    await api.put(`/deals/${d._id}`, { pipeline: trimmed, stage: trimmed });
                }
            }

            await api.post('/pipeline-configs/save', {
                pipelines: nextPipelines,
                stages: nextStages,
                pipelineOptions: nextOptions
            });
        } catch (err) {
            console.warn('Save edited pipeline to backend:', err.message);
        }
    };

    const handleDeletePipeline = async (pipelineId, e, targetIndex, fallbackName) => {
        if (e) e.stopPropagation();
        if (!hasFullAccess) {
            alert('You do not have permission to delete stages. Only Admin, Sales Head, and Sales Manager can delete stages.');
            return;
        }
        const target = (typeof targetIndex === 'number' && targetIndex >= 0 && pipelines[targetIndex])
            ? pipelines[targetIndex]
            : pipelines.find((p, idx) => (p._id && p._id === pipelineId) || (p.id && p.id === pipelineId) || (`pipeline-col-${idx}` === pipelineId));
        const pipelineName = fallbackName || (target ? target.name : pipelineId);

        if (window.confirm(`Are you sure you want to delete the "${pipelineName}" stage?`)) {
            const remaining = pipelines.filter((p, idx) => {
                if (typeof targetIndex === 'number' && targetIndex >= 0) {
                    return idx !== targetIndex;
                }
                return p.id !== pipelineId && p.name !== pipelineName && `pipeline-col-${idx}` !== pipelineId;
            });
            const remainingOptions = pipelineOptions.filter(p => p !== pipelineName && p !== pipelineId);
            const remainingStages = (stages || []).filter(s => s.id !== pipelineId && s.name !== pipelineName);
            setPipelines(remaining);
            setPipelineOptions(remainingOptions);
            setStages(remainingStages);

            let deletedPipelines = [];
            try {
                deletedPipelines = JSON.parse(localStorage.getItem('crm_deleted_pipelines') || '[]');
            } catch (err) { }
            deletedPipelines.push(pipelineName.toLowerCase());
            deletedPipelines.push(String(pipelineId).toLowerCase());
            const uniqueDeleted = Array.from(new Set(deletedPipelines));

            try {
                localStorage.setItem('crm_pipelines', JSON.stringify(remaining));
                localStorage.setItem('crm_stages', JSON.stringify(remainingStages));
                localStorage.setItem('crm_pipeline_options', JSON.stringify(remainingOptions));
                localStorage.setItem('crm_deleted_pipelines', JSON.stringify(uniqueDeleted));
                localStorage.setItem('crm_deleted_stages', JSON.stringify(uniqueDeleted));
            } catch (err) { }

            const fallbackPipeline = remaining.length > 0 ? remaining[0].name : '';
            setDeals(prev => prev.map(d => (d.pipeline === pipelineName || d.pipeline === pipelineId || d.stage === pipelineName || d.stage === pipelineId ? { ...d, pipeline: fallbackPipeline, stage: fallbackPipeline } : d)));
            setActiveColMenuId(null);

            // Persist fallback pipeline to backend for affected deals & sync deletedPipelines & deletedStages to database
            try {
                const affected = deals.filter(d => d.pipeline === pipelineName || d.pipeline === pipelineId || d.stage === pipelineName || d.stage === pipelineId);
                for (const d of affected) {
                    if (d._id && !d._id.toString().startsWith('deal-')) {
                        await api.put(`/deals/${d._id}`, { pipeline: fallbackPipeline, stage: fallbackPipeline });
                    }
                }

                await api.post('/pipeline-configs/save', {
                    pipelines: remaining,
                    stages: remainingStages,
                    pipelineOptions: remainingOptions,
                    deletedPipelines: uniqueDeleted,
                    deletedStages: uniqueDeleted
                });
            } catch (err) {
                console.warn('Save deleted pipeline to backend:', err.message);
            }
        }
    };

    // Contact Stage Handlers
    const handleAddContactStage = async (customName) => {
        if (!hasFullAccess) {
            alert('Only Admin, Sales Head, and Sales Manager can add new stages.');
            return;
        }
        const trimmed = (typeof customName === 'string' ? customName : (addStageModalName || newContactStageName)).trim();
        if (!trimmed) return;
        if (contactStages.some(s => s.name.toLowerCase() === trimmed.toLowerCase())) {
            alert('A stage with this name already exists');
            return;
        }
        const badgeClasses = ['new', 'hot', 'cold', 'won', 'custom'];
        const randomBadge = badgeClasses[contactStages.length % badgeClasses.length];
        const newStage = { id: trimmed, name: trimmed, badgeClass: randomBadge, createdAt: new Date().toISOString() };
        const nextStages = [...contactStages, newStage];
        const nextLeadStages = leadStages.includes(trimmed) ? leadStages : [...leadStages, trimmed];

        let deletedStages = [];
        try {
            deletedStages = JSON.parse(localStorage.getItem('crm_deleted_stages') || '[]');
            deletedStages = deletedStages.filter(d => d !== trimmed.toLowerCase());
            localStorage.setItem('crm_deleted_stages', JSON.stringify(deletedStages));
        } catch (e) { }

        setContactStages(nextStages);
        setLeadStages(nextLeadStages);
        setNewContactStageName('');
        setAddStageModalName('');
        setShowAddStageModal(false);
        setShowAddContactStageInput(false);

        try {
            localStorage.setItem('crm_contact_stages', JSON.stringify(nextStages));
            localStorage.setItem('crm_lead_stages', JSON.stringify(nextLeadStages));
            await api.post('/pipeline-configs/save', {
                contactStages: nextStages,
                leadStages: nextLeadStages,
                deletedStages
            });
        } catch (err) {
            console.warn('Save contact stage to backend:', err.message);
        }
    };

    const handleStartEditContactStage = (stage, index, e) => {
        if (e) e.stopPropagation();
        if (!hasFullAccess) {
            alert('Only Admin, Sales Head, and Sales Manager can edit stage names.');
            return;
        }
        const key = `contact-stage-col-${index}`;
        setEditingContactStageId(key);
        setEditingContactStageName(stage.name || '');
        setActiveColMenuId(null);
    };

    const handleSaveEditContactStage = async (stageId, targetIndex, overrideName, overrideOldName) => {
        if (!hasFullAccess) {
            alert('Only Admin, Sales Head, and Sales Manager can edit stage names.');
            setEditingContactStageId(null);
            return;
        }
        const trimmed = (typeof overrideName === 'string' ? overrideName : editingContactStageName).trim();
        if (!trimmed) {
            setEditingContactStageId(null);
            return;
        }
        const target = (typeof targetIndex === 'number' && targetIndex >= 0 && effectiveContactStages[targetIndex])
            ? effectiveContactStages[targetIndex]
            : effectiveContactStages.find((s, idx) => (s._id && s._id === stageId) || (`contact-stage-col-${idx}` === stageId));

        const oldName = overrideOldName || (target ? target.name : stageId);
        const oldId = target ? (target.id || target._id) : stageId;

        let foundInExisting = false;
        const nextStages = contactStages.map((s, idx) => {
            const isMatch = (typeof targetIndex === 'number' && targetIndex >= 0)
                ? idx === targetIndex
                : ((s._id && s._id === stageId) || (`contact-stage-col-${idx}` === stageId));
            if (isMatch) foundInExisting = true;
            return isMatch ? { ...s, name: trimmed, id: trimmed } : s;
        });

        if (!foundInExisting && !nextStages.some(s => (s.name || s.id || '').toLowerCase() === trimmed.toLowerCase())) {
            nextStages.push({ id: trimmed, name: trimmed, badgeClass: 'new', isCustom: true });
        }

        const nextLeadStages = leadStages.map(s => (s === oldName ? trimmed : s));
        if (!nextLeadStages.includes(trimmed)) nextLeadStages.push(trimmed);

        setContactStages(nextStages);
        setLeadStages(nextLeadStages);
        setContacts(prev => prev.map(c => ((c.leadStage || '').toLowerCase() === oldName.toLowerCase() || (c.leadStage || '').toLowerCase() === String(oldId).toLowerCase() ? { ...c, leadStage: trimmed } : c)));
        setEditingContactStageId(null);
        setEditingContactStageName('');

        try {
            localStorage.setItem('crm_contact_stages', JSON.stringify(nextStages));
            localStorage.setItem('crm_lead_stages', JSON.stringify(nextLeadStages));

            const affected = contacts.filter(c => (c.leadStage || '').toLowerCase() === oldName.toLowerCase() || (c.leadStage || '').toLowerCase() === String(oldId).toLowerCase());
            for (const c of affected) {
                if (c._id && !c._id.toString().startsWith('contact-')) {
                    await api.put(`/pipeline-contacts/${c._id}`, { leadStage: trimmed });
                }
            }

            await api.post('/pipeline-configs/save', {
                contactStages: nextStages,
                leadStages: nextLeadStages
            });
        } catch (err) {
            console.warn('Save edited contact stage to backend:', err.message);
        }
    };

    const handleDeleteContactStage = async (stageId, e, targetIndex, fallbackName) => {
        if (e) e.stopPropagation();
        if (!hasFullAccess) {
            alert('You do not have permission to delete stages. Only Admin, Sales Head, and Sales Manager can delete stages.');
            return;
        }
        const cleanId = String(stageId || '').trim();
        const stageToDelete = (typeof targetIndex === 'number' && targetIndex >= 0 && effectiveContactStages[targetIndex])
            ? effectiveContactStages[targetIndex]
            : contactStages.find((s, idx) => (s.id || '').toLowerCase() === cleanId.toLowerCase() || (s.name || '').toLowerCase() === cleanId.toLowerCase() || `contact-stage-col-${idx}` === cleanId);
        const stageName = fallbackName || (stageToDelete ? stageToDelete.name : cleanId);

        if (window.confirm(`Are you sure you want to delete stage "${stageName}"?`)) {
            const remaining = contactStages.filter((s, idx) => {
                if (typeof targetIndex === 'number' && targetIndex >= 0) {
                    return idx !== targetIndex;
                }
                return (s.id || '').toLowerCase() !== cleanId.toLowerCase() &&
                    (s.name || '').toLowerCase() !== stageName.toLowerCase() &&
                    `contact-stage-col-${idx}` !== cleanId;
            });
            const remainingLeadStages = leadStages.filter(s =>
                String(s).toLowerCase() !== cleanId.toLowerCase() &&
                String(s).toLowerCase() !== stageName.toLowerCase()
            );

            setContactStages(remaining);
            setLeadStages(remainingLeadStages);

            let deletedStages = [];
            try {
                deletedStages = JSON.parse(localStorage.getItem('crm_deleted_stages') || '[]');
            } catch (err) { }
            deletedStages.push(stageName.toLowerCase().trim());
            deletedStages.push(cleanId.toLowerCase().trim());
            const uniqueDeleted = Array.from(new Set(deletedStages.filter(Boolean)));

            try {
                localStorage.setItem('crm_contact_stages', JSON.stringify(remaining));
                localStorage.setItem('crm_lead_stages', JSON.stringify(remainingLeadStages));
                localStorage.setItem('crm_deleted_stages', JSON.stringify(uniqueDeleted));
            } catch (err) { }

            const fallbackStage = remaining.length > 0 ? (remaining[0].name || remaining[0].id) : '';
            setContacts(prev => {
                const next = prev.map(c => {
                    const cStage = (c.leadStage || '').toLowerCase().trim();
                    if (cStage === stageName.toLowerCase() || cStage === cleanId.toLowerCase()) {
                        return { ...c, leadStage: fallbackStage };
                    }
                    return c;
                });
                return next;
            });
            setActiveColMenuId(null);

            // Persist fallback stage to backend for affected contacts & save config to DB
            try {
                const affected = contacts.filter(c => {
                    const cStage = (c.leadStage || '').toLowerCase().trim();
                    return cStage === stageName.toLowerCase() || cStage === cleanId.toLowerCase();
                });
                for (const c of affected) {
                    if (c._id && !c._id.toString().startsWith('contact-')) {
                        await api.put(`/pipeline-contacts/${c._id}`, { leadStage: fallbackStage });
                    }
                }

                await api.post('/pipeline-configs/save', {
                    contactStages: remaining,
                    leadStages: remainingLeadStages,
                    deletedStages: uniqueDeleted
                });
            } catch (err) {
                console.warn('Backend delete contact stage sync:', err.message);
            }
        }
    };

    const handleSaveEditStageModal = async (e) => {
        if (e) e.preventDefault();
        if (!editingStageModal) return;
        const trimmed = (editingStageModal.name || '').trim();
        if (!trimmed) {
            alert('Stage name cannot be empty.');
            return;
        }
        const { type, id, index, originalName } = editingStageModal;
        setEditingStageModal(null);

        if (type === 'pipeline') {
            await handleSaveEditPipeline(id, index, trimmed, originalName);
        } else {
            await handleSaveEditContactStage(id, index, trimmed, originalName);
        }
    };

    const handleCreateStageOption = async (newStage) => {
        const stageName = (newStage || '').trim();
        if (!stageName) return;
        if (!stages.some(s => (s.id || '').toLowerCase() === stageName.toLowerCase() || (s.name || '').toLowerCase() === stageName.toLowerCase())) {
            const badgeClasses = ['custom', 'new', 'hot', 'cold', 'won'];
            const randomBadge = badgeClasses[stages.length % badgeClasses.length];
            const newStageObj = { id: stageName, name: stageName, badgeClass: randomBadge, isCustom: true, createdAt: new Date().toISOString() };
            const nextStages = [...stages, newStageObj];
            setStages(nextStages);
            try {
                localStorage.setItem('crm_stages', JSON.stringify(nextStages));
                await api.post('/pipeline-configs/save', { stages: nextStages });
            } catch (e) {
                console.warn('Save stage config error:', e.message);
            }
        }
    };

    const handleCreateDealTypeOption = (newType) => {
        const typeName = (newType || '').trim();
        if (!typeName) return;
        if (!dealTypes.some(t => t.toLowerCase() === typeName.toLowerCase())) {
            setDealTypes(prev => [...prev, typeName]);
        }
    };

    const handleCreatePipelineOption = async (newPipeline) => {
        const pName = (newPipeline || '').trim();
        if (!pName) return;
        let nextOptions = pipelineOptions;
        if (!pipelineOptions.some(p => p.toLowerCase() === pName.toLowerCase())) {
            nextOptions = [...pipelineOptions, pName];
            setPipelineOptions(nextOptions);
        }
        let nextPipelines = pipelines;
        if (!pipelines.some(p => (p.name || p.id || '').toLowerCase() === pName.toLowerCase())) {
            const badgeClasses = ['new', 'hot', 'cold', 'won', 'old', 'custom'];
            const randomBadge = badgeClasses[pipelines.length % badgeClasses.length];
            nextPipelines = [...pipelines, { id: pName, name: pName, badgeClass: randomBadge, createdAt: new Date().toISOString() }];
            setPipelines(nextPipelines);
        }
        let deletedPipelines = [];
        try {
            deletedPipelines = JSON.parse(localStorage.getItem('crm_deleted_pipelines') || '[]');
            deletedPipelines = deletedPipelines.filter(d => d !== pName.toLowerCase());
            localStorage.setItem('crm_deleted_pipelines', JSON.stringify(deletedPipelines));
        } catch (e) { }

        try {
            localStorage.setItem('crm_pipelines', JSON.stringify(nextPipelines));
            localStorage.setItem('crm_pipeline_options', JSON.stringify(nextOptions));
            await api.post('/pipeline-configs/save', {
                pipelines: nextPipelines,
                pipelineOptions: nextOptions,
                deletedPipelines
            });
        } catch (err) {
            console.warn('Save pipeline option to backend error:', err.message);
        }
    };

    const handleCreateOwnerOption = (newOwner) => {
        const oName = (newOwner || '').trim();
        if (!oName) return;
        if (!customOwners.some(o => o.toLowerCase() === oName.toLowerCase())) {
            setCustomOwners(prev => [...prev, oName]);
        }
    };

    // Active Sales Employees for Task & Activity Assignment
    const activeSalesEmployees = useMemo(() => {
        if (!employees || employees.length === 0) return [];
        return employees.filter(emp => {
            if (!emp) return false;
            if (emp.isActive === false || emp.status === 'Inactive' || emp.status === 'inactive') return false;

            const roleName = (typeof emp.role === 'string' ? emp.role : (emp.role?.name || '')).toLowerCase().trim();
            const department = (emp.department || '').toLowerCase().trim();
            const designation = (emp.designation || '').toLowerCase().trim();

            const isSales =
                roleName === 'sales' ||
                roleName.includes('sales') ||
                roleName.includes('business development') ||
                roleName.includes('bde') ||
                department === 'sales' ||
                department.includes('sales') ||
                designation.includes('sales') ||
                designation.includes('bde');

            return isSales;
        });
    }, [employees]);

    // Dynamic Available Employees for Deal Owner Dropdown (Active + Sales role only)
    const availableAssignEmployees = useMemo(() => {
        let list = [];
        if (employees && employees.length > 0) {
            list = employees.filter(emp => {
                if (!emp) return false;
                // Must be Active
                if (emp.isActive === false || emp.status === 'Inactive' || emp.status === 'inactive') {
                    return false;
                }

                const roleName = (typeof emp.role === 'string' ? emp.role : (emp.role?.name || '')).toLowerCase().trim();
                const department = (emp.department || '').toLowerCase().trim();
                const designation = (emp.designation || '').toLowerCase().trim();

                const isSales =
                    roleName === 'sales' ||
                    roleName.includes('sales') ||
                    roleName.includes('business development') ||
                    roleName.includes('bde') ||
                    department === 'sales' ||
                    department.includes('sales') ||
                    designation.includes('sales') ||
                    designation.includes('bde');

                return isSales;
            });

            // Ensure current deal owner is included if active
            if (detailedDeal?.salesOwner) {
                const curOwnerName = detailedDeal.salesOwner.trim().toLowerCase();
                const alreadyIncluded = list.some(emp => {
                    const fn = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                    return fn === curOwnerName || (emp.name || '').trim().toLowerCase() === curOwnerName;
                });
                if (!alreadyIncluded) {
                    const matchedCur = employees.find(emp => {
                        if (!emp || emp.isActive === false || emp.status === 'Inactive' || emp.status === 'inactive') return false;
                        const fn = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                        return fn === curOwnerName || (emp.name || '').trim().toLowerCase() === curOwnerName;
                    });
                    if (matchedCur) {
                        list.unshift(matchedCur);
                    }
                }
            }
        }

        // Fallback to ownerOptions if list is empty
        if (list.length === 0 && ownerOptions && ownerOptions.length > 0) {
            list = ownerOptions.map((name, i) => ({
                _id: `owner-opt-${i}`,
                firstName: name,
                lastName: '',
                designation: 'Sales Specialist',
                department: 'Sales'
            }));
        }

        if (!dealerSearchQuery.trim()) return list;

        const q = dealerSearchQuery.trim().toLowerCase();
        return list.filter(emp => {
            const fName = `${emp.firstName || ''} ${emp.lastName || ''}`.toLowerCase();
            const n = (emp.name || '').toLowerCase();
            const des = (emp.designation || '').toLowerCase();
            const dep = (emp.department || '').toLowerCase();
            const em = (emp.email || '').toLowerCase();
            return fName.includes(q) || n.includes(q) || des.includes(q) || dep.includes(q) || em.includes(q);
        });
    }, [employees, ownerOptions, dealerSearchQuery, detailedDeal?.salesOwner]);

    // Dynamic Contact Options for Deal Association: Name with Email (or Phone if no email)
    const contactOptions = useMemo(() => {
        const map = new Map();

        // Helper to sanitize placeholder values like '----', '---', '-', '—'
        const sanitizeField = (str) => {
            if (!str) return '';
            const trimmed = String(str).trim();
            if (/^[-—_.\s]+$/.test(trimmed)) return '';
            // Also trim trailing or leading dashes/symbols if entered as "Bipin ----"
            return trimmed.replace(/^[-—_.\s]+|[-—_.\s]+$/g, '').trim();
        };

        // 1. From Contacts list
        (contacts || []).forEach(c => {
            if (!c) return;
            const firstName = sanitizeField(c.firstName);
            const lastName = sanitizeField(c.lastName);
            const rawFullName = sanitizeField(`${firstName} ${lastName}`);
            const fallbackName = sanitizeField(c.name || c.clientName);
            const name = rawFullName || fallbackName || '-';

            const email = sanitizeField(c.email);
            const phoneRaw = sanitizeField(c.phone);
            const countryCode = sanitizeField(c.countryCode);
            const phone = phoneRaw ? `${countryCode ? `${countryCode} ` : ''}${phoneRaw}`.trim() : '';

            // If has email show email; if not, show phone; if neither show '-'
            const contactInfo = email || phone || '-';
            const label = `${name} - ${contactInfo}`;
            const id = email || (phone ? phone : (c._id || label));

            const uniqueKey = `${name.toLowerCase()}_${contactInfo.toLowerCase()}`;
            if (!map.has(uniqueKey)) {
                map.set(uniqueKey, {
                    id: id,
                    label: label,
                    contactId: c._id,
                    name: name !== '-' ? name : '',
                    email: email,
                    phone: phone,
                    rawContact: c
                });
            }
        });

        // 2. From Deals contacts (if any deal has a contact not already in contacts)
        (deals || []).forEach(d => {
            const email = sanitizeField(d.email || d.associatedContact);
            const name = sanitizeField(d.clientName) || '-';
            const phone = sanitizeField(d.phone);

            const contactInfo = email || phone || '-';
            if (name !== '-' || contactInfo !== '-') {
                const uniqueKey = `${name.toLowerCase()}_${contactInfo.toLowerCase()}`;
                if (!map.has(uniqueKey)) {
                    const existingByEmail = Array.from(map.values()).find(o => o.email && email && o.email.toLowerCase() === email.toLowerCase());
                    if (!existingByEmail) {
                        const label = `${name} - ${contactInfo}`;
                        const id = email || (phone ? phone : (d.associatedContact || label));
                        map.set(uniqueKey, {
                            id: id,
                            label: label,
                            name: name !== '-' ? name : '',
                            email: email,
                            phone: phone
                        });
                    }
                }
            }
        });

        return Array.from(map.values());
    }, [contacts, deals]);

    // Dynamic Contact Email Options for Company Owner Multi-Select with Badges (CRM Contacts ONLY)
    const contactEmailOptions = useMemo(() => {
        const map = new Map();

        // 1. From Contacts
        (contacts || []).forEach(c => {
            if (!c) return;
            const email = (c.email || '').trim();
            const name = `${c.firstName || ''} ${c.lastName || ''}`.trim();
            if (email) {
                const lower = email.toLowerCase();
                if (!map.has(lower)) {
                    map.set(lower, {
                        id: email,
                        email: email,
                        name: name || email.split('@')[0] || 'Contact',
                        company: c.company || '',
                        phone: c.phone || '',
                        contactId: c._id,
                        type: 'Contact'
                    });
                }
            }
        });

        // 2. From Deals contacts (client email or associated contact email)
        (deals || []).forEach(d => {
            const email = (d.email || d.associatedContact || '').trim();
            if (email && email.includes('@')) {
                const lower = email.toLowerCase();
                if (!map.has(lower)) {
                    map.set(lower, {
                        id: email,
                        email: email,
                        name: d.clientName || email.split('@')[0] || 'Contact',
                        company: d.associatedCompany || d.dealName || '',
                        phone: d.phone || '',
                        type: 'Contact'
                    });
                }
            }
        });

        return Array.from(map.values());
    }, [contacts, deals]);

    // Dynamic Company Options for Deal Association
    const companyOptions = useMemo(() => {
        const compSet = new Set();
        companies.forEach(c => {
            if (c.name && c.name.trim()) compSet.add(c.name.trim());
        });
        contacts.forEach(c => {
            if (c.company && c.company.trim() && c.company !== 'Direct Client') {
                compSet.add(c.company.trim());
            }
        });
        deals.forEach(d => {
            if (d.associatedCompany && d.associatedCompany.trim()) compSet.add(d.associatedCompany.trim());
            if (d.location && d.location.trim()) compSet.add(d.location.trim());
        });
        return Array.from(compSet);
    }, [companies, contacts, deals]);

    // New Deal Form State
    const [formData, setFormData] = useState({
        clientName: '',
        pipeline: 'Initial',
        stage: 'New Lead',
        dealType: 'New Business',
        currency: '$',
        expectedCloseDate: '',
        dealValue: '',
        salesOwner: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Artiflow' : 'Artiflow',
        location: '',
        dealName: '',
        email: '',
        phone: '',
        associatedContact: '',
        associatedCompany: ''
    });



    // Close menus on outer click
    useEffect(() => {
        const closeMenu = () => {
            setActiveMenuId(null);
            setMoveSubmenuDealId(null);
            setActiveContactMenuId(null);
            setMoveSubmenuContactId(null);
            setActiveContactStatusDropdownId(null);
            setIsDealsToolbarMenuOpen(false);
            setIsSortDropdownOpen(false);
            setIsOwnerDropdownOpen(false);
            setIsDealStatusDropdownOpen(false);
            setIsDateDropdownOpen(false);
            setOwnerSearchQuery('');
            setIsContactsToolbarMenuOpen(false);
            setIsContactSortDropdownOpen(false);
            setIsContactOwnerDropdownOpen(false);
            setIsContactStatusDropdownOpen(false);
            setIsContactDateDropdownOpen(false);
            setContactOwnerSearchQuery('');
            setActiveColMenuId(null);
            setIsDealDetailsMenuOpen(false);
            setIsContactDetailsMenuOpen(false);
            setIsAssocContactMenuOpen(false);
            setIsPipelineDropdownOpen(false);
            setIsStageDropdownOpen(false);
            setIsDealerDropdownOpen(false);
            setDealerSearchQuery('');
            setIsContactLeadStageDropdownOpen(false);
            setIsContactLeadStatusDropdownOpen(false);
            setIsMilestonePopoverOpen(false);
            setIsContactAddedByPopoverOpen(false);
            setIsDealAddedByPopoverOpen(false);
            setIsBulkMoveDropdownOpen(false);
            setIsBulkMoveContactStageDropdownOpen(false);
            setIsTopBulkMoveDropdownOpen(false);
            setIsTopDealBulkMoveDropdownOpen(false);
            setColMoveSubmenuStageId(null);
            setColMoveDealSubmenuStageId(null);
        };
        const handleClickOutsideCurrency = (e) => {
            if (currencyRef.current && !currencyRef.current.contains(e.target)) {
                setIsCurrencyOpen(false);
            }
        };
        window.addEventListener('click', closeMenu);
        document.addEventListener('mousedown', handleClickOutsideCurrency);
        return () => {
            window.removeEventListener('click', closeMenu);
            document.removeEventListener('mousedown', handleClickOutsideCurrency);
        };
    }, []);

    // Dynamic list of all deal owners (from sales team & existing deals)
    const allDealOwners = useMemo(() => {
        const ownerMap = new Map();
        (ownerOptions || []).forEach(name => {
            if (name && name.trim()) {
                ownerMap.set(name.trim().toLowerCase(), name.trim());
            }
        });
        (deals || []).forEach(d => {
            if (d.salesOwner && d.salesOwner.trim()) {
                const key = d.salesOwner.trim().toLowerCase();
                if (!ownerMap.has(key)) {
                    ownerMap.set(key, d.salesOwner.trim());
                }
            }
        });
        return Array.from(ownerMap.values()).sort((a, b) => a.localeCompare(b));
    }, [ownerOptions, deals]);

    // Deal counts per owner for badge counts
    const dealOwnerCounts = useMemo(() => {
        const counts = {};
        (deals || []).forEach(d => {
            if (!isItemOwnedByUser(d, 'deal')) return;
            const o = (d.salesOwner || 'Unassigned').trim();
            counts[o] = (counts[o] || 0) + 1;
            counts[o.toLowerCase()] = (counts[o.toLowerCase()] || 0) + 1;
        });
        return counts;
    }, [deals, isItemOwnedByUser]);

    const sortLabel = useMemo(() => {
        switch (sortBy) {
            case 'value_high': return 'Value: High to Low';
            case 'value_low': return 'Value: Low to High';
            case 'name_asc': case 'name': return 'Name: A - Z';
            case 'name_desc': return 'Name: Z - A';
            case 'date_asc': return 'Close Date: Earliest';
            case 'date_desc': return 'Close Date: Latest';
            case 'oldest': return 'Oldest first';
            default: return 'Newest first';
        }
    }, [sortBy]);

    const dateFilterLabel = useMemo(() => {
        switch (dateFilter) {
            case 'today': return 'Date: Today';
            case 'this_week': return 'Date: This week';
            case 'this_month': return 'Date: This month';
            case 'next_30_days': return 'Date: Next 30 days';
            case 'this_quarter': return 'Date: This quarter';
            case 'this_year': return 'Date: This year';
            case 'custom':
                if (customDateStart && customDateEnd) {
                    return `${formatDateDDMMYYYY(customDateStart)} to ${formatDateDDMMYYYY(customDateEnd)}`;
                }
                if (customDateStart) return `From ${formatDateDDMMYYYY(customDateStart)}`;
                if (customDateEnd) return `Until ${formatDateDDMMYYYY(customDateEnd)}`;
                return 'Date: Custom';
            default:
                return 'All dates';
        }
    }, [dateFilter, customDateStart, customDateEnd]);

    // Dynamic list of all deal statuses / stages for the status dropdown
    const allDealStatuses = useMemo(() => {
        const set = new Set();
        (stages || []).forEach(s => {
            const name = typeof s === 'string' ? s : (s.name || s.id);
            if (name && name.trim()) set.add(name.trim());
        });
        (pipelines || []).forEach(p => {
            const name = typeof p === 'string' ? p : (p.name || p.id);
            if (name && name.trim()) set.add(name.trim());
        });
        (deals || []).forEach(d => {
            if (d.stage && d.stage.trim()) set.add(d.stage.trim());
            if (d.pipeline && d.pipeline.trim()) set.add(d.pipeline.trim());
            if (d.status && d.status.trim()) set.add(d.status.trim());
        });
        return Array.from(set).filter(Boolean);
    }, [stages, pipelines, deals]);

    const dealOriginCounts = useMemo(() => {
        const counts = { all: 0, domestic: 0, international: 0 };
        (deals || []).forEach(d => {
            if (!isItemOwnedByUser(d, 'deal')) return;
            counts.all++;
            const origin = (getDealContactOrigin(d) || 'Domestic').toLowerCase();
            if (origin === 'international') {
                counts.international++;
            } else {
                counts.domestic++;
            }
        });
        return counts;
    }, [deals, getDealContactOrigin, isItemOwnedByUser]);

    const isAnyDealFilterActive = useMemo(() => {
        return dealSearchQuery.trim() !== '' || ownerFilter !== 'all' || dealStatusFilter !== 'all' || dealOriginFilter !== 'all' || dateFilter !== 'all' || sortBy !== 'newest';
    }, [dealSearchQuery, ownerFilter, dealStatusFilter, dealOriginFilter, dateFilter, sortBy]);

    const handleResetDealFilters = () => {
        setDealSearchQuery('');
        setOwnerFilter('all');
        setDealStatusFilter('all');
        setDealOriginFilter('all');
        setDateFilter('all');
        setCustomDateStart('');
        setCustomDateEnd('');
        setSortBy('newest');
        setIsSortDropdownOpen(false);
        setIsOwnerDropdownOpen(false);
        setIsDealStatusDropdownOpen(false);
        setIsDealOriginDropdownOpen(false);
        setIsDateDropdownOpen(false);
    };

    const visiblePipelines = useMemo(() => {
        const stageMap = new Map();
        const addStage = (item) => {
            if (!item) return;
            const name = typeof item === 'string' ? item.trim() : (item.name || item.id || '').trim();
            if (!name) return;
            const key = name.toLowerCase();
            if (stageMap.has(key)) return;
            if (!isStageCreatedOnOrAfterCutoff(item, 'deal')) return;
            stageMap.set(key, typeof item === 'string' ? { id: name, name: name, badgeClass: 'custom', createdAt: DATA_CUTOFF_DATE.toISOString() } : item);
        };

        (pipelines || []).forEach(addStage);
        (stages || []).forEach(addStage);
        (pipelineOptions || []).forEach(addStage);

        let list = Array.from(stageMap.values());
        if (list.length === 0) {
            return [
                { id: 'Lost', name: 'Lost', badgeClass: 'lost', createdAt: DATA_CUTOFF_DATE.toISOString() },
                { id: 'New Lead', name: 'New Lead', badgeClass: 'new', createdAt: DATA_CUTOFF_DATE.toISOString() },
                { id: 'Initial', name: 'Initial', badgeClass: 'new', createdAt: DATA_CUTOFF_DATE.toISOString() }
            ];
        }
        return list;
    }, [pipelines, stages, pipelineOptions, isStageCreatedOnOrAfterCutoff]);

    const visiblePipelineOptions = useMemo(() => {
        const set = new Set();
        visiblePipelines.forEach(p => {
            if (p.name) set.add(p.name);
        });
        (pipelineOptions || []).forEach(po => {
            if (po && isStageCreatedOnOrAfterCutoff(po, 'deal')) set.add(po);
        });
        let list = Array.from(set);
        if (list.length === 0) {
            return ['Lost', 'New Lead', 'Initial'];
        }
        return list;
    }, [visiblePipelines, pipelineOptions, isStageCreatedOnOrAfterCutoff]);

    const visibleStages = useMemo(() => {
        return visiblePipelines;
    }, [visiblePipelines]);

    // Helper to determine the single, canonical stage/column a deal belongs to
    const getDealStageName = useCallback((deal, currentPipelines) => {
        if (!deal) return '';
        const dPipe = (deal.pipeline || '').trim();
        const dStage = (deal.stage || '').trim();
        const cols = currentPipelines || pipelines || [];

        // 1. If deal.pipeline matches a column on the board, that is its column
        const matchedByPipeline = cols.find(p =>
            (p.name || '').trim().toLowerCase() === dPipe.toLowerCase() ||
            (p.id || '').trim().toLowerCase() === dPipe.toLowerCase()
        );
        if (matchedByPipeline) return matchedByPipeline.name;

        // 2. Otherwise if deal.stage matches a column on the board, that is its column
        const matchedByStage = cols.find(p =>
            (p.name || '').trim().toLowerCase() === dStage.toLowerCase() ||
            (p.id || '').trim().toLowerCase() === dStage.toLowerCase()
        );
        if (matchedByStage) return matchedByStage.name;

        // 3. Fallback: default to the first column on the board
        return cols.length > 0 ? cols[0].name : (dPipe || dStage || '');
    }, [pipelines]);

    // Filter deals based on selected stage, owner, date criteria and sort criteria
    const filteredDeals = useMemo(() => {
        return deals.filter(deal => {
            if (!isItemOwnedByUser(deal, 'deal')) {
                return false;
            }
            if (dealSearchQuery.trim()) {
                const q = dealSearchQuery.toLowerCase();
                const clientName = (deal.clientName || '').toLowerCase();
                const dealName = (deal.dealName || '').toLowerCase();
                const email = (deal.email || '').toLowerCase();
                const phone = (deal.phone || '').toLowerCase();
                const location = (deal.location || '').toLowerCase();
                const assocComp = (deal.associatedCompany || '').toLowerCase();
                const assocCont = (deal.associatedContact || '').toLowerCase();
                const salesOwner = (deal.salesOwner || '').toLowerCase();
                if (
                    !clientName.includes(q) &&
                    !dealName.includes(q) &&
                    !email.includes(q) &&
                    !phone.includes(q) &&
                    !location.includes(q) &&
                    !assocComp.includes(q) &&
                    !assocCont.includes(q) &&
                    !salesOwner.includes(q)
                ) {
                    return false;
                }
            }
            if (ownerFilter !== 'all') {
                const dOwner = (deal.salesOwner || '').trim().toLowerCase();
                const fOwner = ownerFilter.trim().toLowerCase();
                if (dOwner !== fOwner && !dOwner.includes(fOwner)) {
                    return false;
                }
            }
            if (dealStatusFilter !== 'all') {
                const sFilter = dealStatusFilter.toLowerCase();
                const dStage = (deal.stage || '').toLowerCase();
                const dPipe = (deal.pipeline || '').toLowerCase();
                const dStatus = (deal.status || '').toLowerCase();
                if (dStage !== sFilter && dPipe !== sFilter && dStatus !== sFilter) {
                    return false;
                }
            }
            if (dealOriginFilter !== 'all') {
                const dOrigin = (getDealContactOrigin(deal) || 'Domestic').toLowerCase();
                if (dOrigin !== dealOriginFilter.toLowerCase()) {
                    return false;
                }
            }
            if (selectedStageFilter !== 'all') {
                const sFilter = selectedStageFilter.toLowerCase();
                const stageName = getDealStageName(deal, pipelines).toLowerCase();
                if (stageName !== sFilter) {
                    return false;
                }
            }
            if (dateFilter !== 'all') {
                const dealDate = parseDealDate(deal.expectedCloseDate) || parseDealDate(deal.createdAt) || parseDealDate(deal.date);
                if (!dealDate) return false;
                const now = new Date();
                const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

                if (dateFilter === 'today') {
                    if (dealDate < startOfToday || dealDate > endOfToday) return false;
                } else if (dateFilter === 'this_week') {
                    const firstDayOfWeek = new Date(startOfToday);
                    const day = firstDayOfWeek.getDay();
                    const diff = firstDayOfWeek.getDate() - day + (day === 0 ? -6 : 1);
                    firstDayOfWeek.setDate(diff);
                    const lastDayOfWeek = new Date(firstDayOfWeek);
                    lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
                    lastDayOfWeek.setHours(23, 59, 59, 999);
                    if (dealDate < firstDayOfWeek || dealDate > lastDayOfWeek) return false;
                } else if (dateFilter === 'this_month') {
                    if (dealDate.getMonth() !== now.getMonth() || dealDate.getFullYear() !== now.getFullYear()) return false;
                } else if (dateFilter === 'next_30_days') {
                    const in30Days = new Date(startOfToday);
                    in30Days.setDate(in30Days.getDate() + 30);
                    in30Days.setHours(23, 59, 59, 999);
                    if (dealDate < startOfToday || dealDate > in30Days) return false;
                } else if (dateFilter === 'this_quarter') {
                    const currentQuarter = Math.floor(now.getMonth() / 3);
                    const dealQuarter = Math.floor(dealDate.getMonth() / 3);
                    if (dealQuarter !== currentQuarter || dealDate.getFullYear() !== now.getFullYear()) return false;
                } else if (dateFilter === 'this_year') {
                    if (dealDate.getFullYear() !== now.getFullYear()) return false;
                } else if (dateFilter === 'custom') {
                    if (customDateStart) {
                        const start = new Date(customDateStart);
                        start.setHours(0, 0, 0, 0);
                        if (dealDate < start) return false;
                    }
                    if (customDateEnd) {
                        const end = new Date(customDateEnd);
                        end.setHours(23, 59, 59, 999);
                        if (dealDate > end) return false;
                    }
                }
            }
            return true;
        }).sort((a, b) => {
            if (sortBy === 'value_high') return (Number(b.dealValue) || 0) - (Number(a.dealValue) || 0);
            if (sortBy === 'value_low') return (Number(a.dealValue) || 0) - (Number(b.dealValue) || 0);
            if (sortBy === 'name' || sortBy === 'name_asc') return (a.clientName || a.dealName || '').localeCompare(b.clientName || b.dealName || '');
            if (sortBy === 'name_desc') return (b.clientName || b.dealName || '').localeCompare(a.clientName || a.dealName || '');
            if (sortBy === 'date_asc') {
                const da = parseDealDate(a.expectedCloseDate) || parseDealDate(a.createdAt) || new Date(0);
                const db = parseDealDate(b.expectedCloseDate) || parseDealDate(b.createdAt) || new Date(0);
                return da - db;
            }
            if (sortBy === 'date_desc') {
                const da = parseDealDate(a.expectedCloseDate) || parseDealDate(a.createdAt) || new Date(0);
                const db = parseDealDate(b.expectedCloseDate) || parseDealDate(b.createdAt) || new Date(0);
                return db - da;
            }
            if (sortBy === 'oldest') {
                const da = parseDealDate(a.createdAt) || parseDealDate(a.date) || new Date(0);
                const db = parseDealDate(b.createdAt) || parseDealDate(b.date) || new Date(0);
                return da - db;
            }
            // default: newest
            const da = parseDealDate(a.createdAt) || parseDealDate(a.date) || new Date(0);
            const db = parseDealDate(b.createdAt) || parseDealDate(b.date) || new Date(0);
            return db - da;
        });
    }, [deals, selectedStageFilter, sortBy, ownerFilter, dealStatusFilter, dealOriginFilter, getDealContactOrigin, dateFilter, customDateStart, customDateEnd, dealSearchQuery, isItemOwnedByUser, getDealStageName, pipelines]);

    // Dynamic badge counts for left navigation menu tabs
    const activeDealsCount = useMemo(() => {
        return deals.filter(d => isItemOwnedByUser(d, 'deal')).length;
    }, [deals, isItemOwnedByUser]);

    const activeContactsCount = useMemo(() => {
        return contacts.filter(c => isItemOwnedByUser(c, 'contact')).length;
    }, [contacts, isItemOwnedByUser]);

    const activeCompaniesCount = useMemo(() => {
        return companies.filter(c => isItemOwnedByUser(c, 'company')).length;
    }, [companies, isItemOwnedByUser]);

    const activeTasksCount = useMemo(() => {
        return userAccessibleTasks.length;
    }, [userAccessibleTasks]);

    // Counts per pipeline
    const pipelineCounts = useMemo(() => {
        const counts = {};
        visiblePipelines.forEach(p => {
            counts[p.name] = 0;
            counts[p.id] = 0;
        });
        filteredDeals.forEach(d => {
            const stageName = getDealStageName(d, visiblePipelines);
            const matchedP = visiblePipelines.find(p => (p.name || '').trim().toLowerCase() === stageName.trim().toLowerCase());
            if (matchedP) {
                counts[matchedP.name] = (counts[matchedP.name] || 0) + 1;
                if (matchedP.id !== matchedP.name) {
                    counts[matchedP.id] = counts[matchedP.name];
                }
            } else if (visiblePipelines.length > 0) {
                const firstP = visiblePipelines[0];
                counts[firstP.name] = (counts[firstP.name] || 0) + 1;
                if (firstP.id !== firstP.name) {
                    counts[firstP.id] = counts[firstP.name];
                }
            }
        });
        return counts;
    }, [filteredDeals, visiblePipelines, getDealStageName]);

    // Handle pipeline change (move deal to another pipeline)
    const handleMovePipeline = async (dealId, nextPipeline, e) => {
        if (e) e.stopPropagation();
        const updateData = {
            pipeline: nextPipeline,
            stage: nextPipeline
        };
        try {
            if (dealId && !dealId.toString().startsWith('deal-')) {
                await api.put(`/deals/${dealId}`, updateData);
            }
        } catch (err) {
            console.warn('Move pipeline API sync:', err.message);
        }
        setDeals(prev =>
            prev.map(d => (String(d._id) === String(dealId) ? { ...d, ...updateData } : d))
        );
        logActivity(dealId, {
            type: 'deal',
            title: 'Stage Moved',
            content: `Moved deal to stage "${nextPipeline}".`
        });
        setActiveMenuId(null);
        setMoveSubmenuDealId(null);
    };

    // Handle stage change (move deal)
    const handleMoveStage = async (dealId, nextStage, e) => {
        if (e) e.stopPropagation();
        const updateData = {
            pipeline: nextStage,
            stage: nextStage
        };
        try {
            if (dealId && !dealId.toString().startsWith('deal-')) {
                await api.put(`/deals/${dealId}`, updateData);
            }
        } catch (err) {
            console.warn('Move stage API sync:', err.message);
        }
        setDeals(prev =>
            prev.map(d => (String(d._id) === String(dealId) ? { ...d, ...updateData } : d))
        );
        logActivity(dealId, {
            type: 'deal',
            title: 'Stage Changed',
            content: `Moved deal to stage "${nextStage}".`
        });
        setActiveMenuId(null);
        setMoveSubmenuDealId(null);
    };

    // Handle contact stage change (move contact to another stage column)
    const handleMoveContactStage = async (contactId, nextStage, e) => {
        if (e) e.stopPropagation();
        try {
            if (contactId && !contactId.toString().startsWith('contact-')) {
                await api.put(`/pipeline-contacts/${contactId}`, { leadStage: nextStage });
            }
        } catch (err) {
            console.warn('Move contact stage API sync:', err.message);
        }
        setContacts(prev =>
            prev.map(c => (c._id === contactId ? { ...c, leadStage: nextStage } : c))
        );
        logActivity(contactId, {
            type: 'contact',
            title: 'Stage Changed',
            content: `Moved contact to stage "${nextStage}".`
        }, true);
        setActiveContactMenuId(null);
        setMoveSubmenuContactId(null);
    };

    // Handle delete deal
    const handleDeleteDeal = async (dealId, e) => {
        if (e) e.stopPropagation();
        if (!canDelete) {
            alert('You do not have permission to delete deals.');
            return;
        }
        if (window.confirm('Are you sure you want to delete this deal?')) {
            const dealToDelete = deals.find(d => d._id === dealId);
            try {
                if (dealId && !dealId.toString().startsWith('deal-')) {
                    await api.delete(`/deals/${dealId}`);
                }
                setDeals(prev => prev.filter(d => d._id !== dealId));
            } catch (err) {
                console.error('Delete deal API error:', err);
                alert(err.response?.data?.message || 'Failed to delete deal. Please check your permissions.');
                return;
            }

            if (dealToDelete) {
                const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';
                const matchingContact = contacts.find(c =>
                    (dealToDelete.associatedContact && c.email && c.email.toLowerCase() === dealToDelete.associatedContact.toLowerCase()) ||
                    (c.email && dealToDelete.email && c.email.toLowerCase() === dealToDelete.email.toLowerCase())
                );
                if (matchingContact) {
                    logActivity(matchingContact._id, {
                        type: 'deal',
                        title: 'Associated Deal Deleted',
                        content: `Deal "${dealToDelete.dealName || dealToDelete.clientName}" was deleted.`,
                        author: authorName
                    }, true);
                }
            }
        }
        setActiveMenuId(null);
        setMoveSubmenuDealId(null);
    };

    // Drag and Drop handlers to move deals between pipelines
    const handleDragStart = (e, dealId) => {
        if (!canUpdate) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.setData('text/plain', dealId);
        e.dataTransfer.effectAllowed = 'move';
        setDraggedDealId(dealId);
    };

    const handleDragEnd = () => {
        setDraggedDealId(null);
        setDragOverColumnId(null);
    };

    const handleDragOver = (e, pipelineId) => {
        if (!canUpdate) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverColumnId !== pipelineId) {
            setDragOverColumnId(pipelineId);
        }
    };

    const handleDragLeave = (e, pipelineId) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        if (dragOverColumnId === pipelineId) {
            setDragOverColumnId(null);
        }
    };

    // Reorder / Shuffle stages handler (Restricted to Admin, Sales Head, Sales Manager)
    const handleReorderStages = async (reorderedList) => {
        if (!hasFullAccess) {
            alert('Only Admin, Sales Head, and Sales Manager can reorder pipeline stages.');
            return;
        }
        if (!Array.isArray(reorderedList) || reorderedList.length === 0) return;

        const normalized = reorderedList.map((item, idx) => ({
            id: item.id || item.name || `stage-${idx}`,
            name: item.name || item.id || `Stage ${idx + 1}`,
            badgeClass: item.badgeClass || 'new',
            createdAt: item.createdAt || DATA_CUTOFF_DATE.toISOString()
        }));
        const newOptions = normalized.map(s => s.name);

        // Immediate responsive UI update
        setPipelines(normalized);
        setStages(normalized);
        setPipelineOptions(newOptions);

        try {
            localStorage.setItem('crm_pipelines', JSON.stringify(normalized));
            localStorage.setItem('crm_stages', JSON.stringify(normalized));
            localStorage.setItem('crm_pipeline_options', JSON.stringify(newOptions));
        } catch (e) { }

        // Persist to server MongoDB config so all sales executives see the exact arranged order
        try {
            setSavingStageOrder(true);
            await api.post('/pipeline-configs/save', {
                pipelines: normalized,
                stages: normalized,
                pipelineOptions: newOptions
            });
        } catch (err) {
            console.error('Failed to save reordered stages to server:', err);
            alert('Failed to save stage order to server. Please try again.');
        } finally {
            setSavingStageOrder(false);
        }
    };

    const handleShiftStageOrder = (fromIndex, toIndex) => {
        if (!hasFullAccess) return;
        if (fromIndex < 0 || toIndex < 0 || fromIndex >= visiblePipelines.length || toIndex >= visiblePipelines.length) return;
        const current = [...visiblePipelines];
        const [moved] = current.splice(fromIndex, 1);
        current.splice(toIndex, 0, moved);
        handleReorderStages(current);
    };

    const handleStageHeaderDragStart = (e, index) => {
        if (!hasFullAccess) return;
        e.dataTransfer.setData('text/plain', `stage-header-${index}`);
        e.dataTransfer.effectAllowed = 'move';
        setDraggedStageColIndex(index);
    };

    const handleStageHeaderDragOver = (e, index) => {
        if (!hasFullAccess || draggedStageColIndex === null) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverStageColIndex !== index) {
            setDragOverStageColIndex(index);
        }
    };

    const handleStageHeaderDragLeave = (e) => {
        // Clear highlight if dragged out of column
        if (e && e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) {
            setDragOverStageColIndex(null);
        }
    };

    const handleStageHeaderDragEnd = () => {
        setDraggedStageColIndex(null);
        setDragOverStageColIndex(null);
    };

    // Auto-clear any stuck drag/highlight state on mouseup, window blur, or drag end
    useEffect(() => {
        const clearDragHighlight = () => {
            setDraggedStageColIndex(null);
            setDragOverStageColIndex(null);
        };
        window.addEventListener('mouseup', clearDragHighlight);
        window.addEventListener('dragend', clearDragHighlight);
        window.addEventListener('drop', clearDragHighlight);
        window.addEventListener('pointerup', clearDragHighlight);
        return () => {
            window.removeEventListener('mouseup', clearDragHighlight);
            window.removeEventListener('dragend', clearDragHighlight);
            window.removeEventListener('drop', clearDragHighlight);
            window.removeEventListener('pointerup', clearDragHighlight);
        };
    }, []);

    const handleStageHeaderDrop = (e, targetIndex) => {
        if (!hasFullAccess) return;
        const data = e.dataTransfer.getData('text/plain');
        if (data && data.startsWith('stage-header-')) {
            e.preventDefault();
            e.stopPropagation();
            const fromIndex = parseInt(data.replace('stage-header-', ''), 10);
            if (!isNaN(fromIndex) && fromIndex !== targetIndex) {
                handleShiftStageOrder(fromIndex, targetIndex);
            }
        }
        setDraggedStageColIndex(null);
        setDragOverStageColIndex(null);
    };

    const handleDrop = async (e, targetPipelineName) => {
        e.preventDefault();
        const dealId = e.dataTransfer?.getData('text/plain') || draggedDealId;
        if (dealId && dealId.startsWith('stage-header-')) {
            setDraggedStageColIndex(null);
            setDragOverStageColIndex(null);
            return;
        }
        setDraggedStageColIndex(null);
        setDragOverStageColIndex(null);
        if (!canUpdate) return;
        if (dealId) {
            const targetDealIds = (selectedKanbanDealIds.includes(dealId))
                ? selectedKanbanDealIds
                : [dealId];

            const updateData = {
                pipeline: targetPipelineName,
                stage: targetPipelineName
            };

            setDeals(prev =>
                prev.map(d => targetDealIds.includes(String(d._id)) ? { ...d, ...updateData } : d)
            );

            const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';
            for (const id of targetDealIds) {
                try {
                    if (id && !id.toString().startsWith('deal-')) {
                        await api.put(`/deals/${id}`, updateData);
                    }
                } catch (err) {
                    console.warn('Drop move pipeline API sync:', err.message);
                }
                logActivity(id, {
                    type: 'deal',
                    title: 'Stage Moved',
                    content: `Moved deal to "${targetPipelineName}".`,
                    author: authorName
                });
            }

            if (selectedKanbanDealIds.includes(dealId)) {
                setSelectedKanbanDealIds([]);
            }
        }
        setDraggedDealId(null);
        setDragOverColumnId(null);
    };

    // Kanban Double Click on Card: multi-select all cards in that specific stage column
    const handleCardDoubleClick = (deal, stageDeals, stageName, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        // Cancel any scheduled single-click deal detail opening
        if (cardClickTimerRef.current) {
            clearTimeout(cardClickTimerRef.current);
            cardClickTimerRef.current = null;
        }

        const stageDealIds = (stageDeals || []).map(d => d._id);
        if (stageDealIds.length === 0) return;

        setSelectedKanbanDealIds(prev => {
            const allStageSelected = stageDealIds.every(id => prev.includes(id));
            if (allStageSelected) {
                // If all cards in this stage were already selected, toggle off / deselect them
                return prev.filter(id => !stageDealIds.includes(id));
            } else {
                // Multi-select all cards of this stage (retaining any already selected cards in other stages)
                return Array.from(new Set([...prev, ...stageDealIds]));
            }
        });
    };

    // Toggle individual card checkbox selection
    const handleToggleDealSelection = (dealId, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (cardClickTimerRef.current) {
            clearTimeout(cardClickTimerRef.current);
            cardClickTimerRef.current = null;
        }
        setSelectedKanbanDealIds(prev =>
            prev.includes(dealId) ? prev.filter(id => id !== dealId) : [...prev, dealId]
        );
    };

    // Toggle all cards of a stage from column header
    const handleToggleStageSelection = (stageDeals, e) => {
        if (e) e.stopPropagation();
        const stageDealIds = (stageDeals || []).map(d => d._id);
        if (stageDealIds.length === 0) return;
        setSelectedKanbanDealIds(prev => {
            const allStageSelected = stageDealIds.every(id => prev.includes(id));
            if (allStageSelected) {
                return prev.filter(id => !stageDealIds.includes(id));
            } else {
                return Array.from(new Set([...prev, ...stageDealIds]));
            }
        });
    };

    // Debounced single click on card to avoid conflict with double-click
    const handleDealCardClick = (dealId, e) => {
        if (e) e.stopPropagation();
        if (cardClickTimerRef.current) {
            clearTimeout(cardClickTimerRef.current);
            cardClickTimerRef.current = null;
        }
        cardClickTimerRef.current = setTimeout(() => {
            setDetailedDealId(dealId);
            cardClickTimerRef.current = null;
        }, 230);
    };

    // Bulk move all selected deals to a target stage
    const handleBulkMoveStage = async (targetStage, customIds = null) => {
        const targetIds = Array.isArray(customIds) && customIds.length > 0 ? customIds : selectedKanbanDealIds;
        if (!targetStage || targetIds.length === 0) return;
        const dealIdsToMove = [...targetIds];
        const updateData = { pipeline: targetStage, stage: targetStage };
        const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';

        setDeals(prev =>
            prev.map(d => dealIdsToMove.includes(d._id) ? { ...d, ...updateData } : d)
        );
        setIsBulkMoveDropdownOpen(false);
        setSelectedKanbanDealIds([]);

        for (const id of dealIdsToMove) {
            try {
                if (id && !id.toString().startsWith('deal-')) {
                    await api.put(`/deals/${id}`, updateData);
                }
            } catch (err) {
                console.warn(`Bulk move deal ${id} API error:`, err.message);
            }
            logActivity(id, {
                type: 'deal',
                title: 'Stage Moved',
                content: `Moved deal to "${targetStage}" via bulk action.`,
                author: authorName
            });
        }
    };

    // Bulk delete all selected deals
    const handleBulkDeleteDeals = async (customIds = null) => {
        const targetIds = Array.isArray(customIds) && customIds.length > 0 ? customIds : selectedKanbanDealIds;
        if (!canDelete || targetIds.length === 0) return;
        if (window.confirm(`Are you sure you want to delete ${targetIds.length} selected deal(s)?`)) {
            const dealIdsToDelete = [...targetIds];
            try {
                await api.post('/deals/bulk-delete', { dealIds: dealIdsToDelete });
                setDeals(prev => prev.filter(d => !dealIdsToDelete.includes(d._id)));
                setSelectedKanbanDealIds([]);
            } catch (err) {
                console.warn('Bulk delete deals API error, trying fallback:', err.message);
                let deletedSuccessfully = [];
                for (const id of dealIdsToDelete) {
                    try {
                        if (id && !id.toString().startsWith('deal-')) {
                            await api.delete(`/deals/${id}`);
                            deletedSuccessfully.push(id);
                        }
                    } catch (singleErr) {
                        console.warn(`Bulk delete deal ${id} API error:`, singleErr.message);
                    }
                }
                if (deletedSuccessfully.length > 0) {
                    setDeals(prev => prev.filter(d => !deletedSuccessfully.includes(d._id)));
                    setSelectedKanbanDealIds(prev => prev.filter(id => !deletedSuccessfully.includes(id)));
                } else {
                    alert(err.response?.data?.message || 'Failed to delete selected deal(s). Please verify your permissions.');
                }
            }
        }
    };

    // Activity popover hover handlers with screen bounds calculation & fixed positioning
    const handleActivityMouseEnter = (id, type = 'deal', e) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        if (e && e.currentTarget) {
            const rect = e.currentTarget.getBoundingClientRect();
            const popoverWidth = 530;
            const popoverHeight = 250;
            const fitsRight = rect.right + popoverWidth + 24 <= window.innerWidth;
            const left = fitsRight ? rect.right + 12 : Math.max(10, rect.left - popoverWidth - 12);

            let top = rect.top - 60;
            if (top + popoverHeight > window.innerHeight - 20) {
                top = window.innerHeight - popoverHeight - 20;
            }
            if (top < 15) {
                top = 15;
            }

            const arrowTop = Math.min(Math.max(18, rect.top - top + 10), popoverHeight - 22);

            setPopoverCoords({
                top,
                left,
                placement: fitsRight ? 'right' : 'left',
                arrowTop
            });
        }
        if (type === 'contact') {
            setHoveredActivityDealId(null);
            setHoveredActivityContactId(id);
        } else {
            setHoveredActivityContactId(null);
            setHoveredActivityDealId(id);
        }
    };

    const handleActivityMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredActivityDealId(null);
            setHoveredActivityContactId(null);
        }, 250);
    };

    // View Deal Detail
    const handleOpenViewDeal = (deal, e) => {
        if (e) e.stopPropagation();
        setViewingDeal(deal);
        setActiveMenuId(null);
        setMoveSubmenuDealId(null);
    };

    // Edit Deal
    const handleOpenEditDeal = (deal, e) => {
        if (e) e.stopPropagation();

        const matchedContact = contacts.find(c =>
            (deal.email && c.email && c.email.toLowerCase() === deal.email.toLowerCase()) ||
            (deal.phone && c.phone && (c.phone === deal.phone || (deal.phone || '').includes(c.phone))) ||
            (deal.associatedContact && (
                (c.name && c.name.toLowerCase() === deal.associatedContact.toLowerCase()) ||
                (`${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase() === deal.associatedContact.toLowerCase()) ||
                (c.email && c.email.toLowerCase() === deal.associatedContact.toLowerCase())
            )) ||
            (deal.clientName && (
                (c.name && c.name.toLowerCase() === deal.clientName.toLowerCase()) ||
                (`${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase() === deal.clientName.toLowerCase())
            ))
        );

        const initialEmail = (deal.email && !deal.email.toLowerCase().endsWith('@example.com'))
            ? deal.email
            : (matchedContact?.email && !matchedContact.email.toLowerCase().endsWith('@example.com')
                ? matchedContact.email
                : (deal.associatedContact && deal.associatedContact.includes('@') && !deal.associatedContact.toLowerCase().endsWith('@example.com') ? deal.associatedContact : ''));

        let initialPhone = deal.phone || '';
        if (matchedContact?.phone) {
            const dealDigits = (deal.phone || '').replace(/\D/g, '');
            const contactDigits = (matchedContact.phone || '').replace(/\D/g, '');
            if (!dealDigits || contactDigits.length > dealDigits.length) {
                initialPhone = matchedContact.countryCode ? `${matchedContact.countryCode} ${matchedContact.phone}` : matchedContact.phone;
            }
        }

        let dealCountry = COUNTRY_CODES[0];
        let phoneDigits = '';
        const rawPhone = String(initialPhone || '').trim();
        if (rawPhone) {
            if (rawPhone.startsWith('+')) {
                const matched = COUNTRY_CODES.find(c => rawPhone.startsWith(c.code));
                if (matched) {
                    dealCountry = matched;
                    phoneDigits = rawPhone.slice(matched.code.length).replace(/\D/g, '');
                    if (matched.maxDigits && phoneDigits.length > matched.maxDigits) {
                        phoneDigits = phoneDigits.slice(0, matched.maxDigits);
                    }
                } else {
                    phoneDigits = rawPhone.replace(/\D/g, '').slice(0, 15);
                }
            } else {
                if (matchedContact?.countryCode) {
                    const matchedCc = COUNTRY_CODES.find(c => c.code === matchedContact.countryCode);
                    if (matchedCc) dealCountry = matchedCc;
                }
                phoneDigits = rawPhone.replace(/\D/g, '');
                const maxLen = dealCountry?.maxDigits || 15;
                if (phoneDigits.length > maxLen) {
                    phoneDigits = phoneDigits.slice(0, maxLen);
                }
            }
        } else if (matchedContact?.countryCode) {
            const matchedCc = COUNTRY_CODES.find(c => c.code === matchedContact.countryCode);
            if (matchedCc) dealCountry = matchedCc;
        }

        setDealSelectedCountry(dealCountry);
        setIsDealCountryCodeOpen(false);
        setEditingDeal(deal);
        setModalStage(deal.stage || deal.pipeline || 'New Lead');
        setFormData({
            clientName: deal.clientName || '',
            pipeline: deal.pipeline || 'New Lead',
            stage: deal.stage || deal.pipeline || 'New Lead',
            dealType: deal.dealType || 'New Business',
            currency: deal.currency || '$',
            expectedCloseDate: formatDateDDMMYYYY(deal.expectedCloseDate),
            dealValue: deal.dealValue || '',
            salesOwner: deal.salesOwner || 'Artiflow',
            location: deal.location || '',
            dealName: deal.dealName || '',
            email: initialEmail,
            phone: phoneDigits,
            associatedContact: deal.associatedContact || deal.clientName || '',
            associatedCompany: deal.associatedCompany || deal.company || ''
        });
        setActiveMenuId(null);
        setMoveSubmenuDealId(null);
        setShowCreateModal(true);
    };

    // Save updated requirement for the detailed deal
    const handleSaveDealRequirement = async (e) => {
        if (e) e.preventDefault();
        if (!detailedDeal?._id) return;

        setIsSavingRequirement(true);
        const updatedText = editingRequirementText.trim();

        try {
            if (!detailedDeal._id.toString().startsWith('deal-sample-') && !detailedDeal._id.toString().startsWith('deal-')) {
                await api.put(`/deals/${detailedDeal._id}`, {
                    requirements: updatedText,
                    description: updatedText || detailedDeal.notes || ''
                });

                // Sync to related lead in background if present
                const relatedLeadId = detailedDeal.relatedLead?._id || (typeof detailedDeal.relatedLead === 'string' ? detailedDeal.relatedLead : null);
                if (relatedLeadId) {
                    api.put(`/leads/${relatedLeadId}`, {
                        requirement: updatedText,
                        requirements: updatedText
                    }).catch(err => console.warn('Sync requirement to lead:', err.message));
                }
            }

            setDeals(prev => prev.map(d => String(d._id) === String(detailedDeal._id) ? {
                ...d,
                requirements: updatedText,
                description: updatedText || d.notes || ''
            } : d));

            logActivity(detailedDeal._id, {
                type: 'deal',
                title: 'Requirement Updated',
                content: `Deal requirement was updated.`
            });

            setIsEditingRequirement(false);
        } catch (err) {
            console.error('Save deal requirement error:', err);
            alert(err.response?.data?.message || 'Failed to update requirement. Please try again.');
        } finally {
            setIsSavingRequirement(false);
        }
    };

    // Open modal with preselected pipeline / stage
    const handleOpenAddDeal = (selectedPipeline) => {
        let targetPipeline = selectedPipeline;
        if (!targetPipeline || targetPipeline === 'New') {
            targetPipeline = (pipelines && pipelines.length > 0 && pipelines.some(p => p.name.toLowerCase() === 'initial'))
                ? 'Initial'
                : (pipelines && pipelines[0] ? pipelines[0].name : 'Initial');
        }
        const dateString = getFutureDateDDMMYYYY(14);

        setEditingDeal(null);
        setModalStage('New Lead');
        setFormData({
            clientName: '',
            pipeline: targetPipeline || 'New Lead',
            stage: 'New Lead',
            dealType: 'New Business',
            currency: '$',
            expectedCloseDate: dateString,
            dealValue: '',
            salesOwner: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Artiflow' : 'Artiflow',
            location: '',
            dealName: '',
            email: '',
            phone: '',
            associatedContact: '',
            associatedCompany: ''
        });
        setShowCreateModal(true);
    };

    // Create deal submit
    const handleCreateDealSubmit = async (e) => {
        e.preventDefault();
        if (!formData.clientName || !formData.clientName.trim()) {
            alert('Please provide a deal name');
            return;
        }

        const cleanPhoneDigits = (formData.phone || '').replace(/\D/g, '');
        const minPhoneDigits = dealSelectedCountry?.minDigits || dealSelectedCountry?.maxDigits || 10;
        const maxPhoneDigits = dealSelectedCountry?.maxDigits || 10;
        if (cleanPhoneDigits && (cleanPhoneDigits.length < minPhoneDigits || cleanPhoneDigits.length > maxPhoneDigits)) {
            const digitMsg = minPhoneDigits === maxPhoneDigits ? `${maxPhoneDigits}` : `${minPhoneDigits} to ${maxPhoneDigits}`;
            alert(`Please enter a valid ${digitMsg}-digit phone number for ${dealSelectedCountry?.country || 'the selected country'}.`);
            return;
        }

        const trimmedEmail = (formData.email || '').trim();
        if (trimmedEmail) {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailRegex.test(trimmedEmail)) {
                alert('Please enter a valid email address.');
                return;
            }
        }

        const selectedStage = (modalStage || formData.stage || 'New').trim();
        const selectedDealType = (formData.dealType || 'New Business').trim();
        const selectedPipeline = (formData.pipeline || 'Sales Pipeline').trim();
        const selectedOwner = (formData.salesOwner || 'Artiflow').trim();

        // Auto add pipeline to pipelines list if new
        if (!pipelines.some(p => p.name.toLowerCase() === selectedPipeline.toLowerCase())) {
            const badgeClasses = ['new', 'hot', 'cold', 'won', 'old', 'custom'];
            const randomBadge = badgeClasses[pipelines.length % badgeClasses.length];
            setPipelines(prev => [...prev, { id: selectedPipeline, name: selectedPipeline, badgeClass: randomBadge, createdAt: new Date().toISOString() }]);
        }
        if (!pipelineOptions.some(p => p.toLowerCase() === selectedPipeline.toLowerCase())) {
            setPipelineOptions(prev => [...prev, selectedPipeline]);
        }

        // Auto add stage to stages list if custom
        if (!stages.some(s => s.id.toLowerCase() === selectedStage.toLowerCase())) {
            const badgeClasses = ['custom', 'new', 'hot', 'cold', 'won'];
            const randomBadge = badgeClasses[stages.length % badgeClasses.length];
            setStages(prev => [...prev, { id: selectedStage, name: selectedStage, badgeClass: randomBadge, isCustom: true, createdAt: new Date().toISOString() }]);
        }

        // Auto add deal type to dealTypes list if custom
        if (!dealTypes.some(t => t.toLowerCase() === selectedDealType.toLowerCase())) {
            setDealTypes(prev => [...prev, selectedDealType]);
        }

        // Auto add owner to customOwners list if custom
        if (!ownerOptions.some(o => o.toLowerCase() === selectedOwner.toLowerCase())) {
            setCustomOwners(prev => [...prev, selectedOwner]);
        }

        const matchedOwnerEmp = (employees || []).find(emp => {
            if (!emp) return false;
            const fName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
            const n = (emp.name || '').trim().toLowerCase();
            const s = selectedOwner.toLowerCase();
            return fName === s || n === s || (emp.email && emp.email.toLowerCase() === s);
        });

        if (editingDeal) {
            const updatePayload = {
                clientName: formData.clientName.trim(),
                dealName: formData.dealName?.trim() || formData.clientName.trim(),
                location: formData.location || editingDeal.location || '',
                dealValue: Number(formData.dealValue) || 0,
                currency: formData.currency || '$',
                expectedCloseDate: formData.expectedCloseDate ? formatDateDDMMYYYY(formData.expectedCloseDate) : editingDeal.expectedCloseDate,
                salesOwner: selectedOwner,
                assignedTo: matchedOwnerEmp?._id || editingDeal.assignedTo || null,
                pipeline: selectedPipeline,
                dealType: selectedDealType,
                stage: selectedStage,
                email: formData.email || editingDeal.email,
                phone: formData.phone || editingDeal.phone,
                associatedContact: formData.associatedContact || '',
                associatedCompany: formData.associatedCompany || ''
            };

            const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';
            const changes = [];
            if (editingDeal.stage !== selectedStage) changes.push(`Stage changed from "${editingDeal.stage}" to "${selectedStage}"`);
            if (editingDeal.pipeline !== selectedPipeline) changes.push(`Pipeline moved to "${selectedPipeline}"`);
            if (Number(editingDeal.dealValue) !== Number(formData.dealValue)) changes.push(`Deal value updated to ${formData.currency || '$'}${Number(formData.dealValue || 0).toLocaleString()}`);
            if (editingDeal.salesOwner !== selectedOwner) {
                if (editingDeal.salesOwner) {
                    changes.push(`Deal owner changed from "${editingDeal.salesOwner}" to "${selectedOwner}"`);
                } else {
                    changes.push(`Deal assigned to ${selectedOwner}`);
                }
            }
            if (formData.associatedContact && editingDeal.associatedContact !== formData.associatedContact) changes.push(`Associated contact: ${formData.associatedContact}`);
            if (formData.associatedCompany && editingDeal.associatedCompany !== formData.associatedCompany) changes.push(`Associated company: ${formData.associatedCompany}`);
            const changeText = changes.length > 0 ? changes.join(' • ') : 'Deal details updated.';

            logActivity(editingDeal._id, {
                type: 'deal',
                title: 'Deal Updated',
                content: changeText,
                author: authorName
            });

            try {
                if (editingDeal._id && !editingDeal._id.toString().startsWith('deal-')) {
                    const res = await api.put(`/deals/${editingDeal._id}`, updatePayload);
                    if (res.data) {
                        setDeals(prev => prev.map(d => (String(d._id) === String(editingDeal._id) ? { ...res.data, assignedTo: matchedOwnerEmp || res.data.assignedTo } : d)));
                        setEditingDeal(null);
                        setShowCreateModal(false);
                        return;
                    }
                }
            } catch (err) {
                console.warn('Update deal API error:', err.message);
            }

            setDeals(prev =>
                prev.map(d =>
                    String(d._id) === String(editingDeal._id)
                        ? {
                            ...d,
                            ...updatePayload,
                            assignedTo: matchedOwnerEmp || d.assignedTo
                        }
                        : d
                )
            );
            setEditingDeal(null);
            setShowCreateModal(false);
            return;
        }

        const newDealPayload = {
            clientName: formData.clientName.trim(),
            dealName: formData.dealName?.trim() || formData.clientName.trim(),
            location: formData.location || '',
            dealValue: Number(formData.dealValue) || 0,
            currency: formData.currency || '$',
            expectedCloseDate: formData.expectedCloseDate ? formatDateDDMMYYYY(formData.expectedCloseDate) : getFutureDateDDMMYYYY(14),
            salesOwner: selectedOwner,
            assignedTo: matchedOwnerEmp?._id || null,
            pipeline: selectedPipeline,
            dealType: selectedDealType,
            stage: selectedStage,
            email: formData.email || `${formData.clientName.toLowerCase().replace(/\s+/g, '.')}@example.com`,
            phone: formData.phone || '+1 555-0100',
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.clientName)}&background=0284c7&color=fff`,
            type: selectedStage === 'Won' ? 'Won' : selectedStage === 'Lost' ? 'Lost' : 'Open',
            associatedContact: formData.associatedContact || '',
            associatedCompany: (formData.associatedCompany && formData.associatedCompany !== 'Direct Client') ? formData.associatedCompany.trim() : '',
            addedBy: (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System'
        };

        // Auto-create / link contact if it does not already exist
        const contactIdentifier = (formData.associatedContact || '').trim();
        const clientName = (formData.clientName || '').trim();
        const dealEmail = (formData.email || '').trim();

        const existingContact = contacts.find(c =>
            (contactIdentifier && c.email && c.email.toLowerCase() === contactIdentifier.toLowerCase()) ||
            (dealEmail && c.email && c.email.toLowerCase() === dealEmail.toLowerCase()) ||
            (`${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase() === clientName.toLowerCase()) ||
            (contactIdentifier && `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase() === contactIdentifier.toLowerCase())
        );

        if (!existingContact && (contactIdentifier || clientName)) {
            let fName = '';
            let lName = '';
            const nameToParse = (contactIdentifier && !contactIdentifier.includes('@')) ? contactIdentifier : clientName;
            if (nameToParse && !nameToParse.includes('@')) {
                const parts = nameToParse.split(/\s+/);
                fName = parts[0] || 'Contact';
                lName = parts.slice(1).join(' ') || '';
            } else if (contactIdentifier && contactIdentifier.includes('@')) {
                const userPart = contactIdentifier.split('@')[0];
                const parts = userPart.split(/[._-]/);
                fName = parts[0] ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1) : 'Contact';
                lName = parts.slice(1).join(' ') || '';
            } else {
                fName = 'Contact';
            }

            const defaultStageName = effectiveContactStages.length > 0
                ? (typeof effectiveContactStages[0] === 'string' ? effectiveContactStages[0] : (effectiveContactStages[0].name || effectiveContactStages[0].id || 'Lead'))
                : (leadStages.length > 0 ? leadStages[0] : 'Lead');

            const contactPayload = {
                firstName: fName,
                lastName: lName,
                email: dealEmail || (contactIdentifier.includes('@') ? contactIdentifier : `${fName.toLowerCase()}.${(lName || 'client').toLowerCase()}@example.com`),
                countryCode: '+1',
                phone: formData.phone || '+1 555-0100',
                contactSource: 'Direct Reference',
                leadStage: defaultStageName,
                leadStatus: 'New',
                owner: selectedOwner || 'Artiflow',
                addedBy: (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System',
                company: (formData.associatedCompany && formData.associatedCompany.trim() && formData.associatedCompany !== 'Direct Client') ? formData.associatedCompany.trim() : ''
            };

            try {
                const resContact = await api.post('/pipeline-contacts', contactPayload);
                const savedNewContact = resContact.data || {
                    _id: `contact-${Date.now()}`,
                    ...contactPayload,
                    createdDate: formatDateDDMMYYYY(new Date()),
                    avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(fName + ' ' + lName)}&background=0284c7&color=fff`
                };

                setContacts(prev => [savedNewContact, ...prev.filter(c => c._id !== savedNewContact._id)]);
            } catch (err) {
                console.warn('Auto create contact API error:', err.message);
                const fallbackContact = {
                    _id: `contact-${Date.now()}`,
                    ...contactPayload,
                    createdDate: formatDateDDMMYYYY(new Date()),
                    avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(fName + ' ' + lName)}&background=0284c7&color=fff`
                };
                setContacts(prev => [fallbackContact, ...prev.filter(c => c._id !== fallbackContact._id)]);
            }
        }

        // Auto-create company if provided and does not exist
        const compName = (formData.associatedCompany || '').trim();
        if (compName && compName !== 'Direct Client') {
            const existingCompany = companies.find(c => c.name && c.name.toLowerCase() === compName.toLowerCase());
            if (!existingCompany) {
                const companyPayload = {
                    name: compName,
                    domain: `${compName.toLowerCase().replace(/[^a-z0-9]/g, '')}.com`,
                    owner: selectedOwner || 'Artiflow',
                    industry: 'Software & Tech',
                    country: 'United States',
                    state: '',
                    city: formData.location || '',
                    postalCode: ''
                };
                try {
                    const resComp = await api.post('/pipeline-companies', companyPayload);
                    if (resComp.data) {
                        setCompanies(prev => [resComp.data, ...prev]);
                    } else {
                        const fallbackCompany = {
                            _id: `comp-${Date.now()}`,
                            ...companyPayload,
                            createdDate: formatDateDDMMYYYY(new Date())
                        };
                        setCompanies(prev => [fallbackCompany, ...prev]);
                    }
                } catch (err) {
                    console.warn('Auto create company API error:', err.message);
                    const fallbackCompany = {
                        _id: `comp-${Date.now()}`,
                        ...companyPayload,
                        createdDate: formatDateDDMMYYYY(new Date())
                    };
                    setCompanies(prev => [fallbackCompany, ...prev]);
                }
            }
        }

        let savedDeal = null;
        try {
            const res = await api.post('/deals', newDealPayload);
            if (res.data) {
                savedDeal = res.data;
                setDeals(prev => [res.data, ...prev]);
            }
        } catch (err) {
            console.warn('Create deal API error:', err.message);
        }

        if (!savedDeal) {
            const fallbackDeal = {
                _id: `deal-${Date.now()}`,
                ...newDealPayload
            };
            savedDeal = fallbackDeal;
            setDeals(prev => [fallbackDeal, ...prev]);
        }

        const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';
        const dealTitle = savedDeal.dealName || savedDeal.clientName || 'Associated Deal';
        logActivity(savedDeal._id, {
            type: 'deal',
            title: 'Deal Created',
            content: `Created deal "${dealTitle}" in stage "${savedDeal.stage || 'New'}" with value ${savedDeal.currency || '₹'} ${Number(savedDeal.dealValue || 0).toLocaleString()}. Assigned to ${savedDeal.salesOwner || 'Sales Specialist'}.`,
            author: authorName
        });

        if (savedDeal.associatedContact) {
            logActivity(savedDeal._id, {
                type: 'contact',
                title: 'Associated Contact Linked',
                content: `Linked contact ${savedDeal.associatedContact} with this deal.`,
                author: authorName
            });
        }

        if (savedDeal.associatedCompany) {
            logActivity(savedDeal._id, {
                type: 'company',
                title: 'Associated Company Linked',
                content: `Linked company "${savedDeal.associatedCompany}" with this deal.`,
                author: authorName
            });
        }

        const targetContact = detailedContact || contacts.find(c =>
            (c.email && savedDeal.associatedContact && c.email.toLowerCase() === savedDeal.associatedContact.toLowerCase()) ||
            (`${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase() === (savedDeal.clientName || '').toLowerCase()) ||
            (savedDeal.associatedContact && `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase() === savedDeal.associatedContact.toLowerCase())
        );

        if (targetContact && targetContact._id) {
            logActivity(targetContact._id, {
                type: 'deal',
                title: 'Deal Registered & Linked',
                content: `Deal "${dealTitle}" (${savedDeal.currency || '₹'}${Number(savedDeal.dealValue || 0).toLocaleString()} • ${savedDeal.stage || 'New'}) registered and linked with contact.`,
                author: authorName,
                createdAt: new Date().toISOString()
            }, true);
        }

        setShowCreateModal(false);
    };

    // Export deals to Excel sheet with all required fields
    const handleExport = () => {
        const dealsToExport = (selectedKanbanDealIds && selectedKanbanDealIds.length > 0)
            ? filteredDeals.filter(d => selectedKanbanDealIds.includes(d._id || d.id))
            : filteredDeals;

        if (!dealsToExport || dealsToExport.length === 0) {
            alert('No deals found to export.');
            return;
        }

        const exportRows = dealsToExport.map(deal => {
            // 1. Client Name
            const clientName = (deal.clientName || deal.dealName || '').trim();

            // Find matched contact if needed for email, phone, location, origin, or source
            const targetEmail = (deal.associatedContact || deal.email || deal.clientEmail || '').trim().toLowerCase();
            const targetName = (deal.clientName || deal.dealName || '').trim().toLowerCase();
            const targetPhone = String(deal.phone || deal.phoneNumber || '').replace(/\D/g, '');
            const matchedContact = (contacts || []).find(c => {
                if (!c) return false;
                if (deal.contactId && String(c._id) === String(deal.contactId)) return true;
                if (deal.associatedContactId && String(c._id) === String(deal.associatedContactId)) return true;
                const cEmail = (c.email || '').trim().toLowerCase();
                if (targetEmail && cEmail && cEmail === targetEmail) return true;
                const cFullName = `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase();
                if (targetName && cFullName && cFullName === targetName) return true;
                const cPhone = String(c.phone || '').replace(/\D/g, '');
                if (targetPhone && cPhone && targetPhone.length >= 7 && (targetPhone.includes(cPhone) || cPhone.includes(targetPhone))) return true;
                return false;
            });

            // 2. Email
            let email = (deal.email || deal.clientEmail || '').trim();
            if (!email && deal.associatedContact && deal.associatedContact.includes('@')) {
                email = deal.associatedContact.trim();
            }
            if (!email && matchedContact && matchedContact.email) {
                email = matchedContact.email.trim();
            }

            // 3. Phone
            let phone = (typeof getDealContactPhone === 'function' ? getDealContactPhone(deal) : '') || deal.phone || deal.phoneNumber || deal.clientPhone || '';
            if (!phone && matchedContact && matchedContact.phone) {
                const cCode = matchedContact.countryCode ? `${matchedContact.countryCode} ` : '';
                const rawP = String(matchedContact.phone).trim();
                phone = rawP.startsWith('+') ? rawP : `${cCode}${rawP}`.trim();
            }

            // 4. Location
            let location = (deal.location || deal.city || '').trim();
            if (!location && matchedContact) {
                location = [matchedContact.city, matchedContact.state, matchedContact.country].filter(Boolean).join(', ') || matchedContact.location || '';
            }

            // 5. Value
            const value = Number(deal.dealValue !== undefined && deal.dealValue !== null ? deal.dealValue : (deal.value || 0));

            // 6. Status
            const status = deal.stage || deal.status || (typeof getDealStageName === 'function' ? getDealStageName(deal, pipelines) : '') || 'New';

            // 7. Deal Owner
            const dealOwner = deal.salesOwner || deal.owner || deal.dealOwner || 'Unassigned';

            // 8. Origin
            const origin = (typeof getDealContactOrigin === 'function' ? getDealContactOrigin(deal) : '') || deal.origin || (matchedContact ? matchedContact.origin : '') || 'Domestic';

            // 9. Contact Source
            let contactSource = (deal.contactSource || deal.source || deal.leadSource || '').trim();
            if (!contactSource && matchedContact && matchedContact.contactSource) {
                contactSource = matchedContact.contactSource.trim();
            }
            if (!contactSource && deal.relatedLead && (deal.relatedLead.source || deal.relatedLead.leadSource)) {
                contactSource = deal.relatedLead.source || deal.relatedLead.leadSource;
            }
            if (!contactSource && deal.relatedMetaLead) {
                contactSource = 'Meta Lead';
            }
            if (!contactSource) {
                contactSource = 'Direct';
            }

            // 10. Created On
            let createdOn = '';
            const createdDateObj = (typeof parseDealDate === 'function' ? parseDealDate(deal.createdAt || deal.createdDate || deal.date) : null) || (deal.createdAt ? new Date(deal.createdAt) : null);
            if (createdDateObj && !isNaN(createdDateObj.getTime())) {
                const y = createdDateObj.getFullYear();
                const m = String(createdDateObj.getMonth() + 1).padStart(2, '0');
                const d = String(createdDateObj.getDate()).padStart(2, '0');
                createdOn = `${y}-${m}-${d}`;
            }

            return {
                'Client Name': clientName,
                'Email': email,
                'Phone': phone,
                'Location': location,
                'Value': value,
                'Status': status,
                'Deal Owner': dealOwner,
                'Origin': origin,
                'Contact Source': contactSource,
                'Created On': createdOn
            };
        });

        const fileDate = new Date().toISOString().slice(0, 10);

        try {
            const ws = XLSX.utils.json_to_sheet(exportRows);
            ws['!cols'] = [
                { wch: 26 }, // Client Name
                { wch: 28 }, // Email
                { wch: 20 }, // Phone
                { wch: 22 }, // Location
                { wch: 14 }, // Value
                { wch: 18 }, // Status
                { wch: 22 }, // Deal Owner
                { wch: 16 }, // Origin
                { wch: 20 }, // Contact Source
                { wch: 16 }  // Created On
            ];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Deals');
            XLSX.writeFile(wb, `pipeline_deals_${fileDate}.xlsx`);

            // Immediately notify all admins about deals export
            try {
                api.post('/pipeline-contacts/export-notify', {
                    category: 'Deals',
                    count: dealsToExport.length,
                    details: `${dealsToExport.length} deal(s) exported to Excel`
                }).catch(err => console.error('Export notify error:', err));
            } catch (e) {
                console.error('Export notification error:', e);
            }
        } catch (err) {
            console.error('XLSX export failed, using CSV fallback:', err);
            const headers = ['Client Name', 'Email', 'Phone', 'Location', 'Value', 'Status', 'Deal Owner', 'Origin', 'Contact Source', 'Created On'];
            const csvRows = [headers.join(',')];
            exportRows.forEach(r => {
                const row = [
                    `"${String(r['Client Name'] || '').replace(/"/g, '""')}"`,
                    `"${String(r['Email'] || '').replace(/"/g, '""')}"`,
                    `"${String(r['Phone'] || '').replace(/"/g, '""')}"`,
                    `"${String(r['Location'] || '').replace(/"/g, '""')}"`,
                    r['Value'] !== undefined ? r['Value'] : 0,
                    `"${String(r['Status'] || '').replace(/"/g, '""')}"`,
                    `"${String(r['Deal Owner'] || '').replace(/"/g, '""')}"`,
                    `"${String(r['Origin'] || '').replace(/"/g, '""')}"`,
                    `"${String(r['Contact Source'] || '').replace(/"/g, '""')}"`,
                    `"${String(r['Created On'] || '').replace(/"/g, '""')}"`
                ];
                csvRows.push(row.join(','));
            });
            const blob = new Blob(['\ufeff' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `pipeline_deals_${fileDate}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            // Immediately notify all admins about deals export fallback
            try {
                api.post('/pipeline-contacts/export-notify', {
                    category: 'Deals',
                    count: dealsToExport.length,
                    details: `${dealsToExport.length} deal(s) exported to CSV`
                }).catch(err => console.error('Export notify error:', err));
            } catch (e) {
                console.error('Export notification error:', e);
            }
        }
    };

    // Contacts Management Handlers
    const handlePhoneChange = (e) => {
        const digitsOnly = e.target.value.replace(/\D/g, '');
        const maxDigits = selectedCountry?.maxDigits || 10;
        setContactFormData(prev => ({
            ...prev,
            phone: digitsOnly.slice(0, maxDigits)
        }));
    };

    const handleSelectCountry = (country) => {
        setSelectedCountry(country);
        setIsCountryCodeOpen(false);
        setContactFormData(prev => ({
            ...prev,
            countryCode: country.code,
            phone: prev.phone.slice(0, country.maxDigits)
        }));
    };

    const handleDealPhoneChange = (e) => {
        const digitsOnly = e.target.value.replace(/\D/g, '');
        const maxDigits = dealSelectedCountry?.maxDigits || 10;
        setFormData(prev => ({
            ...prev,
            phone: digitsOnly.slice(0, maxDigits)
        }));
    };

    const handleSelectDealCountry = (country) => {
        setDealSelectedCountry(country);
        setIsDealCountryCodeOpen(false);
        setFormData(prev => ({
            ...prev,
            phone: (prev.phone || '').replace(/\D/g, '').slice(0, country.maxDigits)
        }));
    };

    const handleLeadStageChangeOrAdd = (val) => {
        const trimmed = (val || '').trim();
        if (!trimmed) return;
        setContactFormData(prev => ({ ...prev, leadStage: trimmed }));

        // Instantly create the stage column in contactStages if not present
        setContactStages(prev => {
            const current = prev || [];
            if (current.some(s => (s.name || s.id || '').toLowerCase() === trimmed.toLowerCase())) {
                return current;
            }
            const badgeClasses = ['new', 'hot', 'cold', 'won', 'custom'];
            const randomBadge = badgeClasses[current.length % badgeClasses.length];
            const newStageObj = { id: trimmed, name: trimmed, badgeClass: randomBadge, isCustom: true, createdAt: new Date().toISOString() };
            const nextStages = [...current, newStageObj];
            try {
                localStorage.setItem('crm_contact_stages', JSON.stringify(nextStages));
                let del = JSON.parse(localStorage.getItem('crm_deleted_stages') || '[]');
                del = del.filter(d => d !== trimmed.toLowerCase());
                localStorage.setItem('crm_deleted_stages', JSON.stringify(del));
                api.post('/pipeline-configs/save', {
                    contactStages: nextStages,
                    deletedStages: del
                }).catch(() => { });
            } catch (e) { }
            return nextStages;
        });

        setLeadStages(prev => Array.from(new Set([...(prev || []), trimmed])));
    };

    const handleOpenCreateContact = () => {
        setEditingContact(null);
        setSelectedCountry(COUNTRY_CODES[0]);
        const defaultStage = effectiveContactStages.length > 0
            ? (typeof effectiveContactStages[0] === 'string' ? effectiveContactStages[0] : (effectiveContactStages[0].name || effectiveContactStages[0].id))
            : 'Lead';
        setContactFormData({
            firstName: '',
            lastName: '',
            email: '',
            countryCode: '+1',
            phone: '',
            origin: 'Domestic',
            contactSource: contactSources[0] || 'Website Form',
            leadStage: defaultStage,
            leadStatus: leadStatuses[0] || 'New',
            owner: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Artiflow' : 'Artiflow',
            addedBy: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Admin System' : 'Admin System',
            company: ''
        });
        if (contactStages.length === 0 && defaultStage) {
            handleLeadStageChangeOrAdd(defaultStage);
        }
        setShowCreateContactModal(true);
    };

    const handleOpenEditContact = (contact, e) => {
        if (e) e.stopPropagation();
        setEditingContact(contact);
        let contactCountry = COUNTRY_CODES.find(c => c.code === contact.countryCode) || COUNTRY_CODES[0];
        let rawPhone = String(contact.phone || '').trim();
        let phoneDigits = '';
        if (rawPhone) {
            if (rawPhone.startsWith('+')) {
                const matched = COUNTRY_CODES.find(c => rawPhone.startsWith(c.code));
                if (matched) {
                    contactCountry = matched;
                    phoneDigits = rawPhone.slice(matched.code.length).replace(/\D/g, '');
                    if (matched.maxDigits && phoneDigits.length > matched.maxDigits) {
                        phoneDigits = phoneDigits.slice(0, matched.maxDigits);
                    }
                } else {
                    phoneDigits = rawPhone.replace(/\D/g, '').slice(0, 15);
                }
            } else {
                phoneDigits = rawPhone.replace(/\D/g, '');
                const maxLen = contactCountry?.maxDigits || 15;
                if (phoneDigits.length > maxLen) {
                    phoneDigits = phoneDigits.slice(0, maxLen);
                }
            }
        }
        setSelectedCountry(contactCountry);
        setIsCountryCodeOpen(false);
        setContactFormData({
            firstName: contact.firstName || '',
            lastName: contact.lastName || '',
            email: contact.email || '',
            countryCode: contactCountry.code,
            phone: phoneDigits,
            origin: contact.origin || 'Domestic',
            contactSource: contact.contactSource || '',
            leadStage: contact.leadStage || '',
            leadStatus: contact.leadStatus || '',
            owner: contact.owner || 'Artiflow',
            company: contact.company || ''
        });
        setActiveContactMenuId(null);
        setShowCreateContactModal(true);
    };

    const handleUpdateContactLeadStatus = async (contactId, newStatus, e) => {
        if (e) e.stopPropagation();
        setContacts(prev => prev.map(c => c._id === contactId ? { ...c, leadStatus: newStatus } : c));
        setActiveContactStatusDropdownId(null);
        setIsContactLeadStatusDropdownOpen(false);

        try {
            if (contactId && !contactId.toString().startsWith('contact-')) {
                await api.put(`/pipeline-contacts/${contactId}`, { leadStatus: newStatus });
            }
        } catch (err) {
            console.warn('Update contact leadStatus error:', err.message);
        }
    };

    const handleDeleteContact = async (contactId, e) => {
        if (e) e.stopPropagation();
        if (!canDelete) {
            alert('You do not have permission to delete contacts.');
            return;
        }
        const contactToDelete = contacts.find(c => c._id === contactId);
        if (window.confirm('Are you sure you want to delete this contact?')) {
            let deletedContacts = [];
            try {
                deletedContacts = JSON.parse(localStorage.getItem('crm_deleted_contacts') || '[]');
            } catch (err) { }
            if (contactId) deletedContacts.push(contactId.toString());
            if (contactToDelete && contactToDelete.email) deletedContacts.push(contactToDelete.email.toLowerCase());
            if (contactToDelete && contactToDelete.firstName) deletedContacts.push(`${contactToDelete.firstName} ${contactToDelete.lastName || ''}`.trim().toLowerCase());
            const uniqueDeletedContacts = Array.from(new Set(deletedContacts));
            try {
                localStorage.setItem('crm_deleted_contacts', JSON.stringify(uniqueDeletedContacts));
            } catch (err) { }

            try {
                if (contactId && !contactId.toString().startsWith('contact-')) {
                    await api.delete(`/pipeline-contacts/${contactId}`);
                }
                setContacts(prev => prev.filter(c => c._id !== contactId));
            } catch (err) {
                console.error('Delete contact API error:', err);
                alert(err.response?.data?.message || 'Failed to delete contact from database.');
                return;
            }
            if (contactToDelete) {
                const targetEmail = (contactToDelete.email || '').toLowerCase();
                const targetFullName = `${contactToDelete.firstName || ''} ${contactToDelete.lastName || ''}`.trim().toLowerCase();
                setDeals(prev =>
                    prev.map(d =>
                        (d.associatedContact && (d.associatedContact.toLowerCase() === targetEmail || d.associatedContact.toLowerCase() === targetFullName))
                            ? { ...d, associatedContact: '' }
                            : d
                    )
                );
            }
        }
        setActiveContactMenuId(null);
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        if (!contactFormData.firstName.trim()) {
            alert('Please enter First Name');
            return;
        }
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const trimmedEmail = (contactFormData.email || '').trim().toLowerCase();
        if (trimmedEmail && !emailRegex.test(trimmedEmail)) {
            alert('Please enter a valid email address.');
            return;
        }

        if (trimmedEmail) {
            const isDuplicate = contacts.some(c =>
                (c.email || '').trim().toLowerCase() === trimmedEmail &&
                (!editingContact || (c._id && editingContact._id && c._id.toString() !== editingContact._id.toString()))
            );
            if (isDuplicate) {
                alert(`A contact with the email "${contactFormData.email.trim()}" already exists. Duplicate email is not allowed.`);
                return;
            }
        }

        const minPhoneDigits = selectedCountry?.minDigits || selectedCountry?.maxDigits || 10;
        const maxPhoneDigits = selectedCountry?.maxDigits || 10;
        const cleanPhone = (contactFormData.phone || '').replace(/\D/g, '');
        if (cleanPhone && (cleanPhone.length < minPhoneDigits || cleanPhone.length > maxPhoneDigits)) {
            const digitMsg = minPhoneDigits === maxPhoneDigits ? `${maxPhoneDigits}` : `${minPhoneDigits} to ${maxPhoneDigits}`;
            alert(`Please enter a valid ${digitMsg}-digit phone number for ${selectedCountry?.country || 'the selected country'}.`);
            return;
        }

        const trimmedStage = (contactFormData.leadStage || '').trim() || (effectiveContactStages[0]?.name || 'Lead');

        // Auto-register any new custom dropdown items
        if (!contactSources.includes(contactFormData.contactSource)) {
            setContactSources(prev => [...prev, contactFormData.contactSource]);
        }
        if (!leadStages.includes(trimmedStage)) {
            setLeadStages(prev => [...prev, trimmedStage]);
        }
        if (!leadStatuses.includes(contactFormData.leadStatus)) {
            setLeadStatuses(prev => [...prev, contactFormData.leadStatus]);
        }

        // Ensure contactStages has this stage column and un-delete it if previously in deletedStages
        if (!contactStages.some(s => (s.name || s.id || '').toLowerCase() === trimmedStage.toLowerCase())) {
            const newStageObj = { id: trimmedStage, name: trimmedStage, badgeClass: 'new', isCustom: true, createdAt: new Date().toISOString() };
            const nextStages = [...contactStages, newStageObj];
            setContactStages(nextStages);
            try {
                localStorage.setItem('crm_contact_stages', JSON.stringify(nextStages));
                let del = JSON.parse(localStorage.getItem('crm_deleted_stages') || '[]');
                del = del.filter(d => d !== trimmedStage.toLowerCase());
                localStorage.setItem('crm_deleted_stages', JSON.stringify(del));
                api.post('/pipeline-configs/save', {
                    contactStages: nextStages,
                    deletedStages: del
                }).catch(() => { });
            } catch (e) { }
        }

        // Un-delete this contact from crm_deleted_contacts if it was previously deleted
        try {
            let delContacts = JSON.parse(localStorage.getItem('crm_deleted_contacts') || '[]');
            const cleanEmail = contactFormData.email.trim().toLowerCase();
            const cleanFullName = `${contactFormData.firstName.trim()} ${contactFormData.lastName.trim()}`.toLowerCase();
            delContacts = delContacts.filter(d =>
                d &&
                d.toLowerCase() !== cleanEmail &&
                d.toLowerCase() !== cleanFullName &&
                (editingContact ? d !== editingContact._id?.toString() : true)
            );
            localStorage.setItem('crm_deleted_contacts', JSON.stringify(delContacts));
        } catch (e) { }

        const payload = {
            firstName: contactFormData.firstName.trim(),
            lastName: contactFormData.lastName.trim(),
            email: contactFormData.email.trim(),
            countryCode: contactFormData.countryCode,
            phone: contactFormData.phone,
            origin: contactFormData.origin || 'Domestic',
            contactSource: contactFormData.contactSource || 'Website Form',
            leadStage: trimmedStage,
            leadStatus: contactFormData.leadStatus || 'New',
            owner: contactFormData.owner || '',
            addedBy: contactFormData.addedBy || contactFormData.owner || (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Admin System'),
            company: contactFormData.company.trim() || ''
        };

        let savedContact = null;
        if (editingContact) {
            try {
                if (editingContact._id && !editingContact._id.toString().startsWith('contact-')) {
                    const res = await api.put(`/pipeline-contacts/${editingContact._id}`, payload);
                    if (res.data) {
                        savedContact = res.data;
                        setContacts(prev => prev.map(c => (c._id === editingContact._id ? res.data : c)));
                    }
                }
            } catch (err) {
                console.warn('Update contact API error:', err.message);
                const errMsg = err.response?.data?.message || 'Failed to update contact. Please check your inputs.';
                alert(errMsg);
                return;
            }

            if (!savedContact) {
                savedContact = { ...editingContact, ...payload };
                setContacts(prev =>
                    prev.map(c =>
                        c._id === editingContact._id ? { ...c, ...payload } : c
                    )
                );
            }
        } else {
            try {
                const res = await api.post('/pipeline-contacts', payload);
                if (res.data) {
                    savedContact = res.data;
                    setContacts(prev => [res.data, ...prev.filter(c => c._id !== res.data._id)]);
                }
            } catch (err) {
                console.warn('Create contact API error:', err.message);
                const errMsg = err.response?.data?.message || 'Failed to create contact. Please check your inputs.';
                alert(errMsg);
                return;
            }
        }

        if (detailedDealId && savedContact) {
            const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';
            logActivity(detailedDealId, {
                type: 'contact',
                title: 'Associated Contact Added',
                content: `Created and linked contact "${savedContact.firstName} ${savedContact.lastName}" (${savedContact.email}) to this deal.`,
                author: authorName
            });
            setDeals(prev => prev.map(d => d._id === detailedDealId ? {
                ...d,
                associatedContact: savedContact.email,
                email: savedContact.email || d.email,
                phone: savedContact.phone ? `${savedContact.countryCode || '+1'} ${savedContact.phone}` : d.phone,
                associatedCompany: (!d.associatedCompany || d.associatedCompany === 'Direct Client') && savedContact.company ? savedContact.company : d.associatedCompany
            } : d));
            try {
                if (detailedDealId && !detailedDealId.toString().startsWith('deal-')) {
                    api.put(`/deals/${detailedDealId}`, {
                        associatedContact: savedContact.email,
                        email: savedContact.email,
                        phone: savedContact.phone ? `${savedContact.countryCode || '+1'} ${savedContact.phone}` : undefined
                    }).catch(() => { });
                }
            } catch (e) { }
        }

        if (showCreateModal && savedContact) {
            setFormData(prev => ({
                ...prev,
                associatedContact: savedContact.email || prev.associatedContact,
                email: savedContact.email || prev.email,
                phone: savedContact.phone ? `${savedContact.countryCode || '+1'} ${savedContact.phone}` : prev.phone,
                clientName: prev.clientName || `${savedContact.firstName} ${savedContact.lastName}`.trim(),
                associatedCompany: (savedContact.company && savedContact.company !== 'Direct Client') ? savedContact.company : prev.associatedCompany
            }));
        }

        setShowCreateContactModal(false);
        setEditingContact(null);
    };

    const handleExportContacts = () => {
        const csvContent =
            'data:text/csv;charset=utf-8,' +
            ['First Name,Last Name,Email,Phone,Company,Lead Stage,Lead Status,Contact Source,Type,Owner,Created Date']
                .concat(
                    filteredContacts.map(
                        c => `"${c.firstName}","${c.lastName}","${c.email}","${c.countryCode} ${c.phone}","${c.company || ''}","${c.leadStage}","${c.leadStatus}","${c.contactSource}","${c.contactType}","${c.owner}","${c.createdDate || ''}"`
                    )
                )
                .join('\n');

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `contacts_crm_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Immediately notify all admins about contacts export
        try {
            api.post('/pipeline-contacts/export-notify', {
                category: 'Contacts',
                count: filteredContacts.length,
                details: `${filteredContacts.length} contact(s) exported to CSV`
            }).catch(err => console.error('Export notify error:', err));
        } catch (e) {
            console.error('Export notification error:', e);
        }
    };

    // Drag and drop for contacts across stages
    const handleContactDragStart = (e, contactId) => {
        if (!canUpdate) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.setData('text/plain', contactId);
        e.dataTransfer.effectAllowed = 'move';
        setDraggedContactId(contactId);
    };

    const handleContactDragEnd = () => {
        setDraggedContactId(null);
        setDragOverContactColumnId(null);
    };

    const handleContactDragOver = (e, stageId) => {
        if (!canUpdate) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverContactColumnId !== stageId) {
            setDragOverContactColumnId(stageId);
        }
    };

    const handleContactDragLeave = (e, stageId) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        if (dragOverContactColumnId === stageId) {
            setDragOverContactColumnId(null);
        }
    };

    const handleContactDrop = async (e, targetStageName) => {
        e.preventDefault();
        if (!canUpdate) return;
        const contactId = e.dataTransfer?.getData('text/plain') || draggedContactId;
        if (contactId) {
            const targetContactIds = (selectedKanbanContactIds.includes(contactId))
                ? selectedKanbanContactIds
                : [contactId];

            const updateData = { leadStage: targetStageName };
            setContacts(prev =>
                prev.map(c => targetContactIds.includes(c._id) ? { ...c, ...updateData } : c)
            );

            const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';

            try {
                await api.post('/pipeline-contacts/bulk-stage', {
                    contactIds: targetContactIds,
                    targetStage: targetStageName
                });
            } catch (err) {
                console.warn('Bulk drop move API error, falling back to individual calls:', err.message);
                for (const id of targetContactIds) {
                    if (id && !id.toString().startsWith('contact-')) {
                        api.put(`/pipeline-contacts/${id}`, updateData).catch(() => { });
                    }
                }
            }

            targetContactIds.slice(0, 50).forEach(id => {
                logActivity(id, {
                    type: 'contact',
                    title: 'Stage Moved',
                    content: `Moved contact to "${targetStageName}".`,
                    author: authorName
                });
            });

            if (selectedKanbanContactIds.includes(contactId)) {
                setSelectedKanbanContactIds([]);
            }
        }
        setDraggedContactId(null);
        setDragOverContactColumnId(null);
    };

    // Contacts Kanban Double Click on Card: multi-select all contacts in that specific stage column
    const handleContactCardDoubleClick = (contact, stageContacts, stageName, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        // Cancel any scheduled single-click contact detail opening
        if (contactClickTimerRef.current) {
            clearTimeout(contactClickTimerRef.current);
            contactClickTimerRef.current = null;
        }

        const stageContactIds = (stageContacts || []).map(c => c._id);
        if (stageContactIds.length === 0) return;

        setSelectedKanbanContactIds(prev => {
            const allStageSelected = stageContactIds.every(id => prev.includes(id));
            if (allStageSelected) {
                // If all cards in this stage were already selected, toggle off / deselect them
                return prev.filter(id => !stageContactIds.includes(id));
            } else {
                // Multi-select all cards of this stage (retaining any already selected cards in other stages)
                return Array.from(new Set([...prev, ...stageContactIds]));
            }
        });
    };

    // Toggle individual contact checkbox selection
    const handleToggleContactSelection = (contactId, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (contactClickTimerRef.current) {
            clearTimeout(contactClickTimerRef.current);
            contactClickTimerRef.current = null;
        }
        setSelectedKanbanContactIds(prev =>
            prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId]
        );
    };

    // Toggle all contacts of a stage from column header
    const handleToggleContactStageSelection = (stageContacts, e) => {
        if (e) e.stopPropagation();
        const stageContactIds = (stageContacts || []).map(c => c._id);
        if (stageContactIds.length === 0) return;
        setSelectedKanbanContactIds(prev => {
            const allStageSelected = stageContactIds.every(id => prev.includes(id));
            if (allStageSelected) {
                return prev.filter(id => !stageContactIds.includes(id));
            } else {
                return Array.from(new Set([...prev, ...stageContactIds]));
            }
        });
    };

    // Toggle all filtered contacts (for list view)
    const handleToggleAllFilteredContacts = (e) => {
        if (e) e.stopPropagation();
        const filteredIds = filteredContacts.map(c => c._id);
        if (filteredIds.length === 0) return;
        setSelectedKanbanContactIds(prev => {
            const allSelected = filteredIds.every(id => prev.includes(id));
            if (allSelected) {
                return prev.filter(id => !filteredIds.includes(id));
            } else {
                return Array.from(new Set([...prev, ...filteredIds]));
            }
        });
    };

    // Debounced single click on contact card to avoid conflict with double-click
    const handleContactCardClick = (contactId, e) => {
        if (e) e.stopPropagation();
        if (contactClickTimerRef.current) {
            clearTimeout(contactClickTimerRef.current);
            contactClickTimerRef.current = null;
        }
        contactClickTimerRef.current = setTimeout(() => {
            setDetailedContactId(contactId);
            contactClickTimerRef.current = null;
        }, 230);
    };

    // Bulk move all selected contacts to a target stage
    const handleBulkMoveContactStage = async (targetStage, customIds = null) => {
        const targetIds = Array.isArray(customIds) && customIds.length > 0 ? customIds : selectedKanbanContactIds;
        if (!targetStage || targetIds.length === 0) return;
        const contactIdsToMove = [...targetIds];
        const updateData = { leadStage: targetStage };
        const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';

        // Optimistic UI update immediately
        setContacts(prev =>
            prev.map(c => contactIdsToMove.includes(c._id) ? { ...c, ...updateData } : c)
        );
        setIsBulkMoveContactStageDropdownOpen(false);
        setSelectedKanbanContactIds([]);

        try {
            await api.post('/pipeline-contacts/bulk-stage', {
                contactIds: contactIdsToMove,
                targetStage
            });
        } catch (err) {
            console.warn('Bulk move API error, falling back to individual calls:', err.message);
            for (const id of contactIdsToMove) {
                if (id && !id.toString().startsWith('contact-')) {
                    api.put(`/pipeline-contacts/${id}`, updateData).catch(() => { });
                }
            }
        }

        contactIdsToMove.slice(0, 50).forEach(id => {
            logActivity(id, {
                type: 'contact',
                title: 'Stage Moved',
                content: `Moved contact to stage "${targetStage}" via bulk action.`,
                author: authorName
            });
        });
    };

    // Bulk delete all selected contacts
    const handleBulkDeleteContacts = async (customIds = null) => {
        const targetIds = Array.isArray(customIds) && customIds.length > 0 ? customIds : selectedKanbanContactIds;
        if (!canDelete || targetIds.length === 0) return;
        if (window.confirm(`Are you sure you want to delete ${targetIds.length} selected contact(s)?`)) {
            const contactIdsToDelete = [...targetIds];
            try {
                await api.post('/pipeline-contacts/bulk-delete', { contactIds: contactIdsToDelete });
                setContacts(prev => prev.filter(c => !contactIdsToDelete.includes(c._id)));
                setSelectedKanbanContactIds([]);
            } catch (err) {
                console.warn('Bulk delete contacts API error, falling back to individual calls:', err.message);
                let deletedSuccessfully = [];
                for (const id of contactIdsToDelete) {
                    try {
                        if (id && !id.toString().startsWith('contact-')) {
                            await api.delete(`/pipeline-contacts/${id}`);
                            deletedSuccessfully.push(id);
                        }
                    } catch (singleErr) {
                        console.warn(`Bulk delete contact ${id} API error:`, singleErr.message);
                    }
                }
                if (deletedSuccessfully.length > 0) {
                    setContacts(prev => prev.filter(c => !deletedSuccessfully.includes(c._id)));
                    setSelectedKanbanContactIds(prev => prev.filter(id => !deletedSuccessfully.includes(id)));
                } else {
                    alert(err.response?.data?.message || 'Failed to delete selected contact(s). Please verify your permissions.');
                }
            }
        }
    };

    // Bulk export selected contacts to CSV
    const handleBulkExportContacts = () => {
        if (selectedKanbanContactIds.length === 0) return;
        const contactsToExport = contacts.filter(c => selectedKanbanContactIds.includes(c._id));
        if (contactsToExport.length === 0) return;

        const csvContent =
            'data:text/csv;charset=utf-8,' +
            ['First Name,Last Name,Email,Phone,Company,Lead Stage,Lead Status,Contact Source,Type,Owner,Created Date']
                .concat(
                    contactsToExport.map(
                        c =>
                            `"${c.firstName || ''}","${c.lastName || ''}","${c.email || ''}","${c.phone || ''}","${c.company || ''}","${c.leadStage || ''}","${c.leadStatus || ''}","${c.contactSource || ''}","${c.contactType || ''}","${c.owner || ''}","${c.createdDate || ''}"`
                    )
                )
                .join('\n');

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `crm_contacts_selected_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Immediately notify all admins about bulk contacts export
        try {
            api.post('/pipeline-contacts/export-notify', {
                category: 'Contacts',
                count: contactsToExport.length,
                details: `${contactsToExport.length} selected contact(s) exported to CSV`
            }).catch(err => console.error('Export notify error:', err));
        } catch (e) {
            console.error('Export notification error:', e);
        }
    };

    const handleChangeDealStage = async (newStage) => {
        if (!detailedDeal) return;
        const updateData = {
            pipeline: newStage,
            stage: newStage
        };
        try {
            if (detailedDeal._id && !detailedDeal._id.toString().startsWith('deal-')) {
                await api.put(`/deals/${detailedDeal._id}`, updateData);
            }
        } catch (err) {
            console.warn('Change deal stage API sync:', err.message);
        }
        setDeals(prev =>
            prev.map(d => (String(d._id) === String(detailedDeal._id) ? { ...d, ...updateData } : d))
        );
        const newAct = {
            id: `act-${Date.now()}`,
            type: 'activity',
            author: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Admin' : 'Admin',
            title: `Stage updated to ${newStage}`,
            content: `Moved deal stage to ${newStage}.`,
            time: 'Just now',
            date: formatDateDDMMYYYY(new Date())
        };
        setDealActivities(prev => ({
            ...prev,
            [detailedDeal._id]: [newAct, ...(prev[detailedDeal._id] || [])]
        }));
    };

    // --------------------------------------------------------------------------
    // Company Management Handlers
    // --------------------------------------------------------------------------
    const handleOpenCreateCompany = () => {
        setEditingCompany(null);
        setCompanyFormData({
            name: '',
            domain: '',
            owner: [],
            industry: 'Software & Tech',
            country: 'United States',
            state: '',
            city: '',
            postalCode: ''
        });
        setShowCreateCompanyModal(true);
    };

    const handleOpenEditCompany = (company, e) => {
        if (e) e.stopPropagation();
        setEditingCompany(company);
        let initialOwner = [];
        if (Array.isArray(company.owner)) {
            initialOwner = company.owner.filter(Boolean);
        } else if (typeof company.owner === 'string' && company.owner.trim()) {
            initialOwner = company.owner.split(',').map(s => s.trim()).filter(Boolean);
        }
        setCompanyFormData({
            name: company.name || '',
            domain: company.domain || '',
            owner: initialOwner,
            industry: company.industry || 'Software & Tech',
            country: company.country || 'United States',
            state: company.state || '',
            city: company.city || '',
            postalCode: company.postalCode || ''
        });
        setActiveCompanyMenuId(null);
        setShowCreateCompanyModal(true);
    };

    const handleDeleteCompany = async (companyId, e) => {
        if (e) e.stopPropagation();
        if (!canDelete) {
            alert('You do not have permission to delete companies.');
            return;
        }
        if (window.confirm('Are you sure you want to delete this company?')) {
            try {
                if (companyId && !companyId.toString().startsWith('comp-')) {
                    await api.delete(`/pipeline-companies/${companyId}`);
                }
            } catch (err) {
                console.warn('Delete company API sync:', err.message);
            }
            setCompanies(prev => prev.filter(c => c._id !== companyId));
        }
        setActiveCompanyMenuId(null);
    };

    const handleCompanySubmit = async (e) => {
        e.preventDefault();
        if (!companyFormData.name.trim()) {
            alert('Please enter a company name');
            return;
        }

        if (companyFormData.domain && companyFormData.domain.trim() && !isValidCompanyDomain(companyFormData.domain)) {
            alert('Please enter a valid company domain.');
            return;
        }

        // Auto-register any new custom dropdown items
        if (!industryOptions.includes(companyFormData.industry)) {
            setIndustryOptions(prev => [...prev, companyFormData.industry]);
        }
        if (!countryListOptions.includes(companyFormData.country)) {
            setCountryListOptions(prev => [...prev, companyFormData.country]);
        }

        const ownerValue = Array.isArray(companyFormData.owner)
            ? companyFormData.owner.filter(Boolean).join(', ')
            : (companyFormData.owner || '');

        const payload = {
            name: companyFormData.name.trim(),
            domain: companyFormData.domain.trim(),
            owner: ownerValue || 'Artiflow',
            industry: companyFormData.industry,
            country: companyFormData.country,
            state: companyFormData.state.trim(),
            city: companyFormData.city.trim(),
            postalCode: companyFormData.postalCode.trim()
        };

        let savedCompanyName = payload.name;
        if (editingCompany) {
            try {
                if (editingCompany._id && !editingCompany._id.toString().startsWith('comp-')) {
                    const res = await api.put(`/pipeline-companies/${editingCompany._id}`, payload);
                    if (res.data) {
                        setCompanies(prev => prev.map(c => (c._id === editingCompany._id ? res.data : c)));
                        savedCompanyName = res.data.name || payload.name;
                    }
                }
            } catch (err) {
                console.warn('Update company API error:', err.message);
            }

            if (!savedCompanyName) {
                setCompanies(prev =>
                    prev.map(c =>
                        c._id === editingCompany._id ? { ...c, ...payload } : c
                    )
                );
            }
        } else {
            try {
                const res = await api.post('/pipeline-companies', payload);
                if (res.data) {
                    setCompanies(prev => [res.data, ...prev]);
                    savedCompanyName = res.data.name || payload.name;
                }
            } catch (err) {
                console.warn('Create company API error:', err.message);
            }

            if (!savedCompanyName) {
                const newCompany = {
                    _id: `comp-${Date.now()}`,
                    ...payload,
                    createdDate: formatDateDDMMYYYY(new Date())
                };
                setCompanies(prev => [newCompany, ...prev]);
            }
        }

        // Automatically associate selected contact emails & their deals with this company
        const selectedOwnerEmails = Array.isArray(companyFormData.owner)
            ? companyFormData.owner.map(e => e.trim().toLowerCase()).filter(Boolean)
            : (companyFormData.owner ? [companyFormData.owner.trim().toLowerCase()] : []);

        if (selectedOwnerEmails.length > 0 && savedCompanyName) {
            setContacts(prev => prev.map(c => {
                const cEmail = (c.email || '').trim().toLowerCase();
                if (selectedOwnerEmails.includes(cEmail)) {
                    return { ...c, company: savedCompanyName };
                }
                return c;
            }));

            // Async sync to backend for all matched contacts
            contacts.forEach(async (c) => {
                const cEmail = (c.email || '').trim().toLowerCase();
                if (selectedOwnerEmails.includes(cEmail) && c._id && !c._id.toString().startsWith('cont-')) {
                    try {
                        await api.put(`/pipeline-contacts/${c._id}`, { ...c, company: savedCompanyName });
                    } catch (err) {
                        console.warn('Sync contact company association:', err.message);
                    }
                }
            });

            // Update associated deals for these contacts
            setDeals(prev => prev.map(d => {
                const dEmail = (d.email || d.associatedContact || '').trim().toLowerCase();
                const matchesContact = selectedOwnerEmails.includes(dEmail) || contacts.some(c => selectedOwnerEmails.includes((c.email || '').trim().toLowerCase()) && (c._id === d.associatedContactId || c._id === d.contactId));
                if (matchesContact && (!d.associatedCompany || d.associatedCompany === 'Direct Client' || !d.company)) {
                    if (d._id && !d._id.toString().startsWith('deal-sample-') && !d._id.toString().startsWith('deal-')) {
                        api.put(`/deals/${d._id}`, { company: savedCompanyName, associatedCompany: savedCompanyName }).catch(() => { });
                    }
                    return { ...d, associatedCompany: savedCompanyName, company: savedCompanyName };
                }
                return d;
            }));
        }

        if (showCreateContactModal) {
            setContactFormData(prev => ({ ...prev, company: savedCompanyName }));
        }
        if (showCreateModal) {
            setFormData(prev => ({ ...prev, associatedCompany: savedCompanyName }));
        }
        if (detailedDealId && savedCompanyName) {
            const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';
            logActivity(detailedDealId, {
                type: 'company',
                title: 'Company Registered & Linked',
                content: `Company "${savedCompanyName}" registered and linked with deal.`,
                author: authorName,
                createdAt: new Date().toISOString()
            }, false);
            setDeals(prev => prev.map(d => d._id === detailedDealId ? {
                ...d,
                associatedCompany: savedCompanyName
            } : d));
            if (detailedDealId && !detailedDealId.toString().startsWith('deal-sample-') && !detailedDealId.toString().startsWith('deal-')) {
                api.put(`/deals/${detailedDealId}`, { company: savedCompanyName, associatedCompany: savedCompanyName }).catch(() => { });
            }
        }
        if (detailedContactId && savedCompanyName) {
            const authorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System';
            logActivity(detailedContactId, {
                type: 'company',
                title: 'Company Registered & Linked',
                content: `Company "${savedCompanyName}" registered and linked with contact.`,
                author: authorName,
                createdAt: new Date().toISOString()
            }, true);
            setContacts(prev => prev.map(c => c._id === detailedContactId ? { ...c, company: savedCompanyName } : c));
            if (detailedContactId && !detailedContactId.toString().startsWith('contact-')) {
                api.put(`/pipeline-contacts/${detailedContactId}`, { company: savedCompanyName }).catch(() => { });
            }
        }

        setShowCreateCompanyModal(false);
        setEditingCompany(null);
    };

    const handleExportCompanies = () => {
        const csvContent =
            'data:text/csv;charset=utf-8,' +
            ['Company Name,Domain,Owner,Industry,Country,State,City,Postal Code,Created Date']
                .concat(
                    filteredCompanies.map(
                        c => `"${c.name}","${c.domain || ''}","${c.owner || ''}","${c.industry || ''}","${c.country || ''}","${c.state || ''}","${c.city || ''}","${c.postalCode || ''}","${c.createdDate || ''}"`
                    )
                )
                .join('\n');

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `crm_companies_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Immediately notify all admins about companies export
        try {
            api.post('/pipeline-contacts/export-notify', {
                category: 'Companies',
                count: filteredCompanies.length,
                details: `${filteredCompanies.length} company record(s) exported to CSV`
            }).catch(err => console.error('Export notify error:', err));
        } catch (e) {
            console.error('Export notification error:', e);
        }
    };

    // Companies Double Click on Card: multi-select all companies (or double-click again to deselect)
    const handleCompanyDoubleClick = (company, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (companyClickTimerRef.current) {
            clearTimeout(companyClickTimerRef.current);
            companyClickTimerRef.current = null;
        }

        // If company belongs to an industry, select all companies in that industry;
        // if all in that industry are already selected, toggle all filtered companies
        const sameIndustryIds = (company && company.industry)
            ? filteredCompanies.filter(c => (c.industry || '').toLowerCase() === (company.industry || '').toLowerCase()).map(c => c._id)
            : filteredCompanies.map(c => c._id);

        const targetIds = sameIndustryIds.length > 0 ? sameIndustryIds : filteredCompanies.map(c => c._id);
        if (targetIds.length === 0) return;

        setSelectedCompanyIds(prev => {
            const allSelected = targetIds.every(id => prev.includes(id));
            if (allSelected) {
                return prev.filter(id => !targetIds.includes(id));
            } else {
                return Array.from(new Set([...prev, ...targetIds]));
            }
        });
    };

    // Toggle individual company checkbox selection
    const handleToggleCompanySelection = (companyId, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (companyClickTimerRef.current) {
            clearTimeout(companyClickTimerRef.current);
            companyClickTimerRef.current = null;
        }
        setSelectedCompanyIds(prev =>
            prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]
        );
    };

    // Toggle all filtered companies (for table header or toolbar)
    const handleToggleAllFilteredCompanies = (e) => {
        if (e) e.stopPropagation();
        const filteredIds = filteredCompanies.map(c => c._id);
        if (filteredIds.length === 0) return;
        setSelectedCompanyIds(prev => {
            const allSelected = filteredIds.every(id => prev.includes(id));
            if (allSelected) {
                return prev.filter(id => !filteredIds.includes(id));
            } else {
                return Array.from(new Set([...prev, ...filteredIds]));
            }
        });
    };

    // Debounced single click on company card/row to open details without double-click conflict
    const handleCompanyClick = (company, e) => {
        if (e) e.stopPropagation();
        if (companyClickTimerRef.current) {
            clearTimeout(companyClickTimerRef.current);
            companyClickTimerRef.current = null;
        }
        companyClickTimerRef.current = setTimeout(() => {
            setViewingCompany(company);
            companyClickTimerRef.current = null;
        }, 230);
    };

    // Bulk delete all selected companies
    const handleBulkDeleteCompanies = async () => {
        if (!canDelete || selectedCompanyIds.length === 0) return;
        if (window.confirm(`Are you sure you want to delete ${selectedCompanyIds.length} selected company(ies)?`)) {
            const companyIdsToDelete = [...selectedCompanyIds];
            setCompanies(prev => prev.filter(c => !companyIdsToDelete.includes(c._id)));
            for (const id of companyIdsToDelete) {
                try {
                    if (id && !id.toString().startsWith('comp-')) {
                        await api.delete(`/pipeline-companies/${id}`);
                    }
                } catch (err) {
                    console.warn(`Bulk delete company ${id} API error:`, err.message);
                }
            }
            setSelectedCompanyIds([]);
        }
    };

    // Bulk update company industry
    const handleBulkUpdateCompanyIndustry = async (newIndustry) => {
        if (!newIndustry || selectedCompanyIds.length === 0) return;
        const idsToUpdate = [...selectedCompanyIds];
        setCompanies(prev =>
            prev.map(c => idsToUpdate.includes(c._id) ? { ...c, industry: newIndustry } : c)
        );
        for (const id of idsToUpdate) {
            try {
                if (id && !id.toString().startsWith('comp-')) {
                    await api.put(`/pipeline-companies/${id}`, { industry: newIndustry });
                }
            } catch (err) {
                console.warn(`Bulk update company industry ${id} API error:`, err.message);
            }
        }
        setIsBulkCompanyIndustryDropdownOpen(false);
        setSelectedCompanyIds([]);
    };

    // Bulk update company owner
    const handleBulkUpdateCompanyOwner = async (newOwner) => {
        if (!newOwner || selectedCompanyIds.length === 0) return;
        const idsToUpdate = [...selectedCompanyIds];
        setCompanies(prev =>
            prev.map(c => idsToUpdate.includes(c._id) ? { ...c, owner: newOwner } : c)
        );
        for (const id of idsToUpdate) {
            try {
                if (id && !id.toString().startsWith('comp-')) {
                    await api.put(`/pipeline-companies/${id}`, { owner: newOwner });
                }
            } catch (err) {
                console.warn(`Bulk update company owner ${id} API error:`, err.message);
            }
        }
        setIsBulkCompanyOwnerDropdownOpen(false);
        setSelectedCompanyIds([]);
    };

    // Bulk export selected companies
    const handleBulkExportCompanies = () => {
        if (selectedCompanyIds.length === 0) return;
        const companiesToExport = filteredCompanies.filter(c => selectedCompanyIds.includes(c._id));
        if (companiesToExport.length === 0) return;
        const csvContent =
            'data:text/csv;charset=utf-8,' +
            ['Company Name,Domain,Owner,Industry,Country,State,City,Postal Code,Created Date']
                .concat(
                    companiesToExport.map(
                        c => `"${c.name}","${c.domain || ''}","${c.owner || ''}","${c.industry || ''}","${c.country || ''}","${c.state || ''}","${c.city || ''}","${c.postalCode || ''}","${c.createdDate || ''}"`
                    )
                )
                .join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `crm_companies_selected_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Immediately notify all admins about bulk companies export
        try {
            api.post('/pipeline-contacts/export-notify', {
                category: 'Companies',
                count: companiesToExport.length,
                details: `${companiesToExport.length} selected company record(s) exported to CSV`
            }).catch(err => console.error('Export notify error:', err));
        } catch (e) {
            console.error('Export notification error:', e);
        }
    };

    const handleExportTasks = () => {
        const csvContent =
            'data:text/csv;charset=utf-8,' +
            ['Title,Description,Status,Priority,Type,Assignee,Due Date,Associated Deal,Associated Contact']
                .concat(
                    filteredTasks.map(
                        t => `"${(t.title || '').replace(/"/g, '""')}","${(t.description || '').replace(/"/g, '""')}","${t.completed ? 'Completed' : t.status || 'Pending'}","${t.priority || 'Medium'}","${t.type || 'Follow-up'}","${(t.assignee || '').replace(/"/g, '""')}","${t.dueDate || ''}","${(t.associatedDealName || '').replace(/"/g, '""')}","${(t.associatedContactName || '').replace(/"/g, '""')}"`
                    )
                )
                .join('\n');

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `crm_tasks_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Immediately notify all admins about tasks export
        try {
            api.post('/pipeline-contacts/export-notify', {
                category: 'Tasks',
                count: filteredTasks.length,
                details: `${filteredTasks.length} task(s) exported to CSV`
            }).catch(err => console.error('Export notify error:', err));
        } catch (e) {
            console.error('Export notification error:', e);
        }
    };

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (countryCodeRef.current && !countryCodeRef.current.contains(event.target)) {
                setIsCountryCodeOpen(false);
            }
            if (dealCountryCodeRef.current && !dealCountryCodeRef.current.contains(event.target)) {
                setIsDealCountryCodeOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!isLoading && !canRead) {
        return (
            <div className="pipeline-page-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
                <div style={{ maxWidth: '480px', width: '100%', background: '#fff', padding: '40px', borderRadius: '16px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)', textAlign: 'center', border: '1px solid #fee2e2' }}>
                    <div style={{ width: '64px', height: '64px', background: '#fef2f2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#dc2626' }}>
                        <AlertCircle size={32} />
                    </div>
                    <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Access Restricted</h2>
                    <p style={{ color: '#4b5563', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' }}>
                        Your role does not have permission to access the Pipeline & CRM module. Please contact your system administrator or Sales Head to request access.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="pipeline-page-wrapper">
            {/* ------------------------------------------------------------------
          1. Left Navigation Tabs Sub-Sidebar (hidden when in deal or contact details view)
          ------------------------------------------------------------------ */}
            {!(activeTab === 'deals' && detailedDeal) && !(activeTab === 'contacts' && detailedContact) && (
                <aside className={`pipeline-sub-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                    <div className="pipeline-sub-sidebar-header">
                        {!isSidebarCollapsed && <span className="pipeline-sub-sidebar-title">Menu</span>}
                        <button
                            type="button"
                            className="pipeline-sidebar-toggle-btn"
                            onClick={() => setIsSidebarCollapsed(prev => !prev)}
                            title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        >
                            {isSidebarCollapsed ? <ChevronsRight size={15} /> : <ChevronsLeft size={15} />}
                        </button>
                    </div>

                    <ul className="pipeline-sub-sidebar-menu">
                        {visibleSidebarTabs.map(tab => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <li
                                    key={tab.id}
                                    className={`pipeline-sub-sidebar-item ${isActive ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab(tab.id);
                                        setDetailedDealId(null);
                                        setDetailedContactId(null);
                                    }}
                                    title={isSidebarCollapsed ? tab.label : ''}
                                >
                                    <div className="pipeline-sub-sidebar-item-left">
                                        <Icon size={16} className="pipeline-sub-sidebar-icon" />
                                        {!isSidebarCollapsed && <span>{tab.label}</span>}
                                    </div>
                                    {!isSidebarCollapsed && tab.id === 'deals' && (
                                        <span className="pipeline-sub-sidebar-count">{activeDealsCount}</span>
                                    )}
                                    {!isSidebarCollapsed && tab.id === 'contacts' && (
                                        <span className="pipeline-sub-sidebar-count">{activeContactsCount}</span>
                                    )}
                                    {!isSidebarCollapsed && tab.id === 'companies' && (
                                        <span className="pipeline-sub-sidebar-count">{activeCompaniesCount}</span>
                                    )}
                                    {!isSidebarCollapsed && tab.id === 'tasks' && (
                                        <span className="pipeline-sub-sidebar-count">{activeTasksCount}</span>
                                    )}
                                    {!isSidebarCollapsed && tab.id === 'reminders' && (
                                        <span className="pipeline-sub-sidebar-count">{activeRemindersCount}</span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    {/* Admin Data View Mode Section at bottom of sub-sidebar */}
                    {isAdmin && !isSidebarCollapsed && (
                        <div className="pipeline-sidebar-data-view-card">
                            <div className="pipeline-sidebar-data-view-header">
                                <div className="pipeline-sidebar-data-view-label-wrap">
                                    <Calendar size={13} className="pipeline-sidebar-data-view-icon" />
                                    <span className="pipeline-sidebar-data-view-label">Data View Mode</span>
                                </div>
                                <span className={`pipeline-data-view-pill-badge ${adminDataViewMode}`}>
                                    {adminDataViewMode.toUpperCase()}
                                </span>
                            </div>

                            <div className="pipeline-data-view-segmented-control" role="group" aria-label="Data View Mode">
                                <button
                                    type="button"
                                    className={`pipeline-data-view-btn ${adminDataViewMode === 'all' ? 'active' : ''}`}
                                    onClick={() => handleSetAdminDataViewMode('all')}
                                    title="Show all historical and new pipeline data"
                                >
                                    All
                                </button>
                                <button
                                    type="button"
                                    className={`pipeline-data-view-btn ${adminDataViewMode === 'new' ? 'active' : ''}`}
                                    onClick={() => handleSetAdminDataViewMode('new')}
                                    title="Show only new data from 17 Oct 2026"
                                >
                                    New
                                </button>
                                <button
                                    type="button"
                                    className={`pipeline-data-view-btn ${adminDataViewMode === 'old' ? 'active' : ''}`}
                                    onClick={() => handleSetAdminDataViewMode('old')}
                                    title="Show only old data before 17 Oct 2026"
                                >
                                    Old
                                </button>
                            </div>

                            <div className="pipeline-data-view-status-caption">
                                {adminDataViewMode === 'new' && (
                                    <span className="pipeline-data-view-desc new">
                                        <span className="status-dot green"></span>
                                        From 17 OCT 2026
                                    </span>
                                )}
                                {adminDataViewMode === 'old' && (
                                    <span className="pipeline-data-view-desc old">
                                        <span className="status-dot amber"></span>
                                        Before 17 OCT 2026
                                    </span>
                                )}
                                {adminDataViewMode === 'all' && (
                                    <span className="pipeline-data-view-desc all">
                                        <span className="status-dot purple"></span>
                                        All Old + New Data
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Collapsed state quick toggle button */}
                    {isAdmin && isSidebarCollapsed && (
                        <div className="pipeline-sidebar-data-view-collapsed" title={`Data View Mode: ${adminDataViewMode.toUpperCase()} (Click to cycle All/New/Old)`}>
                            <button
                                type="button"
                                className={`pipeline-data-view-collapsed-btn ${adminDataViewMode}`}
                                onClick={() => {
                                    const nextMode = adminDataViewMode === 'new' ? 'old' : adminDataViewMode === 'old' ? 'all' : 'new';
                                    handleSetAdminDataViewMode(nextMode);
                                }}
                            >
                                <Calendar size={13} />
                                <span className="collapsed-mode-indicator">{adminDataViewMode[0].toUpperCase()}</span>
                            </button>
                        </div>
                    )}
                </aside>
            )}

            {/* ------------------------------------------------------------------
          2. Main Content Area
          ------------------------------------------------------------------ */}
            <main className="pipeline-main-content">
                {/* DEALS TAB (Pipeline Management Board or 3-Section Deal Activity Detail View) */}
                {activeTab === 'deals' && (
                    (detailedDealId && isLoading) ? (
                        renderDetailSkeleton()
                    ) : detailedDeal ? (
                        <div className="pipeline-detail-view-container">
                            {/* Top Header Bar */}
                            <div className="pipeline-detail-top-nav">
                                <div className="pipeline-detail-nav-left">
                                    <button
                                        type="button"
                                        className="pipeline-detail-back-btn"
                                        onClick={() => {
                                            setDetailedDealId(null);
                                            setDetailedContactId(null);
                                        }}
                                        title="Back to Pipeline Board"
                                    >
                                        <ChevronsLeft size={16} />
                                        <span>Back to Deals</span>
                                    </button>

                                    <div className="pipeline-detail-title-group">
                                        <div className="pipeline-card-avatar" style={{ width: '38px', height: '38px', fontSize: '15px' }}>
                                            {detailedDeal.clientName.charAt(0)}
                                        </div>
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <h2 className="pipeline-detail-deal-title">{detailedDeal.clientName}</h2>
                                                <span className={`pipeline-stage-badge ${detailedDeal.stage === 'New' ? 'new' : detailedDeal.stage === 'Hot' ? 'hot' : detailedDeal.stage === 'Won' ? 'won' : 'lost'}`}>
                                                    {detailedDeal.stage}
                                                </span>
                                            </div>
                                            <span style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '3px', flexWrap: 'wrap' }}>
                                                {dealDisplayLocation !== '-' && (
                                                    <><MapPin size={11} /> {dealDisplayLocation} • </>
                                                )}
                                                {(() => {
                                                    const dealCompany = getDealCompany(detailedDeal);
                                                    return dealCompany ? <><Building2 size={11} /> {dealCompany} • </> : null;
                                                })()}
                                                {detailedDeal.pipeline} • {detailedDeal.dealType || 'New Business'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="pipeline-detail-nav-right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <NotificationBell filterType="CRM" />
                                </div>
                            </div>

                            {/* 3-Section Split Body */}
                            <div className="pipeline-detail-split-body three-sections">
                                {/* 1. LEFT SECTION: Deal Details, Project Details & Payment Details */}
                                <div className="pipeline-detail-col-deal">
                                    <div className="pipeline-detail-card-panel">
                                        {/* Accordion 1: Deal Details */}
                                        <div className="pipeline-accordion-section">
                                            <div
                                                className="pipeline-accordion-header"
                                                onClick={() => setDealAccordionState(prev => ({ ...prev, deal: !prev.deal }))}
                                            >
                                                <div className="pipeline-accordion-title-wrap">
                                                    <span className="pipeline-accordion-title-icon">
                                                        <Tag size={12} strokeWidth={2.5} />
                                                    </span>
                                                    <span>Deal Details</span>
                                                </div>

                                                <div className="pipeline-accordion-actions" onClick={e => e.stopPropagation()}>
                                                    <div style={{ position: 'relative' }}>
                                                        <button
                                                            type="button"
                                                            className="pipeline-card-more-btn"
                                                            style={{ padding: '2px 4px', border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setIsDealDetailsMenuOpen(prev => !prev);
                                                            }}
                                                            title="Deal Options"
                                                        >
                                                            <MoreHorizontal size={16} />
                                                        </button>

                                                        {isDealDetailsMenuOpen && (
                                                            <div
                                                                className="pipeline-card-dropdown-menu"
                                                                style={{ top: 'calc(100% + 4px)', right: '0', minWidth: '150px', zIndex: 90 }}
                                                                onClick={e => e.stopPropagation()}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-card-menu-item"
                                                                    onClick={() => {
                                                                        setIsDealDetailsMenuOpen(false);
                                                                        handleOpenEditDeal(detailedDeal);
                                                                    }}
                                                                >
                                                                    <div className="pipeline-card-menu-item-left">
                                                                        <Edit2 size={13} style={{ color: '#64748b' }} />
                                                                        <span>Edit</span>
                                                                    </div>
                                                                </button>
                                                                <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-card-menu-item danger"
                                                                    onClick={() => {
                                                                        setIsDealDetailsMenuOpen(false);
                                                                        handleDeleteDeal(detailedDeal._id);
                                                                        setDetailedDealId(null);
                                                                    }}
                                                                >
                                                                    <div className="pipeline-card-menu-item-left">
                                                                        <Trash2 size={13} />
                                                                        <span>Delete</span>
                                                                    </div>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className={`pipeline-accordion-toggle-btn ${dealAccordionState.deal ? 'open' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDealAccordionState(prev => ({ ...prev, deal: !prev.deal }));
                                                        }}
                                                        title={dealAccordionState.deal ? 'Collapse Section' : 'Expand Section'}
                                                    >
                                                        <ChevronDown size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className={`pipeline-accordion-body ${dealAccordionState.deal ? '' : 'collapsed'}`}>
                                                <div className="pipeline-detail-kv-grid">
                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Deal Name</span>
                                                        <span className="pipeline-detail-v-val">
                                                            {(() => {
                                                                const raw = detailedDeal.dealName || detailedDeal.clientName || '';
                                                                const dt = (detailedDeal.dealType || 'New Business').trim();
                                                                const regex = new RegExp(`\\s*-\\s*${dt}$`, 'i');
                                                                if (regex.test(raw)) {
                                                                    return raw.replace(regex, '').trim();
                                                                }
                                                                return raw.replace(/\s*-\s*New Business$/i, '').trim();
                                                            })()}
                                                        </span>
                                                    </div>

                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Company</span>
                                                        <span className="pipeline-detail-v-val">
                                                            {getDealCompany(detailedDeal) || '—'}
                                                        </span>
                                                    </div>

                                                    {/* Interactive Stage (formerly Pipeline) Pill Badge with Dropdown */}
                                                    <div className="pipeline-detail-kv-row" style={{ position: 'relative' }}>
                                                        <span className="pipeline-detail-k-label">Stage</span>
                                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                                            <button
                                                                type="button"
                                                                className="pipeline-pill-badge-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setIsPipelineDropdownOpen(prev => !prev);
                                                                }}
                                                                title="Click to switch stage"
                                                            >
                                                                <span>{detailedDeal.pipeline || 'Sales Pipeline'}</span>
                                                                <ChevronDown size={11} />
                                                            </button>

                                                            {isPipelineDropdownOpen && (
                                                                <div
                                                                    className="pipeline-card-dropdown-menu"
                                                                    style={{ top: 'calc(100% + 4px)', right: 0, minWidth: '190px', zIndex: 120 }}
                                                                    onClick={e => e.stopPropagation()}
                                                                >
                                                                    <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                        Select Stage
                                                                    </div>
                                                                    {visiblePipelines.map(p => {
                                                                        const isSelected = (detailedDeal.pipeline || 'Sales Pipeline') === p.name;
                                                                        return (
                                                                            <button
                                                                                key={p.id}
                                                                                type="button"
                                                                                className={`pipeline-card-menu-item ${isSelected ? 'active' : ''}`}
                                                                                onClick={async () => {
                                                                                    const updateData = { pipeline: p.name, stage: p.name };
                                                                                    try {
                                                                                        if (detailedDeal._id && !detailedDeal._id.toString().startsWith('deal-sample-')) {
                                                                                            await api.put(`/deals/${detailedDeal._id}`, updateData);
                                                                                        }
                                                                                    } catch (err) {
                                                                                        console.warn('Update deal pipeline API error:', err.message);
                                                                                    }
                                                                                    setDeals(prev => prev.map(d => String(d._id) === String(detailedDeal._id) ? { ...d, ...updateData } : d));
                                                                                    logActivity(detailedDeal._id, {
                                                                                        type: 'deal',
                                                                                        title: 'Pipeline Stage Updated',
                                                                                        content: `Pipeline changed to "${p.name}".`
                                                                                    });
                                                                                    setIsPipelineDropdownOpen(false);
                                                                                }}
                                                                            >
                                                                                <div className="pipeline-card-menu-item-left">
                                                                                    <Tag size={13} style={{ color: isSelected ? '#0284c7' : '#64748b' }} />
                                                                                    <span style={{ fontWeight: isSelected ? 700 : 500 }}>{p.name}</span>
                                                                                </div>
                                                                                {isSelected && (
                                                                                    <CheckCircle size={13} style={{ color: '#0284c7', marginLeft: 'auto' }} />
                                                                                )}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Interactive Status (formerly Stage) Pill Badge with Dropdown */}
                                                    <div className="pipeline-detail-kv-row" style={{ position: 'relative' }}>
                                                        <span className="pipeline-detail-k-label">Status</span>
                                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                                            <button
                                                                type="button"
                                                                className={`pipeline-stage-pill-badge-btn ${detailedDeal.stage === 'New' ? 'new' : detailedDeal.stage === 'Hot' ? 'hot' : detailedDeal.stage === 'Won' ? 'won' : detailedDeal.stage === 'Lost' ? 'lost' : 'custom'}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setIsStageDropdownOpen(prev => !prev);
                                                                }}
                                                                title="Click to change status"
                                                            >
                                                                <span>{detailedDeal.stage}</span>
                                                                <ChevronDown size={11} />
                                                            </button>

                                                            {isStageDropdownOpen && (
                                                                <div
                                                                    className="pipeline-card-dropdown-menu"
                                                                    style={{ top: 'calc(100% + 4px)', right: 0, minWidth: '170px', zIndex: 120 }}
                                                                    onClick={e => e.stopPropagation()}
                                                                >
                                                                    <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                        Select Status
                                                                    </div>
                                                                    {visibleStages.map(s => {
                                                                        const isSelected = detailedDeal.stage === s.name;
                                                                        return (
                                                                            <button
                                                                                key={s.id}
                                                                                type="button"
                                                                                className={`pipeline-card-menu-item ${isSelected ? 'active' : ''}`}
                                                                                onClick={async () => {
                                                                                    const updateData = { stage: s.name, pipeline: s.name };
                                                                                    try {
                                                                                        if (detailedDeal._id && !detailedDeal._id.toString().startsWith('deal-sample-')) {
                                                                                            await api.put(`/deals/${detailedDeal._id}`, updateData);
                                                                                        }
                                                                                    } catch (err) {
                                                                                        console.warn('Update deal stage API error:', err.message);
                                                                                    }
                                                                                    setDeals(prev => prev.map(d => String(d._id) === String(detailedDeal._id) ? { ...d, ...updateData } : d));
                                                                                    logActivity(detailedDeal._id, {
                                                                                        type: 'deal',
                                                                                        title: 'Status Updated',
                                                                                        content: `Status updated to "${s.name}".`
                                                                                    });
                                                                                    setIsStageDropdownOpen(false);
                                                                                }}
                                                                            >
                                                                                <div className="pipeline-card-menu-item-left">
                                                                                    <span className={`pipeline-stage-badge ${s.badgeClass || 'new'}`} style={{ fontSize: '10.5px', padding: '1px 6px' }}>
                                                                                        {s.name}
                                                                                    </span>
                                                                                </div>
                                                                                {isSelected && (
                                                                                    <CheckCircle size={13} style={{ color: '#0284c7', marginLeft: 'auto' }} />
                                                                                )}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Deal Value</span>
                                                        <span className="pipeline-detail-v-val" style={{ fontWeight: 700, color: '#0284c7' }}>
                                                            {detailedDeal.currency || '$'} {Number(detailedDeal.dealValue).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Deal Type</span>
                                                        <span className="pipeline-detail-v-val">{detailedDeal.dealType || 'New Business'}</span>
                                                    </div>
                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Expected Close Date</span>
                                                        <span className="pipeline-detail-v-val">{formatDateDDMMYYYY(detailedDeal.expectedCloseDate)}</span>
                                                    </div>
                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Location / Territory</span>
                                                        <span className="pipeline-detail-v-val">{dealDisplayLocation}</span>
                                                    </div>

                                                    {/* Lead Requirement field with View button just before Deal Owner */}
                                                    <div className="pipeline-detail-kv-row" style={{ alignItems: 'center' }}>
                                                        <span className="pipeline-detail-k-label">Requirement</span>
                                                        <div className="pipeline-detail-v-val" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <button
                                                                type="button"
                                                                className="pipeline-owner-pill-badge-btn"
                                                                onClick={() => {
                                                                    setIsEditingRequirement(false);
                                                                    setShowDealRequirementModal(true);
                                                                }}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    padding: '2.5px 9px',
                                                                    borderRadius: '999px',
                                                                    background: '#e0f2fe',
                                                                    border: '1px solid #bae6fd',
                                                                    color: '#0284c7',
                                                                    fontSize: '11px',
                                                                    fontWeight: 600,
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.15s ease'
                                                                }}
                                                                title="Click to view full requirement details"
                                                            >
                                                                <Eye size={12} style={{ color: '#0284c7' }} />
                                                                <span>View</span>
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Interactive Dealer Name Pill Badge with Hover / Click Popover */}
                                                    <div className="pipeline-detail-kv-row" style={{ position: 'relative' }}>
                                                        <span className="pipeline-detail-k-label">Deal Owner</span>
                                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                                            <button
                                                                type="button"
                                                                className={`pipeline-owner-pill-badge-btn ${isDealerDropdownOpen ? 'active' : ''}`}
                                                                onMouseEnter={(e) => {
                                                                    if (isDealerDropdownOpen) return;
                                                                    if (dealerPopoverTimeoutRef.current) clearTimeout(dealerPopoverTimeoutRef.current);
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    setDealerPopoverCoords({
                                                                        top: Math.max(12, rect.top - 12),
                                                                        left: rect.right + 12
                                                                    });
                                                                    setIsDealerPopoverOpen(true);
                                                                }}
                                                                onMouseLeave={() => {
                                                                    dealerPopoverTimeoutRef.current = setTimeout(() => {
                                                                        setIsDealerPopoverOpen(false);
                                                                    }, 180);
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (dealerPopoverTimeoutRef.current) clearTimeout(dealerPopoverTimeoutRef.current);
                                                                    setIsDealerPopoverOpen(false);
                                                                    setIsDealerDropdownOpen(prev => !prev);
                                                                    setDealerSearchQuery('');
                                                                }}
                                                            >
                                                                <div className="pipeline-owner-pill-avatar">
                                                                    {(detailedDeal.salesOwner || 'Akhil Verma').charAt(0)}
                                                                </div>
                                                                <span>{detailedDeal.salesOwner || 'Akhil Verma'}</span>
                                                                <ChevronDown size={10} style={{ opacity: 0.6, transform: isDealerDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
                                                            </button>

                                                            {isDealerPopoverOpen && (() => {
                                                                const ownerEmp = matchedOwnerEmployee;
                                                                const ownerFullName = detailedDeal.salesOwner || (ownerEmp ? `${ownerEmp.firstName || ''} ${ownerEmp.lastName || ''}`.trim() : '') || 'Deal Specialist';
                                                                const ownerDesignation = ownerEmp?.designation || (typeof ownerEmp?.role === 'string' ? ownerEmp?.role : ownerEmp?.role?.name) || 'Senior Deal Specialist';
                                                                const isOwnerActive = ownerEmp ? (ownerEmp.isActive !== false && ownerEmp.status !== 'Inactive') : true;
                                                                const ownerEmail = ownerEmp?.officialEmail || ownerEmp?.email || (detailedDeal.salesOwner ? `${detailedDeal.salesOwner.toLowerCase().replace(/\s+/g, '.')}@bhoomi.com` : '—');
                                                                const ownerPhone = ownerEmp?.phone || ownerEmp?.mobile || ownerEmp?.phoneNumber || ownerEmp?.alternatePhone || '—';
                                                                const ownerDepartment = ownerEmp?.department || ownerEmp?.dept || 'Sales & Operations';
                                                                const rawAssignedDate = detailedDeal.assignedDate || detailedDeal.createdAt || detailedDeal.createdDate || detailedDeal.date;
                                                                const ownerAssignedDate = formatISTDateTime(rawAssignedDate || new Date());

                                                                return (
                                                                    <div
                                                                        className="pipeline-dealer-hover-popover outside-popover"
                                                                        style={{
                                                                            position: 'fixed',
                                                                            top: `${dealerPopoverCoords.top}px`,
                                                                            left: `${dealerPopoverCoords.left}px`,
                                                                            zIndex: 99999
                                                                        }}
                                                                        onMouseEnter={() => {
                                                                            if (dealerPopoverTimeoutRef.current) clearTimeout(dealerPopoverTimeoutRef.current);
                                                                            setIsDealerPopoverOpen(true);
                                                                        }}
                                                                        onMouseLeave={() => {
                                                                            dealerPopoverTimeoutRef.current = setTimeout(() => {
                                                                                setIsDealerPopoverOpen(false);
                                                                            }, 180);
                                                                        }}
                                                                        onClick={e => e.stopPropagation()}
                                                                    >
                                                                        <div className="pipeline-dealer-popover-header">
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                                                                                <div className="pipeline-detail-clean-svg-icon contact" style={{ width: '32px', height: '32px', minWidth: '32px' }}>
                                                                                    <User size={16} strokeWidth={2} />
                                                                                </div>
                                                                                <div style={{ overflow: 'hidden', minWidth: 0 }}>
                                                                                    <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                                                        {ownerFullName}
                                                                                    </h4>
                                                                                    <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '2px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                                                        {ownerDesignation}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                            <span
                                                                                className={`pipeline-stage-badge ${isOwnerActive ? 'won' : 'lost'}`}
                                                                                style={{
                                                                                    marginLeft: 'auto',
                                                                                    fontSize: '10px',
                                                                                    padding: '2px 8px',
                                                                                    lineHeight: '14px',
                                                                                    height: '18px',
                                                                                    display: 'inline-flex',
                                                                                    alignItems: 'center',
                                                                                    borderRadius: '10px',
                                                                                    fontWeight: 600,
                                                                                    flexShrink: 0
                                                                                }}
                                                                            >
                                                                                {isOwnerActive ? 'Active' : 'Inactive'}
                                                                            </span>
                                                                        </div>

                                                                        <div className="pipeline-dealer-popover-body">
                                                                            <div className="pipeline-dealer-popover-row">
                                                                                <div
                                                                                    className="pipeline-dealer-popover-icon-box"
                                                                                    data-tooltip="Email"
                                                                                    title="Email"
                                                                                >
                                                                                    <Mail size={13} style={{ color: '#0284c7', flexShrink: 0 }} />
                                                                                </div>
                                                                                {ownerEmail && ownerEmail !== '—' && ownerEmail !== '-' ? (
                                                                                    <a
                                                                                        href={`mailto:${ownerEmail}`}
                                                                                        style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', color: '#0284c7' }}
                                                                                        title={ownerEmail}
                                                                                    >
                                                                                        {ownerEmail}
                                                                                    </a>
                                                                                ) : (
                                                                                    <span style={{ color: '#64748b' }}>—</span>
                                                                                )}
                                                                            </div>

                                                                            <div className="pipeline-dealer-popover-row">
                                                                                <div
                                                                                    className="pipeline-dealer-popover-icon-box"
                                                                                    data-tooltip="Phone"
                                                                                    title="Phone"
                                                                                >
                                                                                    <Phone size={13} style={{ color: '#16a34a', flexShrink: 0 }} />
                                                                                </div>
                                                                                {ownerPhone && ownerPhone !== '—' && ownerPhone !== '-' ? (
                                                                                    <a
                                                                                        href={`tel:${ownerPhone.replace(/\s+/g, '')}`}
                                                                                        style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', color: '#16a34a' }}
                                                                                        title={ownerPhone}
                                                                                    >
                                                                                        {ownerPhone}
                                                                                    </a>
                                                                                ) : (
                                                                                    <span style={{ color: '#64748b' }}>—</span>
                                                                                )}
                                                                            </div>

                                                                            <div className="pipeline-dealer-popover-row">
                                                                                <div
                                                                                    className="pipeline-dealer-popover-icon-box"
                                                                                    data-tooltip="Department"
                                                                                    title="Department"
                                                                                >
                                                                                    <Briefcase size={13} style={{ color: '#6366f1', flexShrink: 0 }} />
                                                                                </div>
                                                                                <span style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', color: '#334155' }}>
                                                                                    {ownerDepartment}
                                                                                </span>
                                                                            </div>

                                                                            <div className="pipeline-dealer-popover-row">
                                                                                <div
                                                                                    className="pipeline-dealer-popover-icon-box"
                                                                                    data-tooltip="Assigned On"
                                                                                    title="Assigned On"
                                                                                >
                                                                                    <Calendar size={13} style={{ color: '#f59e0b', flexShrink: 0 }} />
                                                                                </div>
                                                                                <span style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', color: '#334155' }}>
                                                                                    {ownerAssignedDate}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}

                                                            {isDealerDropdownOpen && (
                                                                <div
                                                                    className="pipeline-card-dropdown-menu"
                                                                    style={{
                                                                        top: 'calc(100% + 4px)',
                                                                        right: 0,
                                                                        minWidth: '200px',
                                                                        maxWidth: '225px',
                                                                        zIndex: 120
                                                                    }}
                                                                    onClick={e => e.stopPropagation()}
                                                                    onWheel={e => e.stopPropagation()}
                                                                >
                                                                    <div style={{ padding: '6px 12px 6px 12px', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                        Select Deal Owner
                                                                    </div>

                                                                    {availableAssignEmployees.length > 5 && (
                                                                        <div style={{ padding: '0 8px 6px 8px' }} onClick={e => e.stopPropagation()}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px 8px' }}>
                                                                                <Search size={12} style={{ color: '#94a3b8', flexShrink: 0 }} />
                                                                                <input
                                                                                    type="text"
                                                                                    placeholder="Search employee..."
                                                                                    value={dealerSearchQuery}
                                                                                    onChange={e => setDealerSearchQuery(e.target.value)}
                                                                                    autoFocus
                                                                                    style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '11.5px', width: '100%', color: '#1e293b' }}
                                                                                />
                                                                                {dealerSearchQuery && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => setDealerSearchQuery('')}
                                                                                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, display: 'flex', color: '#94a3b8' }}
                                                                                    >
                                                                                        <X size={11} />
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    <div style={{ maxHeight: '210px', overflowY: 'auto' }} onWheel={e => e.stopPropagation()}>
                                                                        {availableAssignEmployees.length === 0 ? (
                                                                            <div style={{ padding: '12px 10px', textAlign: 'center', fontSize: '12px', color: '#94a3b8' }}>
                                                                                No matching employees
                                                                            </div>
                                                                        ) : (
                                                                            availableAssignEmployees.map(emp => {
                                                                                const empFullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.name || 'Employee';
                                                                                const isSelected = (detailedDeal.salesOwner || '').toLowerCase() === empFullName.toLowerCase() ||
                                                                                    (detailedDeal.assignedTo && typeof detailedDeal.assignedTo === 'object' && detailedDeal.assignedTo._id && detailedDeal.assignedTo._id.toString() === emp._id?.toString()) ||
                                                                                    (detailedDeal.assignedTo && detailedDeal.assignedTo.toString() === emp._id?.toString());

                                                                                return (
                                                                                    <button
                                                                                        key={emp._id || empFullName}
                                                                                        type="button"
                                                                                        className={`pipeline-card-menu-item ${isSelected ? 'active' : ''}`}
                                                                                        onClick={async () => {
                                                                                            try {
                                                                                                if (detailedDeal._id && !detailedDeal._id.toString().startsWith('deal-sample-') && !detailedDeal._id.toString().startsWith('deal-')) {
                                                                                                    await api.put(`/deals/${detailedDeal._id}`, {
                                                                                                        salesOwner: empFullName,
                                                                                                        assignedTo: emp._id || null
                                                                                                    });
                                                                                                }
                                                                                            } catch (err) {
                                                                                                console.warn('Assign deal owner API error:', err.message);
                                                                                            }

                                                                                            // Update deals state
                                                                                            setDeals(prev => prev.map(d => {
                                                                                                if (d._id === detailedDeal._id) {
                                                                                                    return {
                                                                                                        ...d,
                                                                                                        salesOwner: empFullName,
                                                                                                        assignedTo: emp
                                                                                                    };
                                                                                                }
                                                                                                return d;
                                                                                            }));

                                                                                            const actorName = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || user?.email || 'Admin System';
                                                                                            const prevOwner = (detailedDeal.salesOwner || '').trim();
                                                                                            const activityContent = prevOwner && prevOwner.toLowerCase() !== empFullName.toLowerCase()
                                                                                                ? `${actorName} changed deal owner from ${prevOwner} to ${empFullName}.`
                                                                                                : `${actorName} assigned the deal to ${empFullName}.`;

                                                                                            logActivity(detailedDeal._id, {
                                                                                                type: 'deal',
                                                                                                title: 'Deal Owner Assigned',
                                                                                                content: activityContent,
                                                                                                author: actorName
                                                                                            });

                                                                                            setIsDealerDropdownOpen(false);
                                                                                            setDealerSearchQuery('');
                                                                                        }}
                                                                                    >
                                                                                        <div className="pipeline-card-menu-item-left">
                                                                                            <div
                                                                                                style={{
                                                                                                    width: '20px',
                                                                                                    height: '20px',
                                                                                                    fontSize: '10px',
                                                                                                    fontWeight: 700,
                                                                                                    background: isSelected ? '#0284c7' : '#e2e8f0',
                                                                                                    color: isSelected ? '#ffffff' : '#475569',
                                                                                                    borderRadius: '50%',
                                                                                                    display: 'inline-flex',
                                                                                                    alignItems: 'center',
                                                                                                    justifyContent: 'center',
                                                                                                    flexShrink: 0
                                                                                                }}
                                                                                            >
                                                                                                {empFullName.charAt(0).toUpperCase()}
                                                                                            </div>
                                                                                            <span style={{ fontWeight: isSelected ? 700 : 500, fontSize: '12.5px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: '140px' }}>
                                                                                                {empFullName}
                                                                                            </span>
                                                                                        </div>
                                                                                        {isSelected && (
                                                                                            <CheckCircle size={13} style={{ color: '#0284c7', marginLeft: 'auto', flexShrink: 0 }} />
                                                                                        )}
                                                                                    </button>
                                                                                );
                                                                            })
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="pipeline-detail-kv-row" style={{ position: 'relative' }}>
                                                        <span className="pipeline-detail-k-label">Added By</span>
                                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                                            {(() => {
                                                                const addedByName = detailedDeal.addedBy || (detailedDeal.createdBy?.firstName ? `${detailedDeal.createdBy.firstName} ${detailedDeal.createdBy.lastName || ''}`.trim() : '') || detailedDeal.salesOwner || 'Admin System';
                                                                const emp = getAddedByEmployee(addedByName);
                                                                const empFullName = (emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '') || emp?.name || addedByName;
                                                                const empDesignation = emp?.designation || (typeof emp?.role === 'string' ? emp?.role : emp?.role?.name) || (addedByName === 'Admin System' ? 'System Administrator' : 'Deal Specialist');
                                                                const isEmpActive = emp ? (emp.isActive !== false && emp.status !== 'Inactive') : true;
                                                                const empEmail = emp?.officialEmail || emp?.email || (addedByName === 'Admin System' ? 'admin@bhoomi.com' : `${addedByName.toLowerCase().replace(/\s+/g, '.')}@bhoomi.com`);
                                                                const empPhone = emp?.phone || emp?.mobile || emp?.phoneNumber || emp?.alternatePhone || (addedByName === 'Admin System' ? '+91 98765 43210' : '—');
                                                                const empDepartment = emp?.department || emp?.dept || (addedByName === 'Admin System' ? 'Administration & Operations' : 'Sales & Operations');
                                                                const empAddedDate = formatISTDateTime(detailedDeal.createdAt || detailedDeal.createdDate || detailedDeal.date || new Date());

                                                                return (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-owner-pill-badge-btn"
                                                                            onMouseEnter={(e) => {
                                                                                if (dealAddedByPopoverTimeoutRef.current) clearTimeout(dealAddedByPopoverTimeoutRef.current);
                                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                                setDealAddedByPopoverCoords({
                                                                                    top: Math.max(12, rect.top - 12),
                                                                                    left: rect.right + 12
                                                                                });
                                                                                setIsDealAddedByPopoverOpen(true);
                                                                            }}
                                                                            onMouseLeave={() => {
                                                                                dealAddedByPopoverTimeoutRef.current = setTimeout(() => {
                                                                                    setIsDealAddedByPopoverOpen(false);
                                                                                }, 180);
                                                                            }}
                                                                        >
                                                                            <div className="pipeline-owner-pill-avatar">
                                                                                {empFullName.charAt(0).toUpperCase()}
                                                                            </div>
                                                                            <span>{empFullName}</span>
                                                                        </button>

                                                                        {isDealAddedByPopoverOpen && (
                                                                            <div
                                                                                className="pipeline-dealer-hover-popover outside-popover"
                                                                                style={{
                                                                                    position: 'fixed',
                                                                                    top: `${dealAddedByPopoverCoords.top}px`,
                                                                                    left: `${dealAddedByPopoverCoords.left}px`,
                                                                                    zIndex: 99999
                                                                                }}
                                                                                onMouseEnter={() => {
                                                                                    if (dealAddedByPopoverTimeoutRef.current) clearTimeout(dealAddedByPopoverTimeoutRef.current);
                                                                                    setIsDealAddedByPopoverOpen(true);
                                                                                }}
                                                                                onMouseLeave={() => {
                                                                                    dealAddedByPopoverTimeoutRef.current = setTimeout(() => {
                                                                                        setIsDealAddedByPopoverOpen(false);
                                                                                    }, 180);
                                                                                }}
                                                                                onClick={e => e.stopPropagation()}
                                                                            >
                                                                                <div className="pipeline-dealer-popover-header">
                                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                                                                                        <div className="pipeline-detail-clean-svg-icon contact" style={{ width: '32px', height: '32px', minWidth: '32px' }}>
                                                                                            <User size={16} strokeWidth={2} />
                                                                                        </div>
                                                                                        <div style={{ overflow: 'hidden', minWidth: 0 }}>
                                                                                            <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                                                                {empFullName}
                                                                                            </h4>
                                                                                            <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '2px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                                                                {empDesignation}
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                    <span
                                                                                        className={`pipeline-stage-badge ${isEmpActive ? 'won' : 'lost'}`}
                                                                                        style={{
                                                                                            marginLeft: 'auto',
                                                                                            fontSize: '10px',
                                                                                            padding: '2px 8px',
                                                                                            lineHeight: '14px',
                                                                                            height: '18px',
                                                                                            display: 'inline-flex',
                                                                                            alignItems: 'center',
                                                                                            borderRadius: '10px',
                                                                                            fontWeight: 600,
                                                                                            flexShrink: 0
                                                                                        }}
                                                                                    >
                                                                                        {isEmpActive ? 'Active' : 'Inactive'}
                                                                                    </span>
                                                                                </div>

                                                                                <div className="pipeline-dealer-popover-body">
                                                                                    <div className="pipeline-dealer-popover-row">
                                                                                        <div
                                                                                            className="pipeline-dealer-popover-icon-box"
                                                                                            data-tooltip="Email"
                                                                                            title="Email"
                                                                                        >
                                                                                            <Mail size={13} style={{ color: '#0284c7', flexShrink: 0 }} />
                                                                                        </div>
                                                                                        {empEmail && empEmail !== '—' && empEmail !== '-' ? (
                                                                                            <a
                                                                                                href={`mailto:${empEmail}`}
                                                                                                style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', color: '#0284c7' }}
                                                                                                title={empEmail}
                                                                                            >
                                                                                                {empEmail}
                                                                                            </a>
                                                                                        ) : (
                                                                                            <span style={{ color: '#64748b' }}>—</span>
                                                                                        )}
                                                                                    </div>

                                                                                    <div className="pipeline-dealer-popover-row">
                                                                                        <div
                                                                                            className="pipeline-dealer-popover-icon-box"
                                                                                            data-tooltip="Phone"
                                                                                            title="Phone"
                                                                                        >
                                                                                            <Phone size={13} style={{ color: '#16a34a', flexShrink: 0 }} />
                                                                                        </div>
                                                                                        {empPhone && empPhone !== '—' && empPhone !== '-' ? (
                                                                                            <a
                                                                                                href={`tel:${empPhone.replace(/\s+/g, '')}`}
                                                                                                style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', color: '#16a34a' }}
                                                                                                title={empPhone}
                                                                                            >
                                                                                                {empPhone}
                                                                                            </a>
                                                                                        ) : (
                                                                                            <span style={{ color: '#64748b' }}>—</span>
                                                                                        )}
                                                                                    </div>

                                                                                    <div className="pipeline-dealer-popover-row">
                                                                                        <div
                                                                                            className="pipeline-dealer-popover-icon-box"
                                                                                            data-tooltip="Department"
                                                                                            title="Department"
                                                                                        >
                                                                                            <Briefcase size={13} style={{ color: '#6366f1', flexShrink: 0 }} />
                                                                                        </div>
                                                                                        <span style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', color: '#334155' }}>
                                                                                            {empDepartment}
                                                                                        </span>
                                                                                    </div>

                                                                                    <div className="pipeline-dealer-popover-row">
                                                                                        <div
                                                                                            className="pipeline-dealer-popover-icon-box"
                                                                                            data-tooltip="Added On"
                                                                                            title="Added On"
                                                                                        >
                                                                                            <Calendar size={13} style={{ color: '#f59e0b', flexShrink: 0 }} />
                                                                                        </div>
                                                                                        <span style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', color: '#334155' }}>
                                                                                            {empAddedDate}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>

                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Created On</span>
                                                        <span className="pipeline-detail-v-val">
                                                            {formatISTDateTime(detailedDeal.createdAt || detailedDeal.createdDate || detailedDeal.date)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Accordion 2: Project Details */}
                                        <div className="pipeline-accordion-section">
                                            <div
                                                className="pipeline-accordion-header"
                                                onClick={() => setDealAccordionState(prev => ({ ...prev, project: !prev.project }))}
                                            >
                                                <div className="pipeline-accordion-title-wrap">
                                                    <span className="pipeline-accordion-title-icon" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                                                        <Briefcase size={12} strokeWidth={2.5} />
                                                    </span>
                                                    <span>Project Details</span>
                                                </div>

                                                <div className="pipeline-accordion-actions">
                                                    <button
                                                        type="button"
                                                        className={`pipeline-accordion-toggle-btn ${dealAccordionState.project ? 'open' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDealAccordionState(prev => ({ ...prev, project: !prev.project }));
                                                        }}
                                                        title={dealAccordionState.project ? 'Collapse Section' : 'Expand Section'}
                                                    >
                                                        <ChevronDown size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className={`pipeline-accordion-body ${dealAccordionState.project ? '' : 'collapsed'}`}>
                                                {detailedDeal.projectId || detailedDeal.projectName ? (
                                                    <div className="pipeline-detail-kv-grid">
                                                        {detailedDeal.projectId && (
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Project ID</span>
                                                                <span className="pipeline-detail-v-val" style={{ color: '#0284c7', fontFamily: 'monospace', fontWeight: 700 }}>
                                                                    {detailedDeal.projectId}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="pipeline-detail-kv-row">
                                                            <span className="pipeline-detail-k-label">Project Name</span>
                                                            <span className="pipeline-detail-v-val font-semibold">{detailedDeal.projectName}</span>
                                                        </div>
                                                        {detailedDeal.projectType && (
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Project Type</span>
                                                                <span className="pipeline-detail-v-val">{detailedDeal.projectType}</span>
                                                            </div>
                                                        )}
                                                        {detailedDeal.projectCost && (
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Project Cost</span>
                                                                <span className="pipeline-detail-v-val" style={{ fontWeight: 700, color: '#0284c7' }}>
                                                                    {detailedDeal.projectCost}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {detailedDeal.projectTimeline && (
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Timeline</span>
                                                                <span className="pipeline-detail-v-val">{detailedDeal.projectTimeline}</span>
                                                            </div>
                                                        )}
                                                        {detailedDeal.projectStatus && (
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Status</span>
                                                                <span className="pipeline-detail-v-val">{detailedDeal.projectStatus}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div style={{ padding: '24px 16px', textAlign: 'center', color: '#94a3b8' }}>
                                                        <Briefcase size={28} style={{ margin: '0 auto 8px', opacity: 0.45 }} />
                                                        <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#64748b' }}>No Associated Project</p>
                                                        <p style={{ margin: '4px 0 0', fontSize: '11.5px', color: '#94a3b8' }}>There is no active project linked to this deal yet.</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Accordion 3: Payment Details */}
                                        <div className="pipeline-accordion-section">
                                            <div
                                                className="pipeline-accordion-header"
                                                onClick={() => setDealAccordionState(prev => ({ ...prev, payment: !prev.payment }))}
                                            >
                                                <div className="pipeline-accordion-title-wrap">
                                                    <span className="pipeline-accordion-title-icon" style={{ background: '#ecfdf5', color: '#16a34a' }}>
                                                        <CreditCard size={12} strokeWidth={2.5} />
                                                    </span>
                                                    <span>Payment Details</span>
                                                </div>

                                                <div className="pipeline-accordion-actions">
                                                    <button
                                                        type="button"
                                                        className={`pipeline-accordion-toggle-btn ${dealAccordionState.payment ? 'open' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDealAccordionState(prev => ({ ...prev, payment: !prev.payment }));
                                                        }}
                                                        title={dealAccordionState.payment ? 'Collapse Section' : 'Expand Section'}
                                                    >
                                                        <ChevronDown size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className={`pipeline-accordion-body ${dealAccordionState.payment ? '' : 'collapsed'}`}>
                                                {(() => {
                                                    const dealRecords = getDealPaymentRecords(detailedDeal);
                                                    const hasRecords = dealRecords.length > 0;
                                                    const dealVal = parsePaymentAmount(detailedDeal.dealValue);
                                                    const paidVal = dealRecords
                                                        .filter(r => (r.paymentStatus || '').toLowerCase() === 'paid')
                                                        .reduce((sum, r) => sum + parsePaymentAmount(r.paidAmount), 0);
                                                    const pendingRecordsAmount = dealRecords
                                                        .filter(r => (r.paymentStatus || '').toLowerCase() !== 'paid')
                                                        .reduce((sum, r) => sum + parsePaymentAmount(r.paidAmount), 0);

                                                    const totalVal = dealVal > 0 ? dealVal : (paidVal + pendingRecordsAmount);
                                                    const pendingVal = dealVal > 0 ? Math.max(0, dealVal - paidVal) : pendingRecordsAmount;
                                                    const paidPct = totalVal > 0 ? Math.min(100, Math.round((paidVal / totalVal) * 100)) : 0;
                                                    const currency = detailedDeal.currency || '₹';

                                                    if (!hasRecords && totalVal === 0) {
                                                        return (
                                                            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#94a3b8' }}>
                                                                <CreditCard size={28} style={{ margin: '0 auto 8px', opacity: 0.45 }} />
                                                                <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#64748b' }}>No Payment Records</p>
                                                                <p style={{ margin: '4px 0 0', fontSize: '11.5px', color: '#94a3b8' }}>No payment records found for this deal.</p>
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-milestone-pill-badge-btn"
                                                                    style={{ margin: '12px auto 0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setMilestoneModalDealId(detailedDeal._id);
                                                                        setShowMilestoneModal(true);
                                                                        setShowAddPaymentForm(true);
                                                                    }}
                                                                >
                                                                    <Plus size={12} />
                                                                    <span>Add Payment</span>
                                                                </button>
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div className="pipeline-detail-kv-grid">
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Total Amount</span>
                                                                <span className="pipeline-detail-v-val" style={{ fontWeight: 700, color: '#0284c7' }}>
                                                                    {currency} {totalVal.toLocaleString()}
                                                                </span>
                                                            </div>
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Paid Amount</span>
                                                                <span className="pipeline-detail-v-val" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                                    <span style={{ fontWeight: 700, color: '#16a34a' }}>
                                                                        {currency} {paidVal.toLocaleString()}
                                                                    </span>
                                                                    {totalVal > 0 && (
                                                                        <span className="pipeline-stage-badge won" style={{ fontSize: '10px', padding: '1px 6px' }}>
                                                                            {paidPct}% Paid
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Pending Amount</span>
                                                                <span className="pipeline-detail-v-val" style={{ fontWeight: 700, color: '#ea580c' }}>
                                                                    {currency} {pendingVal.toLocaleString()}
                                                                </span>
                                                            </div>

                                                            <div className="pipeline-detail-kv-row" style={{ position: 'relative' }}>
                                                                <span className="pipeline-detail-k-label">Payment Terms</span>
                                                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                                                    <button
                                                                        type="button"
                                                                        className="pipeline-milestone-pill-badge-btn"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setMilestoneModalDealId(detailedDeal._id);
                                                                            setShowMilestoneModal(true);
                                                                            setShowAddPaymentForm(false);
                                                                        }}
                                                                        title="Click to view full Payment Records"
                                                                    >
                                                                        <span>Milestone ({dealRecords.length})</span>
                                                                        <ChevronRight size={11} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 2. MIDDLE SECTION: Activities, Notes, Emails, Messages & Calls */}
                                <div className="pipeline-detail-col-activities">
                                    <div className="pipeline-detail-actions-card">
                                        {/* Activity Tabs */}
                                        <div className="pipeline-activity-tabs-bar">
                                            <div className="pipeline-activity-tab-btns">
                                                <button
                                                    type="button"
                                                    className={`pipeline-act-tab-btn ${detailActiveTab === 'all' ? 'active' : ''}`}
                                                    onClick={() => setDetailActiveTab('all')}
                                                >
                                                    <span>All Activities</span>
                                                    <span className="pipeline-act-tab-badge">{currentDealActivities.length}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`pipeline-act-tab-btn ${detailActiveTab === 'notes' ? 'active' : ''}`}
                                                    onClick={() => setDetailActiveTab('notes')}
                                                >
                                                    <FileText size={14} />
                                                    <span>Notes</span>
                                                    <span className="pipeline-act-tab-badge">{currentDealActivities.filter(a => a.type === 'note').length}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`pipeline-act-tab-btn ${detailActiveTab === 'followups' ? 'active' : ''}`}
                                                    onClick={() => setDetailActiveTab('followups')}
                                                >
                                                    <Calendar size={14} />
                                                    <span>Follow-ups</span>
                                                    <span className="pipeline-act-tab-badge">{currentDealActivities.filter(a => a.type === 'followup').length}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`pipeline-act-tab-btn ${detailActiveTab === 'emails' ? 'active' : ''}`}
                                                    onClick={() => setDetailActiveTab('emails')}
                                                >
                                                    <Mail size={14} />
                                                    <span>Emails</span>
                                                    <span className="pipeline-act-tab-badge">{currentDealActivities.filter(a => a.type === 'email').length}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`pipeline-act-tab-btn ${detailActiveTab === 'tasks' ? 'active' : ''}`}
                                                    onClick={() => setDetailActiveTab('tasks')}
                                                >
                                                    <CheckSquare size={14} />
                                                    <span>Tasks</span>
                                                    <span className="pipeline-act-tab-badge">{dealSpecificTasks.length}</span>
                                                </button>
                                            </div>

                                            {/* Contextual Action Button on Right Side */}
                                            <div className="pipeline-activity-quick-actions">
                                                {detailActiveTab === 'notes' && (
                                                    <button
                                                        type="button"
                                                        className="pipeline-btn-add-activity-tab"
                                                        onClick={() => {
                                                            setNewActivityType('note');
                                                            setIsComposingActivity(true);
                                                        }}
                                                    >
                                                        <Plus size={13} />
                                                        <span>Add Note</span>
                                                    </button>
                                                )}
                                                {detailActiveTab === 'followups' && (
                                                    <button
                                                        type="button"
                                                        className="pipeline-btn-add-activity-tab"
                                                        onClick={() => {
                                                            setIsComposingFollowUp(prev => !prev);
                                                        }}
                                                    >
                                                        <Plus size={13} />
                                                        <span>Add Follow-up</span>
                                                    </button>
                                                )}
                                                {detailActiveTab === 'emails' && (
                                                    <button
                                                        type="button"
                                                        className="pipeline-btn-add-activity-tab"
                                                        disabled
                                                        style={{ opacity: 0.55, cursor: 'not-allowed' }}
                                                        title="Feature not implemented yet"
                                                    >
                                                        <Plus size={13} />
                                                        <span>Log Email</span>
                                                    </button>
                                                )}
                                                {detailActiveTab === 'tasks' && (
                                                    <button
                                                        type="button"
                                                        className="pipeline-btn-add-activity-tab"
                                                        onClick={() => {
                                                            handleOpenCreateTask(detailedDeal._id, '');
                                                        }}
                                                    >
                                                        <Plus size={13} />
                                                        <span>Add Task</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Inline Activity Composer */}
                                        {isComposingActivity && (
                                            (detailActiveTab === 'notes' && newActivityType === 'note') ||
                                            (detailActiveTab === 'emails' && newActivityType === 'email')
                                        ) && (
                                                <form onSubmit={handleAddActivity} className="pipeline-activity-composer">
                                                    <div className="pipeline-activity-composer-header">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            {newActivityType === 'note' && (
                                                                <>
                                                                    <FileText size={16} style={{ color: '#0284c7' }} />
                                                                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '13.5px' }}>Add Internal Note</span>
                                                                </>
                                                            )}
                                                            {newActivityType === 'email' && (
                                                                <>
                                                                    <Mail size={16} style={{ color: '#0284c7' }} />
                                                                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '13.5px' }}>Log Email</span>
                                                                </>
                                                            )}
                                                            {newActivityType === 'message' && (
                                                                <>
                                                                    <MessageSquare size={16} style={{ color: '#0284c7' }} />
                                                                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '13.5px' }}>Log Message</span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {newActivityType === 'email' && (
                                                        <input
                                                            type="text"
                                                            className="pipeline-form-input"
                                                            style={{ padding: '8px 12px', fontSize: '13px' }}
                                                            placeholder="Subject line (e.g. Contract review follow-up)"
                                                            value={newActivitySubject}
                                                            onChange={e => setNewActivitySubject(e.target.value)}
                                                        />
                                                    )}

                                                    <textarea
                                                        className="pipeline-activity-composer-textarea"
                                                        placeholder={
                                                            newActivityType === 'note'
                                                                ? 'Write your note here...'
                                                                : newActivityType === 'email'
                                                                    ? 'Email summary or message body...'
                                                                    : 'Chat notes or SMS conversation details...'
                                                        }
                                                        value={newActivityContent}
                                                        onChange={e => setNewActivityContent(e.target.value)}
                                                        autoFocus
                                                        required
                                                    />

                                                    <div className="pipeline-activity-composer-footer">
                                                        <button
                                                            type="button"
                                                            className="pipeline-btn-cancel"
                                                            style={{ padding: '6px 14px', fontSize: '12.5px' }}
                                                            onClick={() => {
                                                                setIsComposingActivity(false);
                                                                setNewActivityContent('');
                                                                setNewActivitySubject('');
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="submit"
                                                            className="pipeline-btn-save"
                                                            style={{ padding: '6px 18px', fontSize: '12.5px' }}
                                                        >
                                                            {newActivityType === 'note' ? 'Save Note' : newActivityType === 'email' ? 'Log Email' : 'Log Message'}
                                                        </button>
                                                    </div>
                                                </form>
                                            )}

                                        {/* Dedicated Tasks View or Activity Timeline Feed */}
                                        {detailActiveTab === 'tasks' ? (
                                            <div className="pipeline-inline-tasks-panel">

                                                {dealSpecificTasks.length === 0 ? (
                                                    <div className="pipeline-empty-state-mini" style={{ padding: '36px 20px', textAlign: 'center' }}>
                                                        <CheckSquare size={32} style={{ color: '#94a3b8', marginBottom: '8px', opacity: 0.6 }} />
                                                        <p style={{ margin: 0, fontSize: '13.5px', fontWeight: 500, color: '#64748b' }}>No tasks created yet.</p>
                                                    </div>
                                                ) : (
                                                    <div className="pipeline-inline-tasks-list">
                                                        {dealSpecificTasks.map(task => (
                                                            <div key={task._id} className={`pipeline-inline-task-item ${task.completed ? 'completed' : ''}`}>
                                                                <label className="pipeline-task-checkbox-wrap" onClick={e => e.stopPropagation()}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!task.completed}
                                                                        onChange={(e) => handleToggleTaskComplete(task._id, e)}
                                                                    />
                                                                    <span className="pipeline-task-checkmark"></span>
                                                                </label>
                                                                <div className="pipeline-inline-task-main">
                                                                    <div className="pipeline-inline-task-title-row">
                                                                        <span className={`pipeline-inline-task-title ${task.completed ? 'done' : ''}`}>
                                                                            {task.title}
                                                                        </span>
                                                                        <span className={`pipeline-task-priority-pill ${(task.priority || 'medium').toLowerCase()}`}>
                                                                            {task.priority || 'Medium'}
                                                                        </span>
                                                                    </div>
                                                                    {task.description && (
                                                                        <p className="pipeline-inline-task-desc">{task.description}</p>
                                                                    )}
                                                                    <div className="pipeline-inline-task-meta-row">
                                                                        <span className="pipeline-inline-task-meta-item">
                                                                            <Clock size={12} />
                                                                            <span>Due: {task.dueDate ? formatDateDDMMYYYY(task.dueDate) : 'No due date'}</span>
                                                                        </span>
                                                                        <span className="pipeline-inline-task-meta-item">
                                                                            <User size={12} />
                                                                            <span>{task.assignee || 'Artiflow'}</span>
                                                                        </span>
                                                                        <span className="pipeline-inline-task-type-badge">
                                                                            {task.type || 'Follow-up'}
                                                                        </span>
                                                                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                                                                            <button
                                                                                type="button"
                                                                                className="pipeline-mini-icon-btn"
                                                                                title="Edit Task"
                                                                                onClick={(e) => handleOpenEditTask(task, e)}
                                                                            >
                                                                                <Edit2 size={12} />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="pipeline-mini-icon-btn danger"
                                                                                title="Delete Task"
                                                                                onClick={(e) => handleDeleteTask(task._id, e)}
                                                                            >
                                                                                <Trash2 size={12} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ) : detailActiveTab === 'followups' ? (
                                            /* Follow-up Timeline Chaining View */
                                            <div className="pipeline-followup-container">
                                                {/* Header Stats & Quick Action Bar */}
                                                <div className="pipeline-followup-header-stats">
                                                    <div className="pipeline-followup-stats-left">
                                                        <div className="pipeline-followup-stats-icon">
                                                            <Calendar size={18} />
                                                        </div>
                                                        <div>
                                                            <h4 className="pipeline-followup-stats-title">Follow-up Timeline & Touchpoints</h4>
                                                            <p className="pipeline-followup-stats-desc">Connected touchpoint chain of client discussions, meetings, and outcomes</p>
                                                        </div>
                                                    </div>

                                                    <div className="pipeline-followup-stats-pills">
                                                        <span className="pipeline-followup-stat-pill total">
                                                            Total: {currentDealActivities.filter(a => a.type === 'followup').length}
                                                        </span>
                                                        <span className="pipeline-followup-stat-pill completed">
                                                            <Check size={11} />
                                                            Completed: {currentDealActivities.filter(a => a.type === 'followup' && a.status === 'Completed').length}
                                                        </span>
                                                        <span className="pipeline-followup-stat-pill scheduled">
                                                            <Clock size={11} />
                                                            Scheduled: {currentDealActivities.filter(a => a.type === 'followup' && (a.status === 'Scheduled' || !a.status)).length}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Inline Follow-up Composer */}
                                                {isComposingFollowUp && (
                                                    <form onSubmit={handleAddFollowUp} className="pipeline-followup-composer-card">
                                                        <div className="pipeline-followup-composer-header">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <Calendar size={16} style={{ color: '#0284c7' }} />
                                                                <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '14px' }}>Log New Deal Follow-up</span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => setIsComposingFollowUp(false)}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </div>

                                                        {/* Channel Pills Selector */}
                                                        <div className="pipeline-followup-field-group">
                                                            <label className="pipeline-followup-field-label">Follow-up Channel / Touchpoint Type</label>
                                                            <div className="pipeline-followup-channels-bar">
                                                                {[
                                                                    { key: 'Call', label: 'Phone Call', icon: <Phone size={13} />, className: 'channel-call' },
                                                                    { key: 'Meeting', label: 'Meeting', icon: <Users size={13} />, className: 'channel-meeting' },
                                                                    { key: 'WhatsApp', label: 'WhatsApp', icon: <MessageSquare size={13} />, className: 'channel-whatsapp' },
                                                                    { key: 'Email', label: 'Email', icon: <Mail size={13} />, className: 'channel-email' },
                                                                    { key: 'Demo', label: 'Product Demo', icon: <Briefcase size={13} />, className: 'channel-demo' },
                                                                    { key: 'Visit', label: 'Site Visit', icon: <Building2 size={13} />, className: 'channel-visit' }
                                                                ].map(ch => (
                                                                    <button
                                                                        key={ch.key}
                                                                        type="button"
                                                                        className={`pipeline-channel-btn ${ch.className} ${followUpFormData.channel === ch.key ? 'active' : ''}`}
                                                                        onClick={() => setFollowUpFormData(prev => ({ ...prev, channel: ch.key }))}
                                                                    >
                                                                        {ch.icon}
                                                                        <span>{ch.label}</span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* Inputs Grid */}
                                                        <div className="pipeline-followup-form-grid">
                                                            <div className="pipeline-followup-field-group">
                                                                <label className="pipeline-followup-field-label">Follow-up Title / Purpose</label>
                                                                <input
                                                                    type="text"
                                                                    className="pipeline-followup-field-input"
                                                                    placeholder={`e.g. ${followUpFormData.channel} regarding proposal & next steps`}
                                                                    value={followUpFormData.title}
                                                                    onChange={e => setFollowUpFormData(prev => ({ ...prev, title: e.target.value }))}
                                                                />
                                                            </div>

                                                            <div className="pipeline-followup-field-group">
                                                                <label className="pipeline-followup-field-label">Scheduled / Conducted Date</label>
                                                                <input
                                                                    type="date"
                                                                    className="pipeline-followup-field-input"
                                                                    value={followUpFormData.followUpDate}
                                                                    onChange={e => setFollowUpFormData(prev => ({ ...prev, followUpDate: e.target.value }))}
                                                                    required
                                                                />
                                                            </div>

                                                            <div className="pipeline-followup-field-group">
                                                                <label className="pipeline-followup-field-label">Time</label>
                                                                <input
                                                                    type="time"
                                                                    className="pipeline-followup-field-input"
                                                                    value={followUpFormData.followUpTime}
                                                                    onChange={e => setFollowUpFormData(prev => ({ ...prev, followUpTime: e.target.value }))}
                                                                />
                                                            </div>

                                                            <div className="pipeline-followup-field-group">
                                                                <label className="pipeline-followup-field-label">Outcome / Status</label>
                                                                <select
                                                                    className="pipeline-followup-field-input"
                                                                    value={followUpFormData.status}
                                                                    onChange={e => setFollowUpFormData(prev => ({ ...prev, status: e.target.value }))}
                                                                >
                                                                    <option value="Scheduled">Scheduled / Upcoming</option>
                                                                    <option value="Completed">Completed</option>
                                                                    <option value="Follow-up Needed">Follow-up Needed</option>
                                                                    <option value="Rescheduled">Rescheduled</option>
                                                                    <option value="Missed">Missed / No Answer</option>
                                                                </select>
                                                            </div>

                                                            <div className="pipeline-followup-field-group">
                                                                <label className="pipeline-followup-field-label">Next Follow-up Date (Optional)</label>
                                                                <input
                                                                    type="date"
                                                                    className="pipeline-followup-field-input"
                                                                    value={followUpFormData.nextFollowUpDate}
                                                                    onChange={e => setFollowUpFormData(prev => ({ ...prev, nextFollowUpDate: e.target.value }))}
                                                                />
                                                            </div>

                                                            {/* Member Selection */}
                                                            {(() => {
                                                                const currentMemberOptions = getFollowUpMemberOptions('deal', detailedDeal?._id);
                                                                const isSalesPerson = !isAdmin && !isSalesHead && !isSalesManager;
                                                                return (
                                                                    <div className="pipeline-followup-field-group">
                                                                        <label className="pipeline-followup-field-label">
                                                                            {isSalesPerson ? 'Route to Management' : 'Assign / Link Member'}
                                                                        </label>
                                                                        <MemberDropdown
                                                                            options={currentMemberOptions}
                                                                            value={followUpFormData.memberId || ''}
                                                                            onChange={(selId, matchedOpt) => {
                                                                                setFollowUpFormData(prev => ({
                                                                                    ...prev,
                                                                                    memberId: selId,
                                                                                    memberName: matchedOpt ? matchedOpt.name : ''
                                                                                }));
                                                                            }}
                                                                        />
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>

                                                        {/* Remarks Field */}
                                                        <div className="pipeline-followup-field-group">
                                                            <label className="pipeline-followup-field-label">
                                                                Remarks & Discussion Details <span style={{ color: '#ef4444' }}>*</span>
                                                            </label>
                                                            <textarea
                                                                className="pipeline-followup-remarks-textarea"
                                                                placeholder="Enter detailed remarks, client feedback, discussion points, agreed next action items, or objections raised..."
                                                                value={followUpFormData.remarks}
                                                                onChange={e => setFollowUpFormData(prev => ({ ...prev, remarks: e.target.value }))}
                                                                required
                                                            />
                                                        </div>

                                                        {/* Composer Footer */}
                                                        <div className="pipeline-followup-composer-footer">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#64748b' }}>
                                                                <User size={13} />
                                                                <span>
                                                                    Adding as: <strong style={{ color: '#0f172a' }}>{(user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System'}</strong>
                                                                    {' '}({user?.role?.name || user?.role || (roleName ? roleName.charAt(0).toUpperCase() + roleName.slice(1) : 'Sales Executive')})
                                                                </span>
                                                            </div>

                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-btn-cancel"
                                                                    onClick={() => setIsComposingFollowUp(false)}
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    type="submit"
                                                                    className="pipeline-btn-save"
                                                                    disabled={isSubmittingFollowUp}
                                                                >
                                                                    {isSubmittingFollowUp ? 'Saving...' : 'Save Follow-up'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </form>
                                                )}

                                                {/* Chaining Timeline View */}
                                                {(() => {
                                                    const followUps = currentDealActivities.filter(a => a.type === 'followup');
                                                    if (followUps.length === 0) {
                                                        return (
                                                            <div className="pipeline-empty-state-mini" style={{ padding: '48px 20px', textAlign: 'center', background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                                                <Calendar size={36} style={{ color: '#0284c7', marginBottom: '10px', opacity: 0.7 }} />
                                                                <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>No Follow-ups Recorded Yet</h4>
                                                                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b', maxWidth: '420px', marginInline: 'auto' }}>
                                                                    Track scheduled calls, meetings, WhatsApp conversations, and customer touchpoints in a sequential chaining timeline.
                                                                </p>
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-btn-add-followup"
                                                                    style={{ margin: '0 auto' }}
                                                                    onClick={() => setIsComposingFollowUp(true)}
                                                                >
                                                                    <Plus size={14} />
                                                                    <span>Log First Follow-up</span>
                                                                </button>
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div className="pipeline-chain-timeline-wrapper">
                                                            {/* The connecting vertical chain track */}
                                                            <div className="pipeline-chain-track-line" />

                                                            {/* Chained Node Items */}
                                                            {followUps.map((act, idx) => {
                                                                const chKey = (act.channel || 'Call').toLowerCase();
                                                                const chClass = `channel-${chKey.includes('call') ? 'call' : chKey.includes('meeting') ? 'meeting' : chKey.includes('whatsapp') ? 'whatsapp' : chKey.includes('email') ? 'email' : chKey.includes('demo') ? 'demo' : chKey.includes('visit') ? 'visit' : 'call'}`;

                                                                const statusKey = (act.status || 'Scheduled').toLowerCase().replace(/\s+/g, '-');
                                                                const statusClass = `status-${statusKey.includes('completed') ? 'completed' : statusKey.includes('rescheduled') ? 'rescheduled' : statusKey.includes('missed') ? 'missed' : statusKey.includes('needed') ? 'followup-needed' : 'scheduled'}`;

                                                                const authorInitials = (act.author || 'AS')
                                                                    .split(' ')
                                                                    .map(n => n[0])
                                                                    .join('')
                                                                    .slice(0, 2)
                                                                    .toUpperCase();

                                                                return (
                                                                    <div key={act.id || act._id || idx} className="pipeline-chain-node-row">
                                                                        {/* Left Chaining Node Indicator */}
                                                                        <div className="pipeline-chain-marker-wrap">
                                                                            <div className={`pipeline-chain-marker-circle ${statusClass}`} title={`Channel: ${act.channel || 'Call'}`}>
                                                                                {getFollowUpChannelIcon(act.channel, 18)}
                                                                            </div>
                                                                            <span className="pipeline-chain-step-pill">
                                                                                #{followUps.length - idx}
                                                                            </span>
                                                                        </div>

                                                                        {/* Chaining Card Content */}
                                                                        <div className="pipeline-chain-card">
                                                                            {/* Top Row: Channel, Title, Status, Logged When */}
                                                                            <div className="pipeline-chain-card-top">
                                                                                <div className="pipeline-chain-card-title-group">
                                                                                    <span className={`pipeline-chain-channel-chip ${chClass}`}>
                                                                                        {getFollowUpChannelIcon(act.channel, 12)}
                                                                                        <span>{act.channel || 'Call'}</span>
                                                                                    </span>

                                                                                    <span className="pipeline-chain-title-text">
                                                                                        {act.title || `${act.channel || 'Touchpoint'} Follow-up`}
                                                                                    </span>

                                                                                    <span className={`pipeline-chain-status-pill ${statusClass}`}>
                                                                                        {statusClass.includes('completed') && <Check size={11} />}
                                                                                        {statusClass.includes('scheduled') && <Clock size={11} />}
                                                                                        <span>{act.status || 'Scheduled'}</span>
                                                                                    </span>
                                                                                </div>

                                                                                <div className="pipeline-chain-logged-time" title={formatActivityTimestamp(act)}>
                                                                                    <Clock size={12} />
                                                                                    <span>Added {formatRelativeActivityTime(act) || formatActivityTimestamp(act)}</span>
                                                                                </div>
                                                                            </div>

                                                                            {/* Meta Bar: Author (Who added), Scheduled Date & Time */}
                                                                            <div className="pipeline-chain-meta-bar">
                                                                                <div className="pipeline-chain-author-badge">
                                                                                    <div className="pipeline-chain-author-avatar">{authorInitials}</div>
                                                                                    <span>{act.author || 'Admin System'}</span>
                                                                                    {act.authorRole && (
                                                                                        <span className="pipeline-chain-author-role">{act.authorRole}</span>
                                                                                    )}
                                                                                </div>

                                                                                {(act.memberName || act.assignedToName) && (
                                                                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#0369a1', background: '#f0f9ff', border: '1px solid #bae6fd', padding: '2px 8px', borderRadius: '4px', fontWeight: 500 }}>
                                                                                        <User size={11} />
                                                                                        <span>Assigned: {act.memberName || act.assignedToName}</span>
                                                                                    </div>
                                                                                )}

                                                                                {(act.followUpDate || act.date) && (
                                                                                    <div className="pipeline-chain-sched-item">
                                                                                        <Calendar size={13} style={{ color: '#0284c7' }} />
                                                                                        <span>
                                                                                            Date: <strong>{act.followUpDate ? formatDateDDMMYYYY(act.followUpDate) : act.date}</strong>
                                                                                            {act.followUpTime ? ` at ${act.followUpTime}` : ''}
                                                                                        </span>
                                                                                    </div>
                                                                                )}

                                                                                {act.nextFollowUpDate && (
                                                                                    <div className="pipeline-chain-sched-item" style={{ color: '#6366f1' }}>
                                                                                        <ArrowRightLeft size={12} />
                                                                                        <span>Next Follow-up: <strong>{formatDateDDMMYYYY(act.nextFollowUpDate)}</strong></span>
                                                                                    </div>
                                                                                )}
                                                                            </div>

                                                                            {/* Remarks Field (Prominent & Clean) */}
                                                                            <div className={`pipeline-chain-remarks-box ${statusClass}`}>
                                                                                <div className="pipeline-chain-remarks-header">
                                                                                    <FileText size={12} />
                                                                                    <span>Remarks & Outcomes</span>
                                                                                </div>
                                                                                <p className="pipeline-chain-remarks-content">
                                                                                    {act.remarks || act.content || 'No detailed remarks recorded.'}
                                                                                </p>
                                                                            </div>

                                                                            {/* Bottom Actions */}
                                                                            <div className="pipeline-chain-footer-actions">
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                    {act.status !== 'Completed' ? (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="pipeline-chain-mark-btn"
                                                                                            onClick={() => handleUpdateFollowUpStatus(act.id || act._id, 'Completed')}
                                                                                        >
                                                                                            <Check size={12} />
                                                                                            <span>Mark as Completed</span>
                                                                                        </button>
                                                                                    ) : (
                                                                                        <span style={{ fontSize: '11.5px', color: '#059669', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                                            <CheckCircle size={13} />
                                                                                            <span>Completed Touchpoint</span>
                                                                                        </span>
                                                                                    )}

                                                                                    {canUserEditFollowUp(act, user, hasFullAccess) && (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="pipeline-chain-edit-btn"
                                                                                            title="Edit scheduled follow-up"
                                                                                            onClick={() => handleOpenEditFollowUp(act, 'deal', detailedDeal?._id)}
                                                                                        >
                                                                                            <Edit2 size={12} />
                                                                                            <span>Edit</span>
                                                                                        </button>
                                                                                    )}
                                                                                    {isFollowUpTimePassed(act) && act.status !== 'Completed' && (
                                                                                        <span className="pipeline-chain-expired-badge" title="Scheduled follow-up is overdue">
                                                                                            <Clock size={11} /> Overdue
                                                                                        </span>
                                                                                    )}
                                                                                </div>

                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-mini-icon-btn danger"
                                                                                        title="Delete Follow-up"
                                                                                        disabled={deletingFollowUpId === (act.id || act._id)}
                                                                                        onClick={() => handleDeleteFollowUp(act.id || act._id)}
                                                                                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                                                    >
                                                                                        {deletingFollowUpId === (act.id || act._id) ? (
                                                                                            <Loader2 size={12} className="pipeline-spinner-icon" />
                                                                                        ) : (
                                                                                            <Trash2 size={12} />
                                                                                        )}
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        ) : (
                                            /* Activity Timeline Feed */
                                            <div className="pipeline-activity-timeline-feed" style={{ marginTop: (isComposingActivity && ((detailActiveTab === 'notes' && newActivityType === 'note') || (detailActiveTab === 'emails' && newActivityType === 'email'))) ? '16px' : '0' }}>
                                                {detailActiveTab === 'emails' ? (
                                                    <div style={{ textAlign: 'center', padding: '48px 20px', color: '#94a3b8' }}>
                                                        <Mail size={32} style={{ marginBottom: '8px', opacity: 0.6 }} />
                                                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#475569' }}>Feature not implemented yet</p>
                                                        <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#94a3b8' }}>Email integration and logging will be available soon.</p>
                                                    </div>
                                                ) : currentDealActivities.filter(act => {
                                                    if (detailActiveTab === 'all') return true;
                                                    if (detailActiveTab === 'notes') return act.type === 'note';
                                                    if (detailActiveTab === 'followups') return act.type === 'followup';
                                                    return true;
                                                }).length > 0 ? (
                                                    currentDealActivities.filter(act => {
                                                        if (detailActiveTab === 'all') return true;
                                                        if (detailActiveTab === 'notes') return act.type === 'note';
                                                        if (detailActiveTab === 'followups') return act.type === 'followup';
                                                        return true;
                                                    }).map(act => (
                                                        <div key={act.id} className="pipeline-timeline-item-card">
                                                            <div className={`pipeline-timeline-icon-wrap ${act.type || 'activity'}`}>
                                                                {act.type === 'note' && <FileText size={18} />}
                                                                {act.type === 'followup' && <Calendar size={18} />}
                                                                {act.type === 'email' && <Mail size={18} />}
                                                                {act.type === 'message' && <MessageSquare size={18} />}
                                                                {act.type === 'task' && <CheckSquare size={18} />}
                                                                {act.type === 'deal' && <Briefcase size={18} />}
                                                                {act.type === 'contact' && <User size={18} />}
                                                                {act.type === 'company' && <Building2 size={18} />}
                                                                {act.type === 'payment' && <CreditCard size={18} />}
                                                                {(act.type === 'activity' || !['note', 'followup', 'email', 'message', 'task', 'deal', 'contact', 'company', 'payment'].includes(act.type)) && <Tag size={18} />}
                                                            </div>

                                                            <div className="pipeline-timeline-content">
                                                                <div className="pipeline-timeline-header-row">
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                                        <span className="pipeline-timeline-author-title">{act.author}</span>
                                                                        <span style={{ fontSize: '12px', color: '#64748b' }}>• {act.title}</span>
                                                                        {act.type === 'followup' && act.status && (
                                                                            <span className={`pipeline-chain-status-pill status-${(act.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                {act.status}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        {act.type === 'followup' && (
                                                                            <>
                                                                                {canUserEditFollowUp(act, user, hasFullAccess) && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-timeline-mini-action-btn"
                                                                                        title="Edit Follow-up"
                                                                                        onClick={() => handleOpenEditFollowUp(act, 'deal', detailedDeal?._id)}
                                                                                    >
                                                                                        <Edit2 size={11} />
                                                                                        <span>Edit</span>
                                                                                    </button>
                                                                                )}
                                                                                {isFollowUpTimePassed(act) && act.status !== 'Completed' && (
                                                                                    <span className="pipeline-chain-expired-badge" title="Scheduled follow-up is overdue">
                                                                                        <Clock size={11} /> Overdue
                                                                                    </span>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                        <span className="pipeline-timeline-time-badge">{formatActivityTimestamp(act)}</span>
                                                                    </div>
                                                                </div>

                                                                {act.type === 'followup' ? (
                                                                    <div className="pipeline-chain-remarks-box" style={{ margin: '6px 0 0 0' }}>
                                                                        <div className="pipeline-chain-remarks-header">
                                                                            <FileText size={11} />
                                                                            <span>Remarks & Outcomes ({act.channel || 'Call'})</span>
                                                                        </div>
                                                                        <p className="pipeline-chain-remarks-content">{act.remarks || act.content}</p>
                                                                        {(act.followUpDate || act.date) && (
                                                                            <div style={{ marginTop: '6px', fontSize: '11.5px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                                    <Calendar size={13} color="#0284c7" />
                                                                                    Date: <strong style={{ color: '#334155' }}>{act.followUpDate ? formatDateDDMMYYYY(act.followUpDate) : act.date}</strong>
                                                                                    {act.followUpTime ? ` at ${act.followUpTime}` : ''}
                                                                                </span>
                                                                                {act.nextFollowUpDate && (
                                                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#6366f1' }}>
                                                                                        <ArrowRightLeft size={12} />
                                                                                        Next: <strong style={{ color: '#4338ca' }}>{formatDateDDMMYYYY(act.nextFollowUpDate)}</strong>
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <p className="pipeline-timeline-body-text" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{act.content}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div style={{ textAlign: 'center', padding: '36px 20px', color: '#94a3b8' }}>
                                                        {detailActiveTab === 'notes' ? (
                                                            <>
                                                                <FileText size={32} style={{ marginBottom: '8px', opacity: 0.6 }} />
                                                                <p style={{ margin: 0, fontSize: '13.5px', fontWeight: 500, color: '#64748b' }}>No notes created yet.</p>
                                                            </>
                                                        ) : detailActiveTab === 'followups' ? (
                                                            <>
                                                                <Calendar size={32} style={{ marginBottom: '8px', opacity: 0.6 }} />
                                                                <p style={{ margin: 0, fontSize: '13.5px', fontWeight: 500, color: '#64748b' }}>No follow-ups recorded yet.</p>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FileText size={32} style={{ marginBottom: '8px', opacity: 0.6 }} />
                                                                <p style={{ margin: 0, fontSize: '13.5px', fontWeight: 500, color: '#64748b' }}>No activities recorded yet.</p>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* 3. RIGHT SECTION: Associated Contact and Company Details */}
                                <div className="pipeline-detail-col-assoc">
                                    <div className="pipeline-detail-card-panel">
                                        {/* Section 1: Complete Associated Contact Details */}
                                        <div className="pipeline-detail-panel-section">
                                            <div className="pipeline-detail-section-heading" style={{ position: 'relative' }}>
                                                <span>Associated Contact Details</span>
                                                <div style={{ position: 'relative' }}>
                                                    <button
                                                        type="button"
                                                        className="pipeline-detail-dots-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setIsAssocContactMenuOpen(prev => !prev);
                                                        }}
                                                        title="Contact Actions"
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            color: '#94a3b8',
                                                            cursor: 'pointer',
                                                            padding: '4px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            borderRadius: '4px'
                                                        }}
                                                    >
                                                        <MoreHorizontal size={16} />
                                                    </button>
                                                    {isAssocContactMenuOpen && (
                                                        <div
                                                            className="pipeline-detail-dropdown-menu"
                                                            style={{
                                                                position: 'absolute',
                                                                top: '100%',
                                                                right: 0,
                                                                background: '#ffffff',
                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                                borderRadius: '6px',
                                                                border: '1px solid #e2e8f0',
                                                                zIndex: 100,
                                                                minWidth: '130px',
                                                                padding: '4px 0'
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <button
                                                                type="button"
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                    width: '100%',
                                                                    padding: '7px 12px',
                                                                    fontSize: '12px',
                                                                    color: '#334155',
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    textAlign: 'left'
                                                                }}
                                                                onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                                                onClick={() => {
                                                                    setIsAssocContactMenuOpen(false);
                                                                    if (matchedContactForDeal && !matchedContactForDeal._id?.startsWith('contact-deal-')) {
                                                                        handleOpenEditContact(matchedContactForDeal);
                                                                    } else {
                                                                        setEditingContact(null);
                                                                        const phoneDigits = (detailedDeal.phone || '').replace(/^\+\d+\s*/, '');
                                                                        setContactFormData({
                                                                            firstName: matchedContactForDeal?.firstName || detailedDeal.clientName || '',
                                                                            lastName: matchedContactForDeal?.lastName || '',
                                                                            email: detailedDeal.associatedContact || detailedDeal.email || '',
                                                                            countryCode: detailedDeal.countryCode || '+91',
                                                                            phone: phoneDigits,
                                                                            contactSource: 'Direct Reference',
                                                                            leadStage: 'Lead',
                                                                            leadStatus: 'New',
                                                                            owner: detailedDeal.salesOwner || (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Admin System') || 'Admin System',
                                                                            company: detailedDeal.associatedCompany || ''
                                                                        });
                                                                        setShowCreateContactModal(true);
                                                                    }
                                                                }}
                                                            >
                                                                <Edit2 size={13} /> Edit Contact
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div className="pipeline-detail-clean-svg-icon contact">
                                                    <User size={19} strokeWidth={2} />
                                                </div>
                                                <div style={{ overflow: 'hidden' }}>
                                                    <h4
                                                        style={{
                                                            margin: 0,
                                                            fontSize: '13.5px',
                                                            fontWeight: 700,
                                                            color: '#0f172a',
                                                            whiteSpace: 'nowrap',
                                                            textOverflow: 'ellipsis',
                                                            overflow: 'hidden',
                                                            cursor: 'default',
                                                            textDecoration: 'none'
                                                        }}
                                                    >
                                                        {matchedContactForDeal ? `${matchedContactForDeal.firstName} ${matchedContactForDeal.lastName}`.trim() : (detailedDeal.associatedContact || detailedDeal.clientName)}
                                                    </h4>
                                                    <span
                                                        style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', cursor: 'default' }}
                                                    >
                                                        <Mail size={11} /> {matchedContactForDeal?.email || detailedDeal.associatedContact || detailedDeal.email || 'contact@example.com'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="pipeline-detail-kv-grid" style={{ marginTop: '4px' }}>
                                                <div className="pipeline-detail-kv-row">
                                                    <span className="pipeline-detail-k-label">Phone</span>
                                                    <span className="pipeline-detail-v-val" style={{ color: '#0f172a', cursor: 'default' }}>
                                                        {(() => {
                                                            const raw = (matchedContactForDeal?.phone || detailedDeal.phone || '').trim();
                                                            if (!raw) return '—';
                                                            if (raw.startsWith('+')) return raw;
                                                            const cc = (matchedContactForDeal?.countryCode || detailedDeal.countryCode || '+91').trim();
                                                            return `${cc} ${raw}`.trim();
                                                        })()}
                                                    </span>
                                                </div>
                                                <div className="pipeline-detail-kv-row">
                                                    <span className="pipeline-detail-k-label">Lead Stage</span>
                                                    <span className="pipeline-detail-v-val">
                                                        <span
                                                            className={`pipeline-stage-badge ${(() => {
                                                                const st = (matchedContactForDeal?.leadStage || 'Lead').toLowerCase();
                                                                if (st.includes('won') || st.includes('qualified')) return 'won';
                                                                if (st.includes('lost') || st.includes('junk')) return 'lost';
                                                                if (st.includes('hot') || st.includes('proposal')) return 'hot';
                                                                return 'new';
                                                            })()}`}
                                                            style={{ padding: '2px 8px', fontSize: '11px' }}
                                                        >
                                                            {matchedContactForDeal?.leadStage || 'Lead'}
                                                        </span>
                                                    </span>
                                                </div>
                                                <div className="pipeline-detail-kv-row">
                                                    <span className="pipeline-detail-k-label">Lead Status</span>
                                                    <span className="pipeline-detail-v-val">
                                                        <span
                                                            className={`pipeline-stage-badge ${(() => {
                                                                const st = (matchedContactForDeal?.leadStatus || 'New').toLowerCase();
                                                                if (st === 'won' || st === 'qualified' || st === 'active' || st === 'open') return 'won';
                                                                if (st === 'lost' || st === 'unqualified' || st === 'spam') return 'lost';
                                                                if (st === 'hot' || st === 'in progress' || st === 'contacted') return 'hot';
                                                                return 'new';
                                                            })()}`}
                                                            style={{ padding: '2px 8px', fontSize: '11px' }}
                                                        >
                                                            {matchedContactForDeal?.leadStatus || 'New'}
                                                        </span>
                                                    </span>
                                                </div>
                                                <div className="pipeline-detail-kv-row">
                                                    <span className="pipeline-detail-k-label">Contact Source</span>
                                                    <span className="pipeline-detail-v-val">{matchedContactForDeal?.contactSource || 'Direct Traffic'}</span>
                                                </div>
                                                <div className="pipeline-detail-kv-row">
                                                    <span className="pipeline-detail-k-label">Added By</span>
                                                    <span className="pipeline-detail-v-val">{matchedContactForDeal?.addedBy || matchedContactForDeal?.owner || (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Admin System') || 'Admin System'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Section 2: Complete Associated Company Details */}
                                        <div className="pipeline-detail-panel-section">
                                            <div className="pipeline-detail-section-heading">
                                                <span>Associated Company Details</span>
                                                <button
                                                    type="button"
                                                    className="pipeline-detail-add-badge-btn"
                                                    onClick={() => {
                                                        setCompanyFormData({
                                                            name: (detailedDeal.associatedCompany && detailedDeal.associatedCompany !== 'Direct Client') ? detailedDeal.associatedCompany : ((matchedContactForDeal?.company && matchedContactForDeal.company !== 'Direct Client') ? matchedContactForDeal.company : ''),
                                                            domain: '',
                                                            owner: [],
                                                            industry: '',
                                                            country: 'United States',
                                                            state: '',
                                                            city: (detailedDeal.location && detailedDeal.location !== 'Pembroke Pines' && detailedDeal.location !== 'Pune, India') ? detailedDeal.location : '',
                                                            postalCode: ''
                                                        });
                                                        setShowCreateCompanyModal(true);
                                                    }}
                                                    title="Add / Link Company"
                                                >
                                                    Add+
                                                </button>
                                            </div>

                                            {(() => {
                                                const compFromContact = (matchedContactForDeal?.company && matchedContactForDeal.company !== 'Direct Client') ? matchedContactForDeal.company.trim() : '';
                                                const compFromDeal = (detailedDeal.associatedCompany && detailedDeal.associatedCompany !== 'Direct Client') ? detailedDeal.associatedCompany.trim() : (detailedDeal.company && detailedDeal.company !== 'Direct Client' ? detailedDeal.company.trim() : '');
                                                const effectiveCompany = compFromDeal || compFromContact;
                                                const hasAssociatedCompany = Boolean(matchedCompanyForDeal || effectiveCompany);

                                                if (!hasAssociatedCompany) {
                                                    return (
                                                        <div className="pipeline-detail-empty-subcard" style={{ padding: '14px 12px', textAlign: 'center', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1', marginTop: '6px' }}>
                                                            <Building2 size={20} style={{ color: '#94a3b8', marginBottom: '4px' }} />
                                                            <p style={{ margin: 0, fontSize: '12.5px', fontWeight: 600, color: '#64748b' }}>
                                                                No company details
                                                            </p>
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <div className="pipeline-detail-clean-svg-icon company">
                                                                <Building2 size={19} strokeWidth={2} />
                                                            </div>
                                                            <div style={{ overflow: 'hidden' }}>
                                                                <h4
                                                                    style={{
                                                                        margin: 0,
                                                                        fontSize: '13.5px',
                                                                        fontWeight: 700,
                                                                        color: '#575757f0',
                                                                        whiteSpace: 'nowrap',
                                                                        textOverflow: 'ellipsis',
                                                                        overflow: 'hidden',
                                                                        cursor: (hasAssociatedCompany && matchedCompanyForDeal) ? 'pointer' : 'default'
                                                                    }}
                                                                    onClick={() => {
                                                                        if (hasAssociatedCompany && matchedCompanyForDeal) {
                                                                            setViewingCompany(matchedCompanyForDeal);
                                                                        }
                                                                    }}
                                                                    title={(hasAssociatedCompany && matchedCompanyForDeal) ? "Click to view Company Profile" : ""}
                                                                >
                                                                    {matchedCompanyForDeal?.name || effectiveCompany}
                                                                </h4>
                                                                {matchedCompanyForDeal?.domain && (
                                                                    <a
                                                                        href={`https://${matchedCompanyForDeal.domain.replace(/^https?:\/\//, '')}`}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        style={{ fontSize: '12px', color: '#0284c7', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}
                                                                    >
                                                                        <Globe size={11} /> {matchedCompanyForDeal.domain} <ExternalLink size={10} />
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="pipeline-detail-kv-grid" style={{ marginTop: '4px' }}>
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Industry Type</span>
                                                                <span className="pipeline-detail-v-val">{matchedCompanyForDeal?.industry || '-'}</span>
                                                            </div>
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Company Owner</span>
                                                                <span className="pipeline-detail-v-val">
                                                                    <CompanyOwnerDisplay owner={matchedCompanyForDeal?.owner} fallback={detailedDeal.salesOwner} />
                                                                </span>
                                                            </div>
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Country</span>
                                                                <span className="pipeline-detail-v-val">{matchedCompanyForDeal?.country || '-'}</span>
                                                            </div>
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">State / Region</span>
                                                                <span className="pipeline-detail-v-val">{matchedCompanyForDeal?.state || '-'}</span>
                                                            </div>
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">City</span>
                                                                <span className="pipeline-detail-v-val">{matchedCompanyForDeal?.city || (detailedDeal.location && detailedDeal.location !== 'Pembroke Pines' && detailedDeal.location !== 'Pune, India' ? detailedDeal.location : '-') || '-'}</span>
                                                            </div>
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Postal Code</span>
                                                                <span className="pipeline-detail-v-val">{matchedCompanyForDeal?.postalCode || '-'}</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Top Header */}
                            <div className="pipeline-top-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <h1 className="pipeline-deals-count-title">
                                        Deals Management
                                    </h1>
                                    {selectedKanbanDealIds.length > 0 && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span className="pipeline-stage-selected-pill" style={{ padding: '4px 10px', fontSize: '12px' }} title={`${selectedKanbanDealIds.length} deal(s) selected`}>
                                                <Check size={12} strokeWidth={3} />
                                                <span>{selectedKanbanDealIds.length} selected</span>
                                            </span>

                                            {canUpdate && (
                                                <div style={{ position: 'relative' }}>
                                                    <button
                                                        type="button"
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '5px',
                                                            padding: '5px 11px',
                                                            borderRadius: '6px',
                                                            background: '#e0f2fe',
                                                            color: '#0284c7',
                                                            border: '1px solid #bae6fd',
                                                            fontSize: '12px',
                                                            fontWeight: 600,
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setIsTopDealBulkMoveDropdownOpen(prev => !prev);
                                                        }}
                                                        title="Move all selected deals to another stage"
                                                    >
                                                        <ArrowRightLeft size={12} />
                                                        <span>Move Stage</span>
                                                        <ChevronDown size={11} style={{ transform: isTopDealBulkMoveDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
                                                    </button>

                                                    {isTopDealBulkMoveDropdownOpen && (
                                                        <div
                                                            className="pipeline-card-dropdown-menu"
                                                            style={{ top: 'calc(100% + 4px)', left: 0, minWidth: '180px', zIndex: 120, boxShadow: '0 10px 25px rgba(0,0,0,0.15)' }}
                                                            onClick={e => e.stopPropagation()}
                                                        >
                                                            <div className="pipeline-submenu-header">
                                                                <span className="pipeline-submenu-title">Move {selectedKanbanDealIds.length} to:</span>
                                                            </div>
                                                            {pipelines.map(p => (
                                                                <button
                                                                    key={p.id}
                                                                    type="button"
                                                                    className="pipeline-card-menu-item"
                                                                    onClick={() => {
                                                                        setIsTopDealBulkMoveDropdownOpen(false);
                                                                        handleBulkMoveStage(p.name);
                                                                    }}
                                                                >
                                                                    <span className={`pipeline-menu-indicator ${p.badgeClass || 'new'}`} />
                                                                    <span>{p.name}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {canDelete && (
                                                <button
                                                    type="button"
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '5px',
                                                        padding: '5px 11px',
                                                        borderRadius: '6px',
                                                        background: '#fee2e2',
                                                        color: '#ef4444',
                                                        border: '1px solid #fecaca',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => handleBulkDeleteDeals()}
                                                    title="Delete all selected deals"
                                                >
                                                    <Trash2 size={12} />
                                                    <span>Delete ({selectedKanbanDealIds.length})</span>
                                                </button>
                                            )}

                                            <button
                                                type="button"
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    padding: '5px 9px',
                                                    borderRadius: '6px',
                                                    background: '#f1f5f9',
                                                    color: '#64748b',
                                                    border: '1px solid #e2e8f0',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => {
                                                    setSelectedKanbanDealIds([]);
                                                    setIsTopDealBulkMoveDropdownOpen(false);
                                                    setIsBulkMoveDropdownOpen(false);
                                                }}
                                                title="Deselect all deals"
                                            >
                                                <X size={12} />
                                                <span>Deselect</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {/* View Switcher */}
                                    <div className="pipeline-view-switcher">
                                        <button
                                            className={`pipeline-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                                            onClick={() => setViewMode('list')}
                                            title="List Table View"
                                        >
                                            <List size={16} />
                                        </button>
                                        <button
                                            className={`pipeline-view-btn ${viewMode === 'kanban' ? 'active' : ''}`}
                                            onClick={() => setViewMode('kanban')}
                                            title="Kanban Board View"
                                        >
                                            <Kanban size={16} />
                                        </button>
                                    </div>

                                    {/* Export */}
                                    <button className="pipeline-btn-export" onClick={handleExport} title="Export to Excel">
                                        <Download size={14} />
                                        <span>Export</span>
                                    </button>

                                    <NotificationBell filterType="CRM" />
                                </div>
                            </div>

                            {/* Action Toolbar */}
                            <div className="pipeline-toolbar-row">
                                <div className="pipeline-toolbar-left">
                                    {/* Search Box */}
                                    <div className="pipeline-contacts-search-box">
                                        <Search size={14} className="pipeline-search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Search deals..."
                                            value={dealSearchQuery}
                                            onChange={e => setDealSearchQuery(e.target.value)}
                                            className="pipeline-contacts-search-input"
                                        />
                                        {dealSearchQuery && (
                                            <button
                                                type="button"
                                                className="pipeline-search-clear-btn"
                                                onClick={() => setDealSearchQuery('')}
                                            >
                                                <X size={12} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Sort By Dropdown */}
                                    <div className="pipeline-filter-dropdown-wrap">
                                        <button
                                            type="button"
                                            className={`pipeline-tool-btn ${sortBy !== 'newest' ? 'filter-active' : ''} ${isSortDropdownOpen ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsOwnerDropdownOpen(false);
                                                setIsDealStatusDropdownOpen(false);
                                                setIsDealOriginDropdownOpen(false);
                                                setIsDateDropdownOpen(false);
                                                setIsSortDropdownOpen(prev => !prev);
                                            }}
                                            title="Sort deals"
                                        >
                                            <ArrowUpDown size={14} />
                                            <span>{sortBy !== 'newest' ? `Sort: ${sortLabel}` : 'Sort by'}</span>
                                            <ChevronDown size={13} style={{ color: sortBy !== 'newest' ? '#0284c7' : '#94a3b8' }} />
                                        </button>

                                        {isSortDropdownOpen && (
                                            <div className="pipeline-filter-menu-popover" onClick={e => e.stopPropagation()}>
                                                <div className="pipeline-filter-menu-header">Sort deals by</div>
                                                <div className="pipeline-filter-items-scroll">
                                                    {[
                                                        { id: 'newest', label: 'Newest first' },
                                                        { id: 'oldest', label: 'Oldest first' },
                                                        { id: 'value_high', label: 'Deal Value: High to Low' },
                                                        { id: 'value_low', label: 'Deal Value: Low to High' },
                                                        { id: 'name_asc', label: 'Deal Name (A - Z)' },
                                                        { id: 'name_desc', label: 'Deal Name (Z - A)' },
                                                        { id: 'date_asc', label: 'Expected Close Date (Earliest)' },
                                                        { id: 'date_desc', label: 'Expected Close Date (Latest)' }
                                                    ].map(opt => (
                                                        <button
                                                            key={opt.id}
                                                            type="button"
                                                            className={`pipeline-filter-item-btn ${sortBy === opt.id ? 'selected' : ''}`}
                                                            onClick={() => {
                                                                setSortBy(opt.id);
                                                                setIsSortDropdownOpen(false);
                                                            }}
                                                        >
                                                            <span>{opt.label}</span>
                                                            {sortBy === opt.id && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* All Deal Owners Dropdown (Sales Head, Sales Manager, and Admin only) */}
                                    {hasFullAccess && (
                                        <div className="pipeline-filter-dropdown-wrap">
                                            <button
                                                type="button"
                                                className={`pipeline-tool-btn ${ownerFilter !== 'all' ? 'filter-active' : ''} ${isOwnerDropdownOpen ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsSortDropdownOpen(false);
                                                    setIsDealStatusDropdownOpen(false);
                                                    setIsDealOriginDropdownOpen(false);
                                                    setIsDateDropdownOpen(false);
                                                    setIsOwnerDropdownOpen(prev => !prev);
                                                }}
                                                title="Filter by deal owners"
                                            >
                                                <User size={14} />
                                                <span>{ownerFilter === 'all' ? 'All deal owners' : ownerFilter}</span>
                                                <ChevronDown size={13} style={{ color: ownerFilter !== 'all' ? '#0284c7' : '#94a3b8' }} />
                                            </button>

                                            {isOwnerDropdownOpen && (
                                                <div className="pipeline-filter-menu-popover" style={{ minWidth: '240px' }} onClick={e => e.stopPropagation()}>
                                                    <div className="pipeline-filter-menu-header">Filter by Deal Owner</div>
                                                    {allDealOwners.length > 5 && (
                                                        <div className="pipeline-filter-search-wrap">
                                                            <input
                                                                type="text"
                                                                className="pipeline-filter-search-input"
                                                                placeholder="Search deal owner..."
                                                                value={ownerSearchQuery}
                                                                onChange={e => setOwnerSearchQuery(e.target.value)}
                                                                autoFocus
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="pipeline-filter-items-scroll">
                                                        <button
                                                            type="button"
                                                            className={`pipeline-filter-item-btn ${ownerFilter === 'all' ? 'selected' : ''}`}
                                                            onClick={() => {
                                                                setOwnerFilter('all');
                                                                setIsOwnerDropdownOpen(false);
                                                                setOwnerSearchQuery('');
                                                            }}
                                                        >
                                                            <User size={14} style={{ color: '#64748b' }} />
                                                            <span>All deal owners</span>
                                                            <span className="pipeline-filter-item-badge">{deals.filter(d => isItemOwnedByUser(d, 'deal')).length}</span>
                                                            {ownerFilter === 'all' && <Check size={14} style={{ color: '#0284c7' }} />}
                                                        </button>

                                                        {allDealOwners
                                                            .filter(ownerName => !ownerSearchQuery.trim() || ownerName.toLowerCase().includes(ownerSearchQuery.trim().toLowerCase()))
                                                            .map(ownerName => {
                                                                const count = dealOwnerCounts[ownerName] || dealOwnerCounts[ownerName.toLowerCase()] || 0;
                                                                return (
                                                                    <button
                                                                        key={ownerName}
                                                                        type="button"
                                                                        className={`pipeline-filter-item-btn ${ownerFilter.toLowerCase() === ownerName.toLowerCase() ? 'selected' : ''}`}
                                                                        onClick={() => {
                                                                            setOwnerFilter(ownerName);
                                                                            setIsOwnerDropdownOpen(false);
                                                                            setOwnerSearchQuery('');
                                                                        }}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                width: 18,
                                                                                height: 18,
                                                                                borderRadius: '50%',
                                                                                background: '#e0f2fe',
                                                                                color: '#0284c7',
                                                                                fontSize: '10px',
                                                                                fontWeight: 700,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center'
                                                                            }}
                                                                        >
                                                                            {ownerName.charAt(0).toUpperCase()}
                                                                        </div>
                                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ownerName}</span>
                                                                        <span className="pipeline-filter-item-badge">{count}</span>
                                                                        {ownerFilter.toLowerCase() === ownerName.toLowerCase() && (
                                                                            <Check size={14} style={{ color: '#0284c7' }} />
                                                                        )}
                                                                    </button>
                                                                );
                                                            })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Filter by Status Dropdown */}
                                    <div className="pipeline-filter-dropdown-wrap">
                                        <button
                                            type="button"
                                            className={`pipeline-tool-btn ${dealStatusFilter !== 'all' ? 'filter-active' : ''} ${isDealStatusDropdownOpen ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsSortDropdownOpen(false);
                                                setIsOwnerDropdownOpen(false);
                                                setIsDealOriginDropdownOpen(false);
                                                setIsDateDropdownOpen(false);
                                                setIsDealStatusDropdownOpen(prev => !prev);
                                            }}
                                            title="Filter deals by status / stage"
                                        >
                                            <Tag size={14} />
                                            <span>{dealStatusFilter === 'all' ? 'All statuses' : dealStatusFilter}</span>
                                            <ChevronDown size={13} style={{ color: dealStatusFilter !== 'all' ? '#0284c7' : '#94a3b8' }} />
                                        </button>

                                        {isDealStatusDropdownOpen && (
                                            <div className="pipeline-filter-menu-popover" style={{ minWidth: '210px' }} onClick={e => e.stopPropagation()}>
                                                <div className="pipeline-filter-menu-header">Filter by Status / Stage</div>
                                                <div className="pipeline-filter-items-scroll">
                                                    <button
                                                        type="button"
                                                        className={`pipeline-filter-item-btn ${dealStatusFilter === 'all' ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setDealStatusFilter('all');
                                                            setIsDealStatusDropdownOpen(false);
                                                        }}
                                                    >
                                                        <span>All statuses</span>
                                                        {dealStatusFilter === 'all' && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                    </button>
                                                    {allDealStatuses.map(status => (
                                                        <button
                                                            key={status}
                                                            type="button"
                                                            className={`pipeline-filter-item-btn ${dealStatusFilter.toLowerCase() === status.toLowerCase() ? 'selected' : ''}`}
                                                            onClick={() => {
                                                                setDealStatusFilter(status);
                                                                setIsDealStatusDropdownOpen(false);
                                                            }}
                                                        >
                                                            <span>{status}</span>
                                                            {dealStatusFilter.toLowerCase() === status.toLowerCase() && (
                                                                <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Filter by Origin Dropdown */}
                                    <div className="pipeline-filter-dropdown-wrap">
                                        <button
                                            type="button"
                                            className={`pipeline-tool-btn ${dealOriginFilter !== 'all' ? 'filter-active' : ''} ${isDealOriginDropdownOpen ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsSortDropdownOpen(false);
                                                setIsOwnerDropdownOpen(false);
                                                setIsDealStatusDropdownOpen(false);
                                                setIsDateDropdownOpen(false);
                                                setIsDealOriginDropdownOpen(prev => !prev);
                                            }}
                                            title="Filter deals by origin"
                                        >
                                            <Globe size={14} />
                                            <span>{dealOriginFilter === 'all' ? 'All origins' : dealOriginFilter}</span>
                                            <ChevronDown size={13} style={{ color: dealOriginFilter !== 'all' ? '#0284c7' : '#94a3b8' }} />
                                        </button>

                                        {isDealOriginDropdownOpen && (
                                            <div className="pipeline-filter-menu-popover" style={{ minWidth: '200px' }} onClick={e => e.stopPropagation()}>
                                                <div className="pipeline-filter-menu-header">Filter by Origin</div>
                                                <div className="pipeline-filter-items-scroll">
                                                    <button
                                                        type="button"
                                                        className={`pipeline-filter-item-btn ${dealOriginFilter === 'all' ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setDealOriginFilter('all');
                                                            setIsDealOriginDropdownOpen(false);
                                                        }}
                                                    >
                                                        <span>All origins</span>
                                                        <span className="pipeline-filter-item-badge">{dealOriginCounts.all}</span>
                                                        {dealOriginFilter === 'all' && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`pipeline-filter-item-btn ${dealOriginFilter === 'Domestic' ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setDealOriginFilter('Domestic');
                                                            setIsDealOriginDropdownOpen(false);
                                                        }}
                                                    >
                                                        <span>Domestic</span>
                                                        <span className="pipeline-filter-item-badge">{dealOriginCounts.domestic}</span>
                                                        {dealOriginFilter === 'Domestic' && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`pipeline-filter-item-btn ${dealOriginFilter === 'International' ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setDealOriginFilter('International');
                                                            setIsDealOriginDropdownOpen(false);
                                                        }}
                                                    >
                                                        <span>International</span>
                                                        <span className="pipeline-filter-item-badge">{dealOriginCounts.international}</span>
                                                        {dealOriginFilter === 'International' && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Date Filter Dropdown */}
                                    <div className="pipeline-filter-dropdown-wrap">
                                        <button
                                            type="button"
                                            className={`pipeline-tool-btn ${dateFilter !== 'all' ? 'filter-active' : ''} ${isDateDropdownOpen ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsSortDropdownOpen(false);
                                                setIsOwnerDropdownOpen(false);
                                                setIsDealStatusDropdownOpen(false);
                                                setIsDealOriginDropdownOpen(false);
                                                setIsDateDropdownOpen(prev => !prev);
                                            }}
                                            title="Filter deals by date"
                                        >
                                            <Calendar size={14} />
                                            <span>{dateFilterLabel}</span>
                                            <ChevronDown size={13} style={{ color: dateFilter !== 'all' ? '#0284c7' : '#94a3b8' }} />
                                        </button>

                                        {isDateDropdownOpen && (
                                            <div className="pipeline-filter-menu-popover" style={{ minWidth: '230px' }} onClick={e => e.stopPropagation()}>
                                                <div className="pipeline-filter-menu-header">Filter by Close Date</div>
                                                <div className="pipeline-filter-items-scroll">
                                                    {[
                                                        { id: 'all', label: 'All Dates (All Time)' },
                                                        { id: 'today', label: 'Today' },
                                                        { id: 'this_week', label: 'This Week' },
                                                        { id: 'this_month', label: 'This Month' },
                                                        { id: 'next_30_days', label: 'Next 30 Days' },
                                                        { id: 'this_quarter', label: 'This Quarter' },
                                                        { id: 'this_year', label: 'This Year' },
                                                        { id: 'custom', label: 'Custom Date Range...' }
                                                    ].map(opt => (
                                                        <button
                                                            key={opt.id}
                                                            type="button"
                                                            className={`pipeline-filter-item-btn ${dateFilter === opt.id ? 'selected' : ''}`}
                                                            onClick={() => {
                                                                setDateFilter(opt.id);
                                                                if (opt.id !== 'custom') {
                                                                    setIsDateDropdownOpen(false);
                                                                }
                                                            }}
                                                        >
                                                            <span>{opt.label}</span>
                                                            {dateFilter === opt.id && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                        </button>
                                                    ))}
                                                </div>

                                                {dateFilter === 'custom' && (
                                                    <div className="pipeline-filter-date-custom-panel">
                                                        <div className="pipeline-filter-date-input-group">
                                                            <label className="pipeline-filter-date-label">From Date</label>
                                                            <input
                                                                type="date"
                                                                className="pipeline-filter-date-input"
                                                                value={customDateStart}
                                                                onChange={e => setCustomDateStart(e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="pipeline-filter-date-input-group">
                                                            <label className="pipeline-filter-date-label">To Date</label>
                                                            <input
                                                                type="date"
                                                                className="pipeline-filter-date-input"
                                                                value={customDateEnd}
                                                                onChange={e => setCustomDateEnd(e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="pipeline-filter-date-actions">
                                                            <button
                                                                type="button"
                                                                className="pipeline-filter-date-btn-cancel"
                                                                onClick={() => {
                                                                    setCustomDateStart('');
                                                                    setCustomDateEnd('');
                                                                    setDateFilter('all');
                                                                    setIsDateDropdownOpen(false);
                                                                }}
                                                            >
                                                                Clear
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="pipeline-filter-date-btn-apply"
                                                                onClick={() => setIsDateDropdownOpen(false)}
                                                            >
                                                                Apply
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Reset / Clear All Filters */}
                                    {isAnyDealFilterActive && (
                                        <button
                                            type="button"
                                            className="pipeline-filter-clear-all-btn"
                                            onClick={handleResetDealFilters}
                                            title="Clear all filters"
                                        >
                                            <X size={13} />
                                            <span>Reset Filters</span>
                                        </button>
                                    )}
                                </div>

                                <div className="pipeline-toolbar-right">
                                    {/* Create Deals Primary Button */}
                                    {canCreate && (
                                        <button
                                            className="pipeline-btn-create-deal"
                                            onClick={() => handleOpenAddDeal('Initial')}
                                            title="Create new deal"
                                        >
                                            <Plus size={15} />
                                            <span>Create deals</span>
                                            <ChevronDown size={13} />
                                        </button>
                                    )}

                                    {/* More Options */}
                                    {hasFullAccess && (
                                        <div style={{ position: 'relative' }}>
                                            <button
                                                type="button"
                                                className={`pipeline-btn-more ${isDealsToolbarMenuOpen ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsDealsToolbarMenuOpen(prev => !prev);
                                                }}
                                                title="More Options"
                                            >
                                                <MoreHorizontal size={16} />
                                            </button>
                                            {isDealsToolbarMenuOpen && (
                                                <div
                                                    className="pipeline-card-dropdown-menu"
                                                    style={{ top: 'calc(100% + 6px)', right: 0, minWidth: '160px', zIndex: 100 }}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <button
                                                        type="button"
                                                        className="pipeline-card-menu-item"
                                                        onClick={() => {
                                                            setIsDealsToolbarMenuOpen(false);
                                                            setShowAddStageModal(true);
                                                        }}
                                                    >
                                                        <div className="pipeline-card-menu-item-left">
                                                            <Plus size={14} style={{ color: '#0284c7' }} />
                                                            <span>Add Stage</span>
                                                        </div>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="pipeline-card-menu-item"
                                                        onClick={() => {
                                                            setIsDealsToolbarMenuOpen(false);
                                                            setReorderStagesList([...visiblePipelines]);
                                                            setShowReorderStagesModal(true);
                                                        }}
                                                    >
                                                        <div className="pipeline-card-menu-item-left">
                                                            <ArrowUpDown size={14} style={{ color: '#0284c7' }} />
                                                            <span>Reorder Stages</span>
                                                        </div>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ----------------------------------------------------------------
              3. Kanban Columns Board or List View
              ---------------------------------------------------------------- */}
                            {isLoading ? (
                                viewMode === 'kanban' ? renderKanbanSkeleton(visiblePipelines.length || 4, false) : renderTableSkeleton(7)
                            ) : viewMode === 'kanban' ? (
                                visiblePipelines.length === 0 ? (
                                    <div className="pipeline-empty-state-card" style={{ maxWidth: '480px', margin: '40px auto' }}>
                                        <div className="pipeline-empty-icon-wrap" style={{ background: '#f0f9ff', color: '#0284c7' }}>
                                            <Briefcase size={32} />
                                        </div>
                                        <h3 className="pipeline-empty-state-title">No Deal Stages</h3>
                                        <p className="pipeline-empty-state-desc">
                                            No deal stages are currently configured. Click below to add a custom stage column.
                                        </p>
                                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '12px' }}>
                                            <button
                                                type="button"
                                                className="pipeline-empty-state-action-btn"
                                                onClick={() => setShowAddStageModal(true)}
                                            >
                                                <Plus size={14} />
                                                <span>Add Stage</span>
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="pipeline-kanban-board">
                                        {visiblePipelines.map((pipeline, pIndex) => {
                                            const pipelineDeals = filteredDeals.filter(d => {
                                                const targetStage = getDealStageName(d, visiblePipelines);
                                                return (targetStage || '').trim().toLowerCase() === (pipeline.name || '').trim().toLowerCase();
                                            });
                                            const count = pipelineDeals.length;
                                            const totalStageAmountINR = pipelineDeals.reduce((sum, d) => sum + getDealValueInINR(d), 0);
                                            const colKey = `pipeline-col-${pIndex}`;
                                            const isColEditing = editingPipelineId === colKey;

                                            return (
                                                <div
                                                    key={colKey}
                                                    className={`pipeline-column ${dragOverColumnId === colKey ? 'drag-over' : ''} ${draggedStageColIndex !== null && dragOverStageColIndex === pIndex ? 'stage-col-highlight' : ''}`}
                                                    onDragOver={e => {
                                                        if (draggedStageColIndex !== null) {
                                                            handleStageHeaderDragOver(e, pIndex);
                                                        } else {
                                                            handleDragOver(e, colKey);
                                                        }
                                                    }}
                                                    onDragLeave={e => {
                                                        if (draggedStageColIndex !== null) {
                                                            handleStageHeaderDragLeave(e);
                                                        } else {
                                                            handleDragLeave(e, colKey);
                                                        }
                                                    }}
                                                    onDrop={e => {
                                                        const headerData = e.dataTransfer?.getData('text/plain');
                                                        if (draggedStageColIndex !== null || (headerData && headerData.startsWith('stage-header-'))) {
                                                            handleStageHeaderDrop(e, pIndex);
                                                        } else {
                                                            handleDrop(e, pipeline.name);
                                                        }
                                                    }}
                                                >
                                                    {/* Column Header */}
                                                    <div
                                                        className={`pipeline-column-header ${draggedStageColIndex !== null && dragOverStageColIndex === pIndex ? 'stage-drag-over' : ''}`}
                                                        draggable={hasFullAccess}
                                                        onDragStart={(e) => handleStageHeaderDragStart(e, pIndex)}
                                                        onDragOver={(e) => handleStageHeaderDragOver(e, pIndex)}
                                                        onDragLeave={handleStageHeaderDragLeave}
                                                        onDragEnd={handleStageHeaderDragEnd}
                                                        onDrop={(e) => handleStageHeaderDrop(e, pIndex)}
                                                        style={hasFullAccess ? { cursor: 'grab' } : {}}
                                                    >
                                                        {isColEditing ? (
                                                            <div className="pipeline-inline-edit-wrap">
                                                                <input
                                                                    type="text"
                                                                    className="pipeline-inline-edit-input"
                                                                    value={editingPipelineName}
                                                                    autoFocus
                                                                    onChange={e => setEditingPipelineName(e.target.value)}
                                                                    onKeyDown={e => {
                                                                        if (e.key === 'Enter') handleSaveEditPipeline(colKey, pIndex);
                                                                        if (e.key === 'Escape') setEditingPipelineId(null);
                                                                    }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-inline-save-btn"
                                                                    onClick={() => handleSaveEditPipeline(colKey, pIndex)}
                                                                    title="Save stage name"
                                                                >
                                                                    <CheckCircle size={13} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-inline-cancel-btn"
                                                                    onClick={() => setEditingPipelineId(null)}
                                                                    title="Cancel"
                                                                >
                                                                    <X size={13} />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="pipeline-stage-info">
                                                                    {hasFullAccess && (
                                                                        <span title="Drag column header to reorder stage" style={{ display: 'inline-flex', alignItems: 'center', color: '#94a3b8', cursor: 'grab', flexShrink: 0 }}>
                                                                            <GripVertical size={13} />
                                                                        </span>
                                                                    )}
                                                                    <h3 className="pipeline-stage-name" title={pipeline.name}>
                                                                        {pipeline.name}
                                                                    </h3>
                                                                    <span className={`pipeline-stage-badge ${pipeline.badgeClass || 'new'}`}>
                                                                        {count}
                                                                    </span>
                                                                    {(() => {
                                                                        const stageSelectedCount = pipelineDeals.filter(d => selectedKanbanDealIds.includes(d._id)).length;
                                                                        return stageSelectedCount > 0 ? (
                                                                            <span className="pipeline-stage-selected-pill" title={`${stageSelectedCount} of ${count} deals selected`}>
                                                                                <Check size={10} strokeWidth={3} />
                                                                                <span>{stageSelectedCount}</span>
                                                                            </span>
                                                                        ) : null;
                                                                    })()}
                                                                </div>

                                                                <div className="pipeline-col-header-actions" style={{ position: 'relative' }}>
                                                                    {(() => {
                                                                        const stageSelectedCount = pipelineDeals.filter(d => selectedKanbanDealIds.includes(d._id)).length;
                                                                        const isAllStageSelected = count > 0 && stageSelectedCount === count;
                                                                        if (count > 0 && (selectedKanbanDealIds.length > 0 || stageSelectedCount > 0)) {
                                                                            return (
                                                                                <button
                                                                                    type="button"
                                                                                    className={`pipeline-stage-select-all-btn ${isAllStageSelected ? 'all-selected' : ''}`}
                                                                                    onClick={(e) => handleToggleStageSelection(pipelineDeals, e)}
                                                                                    title={isAllStageSelected ? "Deselect all deals in this stage" : "Select all deals in this stage"}
                                                                                >
                                                                                    {isAllStageSelected ? 'Deselect' : 'Select all'}
                                                                                </button>
                                                                            );
                                                                        }
                                                                        return null;
                                                                    })()}
                                                                    <button
                                                                        type="button"
                                                                        className="pipeline-col-action-btn more"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setActiveColMenuId(activeColMenuId === colKey ? null : colKey);
                                                                        }}
                                                                        title="Stage options"
                                                                    >
                                                                        <MoreHorizontal size={14} />
                                                                    </button>

                                                                    {activeColMenuId === colKey && (
                                                                        <div
                                                                            className="pipeline-card-dropdown-menu"
                                                                            style={{ top: 'calc(100% + 4px)', right: 0, minWidth: '160px', zIndex: 90 }}
                                                                            onClick={e => e.stopPropagation()}
                                                                        >
                                                                            {canCreate && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="pipeline-card-menu-item"
                                                                                    onClick={() => {
                                                                                        setActiveColMenuId(null);
                                                                                        handleOpenAddDeal(pipeline.name);
                                                                                    }}
                                                                                >
                                                                                    <div className="pipeline-card-menu-item-left">
                                                                                        <Plus size={13} style={{ color: '#0284c7' }} />
                                                                                        <span>Add Deal</span>
                                                                                    </div>
                                                                                </button>
                                                                            )}
                                                                            {hasFullAccess && (
                                                                                <>
                                                                                    {pIndex > 0 && (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="pipeline-card-menu-item"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                setActiveColMenuId(null);
                                                                                                handleShiftStageOrder(pIndex, pIndex - 1);
                                                                                            }}
                                                                                            title={`Move "${pipeline.name}" left`}
                                                                                        >
                                                                                            <div className="pipeline-card-menu-item-left">
                                                                                                <ArrowLeft size={13} style={{ color: '#0284c7' }} />
                                                                                                <span>Move Left (←)</span>
                                                                                            </div>
                                                                                        </button>
                                                                                    )}
                                                                                    {pIndex < visiblePipelines.length - 1 && (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="pipeline-card-menu-item"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                setActiveColMenuId(null);
                                                                                                handleShiftStageOrder(pIndex, pIndex + 1);
                                                                                            }}
                                                                                            title={`Move "${pipeline.name}" right`}
                                                                                        >
                                                                                            <div className="pipeline-card-menu-item-left">
                                                                                                <ArrowRight size={13} style={{ color: '#0284c7' }} />
                                                                                                <span>Move Right (→)</span>
                                                                                            </div>
                                                                                        </button>
                                                                                    )}
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-card-menu-item"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            setActiveColMenuId(null);
                                                                                            setReorderStagesList([...visiblePipelines]);
                                                                                            setShowReorderStagesModal(true);
                                                                                        }}
                                                                                        title="Reorder all stages in list"
                                                                                    >
                                                                                        <div className="pipeline-card-menu-item-left">
                                                                                            <ArrowUpDown size={13} style={{ color: '#0284c7' }} />
                                                                                            <span>Reorder Stages</span>
                                                                                        </div>
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-card-menu-item"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            setActiveColMenuId(null);
                                                                                            setEditingStageModal({
                                                                                                type: 'pipeline',
                                                                                                id: colKey,
                                                                                                name: pipeline.name,
                                                                                                originalName: pipeline.name,
                                                                                                index: pIndex
                                                                                            });
                                                                                        }}
                                                                                    >
                                                                                        <div className="pipeline-card-menu-item-left">
                                                                                            <Edit2 size={13} style={{ color: '#64748b' }} />
                                                                                            <span>Edit Stage</span>
                                                                                        </div>
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                            {hasFullAccess && (
                                                                                <>
                                                                                    <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-card-menu-item danger"
                                                                                        onClick={(e) => handleDeletePipeline(colKey, e, pIndex, pipeline.name)}
                                                                                    >
                                                                                        <div className="pipeline-card-menu-item-left">
                                                                                            <Trash2 size={13} />
                                                                                            <span>Delete Stage</span>
                                                                                        </div>
                                                                                    </button>
                                                                                </>
                                                                            )}

                                                                            {pipelineDeals.length > 0 && (
                                                                                <>
                                                                                    <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                                                                                    {canUpdate && (
                                                                                        <>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="pipeline-card-menu-item"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    setColMoveDealSubmenuStageId(colMoveDealSubmenuStageId === pipeline.id ? null : pipeline.id);
                                                                                                }}
                                                                                                title={`Move all ${pipelineDeals.length} deals to another stage`}
                                                                                            >
                                                                                                <div className="pipeline-card-menu-item-left">
                                                                                                    <ArrowRightLeft size={13} style={{ color: '#0284c7' }} />
                                                                                                    <span>Move All Deals ({pipelineDeals.length})</span>
                                                                                                </div>
                                                                                                <ChevronRight size={12} style={{ transform: colMoveDealSubmenuStageId === pipeline.id ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }} />
                                                                                            </button>

                                                                                            {colMoveDealSubmenuStageId === pipeline.id && (
                                                                                                <div style={{ background: '#f8fafc', padding: '4px 0', borderRadius: '6px', margin: '2px 0 4px 0', border: '1px solid #e2e8f0' }}>
                                                                                                    <div style={{ padding: '3px 12px', fontSize: '10.5px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                                                                                                        Move {pipelineDeals.length} deals to:
                                                                                                    </div>
                                                                                                    {visiblePipelines
                                                                                                        .filter(p => (p.name || '').toLowerCase().trim() !== (pipeline.name || '').toLowerCase().trim())
                                                                                                        .map(p => (
                                                                                                            <button
                                                                                                                key={p.id}
                                                                                                                type="button"
                                                                                                                className="pipeline-card-menu-item"
                                                                                                                style={{ paddingLeft: '20px', fontSize: '12px' }}
                                                                                                                onClick={() => {
                                                                                                                    setActiveColMenuId(null);
                                                                                                                    setColMoveDealSubmenuStageId(null);
                                                                                                                    handleBulkMoveStage(p.name, pipelineDeals.map(d => d._id));
                                                                                                                }}
                                                                                                            >
                                                                                                                <span className={`pipeline-menu-indicator ${p.badgeClass || 'new'}`} />
                                                                                                                <span>{p.name}</span>
                                                                                                            </button>
                                                                                                        ))}
                                                                                                </div>
                                                                                            )}
                                                                                        </>
                                                                                    )}
                                                                                    {canDelete && (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="pipeline-card-menu-item danger"
                                                                                            onClick={() => {
                                                                                                setActiveColMenuId(null);
                                                                                                setColMoveDealSubmenuStageId(null);
                                                                                                handleBulkDeleteDeals(pipelineDeals.map(d => d._id));
                                                                                            }}
                                                                                            title={`Delete all ${pipelineDeals.length} deals in this stage`}
                                                                                        >
                                                                                            <div className="pipeline-card-menu-item-left">
                                                                                                <Trash2 size={13} />
                                                                                                <span>Delete All Deals ({pipelineDeals.length})</span>
                                                                                            </div>
                                                                                        </button>
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>

                                                    {/* Deal Cards Scroll Container */}
                                                    <div
                                                        className="pipeline-cards-list"
                                                        onDragOver={e => handleDragOver(e, colKey)}
                                                        onDrop={e => handleDrop(e, pipeline.name)}
                                                    >
                                                        {pipelineDeals.map(deal => {
                                                            const isSelected = selectedKanbanDealIds.includes(deal._id);
                                                            return (
                                                                <div
                                                                    key={deal._id}
                                                                    className={`pipeline-card ${isSelected ? 'selected' : ''} ${activeMenuId === deal._id ? 'menu-active' : ''} ${hoveredActivityDealId === deal._id ? 'activity-hovered' : ''} ${draggedDealId === deal._id ? 'dragging' : ''}`}
                                                                    draggable={canUpdate}
                                                                    onDragStart={e => handleDragStart(e, deal._id)}
                                                                    onDragEnd={handleDragEnd}
                                                                    onDoubleClick={(e) => handleCardDoubleClick(deal, pipelineDeals, pipeline.name, e)}
                                                                    title="Double-click to select all deals in this stage"
                                                                >
                                                                    {/* Card Top: Checkbox, Avatar, Name, Location, 3-dots */}
                                                                    <div className="pipeline-card-top">
                                                                        <div
                                                                            className={`pipeline-card-select-checkbox ${isSelected ? 'selected' : ''} ${selectedKanbanDealIds.length > 0 ? 'visible' : ''}`}
                                                                            onClick={(e) => handleToggleDealSelection(deal._id, e)}
                                                                            title={isSelected ? "Deselect deal (Double-click card to select all in stage)" : "Select deal (Double-click card to select all in stage)"}
                                                                        >
                                                                            {isSelected && <Check size={11} strokeWidth={3.5} />}
                                                                        </div>
                                                                        <div
                                                                            className="pipeline-card-person"
                                                                            style={{ cursor: 'pointer', alignItems: 'flex-start', minWidth: 0, overflow: 'hidden', flex: 1 }}
                                                                            onClick={(e) => handleDealCardClick(deal._id, e)}
                                                                        >
                                                                            {deal.avatar ? (
                                                                                <img
                                                                                    src={deal.avatar}
                                                                                    alt={deal.clientName}
                                                                                    className="pipeline-card-avatar"
                                                                                    onError={e => {
                                                                                        e.target.style.display = 'none';
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <div className="pipeline-card-avatar">
                                                                                    {deal.clientName.charAt(0)}
                                                                                </div>
                                                                            )}
                                                                            <div className="pipeline-card-name-wrap" style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                                                                                <h4 className="pipeline-card-client-name" style={{ margin: 0 }}>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-card-client-name-link"
                                                                                        onClick={(e) => handleDealCardClick(deal._id, e)}
                                                                                        title="View activity and deal details"
                                                                                    >
                                                                                        {deal.clientName}
                                                                                    </button>
                                                                                </h4>

                                                                                {/* Origin + Status badge directly below deal name */}
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px', flexWrap: 'wrap' }}>
                                                                                    {(() => {
                                                                                        const origin = getDealContactOrigin(deal);
                                                                                        return (
                                                                                            <>
                                                                                                {origin && (
                                                                                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '3.5px' }}>
                                                                                                        <Globe size={11} style={{ color: origin === 'International' ? '#ea580c' : '#0284c7', flexShrink: 0 }} />
                                                                                                        <span>{origin}</span>
                                                                                                    </span>
                                                                                                )}
                                                                                                <span
                                                                                                    className={`pipeline-stage-badge ${(() => {
                                                                                                        const st = (deal.stage || deal.status || 'New').toLowerCase();
                                                                                                        if (st === 'won' || st === 'closed won' || st === 'active' || st === 'open') return 'won';
                                                                                                        if (st === 'lost' || st === 'closed lost' || st === 'unqualified' || st === 'spam') return 'lost';
                                                                                                        if (st === 'hot' || st === 'negotiation' || st === 'in progress' || st === 'contacted' || st === 'proposal sent') return 'hot';
                                                                                                        return 'new';
                                                                                                    })()}`}
                                                                                                    style={{ fontSize: '8px', padding: '0.8px 5px', borderRadius: '999px', fontWeight: 600, letterSpacing: '0.2px', lineHeight: '1.1' }}
                                                                                                >
                                                                                                    {deal.stage || deal.status || 'New'}
                                                                                                </span>
                                                                                            </>
                                                                                        );
                                                                                    })()}
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {/* Options Button & Context Menu */}
                                                                        <div style={{ position: 'relative' }}>
                                                                            <button
                                                                                className="pipeline-card-more-btn"
                                                                                onClick={e => {
                                                                                    e.stopPropagation();
                                                                                    if (activeMenuId === deal._id) {
                                                                                        setActiveMenuId(null);
                                                                                        setMoveSubmenuDealId(null);
                                                                                    } else {
                                                                                        setActiveMenuId(deal._id);
                                                                                        setMoveSubmenuDealId(null);
                                                                                    }
                                                                                }}
                                                                                title="Deal options"
                                                                            >
                                                                                <MoreHorizontal size={16} />
                                                                            </button>

                                                                            {/* Main 3-dots Actions Menu Card */}
                                                                            {activeMenuId === deal._id && moveSubmenuDealId !== deal._id && (
                                                                                <div
                                                                                    className="pipeline-card-dropdown-menu"
                                                                                    onClick={e => e.stopPropagation()}
                                                                                >
                                                                                    {canUpdate && (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="pipeline-card-menu-item"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                setMoveSubmenuDealId(deal._id);
                                                                                            }}
                                                                                        >
                                                                                            <div className="pipeline-card-menu-item-left">
                                                                                                <ArrowRightLeft size={14} style={{ color: '#0284c7' }} />
                                                                                                <span>Move</span>
                                                                                            </div>
                                                                                            <ChevronRight size={13} style={{ color: '#94a3b8' }} />
                                                                                        </button>
                                                                                    )}

                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-card-menu-item"
                                                                                        onClick={(e) => handleOpenViewDeal(deal, e)}
                                                                                    >
                                                                                        <div className="pipeline-card-menu-item-left">
                                                                                            <Eye size={14} style={{ color: '#64748b' }} />
                                                                                            <span>View Deal</span>
                                                                                        </div>
                                                                                    </button>

                                                                                    {canUpdate && (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="pipeline-card-menu-item"
                                                                                            onClick={(e) => handleOpenEditDeal(deal, e)}
                                                                                        >
                                                                                            <div className="pipeline-card-menu-item-left">
                                                                                                <Edit2 size={14} style={{ color: '#64748b' }} />
                                                                                                <span>Edit Deal</span>
                                                                                            </div>
                                                                                        </button>
                                                                                    )}

                                                                                    {canDelete && (
                                                                                        <>
                                                                                            <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                                                                                            <button
                                                                                                type="button"
                                                                                                className="pipeline-card-menu-item danger"
                                                                                                onClick={e => handleDeleteDeal(deal._id, e)}
                                                                                            >
                                                                                                <div className="pipeline-card-menu-item-left">
                                                                                                    <Trash2 size={14} />
                                                                                                    <span>Delete Deal</span>
                                                                                                </div>
                                                                                            </button>
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            )}

                                                                            {/* Nested Move Stage Submenu */}
                                                                            {activeMenuId === deal._id && moveSubmenuDealId === deal._id && (
                                                                                <div
                                                                                    className="pipeline-card-dropdown-menu submenu"
                                                                                    onClick={e => e.stopPropagation()}
                                                                                >
                                                                                    <div className="pipeline-submenu-header">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="pipeline-submenu-back"
                                                                                            onClick={() => setMoveSubmenuDealId(null)}
                                                                                        >
                                                                                            <ChevronsLeft size={12} />
                                                                                            <span>Back</span>
                                                                                        </button>
                                                                                        <span className="pipeline-submenu-title">Move Pipeline</span>
                                                                                    </div>

                                                                                    {visiblePipelines.map(p => (
                                                                                        <button
                                                                                            key={p.id}
                                                                                            type="button"
                                                                                            className="pipeline-card-menu-item"
                                                                                            onClick={e => handleMovePipeline(deal._id, p.name, e)}
                                                                                        >
                                                                                            <span className={`pipeline-menu-indicator ${p.badgeClass || 'new'}`} />
                                                                                            <span>{p.name}</span>
                                                                                        </button>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {/* Card Body Fields */}
                                                                    <div className="pipeline-card-body">
                                                                        <div className="pipeline-card-field">
                                                                            <span className="pipeline-card-label">Deal value</span>
                                                                            <span className="pipeline-card-val-strong" style={{ color: '#0284c7' }}>
                                                                                {deal.currency || '$'}{Number(deal.dealValue).toLocaleString()}
                                                                            </span>
                                                                        </div>

                                                                        <div className="pipeline-card-field">
                                                                            <span className="pipeline-card-label">Expected close date</span>
                                                                            <span className="pipeline-card-val">
                                                                                {formatDateDDMMYYYY(deal.expectedCloseDate)}
                                                                            </span>
                                                                        </div>

                                                                        <div className="pipeline-card-field">
                                                                            <span className="pipeline-card-label">Deal Owner</span>
                                                                            <span className="pipeline-card-val">{deal.salesOwner}</span>
                                                                        </div>

                                                                        {(() => {
                                                                            const dealPhone = getDealContactPhone(deal);
                                                                            const displayContact = dealPhone || deal.associatedContact || deal.email;
                                                                            if (!displayContact) return null;

                                                                            return (
                                                                                <div className="pipeline-card-field">
                                                                                    <span className="pipeline-card-label">Contact</span>
                                                                                    <span
                                                                                        className="pipeline-card-val"
                                                                                        style={{
                                                                                            color: '#334155',
                                                                                            fontWeight: 500,
                                                                                            cursor: 'default',
                                                                                            textDecoration: 'none',
                                                                                            display: 'inline-block'
                                                                                        }}
                                                                                    >
                                                                                        {displayContact}
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })()}
                                                                    </div>

                                                                    {/* Card Bottom Actions */}
                                                                    <div className="pipeline-card-actions">
                                                                        <div className="pipeline-card-actions-left">
                                                                            <button
                                                                                type="button"
                                                                                className="pipeline-card-action-circle"
                                                                                title="Milestones & Payment Records"
                                                                                onClick={() => {
                                                                                    setMilestoneModalDealId(deal._id);
                                                                                    setShowMilestoneModal(true);
                                                                                }}
                                                                            >
                                                                                <CreditCard size={12} />
                                                                            </button>
                                                                            <a
                                                                                href={`mailto:${deal.email || 'client@example.com'}`}
                                                                                className="pipeline-card-action-circle"
                                                                                title="Send Mail"
                                                                            >
                                                                                <Mail size={12} />
                                                                            </a>
                                                                            <button
                                                                                type="button"
                                                                                className="pipeline-card-action-circle"
                                                                                title="Send Message"
                                                                                onClick={() => alert(`Message ${deal.clientName}`)}
                                                                            >
                                                                                <MessageSquare size={12} />
                                                                            </button>
                                                                        </div>

                                                                        <button
                                                                            type="button"
                                                                            className={`pipeline-card-view-activity-pill ${hoveredActivityDealId === deal._id ? 'active' : ''}`}
                                                                            onMouseEnter={(e) => handleActivityMouseEnter(deal._id, 'deal', e)}
                                                                            onMouseLeave={handleActivityMouseLeave}
                                                                            onClick={() => {
                                                                                setHoveredActivityDealId(null);
                                                                                setHoveredActivityContactId(null);
                                                                                setDetailedDealId(deal._id);
                                                                            }}
                                                                            title="View full activity timeline and deal details"
                                                                        >
                                                                            <span>View activity</span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>

                                                    {/* Column Footer with Approx. Amount in INR converted via Frankfurter v2 */}
                                                    <div
                                                        className="pipeline-column-footer"
                                                        title="These rates might be fluctuating as per the live rates"
                                                    >
                                                        <span className="pipeline-stage-footer-label" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                            <span>Approximate Value</span>
                                                            <Info size={11} style={{ color: '#94a3b8' }} />
                                                        </span>
                                                        <span
                                                            className="pipeline-stage-footer-amount"
                                                            title="These rates might be fluctuating as per the live rates"
                                                        >
                                                            ₹{totalStageAmountINR.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )
                            ) : (
                                /* Table List View */
                                filteredDeals.length === 0 ? (
                                    <div className="pipeline-empty-state-card" style={{ maxWidth: '480px', margin: '40px auto' }}>
                                        <div className="pipeline-empty-icon-wrap" style={{ background: '#f0f9ff', color: '#0284c7' }}>
                                            <Briefcase size={32} />
                                        </div>
                                        <h3 className="pipeline-empty-state-title">No Deals Found</h3>
                                        <p className="pipeline-empty-state-desc">
                                            No deals match your current filter criteria. Click below to create a new deal.
                                        </p>
                                        <button
                                            type="button"
                                            className="pipeline-empty-state-action-btn"
                                            onClick={() => handleOpenAddDeal()}
                                        >
                                            <Plus size={14} />
                                            <span>Add Deal</span>
                                        </button>
                                    </div>
                                ) : (
                                    <div className="pipeline-table-view">
                                        <div className="pipeline-table-card">
                                            <table className="pipeline-table">
                                                <thead>
                                                    <tr>
                                                        <th>Client / Contact</th>
                                                        <th>Deal Name</th>
                                                        <th>Location</th>
                                                        <th>Value</th>
                                                        <th>Expected Close</th>
                                                        <th>Owner</th>
                                                        <th>Stage</th>
                                                        <th>Status</th>
                                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredDeals.map(deal => (
                                                        <tr key={deal._id}>
                                                            <td>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    <div className="pipeline-card-avatar" style={{ width: '28px', height: '28px' }}>
                                                                        {deal.clientName.charAt(0)}
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="pipeline-card-client-name-link"
                                                                        onClick={() => setDetailedDealId(deal._id)}
                                                                        title="View deal activity and details"
                                                                    >
                                                                        {deal.clientName}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-card-client-name-link"
                                                                    onClick={() => setDetailedDealId(deal._id)}
                                                                    title="View deal activity and details"
                                                                >
                                                                    {deal.dealName || deal.clientName}
                                                                </button>
                                                            </td>
                                                            <td>{deal.location}</td>
                                                            <td style={{ fontWeight: 700, color: '#0284c7' }}>{deal.currency || '$'} {Number(deal.dealValue).toLocaleString()}</td>
                                                            <td>{formatDateDDMMYYYY(deal.expectedCloseDate)}</td>
                                                            <td>{deal.salesOwner}</td>
                                                            <td>
                                                                <span className="pipeline-stage-badge" style={{ fontSize: '11px', background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }}>
                                                                    {deal.pipeline || 'Sales Pipeline'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className={`pipeline-stage-badge ${deal.stage === 'New'
                                                                        ? 'new'
                                                                        : deal.stage === 'Negotiation'
                                                                            ? 'negotiation'
                                                                            : deal.stage === 'Won'
                                                                                ? 'won'
                                                                                : 'lost'
                                                                        }`}
                                                                >
                                                                    {deal.stage}
                                                                </span>
                                                            </td>
                                                            <td style={{ textAlign: 'right' }}>
                                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                                                                    <button
                                                                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}
                                                                        onClick={() => {
                                                                            const nextStage = deal.stage === 'New' ? 'Negotiation' : deal.stage === 'Negotiation' ? 'Won' : 'New';
                                                                            handleMoveStage(deal._id, nextStage);
                                                                        }}
                                                                    >
                                                                        Advance
                                                                    </button>
                                                                    <button
                                                                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #fee2e2', background: '#fef2f2', color: '#ef4444', cursor: 'pointer' }}
                                                                        onClick={e => handleDeleteDeal(deal._id, e)}
                                                                    >
                                                                        <Trash2 size={12} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )
                            )}

                            {/* Floating Bottom Bulk Action Toolbar for Multi-Selected Cards */}
                            {selectedKanbanDealIds.length > 0 && viewMode === 'kanban' && createPortal(
                                <div className="pipeline-kanban-bulk-bar" onClick={e => e.stopPropagation()}>
                                    <div className="pipeline-kanban-bulk-info">
                                        <div className="pipeline-kanban-bulk-badge">
                                            <Check size={14} strokeWidth={3} />
                                            <span>{selectedKanbanDealIds.length} {selectedKanbanDealIds.length === 1 ? 'deal' : 'deals'} selected</span>
                                        </div>
                                    </div>

                                    <div className="pipeline-kanban-bulk-actions">
                                        {/* Bulk Move Stage Dropdown */}
                                        {canUpdate && (
                                            <div style={{ position: 'relative' }}>
                                                <button
                                                    type="button"
                                                    className="pipeline-kanban-bulk-btn primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsBulkMoveDropdownOpen(prev => !prev);
                                                    }}
                                                    title="Move selected deals to another stage"
                                                >
                                                    <ArrowRightLeft size={13} />
                                                    <span>Move Stage</span>
                                                    <ChevronDown size={12} style={{ transform: isBulkMoveDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
                                                </button>

                                                {isBulkMoveDropdownOpen && (
                                                    <div className="pipeline-card-dropdown-menu pipeline-bulk-move-menu" onClick={e => e.stopPropagation()}>
                                                        <div className="pipeline-submenu-header">
                                                            <span className="pipeline-submenu-title">Move Selected To</span>
                                                        </div>
                                                        {visiblePipelines.map(p => (
                                                            <button
                                                                key={p.id}
                                                                type="button"
                                                                className="pipeline-card-menu-item"
                                                                onClick={() => handleBulkMoveStage(p.name)}
                                                            >
                                                                <span className={`pipeline-menu-indicator ${p.badgeClass || 'new'}`} />
                                                                <span>{p.name}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Bulk Delete */}
                                        {canDelete && (
                                            <button
                                                type="button"
                                                className="pipeline-kanban-bulk-btn danger"
                                                onClick={() => handleBulkDeleteDeals()}
                                                title="Delete selected deals"
                                            >
                                                <Trash2 size={13} />
                                                <span>Delete</span>
                                            </button>
                                        )}

                                        {/* Bulk Export */}
                                        <button
                                            type="button"
                                            className="pipeline-kanban-bulk-btn"
                                            style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.3)' }}
                                            onClick={handleExport}
                                            title="Export selected deals to Excel"
                                        >
                                            <Download size={13} />
                                            <span>Export</span>
                                        </button>

                                        {/* Deselect All */}
                                        <button
                                            type="button"
                                            className="pipeline-kanban-bulk-btn cancel"
                                            onClick={() => {
                                                setSelectedKanbanDealIds([]);
                                                setIsBulkMoveDropdownOpen(false);
                                            }}
                                            title="Clear selection"
                                        >
                                            <X size={13} />
                                            <span>Deselect All</span>
                                        </button>
                                    </div>
                                </div>,
                                document.body
                            )}
                        </>
                    )
                )}

                {/* CONTACTS TAB VIEW (Full Interactive Contacts Management & 3-Section Detail View - Admin Only) */}
                {isAdmin && activeTab === 'contacts' && (
                    (detailedContactId && isLoading) ? (
                        renderDetailSkeleton()
                    ) : detailedContact ? (
                        <div className="pipeline-detail-view-container">
                            {/* Top Header Bar */}
                            <div className="pipeline-detail-top-nav">
                                <div className="pipeline-detail-nav-left">
                                    <button
                                        type="button"
                                        className="pipeline-detail-back-btn"
                                        onClick={() => {
                                            setDetailedContactId(null);
                                            setDetailedDealId(null);
                                        }}
                                        title="Back to Contacts Board"
                                    >
                                        <ChevronsLeft size={16} />
                                        <span>Back to Contacts</span>
                                    </button>

                                    <div className="pipeline-detail-title-group">
                                        <div className="pipeline-card-avatar" style={{ width: '38px', height: '38px', fontSize: '15px' }}>
                                            {detailedContact.firstName ? detailedContact.firstName.charAt(0).toUpperCase() : 'C'}
                                        </div>
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <h2 className="pipeline-detail-deal-title">{`${detailedContact.firstName || ''} ${detailedContact.lastName || ''}`.trim() || 'Unnamed Contact'}</h2>
                                                {/* Lead Stage Badge (Refined, smaller, professional) */}
                                                {(() => {
                                                    const stageName = detailedContact.leadStage || 'Lead';
                                                    const stLower = stageName.toLowerCase();
                                                    let stageBg = '#f5f3ff';
                                                    let stageColor = '#6d28d9';
                                                    let stageBorder = '#ddd6fe';
                                                    if (stLower.includes('hot')) {
                                                        stageBg = '#fff7ed'; stageColor = '#c2410c'; stageBorder = '#fed7aa';
                                                    } else if (stLower.includes('won') || stLower.includes('customer')) {
                                                        stageBg = '#ecfdf5'; stageColor = '#047857'; stageBorder = '#a7f3d0';
                                                    } else if (stLower.includes('prop') || stLower.includes('qual')) {
                                                        stageBg = '#eff6ff'; stageColor = '#1d4ed8'; stageBorder = '#bfdbfe';
                                                    } else if (stLower.includes('cold') || stLower.includes('lost')) {
                                                        stageBg = '#f8fafc'; stageColor = '#475569'; stageBorder = '#cbd5e1';
                                                    }
                                                    return (
                                                        <span
                                                            style={{
                                                                fontSize: '10.5px',
                                                                padding: '1.5px 8px',
                                                                background: stageBg,
                                                                color: stageColor,
                                                                border: `1px solid ${stageBorder}`,
                                                                borderRadius: '999px',
                                                                fontWeight: 600,
                                                                letterSpacing: '0.2px',
                                                                lineHeight: '1.2',
                                                                display: 'inline-flex',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            {stageName}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                            <span style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px', flexWrap: 'wrap' }}>
                                                {detailedContact.location && <><MapPin size={11} /> {detailedContact.location} • </>}
                                                {(detailedContact.company && detailedContact.company !== 'Direct Client') ? (
                                                    <>
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 600, color: '#334155' }}>
                                                            <Building2 size={12} style={{ color: '#0284c7', flexShrink: 0 }} />
                                                            {detailedContact.company}
                                                        </span>
                                                        <span style={{ color: '#94a3b8' }}>•</span>
                                                    </>
                                                ) : null}
                                                {/* Lead Status Badge */}
                                                {(() => {
                                                    const st = (detailedContact.leadStatus || 'New').trim();
                                                    const stLower = st.toLowerCase();
                                                    let badgeBg = '#e0f2fe';
                                                    let badgeColor = '#0369a1';
                                                    let badgeBorder = '#bae6fd';
                                                    let dotColor = '#0284c7';

                                                    if (stLower === 'won' || stLower === 'qualified' || stLower === 'active' || stLower === 'open') {
                                                        badgeBg = '#dcfce7'; badgeColor = '#15803d'; badgeBorder = '#86efac'; dotColor = '#16a34a';
                                                    } else if (stLower === 'lost' || stLower === 'unqualified' || stLower === 'spam') {
                                                        badgeBg = '#ffe4e6'; badgeColor = '#be123c'; badgeBorder = '#fecdd3'; dotColor = '#e11d48';
                                                    } else if (stLower === 'hot' || stLower === 'in progress' || stLower === 'contacted') {
                                                        badgeBg = '#fef3c7'; badgeColor = '#b45309'; badgeBorder = '#fde68a'; dotColor = '#d97706';
                                                    }

                                                    return (
                                                        <span
                                                            style={{
                                                                fontSize: '9px',
                                                                padding: '1px 6.5px',
                                                                background: badgeBg,
                                                                color: badgeColor,
                                                                border: `1px solid ${badgeBorder}`,
                                                                borderRadius: '999px',
                                                                fontWeight: 600,
                                                                letterSpacing: '0.25px',
                                                                lineHeight: '1.2',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '3.5px'
                                                            }}
                                                        >
                                                            <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: dotColor, display: 'inline-block', flexShrink: 0 }} />
                                                            {st}
                                                        </span>
                                                    );
                                                })()}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="pipeline-detail-nav-right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <NotificationBell filterType="CRM" />
                                </div>
                            </div>

                            {/* 3-Section Split Body */}
                            <div className="pipeline-detail-split-body three-sections">
                                {/* 1. LEFT SECTION: Contact Details, Project Details & Payment Details */}
                                <div className="pipeline-detail-col-deal">
                                    <div className="pipeline-detail-card-panel">
                                        {/* Accordion 1: Contact Details */}
                                        <div className="pipeline-accordion-section">
                                            <div
                                                className="pipeline-accordion-header"
                                                onClick={() => setContactAccordionState(prev => ({ ...prev, contact: !prev.contact }))}
                                            >
                                                <div className="pipeline-accordion-title-wrap">
                                                    <span className="pipeline-accordion-title-icon">
                                                        <User size={12} strokeWidth={2.5} />
                                                    </span>
                                                    <span>Contact Details</span>
                                                </div>

                                                <div className="pipeline-accordion-actions" onClick={e => e.stopPropagation()}>
                                                    <div style={{ position: 'relative' }}>
                                                        <button
                                                            type="button"
                                                            className="pipeline-card-more-btn"
                                                            style={{ padding: '2px 4px', border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setIsContactDetailsMenuOpen(prev => !prev);
                                                            }}
                                                            title="Contact Options"
                                                        >
                                                            <MoreHorizontal size={16} />
                                                        </button>

                                                        {isContactDetailsMenuOpen && (
                                                            <div
                                                                className="pipeline-card-dropdown-menu"
                                                                style={{ top: 'calc(100% + 4px)', right: '0', minWidth: '150px', zIndex: 90 }}
                                                                onClick={e => e.stopPropagation()}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-card-menu-item"
                                                                    onClick={() => {
                                                                        setIsContactDetailsMenuOpen(false);
                                                                        handleOpenEditContact(detailedContact);
                                                                    }}
                                                                >
                                                                    <div className="pipeline-card-menu-item-left">
                                                                        <Edit2 size={13} style={{ color: '#64748b' }} />
                                                                        <span>Edit</span>
                                                                    </div>
                                                                </button>
                                                                <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-card-menu-item danger"
                                                                    onClick={(e) => {
                                                                        setIsContactDetailsMenuOpen(false);
                                                                        handleDeleteContact(detailedContact._id, e);
                                                                        setDetailedContactId(null);
                                                                    }}
                                                                >
                                                                    <div className="pipeline-card-menu-item-left">
                                                                        <Trash2 size={13} />
                                                                        <span>Delete</span>
                                                                    </div>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className={`pipeline-accordion-toggle-btn ${contactAccordionState.contact ? 'open' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setContactAccordionState(prev => ({ ...prev, contact: !prev.contact }));
                                                        }}
                                                        title={contactAccordionState.contact ? 'Collapse Section' : 'Expand Section'}
                                                    >
                                                        <ChevronDown size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className={`pipeline-accordion-body ${contactAccordionState.contact ? '' : 'collapsed'}`}>
                                                <div className="pipeline-detail-kv-grid">
                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">First Name</span>
                                                        <span className="pipeline-detail-v-val">{detailedContact.firstName || '—'}</span>
                                                    </div>
                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Last Name</span>
                                                        <span className="pipeline-detail-v-val">{detailedContact.lastName || '—'}</span>
                                                    </div>
                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Email</span>
                                                        <span className="pipeline-detail-v-val">
                                                            <a href={`mailto:${detailedContact.email}`} style={{ color: '#0284c7', textDecoration: 'none' }}>
                                                                {detailedContact.email || '—'}
                                                            </a>
                                                        </span>
                                                    </div>
                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Phone</span>
                                                        <span className="pipeline-detail-v-val">
                                                            <a href={`tel:${(detailedContact.countryCode || '') + (detailedContact.phone || '')}`} style={{ color: '#0f172a', textDecoration: 'none' }}>
                                                                {detailedContact.phone ? formatPhoneNumber(detailedContact.phone, detailedContact.countryCode) : '—'}
                                                            </a>
                                                        </span>
                                                    </div>
                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Company</span>
                                                        <span className="pipeline-detail-v-val">
                                                            {(detailedContact.company && detailedContact.company !== 'Direct Client') ? detailedContact.company : '—'}
                                                        </span>
                                                    </div>
                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Origin</span>
                                                        <span className="pipeline-detail-v-val">
                                                            <span className={`pipeline-stage-badge ${detailedContact.origin === 'International' ? 'hot' : 'new'}`} style={{ padding: '2px 8px', fontSize: '11px' }}>
                                                                {detailedContact.origin || 'Domestic'}
                                                            </span>
                                                        </span>
                                                    </div>

                                                    {/* Interactive Lead Stage Pill Badge with Dropdown */}
                                                    <div className="pipeline-detail-kv-row" style={{ position: 'relative' }}>
                                                        <span className="pipeline-detail-k-label">Lead Stage</span>
                                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                                            <button
                                                                type="button"
                                                                className="pipeline-pill-badge-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setIsContactLeadStageDropdownOpen(prev => !prev);
                                                                }}
                                                                title="Click to change lead stage"
                                                            >
                                                                <span>{detailedContact.leadStage || 'Lead Pipeline'}</span>
                                                                <ChevronDown size={11} />
                                                            </button>

                                                            {isContactLeadStageDropdownOpen && (
                                                                <div
                                                                    className="pipeline-card-dropdown-menu"
                                                                    style={{ top: 'calc(100% + 4px)', right: 0, minWidth: '180px', zIndex: 120 }}
                                                                    onClick={e => e.stopPropagation()}
                                                                >
                                                                    <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                        Select Lead Stage
                                                                    </div>
                                                                    {effectiveContactStages.map(s => {
                                                                        const isSelected = (detailedContact.leadStage || 'Lead').toLowerCase() === s.name.toLowerCase();
                                                                        return (
                                                                            <button
                                                                                key={s.id}
                                                                                type="button"
                                                                                className={`pipeline-card-menu-item ${isSelected ? 'active' : ''}`}
                                                                                onClick={() => {
                                                                                    setContacts(prev => prev.map(c => c._id === detailedContact._id ? { ...c, leadStage: s.name } : c));
                                                                                    setIsContactLeadStageDropdownOpen(false);
                                                                                }}
                                                                            >
                                                                                <span style={{ fontWeight: isSelected ? 700 : 500 }}>{s.name}</span>
                                                                                {isSelected && (
                                                                                    <CheckCircle size={13} style={{ color: '#0284c7', marginLeft: 'auto' }} />
                                                                                )}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Interactive Lead Status Pill Badge with Dropdown */}
                                                    <div className="pipeline-detail-kv-row" style={{ position: 'relative' }}>
                                                        <span className="pipeline-detail-k-label">Lead Status</span>
                                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                                            <button
                                                                type="button"
                                                                className={`pipeline-stage-pill-badge-btn ${(detailedContact.leadStatus || 'New').toLowerCase() === 'open' || (detailedContact.leadStatus || 'New').toLowerCase() === 'won' ? 'won' : (detailedContact.leadStatus || 'New').toLowerCase() === 'hot' || (detailedContact.leadStatus || 'New').toLowerCase() === 'in progress' ? 'hot' : (detailedContact.leadStatus || 'New').toLowerCase() === 'unqualified' || (detailedContact.leadStatus || 'New').toLowerCase() === 'lost' ? 'lost' : 'new'}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setIsContactLeadStatusDropdownOpen(prev => !prev);
                                                                }}
                                                                title="Click to change lead status"
                                                            >
                                                                <span>{detailedContact.leadStatus || 'New'}</span>
                                                                <ChevronDown size={11} />
                                                            </button>

                                                            {isContactLeadStatusDropdownOpen && (
                                                                <div
                                                                    className="pipeline-card-dropdown-menu"
                                                                    style={{ top: 'calc(100% + 4px)', right: 0, minWidth: '170px', zIndex: 120 }}
                                                                    onClick={e => e.stopPropagation()}
                                                                >
                                                                    <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                        Select Lead Status
                                                                    </div>
                                                                    {['New', 'Open', 'In Progress', 'Contacted', 'Qualified', 'Unqualified'].map(st => {
                                                                        const isSelected = (detailedContact.leadStatus || 'New').toLowerCase() === st.toLowerCase();
                                                                        return (
                                                                            <button
                                                                                key={st}
                                                                                type="button"
                                                                                className={`pipeline-card-menu-item ${isSelected ? 'active' : ''}`}
                                                                                onClick={() => {
                                                                                    setContacts(prev => prev.map(c => c._id === detailedContact._id ? { ...c, leadStatus: st } : c));
                                                                                    setIsContactLeadStatusDropdownOpen(false);
                                                                                }}
                                                                            >
                                                                                <span style={{ fontWeight: isSelected ? 700 : 500 }}>{st}</span>
                                                                                {isSelected && (
                                                                                    <CheckCircle size={13} style={{ color: '#0284c7', marginLeft: 'auto' }} />
                                                                                )}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Contact Source</span>
                                                        <span className="pipeline-detail-v-val">{detailedContact.contactSource || 'Direct Search'}</span>
                                                    </div>

                                                    <div className="pipeline-detail-kv-row" style={{ position: 'relative' }}>
                                                        <span className="pipeline-detail-k-label">Added By</span>
                                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                                            {(() => {
                                                                const addedByName = detailedContact.addedBy || detailedContact.owner || 'Admin System';
                                                                const emp = getAddedByEmployee(addedByName);
                                                                const empFullName = (emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '') || emp?.name || addedByName;
                                                                const empDesignation = emp?.designation || (typeof emp?.role === 'string' ? emp?.role : emp?.role?.name) || (addedByName === 'Admin System' ? 'System Administrator' : 'Operations Specialist');
                                                                const isEmpActive = emp ? (emp.isActive !== false && emp.status !== 'Inactive') : true;
                                                                const empEmail = emp?.officialEmail || emp?.email || (addedByName === 'Admin System' ? 'admin@bhoomi.com' : `${addedByName.toLowerCase().replace(/\s+/g, '.')}@bhoomi.com`);
                                                                const empPhone = emp?.phone || emp?.mobile || emp?.phoneNumber || emp?.alternatePhone || (addedByName === 'Admin System' ? '+91 98765 43210' : '—');
                                                                const empDepartment = emp?.department || emp?.dept || (addedByName === 'Admin System' ? 'Administration & Operations' : 'Sales & Support');
                                                                const empAddedDate = formatISTDateTime(detailedContact.createdAt || detailedContact.createdDate || detailedContact.date || new Date());

                                                                return (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-owner-pill-badge-btn"
                                                                            onMouseEnter={(e) => {
                                                                                if (contactAddedByPopoverTimeoutRef.current) clearTimeout(contactAddedByPopoverTimeoutRef.current);
                                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                                setContactAddedByPopoverCoords({
                                                                                    top: Math.max(12, rect.top - 12),
                                                                                    left: rect.right + 12
                                                                                });
                                                                                setIsContactAddedByPopoverOpen(true);
                                                                            }}
                                                                            onMouseLeave={() => {
                                                                                contactAddedByPopoverTimeoutRef.current = setTimeout(() => {
                                                                                    setIsContactAddedByPopoverOpen(false);
                                                                                }, 180);
                                                                            }}
                                                                        >
                                                                            <div className="pipeline-owner-pill-avatar">
                                                                                {empFullName.charAt(0).toUpperCase()}
                                                                            </div>
                                                                            <span>{empFullName}</span>
                                                                        </button>

                                                                        {isContactAddedByPopoverOpen && (
                                                                            <div
                                                                                className="pipeline-dealer-hover-popover outside-popover"
                                                                                style={{
                                                                                    position: 'fixed',
                                                                                    top: `${contactAddedByPopoverCoords.top}px`,
                                                                                    left: `${contactAddedByPopoverCoords.left}px`,
                                                                                    zIndex: 99999
                                                                                }}
                                                                                onMouseEnter={() => {
                                                                                    if (contactAddedByPopoverTimeoutRef.current) clearTimeout(contactAddedByPopoverTimeoutRef.current);
                                                                                    setIsContactAddedByPopoverOpen(true);
                                                                                }}
                                                                                onMouseLeave={() => {
                                                                                    contactAddedByPopoverTimeoutRef.current = setTimeout(() => {
                                                                                        setIsContactAddedByPopoverOpen(false);
                                                                                    }, 180);
                                                                                }}
                                                                                onClick={e => e.stopPropagation()}
                                                                            >
                                                                                <div className="pipeline-dealer-popover-header">
                                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                                                                                        <div className="pipeline-detail-clean-svg-icon contact" style={{ width: '32px', height: '32px', minWidth: '32px' }}>
                                                                                            <User size={16} strokeWidth={2} />
                                                                                        </div>
                                                                                        <div style={{ overflow: 'hidden', minWidth: 0 }}>
                                                                                            <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                                                                {empFullName}
                                                                                            </h4>
                                                                                            <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '2px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                                                                {empDesignation}
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                    <span
                                                                                        className={`pipeline-stage-badge ${isEmpActive ? 'won' : 'lost'}`}
                                                                                        style={{
                                                                                            marginLeft: 'auto',
                                                                                            fontSize: '10px',
                                                                                            padding: '2px 8px',
                                                                                            lineHeight: '14px',
                                                                                            height: '18px',
                                                                                            display: 'inline-flex',
                                                                                            alignItems: 'center',
                                                                                            borderRadius: '10px',
                                                                                            fontWeight: 600,
                                                                                            flexShrink: 0
                                                                                        }}
                                                                                    >
                                                                                        {isEmpActive ? 'Active' : 'Inactive'}
                                                                                    </span>
                                                                                </div>

                                                                                <div className="pipeline-dealer-popover-body">
                                                                                    <div className="pipeline-dealer-popover-row">
                                                                                        <div
                                                                                            className="pipeline-dealer-popover-icon-box"
                                                                                            data-tooltip="Email"
                                                                                            title="Email"
                                                                                        >
                                                                                            <Mail size={13} style={{ color: '#0284c7', flexShrink: 0 }} />
                                                                                        </div>
                                                                                        {empEmail && empEmail !== '—' && empEmail !== '-' ? (
                                                                                            <a
                                                                                                href={`mailto:${empEmail}`}
                                                                                                style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', color: '#0284c7' }}
                                                                                                title={empEmail}
                                                                                            >
                                                                                                {empEmail}
                                                                                            </a>
                                                                                        ) : (
                                                                                            <span style={{ color: '#64748b' }}>—</span>
                                                                                        )}
                                                                                    </div>

                                                                                    <div className="pipeline-dealer-popover-row">
                                                                                        <div
                                                                                            className="pipeline-dealer-popover-icon-box"
                                                                                            data-tooltip="Phone"
                                                                                            title="Phone"
                                                                                        >
                                                                                            <Phone size={13} style={{ color: '#16a34a', flexShrink: 0 }} />
                                                                                        </div>
                                                                                        {empPhone && empPhone !== '—' && empPhone !== '-' ? (
                                                                                            <a
                                                                                                href={`tel:${empPhone.replace(/\s+/g, '')}`}
                                                                                                style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', color: '#16a34a' }}
                                                                                                title={empPhone}
                                                                                            >
                                                                                                {empPhone}
                                                                                            </a>
                                                                                        ) : (
                                                                                            <span style={{ color: '#64748b' }}>—</span>
                                                                                        )}
                                                                                    </div>

                                                                                    <div className="pipeline-dealer-popover-row">
                                                                                        <div
                                                                                            className="pipeline-dealer-popover-icon-box"
                                                                                            data-tooltip="Department"
                                                                                            title="Department"
                                                                                        >
                                                                                            <Briefcase size={13} style={{ color: '#6366f1', flexShrink: 0 }} />
                                                                                        </div>
                                                                                        <span style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', color: '#334155' }}>
                                                                                            {empDepartment}
                                                                                        </span>
                                                                                    </div>

                                                                                    <div className="pipeline-dealer-popover-row">
                                                                                        <div
                                                                                            className="pipeline-dealer-popover-icon-box"
                                                                                            data-tooltip="Added On"
                                                                                            title="Added On"
                                                                                        >
                                                                                            <Calendar size={13} style={{ color: '#f59e0b', flexShrink: 0 }} />
                                                                                        </div>
                                                                                        <span style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', color: '#334155' }}>
                                                                                            {empAddedDate}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>

                                                    <div className="pipeline-detail-kv-row">
                                                        <span className="pipeline-detail-k-label">Created On</span>
                                                        <span className="pipeline-detail-v-val">
                                                            {formatISTDateTime(detailedContact.createdAt || detailedContact.createdDate || detailedContact.date)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Accordion 2: Project Details */}
                                        <div className="pipeline-accordion-section">
                                            <div
                                                className="pipeline-accordion-header"
                                                onClick={() => setContactAccordionState(prev => ({ ...prev, project: !prev.project }))}
                                            >
                                                <div className="pipeline-accordion-title-wrap">
                                                    <span className="pipeline-accordion-title-icon" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                                                        <Briefcase size={12} strokeWidth={2.5} />
                                                    </span>
                                                    <span>Project Details</span>
                                                </div>

                                                <div className="pipeline-accordion-actions">
                                                    <button
                                                        type="button"
                                                        className={`pipeline-accordion-toggle-btn ${contactAccordionState.project ? 'open' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setContactAccordionState(prev => ({ ...prev, project: !prev.project }))
                                                        }}
                                                        title={contactAccordionState.project ? 'Collapse Section' : 'Expand Section'}
                                                    >
                                                        <ChevronDown size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className={`pipeline-accordion-body ${contactAccordionState.project ? '' : 'collapsed'}`}>
                                                {detailedContact.projectId || detailedContact.projectName ? (
                                                    <div className="pipeline-detail-kv-grid">
                                                        {detailedContact.projectId && (
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Project ID</span>
                                                                <span className="pipeline-detail-v-val" style={{ color: '#0284c7', fontFamily: 'monospace', fontWeight: 700 }}>
                                                                    {detailedContact.projectId}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="pipeline-detail-kv-row">
                                                            <span className="pipeline-detail-k-label">Project Name</span>
                                                            <span className="pipeline-detail-v-val font-semibold">{detailedContact.projectName}</span>
                                                        </div>
                                                        {detailedContact.projectType && (
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Project Type</span>
                                                                <span className="pipeline-detail-v-val">{detailedContact.projectType}</span>
                                                            </div>
                                                        )}
                                                        {detailedContact.projectCost && (
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Project Cost</span>
                                                                <span className="pipeline-detail-v-val" style={{ fontWeight: 700, color: '#0284c7' }}>
                                                                    {detailedContact.projectCost}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {detailedContact.projectTimeline && (
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Timeline</span>
                                                                <span className="pipeline-detail-v-val">{detailedContact.projectTimeline}</span>
                                                            </div>
                                                        )}
                                                        {detailedContact.projectStatus && (
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Status</span>
                                                                <span className="pipeline-detail-v-val">{detailedContact.projectStatus}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div style={{ padding: '24px 16px', textAlign: 'center', color: '#94a3b8' }}>
                                                        <Briefcase size={28} style={{ margin: '0 auto 8px', opacity: 0.45 }} />
                                                        <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#64748b' }}>No Associated Project</p>
                                                        <p style={{ margin: '4px 0 0', fontSize: '11.5px', color: '#94a3b8' }}>There is no active project linked to this contact yet.</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Accordion 3: Payment Details */}
                                        <div className="pipeline-accordion-section">
                                            <div
                                                className="pipeline-accordion-header"
                                                onClick={() => setContactAccordionState(prev => ({ ...prev, payment: !prev.payment }))}
                                            >
                                                <div className="pipeline-accordion-title-wrap">
                                                    <span className="pipeline-accordion-title-icon" style={{ background: '#ecfdf5', color: '#16a34a' }}>
                                                        <CreditCard size={12} strokeWidth={2.5} />
                                                    </span>
                                                    <span>Payment Details</span>
                                                </div>

                                                <div className="pipeline-accordion-actions">
                                                    <button
                                                        type="button"
                                                        className={`pipeline-accordion-toggle-btn ${contactAccordionState.payment ? 'open' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setContactAccordionState(prev => ({ ...prev, payment: !prev.payment }));
                                                        }}
                                                        title={contactAccordionState.payment ? 'Collapse Section' : 'Expand Section'}
                                                    >
                                                        <ChevronDown size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className={`pipeline-accordion-body ${contactAccordionState.payment ? '' : 'collapsed'}`}>
                                                {(() => {
                                                    const matchedDeal = deals.find(d =>
                                                        d.contactId === detailedContact._id ||
                                                        (d.clientName && detailedContact.firstName && (
                                                            d.clientName.toLowerCase().includes(detailedContact.firstName.toLowerCase()) ||
                                                            detailedContact.firstName.toLowerCase().includes(d.clientName.toLowerCase())
                                                        )) ||
                                                        (d.clientName && detailedContact.company && d.clientName.toLowerCase().includes(detailedContact.company.toLowerCase()))
                                                    );
                                                    const contactDealKey = matchedDeal ? matchedDeal._id : `contact_${detailedContact._id}`;
                                                    const dealRecords = getContactPaymentRecords(detailedContact);
                                                    const hasRecords = dealRecords.length > 0;
                                                    const dealVal = matchedDeal ? parsePaymentAmount(matchedDeal.dealValue) : 0;
                                                    const paidVal = dealRecords
                                                        .filter(r => (r.paymentStatus || '').toLowerCase() === 'paid')
                                                        .reduce((sum, r) => sum + parsePaymentAmount(r.paidAmount), 0);
                                                    const pendingRecordsAmount = dealRecords
                                                        .filter(r => (r.paymentStatus || '').toLowerCase() !== 'paid')
                                                        .reduce((sum, r) => sum + parsePaymentAmount(r.paidAmount), 0);

                                                    const totalVal = dealVal > 0 ? dealVal : (paidVal + pendingRecordsAmount);
                                                    const pendingVal = dealVal > 0 ? Math.max(0, dealVal - paidVal) : pendingRecordsAmount;
                                                    const paidPct = totalVal > 0 ? Math.min(100, Math.round((paidVal / totalVal) * 100)) : 0;
                                                    const currency = matchedDeal?.currency || detailedContact.currency || '₹';

                                                    if (!hasRecords && totalVal === 0) {
                                                        return (
                                                            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#94a3b8' }}>
                                                                <CreditCard size={28} style={{ margin: '0 auto 8px', opacity: 0.45 }} />
                                                                <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#64748b' }}>No Payment Records</p>
                                                                <p style={{ margin: '4px 0 0', fontSize: '11.5px', color: '#94a3b8' }}>No payment records found for this contact.</p>
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-milestone-pill-badge-btn"
                                                                    style={{ margin: '12px auto 0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setMilestoneModalDealId(contactDealKey);
                                                                        setShowMilestoneModal(true);
                                                                        setShowAddPaymentForm(true);
                                                                    }}
                                                                >
                                                                    <Plus size={12} />
                                                                    <span>Add Payment</span>
                                                                </button>
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div className="pipeline-detail-kv-grid">
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Total Amount</span>
                                                                <span className="pipeline-detail-v-val" style={{ fontWeight: 700, color: '#0284c7' }}>
                                                                    {currency} {totalVal.toLocaleString()}
                                                                </span>
                                                            </div>
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Paid Amount</span>
                                                                <span className="pipeline-detail-v-val" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                                    <span style={{ fontWeight: 700, color: '#16a34a' }}>
                                                                        {currency} {paidVal.toLocaleString()}
                                                                    </span>
                                                                    {totalVal > 0 && (
                                                                        <span className="pipeline-stage-badge won" style={{ fontSize: '10px', padding: '1px 6px' }}>
                                                                            {paidPct}% Paid
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                            <div className="pipeline-detail-kv-row">
                                                                <span className="pipeline-detail-k-label">Pending Amount</span>
                                                                <span className="pipeline-detail-v-val" style={{ fontWeight: 700, color: '#ea580c' }}>
                                                                    {currency} {pendingVal.toLocaleString()}
                                                                </span>
                                                            </div>

                                                            <div className="pipeline-detail-kv-row" style={{ position: 'relative' }}>
                                                                <span className="pipeline-detail-k-label">Payment Terms</span>
                                                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                                                    <button
                                                                        type="button"
                                                                        className="pipeline-milestone-pill-badge-btn"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setMilestoneModalDealId(contactDealKey);
                                                                            setShowMilestoneModal(true);
                                                                            setShowAddPaymentForm(false);
                                                                        }}
                                                                        title="Click to view full Payment Records"
                                                                    >
                                                                        <span>Milestone ({dealRecords.length})</span>
                                                                        <ChevronRight size={11} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 2. MIDDLE SECTION: Activities, Notes, Emails, Messages & Calls */}
                                <div className="pipeline-detail-col-activities">
                                    <div className="pipeline-detail-actions-card">
                                        {/* Activity Tabs */}
                                        <div className="pipeline-activity-tabs-bar">
                                            <div className="pipeline-activity-tab-btns">
                                                <button
                                                    type="button"
                                                    className={`pipeline-act-tab-btn ${detailActiveTab === 'all' ? 'active' : ''}`}
                                                    onClick={() => setDetailActiveTab('all')}
                                                >
                                                    <span>All Activities</span>
                                                    <span className="pipeline-act-tab-badge">{currentContactActivities.length}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`pipeline-act-tab-btn ${detailActiveTab === 'notes' ? 'active' : ''}`}
                                                    onClick={() => setDetailActiveTab('notes')}
                                                >
                                                    <FileText size={14} />
                                                    <span>Notes</span>
                                                    <span className="pipeline-act-tab-badge">{currentContactActivities.filter(a => a.type === 'note').length}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`pipeline-act-tab-btn ${detailActiveTab === 'followups' ? 'active' : ''}`}
                                                    onClick={() => setDetailActiveTab('followups')}
                                                >
                                                    <Calendar size={14} />
                                                    <span>Follow-ups</span>
                                                    <span className="pipeline-act-tab-badge">{currentContactActivities.filter(a => a.type === 'followup').length}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`pipeline-act-tab-btn ${detailActiveTab === 'emails' ? 'active' : ''}`}
                                                    onClick={() => setDetailActiveTab('emails')}
                                                >
                                                    <Mail size={14} />
                                                    <span>Emails</span>
                                                    <span className="pipeline-act-tab-badge">{currentContactActivities.filter(a => a.type === 'email').length}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`pipeline-act-tab-btn ${detailActiveTab === 'tasks' ? 'active' : ''}`}
                                                    onClick={() => setDetailActiveTab('tasks')}
                                                >
                                                    <CheckSquare size={14} />
                                                    <span>Tasks</span>
                                                    <span className="pipeline-act-tab-badge">{contactSpecificTasks.length}</span>
                                                </button>
                                            </div>

                                            {/* Contextual Action Button on Right Side */}
                                            <div className="pipeline-activity-quick-actions">
                                                {detailActiveTab === 'notes' && (
                                                    <button
                                                        type="button"
                                                        className="pipeline-btn-add-activity-tab"
                                                        onClick={() => {
                                                            setNewActivityType('note');
                                                            setIsComposingActivity(true);
                                                        }}
                                                    >
                                                        <Plus size={13} />
                                                        <span>Add Note</span>
                                                    </button>
                                                )}
                                                {detailActiveTab === 'followups' && (
                                                    <button
                                                        type="button"
                                                        className="pipeline-btn-add-activity-tab"
                                                        onClick={() => {
                                                            setIsComposingFollowUp(prev => !prev);
                                                        }}
                                                    >
                                                        <Plus size={13} />
                                                        <span>Add Follow-up</span>
                                                    </button>
                                                )}
                                                {detailActiveTab === 'emails' && (
                                                    <button
                                                        type="button"
                                                        className="pipeline-btn-add-activity-tab"
                                                        disabled
                                                        style={{ opacity: 0.55, cursor: 'not-allowed' }}
                                                        title="Feature not implemented yet"
                                                    >
                                                        <Plus size={13} />
                                                        <span>Log Email</span>
                                                    </button>
                                                )}
                                                {detailActiveTab === 'tasks' && (
                                                    <button
                                                        type="button"
                                                        className="pipeline-btn-add-activity-tab"
                                                        onClick={() => {
                                                            handleOpenCreateTask('', detailedContact._id);
                                                        }}
                                                    >
                                                        <Plus size={13} />
                                                        <span>Add Task</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Inline Activity Composer */}
                                        {isComposingActivity && (
                                            (detailActiveTab === 'notes' && newActivityType === 'note') ||
                                            (detailActiveTab === 'emails' && newActivityType === 'email')
                                        ) && (
                                                <form onSubmit={handleAddActivity} className="pipeline-activity-composer">
                                                    <div className="pipeline-activity-composer-header">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            {newActivityType === 'note' && (
                                                                <>
                                                                    <FileText size={16} style={{ color: '#0284c7' }} />
                                                                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '13.5px' }}>Add Internal Note</span>
                                                                </>
                                                            )}
                                                            {newActivityType === 'email' && (
                                                                <>
                                                                    <Mail size={16} style={{ color: '#0284c7' }} />
                                                                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '13.5px' }}>Log Email</span>
                                                                </>
                                                            )}
                                                            {newActivityType === 'message' && (
                                                                <>
                                                                    <MessageSquare size={16} style={{ color: '#0284c7' }} />
                                                                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '13.5px' }}>Log Message</span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {newActivityType === 'email' && (
                                                        <input
                                                            type="text"
                                                            className="pipeline-form-input"
                                                            style={{ padding: '8px 12px', fontSize: '13px' }}
                                                            placeholder="Subject line (e.g. Contract review follow-up)"
                                                            value={newActivitySubject}
                                                            onChange={e => setNewActivitySubject(e.target.value)}
                                                        />
                                                    )}

                                                    <textarea
                                                        className="pipeline-activity-composer-textarea"
                                                        placeholder={
                                                            newActivityType === 'note'
                                                                ? 'Write your note here...'
                                                                : newActivityType === 'email'
                                                                    ? 'Email summary or message body...'
                                                                    : 'Chat notes or SMS conversation details...'
                                                        }
                                                        value={newActivityContent}
                                                        onChange={e => setNewActivityContent(e.target.value)}
                                                        autoFocus
                                                        required
                                                    />

                                                    <div className="pipeline-activity-composer-footer">
                                                        <button
                                                            type="button"
                                                            className="pipeline-btn-cancel"
                                                            style={{ padding: '6px 14px', fontSize: '12.5px' }}
                                                            onClick={() => {
                                                                setIsComposingActivity(false);
                                                                setNewActivityContent('');
                                                                setNewActivitySubject('');
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="submit"
                                                            className="pipeline-btn-save"
                                                            style={{ padding: '6px 18px', fontSize: '12.5px' }}
                                                        >
                                                            {newActivityType === 'note' ? 'Save Note' : newActivityType === 'email' ? 'Log Email' : 'Log Message'}
                                                        </button>
                                                    </div>
                                                </form>
                                            )}

                                        {/* Dedicated Tasks View or Activity Timeline Feed */}
                                        {detailActiveTab === 'tasks' ? (
                                            <div className="pipeline-inline-tasks-panel" style={{ marginTop: isComposingActivity ? '16px' : '0' }}>

                                                {contactSpecificTasks.length === 0 ? (
                                                    <div className="pipeline-empty-state-mini" style={{ padding: '36px 20px', textAlign: 'center' }}>
                                                        <CheckSquare size={32} style={{ color: '#94a3b8', marginBottom: '8px', opacity: 0.6 }} />
                                                        <p style={{ margin: 0, fontSize: '13.5px', fontWeight: 500, color: '#64748b' }}>No tasks created yet.</p>
                                                    </div>
                                                ) : (
                                                    <div className="pipeline-inline-tasks-list">
                                                        {contactSpecificTasks.map(task => (
                                                            <div key={task._id} className={`pipeline-inline-task-item ${task.completed ? 'completed' : ''}`}>
                                                                <label className="pipeline-task-checkbox-wrap" onClick={e => e.stopPropagation()}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!task.completed}
                                                                        onChange={(e) => handleToggleTaskComplete(task._id, e)}
                                                                    />
                                                                    <span className="pipeline-task-checkmark"></span>
                                                                </label>
                                                                <div className="pipeline-inline-task-main">
                                                                    <div className="pipeline-inline-task-title-row">
                                                                        <span className={`pipeline-inline-task-title ${task.completed ? 'done' : ''}`}>
                                                                            {task.title}
                                                                        </span>
                                                                        <span className={`pipeline-task-priority-pill ${(task.priority || 'medium').toLowerCase()}`}>
                                                                            {task.priority || 'Medium'}
                                                                        </span>
                                                                    </div>
                                                                    {task.description && (
                                                                        <p className="pipeline-inline-task-desc">{task.description}</p>
                                                                    )}
                                                                    <div className="pipeline-inline-task-meta-row">
                                                                        <span className="pipeline-inline-task-meta-item">
                                                                            <Clock size={12} />
                                                                            <span>Due: {task.dueDate ? formatDateDDMMYYYY(task.dueDate) : 'No due date'}</span>
                                                                        </span>
                                                                        <span className="pipeline-inline-task-meta-item">
                                                                            <User size={12} />
                                                                            <span>{task.assignee || 'Artiflow'}</span>
                                                                        </span>
                                                                        <span className="pipeline-inline-task-type-badge">
                                                                            {task.type || 'Follow-up'}
                                                                        </span>
                                                                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                                                                            <button
                                                                                type="button"
                                                                                className="pipeline-mini-icon-btn"
                                                                                title="Edit Task"
                                                                                onClick={(e) => handleOpenEditTask(task, e)}
                                                                            >
                                                                                <Edit2 size={12} />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="pipeline-mini-icon-btn danger"
                                                                                title="Delete Task"
                                                                                onClick={(e) => handleDeleteTask(task._id, e)}
                                                                            >
                                                                                <Trash2 size={12} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ) : detailActiveTab === 'followups' ? (
                                            /* Follow-up Timeline Chaining View */
                                            <div className="pipeline-followup-container">
                                                {/* Header Stats & Quick Action Bar */}
                                                <div className="pipeline-followup-header-stats">
                                                    <div className="pipeline-followup-stats-left">
                                                        <div className="pipeline-followup-stats-icon">
                                                            <Calendar size={18} />
                                                        </div>
                                                        <div>
                                                            <h4 className="pipeline-followup-stats-title">Follow-up Timeline & Touchpoints</h4>
                                                            <p className="pipeline-followup-stats-desc">Connected touchpoint chain of client discussions, meetings, and outcomes</p>
                                                        </div>
                                                    </div>

                                                    <div className="pipeline-followup-stats-pills">
                                                        <span className="pipeline-followup-stat-pill total">
                                                            Total: {currentContactActivities.filter(a => a.type === 'followup').length}
                                                        </span>
                                                        <span className="pipeline-followup-stat-pill completed">
                                                            <Check size={11} />
                                                            Completed: {currentContactActivities.filter(a => a.type === 'followup' && a.status === 'Completed').length}
                                                        </span>
                                                        <span className="pipeline-followup-stat-pill scheduled">
                                                            <Clock size={11} />
                                                            Scheduled: {currentContactActivities.filter(a => a.type === 'followup' && (a.status === 'Scheduled' || !a.status)).length}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Inline Follow-up Composer */}
                                                {isComposingFollowUp && (
                                                    <form onSubmit={handleAddFollowUp} className="pipeline-followup-composer-card">
                                                        <div className="pipeline-followup-composer-header">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <Calendar size={16} style={{ color: '#0284c7' }} />
                                                                <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '14px' }}>Log New Follow-up</span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => setIsComposingFollowUp(false)}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </div>

                                                        {/* Channel Pills Selector */}
                                                        <div className="pipeline-followup-field-group">
                                                            <label className="pipeline-followup-field-label">Follow-up Channel / Touchpoint Type</label>
                                                            <div className="pipeline-followup-channels-bar">
                                                                {[
                                                                    { key: 'Call', label: 'Phone Call', icon: <Phone size={13} />, className: 'channel-call' },
                                                                    { key: 'Meeting', label: 'Meeting', icon: <Users size={13} />, className: 'channel-meeting' },
                                                                    { key: 'WhatsApp', label: 'WhatsApp', icon: <MessageSquare size={13} />, className: 'channel-whatsapp' },
                                                                    { key: 'Email', label: 'Email', icon: <Mail size={13} />, className: 'channel-email' },
                                                                    { key: 'Demo', label: 'Product Demo', icon: <Briefcase size={13} />, className: 'channel-demo' },
                                                                    { key: 'Visit', label: 'Site Visit', icon: <Building2 size={13} />, className: 'channel-visit' }
                                                                ].map(ch => (
                                                                    <button
                                                                        key={ch.key}
                                                                        type="button"
                                                                        className={`pipeline-channel-btn ${ch.className} ${followUpFormData.channel === ch.key ? 'active' : ''}`}
                                                                        onClick={() => setFollowUpFormData(prev => ({ ...prev, channel: ch.key }))}
                                                                    >
                                                                        {ch.icon}
                                                                        <span>{ch.label}</span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* Inputs Grid */}
                                                        <div className="pipeline-followup-form-grid">
                                                            <div className="pipeline-followup-field-group">
                                                                <label className="pipeline-followup-field-label">Follow-up Title / Purpose</label>
                                                                <input
                                                                    type="text"
                                                                    className="pipeline-followup-field-input"
                                                                    placeholder={`e.g. ${followUpFormData.channel} regarding project details`}
                                                                    value={followUpFormData.title}
                                                                    onChange={e => setFollowUpFormData(prev => ({ ...prev, title: e.target.value }))}
                                                                />
                                                            </div>

                                                            <div className="pipeline-followup-field-group">
                                                                <label className="pipeline-followup-field-label">Scheduled / Conducted Date</label>
                                                                <input
                                                                    type="date"
                                                                    className="pipeline-followup-field-input"
                                                                    value={followUpFormData.followUpDate}
                                                                    onChange={e => setFollowUpFormData(prev => ({ ...prev, followUpDate: e.target.value }))}
                                                                    required
                                                                />
                                                            </div>

                                                            <div className="pipeline-followup-field-group">
                                                                <label className="pipeline-followup-field-label">Time</label>
                                                                <input
                                                                    type="time"
                                                                    className="pipeline-followup-field-input"
                                                                    value={followUpFormData.followUpTime}
                                                                    onChange={e => setFollowUpFormData(prev => ({ ...prev, followUpTime: e.target.value }))}
                                                                />
                                                            </div>

                                                            <div className="pipeline-followup-field-group">
                                                                <label className="pipeline-followup-field-label">Outcome / Status</label>
                                                                <select
                                                                    className="pipeline-followup-field-input"
                                                                    value={followUpFormData.status}
                                                                    onChange={e => setFollowUpFormData(prev => ({ ...prev, status: e.target.value }))}
                                                                >
                                                                    <option value="Scheduled">Scheduled / Upcoming</option>
                                                                    <option value="Completed">Completed</option>
                                                                    <option value="Follow-up Needed">Follow-up Needed</option>
                                                                    <option value="Rescheduled">Rescheduled</option>
                                                                    <option value="Missed">Missed / No Answer</option>
                                                                </select>
                                                            </div>

                                                            <div className="pipeline-followup-field-group">
                                                                <label className="pipeline-followup-field-label">Next Follow-up Date (Optional)</label>
                                                                <input
                                                                    type="date"
                                                                    className="pipeline-followup-field-input"
                                                                    value={followUpFormData.nextFollowUpDate}
                                                                    onChange={e => setFollowUpFormData(prev => ({ ...prev, nextFollowUpDate: e.target.value }))}
                                                                />
                                                            </div>

                                                            {/* Member Selection */}
                                                            {(() => {
                                                                const currentMemberOptions = getFollowUpMemberOptions('contact', detailedContact?._id);
                                                                const isSalesPerson = !isAdmin && !isSalesHead && !isSalesManager;
                                                                return (
                                                                    <div className="pipeline-followup-field-group">
                                                                        <label className="pipeline-followup-field-label">
                                                                            {isSalesPerson ? 'Route to Management' : 'Assign / Link Member'}
                                                                        </label>
                                                                        <MemberDropdown
                                                                            options={currentMemberOptions}
                                                                            value={followUpFormData.memberId || ''}
                                                                            onChange={(selId, matchedOpt) => {
                                                                                setFollowUpFormData(prev => ({
                                                                                    ...prev,
                                                                                    memberId: selId,
                                                                                    memberName: matchedOpt ? matchedOpt.name : ''
                                                                                }));
                                                                            }}
                                                                        />
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>

                                                        {/* Remarks Field */}
                                                        <div className="pipeline-followup-field-group">
                                                            <label className="pipeline-followup-field-label">
                                                                Remarks & Discussion Details <span style={{ color: '#ef4444' }}>*</span>
                                                            </label>
                                                            <textarea
                                                                className="pipeline-followup-remarks-textarea"
                                                                placeholder="Enter detailed remarks, client feedback, discussion points, agreed next action items, or objections raised..."
                                                                value={followUpFormData.remarks}
                                                                onChange={e => setFollowUpFormData(prev => ({ ...prev, remarks: e.target.value }))}
                                                                required
                                                            />
                                                        </div>

                                                        {/* Composer Footer */}
                                                        <div className="pipeline-followup-composer-footer">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#64748b' }}>
                                                                <User size={13} />
                                                                <span>
                                                                    Adding as: <strong style={{ color: '#0f172a' }}>{(user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || 'Admin System'}</strong>
                                                                    {' '}({user?.role?.name || user?.role || (roleName ? roleName.charAt(0).toUpperCase() + roleName.slice(1) : 'Sales Executive')})
                                                                </span>
                                                            </div>

                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-btn-cancel"
                                                                    onClick={() => setIsComposingFollowUp(false)}
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    type="submit"
                                                                    className="pipeline-btn-save"
                                                                    disabled={isSubmittingFollowUp}
                                                                >
                                                                    {isSubmittingFollowUp ? 'Saving...' : 'Save Follow-up'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </form>
                                                )}

                                                {/* Chaining Timeline View */}
                                                {(() => {
                                                    const followUps = currentContactActivities.filter(a => a.type === 'followup');
                                                    if (followUps.length === 0) {
                                                        return (
                                                            <div className="pipeline-empty-state-mini" style={{ padding: '48px 20px', textAlign: 'center', background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                                                <Calendar size={36} style={{ color: '#0284c7', marginBottom: '10px', opacity: 0.7 }} />
                                                                <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>No Follow-ups Recorded Yet</h4>
                                                                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b', maxWidth: '420px', marginInline: 'auto' }}>
                                                                    Track scheduled calls, meetings, WhatsApp conversations, and customer touchpoints in a sequential chaining timeline.
                                                                </p>
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-btn-add-followup"
                                                                    style={{ margin: '0 auto' }}
                                                                    onClick={() => setIsComposingFollowUp(true)}
                                                                >
                                                                    <Plus size={14} />
                                                                    <span>Log First Follow-up</span>
                                                                </button>
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div className="pipeline-chain-timeline-wrapper">
                                                            {/* The connecting vertical chain track */}
                                                            <div className="pipeline-chain-track-line" />

                                                            {/* Chained Node Items */}
                                                            {followUps.map((act, idx) => {
                                                                const chKey = (act.channel || 'Call').toLowerCase();
                                                                const chClass = `channel-${chKey.includes('call') ? 'call' : chKey.includes('meeting') ? 'meeting' : chKey.includes('whatsapp') ? 'whatsapp' : chKey.includes('email') ? 'email' : chKey.includes('demo') ? 'demo' : chKey.includes('visit') ? 'visit' : 'call'}`;

                                                                const statusKey = (act.status || 'Scheduled').toLowerCase().replace(/\s+/g, '-');
                                                                const statusClass = `status-${statusKey.includes('completed') ? 'completed' : statusKey.includes('rescheduled') ? 'rescheduled' : statusKey.includes('missed') ? 'missed' : statusKey.includes('needed') ? 'followup-needed' : 'scheduled'}`;

                                                                const authorInitials = (act.author || 'AS')
                                                                    .split(' ')
                                                                    .map(n => n[0])
                                                                    .join('')
                                                                    .slice(0, 2)
                                                                    .toUpperCase();

                                                                return (
                                                                    <div key={act.id || act._id || idx} className="pipeline-chain-node-row">
                                                                        {/* Left Chaining Node Indicator */}
                                                                        <div className="pipeline-chain-marker-wrap">
                                                                            <div className={`pipeline-chain-marker-circle ${statusClass}`} title={`Channel: ${act.channel || 'Call'}`}>
                                                                                {getFollowUpChannelIcon(act.channel, 18)}
                                                                            </div>
                                                                            <span className="pipeline-chain-step-pill">
                                                                                #{followUps.length - idx}
                                                                            </span>
                                                                        </div>

                                                                        {/* Chaining Card Content */}
                                                                        <div className="pipeline-chain-card">
                                                                            {/* Top Row: Channel, Title, Status, Logged When */}
                                                                            <div className="pipeline-chain-card-top">
                                                                                <div className="pipeline-chain-card-title-group">
                                                                                    <span className={`pipeline-chain-channel-chip ${chClass}`}>
                                                                                        {getFollowUpChannelIcon(act.channel, 12)}
                                                                                        <span>{act.channel || 'Call'}</span>
                                                                                    </span>

                                                                                    <span className="pipeline-chain-title-text">
                                                                                        {act.title || `${act.channel || 'Touchpoint'} Follow-up`}
                                                                                    </span>

                                                                                    <span className={`pipeline-chain-status-pill ${statusClass}`}>
                                                                                        {statusClass.includes('completed') && <Check size={11} />}
                                                                                        {statusClass.includes('scheduled') && <Clock size={11} />}
                                                                                        <span>{act.status || 'Scheduled'}</span>
                                                                                    </span>
                                                                                </div>

                                                                                <div className="pipeline-chain-logged-time" title={formatActivityTimestamp(act)}>
                                                                                    <Clock size={12} />
                                                                                    <span>Added {formatRelativeActivityTime(act) || formatActivityTimestamp(act)}</span>
                                                                                </div>
                                                                            </div>

                                                                            {/* Meta Bar: Author (Who added), Scheduled Date & Time */}
                                                                            <div className="pipeline-chain-meta-bar">
                                                                                <div className="pipeline-chain-author-badge">
                                                                                    <div className="pipeline-chain-author-avatar">{authorInitials}</div>
                                                                                    <span>{act.author || 'Admin System'}</span>
                                                                                    {act.authorRole && (
                                                                                        <span className="pipeline-chain-author-role">{act.authorRole}</span>
                                                                                    )}
                                                                                </div>

                                                                                {(act.memberName || act.assignedToName) && (
                                                                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#0369a1', background: '#f0f9ff', border: '1px solid #bae6fd', padding: '2px 8px', borderRadius: '4px', fontWeight: 500 }}>
                                                                                        <User size={11} />
                                                                                        <span>Assigned: {act.memberName || act.assignedToName}</span>
                                                                                    </div>
                                                                                )}

                                                                                {(act.followUpDate || act.date) && (
                                                                                    <div className="pipeline-chain-sched-item">
                                                                                        <Calendar size={13} style={{ color: '#0284c7' }} />
                                                                                        <span>
                                                                                            Date: <strong>{act.followUpDate ? formatDateDDMMYYYY(act.followUpDate) : act.date}</strong>
                                                                                            {act.followUpTime ? ` at ${act.followUpTime}` : ''}
                                                                                        </span>
                                                                                    </div>
                                                                                )}

                                                                                {act.nextFollowUpDate && (
                                                                                    <div className="pipeline-chain-sched-item" style={{ color: '#6366f1' }}>
                                                                                        <ArrowRightLeft size={12} />
                                                                                        <span>Next Follow-up: <strong>{formatDateDDMMYYYY(act.nextFollowUpDate)}</strong></span>
                                                                                    </div>
                                                                                )}
                                                                            </div>

                                                                            {/* Remarks Field (Prominent & Clean) */}
                                                                            <div className={`pipeline-chain-remarks-box ${statusClass}`}>
                                                                                <div className="pipeline-chain-remarks-header">
                                                                                    <FileText size={12} />
                                                                                    <span>Remarks & Outcomes</span>
                                                                                </div>
                                                                                <p className="pipeline-chain-remarks-content">
                                                                                    {act.remarks || act.content || 'No detailed remarks recorded.'}
                                                                                </p>
                                                                            </div>

                                                                            {/* Bottom Actions */}
                                                                            <div className="pipeline-chain-footer-actions">
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                    {act.status !== 'Completed' ? (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="pipeline-chain-mark-btn"
                                                                                            onClick={() => handleUpdateFollowUpStatus(act.id || act._id, 'Completed')}
                                                                                        >
                                                                                            <Check size={12} />
                                                                                            <span>Mark as Completed</span>
                                                                                        </button>
                                                                                    ) : (
                                                                                        <span style={{ fontSize: '11.5px', color: '#059669', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                                            <CheckCircle size={13} />
                                                                                            <span>Completed Touchpoint</span>
                                                                                        </span>
                                                                                    )}

                                                                                    {canUserEditFollowUp(act, user, hasFullAccess) && (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="pipeline-chain-edit-btn"
                                                                                            title="Edit scheduled follow-up"
                                                                                            onClick={() => handleOpenEditFollowUp(act, 'contact', detailedContact?._id)}
                                                                                        >
                                                                                            <Edit2 size={12} />
                                                                                            <span>Edit</span>
                                                                                        </button>
                                                                                    )}
                                                                                    {isFollowUpTimePassed(act) && act.status !== 'Completed' && (
                                                                                        <span className="pipeline-chain-expired-badge" title="Scheduled follow-up is overdue">
                                                                                            <Clock size={11} /> Overdue
                                                                                        </span>
                                                                                    )}
                                                                                </div>

                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-mini-icon-btn danger"
                                                                                        title="Delete Follow-up"
                                                                                        disabled={deletingFollowUpId === (act.id || act._id)}
                                                                                        onClick={() => handleDeleteFollowUp(act.id || act._id)}
                                                                                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                                                    >
                                                                                        {deletingFollowUpId === (act.id || act._id) ? (
                                                                                            <Loader2 size={12} className="pipeline-spinner-icon" />
                                                                                        ) : (
                                                                                            <Trash2 size={12} />
                                                                                        )}
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        ) : (
                                            /* Timeline Stream */
                                            <div className="pipeline-activity-timeline-feed" style={{ marginTop: (isComposingActivity && ((detailActiveTab === 'notes' && newActivityType === 'note') || (detailActiveTab === 'emails' && newActivityType === 'email'))) ? '16px' : '0' }}>
                                                {detailActiveTab === 'emails' ? (
                                                    <div style={{ textAlign: 'center', padding: '48px 20px', color: '#94a3b8' }}>
                                                        <Mail size={32} style={{ marginBottom: '8px', opacity: 0.6 }} />
                                                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#475569' }}>Feature not implemented yet</p>
                                                        <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#94a3b8' }}>Email integration and logging will be available soon.</p>
                                                    </div>
                                                ) : currentContactActivities.filter(act => {
                                                    if (detailActiveTab === 'all') return true;
                                                    if (detailActiveTab === 'notes') return act.type === 'note';
                                                    if (detailActiveTab === 'followups') return act.type === 'followup';
                                                    return true;
                                                }).length > 0 ? (
                                                    currentContactActivities.filter(act => {
                                                        if (detailActiveTab === 'all') return true;
                                                        if (detailActiveTab === 'notes') return act.type === 'note';
                                                        if (detailActiveTab === 'followups') return act.type === 'followup';
                                                        return true;
                                                    }).map(act => (
                                                        <div key={act.id} className="pipeline-timeline-item-card">
                                                            <div className={`pipeline-timeline-icon-wrap ${act.type || 'activity'}`}>
                                                                {act.type === 'note' && <FileText size={18} />}
                                                                {act.type === 'followup' && <Calendar size={18} />}
                                                                {act.type === 'email' && <Mail size={18} />}
                                                                {act.type === 'message' && <MessageSquare size={18} />}
                                                                {act.type === 'task' && <CheckSquare size={18} />}
                                                                {act.type === 'deal' && <Briefcase size={18} />}
                                                                {act.type === 'contact' && <User size={18} />}
                                                                {act.type === 'company' && <Building2 size={18} />}
                                                                {act.type === 'payment' && <CreditCard size={18} />}
                                                                {(act.type === 'activity' || !['note', 'followup', 'email', 'message', 'task', 'deal', 'contact', 'company', 'payment'].includes(act.type)) && <Tag size={18} />}
                                                            </div>

                                                            <div className="pipeline-timeline-content">
                                                                <div className="pipeline-timeline-header-row">
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                                        <span className="pipeline-timeline-author-title">{act.author}</span>
                                                                        <span style={{ fontSize: '12px', color: '#64748b' }}>• {act.title}</span>
                                                                        {act.type === 'followup' && act.status && (
                                                                            <span className={`pipeline-chain-status-pill status-${(act.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                {act.status}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        {act.type === 'followup' && (
                                                                            <>
                                                                                {canUserEditFollowUp(act, user, hasFullAccess) && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-timeline-mini-action-btn"
                                                                                        title="Edit Follow-up"
                                                                                        onClick={() => handleOpenEditFollowUp(act, 'contact', detailedContact?._id)}
                                                                                    >
                                                                                        <Edit2 size={11} />
                                                                                        <span>Edit</span>
                                                                                    </button>
                                                                                )}
                                                                                {isFollowUpTimePassed(act) && act.status !== 'Completed' && (
                                                                                    <span className="pipeline-chain-expired-badge" title="Scheduled follow-up is overdue">
                                                                                        <Clock size={11} /> Overdue
                                                                                    </span>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                        <span className="pipeline-timeline-time-badge">{formatActivityTimestamp(act)}</span>
                                                                    </div>
                                                                </div>

                                                                {act.type === 'followup' ? (
                                                                    <div className="pipeline-chain-remarks-box" style={{ margin: '6px 0 0 0' }}>
                                                                        <div className="pipeline-chain-remarks-header">
                                                                            <FileText size={11} />
                                                                            <span>Remarks & Outcomes ({act.channel || 'Call'})</span>
                                                                        </div>
                                                                        <p className="pipeline-chain-remarks-content">{act.remarks || act.content}</p>
                                                                        {(act.followUpDate || act.date) && (
                                                                            <div style={{ marginTop: '6px', fontSize: '11.5px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                                    <Calendar size={13} color="#0284c7" />
                                                                                    Date: <strong style={{ color: '#334155' }}>{act.followUpDate ? formatDateDDMMYYYY(act.followUpDate) : act.date}</strong>
                                                                                    {act.followUpTime ? ` at ${act.followUpTime}` : ''}
                                                                                </span>
                                                                                {act.nextFollowUpDate && (
                                                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#6366f1' }}>
                                                                                        <ArrowRightLeft size={12} />
                                                                                        Next: <strong style={{ color: '#4338ca' }}>{formatDateDDMMYYYY(act.nextFollowUpDate)}</strong>
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <p className="pipeline-timeline-body-text" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{act.content}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div style={{ textAlign: 'center', padding: '36px 20px', color: '#94a3b8' }}>
                                                        {detailActiveTab === 'notes' ? (
                                                            <>
                                                                <FileText size={32} style={{ marginBottom: '8px', opacity: 0.6 }} />
                                                                <p style={{ margin: 0, fontSize: '13.5px', fontWeight: 500, color: '#64748b' }}>No notes created yet.</p>
                                                            </>
                                                        ) : detailActiveTab === 'followups' ? (
                                                            <>
                                                                <Calendar size={32} style={{ marginBottom: '8px', opacity: 0.6 }} />
                                                                <p style={{ margin: 0, fontSize: '13.5px', fontWeight: 500, color: '#64748b' }}>No follow-ups recorded yet.</p>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FileText size={32} style={{ marginBottom: '8px', opacity: 0.6 }} />
                                                                <p style={{ margin: 0, fontSize: '13.5px', fontWeight: 500, color: '#64748b' }}>No activities recorded yet.</p>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* 3. RIGHT SECTION: Associated Deal Details & Associated Company Details */}
                                <div className="pipeline-detail-col-assoc">
                                    <div className="pipeline-detail-card-panel">
                                        {/* Section 1: Associated Deal Details */}
                                        <div className="pipeline-detail-panel-section">
                                            <div
                                                className="pipeline-detail-section-heading"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => setContactAccordionState(prev => ({ ...prev, deals: prev.deals === false ? true : false }))}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span>Associated Deal Details</span>
                                                    {matchedDealsForContact.length > 0 && (
                                                        <span className="pipeline-act-tab-badge" style={{ background: '#e0f2fe', color: '#0284c7', fontSize: '11px', fontWeight: 700, padding: '1px 7px', borderRadius: '10px' }}>
                                                            {matchedDealsForContact.length}
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={e => e.stopPropagation()}>
                                                    <button
                                                        type="button"
                                                        className="pipeline-detail-add-badge-btn"
                                                        onClick={() => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                clientName: `${detailedContact.firstName || ''} ${detailedContact.lastName || ''}`.trim(),
                                                                associatedContact: detailedContact.email || '',
                                                                email: detailedContact.email || '',
                                                                phone: detailedContact.phone ? `${detailedContact.countryCode || '+1'} ${detailedContact.phone}` : '',
                                                                associatedCompany: (detailedContact.company && detailedContact.company !== 'Direct Client') ? detailedContact.company : '',
                                                                salesOwner: detailedContact.owner || 'Artiflow',
                                                                stage: 'New Lead'
                                                            }));
                                                            setModalStage('New Lead');
                                                            setEditingDeal(null);
                                                            setShowCreateModal(true);
                                                        }}
                                                        title="Add / Link Deal"
                                                    >
                                                        Add+
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`pipeline-accordion-toggle-btn ${contactAccordionState.deals !== false ? 'open' : ''}`}
                                                        onClick={() => setContactAccordionState(prev => ({ ...prev, deals: prev.deals === false ? true : false }))}
                                                        title={contactAccordionState.deals !== false ? 'Collapse Section' : 'Expand Section'}
                                                    >
                                                        <ChevronDown size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            {contactAccordionState.deals !== false && (
                                                matchedDealsForContact.length > 0 ? (
                                                    <div
                                                        className="pipeline-assoc-scroll-container"
                                                        style={{
                                                            maxHeight: matchedDealsForContact.length > 1 ? '290px' : 'none',
                                                            overflowY: matchedDealsForContact.length > 1 ? 'auto' : 'visible',
                                                            paddingRight: matchedDealsForContact.length > 1 ? '6px' : '0'
                                                        }}
                                                    >
                                                        {matchedDealsForContact.map((deal, idx) => {
                                                            const isDealCollapsed = !!collapsedDeals[deal._id];
                                                            const rawStage = (deal.stage || 'New').trim();
                                                            const sLower = rawStage.toLowerCase();
                                                            let stageTheme = 'new';
                                                            if (sLower.includes('won')) stageTheme = 'won';
                                                            else if (sLower.includes('lost') || sLower.includes('cancel')) stageTheme = 'lost';
                                                            else if (sLower.includes('hot') || sLower.includes('qual') || sLower.includes('prop')) stageTheme = 'hot';
                                                            else if (sLower.includes('neg') || sLower.includes('rev')) stageTheme = 'negotiation';
                                                            else if (sLower.includes('cold') || sLower.includes('hold')) stageTheme = 'cold';

                                                            return (
                                                                <div
                                                                    key={deal._id || idx}
                                                                    style={{
                                                                        marginTop: idx > 0 ? '16px' : '0',
                                                                        paddingTop: idx > 0 ? '16px' : '0',
                                                                        borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none'
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                                                                        onClick={() => setCollapsedDeals(prev => ({ ...prev, [deal._id]: !prev[deal._id] }))}
                                                                    >
                                                                        <div className="pipeline-detail-clean-svg-icon contact">
                                                                            <Briefcase size={19} strokeWidth={2} />
                                                                        </div>
                                                                        <div style={{ overflow: 'hidden', flex: 1 }}>
                                                                            <h4
                                                                                className="pipeline-card-client-name"
                                                                                style={{ margin: 0, fontSize: '13.5px', fontWeight: 700, color: '#0284c7', cursor: 'pointer', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setDetailedContactId(null);
                                                                                    setActiveTab('deals');
                                                                                    setDetailedDealId(deal._id);
                                                                                }}
                                                                                title="Click to view Deal Details"
                                                                            >
                                                                                {(() => {
                                                                                    const raw = deal.dealName || deal.clientName || '';
                                                                                    const dt = (deal.dealType || 'New Business').trim();
                                                                                    const escapedDt = dt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                                                                    const regex = new RegExp(`\\s*[-–—]\\s*(?:${escapedDt}|New Business)$`, 'i');
                                                                                    return raw.replace(regex, '').trim();
                                                                                })()}
                                                                            </h4>
                                                                            <span style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                                                {deal.pipeline || 'Sales Pipeline'} • {deal.dealType || 'New Business'}
                                                                                {isDealCollapsed && (
                                                                                    <strong style={{ color: '#0284c7', marginLeft: '4px' }}>
                                                                                        • {deal.currency || '$'}{Number(deal.dealValue || 0).toLocaleString()}
                                                                                    </strong>
                                                                                )}
                                                                            </span>
                                                                        </div>

                                                                        {/* Arrow button to open/close this deal's details */}
                                                                        <button
                                                                            type="button"
                                                                            className={`pipeline-accordion-toggle-btn ${!isDealCollapsed ? 'open' : ''}`}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setCollapsedDeals(prev => ({ ...prev, [deal._id]: !prev[deal._id] }));
                                                                            }}
                                                                            title={isDealCollapsed ? 'Open Details' : 'Close Details'}
                                                                            style={{ marginLeft: 'auto', flexShrink: 0 }}
                                                                        >
                                                                            <ChevronDown size={14} />
                                                                        </button>
                                                                    </div>

                                                                    {!isDealCollapsed && (
                                                                        <div className="pipeline-detail-kv-grid" style={{ marginTop: '8px' }}>
                                                                            <div className="pipeline-detail-kv-row">
                                                                                <span className="pipeline-detail-k-label">Deal Value</span>
                                                                                <span className="pipeline-detail-v-val" style={{ fontWeight: 700, color: '#0284c7' }}>
                                                                                    {deal.currency || '$'} {Number(deal.dealValue || 0).toLocaleString()}
                                                                                </span>
                                                                            </div>
                                                                            <div className="pipeline-detail-kv-row">
                                                                                <span className="pipeline-detail-k-label">Stage</span>
                                                                                <span className="pipeline-detail-v-val">
                                                                                    <span
                                                                                        className={`pipeline-stage-badge ${stageTheme}`}
                                                                                        style={{
                                                                                            display: 'inline-flex',
                                                                                            alignItems: 'center',
                                                                                            gap: '5px',
                                                                                            padding: '3px 10px',
                                                                                            fontSize: '11px',
                                                                                            fontWeight: 700,
                                                                                            borderRadius: '999px',
                                                                                            lineHeight: '1.2'
                                                                                        }}
                                                                                    >
                                                                                        <span
                                                                                            style={{
                                                                                                width: '6px',
                                                                                                height: '6px',
                                                                                                borderRadius: '50%',
                                                                                                backgroundColor: 'currentColor',
                                                                                                display: 'inline-block',
                                                                                                flexShrink: 0
                                                                                            }}
                                                                                        />
                                                                                        {rawStage}
                                                                                    </span>
                                                                                </span>
                                                                            </div>
                                                                            <div className="pipeline-detail-kv-row">
                                                                                <span className="pipeline-detail-k-label">Expected Close</span>
                                                                                <span className="pipeline-detail-v-val">{formatDateDDMMYYYY(deal.expectedCloseDate)}</span>
                                                                            </div>
                                                                            <div className="pipeline-detail-kv-row">
                                                                                <span className="pipeline-detail-k-label">Deal Owner</span>
                                                                                <span className="pipeline-detail-v-val">{deal.salesOwner || 'Artiflow'}</span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <div style={{ textAlign: 'center', padding: '16px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                                                        <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>No deal associated with this contact yet.</p>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    clientName: `${detailedContact.firstName || ''} ${detailedContact.lastName || ''}`.trim(),
                                                                    associatedContact: detailedContact.email || '',
                                                                    email: detailedContact.email || '',
                                                                    phone: detailedContact.phone ? `${detailedContact.countryCode || '+1'} ${detailedContact.phone}` : '',
                                                                    associatedCompany: (detailedContact.company && detailedContact.company !== 'Direct Client') ? detailedContact.company : '',
                                                                    salesOwner: detailedContact.owner || 'Artiflow',
                                                                    stage: 'New Lead'
                                                                }));
                                                                setModalStage('New Lead');
                                                                setEditingDeal(null);
                                                                setShowCreateModal(true);
                                                            }}
                                                            style={{
                                                                marginTop: '8px',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                fontSize: '11.5px',
                                                                fontWeight: 600,
                                                                color: '#0284c7',
                                                                background: '#fff',
                                                                border: '1px solid #bae6fd',
                                                                borderRadius: '6px',
                                                                padding: '3px 10px',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <Plus size={12} />
                                                            <span>Create Associated Deal</span>
                                                        </button>
                                                    </div>
                                                )
                                            )}
                                        </div>

                                        {/* Section 2: Associated Company Details */}
                                        <div className="pipeline-detail-panel-section">
                                            <div
                                                className="pipeline-detail-section-heading"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => setContactAccordionState(prev => ({ ...prev, company: prev.company === false ? true : false }))}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span>Associated Company Details</span>
                                                    {matchedCompaniesForContact.length > 0 && (
                                                        <span className="pipeline-act-tab-badge" style={{ background: '#e0f2fe', color: '#0284c7', fontSize: '11px', fontWeight: 700, padding: '1px 7px', borderRadius: '10px' }}>
                                                            {matchedCompaniesForContact.length}
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={e => e.stopPropagation()}>
                                                    <button
                                                        type="button"
                                                        className="pipeline-detail-add-badge-btn"
                                                        onClick={() => {
                                                            setCompanyFormData({
                                                                name: (detailedContact.company && detailedContact.company !== 'Direct Client') ? detailedContact.company : '',
                                                                domain: '',
                                                                owner: [],
                                                                industry: '',
                                                                country: 'United States',
                                                                state: '',
                                                                city: detailedContact.location || '',
                                                                postalCode: ''
                                                            });
                                                            setShowCreateCompanyModal(true);
                                                        }}
                                                        title="Add / Link Company"
                                                    >
                                                        Add+
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`pipeline-accordion-toggle-btn ${contactAccordionState.company !== false ? 'open' : ''}`}
                                                        onClick={() => setContactAccordionState(prev => ({ ...prev, company: prev.company === false ? true : false }))}
                                                        title={contactAccordionState.company !== false ? 'Collapse Section' : 'Expand Section'}
                                                    >
                                                        <ChevronDown size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            {contactAccordionState.company !== false && (
                                                matchedCompaniesForContact.length === 0 ? (
                                                    <div className="pipeline-detail-empty-subcard">
                                                        <p style={{ margin: 0, fontSize: '11.5px', color: '#94a3b8' }}>
                                                            No company associated with this contact yet.
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="pipeline-assoc-scroll-container"
                                                        style={{
                                                            maxHeight: matchedCompaniesForContact.length > 1 ? '290px' : 'none',
                                                            overflowY: matchedCompaniesForContact.length > 1 ? 'auto' : 'visible',
                                                            paddingRight: matchedCompaniesForContact.length > 1 ? '6px' : '0'
                                                        }}
                                                    >
                                                        {matchedCompaniesForContact.map((company, idx) => {
                                                            const isCompCollapsed = !!collapsedCompanies[company._id];
                                                            return (
                                                                <div
                                                                    key={company._id || idx}
                                                                    style={{
                                                                        marginTop: idx > 0 ? '16px' : '0',
                                                                        paddingTop: idx > 0 ? '16px' : '0',
                                                                        borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none'
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                                                                        onClick={() => setCollapsedCompanies(prev => ({ ...prev, [company._id]: !prev[company._id] }))}
                                                                    >
                                                                        <div className="pipeline-detail-clean-svg-icon company">
                                                                            <Building2 size={19} strokeWidth={2} />
                                                                        </div>
                                                                        <div style={{ overflow: 'hidden', flex: 1 }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                <h4
                                                                                    style={{
                                                                                        margin: 0,
                                                                                        fontSize: '13.5px',
                                                                                        fontWeight: 700,
                                                                                        color: '#0f172a',
                                                                                        whiteSpace: 'nowrap',
                                                                                        textOverflow: 'ellipsis',
                                                                                        overflow: 'hidden',
                                                                                        cursor: 'pointer'
                                                                                    }}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setViewingCompany(company);
                                                                                    }}
                                                                                    title="Click to view Company Details"
                                                                                >
                                                                                    {company.name}
                                                                                </h4>
                                                                                {company.sourceLabel && matchedCompaniesForContact.length > 1 && (
                                                                                    <span style={{ fontSize: '10px', background: '#e0f2fe', color: '#0369a1', padding: '1px 6px', borderRadius: '4px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                                                                        {company.sourceLabel}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            {company.domain ? (
                                                                                <a
                                                                                    href={`https://${company.domain.replace(/^https?:\/\//, '')}`}
                                                                                    target="_blank"
                                                                                    rel="noreferrer"
                                                                                    style={{ fontSize: '12px', color: '#0284c7', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}
                                                                                    onClick={e => e.stopPropagation()}
                                                                                >
                                                                                    <Globe size={11} /> {company.domain} <ExternalLink size={10} />
                                                                                </a>
                                                                            ) : (
                                                                                <span style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                                                                                    Client organization
                                                                                </span>
                                                                            )}
                                                                        </div>

                                                                        {/* Arrow button to open/close this company's details */}
                                                                        <button
                                                                            type="button"
                                                                            className={`pipeline-accordion-toggle-btn ${!isCompCollapsed ? 'open' : ''}`}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setCollapsedCompanies(prev => ({ ...prev, [company._id]: !prev[company._id] }));
                                                                            }}
                                                                            title={isCompCollapsed ? 'Open Details' : 'Close Details'}
                                                                            style={{ marginLeft: 'auto', flexShrink: 0 }}
                                                                        >
                                                                            <ChevronDown size={14} />
                                                                        </button>
                                                                    </div>

                                                                    {!isCompCollapsed && (
                                                                        <div className="pipeline-detail-kv-grid" style={{ marginTop: '6px' }}>
                                                                            <div className="pipeline-detail-kv-row">
                                                                                <span className="pipeline-detail-k-label">Industry Type</span>
                                                                                <span className="pipeline-detail-v-val">{company.industry || '—'}</span>
                                                                            </div>
                                                                            <div className="pipeline-detail-kv-row">
                                                                                <span className="pipeline-detail-k-label">Company Owner</span>
                                                                                <span className="pipeline-detail-v-val">
                                                                                    <CompanyOwnerDisplay owner={company.owner} fallback={detailedContact.owner} />
                                                                                </span>
                                                                            </div>
                                                                            <div className="pipeline-detail-kv-row">
                                                                                <span className="pipeline-detail-k-label">Country</span>
                                                                                <span className="pipeline-detail-v-val">{company.country || '—'}</span>
                                                                            </div>
                                                                            <div className="pipeline-detail-kv-row">
                                                                                <span className="pipeline-detail-k-label">State / Region</span>
                                                                                <span className="pipeline-detail-v-val">{company.state || '—'}</span>
                                                                            </div>
                                                                            <div className="pipeline-detail-kv-row">
                                                                                <span className="pipeline-detail-k-label">City</span>
                                                                                <span className="pipeline-detail-v-val">{company.city || detailedContact.location || '—'}</span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="pipeline-tab-page-view">
                            {/* Top Header */}
                            <div className="pipeline-top-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <h1 className="pipeline-deals-count-title">Contacts</h1>
                                    {selectedKanbanContactIds.length > 0 && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span className="pipeline-stage-selected-pill" style={{ padding: '4px 10px', fontSize: '12px' }} title={`${selectedKanbanContactIds.length} contact(s) selected`}>
                                                <Check size={12} strokeWidth={3} />
                                                <span>{selectedKanbanContactIds.length} selected</span>
                                            </span>

                                            {canUpdate && (
                                                <div style={{ position: 'relative' }}>
                                                    <button
                                                        type="button"
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '5px',
                                                            padding: '5px 11px',
                                                            borderRadius: '6px',
                                                            background: '#e0f2fe',
                                                            color: '#0284c7',
                                                            border: '1px solid #bae6fd',
                                                            fontSize: '12px',
                                                            fontWeight: 600,
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setIsTopBulkMoveDropdownOpen(prev => !prev);
                                                        }}
                                                        title="Move all selected contacts to another stage"
                                                    >
                                                        <ArrowRightLeft size={12} />
                                                        <span>Move Stage</span>
                                                        <ChevronDown size={11} style={{ transform: isTopBulkMoveDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
                                                    </button>

                                                    {isTopBulkMoveDropdownOpen && (
                                                        <div
                                                            className="pipeline-card-dropdown-menu"
                                                            style={{ top: 'calc(100% + 4px)', left: 0, minWidth: '180px', zIndex: 120, boxShadow: '0 10px 25px rgba(0,0,0,0.15)' }}
                                                            onClick={e => e.stopPropagation()}
                                                        >
                                                            <div className="pipeline-submenu-header">
                                                                <span className="pipeline-submenu-title">Move {selectedKanbanContactIds.length} to:</span>
                                                            </div>
                                                            {effectiveContactStages.map(s => {
                                                                const stageName = typeof s === 'string' ? s : (s.name || s.id);
                                                                return (
                                                                    <button
                                                                        key={s.id || stageName}
                                                                        type="button"
                                                                        className="pipeline-card-menu-item"
                                                                        onClick={() => {
                                                                            setIsTopBulkMoveDropdownOpen(false);
                                                                            handleBulkMoveContactStage(stageName);
                                                                        }}
                                                                    >
                                                                        <span className={`pipeline-menu-indicator ${s.badgeClass || 'new'}`} />
                                                                        <span>{stageName}</span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {canDelete && (
                                                <button
                                                    type="button"
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '5px',
                                                        padding: '5px 11px',
                                                        borderRadius: '6px',
                                                        background: '#fee2e2',
                                                        color: '#ef4444',
                                                        border: '1px solid #fecaca',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => handleBulkDeleteContacts()}
                                                    title="Delete all selected contacts"
                                                >
                                                    <Trash2 size={12} />
                                                    <span>Delete ({selectedKanbanContactIds.length})</span>
                                                </button>
                                            )}

                                            <button
                                                type="button"
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    padding: '5px 9px',
                                                    borderRadius: '6px',
                                                    background: '#f1f5f9',
                                                    color: '#64748b',
                                                    border: '1px solid #e2e8f0',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => {
                                                    setSelectedKanbanContactIds([]);
                                                    setIsTopBulkMoveDropdownOpen(false);
                                                    setIsBulkMoveContactStageDropdownOpen(false);
                                                }}
                                                title="Deselect all contacts"
                                            >
                                                <X size={12} />
                                                <span>Deselect</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {/* View Switcher: Table List or Kanban Board */}
                                    <div className="pipeline-view-switcher">
                                        <button
                                            type="button"
                                            className={`pipeline-view-btn ${contactViewMode === 'list' ? 'active' : ''}`}
                                            onClick={() => setContactViewMode('list')}
                                            title="List Table View"
                                        >
                                            <List size={16} />
                                        </button>
                                        <button
                                            type="button"
                                            className={`pipeline-view-btn ${contactViewMode === 'kanban' ? 'active' : ''}`}
                                            onClick={() => setContactViewMode('kanban')}
                                            title="Kanban Board View"
                                        >
                                            <Kanban size={16} />
                                        </button>
                                    </div>

                                    {/* Export */}
                                    <button
                                        type="button"
                                        className="pipeline-btn-export"
                                        onClick={handleExportContacts}
                                        title="Export contacts to CSV"
                                    >
                                        <Download size={14} />
                                        <span>Export</span>
                                    </button>

                                    <NotificationBell filterType="CRM" />
                                </div>
                            </div>

                            {/* Action Toolbar */}
                            <div className="pipeline-toolbar-row">
                                <div className="pipeline-toolbar-left">
                                    {/* Search Box */}
                                    <div className="pipeline-contacts-search-box">
                                        <Search size={14} className="pipeline-search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Search contacts..."
                                            value={contactSearchQuery}
                                            onChange={e => setContactSearchQuery(e.target.value)}
                                            className="pipeline-contacts-search-input"
                                        />
                                        {contactSearchQuery && (
                                            <button
                                                type="button"
                                                className="pipeline-search-clear-btn"
                                                onClick={() => setContactSearchQuery('')}
                                            >
                                                <X size={12} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Sort by Dropdown */}
                                    <div className="pipeline-filter-dropdown-wrap">
                                        <button
                                            type="button"
                                            className={`pipeline-tool-btn ${contactSortBy !== 'newest' ? 'filter-active' : ''} ${isContactSortDropdownOpen ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsContactOwnerDropdownOpen(false);
                                                setIsContactStatusDropdownOpen(false);
                                                setIsContactOriginDropdownOpen(false);
                                                setIsContactDateDropdownOpen(false);
                                                setIsContactSortDropdownOpen(prev => !prev);
                                            }}
                                            title="Sort Contacts"
                                        >
                                            <ArrowUpDown size={14} />
                                            <span>{contactSortBy !== 'newest' ? `Sort: ${contactSortLabel}` : 'Sort by'}</span>
                                            <ChevronDown size={13} style={{ color: contactSortBy !== 'newest' ? '#0284c7' : '#94a3b8' }} />
                                        </button>

                                        {isContactSortDropdownOpen && (
                                            <div className="pipeline-filter-menu-popover" onClick={e => e.stopPropagation()}>
                                                <div className="pipeline-filter-menu-header">Sort contacts by</div>
                                                <div className="pipeline-filter-items-scroll">
                                                    {[
                                                        { id: 'newest', label: 'Newest first' },
                                                        { id: 'oldest', label: 'Oldest first' },
                                                        { id: 'name_asc', label: 'Contact Name (A - Z)' },
                                                        { id: 'name_desc', label: 'Contact Name (Z - A)' },
                                                        { id: 'company', label: 'Company Name (A - Z)' },
                                                        { id: 'stage', label: 'Lead Stage' },
                                                        { id: 'status', label: 'Lead Status' }
                                                    ].map(opt => (
                                                        <button
                                                            key={opt.id}
                                                            type="button"
                                                            className={`pipeline-filter-item-btn ${contactSortBy === opt.id ? 'selected' : ''}`}
                                                            onClick={() => {
                                                                setContactSortBy(opt.id);
                                                                setIsContactSortDropdownOpen(false);
                                                            }}
                                                        >
                                                            <span>{opt.label}</span>
                                                            {contactSortBy === opt.id && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* All Contact Owners Dropdown (Sales Head, Sales Manager, and Admin only) */}
                                    {hasFullAccess && (
                                        <div className="pipeline-filter-dropdown-wrap">
                                            <button
                                                type="button"
                                                className={`pipeline-tool-btn ${contactOwnerFilter !== 'all' ? 'filter-active' : ''} ${isContactOwnerDropdownOpen ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsContactSortDropdownOpen(false);
                                                    setIsContactStatusDropdownOpen(false);
                                                    setIsContactOriginDropdownOpen(false);
                                                    setIsContactDateDropdownOpen(false);
                                                    setIsContactOwnerDropdownOpen(prev => !prev);
                                                }}
                                                title="Filter by contact owners"
                                            >
                                                <User size={14} />
                                                <span>{contactOwnerFilter === 'all' ? 'All contact owners' : contactOwnerFilter}</span>
                                                <ChevronDown size={13} style={{ color: contactOwnerFilter !== 'all' ? '#0284c7' : '#94a3b8' }} />
                                            </button>

                                            {isContactOwnerDropdownOpen && (
                                                <div className="pipeline-filter-menu-popover" style={{ minWidth: '240px' }} onClick={e => e.stopPropagation()}>
                                                    <div className="pipeline-filter-menu-header">Filter by Contact Owner</div>
                                                    {allContactOwners.length > 5 && (
                                                        <div className="pipeline-filter-search-wrap">
                                                            <input
                                                                type="text"
                                                                className="pipeline-filter-search-input"
                                                                placeholder="Search contact owner..."
                                                                value={contactOwnerSearchQuery}
                                                                onChange={e => setContactOwnerSearchQuery(e.target.value)}
                                                                autoFocus
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="pipeline-filter-items-scroll">
                                                        <button
                                                            type="button"
                                                            className={`pipeline-filter-item-btn ${contactOwnerFilter === 'all' ? 'selected' : ''}`}
                                                            onClick={() => {
                                                                setContactOwnerFilter('all');
                                                                setIsContactOwnerDropdownOpen(false);
                                                                setContactOwnerSearchQuery('');
                                                            }}
                                                        >
                                                            <User size={14} style={{ color: '#64748b' }} />
                                                            <span>All contact owners</span>
                                                            <span className="pipeline-filter-item-badge">{contacts.filter(c => isItemOwnedByUser(c, 'contact')).length}</span>
                                                            {contactOwnerFilter === 'all' && <Check size={14} style={{ color: '#0284c7' }} />}
                                                        </button>

                                                        {allContactOwners
                                                            .filter(ownerName => !contactOwnerSearchQuery.trim() || ownerName.toLowerCase().includes(contactOwnerSearchQuery.trim().toLowerCase()))
                                                            .map(ownerName => {
                                                                const count = contactOwnerCounts[ownerName] || contactOwnerCounts[ownerName.toLowerCase()] || 0;
                                                                return (
                                                                    <button
                                                                        key={ownerName}
                                                                        type="button"
                                                                        className={`pipeline-filter-item-btn ${contactOwnerFilter.toLowerCase() === ownerName.toLowerCase() ? 'selected' : ''}`}
                                                                        onClick={() => {
                                                                            setContactOwnerFilter(ownerName);
                                                                            setIsContactOwnerDropdownOpen(false);
                                                                            setContactOwnerSearchQuery('');
                                                                        }}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                width: 18,
                                                                                height: 18,
                                                                                borderRadius: '50%',
                                                                                background: '#e0f2fe',
                                                                                color: '#0284c7',
                                                                                fontSize: '10px',
                                                                                fontWeight: 700,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center'
                                                                            }}
                                                                        >
                                                                            {ownerName.charAt(0).toUpperCase()}
                                                                        </div>
                                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ownerName}</span>
                                                                        <span className="pipeline-filter-item-badge">{count}</span>
                                                                        {contactOwnerFilter.toLowerCase() === ownerName.toLowerCase() && (
                                                                            <Check size={14} style={{ color: '#0284c7' }} />
                                                                        )}
                                                                    </button>
                                                                );
                                                            })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Filter by Status Dropdown */}
                                    <div className="pipeline-filter-dropdown-wrap">
                                        <button
                                            type="button"
                                            className={`pipeline-tool-btn ${contactStatusFilter !== 'all' ? 'filter-active' : ''} ${isContactStatusDropdownOpen ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsContactSortDropdownOpen(false);
                                                setIsContactOwnerDropdownOpen(false);
                                                setIsContactOriginDropdownOpen(false);
                                                setIsContactDateDropdownOpen(false);
                                                setIsContactStatusDropdownOpen(prev => !prev);
                                            }}
                                            title="Filter by status"
                                        >
                                            <Tag size={14} />
                                            <span>{contactStatusFilter === 'all' ? 'All statuses' : contactStatusFilter}</span>
                                            <ChevronDown size={13} style={{ color: contactStatusFilter !== 'all' ? '#0284c7' : '#94a3b8' }} />
                                        </button>

                                        {isContactStatusDropdownOpen && (
                                            <div className="pipeline-filter-menu-popover" style={{ minWidth: '200px' }} onClick={e => e.stopPropagation()}>
                                                <div className="pipeline-filter-menu-header">Filter by Status</div>
                                                <div className="pipeline-filter-items-scroll">
                                                    <button
                                                        type="button"
                                                        className={`pipeline-filter-item-btn ${contactStatusFilter === 'all' ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setContactStatusFilter('all');
                                                            setIsContactStatusDropdownOpen(false);
                                                        }}
                                                    >
                                                        <span>All statuses</span>
                                                        {contactStatusFilter === 'all' && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                    </button>
                                                    {(['New', 'Contacted', 'In Progress', 'Qualified', 'Unqualified', 'Won', 'Lost', ...leadStatuses.filter(s => !['New', 'Contacted', 'In Progress', 'Qualified', 'Unqualified', 'Won', 'Lost'].some(x => x.toLowerCase() === s.toLowerCase()))]).map(status => (
                                                        <button
                                                            key={status}
                                                            type="button"
                                                            className={`pipeline-filter-item-btn ${contactStatusFilter.toLowerCase() === status.toLowerCase() ? 'selected' : ''}`}
                                                            onClick={() => {
                                                                setContactStatusFilter(status);
                                                                setIsContactStatusDropdownOpen(false);
                                                            }}
                                                        >
                                                            <span>{status}</span>
                                                            {contactStatusFilter.toLowerCase() === status.toLowerCase() && (
                                                                <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Filter by Origin Dropdown */}
                                    <div className="pipeline-filter-dropdown-wrap">
                                        <button
                                            type="button"
                                            className={`pipeline-tool-btn ${contactOriginFilter !== 'all' ? 'filter-active' : ''} ${isContactOriginDropdownOpen ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsContactSortDropdownOpen(false);
                                                setIsContactOwnerDropdownOpen(false);
                                                setIsContactStatusDropdownOpen(false);
                                                setIsContactDateDropdownOpen(false);
                                                setIsContactOriginDropdownOpen(prev => !prev);
                                            }}
                                            title="Filter contacts by origin"
                                        >
                                            <Globe size={14} />
                                            <span>{contactOriginFilter === 'all' ? 'All origins' : contactOriginFilter}</span>
                                            <ChevronDown size={13} style={{ color: contactOriginFilter !== 'all' ? '#0284c7' : '#94a3b8' }} />
                                        </button>

                                        {isContactOriginDropdownOpen && (
                                            <div className="pipeline-filter-menu-popover" style={{ minWidth: '200px' }} onClick={e => e.stopPropagation()}>
                                                <div className="pipeline-filter-menu-header">Filter by Origin</div>
                                                <div className="pipeline-filter-items-scroll">
                                                    <button
                                                        type="button"
                                                        className={`pipeline-filter-item-btn ${contactOriginFilter === 'all' ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setContactOriginFilter('all');
                                                            setIsContactOriginDropdownOpen(false);
                                                        }}
                                                    >
                                                        <span>All origins</span>
                                                        <span className="pipeline-filter-item-badge">{contactOriginCounts.all}</span>
                                                        {contactOriginFilter === 'all' && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`pipeline-filter-item-btn ${contactOriginFilter === 'Domestic' ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setContactOriginFilter('Domestic');
                                                            setIsContactOriginDropdownOpen(false);
                                                        }}
                                                    >
                                                        <span>Domestic</span>
                                                        <span className="pipeline-filter-item-badge">{contactOriginCounts.domestic}</span>
                                                        {contactOriginFilter === 'Domestic' && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`pipeline-filter-item-btn ${contactOriginFilter === 'International' ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setContactOriginFilter('International');
                                                            setIsContactOriginDropdownOpen(false);
                                                        }}
                                                    >
                                                        <span>International</span>
                                                        <span className="pipeline-filter-item-badge">{contactOriginCounts.international}</span>
                                                        {contactOriginFilter === 'International' && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Date Filter Dropdown */}
                                    <div className="pipeline-filter-dropdown-wrap">
                                        <button
                                            type="button"
                                            className={`pipeline-tool-btn ${contactDateFilter !== 'all' ? 'filter-active' : ''} ${isContactDateDropdownOpen ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsContactSortDropdownOpen(false);
                                                setIsContactOwnerDropdownOpen(false);
                                                setIsContactStatusDropdownOpen(false);
                                                setIsContactOriginDropdownOpen(false);
                                                setIsContactDateDropdownOpen(prev => !prev);
                                            }}
                                            title="Filter contacts by date"
                                        >
                                            <Calendar size={14} />
                                            <span>{contactDateFilterLabel}</span>
                                            <ChevronDown size={13} style={{ color: contactDateFilter !== 'all' ? '#0284c7' : '#94a3b8' }} />
                                        </button>

                                        {isContactDateDropdownOpen && (
                                            <div className="pipeline-filter-menu-popover" style={{ minWidth: '230px' }} onClick={e => e.stopPropagation()}>
                                                <div className="pipeline-filter-menu-header">Filter by Creation Date</div>
                                                <div className="pipeline-filter-items-scroll">
                                                    {[
                                                        { id: 'all', label: 'All Dates (All Time)' },
                                                        { id: 'today', label: 'Today' },
                                                        { id: 'this_week', label: 'This Week' },
                                                        { id: 'this_month', label: 'This Month' },
                                                        { id: 'next_30_days', label: 'Next 30 Days' },
                                                        { id: 'this_quarter', label: 'This Quarter' },
                                                        { id: 'this_year', label: 'This Year' },
                                                        { id: 'custom', label: 'Custom Date Range...' }
                                                    ].map(opt => (
                                                        <button
                                                            key={opt.id}
                                                            type="button"
                                                            className={`pipeline-filter-item-btn ${contactDateFilter === opt.id ? 'selected' : ''}`}
                                                            onClick={() => {
                                                                setContactDateFilter(opt.id);
                                                                if (opt.id !== 'custom') {
                                                                    setIsContactDateDropdownOpen(false);
                                                                }
                                                            }}
                                                        >
                                                            <span>{opt.label}</span>
                                                            {contactDateFilter === opt.id && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                        </button>
                                                    ))}
                                                </div>

                                                {contactDateFilter === 'custom' && (
                                                    <div className="pipeline-filter-date-custom-panel">
                                                        <div className="pipeline-filter-date-input-group">
                                                            <label className="pipeline-filter-date-label">From Date</label>
                                                            <input
                                                                type="date"
                                                                className="pipeline-filter-date-input"
                                                                value={customContactDateStart}
                                                                onChange={e => setCustomContactDateStart(e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="pipeline-filter-date-input-group">
                                                            <label className="pipeline-filter-date-label">To Date</label>
                                                            <input
                                                                type="date"
                                                                className="pipeline-filter-date-input"
                                                                value={customContactDateEnd}
                                                                onChange={e => setCustomContactDateEnd(e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="pipeline-filter-date-actions">
                                                            <button
                                                                type="button"
                                                                className="pipeline-filter-date-btn-cancel"
                                                                onClick={() => {
                                                                    setCustomContactDateStart('');
                                                                    setCustomContactDateEnd('');
                                                                    setContactDateFilter('all');
                                                                    setIsContactDateDropdownOpen(false);
                                                                }}
                                                            >
                                                                Clear
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="pipeline-filter-date-btn-apply"
                                                                onClick={() => setIsContactDateDropdownOpen(false)}
                                                            >
                                                                Apply
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Reset / Clear All Filters */}
                                    {isAnyContactFilterActive && (
                                        <button
                                            type="button"
                                            className="pipeline-filter-clear-all-btn"
                                            onClick={handleResetContactFilters}
                                            title="Clear all filters"
                                        >
                                            <X size={13} />
                                            <span>Reset Filters</span>
                                        </button>
                                    )}
                                </div>

                                <div className="pipeline-toolbar-right">
                                    {/* Create Contacts Primary Button */}
                                    {canCreate && (
                                        <button
                                            type="button"
                                            className="pipeline-btn-create-deal"
                                            onClick={handleOpenCreateContact}
                                            title="Create new contact"
                                        >
                                            <Plus size={15} />
                                            <span>Create contacts</span>
                                            <ChevronDown size={13} />
                                        </button>
                                    )}

                                    {/* More Options */}
                                    {canCreate && (
                                        <div style={{ position: 'relative' }}>
                                            <button
                                                type="button"
                                                className={`pipeline-btn-more ${isContactsToolbarMenuOpen ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsContactsToolbarMenuOpen(prev => !prev);
                                                }}
                                                title="More Options"
                                            >
                                                <MoreHorizontal size={16} />
                                            </button>
                                            {isContactsToolbarMenuOpen && (
                                                <div
                                                    className="pipeline-card-dropdown-menu"
                                                    style={{ top: 'calc(100% + 6px)', right: 0, minWidth: '160px', zIndex: 100 }}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <button
                                                        type="button"
                                                        className="pipeline-card-menu-item"
                                                        onClick={() => {
                                                            setIsContactsToolbarMenuOpen(false);
                                                            setShowAddStageModal(true);
                                                        }}
                                                    >
                                                        <div className="pipeline-card-menu-item-left">
                                                            <Plus size={14} style={{ color: '#0284c7' }} />
                                                            <span>Add Stage</span>
                                                        </div>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Contacts Content Body: Kanban Stage Columns or Table View */}
                            <div className="pipeline-board-container">
                                {isLoading ? (
                                    contactViewMode === 'kanban' ? renderKanbanSkeleton(effectiveContactStages.length || 4, true) : renderTableSkeleton(7)
                                ) : contactViewMode === 'kanban' ? (
                                    effectiveContactStages.length === 0 ? (
                                        <div className="pipeline-empty-state-card" style={{ maxWidth: '480px', margin: '40px auto' }}>
                                            <div className="pipeline-empty-icon-wrap" style={{ background: '#f0f9ff', color: '#0284c7' }}>
                                                <Users size={32} />
                                            </div>
                                            <h3 className="pipeline-empty-state-title">No Contact Stages</h3>
                                            <p className="pipeline-empty-state-desc">
                                                No contact stages currently exist. Click below to add a custom stage column.
                                            </p>
                                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '12px' }}>
                                                <button
                                                    type="button"
                                                    className="pipeline-empty-state-action-btn"
                                                    onClick={() => setShowAddStageModal(true)}
                                                >
                                                    <Plus size={14} />
                                                    <span>Add Stage</span>
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="pipeline-kanban-board">
                                            {effectiveContactStages.map((stage, sIndex) => {
                                                const activeStageNames = new Set(effectiveContactStages.map(s => (s.name || s.id || '').toLowerCase()));
                                                const stageContacts = filteredContacts.filter(c => {
                                                    const cStage = (c.leadStage || '').toLowerCase().trim();
                                                    const sName = (stage.name || '').toLowerCase().trim();
                                                    const sId = (stage.id || '').toLowerCase().trim();
                                                    if (cStage === sName || cStage === sId) return true;
                                                    if ((!cStage || !activeStageNames.has(cStage)) && sIndex === 0) {
                                                        return true;
                                                    }
                                                    return false;
                                                });

                                                const stageColKey = `contact-stage-col-${sIndex}`;
                                                const isStageEditing = editingContactStageId === stageColKey;

                                                return (
                                                    <div
                                                        key={stageColKey}
                                                        className={`pipeline-column ${dragOverContactColumnId === stageColKey ? 'drag-over' : ''}`}
                                                        onDragOver={e => handleContactDragOver(e, stageColKey)}
                                                        onDragLeave={e => handleContactDragLeave(e, stageColKey)}
                                                        onDrop={e => handleContactDrop(e, stage.name)}
                                                    >
                                                        {/* Column Header */}
                                                        <div className="pipeline-column-header">
                                                            {isStageEditing ? (
                                                                <div className="pipeline-inline-edit-wrap">
                                                                    <input
                                                                        type="text"
                                                                        className="pipeline-inline-edit-input"
                                                                        value={editingContactStageName}
                                                                        autoFocus
                                                                        onChange={e => setEditingContactStageName(e.target.value)}
                                                                        onKeyDown={e => {
                                                                            if (e.key === 'Enter') handleSaveEditContactStage(stageColKey, sIndex);
                                                                            if (e.key === 'Escape') setEditingContactStageId(null);
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="pipeline-inline-save-btn"
                                                                        onClick={() => handleSaveEditContactStage(stageColKey, sIndex)}
                                                                        title="Save stage name"
                                                                    >
                                                                        <CheckCircle size={13} />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="pipeline-inline-cancel-btn"
                                                                        onClick={() => setEditingContactStageId(null)}
                                                                        title="Cancel"
                                                                    >
                                                                        <X size={13} />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <div className="pipeline-stage-info">
                                                                        <h3 className="pipeline-stage-name" title={stage.name}>
                                                                            {stage.name}
                                                                        </h3>
                                                                        <span className={`pipeline-stage-badge ${stage.badgeClass || 'new'}`}>
                                                                            {stageContacts.length}
                                                                        </span>
                                                                        {(() => {
                                                                            const stageSelectedCount = stageContacts.filter(c => selectedKanbanContactIds.includes(c._id)).length;
                                                                            return stageSelectedCount > 0 ? (
                                                                                <span className="pipeline-stage-selected-pill" title={`${stageSelectedCount} of ${stageContacts.length} contacts selected`}>
                                                                                    <Check size={10} strokeWidth={3} />
                                                                                    <span>{stageSelectedCount}</span>
                                                                                </span>
                                                                            ) : null;
                                                                        })()}
                                                                    </div>

                                                                    <div className="pipeline-col-header-actions" style={{ position: 'relative' }}>
                                                                        {(() => {
                                                                            const stageSelectedCount = stageContacts.filter(c => selectedKanbanContactIds.includes(c._id)).length;
                                                                            const isAllStageSelected = stageContacts.length > 0 && stageSelectedCount === stageContacts.length;
                                                                            if (stageContacts.length > 0 && (selectedKanbanContactIds.length > 0 || stageSelectedCount > 0)) {
                                                                                return (
                                                                                    <button
                                                                                        type="button"
                                                                                        className={`pipeline-stage-select-all-btn ${isAllStageSelected ? 'all-selected' : ''}`}
                                                                                        onClick={(e) => handleToggleContactStageSelection(stageContacts, e)}
                                                                                        title={isAllStageSelected ? "Deselect all contacts in this stage" : "Select all contacts in this stage"}
                                                                                    >
                                                                                        {isAllStageSelected ? 'Deselect' : 'Select all'}
                                                                                    </button>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        })()}
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-col-action-btn more"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setActiveColMenuId(activeColMenuId === stageColKey ? null : stageColKey);
                                                                            }}
                                                                            title="Stage options"
                                                                        >
                                                                            <MoreHorizontal size={14} />
                                                                        </button>

                                                                        {activeColMenuId === stageColKey && (
                                                                            <div
                                                                                className="pipeline-card-dropdown-menu"
                                                                                style={{ top: 'calc(100% + 4px)', right: 0, minWidth: '160px', zIndex: 90 }}
                                                                                onClick={e => e.stopPropagation()}
                                                                            >
                                                                                {canCreate && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-card-menu-item"
                                                                                        onClick={() => {
                                                                                            setActiveColMenuId(null);
                                                                                            handleOpenCreateContact();
                                                                                            setContactFormData(prev => ({ ...prev, leadStage: stage.name }));
                                                                                        }}
                                                                                    >
                                                                                        <div className="pipeline-card-menu-item-left">
                                                                                            <Plus size={13} style={{ color: '#0284c7' }} />
                                                                                            <span>Add Contact</span>
                                                                                        </div>
                                                                                    </button>
                                                                                )}
                                                                                {hasFullAccess && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-card-menu-item"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            setActiveColMenuId(null);
                                                                                            setEditingStageModal({
                                                                                                type: 'contactStage',
                                                                                                id: stageColKey,
                                                                                                name: stage.name,
                                                                                                originalName: stage.name,
                                                                                                index: sIndex
                                                                                            });
                                                                                        }}
                                                                                    >
                                                                                        <div className="pipeline-card-menu-item-left">
                                                                                            <Edit2 size={13} style={{ color: '#64748b' }} />
                                                                                            <span>Edit Stage</span>
                                                                                        </div>
                                                                                    </button>
                                                                                )}
                                                                                {hasFullAccess && (
                                                                                    <>
                                                                                        <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                                                                                        <button
                                                                                            type="button"
                                                                                            className="pipeline-card-menu-item danger"
                                                                                            onClick={(e) => handleDeleteContactStage(stageColKey, e, sIndex, stage.name)}
                                                                                        >
                                                                                            <div className="pipeline-card-menu-item-left">
                                                                                                <Trash2 size={13} />
                                                                                                <span>Delete Stage</span>
                                                                                            </div>
                                                                                        </button>
                                                                                    </>
                                                                                )}

                                                                                {stageContacts.length > 0 && (
                                                                                    <>
                                                                                        <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                                                                                        {canUpdate && (
                                                                                            <>
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="pipeline-card-menu-item"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        setColMoveSubmenuStageId(colMoveSubmenuStageId === stage.id ? null : stage.id);
                                                                                                    }}
                                                                                                    title={`Move all ${stageContacts.length} contacts to another stage`}
                                                                                                >
                                                                                                    <div className="pipeline-card-menu-item-left">
                                                                                                        <ArrowRightLeft size={13} style={{ color: '#0284c7' }} />
                                                                                                        <span>Move All Contacts ({stageContacts.length})</span>
                                                                                                    </div>
                                                                                                    <ChevronRight size={12} style={{ transform: colMoveSubmenuStageId === stage.id ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }} />
                                                                                                </button>

                                                                                                {colMoveSubmenuStageId === stage.id && (
                                                                                                    <div style={{ background: '#f8fafc', padding: '4px 0', borderRadius: '6px', margin: '2px 0 4px 0', border: '1px solid #e2e8f0' }}>
                                                                                                        <div style={{ padding: '3px 12px', fontSize: '10.5px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                                                                                                            Move {stageContacts.length} contacts to:
                                                                                                        </div>
                                                                                                        {effectiveContactStages
                                                                                                            .filter(s => {
                                                                                                                const sName = typeof s === 'string' ? s : (s.name || s.id);
                                                                                                                return (sName || '').toLowerCase().trim() !== (stage.name || '').toLowerCase().trim();
                                                                                                            })
                                                                                                            .map(s => {
                                                                                                                const sName = typeof s === 'string' ? s : (s.name || s.id);
                                                                                                                return (
                                                                                                                    <button
                                                                                                                        key={s.id || sName}
                                                                                                                        type="button"
                                                                                                                        className="pipeline-card-menu-item"
                                                                                                                        style={{ paddingLeft: '20px', fontSize: '12px' }}
                                                                                                                        onClick={() => {
                                                                                                                            setActiveColMenuId(null);
                                                                                                                            setColMoveSubmenuStageId(null);
                                                                                                                            handleBulkMoveContactStage(sName, stageContacts.map(c => c._id));
                                                                                                                        }}
                                                                                                                    >
                                                                                                                        <span className={`pipeline-menu-indicator ${s.badgeClass || 'new'}`} />
                                                                                                                        <span>{sName}</span>
                                                                                                                    </button>
                                                                                                                );
                                                                                                            })}
                                                                                                    </div>
                                                                                                )}
                                                                                            </>
                                                                                        )}
                                                                                        {canDelete && (
                                                                                            <button
                                                                                                type="button"
                                                                                                className="pipeline-card-menu-item danger"
                                                                                                onClick={() => {
                                                                                                    setActiveColMenuId(null);
                                                                                                    setColMoveSubmenuStageId(null);
                                                                                                    handleBulkDeleteContacts(stageContacts.map(c => c._id));
                                                                                                }}
                                                                                                title={`Delete all ${stageContacts.length} contacts in this stage`}
                                                                                            >
                                                                                                <div className="pipeline-card-menu-item-left">
                                                                                                    <Trash2 size={13} />
                                                                                                    <span>Delete All Contacts ({stageContacts.length})</span>
                                                                                                </div>
                                                                                            </button>
                                                                                        )}
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>

                                                        {/* Contact Cards List */}
                                                        <div
                                                            className="pipeline-cards-list"
                                                            onDragOver={e => handleContactDragOver(e, stage.id)}
                                                            onDrop={e => handleContactDrop(e, stage.name)}
                                                        >
                                                            {stageContacts.length === 0 ? (
                                                                <div className="pipeline-column-empty">
                                                                    <p>No contacts in this stage</p>
                                                                </div>
                                                            ) : (
                                                                stageContacts.map(contact => {
                                                                    const countryObj = COUNTRY_CODES.find(c => c.code === contact.countryCode) || COUNTRY_CODES[0];
                                                                    const displayName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unnamed Contact';
                                                                    const displayLocation = contact.location || ((contact.company && contact.company !== 'Direct Client') ? contact.company : '');

                                                                    const isSelected = selectedKanbanContactIds.includes(contact._id);

                                                                    return (
                                                                        <div
                                                                            key={contact._id}
                                                                            className={`pipeline-card ${isSelected ? 'selected' : ''} ${activeContactMenuId === contact._id || activeContactStatusDropdownId === contact._id ? 'menu-active' : ''} ${hoveredActivityContactId === contact._id ? 'activity-hovered' : ''} ${draggedContactId === contact._id ? 'dragging' : ''}`}
                                                                            draggable={canUpdate}
                                                                            onDragStart={e => handleContactDragStart(e, contact._id)}
                                                                            onDragEnd={handleContactDragEnd}
                                                                            onDoubleClick={(e) => handleContactCardDoubleClick(contact, stageContacts, stage.name, e)}
                                                                            title="Double-click to select all contacts in this stage"
                                                                        >
                                                                            {/* 1. Card Top */}
                                                                            <div className="pipeline-card-top">
                                                                                <div
                                                                                    className={`pipeline-card-select-checkbox ${isSelected ? 'selected' : ''} ${selectedKanbanContactIds.length > 0 ? 'visible' : ''}`}
                                                                                    onClick={(e) => handleToggleContactSelection(contact._id, e)}
                                                                                    title={isSelected ? "Deselect contact (Double-click card to select all in stage)" : "Select contact (Double-click card to select all in stage)"}
                                                                                >
                                                                                    {isSelected && <Check size={11} strokeWidth={3.5} />}
                                                                                </div>
                                                                                <div className="pipeline-card-person" style={{ cursor: 'pointer', alignItems: 'flex-start', minWidth: 0, overflow: 'hidden', flex: 1 }} onClick={(e) => handleContactCardClick(contact._id, e)}>
                                                                                    {contact.avatar ? (
                                                                                        <img
                                                                                            src={contact.avatar}
                                                                                            alt={displayName}
                                                                                            className="pipeline-card-avatar"
                                                                                            onError={e => { e.target.style.display = 'none'; }}
                                                                                        />
                                                                                    ) : (
                                                                                        <div className="pipeline-card-avatar">
                                                                                            {contact.firstName ? contact.firstName.slice(0, 2).toUpperCase() : 'CO'}
                                                                                        </div>
                                                                                    )}
                                                                                    <div className="pipeline-card-name-wrap" style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                                                                                        <h4 className="pipeline-card-client-name" style={{ margin: 0 }}>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="pipeline-card-client-name-link"
                                                                                                onClick={() => setDetailedContactId(contact._id)}
                                                                                                title={displayName}
                                                                                            >
                                                                                                {displayName}
                                                                                            </button>
                                                                                        </h4>

                                                                                        {/* Origin + Status badge directly below contact name */}
                                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px', flexWrap: 'wrap' }}>
                                                                                            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '3.5px' }}>
                                                                                                <Globe size={11} style={{ color: contact.origin === 'International' ? '#ea580c' : '#0284c7', flexShrink: 0 }} />
                                                                                                <span>{contact.origin || 'Domestic'}</span>
                                                                                            </span>
                                                                                            <span
                                                                                                className={`pipeline-stage-badge ${(() => {
                                                                                                    const st = (contact.leadStatus || 'New').toLowerCase();
                                                                                                    if (st === 'won' || st === 'qualified' || st === 'active' || st === 'open') return 'won';
                                                                                                    if (st === 'lost' || st === 'unqualified' || st === 'spam') return 'lost';
                                                                                                    if (st === 'hot' || st === 'in progress' || st === 'contacted') return 'hot';
                                                                                                    return 'new';
                                                                                                })()
                                                                                                    }`}
                                                                                                style={{ fontSize: '8px', padding: '0.8px 5px', borderRadius: '999px', fontWeight: 600, letterSpacing: '0.2px', lineHeight: '1.1' }}
                                                                                            >
                                                                                                {contact.leadStatus || 'New'}
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <div className="pipeline-card-menu-container">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-card-more-btn"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            if (activeContactMenuId === contact._id) {
                                                                                                setActiveContactMenuId(null);
                                                                                                setMoveSubmenuContactId(null);
                                                                                            } else {
                                                                                                setActiveContactMenuId(contact._id);
                                                                                                setMoveSubmenuContactId(null);
                                                                                            }
                                                                                        }}
                                                                                        title="Options"
                                                                                    >
                                                                                        <MoreHorizontal size={16} />
                                                                                    </button>

                                                                                    {/* Main Contact 3-dots Menu */}
                                                                                    {activeContactMenuId === contact._id && moveSubmenuContactId !== contact._id && (
                                                                                        <div className="pipeline-card-dropdown-menu" onClick={e => e.stopPropagation()}>
                                                                                            {canUpdate && (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="pipeline-card-menu-item"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        setMoveSubmenuContactId(contact._id);
                                                                                                    }}
                                                                                                >
                                                                                                    <div className="pipeline-card-menu-item-left">
                                                                                                        <ArrowRightLeft size={14} style={{ color: '#0284c7' }} />
                                                                                                        <span>Move</span>
                                                                                                    </div>
                                                                                                    <ChevronRight size={13} style={{ color: '#94a3b8' }} />
                                                                                                </button>
                                                                                            )}

                                                                                            <button
                                                                                                type="button"
                                                                                                className="pipeline-card-menu-item"
                                                                                                onClick={() => {
                                                                                                    setDetailedContactId(contact._id);
                                                                                                    setActiveContactMenuId(null);
                                                                                                }}
                                                                                            >
                                                                                                <div className="pipeline-card-menu-item-left">
                                                                                                    <Eye size={14} style={{ color: '#64748b' }} />
                                                                                                    <span>View Contact</span>
                                                                                                </div>
                                                                                            </button>

                                                                                            {canUpdate && (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="pipeline-card-menu-item"
                                                                                                    onClick={(e) => handleOpenEditContact(contact, e)}
                                                                                                >
                                                                                                    <div className="pipeline-card-menu-item-left">
                                                                                                        <Edit2 size={14} style={{ color: '#64748b' }} />
                                                                                                        <span>Edit Contact</span>
                                                                                                    </div>
                                                                                                </button>
                                                                                            )}

                                                                                            {canDelete && (
                                                                                                <>
                                                                                                    <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="pipeline-card-menu-item danger"
                                                                                                        onClick={(e) => handleDeleteContact(contact._id, e)}
                                                                                                    >
                                                                                                        <div className="pipeline-card-menu-item-left">
                                                                                                            <Trash2 size={14} />
                                                                                                            <span>Delete Contact</span>
                                                                                                        </div>
                                                                                                    </button>
                                                                                                </>
                                                                                            )}
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Nested Move Stage Submenu for Contact */}
                                                                                    {activeContactMenuId === contact._id && moveSubmenuContactId === contact._id && (
                                                                                        <div
                                                                                            className="pipeline-card-dropdown-menu submenu"
                                                                                            onClick={e => e.stopPropagation()}
                                                                                        >
                                                                                            <div className="pipeline-submenu-header">
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="pipeline-submenu-back"
                                                                                                    onClick={() => setMoveSubmenuContactId(null)}
                                                                                                >
                                                                                                    <ChevronsLeft size={12} />
                                                                                                    <span>Back</span>
                                                                                                </button>
                                                                                                <span className="pipeline-submenu-title">Move Stage</span>
                                                                                            </div>

                                                                                            {effectiveContactStages.map(s => {
                                                                                                const stageName = typeof s === 'string' ? s : (s.name || s.id);
                                                                                                const isCurrent = (contact.leadStage || '').toLowerCase() === stageName.toLowerCase();
                                                                                                return (
                                                                                                    <button
                                                                                                        key={s.id || stageName}
                                                                                                        type="button"
                                                                                                        className={`pipeline-card-menu-item ${isCurrent ? 'active' : ''}`}
                                                                                                        onClick={e => handleMoveContactStage(contact._id, stageName, e)}
                                                                                                    >
                                                                                                        <span className={`pipeline-menu-indicator ${s.badgeClass || 'new'}`} />
                                                                                                        <span>{stageName}</span>
                                                                                                    </button>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>

                                                                            {/* 2. Card Body Fields with Ellipsis Truncation */}
                                                                            <div className="pipeline-card-body" style={{ minWidth: 0, overflow: 'hidden' }}>
                                                                                <div className="pipeline-card-field" style={{ minWidth: 0, overflow: 'hidden' }}>
                                                                                    <span className="pipeline-card-label">Email</span>
                                                                                    {contact.email ? (
                                                                                        <a
                                                                                            href={`mailto:${contact.email}`}
                                                                                            className="pipeline-card-val-strong"
                                                                                            title={contact.email}
                                                                                            style={{
                                                                                                color: '#0284c7',
                                                                                                textDecoration: 'none',
                                                                                                fontSize: '12px',
                                                                                                display: 'block',
                                                                                                width: '100%',
                                                                                                maxWidth: '100%',
                                                                                                overflow: 'hidden',
                                                                                                textOverflow: 'ellipsis',
                                                                                                whiteSpace: 'nowrap'
                                                                                            }}
                                                                                        >
                                                                                            {contact.email}
                                                                                        </a>
                                                                                    ) : (
                                                                                        <span className="pipeline-card-val" style={{ color: '#94a3b8' }}>—</span>
                                                                                    )}
                                                                                </div>

                                                                                <div className="pipeline-card-field" style={{ minWidth: 0, overflow: 'hidden' }}>
                                                                                    <span className="pipeline-card-label">Phone</span>
                                                                                    <span
                                                                                        className="pipeline-card-val"
                                                                                        title={contact.phone ? `${countryObj.flag} ${formatPhoneNumber(contact.phone, contact.countryCode)}` : '—'}
                                                                                        style={{
                                                                                            display: 'block',
                                                                                            width: '100%',
                                                                                            maxWidth: '100%',
                                                                                            overflow: 'hidden',
                                                                                            textOverflow: 'ellipsis',
                                                                                            whiteSpace: 'nowrap'
                                                                                        }}
                                                                                    >
                                                                                        {contact.phone ? `${countryObj.flag} ${formatPhoneNumber(contact.phone, contact.countryCode)}` : '—'}
                                                                                    </span>
                                                                                </div>

                                                                                <div className="pipeline-card-field" style={{ minWidth: 0, overflow: 'hidden' }}>
                                                                                    <span className="pipeline-card-label">Added By</span>
                                                                                    <span
                                                                                        className="pipeline-card-val"
                                                                                        title={contact.addedBy || contact.owner || 'Admin System'}
                                                                                        style={{
                                                                                            display: 'block',
                                                                                            width: '100%',
                                                                                            maxWidth: '100%',
                                                                                            overflow: 'hidden',
                                                                                            textOverflow: 'ellipsis',
                                                                                            whiteSpace: 'nowrap'
                                                                                        }}
                                                                                    >
                                                                                        {contact.addedBy || contact.owner || 'Admin System'}
                                                                                    </span>
                                                                                </div>
                                                                            </div>

                                                                            {/* 3. Card Bottom Actions */}
                                                                            <div className="pipeline-card-actions">
                                                                                <div className="pipeline-card-actions-left">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-card-action-circle"
                                                                                        title="View Notes"
                                                                                        onClick={() => alert(`Notes for ${displayName}`)}
                                                                                    >
                                                                                        <FileText size={12} />
                                                                                    </button>
                                                                                    <a
                                                                                        href={`mailto:${contact.email}`}
                                                                                        className="pipeline-card-action-circle"
                                                                                        title="Send Mail"
                                                                                    >
                                                                                        <Mail size={12} />
                                                                                    </a>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pipeline-card-action-circle"
                                                                                        title="Send Message"
                                                                                        onClick={() => alert(`Message ${displayName}`)}
                                                                                    >
                                                                                        <MessageSquare size={12} />
                                                                                    </button>
                                                                                </div>

                                                                                <button
                                                                                    type="button"
                                                                                    className={`pipeline-card-view-activity-pill ${hoveredActivityContactId === contact._id ? 'active' : ''}`}
                                                                                    onMouseEnter={(e) => handleActivityMouseEnter(contact._id, 'contact', e)}
                                                                                    onMouseLeave={handleActivityMouseLeave}
                                                                                    onClick={() => {
                                                                                        setHoveredActivityDealId(null);
                                                                                        setHoveredActivityContactId(null);
                                                                                        setDetailedContactId(contact._id);
                                                                                    }}
                                                                                    title="View activity"
                                                                                >
                                                                                    <span>View activity</span>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )
                                ) : (
                                    /* Table List View */
                                    filteredContacts.length === 0 ? (
                                        <div className="pipeline-empty-state-card" style={{ maxWidth: '480px', margin: '40px auto' }}>
                                            <div className="pipeline-empty-icon-wrap" style={{ background: '#f0f9ff', color: '#0284c7' }}>
                                                <Users size={32} />
                                            </div>
                                            <h3 className="pipeline-empty-state-title">No Contacts Found</h3>
                                            <p className="pipeline-empty-state-desc">
                                                {contactSearchQuery || contactStatusFilter !== 'all'
                                                    ? 'No contacts match your current search or status filter.'
                                                    : 'No contacts registered yet. Click below to add your first contact.'}
                                            </p>
                                            <button
                                                type="button"
                                                className="pipeline-empty-state-action-btn"
                                                onClick={handleOpenCreateContact}
                                            >
                                                <Plus size={14} />
                                                <span>Add Contact</span>
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="pipeline-table-view">
                                            <div className="pipeline-table-card">
                                                <table className="pipeline-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Contact Name</th>
                                                            <th>Email Address</th>
                                                            <th>Phone Number</th>
                                                            <th>Origin</th>
                                                            <th>Company</th>
                                                            <th>Lead Stage</th>
                                                            <th>Lead Status</th>
                                                            <th>Source</th>
                                                            <th>Added By</th>
                                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredContacts.map(contact => {
                                                            const countryObj = COUNTRY_CODES.find(c => c.code === contact.countryCode) || COUNTRY_CODES[0];
                                                            const displayName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unnamed Contact';
                                                            return (
                                                                <tr key={contact._id}>
                                                                    <td>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', cursor: 'pointer' }} onClick={() => setDetailedContactId(contact._id)}>
                                                                            <div className="pipeline-card-avatar" style={{ width: '30px', height: '30px', fontSize: '12px' }}>
                                                                                {contact.firstName ? contact.firstName.charAt(0) : 'C'}
                                                                            </div>
                                                                            <span className="pipeline-card-client-name" style={{ fontWeight: 600, color: '#0284c7', cursor: 'pointer' }}>
                                                                                {displayName}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <a href={`mailto:${contact.email}`} style={{ color: '#0284c7', textDecoration: 'none', fontWeight: 500 }}>
                                                                            {contact.email}
                                                                        </a>
                                                                    </td>
                                                                    <td>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                                            <span>{countryObj.flag}</span>
                                                                            <span style={{ fontWeight: 500, color: '#334155' }}>
                                                                                {contact.phone ? formatPhoneNumber(contact.phone, contact.countryCode) : '—'}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className={`pipeline-stage-badge ${contact.origin === 'International' ? 'hot' : 'new'}`}
                                                                            style={{ fontSize: '11px', padding: '2px 8px' }}
                                                                        >
                                                                            {contact.origin || 'Domestic'}
                                                                        </span>
                                                                    </td>
                                                                    <td>{(contact.company && contact.company !== 'Direct Client') ? contact.company : '—'}</td>
                                                                    <td>
                                                                        <span className="pipeline-stage-badge new">{contact.leadStage}</span>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className={`pipeline-stage-badge ${(() => {
                                                                                const st = (contact.leadStatus || 'New').toLowerCase();
                                                                                if (st === 'won' || st === 'qualified' || st === 'active' || st === 'open') return 'won';
                                                                                if (st === 'lost' || st === 'unqualified' || st === 'spam') return 'lost';
                                                                                if (st === 'hot' || st === 'in progress' || st === 'contacted') return 'hot';
                                                                                return 'new';
                                                                            })()
                                                                                }`}
                                                                            style={{ fontSize: '11px', padding: '2px 8px' }}
                                                                        >
                                                                            {contact.leadStatus || 'New'}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span className="pipeline-contact-source-tag">{contact.contactSource}</span>
                                                                    </td>
                                                                    <td>{contact.addedBy || contact.owner || '—'}</td>
                                                                    <td style={{ textAlign: 'right' }}>
                                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                                                                            <button
                                                                                type="button"
                                                                                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}
                                                                                onClick={() => setDetailedContactId(contact._id)}
                                                                                title="View details"
                                                                            >
                                                                                <Eye size={12} />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}
                                                                                onClick={(e) => handleOpenEditContact(contact, e)}
                                                                                title="Edit contact"
                                                                            >
                                                                                <Edit2 size={12} />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #fee2e2', background: '#fef2f2', color: '#ef4444', cursor: 'pointer' }}
                                                                                onClick={(e) => handleDeleteContact(contact._id, e)}
                                                                                title="Delete contact"
                                                                            >
                                                                                <Trash2 size={12} />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>

                            {/* Floating Bottom Bulk Action Toolbar for Multi-Selected Contacts */}
                            {selectedKanbanContactIds.length > 0 && contactViewMode === 'kanban' && createPortal(
                                <div className="pipeline-kanban-bulk-bar" onClick={e => e.stopPropagation()}>
                                    <div className="pipeline-kanban-bulk-info">
                                        <div className="pipeline-kanban-bulk-badge">
                                            <Check size={14} strokeWidth={3} />
                                            <span>{selectedKanbanContactIds.length} {selectedKanbanContactIds.length === 1 ? 'contact' : 'contacts'} selected</span>
                                        </div>
                                    </div>

                                    <div className="pipeline-kanban-bulk-actions">
                                        {/* Bulk Move Stage Dropdown */}
                                        {canUpdate && (
                                            <div style={{ position: 'relative' }}>
                                                <button
                                                    type="button"
                                                    className="pipeline-kanban-bulk-btn primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsBulkMoveContactStageDropdownOpen(prev => !prev);
                                                    }}
                                                    title="Move selected contacts to another stage"
                                                >
                                                    <ArrowRightLeft size={13} />
                                                    <span>Move Stage</span>
                                                    <ChevronDown size={12} style={{ transform: isBulkMoveContactStageDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
                                                </button>

                                                {isBulkMoveContactStageDropdownOpen && (
                                                    <div className="pipeline-card-dropdown-menu pipeline-bulk-move-menu" onClick={e => e.stopPropagation()}>
                                                        <div className="pipeline-submenu-header">
                                                            <span className="pipeline-submenu-title">Move Selected To</span>
                                                        </div>
                                                        {effectiveContactStages.map(s => {
                                                            const stageName = typeof s === 'string' ? s : (s.name || s.id);
                                                            return (
                                                                <button
                                                                    key={s.id || stageName}
                                                                    type="button"
                                                                    className="pipeline-card-menu-item"
                                                                    onClick={() => handleBulkMoveContactStage(stageName)}
                                                                >
                                                                    <span className={`pipeline-menu-indicator ${s.badgeClass || 'new'}`} />
                                                                    <span>{stageName}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Bulk Delete */}
                                        {canDelete && (
                                            <button
                                                type="button"
                                                className="pipeline-kanban-bulk-btn danger"
                                                onClick={() => handleBulkDeleteContacts()}
                                                title="Delete selected contacts"
                                            >
                                                <Trash2 size={13} />
                                                <span>Delete</span>
                                            </button>
                                        )}

                                        {/* Bulk Export */}
                                        <button
                                            type="button"
                                            className="pipeline-kanban-bulk-btn"
                                            style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.3)' }}
                                            onClick={handleBulkExportContacts}
                                            title="Export selected contacts to CSV"
                                        >
                                            <Download size={13} />
                                            <span>Export</span>
                                        </button>

                                        {/* Deselect All */}
                                        <button
                                            type="button"
                                            className="pipeline-kanban-bulk-btn cancel"
                                            onClick={() => {
                                                setSelectedKanbanContactIds([]);
                                                setIsBulkMoveContactStageDropdownOpen(false);
                                            }}
                                            title="Clear selection"
                                        >
                                            <X size={13} />
                                            <span>Deselect All</span>
                                        </button>
                                    </div>
                                </div>,
                                document.body
                            )}
                        </div>
                    )
                )}

                {/* COMPANIES TAB VIEW */}
                {activeTab === 'companies' && (
                    <div className="pipeline-tab-page-view">
                        {/* Top Header */}
                        <div className="pipeline-top-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <h1 className="pipeline-deals-count-title">
                                    Companies Management<span className="pipeline-title-count-pill"></span>
                                </h1>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {/* View Switcher: List Table or Cards Grid */}
                                <div className="pipeline-view-switcher">
                                    <button
                                        type="button"
                                        className={`pipeline-view-btn ${companyViewMode === 'list' ? 'active' : ''}`}
                                        onClick={() => setCompanyViewMode('list')}
                                        title="List Table View"
                                    >
                                        <List size={16} />
                                    </button>
                                    <button
                                        type="button"
                                        className={`pipeline-view-btn ${companyViewMode === 'grid' ? 'active' : ''}`}
                                        onClick={() => setCompanyViewMode('grid')}
                                        title="Cards Grid View"
                                    >
                                        <Kanban size={16} />
                                    </button>
                                </div>

                                {/* Export */}
                                <button
                                    type="button"
                                    className="pipeline-btn-export"
                                    onClick={handleExportCompanies}
                                    title="Export companies to CSV"
                                >
                                    <Download size={14} />
                                    <span>Export</span>
                                </button>

                                <NotificationBell filterType="CRM" />
                            </div>
                        </div>

                        {/* Action Toolbar */}
                        <div className="pipeline-toolbar-row">
                            <div className="pipeline-toolbar-left">
                                {/* Search Box */}
                                <div className="pipeline-contacts-search-box">
                                    <Search size={14} className="pipeline-search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Search companies..."
                                        value={companySearchQuery}
                                        onChange={e => setCompanySearchQuery(e.target.value)}
                                        className="pipeline-contacts-search-input"
                                    />
                                    {companySearchQuery && (
                                        <button
                                            type="button"
                                            className="pipeline-search-clear-btn"
                                            onClick={() => setCompanySearchQuery('')}
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>

                                {/* Sort by Dropdown */}
                                <div className="pipeline-filter-dropdown-wrap">
                                    <button
                                        type="button"
                                        className={`pipeline-tool-btn ${companySortBy !== 'newest' ? 'filter-active' : ''} ${isCompanySortDropdownOpen ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsCompanyIndustryDropdownOpen(false);
                                            setIsCompanyOwnerDropdownOpen(false);
                                            setIsCompanyDateDropdownOpen(false);
                                            setIsCompanySortDropdownOpen(prev => !prev);
                                        }}
                                        title="Sort Companies"
                                    >
                                        <ArrowUpDown size={14} />
                                        <span>{companySortBy !== 'newest' ? `Sort: ${companySortLabel}` : 'Sort by'}</span>
                                        <ChevronDown size={13} style={{ color: companySortBy !== 'newest' ? '#0284c7' : '#94a3b8' }} />
                                    </button>

                                    {isCompanySortDropdownOpen && (
                                        <div className="pipeline-filter-menu-popover" onClick={e => e.stopPropagation()}>
                                            <div className="pipeline-filter-menu-header">Sort companies by</div>
                                            <div className="pipeline-filter-items-scroll">
                                                {[
                                                    { id: 'newest', label: 'Newest first' },
                                                    { id: 'oldest', label: 'Oldest first' },
                                                    { id: 'name_asc', label: 'Company Name (A - Z)' },
                                                    { id: 'name_desc', label: 'Company Name (Z - A)' },
                                                    { id: 'industry', label: 'Industry (A - Z)' },
                                                    { id: 'country', label: 'Country (A - Z)' }
                                                ].map(opt => (
                                                    <button
                                                        key={opt.id}
                                                        type="button"
                                                        className={`pipeline-filter-item-btn ${companySortBy === opt.id ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setCompanySortBy(opt.id);
                                                            setIsCompanySortDropdownOpen(false);
                                                        }}
                                                    >
                                                        <span>{opt.label}</span>
                                                        {companySortBy === opt.id && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* All Industries Dropdown */}
                                <div className="pipeline-filter-dropdown-wrap">
                                    <button
                                        type="button"
                                        className={`pipeline-tool-btn ${companyIndustryFilter !== 'all' ? 'filter-active' : ''} ${isCompanyIndustryDropdownOpen ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsCompanySortDropdownOpen(false);
                                            setIsCompanyOwnerDropdownOpen(false);
                                            setIsCompanyDateDropdownOpen(false);
                                            setIsCompanyIndustryDropdownOpen(prev => !prev);
                                        }}
                                        title="Filter by Industry"
                                    >
                                        <Building size={14} />
                                        <span>{companyIndustryFilter === 'all' ? 'All industries' : companyIndustryFilter}</span>
                                        <ChevronDown size={13} style={{ color: companyIndustryFilter !== 'all' ? '#0284c7' : '#94a3b8' }} />
                                    </button>

                                    {isCompanyIndustryDropdownOpen && (
                                        <div className="pipeline-filter-menu-popover" style={{ minWidth: '220px' }} onClick={e => e.stopPropagation()}>
                                            <div className="pipeline-filter-menu-header">Filter by Industry</div>
                                            <div className="pipeline-filter-items-scroll">
                                                <button
                                                    type="button"
                                                    className={`pipeline-filter-item-btn ${companyIndustryFilter === 'all' ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        setCompanyIndustryFilter('all');
                                                        setIsCompanyIndustryDropdownOpen(false);
                                                    }}
                                                >
                                                    <Building2 size={14} style={{ color: '#64748b' }} />
                                                    <span>All industries</span>
                                                    <span className="pipeline-filter-item-badge">{companies.filter(c => isItemOwnedByUser(c, 'company')).length}</span>
                                                    {companyIndustryFilter === 'all' && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                </button>
                                                {industryOptions.map(ind => {
                                                    const count = companyIndustryCounts[ind] || companyIndustryCounts[ind.toLowerCase()] || 0;
                                                    return (
                                                        <button
                                                            key={ind}
                                                            type="button"
                                                            className={`pipeline-filter-item-btn ${companyIndustryFilter.toLowerCase() === ind.toLowerCase() ? 'selected' : ''}`}
                                                            onClick={() => {
                                                                setCompanyIndustryFilter(ind);
                                                                setIsCompanyIndustryDropdownOpen(false);
                                                            }}
                                                        >
                                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ind}</span>
                                                            <span className="pipeline-filter-item-badge">{count}</span>
                                                            {companyIndustryFilter.toLowerCase() === ind.toLowerCase() && (
                                                                <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* All Company Owners Dropdown (Sales Head, Sales Manager, and Admin only) */}
                                {hasFullAccess && (
                                    <div className="pipeline-filter-dropdown-wrap">
                                        <button
                                            type="button"
                                            className={`pipeline-tool-btn ${companyOwnerFilter !== 'all' ? 'filter-active' : ''} ${isCompanyOwnerDropdownOpen ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsCompanySortDropdownOpen(false);
                                                setIsCompanyIndustryDropdownOpen(false);
                                                setIsCompanyDateDropdownOpen(false);
                                                setIsCompanyOwnerDropdownOpen(prev => !prev);
                                            }}
                                            title="Filter by company owners"
                                        >
                                            <User size={14} />
                                            <span>{companyOwnerFilter === 'all' ? 'All company owners' : companyOwnerFilter}</span>
                                            <ChevronDown size={13} style={{ color: companyOwnerFilter !== 'all' ? '#0284c7' : '#94a3b8' }} />
                                        </button>

                                        {isCompanyOwnerDropdownOpen && (
                                            <div className="pipeline-filter-menu-popover" style={{ minWidth: '240px' }} onClick={e => e.stopPropagation()}>
                                                <div className="pipeline-filter-menu-header">Filter by Company Owner</div>
                                                {allCompanyOwners.length > 5 && (
                                                    <div className="pipeline-filter-search-wrap">
                                                        <input
                                                            type="text"
                                                            className="pipeline-filter-search-input"
                                                            placeholder="Search company owner..."
                                                            value={companyOwnerSearchQuery}
                                                            onChange={e => setCompanyOwnerSearchQuery(e.target.value)}
                                                            autoFocus
                                                        />
                                                    </div>
                                                )}
                                                <div className="pipeline-filter-items-scroll">
                                                    <button
                                                        type="button"
                                                        className={`pipeline-filter-item-btn ${companyOwnerFilter === 'all' ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setCompanyOwnerFilter('all');
                                                            setIsCompanyOwnerDropdownOpen(false);
                                                            setCompanyOwnerSearchQuery('');
                                                        }}
                                                    >
                                                        <User size={14} style={{ color: '#64748b' }} />
                                                        <span>All company owners</span>
                                                        <span className="pipeline-filter-item-badge">{companies.filter(c => isItemOwnedByUser(c, 'company')).length}</span>
                                                        {companyOwnerFilter === 'all' && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                    </button>

                                                    {allCompanyOwners
                                                        .filter(ownerName => !companyOwnerSearchQuery.trim() || ownerName.toLowerCase().includes(companyOwnerSearchQuery.trim().toLowerCase()))
                                                        .map(ownerName => {
                                                            const count = companyOwnerCounts[ownerName] || companyOwnerCounts[ownerName.toLowerCase()] || 0;
                                                            return (
                                                                <button
                                                                    key={ownerName}
                                                                    type="button"
                                                                    className={`pipeline-filter-item-btn ${companyOwnerFilter.toLowerCase() === ownerName.toLowerCase() ? 'selected' : ''}`}
                                                                    onClick={() => {
                                                                        setCompanyOwnerFilter(ownerName);
                                                                        setIsCompanyOwnerDropdownOpen(false);
                                                                        setCompanyOwnerSearchQuery('');
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            width: 18,
                                                                            height: 18,
                                                                            borderRadius: '50%',
                                                                            background: '#e0f2fe',
                                                                            color: '#0284c7',
                                                                            fontSize: '10px',
                                                                            fontWeight: 700,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center'
                                                                        }}
                                                                    >
                                                                        {ownerName.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ownerName}</span>
                                                                    <span className="pipeline-filter-item-badge">{count}</span>
                                                                    {companyOwnerFilter.toLowerCase() === ownerName.toLowerCase() && (
                                                                        <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />
                                                                    )}
                                                                </button>
                                                            );
                                                        })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Date Filter Dropdown */}
                                <div className="pipeline-filter-dropdown-wrap">
                                    <button
                                        type="button"
                                        className={`pipeline-tool-btn ${companyDateFilter !== 'all' ? 'filter-active' : ''} ${isCompanyDateDropdownOpen ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsCompanySortDropdownOpen(false);
                                            setIsCompanyIndustryDropdownOpen(false);
                                            setIsCompanyOwnerDropdownOpen(false);
                                            setIsCompanyDateDropdownOpen(prev => !prev);
                                        }}
                                        title="Filter companies by date"
                                    >
                                        <Calendar size={14} />
                                        <span>{companyDateFilterLabel}</span>
                                        <ChevronDown size={13} style={{ color: companyDateFilter !== 'all' ? '#0284c7' : '#94a3b8' }} />
                                    </button>

                                    {isCompanyDateDropdownOpen && (
                                        <div className="pipeline-filter-menu-popover" style={{ minWidth: '230px' }} onClick={e => e.stopPropagation()}>
                                            <div className="pipeline-filter-menu-header">Filter by Creation Date</div>
                                            <div className="pipeline-filter-items-scroll">
                                                {[
                                                    { id: 'all', label: 'All Dates (All Time)' },
                                                    { id: 'today', label: 'Today' },
                                                    { id: 'this_week', label: 'This Week' },
                                                    { id: 'this_month', label: 'This Month' },
                                                    { id: 'next_30_days', label: 'Next 30 Days' },
                                                    { id: 'this_quarter', label: 'This Quarter' },
                                                    { id: 'this_year', label: 'This Year' },
                                                    { id: 'custom', label: 'Custom Date Range...' }
                                                ].map(opt => (
                                                    <button
                                                        key={opt.id}
                                                        type="button"
                                                        className={`pipeline-filter-item-btn ${companyDateFilter === opt.id ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setCompanyDateFilter(opt.id);
                                                            if (opt.id !== 'custom') {
                                                                setIsCompanyDateDropdownOpen(false);
                                                            }
                                                        }}
                                                    >
                                                        <span>{opt.label}</span>
                                                        {companyDateFilter === opt.id && <Check size={14} style={{ marginLeft: 'auto', color: '#0284c7' }} />}
                                                    </button>
                                                ))}
                                            </div>

                                            {companyDateFilter === 'custom' && (
                                                <div className="pipeline-filter-date-custom-panel">
                                                    <div className="pipeline-filter-date-input-group">
                                                        <label className="pipeline-filter-date-label">From Date</label>
                                                        <input
                                                            type="date"
                                                            className="pipeline-filter-date-input"
                                                            value={customCompanyDateStart}
                                                            onChange={e => setCustomCompanyDateStart(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="pipeline-filter-date-input-group">
                                                        <label className="pipeline-filter-date-label">To Date</label>
                                                        <input
                                                            type="date"
                                                            className="pipeline-filter-date-input"
                                                            value={customCompanyDateEnd}
                                                            onChange={e => setCustomCompanyDateEnd(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="pipeline-filter-date-actions">
                                                        <button
                                                            type="button"
                                                            className="pipeline-filter-date-btn-cancel"
                                                            onClick={() => {
                                                                setCustomCompanyDateStart('');
                                                                setCustomCompanyDateEnd('');
                                                                setCompanyDateFilter('all');
                                                                setIsCompanyDateDropdownOpen(false);
                                                            }}
                                                        >
                                                            Clear
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="pipeline-filter-date-btn-apply"
                                                            onClick={() => setIsCompanyDateDropdownOpen(false)}
                                                        >
                                                            Apply
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Reset / Clear All Filters */}
                                {isAnyCompanyFilterActive && (
                                    <button
                                        type="button"
                                        className="pipeline-filter-clear-all-btn"
                                        onClick={handleResetCompanyFilters}
                                        title="Clear all filters"
                                    >
                                        <X size={13} />
                                        <span>Reset Filters</span>
                                    </button>
                                )}
                            </div>

                            <div className="pipeline-toolbar-right">
                                {/* Create Company Primary Button */}
                                {canCreate && (
                                    <button
                                        type="button"
                                        className="pipeline-btn-create-deal"
                                        onClick={handleOpenCreateCompany}
                                        title="Create new company"
                                    >
                                        <Plus size={15} />
                                        <span>Create companies</span>
                                        <ChevronDown size={13} />
                                    </button>
                                )}

                                {/* More Options */}
                                <div style={{ position: 'relative' }}>
                                    <button
                                        type="button"
                                        className={`pipeline-btn-more ${isCompaniesToolbarMenuOpen ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsCompaniesToolbarMenuOpen(prev => !prev);
                                        }}
                                        title="More Options"
                                    >
                                        <MoreHorizontal size={16} />
                                    </button>
                                    {isCompaniesToolbarMenuOpen && (
                                        <div
                                            className="pipeline-card-dropdown-menu"
                                            style={{ top: 'calc(100% + 6px)', right: 0, minWidth: '160px', zIndex: 100 }}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            {canCreate && (
                                                <button
                                                    type="button"
                                                    className="pipeline-card-menu-item"
                                                    onClick={() => {
                                                        setIsCompaniesToolbarMenuOpen(false);
                                                        handleOpenCreateCompany();
                                                    }}
                                                >
                                                    <div className="pipeline-card-menu-item-left">
                                                        <Plus size={13} style={{ color: '#0284c7' }} />
                                                        <span>Add Company</span>
                                                    </div>
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="pipeline-card-menu-item"
                                                onClick={() => {
                                                    setIsCompaniesToolbarMenuOpen(false);
                                                    handleExportCompanies();
                                                }}
                                            >
                                                <div className="pipeline-card-menu-item-left">
                                                    <Download size={13} style={{ color: '#64748b' }} />
                                                    <span>Export Companies</span>
                                                </div>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Content Body: Grid View or Table View */}
                        <div className="pipeline-board-container" style={{ padding: '20px 28px 40px 28px' }}>
                            {isLoading ? (
                                companyViewMode === 'grid' ? renderCompanyGridSkeleton(8) : renderTableSkeleton(7)
                            ) : filteredCompanies.length === 0 ? (
                                <div className="pipeline-empty-state-card" style={{ maxWidth: '480px', margin: '40px auto' }}>
                                    <div className="pipeline-empty-icon-wrap">
                                        <Building2 size={32} />
                                    </div>
                                    <h3 className="pipeline-empty-state-title">No Companies Found</h3>
                                    <p className="pipeline-empty-state-desc">
                                        {isAnyCompanyFilterActive
                                            ? 'No companies match your current search or filter criteria.'
                                            : 'No company accounts registered yet. Click below to add your first company.'}
                                    </p>
                                    <button
                                        type="button"
                                        className="pipeline-empty-state-action-btn"
                                        onClick={handleOpenCreateCompany}
                                    >
                                        <Plus size={14} />
                                        <span>Add Company</span>
                                    </button>
                                </div>
                            ) : companyViewMode === 'grid' ? (
                                <div className="pipeline-company-grid">
                                    {filteredCompanies.map(company => {
                                        const displayLocation = [company.city, company.state, company.country].filter(Boolean).join(', ') || 'Not specified';
                                        return (
                                            <div key={company._id} className="pipeline-company-card">
                                                {/* Top Info */}
                                                <div className="pipeline-company-card-top">
                                                    <div className="pipeline-company-info-wrap">
                                                        <div className="pipeline-company-logo-avatar">
                                                            {company.name ? company.name.charAt(0).toUpperCase() : 'C'}
                                                        </div>
                                                        <div>
                                                            <h3 className="pipeline-company-name-title">{company.name}</h3>
                                                            {company.domain ? (
                                                                <a
                                                                    href={`https://${company.domain.replace(/^https?:\/\//, '')}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="pipeline-company-domain-link"
                                                                    onClick={e => e.stopPropagation()}
                                                                >
                                                                    <span>{company.domain}</span>
                                                                    <ExternalLink size={11} />
                                                                </a>
                                                            ) : (
                                                                <span style={{ fontSize: '12px', color: '#94a3b8' }}>No domain</span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* 3-dots Menu */}
                                                    <div style={{ position: 'relative' }}>
                                                        <button
                                                            type="button"
                                                            className="pipeline-card-more-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveCompanyMenuId(activeCompanyMenuId === company._id ? null : company._id);
                                                            }}
                                                            title="Company options"
                                                        >
                                                            <MoreHorizontal size={16} />
                                                        </button>

                                                        {activeCompanyMenuId === company._id && (
                                                            <div className="pipeline-card-dropdown-menu" onClick={e => e.stopPropagation()}>
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-card-menu-item"
                                                                    onClick={() => {
                                                                        setViewingCompany(company);
                                                                        setActiveCompanyMenuId(null);
                                                                    }}
                                                                >
                                                                    <div className="pipeline-card-menu-item-left">
                                                                        <Eye size={14} style={{ color: '#64748b' }} />
                                                                        <span>View Details</span>
                                                                    </div>
                                                                </button>

                                                                {canUpdate && (
                                                                    <button
                                                                        type="button"
                                                                        className="pipeline-card-menu-item"
                                                                        onClick={(e) => handleOpenEditCompany(company, e)}
                                                                    >
                                                                        <div className="pipeline-card-menu-item-left">
                                                                            <Edit2 size={14} style={{ color: '#64748b' }} />
                                                                            <span>Edit Company</span>
                                                                        </div>
                                                                    </button>
                                                                )}

                                                                {canDelete && (
                                                                    <>
                                                                        <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-card-menu-item danger"
                                                                            onClick={(e) => handleDeleteCompany(company._id, e)}
                                                                        >
                                                                            <div className="pipeline-card-menu-item-left">
                                                                                <Trash2 size={14} />
                                                                                <span>Delete Company</span>
                                                                            </div>
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Body Details */}
                                                <div className="pipeline-company-card-body">
                                                    <div className="pipeline-company-detail-row">
                                                        <span className="pipeline-company-detail-label">Industry</span>
                                                        <span className="pipeline-company-industry-tag">{company.industry || 'General'}</span>
                                                    </div>

                                                    <div className="pipeline-company-detail-row">
                                                        <span className="pipeline-company-detail-label">Location</span>
                                                        <span className="pipeline-company-detail-val" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <MapPin size={11} style={{ color: '#64748b' }} />
                                                            {displayLocation}
                                                        </span>
                                                    </div>

                                                    {company.postalCode && (
                                                        <div className="pipeline-company-detail-row">
                                                            <span className="pipeline-company-detail-label">Postal Code</span>
                                                            <span className="pipeline-company-detail-val">{company.postalCode}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Footer */}
                                                <div className="pipeline-company-card-footer">
                                                    <div className="pipeline-company-owner-badge" style={{ flexWrap: 'wrap', gap: '4px' }}>
                                                        <User size={13} style={{ color: '#0284c7', flexShrink: 0 }} />
                                                        <CompanyOwnerDisplay owner={company.owner} fallback="Artiflow" />
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="pipeline-company-btn-action"
                                                        onClick={() => setViewingCompany(company)}
                                                    >
                                                        <span>View details</span>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                /* Table List View */
                                <div className="pipeline-table-view">
                                    <div className="pipeline-table-card">
                                        <table className="pipeline-table">
                                            <thead>
                                                <tr>
                                                    <th>Company Name</th>
                                                    <th>Domain</th>
                                                    <th>Industry</th>
                                                    <th>Location</th>
                                                    <th>Postal Code</th>
                                                    <th>Company Owner</th>
                                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredCompanies.map(company => (
                                                    <tr key={company._id}>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                                                                <div className="pipeline-company-logo-avatar" style={{ width: '32px', height: '32px', fontSize: '13px', borderRadius: '8px' }}>
                                                                    {company.name ? company.name.charAt(0).toUpperCase() : 'C'}
                                                                </div>
                                                                <span style={{ fontWeight: 600, color: '#0f172a' }}>{company.name}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {company.domain ? (
                                                                <a
                                                                    href={`https://${company.domain.replace(/^https?:\/\//, '')}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{ color: '#0284c7', textDecoration: 'none', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                                                >
                                                                    <span>{company.domain}</span>
                                                                    <ExternalLink size={11} />
                                                                </a>
                                                            ) : (
                                                                <span style={{ color: '#94a3b8' }}>—</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="pipeline-company-industry-tag">{company.industry || 'General'}</span>
                                                        </td>
                                                        <td>{[company.city, company.state, company.country].filter(Boolean).join(', ') || '—'}</td>
                                                        <td>{company.postalCode || '—'}</td>
                                                        <td>
                                                            <CompanyOwnerDisplay owner={company.owner} fallback="Artiflow" />
                                                        </td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                                                                <button
                                                                    type="button"
                                                                    style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}
                                                                    onClick={() => setViewingCompany(company)}
                                                                    title="View details"
                                                                >
                                                                    <Eye size={12} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}
                                                                    onClick={(e) => handleOpenEditCompany(company, e)}
                                                                    title="Edit company"
                                                                >
                                                                    <Edit2 size={12} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #fee2e2', background: '#fef2f2', color: '#ef4444', cursor: 'pointer' }}
                                                                    onClick={(e) => handleDeleteCompany(company._id, e)}
                                                                    title="Delete company"
                                                                >
                                                                    <Trash2 size={12} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* TASKS TAB (Complete CRM Task Management) */}
                {activeTab === 'tasks' && (
                    <div className="pipeline-tab-page-view">
                        {/* Top Header */}
                        <div className="pipeline-top-header">
                            <h1 className="pipeline-deals-count-title">Tasks Management</h1>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <NotificationBell filterType="CRM" />
                            </div>
                        </div>

                        {/* Action Toolbar */}
                        <div className="pipeline-toolbar-row">
                            <div className="pipeline-toolbar-left">
                                {/* Search Tasks */}
                                <div className="pipeline-contacts-search-box">
                                    <Search size={14} className="pipeline-search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Search tasks, deals, contacts..."
                                        value={taskSearchQuery}
                                        onChange={e => setTaskSearchQuery(e.target.value)}
                                        className="pipeline-contacts-search-input"
                                    />
                                    {taskSearchQuery && (
                                        <button
                                            type="button"
                                            className="pipeline-search-clear-btn"
                                            onClick={() => setTaskSearchQuery('')}
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>

                                {/* Filter by Status */}
                                <button
                                    type="button"
                                    className="pipeline-tool-btn"
                                    onClick={() => {
                                        const stList = ['all', 'Pending', 'In Progress', 'Completed'];
                                        const idx = stList.indexOf(taskStatusFilter);
                                        setTaskStatusFilter(stList[(idx + 1) % stList.length]);
                                    }}
                                    title="Filter by Status"
                                >
                                    <CheckCircle size={14} />
                                    <span>{taskStatusFilter === 'all' ? 'All Statuses' : taskStatusFilter}</span>
                                    <ChevronDown size={13} style={{ color: '#94a3b8' }} />
                                </button>

                                {/* Filter by Priority */}
                                <button
                                    type="button"
                                    className="pipeline-tool-btn"
                                    onClick={() => {
                                        const prList = ['all', 'High', 'Medium', 'Low'];
                                        const idx = prList.indexOf(taskPriorityFilter);
                                        setTaskPriorityFilter(prList[(idx + 1) % prList.length]);
                                    }}
                                    title="Filter by Priority"
                                >
                                    <Tag size={14} />
                                    <span>{taskPriorityFilter === 'all' ? 'All Priorities' : `${taskPriorityFilter} Priority`}</span>
                                    <ChevronDown size={13} style={{ color: '#94a3b8' }} />
                                </button>

                                {/* Filter by Type */}
                                <button
                                    type="button"
                                    className="pipeline-tool-btn"
                                    onClick={() => {
                                        const tList = ['all', ...TASK_TYPE_OPTIONS];
                                        const idx = tList.indexOf(taskTypeFilter);
                                        setTaskTypeFilter(tList[(idx + 1) % tList.length]);
                                    }}
                                    title="Filter by Type"
                                >
                                    <Filter size={14} />
                                    <span>{taskTypeFilter === 'all' ? 'All Types' : taskTypeFilter}</span>
                                    <ChevronDown size={13} style={{ color: '#94a3b8' }} />
                                </button>

                                {/* Sort by */}
                                <button
                                    type="button"
                                    className="pipeline-tool-btn"
                                    onClick={() => {
                                        setTaskSortBy(prev => (prev === 'dueDate_asc' ? 'dueDate_desc' : prev === 'dueDate_desc' ? 'priority' : 'dueDate_asc'));
                                    }}
                                    title="Sort Tasks"
                                >
                                    <ArrowUpDown size={14} />
                                    <span>
                                        Sort: {taskSortBy === 'dueDate_asc' ? 'Due Soonest' : taskSortBy === 'dueDate_desc' ? 'Due Latest' : 'Priority (High)'}
                                    </span>
                                </button>
                            </div>

                            <div className="pipeline-toolbar-right">
                                {/* View Switcher: List Table or Kanban Board */}
                                <div className="pipeline-view-switcher">
                                    <button
                                        type="button"
                                        className={`pipeline-view-btn ${taskViewMode === 'list' ? 'active' : ''}`}
                                        onClick={() => setTaskViewMode('list')}
                                        title="List Table View"
                                    >
                                        <List size={16} />
                                    </button>
                                    <button
                                        type="button"
                                        className={`pipeline-view-btn ${taskViewMode === 'kanban' ? 'active' : ''}`}
                                        onClick={() => setTaskViewMode('kanban')}
                                        title="Kanban Board View"
                                    >
                                        <Kanban size={16} />
                                    </button>
                                </div>

                                {/* Export */}
                                <button
                                    type="button"
                                    className="pipeline-btn-export"
                                    onClick={handleExportTasks}
                                    title="Export tasks to CSV"
                                >
                                    <Download size={14} />
                                    <span>Export</span>
                                </button>

                                {/* Create Task Primary Button */}
                                {canCreate && (
                                    <button
                                        type="button"
                                        className="pipeline-btn-create-deal"
                                        onClick={() => handleOpenCreateTask()}
                                        title="Create new task"
                                    >
                                        <Plus size={15} />
                                        <span>Create tasks</span>
                                        <ChevronDown size={13} />
                                    </button>
                                )}

                                {/* More Options */}
                                {canCreate && (
                                    <div style={{ position: 'relative' }}>
                                        <button
                                            type="button"
                                            className={`pipeline-btn-more ${isTasksToolbarMenuOpen ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsTasksToolbarMenuOpen(prev => !prev);
                                            }}
                                            title="More Options"
                                        >
                                            <MoreHorizontal size={16} />
                                        </button>
                                        {isTasksToolbarMenuOpen && (
                                            <div
                                                className="pipeline-card-dropdown-menu"
                                                style={{ top: 'calc(100% + 6px)', right: 0, minWidth: '160px', zIndex: 100 }}
                                                onClick={e => e.stopPropagation()}
                                            >
                                                <button
                                                    type="button"
                                                    className="pipeline-card-menu-item"
                                                    onClick={() => {
                                                        setIsTasksToolbarMenuOpen(false);
                                                        handleOpenCreateTask();
                                                    }}
                                                >
                                                    <div className="pipeline-card-menu-item-left">
                                                        <Plus size={13} style={{ color: '#0284c7' }} />
                                                        <span>Add Task</span>
                                                    </div>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="pipeline-card-menu-item"
                                                    onClick={() => {
                                                        setIsTasksToolbarMenuOpen(false);
                                                        handleExportTasks();
                                                    }}
                                                >
                                                    <div className="pipeline-card-menu-item-left">
                                                        <Download size={13} style={{ color: '#64748b' }} />
                                                        <span>Export Tasks</span>
                                                    </div>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Metrics Row */}
                        <div className="pipeline-tasks-metrics-row">
                            <div className="pipeline-task-stat-card">
                                <div className="pipeline-task-stat-icon total">
                                    <CheckSquare size={18} />
                                </div>
                                <div>
                                    <span className="pipeline-task-stat-label">Total Tasks</span>
                                    <h4 className="pipeline-task-stat-value">{userAccessibleTasks.length}</h4>
                                </div>
                            </div>
                            <div className="pipeline-task-stat-card">
                                <div className="pipeline-task-stat-icon pending">
                                    <Clock size={18} />
                                </div>
                                <div>
                                    <span className="pipeline-task-stat-label">Pending / In Progress</span>
                                    <h4 className="pipeline-task-stat-value">{userAccessibleTasks.filter(t => !t.completed && t.status !== 'Completed').length}</h4>
                                </div>
                            </div>
                            <div className="pipeline-task-stat-card">
                                <div className="pipeline-task-stat-icon completed">
                                    <CheckCircle size={18} />
                                </div>
                                <div>
                                    <span className="pipeline-task-stat-label">Completed</span>
                                    <h4 className="pipeline-task-stat-value">{userAccessibleTasks.filter(t => t.completed || t.status === 'Completed').length}</h4>
                                </div>
                            </div>
                            <div className="pipeline-task-stat-card">
                                <div className="pipeline-task-stat-icon high">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <span className="pipeline-task-stat-label">High Priority</span>
                                    <h4 className="pipeline-task-stat-value">{userAccessibleTasks.filter(t => (t.priority || '').toLowerCase() === 'high' && !t.completed && t.status !== 'Completed').length}</h4>
                                </div>
                            </div>
                        </div>

                        {/* Content Body: List View or Kanban View */}
                        <div className="pipeline-board-container" style={{ padding: '0 28px 40px 28px' }}>
                            {isLoading ? (
                                taskViewMode === 'list' ? renderTaskListSkeleton(7) : renderKanbanSkeleton(3, false)
                            ) : filteredTasks.length === 0 ? (
                                <div className="pipeline-empty-state-card" style={{ maxWidth: '480px', margin: '40px auto' }}>
                                    <div className="pipeline-empty-icon-wrap" style={{ background: '#eef2ff', color: '#6366f1' }}>
                                        <CheckSquare size={32} />
                                    </div>
                                    <h3 className="pipeline-empty-state-title">No Tasks Found</h3>
                                    <p className="pipeline-empty-state-desc">
                                        {taskSearchQuery || taskStatusFilter !== 'all' || taskPriorityFilter !== 'all' || taskTypeFilter !== 'all'
                                            ? 'No tasks match your current search and filter criteria.'
                                            : 'You have no scheduled tasks. Keep deals moving by scheduling follow-ups, calls, or meetings.'}
                                    </p>
                                    <button
                                        type="button"
                                        className="pipeline-empty-state-action-btn"
                                        onClick={() => handleOpenCreateTask()}
                                    >
                                        <Plus size={14} />
                                        <span>Create Your First Task</span>
                                    </button>
                                </div>
                            ) : taskViewMode === 'list' ? (
                                /* Table List View */
                                <div className="pipeline-table-view" style={{ minWidth: 0, maxWidth: '100%', width: '100%', padding: 0 }}>
                                    <div className="pipeline-table-card">
                                        <table className="pipeline-table compact-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '38px', textAlign: 'center', whiteSpace: 'nowrap' }}>Done</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Task Title</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Type</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Associated Deal</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Associated Contact</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Due Date</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Priority</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Assignee</th>
                                                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredTasks.map(task => {
                                                    const isOverdue = task.dueDate && !task.completed && new Date(task.dueDate) < new Date(new Date().setHours(0, 0, 0, 0));
                                                    return (
                                                        <tr key={task._id} className={task.completed ? 'task-row-completed' : ''}>
                                                            <td style={{ textAlign: 'center', width: '38px' }}>
                                                                <label className="pipeline-task-checkbox-wrap" onClick={e => e.stopPropagation()}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!task.completed}
                                                                        onChange={(e) => handleToggleTaskComplete(task._id, e)}
                                                                    />
                                                                    <span className="pipeline-task-checkmark"></span>
                                                                </label>
                                                            </td>
                                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', maxWidth: '320px' }}>
                                                                    <span
                                                                        style={{
                                                                            fontWeight: 600,
                                                                            fontSize: '12px',
                                                                            color: task.completed ? '#94a3b8' : '#0f172a',
                                                                            textDecoration: task.completed ? 'line-through' : 'none',
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis'
                                                                        }}
                                                                        title={task.title}
                                                                    >
                                                                        {task.title}
                                                                    </span>
                                                                    {task.description && (
                                                                        <span
                                                                            style={{
                                                                                fontSize: '11px',
                                                                                color: '#64748b',
                                                                                whiteSpace: 'nowrap',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                maxWidth: '160px',
                                                                                opacity: 0.85
                                                                            }}
                                                                            title={task.description}
                                                                        >
                                                                            — {task.description}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                                <span className={`pipeline-task-type-badge ${(task.type || 'follow-up').toLowerCase()}`} style={{ fontSize: '10.5px', padding: '1.5px 7px', whiteSpace: 'nowrap' }}>
                                                                    {task.type || 'Follow-up'}
                                                                </span>
                                                            </td>
                                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                                {task.associatedDealId || task.associatedDealName ? (
                                                                    <button
                                                                        type="button"
                                                                        className="pipeline-task-entity-link deal"
                                                                        style={{ fontSize: '11px', padding: '2px 7px', whiteSpace: 'nowrap' }}
                                                                        onClick={() => {
                                                                            const matchedD = deals.find(d => d._id === task.associatedDealId || (d.dealName || d.clientName) === task.associatedDealName);
                                                                            if (matchedD) {
                                                                                setDetailedContactId(null);
                                                                                setDetailedDealId(matchedD._id);
                                                                                setActiveTab('deals');
                                                                            }
                                                                        }}
                                                                        title="Open Deal"
                                                                    >
                                                                        <Briefcase size={10} style={{ flexShrink: 0 }} />
                                                                        <span style={{ maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            {task.associatedDealName || 'View Deal'}
                                                                        </span>
                                                                    </button>
                                                                ) : (
                                                                    <span style={{ color: '#94a3b8', fontSize: '11.5px' }}>—</span>
                                                                )}
                                                            </td>
                                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                                {task.associatedContactId || task.associatedContactName ? (
                                                                    <button
                                                                        type="button"
                                                                        className="pipeline-task-entity-link contact"
                                                                        style={{ fontSize: '11px', padding: '2px 7px', whiteSpace: 'nowrap' }}
                                                                        onClick={() => {
                                                                            const matchedC = contacts.find(c => c._id === task.associatedContactId || `${c.firstName} ${c.lastName}`.trim() === task.associatedContactName);
                                                                            if (matchedC) {
                                                                                setDetailedDealId(null);
                                                                                setDetailedContactId(matchedC._id);
                                                                                setActiveTab('contacts');
                                                                            }
                                                                        }}
                                                                        title="Open Contact"
                                                                    >
                                                                        <User size={10} style={{ flexShrink: 0 }} />
                                                                        <span style={{ maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            {task.associatedContactName || 'View Contact'}
                                                                        </span>
                                                                    </button>
                                                                ) : (
                                                                    <span style={{ color: '#94a3b8', fontSize: '11.5px' }}>—</span>
                                                                )}
                                                            </td>
                                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', color: isOverdue ? '#dc2626' : '#475569', fontWeight: isOverdue ? 700 : 500, whiteSpace: 'nowrap' }}>
                                                                    <Clock size={11} style={{ flexShrink: 0 }} />
                                                                    <span>{task.dueDate ? formatDateDDMMYYYY(task.dueDate) : '—'}</span>
                                                                    {isOverdue && (
                                                                        <span style={{ fontSize: '9.5px', background: '#fee2e2', color: '#dc2626', padding: '1px 4.5px', borderRadius: '4px', fontWeight: 700, whiteSpace: 'nowrap', lineHeight: '1.2' }}>
                                                                            Overdue
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                                <span className={`pipeline-task-priority-pill ${(task.priority || 'medium').toLowerCase()}`} style={{ fontSize: '10.5px', padding: '1.5px 7px', whiteSpace: 'nowrap' }}>
                                                                    {task.priority || 'Medium'}
                                                                </span>
                                                            </td>
                                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>
                                                                    <div className="pipeline-owner-pill-avatar" style={{ width: '20px', height: '20px', fontSize: '9.5px', flexShrink: 0 }}>
                                                                        {(task.assignee || 'A').charAt(0)}
                                                                    </div>
                                                                    <span style={{ fontSize: '11.5px', fontWeight: 500, color: '#334155', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {task.assignee || 'Artiflow'}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                <div style={{ display: 'inline-flex', justifyContent: 'flex-end', gap: '4px' }}>
                                                                    {canUpdate && (
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-mini-icon-btn"
                                                                            style={{ width: '24px', height: '24px' }}
                                                                            onClick={(e) => handleOpenEditTask(task, e)}
                                                                            title="Edit Task"
                                                                        >
                                                                            <Edit2 size={11} />
                                                                        </button>
                                                                    )}
                                                                    {canDelete && (
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-mini-icon-btn danger"
                                                                            style={{ width: '24px', height: '24px' }}
                                                                            onClick={(e) => handleDeleteTask(task._id, e)}
                                                                            title="Delete Task"
                                                                        >
                                                                            <Trash2 size={11} />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                /* Kanban Columns Board View */
                                <div className="pipeline-task-kanban-board">
                                    {['Pending', 'In Progress', 'Completed'].map(colStatus => {
                                        const colTasks = filteredTasks.filter(t => {
                                            if (colStatus === 'Completed') return t.completed || t.status === 'Completed';
                                            if (colStatus === 'In Progress') return !t.completed && t.status === 'In Progress';
                                            return !t.completed && t.status !== 'In Progress';
                                        });
                                        return (
                                            <div
                                                key={colStatus}
                                                className="pipeline-task-kanban-col"
                                                onDragOver={(e) => {
                                                    e.preventDefault();
                                                    e.currentTarget.classList.add('pipeline-drag-over');
                                                }}
                                                onDragLeave={(e) => {
                                                    e.currentTarget.classList.remove('pipeline-drag-over');
                                                }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    e.currentTarget.classList.remove('pipeline-drag-over');
                                                    const taskId = e.dataTransfer.getData('text/plain');
                                                    if (taskId) {
                                                        handleUpdateTaskStatus(taskId, colStatus);
                                                    }
                                                }}
                                            >
                                                <div className="pipeline-task-kanban-col-header">
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span className={`pipeline-task-status-dot ${colStatus.toLowerCase().replace(/\s+/g, '-')}`} />
                                                        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{colStatus}</h4>
                                                    </div>
                                                    <span className="pipeline-col-count">{colTasks.length}</span>
                                                </div>

                                                <div className="pipeline-task-kanban-col-body">
                                                    {colTasks.map(task => (
                                                        <div
                                                            key={task._id}
                                                            className={`pipeline-task-kanban-card ${task.completed ? 'completed' : ''}`}
                                                            draggable={canUpdate}
                                                            onDragStart={(e) => {
                                                                e.dataTransfer.setData('text/plain', task._id);
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                                                                <span className={`pipeline-task-type-badge ${(task.type || 'follow-up').toLowerCase()}`}>
                                                                    {task.type || 'Follow-up'}
                                                                </span>
                                                                <span className={`pipeline-task-priority-pill ${(task.priority || 'medium').toLowerCase()}`}>
                                                                    {task.priority || 'Medium'}
                                                                </span>
                                                            </div>

                                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                                                                <label className="pipeline-task-checkbox-wrap" onClick={e => e.stopPropagation()} style={{ marginTop: '2px' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!task.completed}
                                                                        disabled={!canUpdate}
                                                                        onChange={(e) => canUpdate && handleToggleTaskComplete(task._id, e)}
                                                                    />
                                                                    <span className="pipeline-task-checkmark"></span>
                                                                </label>
                                                                <h4 className={`pipeline-task-card-title ${task.completed ? 'done' : ''}`}>
                                                                    {task.title}
                                                                </h4>
                                                            </div>

                                                            {task.description && (
                                                                <p className="pipeline-task-card-desc">{task.description}</p>
                                                            )}

                                                            {(task.associatedDealName || task.associatedContactName) && (
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', margin: '8px 0' }}>
                                                                    {task.associatedDealName && (
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-task-entity-link deal"
                                                                            style={{ border: 'none', cursor: 'pointer', textAlign: 'left' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                const matchedD = deals.find(d => String(d._id) === String(task.associatedDealId) || (d.dealName || d.clientName) === task.associatedDealName);
                                                                                if (matchedD) {
                                                                                    setDetailedContactId(null);
                                                                                    setDetailedDealId(matchedD._id);
                                                                                    setActiveTab('deals');
                                                                                }
                                                                            }}
                                                                            title="Open Deal Details"
                                                                        >
                                                                            <Briefcase size={10} /> {task.associatedDealName}
                                                                        </button>
                                                                    )}
                                                                    {task.associatedContactName && (
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-task-entity-link contact"
                                                                            style={{ border: 'none', cursor: 'pointer', textAlign: 'left' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                const matchedC = contacts.find(c => String(c._id) === String(task.associatedContactId) || `${c.firstName || ''} ${c.lastName || ''}`.trim() === task.associatedContactName);
                                                                                if (matchedC) {
                                                                                    setDetailedDealId(null);
                                                                                    setDetailedContactId(matchedC._id);
                                                                                    setActiveTab('contacts');
                                                                                }
                                                                            }}
                                                                            title="Open Contact Details"
                                                                        >
                                                                            <User size={10} /> {task.associatedContactName}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}

                                                            <div className="pipeline-task-card-footer">
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11.5px', color: '#64748b' }}>
                                                                    <Clock size={11} />
                                                                    <span>{task.dueDate ? formatDateDDMMYYYY(task.dueDate) : 'No date'}</span>
                                                                </div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    <div className="pipeline-owner-pill-avatar" style={{ width: '20px', height: '20px', fontSize: '10px' }} title={task.assignee}>
                                                                        {(task.assignee || 'A').charAt(0)}
                                                                    </div>
                                                                    {canUpdate && (
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-mini-icon-btn"
                                                                            onClick={(e) => handleOpenEditTask(task, e)}
                                                                            title="Edit"
                                                                        >
                                                                            <Edit2 size={11} />
                                                                        </button>
                                                                    )}
                                                                    {canDelete && (
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-mini-icon-btn danger"
                                                                            onClick={(e) => handleDeleteTask(task._id, e)}
                                                                            title="Delete"
                                                                        >
                                                                            <Trash2 size={11} />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ====================================================================
            5. REMINDERS MANAGEMENT TAB VIEW
            ==================================================================== */}
                {activeTab === 'reminders' && (
                    <div className="pipeline-tab-page-view">
                        {/* Top Header */}
                        <div className="pipeline-top-header" style={{ padding: '16px 28px', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <h1 className="pipeline-deals-count-title" style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>
                                    Reminders & Follow-ups
                                </h1>
                                <span className="pipeline-badge-filter" style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                                    {filteredReminders.length} {filteredReminders.length === 1 ? 'Reminder' : 'Reminders'}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <NotificationBell filterType="CRM" />
                            </div>
                        </div>

                        {/* Action Toolbar */}
                        <div className="pipeline-toolbar-row">
                            <div className="pipeline-toolbar-left">
                                {/* Search Reminders */}
                                <div className="pipeline-contacts-search-box">
                                    <Search size={14} className="pipeline-search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Search reminders, deals, contacts..."
                                        value={reminderSearchQuery}
                                        onChange={e => setReminderSearchQuery(e.target.value)}
                                        className="pipeline-contacts-search-input"
                                    />
                                    {reminderSearchQuery && (
                                        <button
                                            type="button"
                                            className="pipeline-search-clear-btn"
                                            onClick={() => setReminderSearchQuery('')}
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>

                                {/* Filter by Status */}
                                <button
                                    type="button"
                                    className="pipeline-tool-btn"
                                    onClick={() => {
                                        const stList = ['all', 'Scheduled', 'Completed', 'Follow-up Needed', 'Rescheduled', 'Missed'];
                                        const idx = stList.indexOf(reminderStatusFilter);
                                        setReminderStatusFilter(stList[(idx + 1) % stList.length]);
                                    }}
                                    title="Filter by Status"
                                >
                                    <CheckCircle size={14} />
                                    <span>{reminderStatusFilter === 'all' ? 'All Statuses' : reminderStatusFilter}</span>
                                    <ChevronDown size={13} style={{ color: '#94a3b8' }} />
                                </button>

                                {/* Filter by Channel */}
                                <button
                                    type="button"
                                    className="pipeline-tool-btn"
                                    onClick={() => {
                                        const chList = ['all', 'Call', 'Meeting', 'WhatsApp', 'Email', 'Demo', 'Visit'];
                                        const idx = chList.indexOf(reminderChannelFilter);
                                        setReminderChannelFilter(chList[(idx + 1) % chList.length]);
                                    }}
                                    title="Filter by Channel"
                                >
                                    <Phone size={14} />
                                    <span>{reminderChannelFilter === 'all' ? 'All Channels' : `${reminderChannelFilter}`}</span>
                                    <ChevronDown size={13} style={{ color: '#94a3b8' }} />
                                </button>

                                {/* Filter by Timing */}
                                <button
                                    type="button"
                                    className="pipeline-tool-btn"
                                    onClick={() => {
                                        const tmList = ['all', 'today', 'upcoming', 'overdue'];
                                        const idx = tmList.indexOf(reminderTimingFilter);
                                        setReminderTimingFilter(tmList[(idx + 1) % tmList.length]);
                                    }}
                                    title="Filter by Timing"
                                >
                                    <Clock size={14} />
                                    <span>
                                        {reminderTimingFilter === 'all'
                                            ? 'All Timing'
                                            : reminderTimingFilter === 'today'
                                                ? 'Scheduled Today'
                                                : reminderTimingFilter === 'upcoming'
                                                    ? 'Upcoming'
                                                    : 'Overdue'}
                                    </span>
                                    <ChevronDown size={13} style={{ color: '#94a3b8' }} />
                                </button>

                                {/* Sort by */}
                                <button
                                    type="button"
                                    className="pipeline-tool-btn"
                                    onClick={() => {
                                        setReminderSortBy(prev => (prev === 'date_asc' ? 'date_desc' : 'date_asc'));
                                    }}
                                    title="Sort Reminders"
                                >
                                    <ArrowUpDown size={14} />
                                    <span>Sort: {reminderSortBy === 'date_asc' ? 'Due Soonest' : 'Due Latest'}</span>
                                </button>
                            </div>

                            <div className="pipeline-toolbar-right">
                                {/* Export Reminders */}
                                <button
                                    type="button"
                                    className="pipeline-btn-export"
                                    onClick={handleExportReminders}
                                    title="Export Reminders to Excel"
                                >
                                    <Download size={14} />
                                    <span>Export</span>
                                </button>

                                {/* Set Reminder Button */}
                                {canCreate && (
                                    <button
                                        type="button"
                                        className="pipeline-btn-create-deal"
                                        onClick={() => handleOpenCreateReminder('deal')}
                                        title="Set new reminder"
                                    >
                                        <Plus size={15} />
                                        <span>Set Reminder</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Metrics Row */}
                        <div className="pipeline-tasks-metrics-row">
                            <div className="pipeline-task-stat-card">
                                <div className="pipeline-task-stat-icon total">
                                    <Bell size={18} />
                                </div>
                                <div>
                                    <span className="pipeline-task-stat-label">Total Reminders</span>
                                    <h4 className="pipeline-task-stat-value">{allReminders.length}</h4>
                                </div>
                            </div>
                            <div className="pipeline-task-stat-card">
                                <div className="pipeline-task-stat-icon pending">
                                    <Clock size={18} />
                                </div>
                                <div>
                                    <span className="pipeline-task-stat-label">Scheduled / Upcoming</span>
                                    <h4 className="pipeline-task-stat-value">
                                        {allReminders.filter(r => r.status === 'Scheduled' || !r.status || r.status === 'Follow-up Needed').length}
                                    </h4>
                                </div>
                            </div>
                            <div className="pipeline-task-stat-card">
                                <div className="pipeline-task-stat-icon high">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <span className="pipeline-task-stat-label">Due Today / Overdue</span>
                                    <h4 className="pipeline-task-stat-value">
                                        {allReminders.filter(r => isReminderToday(r) || isReminderOverdue(r)).length}
                                    </h4>
                                </div>
                            </div>
                            <div className="pipeline-task-stat-card">
                                <div className="pipeline-task-stat-icon completed">
                                    <CheckCircle size={18} />
                                </div>
                                <div>
                                    <span className="pipeline-task-stat-label">Completed</span>
                                    <h4 className="pipeline-task-stat-value">
                                        {allReminders.filter(r => r.status === 'Completed').length}
                                    </h4>
                                </div>
                            </div>
                        </div>

                        {/* Content Body: List Table View */}
                        <div className="pipeline-board-container" style={{ padding: '0 28px 40px 28px' }}>
                            {isLoading ? (
                                renderTaskListSkeleton(7)
                            ) : filteredReminders.length === 0 ? (
                                <div className="pipeline-empty-state-card" style={{ maxWidth: '480px', margin: '40px auto' }}>
                                    <div className="pipeline-empty-icon-wrap" style={{ background: '#f0fdf4', color: '#16a34a' }}>
                                        <Bell size={28} />
                                    </div>
                                    <h3 className="pipeline-empty-title">No Reminders Found</h3>
                                    <p className="pipeline-empty-desc">
                                        {reminderSearchQuery || reminderStatusFilter !== 'all' || reminderChannelFilter !== 'all' || reminderTimingFilter !== 'all'
                                            ? 'No follow-up reminders match your search or filter criteria. Try clearing some filters.'
                                            : 'You do not have any scheduled follow-up reminders yet. Set follow-ups for your deals and contacts to stay on track.'}
                                    </p>
                                    {canCreate && (
                                        <button
                                            type="button"
                                            className="pipeline-btn-create-deal"
                                            style={{ marginTop: '14px' }}
                                            onClick={() => handleOpenCreateReminder('deal')}
                                        >
                                            <Plus size={14} />
                                            <span>Set First Reminder</span>
                                        </button>
                                    )}
                                </div>
                            ) : (
                                /* Table / List View */
                                <div className="pipeline-table-view" style={{ minWidth: 0, maxWidth: '100%', width: '100%', padding: 0 }}>
                                    <div className="pipeline-table-card pipeline-reminders-table-card">
                                        <table className="pipeline-table compact-table pipeline-reminders-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '42px', textAlign: 'center', whiteSpace: 'nowrap' }}>Done</th>
                                                    <th style={{ minWidth: '220px', whiteSpace: 'nowrap' }}>Follow-up / Purpose</th>
                                                    <th style={{ minWidth: '100px', whiteSpace: 'nowrap' }}>Channel</th>
                                                    <th style={{ minWidth: '180px', whiteSpace: 'nowrap' }}>Associated Deal</th>
                                                    <th style={{ minWidth: '190px', whiteSpace: 'nowrap' }}>Associated Contact</th>
                                                    <th style={{ minWidth: '190px', whiteSpace: 'nowrap' }}>Scheduled Date & Time</th>
                                                    <th style={{ minWidth: '130px', whiteSpace: 'nowrap' }}>Next Follow-up</th>
                                                    <th style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>Status</th>
                                                    <th style={{ minWidth: '150px', whiteSpace: 'nowrap' }}>Set By</th>
                                                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap', minWidth: '90px' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredReminders.map(reminder => {
                                                    const isDone = reminder.status === 'Completed';
                                                    const isOverdue = isReminderOverdue(reminder);
                                                    const isToday = isReminderToday(reminder);

                                                    return (
                                                        <tr key={reminder._id} className={isDone ? 'pipeline-row-completed' : ''}>
                                                            {/* Checkbox */}
                                                            <td style={{ textAlign: 'center' }}>
                                                                <label className="pipeline-task-checkbox-wrap" onClick={e => e.stopPropagation()}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isDone}
                                                                        disabled={!canUpdate || togglingReminderId === (reminder._id || reminder.id)}
                                                                        onChange={(e) => canUpdate && togglingReminderId !== (reminder._id || reminder.id) && handleToggleReminderStatus(reminder, e)}
                                                                    />
                                                                    <span className="pipeline-task-checkmark"></span>
                                                                </label>
                                                            </td>

                                                            {/* Title & Remarks */}
                                                            <td>
                                                                <div>
                                                                    <span className={`pipeline-task-row-title ${isDone ? 'done' : ''}`} style={{ fontWeight: 600, color: '#0f172a' }}>
                                                                        {reminder.title || 'Follow-up Discussion'}
                                                                    </span>
                                                                    {reminder.remarks && (
                                                                        <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#64748b', maxWidth: '320px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={reminder.remarks}>
                                                                            {reminder.remarks}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* Channel */}
                                                            <td>
                                                                <span className={`pipeline-task-type-badge ${(reminder.channel || 'call').toLowerCase()}`}>
                                                                    {getFollowUpChannelIcon(reminder.channel, 12)}
                                                                    <span style={{ marginLeft: '4px' }}>{reminder.channel || 'Call'}</span>
                                                                </span>
                                                            </td>

                                                            {/* Associated Deal (Clean text only, hover underline & blue) */}
                                                            <td>
                                                                {reminder.dealName || (reminder.entityType === 'deal' && reminder.entityName) ? (
                                                                    <button
                                                                        type="button"
                                                                        className="pipeline-clean-text-link"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const dealId = reminder.entityType === 'deal' ? reminder.entityId : (deals.find(d => (d.dealName || d.clientName) === reminder.dealName)?._id);
                                                                            if (dealId) {
                                                                                setDetailedContactId(null);
                                                                                setDetailedDealId(dealId);
                                                                                setActiveTab('deals');
                                                                            }
                                                                        }}
                                                                        title="Open Deal Details"
                                                                    >
                                                                        {reminder.dealName || reminder.entityName}
                                                                    </button>
                                                                ) : (
                                                                    <span style={{ color: '#cbd5e1' }}>—</span>
                                                                )}
                                                            </td>

                                                            {/* Associated Contact (Only number and email in text, hover underline & blue) */}
                                                            <td>
                                                                {(() => {
                                                                    const phone = reminder.contactPhone;
                                                                    const email = reminder.contactEmail;

                                                                    if (!phone && !email) {
                                                                        return <span style={{ color: '#cbd5e1' }}>—</span>;
                                                                    }

                                                                    const contactId = reminder.entityType === 'contact'
                                                                        ? reminder.entityId
                                                                        : (reminder.contactId || contacts.find(c =>
                                                                            (c.email && email && c.email.toLowerCase() === email.toLowerCase()) ||
                                                                            (c.phone && phone && c.phone === phone) ||
                                                                            (reminder.contactName && `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase() === reminder.contactName.toLowerCase())
                                                                        )?._id);

                                                                    return (
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-contact-details-link"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                if (contactId) {
                                                                                    setDetailedDealId(null);
                                                                                    setDetailedContactId(contactId);
                                                                                    setActiveTab('contacts');
                                                                                }
                                                                            }}
                                                                            title={contactId ? "Open Contact Details" : ""}
                                                                        >
                                                                            {phone && (
                                                                                <span className="pipeline-contact-info-item">
                                                                                    <Phone size={11} style={{ color: '#64748b' }} /> {phone}
                                                                                </span>
                                                                            )}
                                                                            {email && (
                                                                                <span className="pipeline-contact-info-item">
                                                                                    <Mail size={11} style={{ color: '#64748b' }} /> {email}
                                                                                </span>
                                                                            )}
                                                                        </button>
                                                                    );
                                                                })()}
                                                            </td>

                                                            {/* Scheduled Date & Time */}
                                                            <td>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                        <span style={{ fontSize: '12.5px', fontWeight: 600, color: isOverdue ? '#dc2626' : (isToday ? '#16a34a' : '#334155') }}>
                                                                            {reminder.followUpDate ? formatDateDDMMYYYY(reminder.followUpDate) : 'Not scheduled'}
                                                                        </span>
                                                                        {isOverdue && (
                                                                            <span style={{ fontSize: '10px', fontWeight: 700, background: '#fee2e2', color: '#dc2626', padding: '1px 5px', borderRadius: '4px' }}>
                                                                                OVERDUE
                                                                            </span>
                                                                        )}
                                                                        {isToday && (
                                                                            <span style={{ fontSize: '10px', fontWeight: 700, background: '#dcfce7', color: '#16a34a', padding: '1px 5px', borderRadius: '4px' }}>
                                                                                TODAY
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {reminder.followUpTime && (
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#64748b' }}>
                                                                            <Clock size={11} />
                                                                            <span>{reminder.followUpTime}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* Next Follow-up */}
                                                            <td>
                                                                {reminder.nextFollowUpDate ? (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#0284c7' }}>
                                                                        <Calendar size={12} />
                                                                        <span>{formatDateDDMMYYYY(reminder.nextFollowUpDate)}</span>
                                                                    </div>
                                                                ) : (
                                                                    <span style={{ color: '#cbd5e1' }}>—</span>
                                                                )}
                                                            </td>

                                                            {/* Status */}
                                                            <td>
                                                                <span className={`pipeline-task-status-badge ${(reminder.status || 'scheduled').toLowerCase().replace(/\s+/g, '-')}`}>
                                                                    {reminder.status || 'Scheduled'}
                                                                </span>
                                                            </td>

                                                            {/* Set By / Author & Linked Member */}
                                                            <td>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                        <div className="pipeline-owner-pill-avatar" style={{ width: '22px', height: '22px', fontSize: '10px' }} title={`Created by: ${reminder.author || 'Admin System'}`}>
                                                                            {(reminder.author || 'A').charAt(0).toUpperCase()}
                                                                        </div>
                                                                        <span style={{ fontSize: '12px', color: '#334155', fontWeight: 500 }}>
                                                                            {reminder.author || 'Admin System'}
                                                                        </span>
                                                                    </div>
                                                                    {(reminder.memberName || reminder.assignedToName) && (
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#0369a1', background: '#f0f9ff', border: '1px solid #bae6fd', padding: '1px 6px', borderRadius: '4px', width: 'fit-content' }}>
                                                                            <User size={10} />
                                                                            <span>For: {reminder.memberName || reminder.assignedToName}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* Actions */}
                                                            <td style={{ textAlign: 'right' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                                                                    {canUpdate && (
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-mini-icon-btn"
                                                                            style={{ width: '24px', height: '24px' }}
                                                                            onClick={(e) => handleOpenEditReminder(reminder, e)}
                                                                            disabled={deletingReminderId === (reminder._id || reminder.id) || isSubmittingReminder}
                                                                            title="Edit Reminder"
                                                                        >
                                                                            <Edit2 size={11} />
                                                                        </button>
                                                                    )}
                                                                    {canDelete && (
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-mini-icon-btn danger"
                                                                            style={{ width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                                            onClick={(e) => handleDeleteReminder(reminder, e)}
                                                                            disabled={deletingReminderId === (reminder._id || reminder.id)}
                                                                            title="Delete Reminder"
                                                                        >
                                                                            {deletingReminderId === (reminder._id || reminder.id) ? (
                                                                                <Loader2 size={11} className="pipeline-spinner-icon" />
                                                                            ) : (
                                                                                <Trash2 size={11} />
                                                                            )}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </main>

            {/* ------------------------------------------------------------------
        4. Activity Hover Popover (Fixed Position floating outside cards)
        ------------------------------------------------------------------ */}
            {(hoveredDeal || hoveredContact) && (() => {
                const isDeal = !!hoveredDeal;
                const itemName = isDeal
                    ? (hoveredDeal.clientName || hoveredDeal.dealName || 'Deal')
                    : (`${hoveredContact.firstName || ''} ${hoveredContact.lastName || ''}`.trim() || 'Contact');
                const itemEmail = isDeal
                    ? (hoveredDeal.email || hoveredDeal.associatedContact || '')
                    : (hoveredContact.email || '');
                const itemPhone = isDeal ? (hoveredDeal.phone || '') : (hoveredContact.phone || '');
                const nextTask = hoveredUpcomingTasks && hoveredUpcomingTasks.length > 0 ? hoveredUpcomingTasks[0] : null;

                return (
                    <div
                        className={`pipeline-activity-hover-popover fixed ${popoverCoords.placement}`}
                        style={{
                            top: `${popoverCoords.top}px`,
                            left: `${popoverCoords.left}px`,
                            '--arrow-top': `${popoverCoords.arrowTop}px`
                        }}
                        onMouseEnter={() => {
                            if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                        }}
                        onMouseLeave={handleActivityMouseLeave}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Left Column: Previous activity */}
                        <div className="pipeline-act-popover-left">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                <h4 className="pipeline-act-popover-title" style={{ margin: 0 }}>Previous activity</h4>
                                {hoveredActivities.length > 0 && (
                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, background: '#f1f5f9', padding: '1px 6px', borderRadius: '999px' }}>
                                        {hoveredActivities.length}
                                    </span>
                                )}
                            </div>

                            <div className="pipeline-act-timeline">
                                {hoveredActivities.length === 0 ? (
                                    <div className="pipeline-act-empty">
                                        <Clock size={20} style={{ color: '#cbd5e1', marginBottom: '4px' }} />
                                        <span>No previous activity recorded</span>
                                    </div>
                                ) : (
                                    hoveredActivities.slice(0, 3).map((act, actIdx) => {
                                        const actIcon = (() => {
                                            if (act.type === 'payment') return <CreditCard size={12} className="pipeline-act-icon" />;
                                            if (act.type === 'note') return <FileText size={12} className="pipeline-act-icon" />;
                                            if (act.type === 'email') return <Mail size={12} className="pipeline-act-icon" />;
                                            if (act.type === 'message') return <MessageSquare size={12} className="pipeline-act-icon" />;
                                            if (act.type === 'task') return <CheckSquare size={12} className="pipeline-act-icon" />;
                                            if (act.type === 'deal') return <Briefcase size={12} className="pipeline-act-icon" />;
                                            if (act.type === 'contact') return <User size={12} className="pipeline-act-icon" />;
                                            if (act.type === 'company') return <Building2 size={12} className="pipeline-act-icon" />;
                                            return <Tag size={12} className="pipeline-act-icon" />;
                                        })();

                                        const displayTitle = act.title || (act.type === 'note' ? 'Internal Note' : act.type === 'email' ? 'Email' : 'Activity');
                                        const relativeTimeStr = formatRelativeActivityTime(act);

                                        return (
                                            <div key={act.id || `${act.title}-${actIdx}`} className="pipeline-act-item">
                                                <div className={`pipeline-act-icon-wrap ${act.type || 'activity'}`}>
                                                    {actIcon}
                                                </div>
                                                <div className="pipeline-act-details">
                                                    <a
                                                        href="#activity"
                                                        className="pipeline-act-link"
                                                        title={`${displayTitle}${act.content ? `\n${act.content}` : ''}`}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setHoveredActivityDealId(null);
                                                            setHoveredActivityContactId(null);
                                                            if (isDeal) {
                                                                setDetailedDealId(hoveredDeal._id);
                                                            } else {
                                                                setDetailedContactId(hoveredContact._id);
                                                            }
                                                        }}
                                                    >
                                                        {displayTitle}
                                                    </a>
                                                    <span className="pipeline-act-time" title={formatActivityTimestamp(act)}>
                                                        {relativeTimeStr}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            <button
                                type="button"
                                className="pipeline-act-view-all-btn"
                                onClick={(e) => {
                                    setHoveredActivityDealId(null);
                                    setHoveredActivityContactId(null);
                                    if (isDeal) {
                                        setDetailedDealId(hoveredDeal._id);
                                    } else {
                                        setDetailedContactId(hoveredContact._id);
                                    }
                                }}
                            >
                                <span>View all activity</span>
                                <ExternalLink size={12} />
                            </button>
                        </div>

                        {/* Right Column: Upcoming next step + Quick Actions */}
                        <div className="pipeline-act-popover-right">
                            <h4 className="pipeline-act-popover-title">
                                {nextTask ? 'Upcoming next step' : 'No upcoming next step'}
                            </h4>

                            {nextTask ? (
                                <div className="pipeline-act-next-step-box" style={{ marginBottom: '12px', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: '#0f172a' }}>
                                        <CheckSquare size={13} style={{ color: '#0284c7', flexShrink: 0 }} />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nextTask.title}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', fontSize: '10.5px', color: '#64748b' }}>
                                        <span>Due: {nextTask.dueDate ? formatDateDDMMYYYY(nextTask.dueDate) : 'No due date'}</span>
                                        <span>•</span>
                                        <span>{nextTask.assignee || 'Assigned'}</span>
                                    </div>
                                </div>
                            ) : null}

                            <div className="pipeline-act-quick-buttons">
                                <button
                                    type="button"
                                    className="pipeline-act-circle-btn"
                                    title="Add Note"
                                    onClick={() => {
                                        setHoveredActivityDealId(null);
                                        setHoveredActivityContactId(null);
                                        if (isDeal) {
                                            setDetailedDealId(hoveredDeal._id);
                                        } else {
                                            setDetailedContactId(hoveredContact._id);
                                        }
                                        setDetailActiveTab('notes');
                                        setNewActivityType('note');
                                        setIsComposingActivity(true);
                                    }}
                                >
                                    <FileText size={15} />
                                </button>

                                <a
                                    href={`mailto:${itemEmail || 'contact@example.com'}`}
                                    className="pipeline-act-circle-btn"
                                    title="Send Email"
                                >
                                    <Mail size={15} />
                                </a>

                                <button
                                    type="button"
                                    className="pipeline-act-circle-btn"
                                    title="Create Task"
                                    onClick={() => {
                                        setHoveredActivityDealId(null);
                                        setHoveredActivityContactId(null);
                                        handleOpenCreateTask(isDeal ? hoveredDeal._id : '', isDeal ? '' : hoveredContact._id);
                                    }}
                                >
                                    <CheckSquare size={15} />
                                </button>

                                <button
                                    type="button"
                                    className="pipeline-act-circle-btn"
                                    title="Schedule Meeting - Feature will be implemented soon"
                                    style={{ opacity: 0.55, cursor: 'not-allowed' }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                >
                                    <Calendar size={15} />
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ------------------------------------------------------------------
        5. View Deal Details Modal
        ------------------------------------------------------------------ */}
            {viewingDeal && (
                <div className="pipeline-modal-backdrop" onClick={() => setViewingDeal(null)}>
                    <div className="pipeline-modal-card pipeline-view-deal-modal" onClick={e => e.stopPropagation()}>
                        <div className="pipeline-modal-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                <div className="pipeline-card-avatar" style={{ width: '44px', height: '44px', fontSize: '17px', borderRadius: '12px' }}>
                                    {viewingDeal.clientName.charAt(0)}
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '19px', margin: 0, fontWeight: 700 }}>{viewingDeal.clientName}</h3>
                                    <p className="pipeline-modal-subtitle" style={{ margin: '2px 0 0 0' }}>
                                        {(() => {
                                            const raw = viewingDeal.dealName || viewingDeal.clientName || '';
                                            const dt = (viewingDeal.dealType || 'New Business').trim();
                                            const regex = new RegExp(`\\s*-\\s*${dt}$`, 'i');
                                            if (regex.test(raw)) return raw.replace(regex, '').trim();
                                            return raw.replace(/\s*-\s*New Business$/i, '').trim();
                                        })()}
                                    </p>
                                </div>
                            </div>
                            <button
                                className="pipeline-modal-close-btn"
                                onClick={() => setViewingDeal(null)}
                                title="Close"
                            >
                                <X size={19} />
                            </button>
                        </div>

                        <div className="pipeline-modal-body">
                            <div className="pipeline-view-details-grid">
                                <div className="pipeline-view-detail-card">
                                    <span className="pipeline-view-detail-label">Stage</span>
                                    <span className="pipeline-view-detail-value font-semibold">{viewingDeal.pipeline || 'Sales Pipeline'}</span>
                                </div>

                                <div className="pipeline-view-detail-card">
                                    <span className="pipeline-view-detail-label">Status</span>
                                    <div>
                                        <span className={`pipeline-stage-badge ${(viewingDeal.stage || 'new').toLowerCase()}`}>
                                            {viewingDeal.stage}
                                        </span>
                                    </div>
                                </div>

                                <div className="pipeline-view-detail-card highlight">
                                    <span className="pipeline-view-detail-label">Deal Value</span>
                                    <span className="pipeline-view-detail-value price">
                                        {viewingDeal.currency || '$'} {Number(viewingDeal.dealValue || 0).toLocaleString()}
                                    </span>
                                </div>

                                <div className="pipeline-view-detail-card">
                                    <span className="pipeline-view-detail-label">Deal Type</span>
                                    <span className="pipeline-view-detail-value">{viewingDeal.dealType || 'New Business'}</span>
                                </div>

                                <div className="pipeline-view-detail-card">
                                    <span className="pipeline-view-detail-label">Expected Close Date</span>
                                    <span className="pipeline-view-detail-value">{formatDateDDMMYYYY(viewingDeal.expectedCloseDate)}</span>
                                </div>

                                <div className="pipeline-view-detail-card">
                                    <span className="pipeline-view-detail-label">Deal Owner</span>
                                    <span className="pipeline-view-detail-value">{viewingDeal.salesOwner || 'Artiflow'}</span>
                                </div>

                                <div className="pipeline-view-detail-card">
                                    <span className="pipeline-view-detail-label">Email</span>
                                    <span className="pipeline-view-detail-value">{viewingDeal.email || '—'}</span>
                                </div>

                                <div className="pipeline-view-detail-card">
                                    <span className="pipeline-view-detail-label">Phone</span>
                                    <span className="pipeline-view-detail-value">{viewingDeal.phone || '—'}</span>
                                </div>

                                <div className="pipeline-view-detail-card">
                                    <span className="pipeline-view-detail-label">Location</span>
                                    <span className="pipeline-view-detail-value">
                                        {(() => {
                                            const comp = companies.find(c => c.name && viewingDeal.associatedCompany && c.name.toLowerCase() === viewingDeal.associatedCompany.toLowerCase());
                                            if (comp?.city || comp?.state || comp?.country) {
                                                return [comp.city, comp.state, comp.country].filter(Boolean).join(', ');
                                            }
                                            if (viewingDeal.location && viewingDeal.location !== 'Pembroke Pines' && viewingDeal.location !== 'Pune, India' && viewingDeal.location !== 'solar panel comp') {
                                                return viewingDeal.location;
                                            }
                                            return '-';
                                        })()}
                                    </span>
                                </div>

                                {viewingDeal.associatedContact && (
                                    <div className="pipeline-view-detail-card">
                                        <span className="pipeline-view-detail-label">Associated Contact</span>
                                        <span
                                            className="pipeline-view-detail-value font-semibold"
                                            style={{
                                                color: '#0284c7',
                                                cursor: 'pointer',
                                                textDecoration: 'none',
                                                display: 'inline-block'
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                                            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                                            onClick={() => {
                                                const targetEmail = (viewingDeal.associatedContact || '').trim().toLowerCase();
                                                const targetName = (viewingDeal.clientName || '').trim().toLowerCase();
                                                const matched = contacts.find(c => {
                                                    if (viewingDeal.contactId && c._id === viewingDeal.contactId) return true;
                                                    const ce = (c.email || '').trim().toLowerCase();
                                                    const cn = `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase();
                                                    return (ce && ce === targetEmail) || (cn && (cn === targetEmail || cn === targetName));
                                                });
                                                setViewingDeal(null);
                                                setDetailedDealId(null);
                                                if (matched) {
                                                    setActiveTab('contacts');
                                                    setDetailedContactId(matched._id);
                                                } else {
                                                    setActiveTab('contacts');
                                                    setDetailedContactId(null);
                                                }
                                            }}
                                            title="Click to view contact details"
                                        >
                                            {viewingDeal.associatedContact}
                                        </span>
                                    </div>
                                )}

                                {viewingDeal.associatedCompany && (
                                    <div className="pipeline-view-detail-card">
                                        <span className="pipeline-view-detail-label">Associated Company</span>
                                        <span className="pipeline-view-detail-value font-semibold">{viewingDeal.associatedCompany}</span>
                                    </div>
                                )}

                                <div className="pipeline-view-detail-card">
                                    <span className="pipeline-view-detail-label">Added By</span>
                                    <span className="pipeline-view-detail-value">
                                        {viewingDeal.addedBy || (viewingDeal.createdBy?.firstName ? `${viewingDeal.createdBy.firstName} ${viewingDeal.createdBy.lastName || ''}`.trim() : '') || viewingDeal.salesOwner || 'Admin System'}
                                    </span>
                                </div>

                                <div className="pipeline-view-detail-card">
                                    <span className="pipeline-view-detail-label">Created On</span>
                                    <span className="pipeline-view-detail-value">
                                        {formatISTDateTime(viewingDeal.createdAt || viewingDeal.createdDate || viewingDeal.date)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="pipeline-modal-footer">
                            <button
                                type="button"
                                className="pipeline-btn-cancel"
                                onClick={() => setViewingDeal(null)}
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                className="pipeline-btn-save"
                                onClick={() => {
                                    const target = viewingDeal;
                                    setViewingDeal(null);
                                    handleOpenEditDeal(target);
                                }}
                            >
                                Edit Deal
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ------------------------------------------------------------------
          5. Create / Edit Deal Modal (with Associate Deal with section)
          ------------------------------------------------------------------ */}
            {showCreateModal && (
                <div className="pipeline-modal-backdrop" onClick={() => { setShowCreateModal(false); setEditingDeal(null); }}>
                    <div className="pipeline-modal-card pipeline-modal-wide" onClick={e => e.stopPropagation()}>
                        <div className="pipeline-modal-header">
                            <div>
                                <h3 className="pipeline-modal-title">{editingDeal ? 'Edit Deal' : 'Create New Deal'}</h3>
                                <p className="pipeline-modal-subtitle">
                                    {editingDeal ? 'Update the details for this deal opportunity.' : 'Fill in the details below to add a new opportunity.'}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="pipeline-modal-close-btn"
                                onClick={() => { setShowCreateModal(false); setEditingDeal(null); }}
                                title="Close"
                            >
                                <X size={19} />
                            </button>
                        </div>

                        <form onSubmit={handleCreateDealSubmit}>
                            <div className="pipeline-modal-body">
                                {/* Row 1: Deal Name & Deal Owner */}
                                <div className="pipeline-form-row-grid">
                                    <div className="pipeline-form-group">
                                        <label>Deal Name <span className="pipeline-required">*</span></label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="Enter deal name"
                                            className="pipeline-form-input"
                                            value={formData.clientName}
                                            onChange={e => setFormData({ ...formData, clientName: e.target.value })}
                                        />
                                    </div>

                                    <div className="pipeline-form-group">
                                        <label>Deal Owner <span className="pipeline-required">*</span></label>
                                        <CreatableSelect
                                            value={formData.salesOwner}
                                            onChange={val => setFormData({ ...formData, salesOwner: val })}
                                            options={ownerOptions}
                                            placeholder="Select deal owner..."
                                            isSearchable={false}
                                            isCreatable={false}
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Row 2: Stage & Status */}
                                <div className="pipeline-form-row-grid">
                                    <div className="pipeline-form-group">
                                        <label>Stage <span className="pipeline-required">*</span></label>
                                        <CreatableSelect
                                            value={formData.pipeline}
                                            onChange={val => setFormData({ ...formData, pipeline: val })}
                                            options={visiblePipelineOptions}
                                            placeholder="Select or add stage..."
                                            onCreateOption={handleCreatePipelineOption}
                                            isSearchable={true}
                                            isCreatable={true}
                                            required
                                        />
                                    </div>

                                    <div className="pipeline-form-group">
                                        <label>Status <span className="pipeline-required">*</span></label>
                                        <CreatableSelect
                                            value={modalStage}
                                            onChange={val => {
                                                setModalStage(val);
                                                setFormData({ ...formData, stage: val });
                                            }}
                                            options={visibleStages.map(s => s.name)}
                                            placeholder="Select or add status..."
                                            onCreateOption={handleCreateStageOption}
                                            isSearchable={true}
                                            isCreatable={true}
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Row 3: Deal Type & Deal Value */}
                                <div className="pipeline-form-row-grid">
                                    <div className="pipeline-form-group">
                                        <label>Deal Type <span className="pipeline-required">*</span></label>
                                        <CreatableSelect
                                            value={formData.dealType}
                                            onChange={val => setFormData({ ...formData, dealType: val })}
                                            options={dealTypes}
                                            placeholder="Select or add type..."
                                            onCreateOption={handleCreateDealTypeOption}
                                            isSearchable={true}
                                            isCreatable={true}
                                            required
                                        />
                                    </div>

                                    <div className="pipeline-form-group">
                                        <label>Deal Value <span className="pipeline-required">*</span></label>
                                        <div className="pipeline-amount-custom-group">
                                            {/* Currency Selector Dropdown Card */}
                                            <div className="pipeline-currency-picker-wrap" ref={currencyRef}>
                                                <button
                                                    type="button"
                                                    className="pipeline-currency-trigger-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsCurrencyOpen(prev => !prev);
                                                    }}
                                                >
                                                    <span className="pipeline-currency-curr-label">{formData.currency || '$'}</span>
                                                    <ChevronDown size={14} className={`pipeline-chevron-icon ${isCurrencyOpen ? 'open' : ''}`} />
                                                </button>

                                                {isCurrencyOpen && (
                                                    <div className="pipeline-creatable-dropdown pipeline-currency-dropdown placement-bottom">
                                                        <div className="pipeline-creatable-options-list">
                                                            {CURRENCY_OPTIONS.map((c) => {
                                                                const isSelected = (formData.currency || '$') === c.symbol;
                                                                return (
                                                                    <div
                                                                        key={c.code}
                                                                        className={`pipeline-creatable-option ${isSelected ? 'selected' : ''}`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setFormData({ ...formData, currency: c.symbol });
                                                                            setIsCurrencyOpen(false);
                                                                        }}
                                                                    >
                                                                        <span className="pipeline-creatable-opt-text">{c.label}</span>
                                                                        {isSelected && <CheckCircle size={14} className="pipeline-creatable-opt-check" />}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Numeric Amount Input */}
                                            <input
                                                type="number"
                                                required
                                                min="0"
                                                step="any"
                                                placeholder="Enter amount"
                                                className="pipeline-amount-number-input"
                                                value={formData.dealValue}
                                                onChange={e => setFormData({ ...formData, dealValue: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Row 4: Phone Number & Email Address */}
                                {(() => {
                                    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                                    const cleanEmail = (formData.email || '').trim().toLowerCase();
                                    const isEmailValid = emailRegex.test(cleanEmail);
                                    const isEmailDirty = Boolean(cleanEmail.length > 0);

                                    const cleanPhoneDigits = (formData.phone || '').replace(/\D/g, '');
                                    const minPhoneDigits = dealSelectedCountry?.minDigits || dealSelectedCountry?.maxDigits || 10;
                                    const maxPhoneDigits = dealSelectedCountry?.maxDigits || 10;
                                    const isPhoneValid = cleanPhoneDigits.length >= minPhoneDigits && cleanPhoneDigits.length <= maxPhoneDigits;
                                    const isPhoneDirty = cleanPhoneDigits.length > 0;

                                    return (
                                        <div className="pipeline-form-row-grid">
                                            {/* Phone Number */}
                                            <div className="pipeline-form-group">
                                                <label>Phone Number</label>
                                                <div
                                                    className="pipeline-phone-input-combo"
                                                    style={{
                                                        borderColor: isPhoneDirty ? (isPhoneValid ? '#10b981' : '#ef4444') : undefined,
                                                        borderWidth: isPhoneDirty ? '1.5px' : undefined,
                                                        boxShadow: 'none',
                                                        backgroundColor: '#ffffff'
                                                    }}
                                                >
                                                    <div className="pipeline-country-picker-wrap" ref={dealCountryCodeRef}>
                                                        <button
                                                            type="button"
                                                            className="pipeline-country-trigger-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setIsDealCountryCodeOpen(prev => !prev);
                                                            }}
                                                            title="Select Country Code"
                                                        >
                                                            <span className="pipeline-country-flag">{dealSelectedCountry?.flag}</span>
                                                            <span className="pipeline-country-code-text">{dealSelectedCountry?.code}</span>
                                                            <ChevronDown size={14} className={`pipeline-chevron-icon ${isDealCountryCodeOpen ? 'open' : ''}`} />
                                                        </button>

                                                        {isDealCountryCodeOpen && (
                                                            <div className="pipeline-creatable-dropdown pipeline-country-dropdown placement-bottom">
                                                                <div className="pipeline-creatable-options-list">
                                                                    {COUNTRY_CODES.map((country) => {
                                                                        const isSelected = dealSelectedCountry?.code === country.code;
                                                                        return (
                                                                            <div
                                                                                key={country.code + country.country}
                                                                                className={`pipeline-creatable-option ${isSelected ? 'selected' : ''}`}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleSelectDealCountry(country);
                                                                                }}
                                                                            >
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                    <span style={{ fontSize: '15px' }}>{country.flag}</span>
                                                                                    <span className="pipeline-creatable-opt-text">{country.country}</span>
                                                                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>({country.code})</span>
                                                                                </div>
                                                                                {isSelected && <CheckCircle size={14} className="pipeline-creatable-opt-check" />}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <input
                                                        type="tel"
                                                        placeholder={dealSelectedCountry?.formatPlaceholder || 'Enter phone number'}
                                                        className="pipeline-phone-digits-input"
                                                        value={formData.phone || ''}
                                                        onChange={handleDealPhoneChange}
                                                        maxLength={dealSelectedCountry?.maxDigits || 15}
                                                    />

                                                    {isPhoneDirty && isPhoneValid && (
                                                        <CheckCircle size={16} className="pipeline-field-icon" style={{ color: '#16a34a', marginRight: '10px' }} />
                                                    )}
                                                </div>
                                            </div>

                                            {/* Email Address */}
                                            <div className="pipeline-form-group">
                                                <label>Email Address</label>
                                                <div className="pipeline-input-with-icon">
                                                    <input
                                                        type="email"
                                                        placeholder="Enter email address"
                                                        className="pipeline-form-input"
                                                        value={formData.email || ''}
                                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                                        style={{
                                                            borderColor: isEmailDirty ? (isEmailValid ? '#10b981' : '#ef4444') : undefined,
                                                            borderWidth: isEmailDirty ? '1.5px' : undefined,
                                                            boxShadow: 'none',
                                                            backgroundColor: '#ffffff'
                                                        }}
                                                    />
                                                    {isEmailDirty && isEmailValid ? (
                                                        <CheckCircle size={16} className="pipeline-field-icon" style={{ color: '#16a34a' }} />
                                                    ) : (
                                                        <Mail size={16} className="pipeline-field-icon" style={{ color: isEmailDirty && !isEmailValid ? '#ef4444' : undefined }} />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Row 5: Close Date */}
                                <div className="pipeline-form-row-grid">
                                    <div className="pipeline-form-group">
                                        <label>Close Date <span className="pipeline-required">*</span></label>
                                        <div className="pipeline-date-input-wrap">
                                            <input
                                                type="text"
                                                required
                                                placeholder="DD-MM-YYYY"
                                                className="pipeline-form-input"
                                                value={formData.expectedCloseDate}
                                                onChange={e => setFormData({ ...formData, expectedCloseDate: e.target.value })}
                                            />
                                            <input
                                                type="date"
                                                tabIndex={-1}
                                                className="pipeline-hidden-date-picker"
                                                onChange={e => {
                                                    if (e.target.value) {
                                                        const [y, m, d] = e.target.value.split('-');
                                                        setFormData({ ...formData, expectedCloseDate: `${d}-${m}-${y}` });
                                                    }
                                                }}
                                            />
                                            <Calendar size={16} className="pipeline-date-picker-icon" />
                                        </div>
                                    </div>
                                </div>

                                {/* Section Divider & Associate Deal with Header */}
                                <hr className="pipeline-modal-section-divider" />
                                <h4 className="pipeline-modal-section-title">Associate Deal with</h4>

                                {/* Associate Deal with Company */}
                                <div className="pipeline-form-group">
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            Company <Info size={13} className="pipeline-info-icon" title="Select or type associated company name" />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setEditingCompany(null);
                                                setCompanyFormData({
                                                    name: formData.associatedCompany || '',
                                                    domain: '',
                                                    owner: [],
                                                    industry: 'Software & Tech',
                                                    country: 'United States',
                                                    state: '',
                                                    city: formData.location || '',
                                                    postalCode: ''
                                                });
                                                setShowCreateCompanyModal(true);
                                            }}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                padding: '0',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                color: '#0284c7',
                                                cursor: 'pointer',
                                                textDecoration: 'none'
                                            }}
                                            title="Add a new company via modal"
                                        >
                                            Add+
                                        </button>
                                    </div>
                                    <CreatableSelect
                                        value={formData.associatedCompany}
                                        onChange={val => setFormData({ ...formData, associatedCompany: val })}
                                        options={companyOptions}
                                        placeholder="Search"
                                        isSearchable={true}
                                        isCreatable={true}
                                        placement="top"
                                    />
                                </div>
                            </div>

                            <div className="pipeline-modal-footer">
                                <button
                                    type="button"
                                    className="pipeline-btn-cancel"
                                    onClick={() => { setShowCreateModal(false); setEditingDeal(null); }}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="pipeline-btn-save">
                                    {editingDeal ? 'Save Changes' : 'Create Deal'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ------------------------------------------------------------------
          6. Create & Edit Contact Modal (Wide Professional Balanced Layout)
          ------------------------------------------------------------------ */}
            {showCreateContactModal && (
                <div className="pipeline-modal-backdrop" onClick={() => { setShowCreateContactModal(false); setEditingContact(null); }}>
                    <div className="pipeline-modal-card pipeline-modal-wide" onClick={e => e.stopPropagation()}>
                        <div className="pipeline-modal-header">
                            <div>
                                <h3 className="pipeline-modal-title">{editingContact ? 'Edit Contact' : 'Create Contact'}</h3>
                                <p className="pipeline-modal-subtitle">
                                    {editingContact ? 'Update contact and lead qualification details' : 'Add a new client lead, contact, or partner to your CRM database'}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="pipeline-modal-close-btn"
                                onClick={() => { setShowCreateContactModal(false); setEditingContact(null); }}
                                title="Close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleContactSubmit}>
                            <div className="pipeline-modal-body">
                                {/* Row 1: First Name & Last Name */}
                                <div className="pipeline-form-row-grid">
                                    <div className="pipeline-form-group">
                                        <label>First Name <span className="pipeline-required">*</span></label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="Enter first name"
                                            className="pipeline-form-input"
                                            value={contactFormData.firstName}
                                            onChange={e => setContactFormData({ ...contactFormData, firstName: e.target.value })}
                                        />
                                    </div>

                                    <div className="pipeline-form-group">
                                        <label>Last Name</label>
                                        <input
                                            type="text"
                                            placeholder="Enter last name"
                                            className="pipeline-form-input"
                                            value={contactFormData.lastName}
                                            onChange={e => setContactFormData({ ...contactFormData, lastName: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Row 2: Email Address & Phone Number with Real-time Validation */}
                                {(() => {
                                    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                                    const cleanEmail = (contactFormData.email || '').trim().toLowerCase();
                                    const isEmailValid = emailRegex.test(cleanEmail);
                                    const isEmailDirty = Boolean(cleanEmail.length > 0);
                                    const isDuplicateEmail = Boolean(
                                        cleanEmail &&
                                        contacts.some(c =>
                                            (c.email || '').trim().toLowerCase() === cleanEmail &&
                                            (!editingContact || (c._id && editingContact._id && c._id.toString() !== editingContact._id.toString()))
                                        )
                                    );
                                    const isEmailInvalidOrDuplicate = isEmailDirty && (!isEmailValid || isDuplicateEmail);

                                    const cleanPhoneDigits = (contactFormData.phone || '').replace(/\D/g, '');
                                    const requiredPhoneDigits = selectedCountry?.maxDigits || 10;
                                    const isPhoneValid = cleanPhoneDigits.length === requiredPhoneDigits;
                                    const isPhoneDirty = cleanPhoneDigits.length > 0;

                                    return (
                                        <>
                                            <div className="pipeline-form-row-grid">
                                                {/* Email Address */}
                                                <div className="pipeline-form-group">
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                        <label style={{ margin: 0 }}>Email Address</label>
                                                        {isDuplicateEmail && (
                                                            <span style={{ fontSize: '11px', color: '#ef4444', fontWeight: 600 }}>Already exists</span>
                                                        )}
                                                    </div>
                                                    <div className="pipeline-input-with-icon">
                                                        <input
                                                            type="email"
                                                            placeholder="Enter email address"
                                                            className="pipeline-form-input"
                                                            value={contactFormData.email}
                                                            onChange={e => setContactFormData({ ...contactFormData, email: e.target.value })}
                                                            style={{
                                                                borderColor: isEmailDirty ? (isEmailInvalidOrDuplicate ? '#ef4444' : '#10b981') : undefined,
                                                                borderWidth: isEmailDirty ? '1.5px' : undefined,
                                                                boxShadow: 'none',
                                                                backgroundColor: '#ffffff'
                                                            }}
                                                        />
                                                        {isEmailDirty && isEmailValid && !isDuplicateEmail ? (
                                                            <CheckCircle size={16} className="pipeline-field-icon" style={{ color: '#16a34a' }} />
                                                        ) : (
                                                            <Mail size={16} className="pipeline-field-icon" style={{ color: isEmailInvalidOrDuplicate ? '#ef4444' : undefined }} />
                                                        )}
                                                    </div>
                                                    {isEmailDirty && !isEmailValid && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontSize: '11px', color: '#ef4444' }}>
                                                            <AlertCircle size={12} />
                                                            <span>Please enter a complete and valid email address.</span>
                                                        </div>
                                                    )}
                                                    {isEmailDirty && isEmailValid && isDuplicateEmail && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontSize: '11px', color: '#ef4444' }}>
                                                            <AlertCircle size={12} />
                                                            <span>This email already belongs to an existing contact. Duplicate emails are not allowed.</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Phone Number with Real-time Count & Country Limit */}
                                                <div className="pipeline-form-group">
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                        <label style={{ margin: 0 }}>Phone Number</label>
                                                    </div>

                                                    <div
                                                        className="pipeline-phone-input-combo"
                                                        style={{
                                                            borderColor: isPhoneDirty ? (isPhoneValid ? '#10b981' : '#ef4444') : undefined,
                                                            borderWidth: isPhoneDirty ? '1.5px' : undefined,
                                                            boxShadow: 'none',
                                                            backgroundColor: '#ffffff'
                                                        }}
                                                    >
                                                        {/* Country Code Dropdown Trigger */}
                                                        <div className="pipeline-country-picker-wrap" ref={countryCodeRef}>
                                                            <button
                                                                type="button"
                                                                className="pipeline-country-trigger-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setIsCountryCodeOpen(prev => !prev);
                                                                }}
                                                                title="Select Country Code"
                                                            >
                                                                <span className="pipeline-country-flag">{selectedCountry?.flag}</span>
                                                                <span className="pipeline-country-code-text">{selectedCountry?.code}</span>
                                                                <ChevronDown size={14} className={`pipeline-chevron-icon ${isCountryCodeOpen ? 'open' : ''}`} />
                                                            </button>

                                                            {isCountryCodeOpen && (
                                                                <div className="pipeline-creatable-dropdown pipeline-country-dropdown placement-bottom">
                                                                    <div className="pipeline-creatable-options-list">
                                                                        {COUNTRY_CODES.map((country) => {
                                                                            const isSelected = selectedCountry?.code === country.code;
                                                                            return (
                                                                                <div
                                                                                    key={country.code + country.country}
                                                                                    className={`pipeline-creatable-option ${isSelected ? 'selected' : ''}`}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleSelectCountry(country);
                                                                                    }}
                                                                                >
                                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                        <span style={{ fontSize: '15px' }}>{country.flag}</span>
                                                                                        <span className="pipeline-creatable-opt-text">{country.country}</span>
                                                                                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>({country.code})</span>
                                                                                    </div>
                                                                                    {isSelected && <CheckCircle size={14} className="pipeline-creatable-opt-check" />}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Numeric Phone Number Input */}
                                                        <div className="pipeline-phone-input-box" style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                                                            <input
                                                                type="tel"
                                                                placeholder={selectedCountry?.formatPlaceholder || `Enter ${requiredPhoneDigits} digits`}
                                                                className="pipeline-phone-input-field"
                                                                value={contactFormData.phone}
                                                                onChange={handlePhoneChange}
                                                                maxLength={requiredPhoneDigits}
                                                            />
                                                            {isPhoneDirty && isPhoneValid && (
                                                                <CheckCircle size={15} style={{ color: '#16a34a', marginRight: '10px', flexShrink: 0 }} />
                                                            )}
                                                        </div>
                                                    </div>
                                                    {isPhoneDirty && !isPhoneValid && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontSize: '11px', color: '#d97706' }}>
                                                            <Info size={12} />
                                                            <span>Requires exactly {requiredPhoneDigits} digits for {selectedCountry?.country || 'selected country'} ({cleanPhoneDigits.length} entered)</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Row 3: Company Dropdown with Add Text Button & Origin Dropdown */}
                                            <div className="pipeline-form-row-grid">
                                                <div className="pipeline-form-group">
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                        <label style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                                                            Company / Organization
                                                        </label>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setEditingCompany(null);
                                                                setCompanyFormData({
                                                                    name: '',
                                                                    domain: '',
                                                                    owner: [],
                                                                    industry: '',
                                                                    country: selectedCountry?.country || 'United States',
                                                                    state: '',
                                                                    city: '',
                                                                    postalCode: ''
                                                                });
                                                                setShowCreateCompanyModal(true);
                                                            }}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                padding: '0',
                                                                fontSize: '12px',
                                                                fontWeight: 600,
                                                                color: '#0284c7',
                                                                cursor: 'pointer',
                                                                textDecoration: 'none'
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.color = '#0369a1'}
                                                            onMouseLeave={e => e.currentTarget.style.color = '#0284c7'}
                                                            title="Add a new company via modal"
                                                        >
                                                            Add Company +
                                                        </button>
                                                    </div>
                                                    <CreatableSelect
                                                        value={contactFormData.company}
                                                        onChange={val => setContactFormData({ ...contactFormData, company: val })}
                                                        options={companyOptions}
                                                        placeholder="Select company..."
                                                        isSearchable={false}
                                                        isCreatable={false}
                                                    />
                                                </div>

                                                <div className="pipeline-form-group">
                                                    <label>Origin <span className="pipeline-required">*</span></label>
                                                    <CreatableSelect
                                                        value={contactFormData.origin || 'Domestic'}
                                                        onChange={val => setContactFormData({ ...contactFormData, origin: val })}
                                                        options={['Domestic', 'International']}
                                                        placeholder="Select origin..."
                                                        isSearchable={false}
                                                        isCreatable={false}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}

                                {/* Row 4: Contact Source & Lead Stage */}
                                <div className="pipeline-form-row-grid">
                                    <div className="pipeline-form-group">
                                        <label>Contact Source <span className="pipeline-required">*</span></label>
                                        <CreatableSelect
                                            value={contactFormData.contactSource}
                                            onChange={val => setContactFormData({ ...contactFormData, contactSource: val })}
                                            onCreateOption={newOpt => setContactSources(prev => Array.from(new Set([...prev, newOpt])))}
                                            options={contactSources}
                                            placeholder="Select or enter source..."
                                            isSearchable={true}
                                            isCreatable={true}
                                            required
                                        />
                                    </div>

                                    <div className="pipeline-form-group">
                                        <label>Lead Stage <span className="pipeline-required">*</span></label>
                                        <CreatableSelect
                                            value={contactFormData.leadStage}
                                            onChange={val => handleLeadStageChangeOrAdd(val)}
                                            onCreateOption={newOpt => handleLeadStageChangeOrAdd(newOpt)}
                                            options={contactStageOptions}
                                            placeholder="Select or enter lead stage..."
                                            isSearchable={true}
                                            isCreatable={true}
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Row 5: Lead Status */}
                                <div className="pipeline-form-row-grid">
                                    <div className="pipeline-form-group">
                                        <label>Lead Status <span className="pipeline-required">*</span></label>
                                        <CreatableSelect
                                            value={contactFormData.leadStatus}
                                            onChange={val => setContactFormData({ ...contactFormData, leadStatus: val })}
                                            onCreateOption={newOpt => setLeadStatuses(prev => Array.from(new Set([...prev, newOpt])))}
                                            options={leadStatuses}
                                            placeholder="Select or enter status..."
                                            isSearchable={true}
                                            isCreatable={true}
                                            placement="top"
                                            required
                                        />
                                    </div>
                                    <div className="pipeline-form-group" style={{ visibility: 'hidden' }}></div>
                                </div>
                            </div>

                            <div className="pipeline-modal-footer">
                                <button
                                    type="button"
                                    className="pipeline-btn-cancel"
                                    onClick={() => { setShowCreateContactModal(false); setEditingContact(null); }}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="pipeline-btn-save">
                                    {editingContact ? 'Save Changes' : 'Create Contact'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div >
            )
            }

            {/* ------------------------------------------------------------------
          7. View Contact Details Modal
          ------------------------------------------------------------------ */}
            {
                viewingContact && (
                    <div className="pipeline-modal-backdrop" onClick={() => setViewingContact(null)}>
                        <div className="pipeline-modal-card pipeline-view-deal-modal" onClick={e => e.stopPropagation()}>
                            <div className="pipeline-modal-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                    <div className="pipeline-card-avatar" style={{ width: '46px', height: '46px', fontSize: '18px', borderRadius: '12px' }}>
                                        {viewingContact.firstName?.charAt(0)}{viewingContact.lastName?.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '19px', margin: 0, fontWeight: 700 }}>
                                            {viewingContact.firstName} {viewingContact.lastName}
                                        </h3>
                                        <p className="pipeline-modal-subtitle" style={{ margin: '2px 0 0 0' }}>
                                            {(viewingContact.company && viewingContact.company !== 'Direct Client') ? `${viewingContact.company} • ` : ''}{viewingContact.leadStage}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="pipeline-modal-close-btn"
                                    onClick={() => setViewingContact(null)}
                                    title="Close"
                                >
                                    <X size={19} />
                                </button>
                            </div>

                            <div className="pipeline-modal-body">
                                <div className="pipeline-view-details-grid">
                                    <div className="pipeline-view-detail-card">
                                        <span className="pipeline-view-detail-label">Email Address</span>
                                        <a href={`mailto:${viewingContact.email}`} className="pipeline-view-detail-value" style={{ color: '#0284c7', textDecoration: 'none', fontWeight: 600 }}>
                                            {viewingContact.email}
                                        </a>
                                    </div>

                                    <div className="pipeline-view-detail-card">
                                        <span className="pipeline-view-detail-label">Phone Number</span>
                                        <span className="pipeline-view-detail-value font-semibold">
                                            {viewingContact.countryCode} {viewingContact.phone}
                                        </span>
                                    </div>

                                    <div className="pipeline-view-detail-card">
                                        <span className="pipeline-view-detail-label">Company</span>
                                        <span className="pipeline-view-detail-value">
                                            {(viewingContact.company && viewingContact.company !== 'Direct Client') ? viewingContact.company : '—'}
                                        </span>
                                    </div>

                                    <div className="pipeline-view-detail-card">
                                        <span className="pipeline-view-detail-label">Lead Stage</span>
                                        <div>
                                            <span className="pipeline-stage-badge new">
                                                {viewingContact.leadStage}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="pipeline-view-detail-card">
                                        <span className="pipeline-view-detail-label">Lead Status</span>
                                        <div>
                                            <span className="pipeline-stage-badge won">
                                                {viewingContact.leadStatus}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="pipeline-view-detail-card">
                                        <span className="pipeline-view-detail-label">Contact Source</span>
                                        <span className="pipeline-view-detail-value">{viewingContact.contactSource}</span>
                                    </div>

                                    <div className="pipeline-view-detail-card">
                                        <span className="pipeline-view-detail-label">Added By</span>
                                        <span className="pipeline-view-detail-value">{viewingContact.addedBy || viewingContact.owner || '—'}</span>
                                    </div>

                                    <div className="pipeline-view-detail-card">
                                        <span className="pipeline-view-detail-label">Created On</span>
                                        <span className="pipeline-view-detail-value">
                                            {formatISTDateTime(viewingContact.createdAt || viewingContact.createdDate || viewingContact.date)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="pipeline-modal-footer">
                                <button
                                    type="button"
                                    className="pipeline-btn-cancel"
                                    onClick={() => setViewingContact(null)}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="pipeline-btn-save"
                                    onClick={(e) => {
                                        const c = viewingContact;
                                        setViewingContact(null);
                                        handleOpenEditContact(c, e);
                                    }}
                                >
                                    Edit Contact
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* ------------------------------------------------------------------
          8. Create & Edit Company Modal (Wide Professional 2-Column Layout)
          ------------------------------------------------------------------ */}
            {
                showCreateCompanyModal && (
                    <div className="pipeline-modal-backdrop" style={{ zIndex: 10050 }} onClick={() => { setShowCreateCompanyModal(false); setEditingCompany(null); }}>
                        <div className="pipeline-modal-card pipeline-modal-wide" onClick={e => e.stopPropagation()}>
                            <div className="pipeline-modal-header">
                                <div>
                                    <h3 className="pipeline-modal-title">{editingCompany ? 'Edit Company' : 'Create Company'}</h3>
                                    <p className="pipeline-modal-subtitle">
                                        {editingCompany ? 'Update corporate account and organization details' : 'Register a new enterprise client, account, or parent organization'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="pipeline-modal-close-btn"
                                    onClick={() => { setShowCreateCompanyModal(false); setEditingCompany(null); }}
                                    title="Close"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={handleCompanySubmit}>
                                <div className="pipeline-modal-body">
                                    {/* Row 1: Company Name & Company Domain */}
                                    <div className="pipeline-form-row-grid">
                                        <div className="pipeline-form-group">
                                            <label>Company Name <span className="pipeline-required">*</span></label>
                                            <input
                                                type="text"
                                                required
                                                placeholder="Enter company name"
                                                className="pipeline-form-input"
                                                value={companyFormData.name}
                                                onChange={e => setCompanyFormData({ ...companyFormData, name: e.target.value })}
                                            />
                                        </div>

                                        <div className="pipeline-form-group">
                                            <label>Company Domain</label>
                                            <div className="pipeline-input-with-icon">
                                                <input
                                                    type="text"
                                                    placeholder="Enter domain name"
                                                    className="pipeline-form-input"
                                                    style={
                                                        companyFormData.domain && companyFormData.domain.trim()
                                                            ? isValidCompanyDomain(companyFormData.domain)
                                                                ? { borderColor: '#10b981', boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.15)' }
                                                                : { borderColor: '#ef4444', boxShadow: '0 0 0 2px rgba(239, 68, 68, 0.15)' }
                                                            : {}
                                                    }
                                                    value={companyFormData.domain}
                                                    onChange={e => setCompanyFormData({ ...companyFormData, domain: e.target.value })}
                                                />
                                                <Globe
                                                    size={16}
                                                    className="pipeline-field-icon"
                                                    style={{
                                                        color: companyFormData.domain && companyFormData.domain.trim()
                                                            ? isValidCompanyDomain(companyFormData.domain)
                                                                ? '#10b981'
                                                                : '#ef4444'
                                                            : undefined
                                                    }}
                                                />
                                            </div>
                                            {companyFormData.domain && companyFormData.domain.trim() && (
                                                isValidCompanyDomain(companyFormData.domain) ? (
                                                    showValidDomainMsg && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontSize: '11px', color: '#10b981', fontWeight: 500, animation: 'pipelineModalFade 0.2s ease-out' }}>
                                                            <Check size={12} /> Valid domain
                                                        </div>
                                                    )
                                                ) : (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontSize: '11px', color: '#ef4444', fontWeight: 500 }}>
                                                        <AlertCircle size={12} /> Please enter a valid domain.
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 2: Company Owner & Industry Type */}
                                    <div className="pipeline-form-row-grid">
                                        <div className="pipeline-form-group">
                                            <label>Company Owner <span className="pipeline-required">*</span></label>
                                            <MultiSelectContactEmails
                                                values={companyFormData.owner}
                                                onChange={vals => setCompanyFormData({ ...companyFormData, owner: vals })}
                                                options={contactEmailOptions}
                                                placeholder="Select contact emails..."
                                                isCreatable={true}
                                            />
                                        </div>

                                        <div className="pipeline-form-group">
                                            <label>Industry Type <span className="pipeline-required">*</span></label>
                                            <CreatableSelect
                                                value={companyFormData.industry}
                                                onChange={val => setCompanyFormData({ ...companyFormData, industry: val })}
                                                options={industryOptions}
                                                placeholder="Select industry..."
                                                isSearchable={true}
                                                isCreatable={true}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Row 3: Country & State/Region (Cascading Dropdowns) */}
                                    <div className="pipeline-form-row-grid">
                                        <div className="pipeline-form-group">
                                            <label>Country <span className="pipeline-required">*</span></label>
                                            <CreatableSelect
                                                value={companyFormData.country}
                                                onChange={val => {
                                                    const newStates = COUNTRY_STATE_MAP[val] || [];
                                                    const keepState = newStates.includes(companyFormData.state) ? companyFormData.state : '';
                                                    setCompanyFormData({
                                                        ...companyFormData,
                                                        country: val,
                                                        state: keepState
                                                    });
                                                }}
                                                options={countryListOptions}
                                                placeholder="Select country..."
                                                isSearchable={true}
                                                isCreatable={true}
                                                required
                                            />
                                        </div>

                                        <div className="pipeline-form-group">
                                            <label>State / Region</label>
                                            <CreatableSelect
                                                value={companyFormData.state}
                                                onChange={val => setCompanyFormData({ ...companyFormData, state: val })}
                                                options={availableCompanyStates}
                                                placeholder={companyFormData.country ? "Select state / region..." : "Select country first"}
                                                isSearchable={true}
                                                isCreatable={true}
                                            />
                                        </div>
                                    </div>

                                    {/* Row 4: City & Postal Code */}
                                    <div className="pipeline-form-row-grid">
                                        <div className="pipeline-form-group">
                                            <label>City</label>
                                            <input
                                                type="text"
                                                placeholder="Enter city"
                                                className="pipeline-form-input"
                                                value={companyFormData.city}
                                                onChange={e => setCompanyFormData({ ...companyFormData, city: e.target.value })}
                                            />
                                        </div>

                                        <div className="pipeline-form-group">
                                            <label>Postal Code</label>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                placeholder="Enter postal code"
                                                className="pipeline-form-input"
                                                value={companyFormData.postalCode}
                                                onChange={e => {
                                                    const numericVal = e.target.value.replace(/\D/g, '');
                                                    setCompanyFormData({ ...companyFormData, postalCode: numericVal });
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="pipeline-modal-footer">
                                    <button
                                        type="button"
                                        className="pipeline-btn-cancel"
                                        onClick={() => { setShowCreateCompanyModal(false); setEditingCompany(null); }}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="pipeline-btn-save">
                                        {editingCompany ? 'Save Changes' : 'Create Company'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* ------------------------------------------------------------------
          9. View Company Details Modal
          ------------------------------------------------------------------ */}
            {
                viewingCompany && (
                    <div className="pipeline-modal-backdrop" onClick={() => setViewingCompany(null)}>
                        <div
                            className="pipeline-modal-card"
                            style={{
                                maxWidth: '820px',
                                width: '100%',
                                borderRadius: '16px',
                                overflow: 'hidden',
                                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: '90vh'
                            }}
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div
                                className="pipeline-modal-header"
                                style={{
                                    padding: '20px 24px',
                                    borderBottom: '1px solid #f1f5f9',
                                    background: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <div
                                        style={{
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '12px',
                                            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                                            color: '#ffffff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '20px',
                                            fontWeight: '700',
                                            boxShadow: '0 4px 10px rgba(2, 132, 199, 0.25)',
                                            flexShrink: 0
                                        }}
                                    >
                                        {viewingCompany.name ? viewingCompany.name.charAt(0).toUpperCase() : 'C'}
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '18px', margin: 0, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>
                                            {viewingCompany.name}
                                        </h3>
                                        {viewingCompany.domain && (
                                            <a
                                                href={`https://${viewingCompany.domain.replace(/^https?:\/\//, '')}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '5px',
                                                    color: '#0284c7',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    textDecoration: 'none',
                                                    marginTop: '4px',
                                                    padding: '2px 8px',
                                                    background: '#f0f9ff',
                                                    borderRadius: '6px',
                                                    border: '1px solid #e0f2fe',
                                                    transition: 'all 0.15s ease'
                                                }}
                                            >
                                                <span>{viewingCompany.domain}</span>
                                                <ExternalLink size={11} />
                                            </a>
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="pipeline-modal-close-btn"
                                    onClick={() => setViewingCompany(null)}
                                    title="Close"
                                    style={{
                                        background: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '50%',
                                        width: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        color: '#64748b'
                                    }}
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Body */}
                            <div
                                className="pipeline-modal-body"
                                style={{
                                    padding: '24px',
                                    overflowY: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '20px',
                                    background: '#ffffff'
                                }}
                            >
                                {/* 3-Column Minimal Grid for Company Attributes */}
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                        gap: '12px'
                                    }}
                                >
                                    <div
                                        style={{
                                            background: '#f8fafc',
                                            border: '1px solid #f1f5f9',
                                            borderRadius: '10px',
                                            padding: '12px 14px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '4px'
                                        }}
                                    >
                                        <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b' }}>
                                            Industry
                                        </span>
                                        <div>
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    background: '#e0f2fe',
                                                    color: '#0369a1',
                                                    padding: '2px 8px',
                                                    borderRadius: '5px',
                                                    fontSize: '12px',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {viewingCompany.industry || 'General'}
                                            </span>
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            background: '#f8fafc',
                                            border: '1px solid #f1f5f9',
                                            borderRadius: '10px',
                                            padding: '12px 14px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '4px'
                                        }}
                                    >
                                        <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b' }}>
                                            Company Owner
                                        </span>
                                        <div style={{ marginTop: '2px' }}>
                                            <CompanyOwnerDisplay owner={viewingCompany.owner} fallback="Artiflow" />
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            background: '#f8fafc',
                                            border: '1px solid #f1f5f9',
                                            borderRadius: '10px',
                                            padding: '12px 14px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '4px'
                                        }}
                                    >
                                        <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b' }}>
                                            Country
                                        </span>
                                        <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#0f172a' }}>
                                            {viewingCompany.country || 'United States'}
                                        </span>
                                    </div>

                                    <div
                                        style={{
                                            background: '#f8fafc',
                                            border: '1px solid #f1f5f9',
                                            borderRadius: '10px',
                                            padding: '12px 14px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '4px'
                                        }}
                                    >
                                        <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b' }}>
                                            State / Region
                                        </span>
                                        <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#0f172a' }}>
                                            {viewingCompany.state || '—'}
                                        </span>
                                    </div>

                                    <div
                                        style={{
                                            background: '#f8fafc',
                                            border: '1px solid #f1f5f9',
                                            borderRadius: '10px',
                                            padding: '12px 14px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '4px'
                                        }}
                                    >
                                        <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b' }}>
                                            City
                                        </span>
                                        <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#0f172a' }}>
                                            {viewingCompany.city || '—'}
                                        </span>
                                    </div>

                                    <div
                                        style={{
                                            background: '#f8fafc',
                                            border: '1px solid #f1f5f9',
                                            borderRadius: '10px',
                                            padding: '12px 14px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '4px'
                                        }}
                                    >
                                        <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b' }}>
                                            Postal Code
                                        </span>
                                        <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#0f172a' }}>
                                            {viewingCompany.postalCode || '—'}
                                        </span>
                                    </div>

                                    <div
                                        style={{
                                            background: '#f8fafc',
                                            border: '1px solid #f1f5f9',
                                            borderRadius: '10px',
                                            padding: '12px 14px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '4px',
                                            gridColumn: 'span 3'
                                        }}
                                    >
                                        <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b' }}>
                                            Created Date
                                        </span>
                                        <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#0f172a' }}>
                                            {viewingCompany.createdDate || '—'}
                                        </span>
                                    </div>
                                </div>

                                {/* Associated Contacts & Deals Section for Company */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    {(() => {
                                        const targetCompName = (viewingCompany.name || '').trim().toLowerCase();
                                        const compContacts = targetCompName
                                            ? contacts.filter(c => {
                                                const cComp = (c.company || '').trim().toLowerCase();
                                                return cComp && cComp === targetCompName;
                                            })
                                            : [];

                                        const compDeals = targetCompName
                                            ? deals.filter(d => {
                                                const assocComp = (d.associatedCompany || '').trim().toLowerCase();
                                                const comp = (d.company || '').trim().toLowerCase();
                                                return (assocComp && assocComp === targetCompName) ||
                                                    (comp && comp === targetCompName);
                                            })
                                            : [];

                                        return (
                                            <>
                                                {/* Associated Contacts Box */}
                                                <div
                                                    style={{
                                                        background: '#fbfcfd',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '12px',
                                                        padding: '16px'
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            marginBottom: '12px'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <div
                                                                style={{
                                                                    width: '26px',
                                                                    height: '26px',
                                                                    borderRadius: '6px',
                                                                    background: '#f0f9ff',
                                                                    color: '#0284c7',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                <Users size={14} />
                                                            </div>
                                                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>
                                                                Associated Contacts
                                                            </span>
                                                            <span
                                                                style={{
                                                                    fontSize: '11px',
                                                                    fontWeight: 600,
                                                                    background: '#e2e8f0',
                                                                    color: '#475569',
                                                                    padding: '1px 6px',
                                                                    borderRadius: '10px'
                                                                }}
                                                            >
                                                                {compContacts.length}
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const targetCompany = viewingCompany.name;
                                                                setViewingCompany(null);
                                                                setEditingContact(null);
                                                                setSelectedCountry(COUNTRY_CODES[0]);
                                                                setContactFormData({
                                                                    firstName: '',
                                                                    lastName: '',
                                                                    email: '',
                                                                    countryCode: '+1',
                                                                    phone: '',
                                                                    contactSource: 'Direct Reference',
                                                                    leadStage: effectiveContactStages.length > 0 ? (typeof effectiveContactStages[0] === 'string' ? effectiveContactStages[0] : effectiveContactStages[0].name) : 'Lead',
                                                                    leadStatus: 'New',
                                                                    owner: viewingCompany.owner || 'Artiflow',
                                                                    company: targetCompany
                                                                });
                                                                setShowCreateContactModal(true);
                                                            }}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                fontSize: '11.5px',
                                                                fontWeight: 600,
                                                                color: '#0284c7',
                                                                background: '#ffffff',
                                                                border: '1px solid #bae6fd',
                                                                borderRadius: '6px',
                                                                padding: '4px 9px',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.15s ease'
                                                            }}
                                                            title="Add contact for this company"
                                                        >
                                                            <Plus size={12} />
                                                            <span>Add Contact</span>
                                                        </button>
                                                    </div>

                                                    {compContacts.length > 0 ? (
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px' }}>
                                                            {compContacts.map(c => (
                                                                <div
                                                                    key={c._id}
                                                                    onClick={() => {
                                                                        setViewingCompany(null);
                                                                        setDetailedDealId(null);
                                                                        setActiveTab('contacts');
                                                                        setDetailedContactId(c._id);
                                                                    }}
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        gap: '8px',
                                                                        background: '#ffffff',
                                                                        border: '1px solid #e2e8f0',
                                                                        borderRadius: '8px',
                                                                        padding: '8px 10px',
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.15s ease'
                                                                    }}
                                                                    onMouseEnter={e => {
                                                                        e.currentTarget.style.borderColor = '#0284c7';
                                                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(2, 132, 199, 0.08)';
                                                                    }}
                                                                    onMouseLeave={e => {
                                                                        e.currentTarget.style.borderColor = '#e2e8f0';
                                                                        e.currentTarget.style.boxShadow = 'none';
                                                                    }}
                                                                    title="Click to view contact profile"
                                                                >
                                                                    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                                                        <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '12.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            {c.firstName} {c.lastName}
                                                                        </span>
                                                                        {(c.email || c.phone) && (
                                                                            <span style={{ color: '#64748b', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                {c.email || c.phone}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <ChevronRight size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                                                            No contacts linked to this company yet.
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Associated Deals Box */}
                                                <div
                                                    style={{
                                                        background: '#fbfcfd',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '12px',
                                                        padding: '16px'
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            marginBottom: '12px'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <div
                                                                style={{
                                                                    width: '26px',
                                                                    height: '26px',
                                                                    borderRadius: '6px',
                                                                    background: '#f0fdf4',
                                                                    color: '#16a34a',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                <Briefcase size={14} />
                                                            </div>
                                                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>
                                                                Associated Deals
                                                            </span>
                                                            <span
                                                                style={{
                                                                    fontSize: '11px',
                                                                    fontWeight: 600,
                                                                    background: '#e2e8f0',
                                                                    color: '#475569',
                                                                    padding: '1px 6px',
                                                                    borderRadius: '10px'
                                                                }}
                                                            >
                                                                {compDeals.length}
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const targetCompany = viewingCompany.name;
                                                                setViewingCompany(null);
                                                                setEditingDeal(null);
                                                                setModalStage('New Lead');
                                                                setFormData({
                                                                    clientName: targetCompany,
                                                                    pipeline: pipelines[0] ? pipelines[0].name : 'New Lead',
                                                                    stage: 'New Lead',
                                                                    dealType: 'New Business',
                                                                    currency: '$',
                                                                    expectedCloseDate: getFutureDateDDMMYYYY(14),
                                                                    dealValue: '',
                                                                    salesOwner: viewingCompany.owner || 'Artiflow',
                                                                    location: viewingCompany.city || '',
                                                                    dealName: `${targetCompany} Deal`,
                                                                    email: '',
                                                                    phone: '',
                                                                    associatedContact: '',
                                                                    associatedCompany: targetCompany
                                                                });
                                                                setShowCreateModal(true);
                                                            }}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                fontSize: '11.5px',
                                                                fontWeight: 600,
                                                                color: '#0284c7',
                                                                background: '#ffffff',
                                                                border: '1px solid #bae6fd',
                                                                borderRadius: '6px',
                                                                padding: '4px 9px',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.15s ease'
                                                            }}
                                                            title="Add deal for this company"
                                                        >
                                                            <Plus size={12} />
                                                            <span>Add Deal</span>
                                                        </button>
                                                    </div>

                                                    {compDeals.length > 0 ? (
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '8px' }}>
                                                            {compDeals.map(d => (
                                                                <div
                                                                    key={d._id}
                                                                    onClick={() => {
                                                                        setViewingCompany(null);
                                                                        setDetailedContactId(null);
                                                                        setActiveTab('deals');
                                                                        setDetailedDealId(d._id);
                                                                    }}
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        gap: '8px',
                                                                        background: '#ffffff',
                                                                        border: '1px solid #e2e8f0',
                                                                        borderRadius: '8px',
                                                                        padding: '8px 10px',
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.15s ease'
                                                                    }}
                                                                    onMouseEnter={e => {
                                                                        e.currentTarget.style.borderColor = '#0284c7';
                                                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(2, 132, 199, 0.08)';
                                                                    }}
                                                                    onMouseLeave={e => {
                                                                        e.currentTarget.style.borderColor = '#e2e8f0';
                                                                        e.currentTarget.style.boxShadow = 'none';
                                                                    }}
                                                                    title="Click to view deal board"
                                                                >
                                                                    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                                                        <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '12.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                            {d.dealName || d.clientName}
                                                                        </span>
                                                                        <span style={{ color: '#16a34a', fontWeight: 700, fontSize: '11.5px' }}>
                                                                            {d.currency || '$'}{Number(d.dealValue || 0).toLocaleString()}
                                                                        </span>
                                                                    </div>
                                                                    <span className={`pipeline-stage-badge ${(d.stage || 'new').toLowerCase()}`} style={{ padding: '2px 6px', fontSize: '10px', flexShrink: 0 }}>
                                                                        {d.stage}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                                                            No deals linked to this company yet.
                                                        </p>
                                                    )}
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Footer */}
                            <div
                                className="pipeline-modal-footer"
                                style={{
                                    padding: '16px 24px',
                                    borderTop: '1px solid #f1f5f9',
                                    background: '#f8fafc',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '10px'
                                }}
                            >
                                <button
                                    type="button"
                                    className="pipeline-btn-cancel"
                                    onClick={() => setViewingCompany(null)}
                                    style={{
                                        padding: '8px 18px',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: '13px'
                                    }}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="pipeline-btn-save"
                                    onClick={(e) => {
                                        const c = viewingCompany;
                                        setViewingCompany(null);
                                        handleOpenEditCompany(c, e);
                                    }}
                                    style={{
                                        padding: '8px 18px',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: '13px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <Edit2 size={13} />
                                    <span>Edit Company</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* ------------------------------------------------------------------
        Add Pipeline Modal
        ------------------------------------------------------------------ */}
            {
                showAddPipelineModal && (
                    <div className="pipeline-modal-backdrop" onClick={() => setShowAddPipelineModal(false)}>
                        <div className="pipeline-modal-card" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
                            <div className="pipeline-modal-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div className="pipeline-modal-icon-badge" style={{ background: '#f0f9ff', color: '#0284c7' }}>
                                        <Plus size={18} />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>Add New Pipeline</h3>
                                        <p className="pipeline-modal-subtitle">Create a dedicated pipeline board for your deals</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="pipeline-modal-close-btn"
                                    onClick={() => setShowAddPipelineModal(false)}
                                    title="Close"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={(e) => { e.preventDefault(); handleAddPipeline(addPipelineModalName); }}>
                                <div className="pipeline-modal-body" style={{ padding: '20px 24px' }}>
                                    <div className="pipeline-form-group">
                                        <label className="pipeline-form-label">
                                            Pipeline Name <span className="required-star">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="pipeline-form-input"
                                            placeholder="e.g. Enterprise Sales, Partnership Pipeline"
                                            value={addPipelineModalName}
                                            onChange={e => setAddPipelineModalName(e.target.value)}
                                            autoFocus
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="pipeline-modal-footer">
                                    <button
                                        type="button"
                                        className="pipeline-btn-cancel"
                                        onClick={() => {
                                            setShowAddPipelineModal(false);
                                            setAddPipelineModalName('');
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="pipeline-btn-save"
                                        disabled={!addPipelineModalName.trim()}
                                    >
                                        Create Pipeline
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* ------------------------------------------------------------------
        Add Contact Stage Modal
        ------------------------------------------------------------------ */}
            {/* ------------------------------------------------------------------
         Reorder Pipeline Stages Modal
        ------------------------------------------------------------------ */}
            {
                showReorderStagesModal && (
                    <div className="pipeline-modal-backdrop" onClick={() => setShowReorderStagesModal(false)}>
                        <div className="pipeline-modal-card" style={{ maxWidth: '520px', width: '90%', borderRadius: '14px', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                            <div className="pipeline-modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#ffffff' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div className="pipeline-modal-icon-badge" style={{ background: '#e0f2fe', color: '#0284c7', width: '34px', height: '34px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <ArrowUpDown size={17} />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                                            Reorder Pipeline Stages
                                        </h3>
                                        <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                                            Move stages up or down to set the exact order for all sales executives.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="pipeline-modal-close-btn"
                                    onClick={() => setShowReorderStagesModal(false)}
                                    title="Close"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="pipeline-modal-body" style={{ padding: '16px 20px', maxHeight: '420px', overflowY: 'auto' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {reorderStagesList.map((stage, idx) => {
                                        const count = deals.filter(d => (d.pipeline || d.stage || '').toLowerCase().trim() === (stage.name || '').toLowerCase().trim()).length;
                                        return (
                                            <div
                                                key={stage.id || stage.name || idx}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: '10px 14px',
                                                    background: '#f8fafc',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '8px',
                                                    transition: 'all 0.15s ease'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', background: '#e2e8f0', width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        {idx + 1}
                                                    </span>
                                                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                                        {stage.name}
                                                    </span>
                                                    <span className={`pipeline-stage-badge ${stage.badgeClass || 'new'}`} style={{ fontSize: '10px', padding: '1px 6px' }}>
                                                        {count} deals
                                                    </span>
                                                </div>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <button
                                                        type="button"
                                                        disabled={idx === 0}
                                                        onClick={() => {
                                                            if (idx === 0) return;
                                                            const copy = [...reorderStagesList];
                                                            const [moved] = copy.splice(idx, 1);
                                                            copy.splice(idx - 1, 0, moved);
                                                            setReorderStagesList(copy);
                                                        }}
                                                        style={{
                                                            padding: '5px 8px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #cbd5e1',
                                                            background: idx === 0 ? '#f1f5f9' : '#fff',
                                                            color: idx === 0 ? '#cbd5e1' : '#0284c7',
                                                            cursor: idx === 0 ? 'not-allowed' : 'pointer'
                                                        }}
                                                        title="Move Up"
                                                    >
                                                        <ArrowUp size={13} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={idx === reorderStagesList.length - 1}
                                                        onClick={() => {
                                                            if (idx === reorderStagesList.length - 1) return;
                                                            const copy = [...reorderStagesList];
                                                            const [moved] = copy.splice(idx, 1);
                                                            copy.splice(idx + 1, 0, moved);
                                                            setReorderStagesList(copy);
                                                        }}
                                                        style={{
                                                            padding: '5px 8px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #cbd5e1',
                                                            background: idx === reorderStagesList.length - 1 ? '#f1f5f9' : '#fff',
                                                            color: idx === reorderStagesList.length - 1 ? '#cbd5e1' : '#0284c7',
                                                            cursor: idx === reorderStagesList.length - 1 ? 'not-allowed' : 'pointer'
                                                        }}
                                                        title="Move Down"
                                                    >
                                                        <ArrowDown size={13} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="pipeline-modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                                <button
                                    type="button"
                                    className="pipeline-btn-cancel"
                                    onClick={() => setShowReorderStagesModal(false)}
                                    style={{ padding: '7px 16px', fontSize: '13px' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="pipeline-btn-primary"
                                    disabled={savingStageOrder}
                                    onClick={async () => {
                                        await handleReorderStages(reorderStagesList);
                                        setShowReorderStagesModal(false);
                                    }}
                                    style={{ padding: '7px 18px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                >
                                    <CheckCircle size={14} />
                                    <span>{savingStageOrder ? 'Saving Order...' : 'Save Stage Order'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                showAddStageModal && (
                    <div className="pipeline-modal-backdrop" onClick={() => setShowAddStageModal(false)}>
                        <div className="pipeline-modal-card" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
                            <div className="pipeline-modal-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div className="pipeline-modal-icon-badge" style={{ background: '#f0f9ff', color: '#0284c7' }}>
                                        <Plus size={18} />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>
                                            {activeTab === 'contacts' ? 'Add Contact Stage' : 'Add Deal Stage'}
                                        </h3>
                                        <p className="pipeline-modal-subtitle">
                                            {activeTab === 'contacts' ? 'Add a new stage column to the contacts board' : 'Add a new stage option to the deal pipeline'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="pipeline-modal-close-btn"
                                    onClick={() => setShowAddStageModal(false)}
                                    title="Close"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={async (e) => {
                                e.preventDefault();
                                if (activeTab === 'contacts') {
                                    await handleAddContactStage(addStageModalName);
                                } else {
                                    await handleAddPipeline(addStageModalName);
                                    await handleCreateStageOption(addStageModalName);
                                    setShowAddStageModal(false);
                                    setAddStageModalName('');
                                }
                            }}>
                                <div className="pipeline-modal-body" style={{ padding: '20px 24px' }}>
                                    <div className="pipeline-form-group">
                                        <label className="pipeline-form-label">
                                            Stage Name <span className="required-star">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="pipeline-form-input"
                                            placeholder="e.g. Inbound Lead, Proposal Sent, Customer"
                                            value={addStageModalName}
                                            onChange={e => setAddStageModalName(e.target.value)}
                                            autoFocus
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="pipeline-modal-footer">
                                    <button
                                        type="button"
                                        className="pipeline-btn-cancel"
                                        onClick={() => {
                                            setShowAddStageModal(false);
                                            setAddStageModalName('');
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="pipeline-btn-save"
                                        disabled={!addStageModalName.trim()}
                                    >
                                        Create Stage
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* ------------------------------------------------------------------
        Edit Stage Modal
        ------------------------------------------------------------------ */}
            {
                editingStageModal && (
                    <div className="pipeline-modal-backdrop" onClick={() => setEditingStageModal(null)}>
                        <div className="pipeline-modal-card" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
                            <div className="pipeline-modal-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div className="pipeline-modal-icon-badge" style={{ background: '#f0f9ff', color: '#0284c7' }}>
                                        <Edit2 size={18} />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>
                                            {editingStageModal.type === 'contactStage' ? 'Edit Contact Stage' : 'Edit Deal Stage'}
                                        </h3>
                                        <p className="pipeline-modal-subtitle">
                                            Update the name for this stage column
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="pipeline-modal-close-btn"
                                    onClick={() => setEditingStageModal(null)}
                                    title="Close"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={handleSaveEditStageModal}>
                                <div className="pipeline-modal-body" style={{ padding: '20px 24px' }}>
                                    <div className="pipeline-form-group">
                                        <label className="pipeline-form-label">
                                            Stage Name <span className="required-star">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="pipeline-form-input"
                                            placeholder="Enter stage name"
                                            value={editingStageModal.name || ''}
                                            onChange={e => setEditingStageModal(prev => ({ ...prev, name: e.target.value }))}
                                            autoFocus
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="pipeline-modal-footer">
                                    <button
                                        type="button"
                                        className="pipeline-btn-cancel"
                                        onClick={() => setEditingStageModal(null)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="pipeline-btn-save"
                                        disabled={!editingStageModal.name?.trim()}
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* ------------------------------------------------------------------
        Create / Edit Task Modal
        ------------------------------------------------------------------ */}
            {
                showCreateTaskModal && (
                    <div className="pipeline-modal-backdrop" onClick={() => { setShowCreateTaskModal(false); setEditingTask(null); }}>
                        <div className="pipeline-modal-card" style={{ maxWidth: '720px', width: '92%' }} onClick={e => e.stopPropagation()}>
                            <div className="pipeline-modal-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div className="pipeline-modal-icon-badge" style={{ background: '#eef2ff', color: '#6366f1' }}>
                                        <CheckSquare size={18} />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>
                                            {editingTask ? 'Edit Task' : 'Create New Task'}
                                        </h3>
                                        <p className="pipeline-modal-subtitle">Schedule follow-ups, calls, meetings, or deal milestones</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="pipeline-modal-close-btn"
                                    onClick={() => { setShowCreateTaskModal(false); setEditingTask(null); }}
                                    title="Close"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={handleTaskSubmit}>
                                <div className="pipeline-modal-body" style={{ padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    <div className="pipeline-form-group">
                                        <label className="pipeline-form-label">
                                            Task Title <span className="required-star">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="pipeline-form-input"
                                            placeholder="Enter task title"
                                            value={taskFormData.title}
                                            onChange={e => setTaskFormData(prev => ({ ...prev, title: e.target.value }))}
                                            autoFocus
                                            required
                                        />
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                                        <div className="pipeline-form-group">
                                            <label className="pipeline-form-label">Task Type</label>
                                            <CreatableSelect
                                                value={taskFormData.type}
                                                onChange={val => setTaskFormData(prev => ({ ...prev, type: val }))}
                                                options={TASK_TYPE_OPTIONS}
                                                placeholder="Select task type..."
                                                isSearchable={false}
                                                isCreatable={false}
                                            />
                                        </div>

                                        <div className="pipeline-form-group">
                                            <label className="pipeline-form-label">Due Date</label>
                                            <input
                                                type="date"
                                                className="pipeline-form-input"
                                                value={taskFormData.dueDate}
                                                onChange={e => setTaskFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                                        <div className="pipeline-form-group">
                                            <label className="pipeline-form-label">Priority</label>
                                            <CreatableSelect
                                                value={taskFormData.priority ? `${taskFormData.priority} Priority` : 'Medium Priority'}
                                                onChange={val => {
                                                    const clean = val.replace(/\s*Priority\s*/i, '').trim();
                                                    setTaskFormData(prev => ({ ...prev, priority: clean || val }));
                                                }}
                                                options={TASK_PRIORITY_OPTIONS.map(opt => `${opt} Priority`)}
                                                placeholder="Select priority..."
                                                isSearchable={false}
                                                isCreatable={false}
                                            />
                                        </div>

                                        <div className="pipeline-form-group">
                                            <label className="pipeline-form-label">Status</label>
                                            <CreatableSelect
                                                value={taskFormData.status}
                                                onChange={val => setTaskFormData(prev => ({ ...prev, status: val }))}
                                                options={TASK_STATUS_OPTIONS}
                                                placeholder="Select status..."
                                                isSearchable={false}
                                                isCreatable={false}
                                            />
                                        </div>
                                    </div>

                                    {/* Associated Deal and Contact Selection */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                                        {/* Associated Deal */}
                                        <div className="pipeline-form-group">
                                            <label className="pipeline-form-label">Associated Deal</label>
                                            {(() => {
                                                // Case 1: Deal context (Deal is locked)
                                                if (taskLockedContext.dealId) {
                                                    const lockedDeal = deals.find(d => d._id === taskLockedContext.dealId || d._id === taskFormData.associatedDealId) || detailedDeal;
                                                    const dealName = lockedDeal ? (lockedDeal.dealName || lockedDeal.clientName) : (taskFormData.associatedDealId || 'Selected Deal');
                                                    return (
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                padding: '8px 12px',
                                                                backgroundColor: '#f8fafc',
                                                                border: '1.5px solid #e2e8f0',
                                                                borderRadius: '8px',
                                                                minHeight: '38px',
                                                                boxSizing: 'border-box'
                                                            }}
                                                            title="Associated deal is pre-selected for this task"
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', minWidth: 0 }}>
                                                                <div style={{
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    borderRadius: '6px',
                                                                    background: '#e0f2fe',
                                                                    color: '#0284c7',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    flexShrink: 0
                                                                }}>
                                                                    <Briefcase size={13} strokeWidth={2.2} />
                                                                </div>
                                                                <span style={{
                                                                    fontSize: '13.5px',
                                                                    fontWeight: 600,
                                                                    color: '#1e293b',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>
                                                                    {dealName}
                                                                </span>
                                                            </div>
                                                            {lockedDeal && (
                                                                <span
                                                                    className={`pipeline-stage-badge ${(lockedDeal.stage || 'new').toLowerCase()}`}
                                                                    style={{ fontSize: '10.5px', padding: '1px 7px', flexShrink: 0, fontWeight: 600 }}
                                                                >
                                                                    {lockedDeal.stage || 'Deal'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                // Case 2: Contact context
                                                const targetContactId = taskFormData.associatedContactId || taskLockedContext.contactId;
                                                if (targetContactId) {
                                                    const contactAssocDeals = getContactAssociatedDeals(targetContactId);
                                                    if (contactAssocDeals.length === 1) {
                                                        // Single deal: display as locked card without dropdown
                                                        const singleD = contactAssocDeals[0];
                                                        const dealName = singleD.dealName || singleD.clientName || 'Associated Deal';
                                                        return (
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    padding: '8px 12px',
                                                                    backgroundColor: '#f8fafc',
                                                                    border: '1.5px solid #e2e8f0',
                                                                    borderRadius: '8px',
                                                                    minHeight: '38px',
                                                                    boxSizing: 'border-box'
                                                                }}
                                                                title="Associated deal for this contact"
                                                            >
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', minWidth: 0 }}>
                                                                    <div style={{
                                                                        width: '24px',
                                                                        height: '24px',
                                                                        borderRadius: '6px',
                                                                        background: '#e0f2fe',
                                                                        color: '#0284c7',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        flexShrink: 0
                                                                    }}>
                                                                        <Briefcase size={13} strokeWidth={2.2} />
                                                                    </div>
                                                                    <span style={{
                                                                        fontSize: '13.5px',
                                                                        fontWeight: 600,
                                                                        color: '#1e293b',
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis'
                                                                    }}>
                                                                        {dealName}
                                                                    </span>
                                                                </div>
                                                                {singleD.stage && (
                                                                    <span
                                                                        className={`pipeline-stage-badge ${(singleD.stage || 'new').toLowerCase()}`}
                                                                        style={{ fontSize: '10.5px', padding: '1px 7px', flexShrink: 0, fontWeight: 600 }}
                                                                    >
                                                                        {singleD.stage}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    } else if (contactAssocDeals.length > 1) {
                                                        // Multiple deals: show multi-select dropdown strictly for this contact's deals
                                                        return (
                                                            <MultiSelectContactDeals
                                                                deals={contactAssocDeals}
                                                                selectedIds={taskFormData.associatedDealIds || []}
                                                                onChange={ids => setTaskFormData(prev => ({
                                                                    ...prev,
                                                                    associatedDealIds: ids,
                                                                    associatedDealId: ids[0] || ''
                                                                }))}
                                                                placeholder="Select associated deals..."
                                                            />
                                                        );
                                                    } else {
                                                        // 0 deals linked to contact
                                                        return (
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                padding: '8px 12px',
                                                                backgroundColor: '#f8fafc',
                                                                border: '1.5px dashed #cbd5e1',
                                                                borderRadius: '8px',
                                                                minHeight: '38px',
                                                                color: '#64748b',
                                                                fontSize: '13px'
                                                            }}>
                                                                <Briefcase size={14} style={{ color: '#94a3b8' }} />
                                                                <span>No deals associated with this contact</span>
                                                            </div>
                                                        );
                                                    }
                                                }

                                                // Case 3: Generic task creation
                                                return (
                                                    <CreatableSelect
                                                        value={deals.find(d => d._id === taskFormData.associatedDealId)?.dealName || deals.find(d => d._id === taskFormData.associatedDealId)?.clientName || ''}
                                                        onChange={val => {
                                                            const matched = deals.find(d => d._id === val || (d.dealName || d.clientName) === val);
                                                            setTaskFormData(prev => ({
                                                                ...prev,
                                                                associatedDealId: matched ? matched._id : val,
                                                                associatedDealIds: matched ? [matched._id] : (val ? [val] : []),
                                                                associatedContactId: prev.associatedContactId || (matched ? (matched.associatedContactId || matched.contactId || '') : '')
                                                            }));
                                                        }}
                                                        options={deals.map(d => ({ id: d._id, label: d.dealName || d.clientName }))}
                                                        placeholder="Select associated deal..."
                                                        isSearchable={true}
                                                        isCreatable={false}
                                                    />
                                                );
                                            })()}
                                        </div>

                                        {/* Associated Contact */}
                                        <div className="pipeline-form-group">
                                            <label className="pipeline-form-label">Associated Contact</label>
                                            {(() => {
                                                const targetDealId = taskFormData.associatedDealId || taskLockedContext.dealId;
                                                const dealAssocContacts = targetDealId ? getDealAssociatedContacts(targetDealId) : [];

                                                // Case 1: In Deal context
                                                if (targetDealId) {
                                                    if (dealAssocContacts.length === 1) {
                                                        // Single contact: display as locked card without dropdown
                                                        const singleC = dealAssocContacts[0];
                                                        const name = `${singleC.firstName || ''} ${singleC.lastName || ''}`.trim() || singleC.email || singleC.company || 'Associated Contact';
                                                        return (
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    padding: '8px 12px',
                                                                    backgroundColor: '#f8fafc',
                                                                    border: '1.5px solid #e2e8f0',
                                                                    borderRadius: '8px',
                                                                    minHeight: '38px',
                                                                    boxSizing: 'border-box'
                                                                }}
                                                                title="Associated contact for this deal"
                                                            >
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', minWidth: 0 }}>
                                                                    <div style={{
                                                                        width: '24px',
                                                                        height: '24px',
                                                                        borderRadius: '6px',
                                                                        background: '#f0fdf4',
                                                                        color: '#16a34a',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        flexShrink: 0
                                                                    }}>
                                                                        <User size={13} strokeWidth={2.2} />
                                                                    </div>
                                                                    <span style={{
                                                                        fontSize: '13.5px',
                                                                        fontWeight: 600,
                                                                        color: '#1e293b',
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis'
                                                                    }}>
                                                                        {name}
                                                                    </span>
                                                                </div>
                                                                {singleC.email && (
                                                                    <span style={{ fontSize: '11.5px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
                                                                        {singleC.email}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    } else if (dealAssocContacts.length > 1) {
                                                        // Multiple contacts: show multi-select dropdown strictly for this deal's contacts
                                                        return (
                                                            <MultiSelectDealContacts
                                                                contacts={dealAssocContacts}
                                                                selectedIds={taskFormData.associatedContactIds || []}
                                                                onChange={ids => setTaskFormData(prev => ({
                                                                    ...prev,
                                                                    associatedContactIds: ids,
                                                                    associatedContactId: ids[0] || ''
                                                                }))}
                                                                placeholder="Select associated contacts..."
                                                            />
                                                        );
                                                    } else {
                                                        // 0 contacts linked to deal
                                                        return (
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                padding: '8px 12px',
                                                                backgroundColor: '#f8fafc',
                                                                border: '1.5px dashed #cbd5e1',
                                                                borderRadius: '8px',
                                                                minHeight: '38px',
                                                                color: '#64748b',
                                                                fontSize: '13px'
                                                            }}>
                                                                <User size={14} style={{ color: '#94a3b8' }} />
                                                                <span>No contacts associated with this deal</span>
                                                            </div>
                                                        );
                                                    }
                                                }

                                                // Case 2: In Contact context (without deal)
                                                if (taskLockedContext.contactId && !taskLockedContext.dealId) {
                                                    const lockedContact = contacts.find(c => c._id === taskLockedContext.contactId) || detailedContact;
                                                    const contactName = lockedContact ? `${lockedContact.firstName || ''} ${lockedContact.lastName || ''}`.trim() || lockedContact.company : 'Selected Contact';
                                                    return (
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                padding: '8px 12px',
                                                                backgroundColor: '#f8fafc',
                                                                border: '1.5px solid #e2e8f0',
                                                                borderRadius: '8px',
                                                                minHeight: '38px',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', minWidth: 0 }}>
                                                                <div style={{
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    borderRadius: '6px',
                                                                    background: '#f0fdf4',
                                                                    color: '#16a34a',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    flexShrink: 0
                                                                }}>
                                                                    <User size={13} strokeWidth={2.2} />
                                                                </div>
                                                                <span style={{
                                                                    fontSize: '13.5px',
                                                                    fontWeight: 600,
                                                                    color: '#1e293b',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>
                                                                    {contactName}
                                                                </span>
                                                            </div>
                                                            {lockedContact?.leadStage && (
                                                                <span className="pipeline-stage-badge new" style={{ fontSize: '10.5px', padding: '1px 7px', flexShrink: 0 }}>
                                                                    {lockedContact.leadStage}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                // Case 3: Generic task creation
                                                return (
                                                    <CreatableSelect
                                                        value={(() => {
                                                            const c = contacts.find(item => item._id === taskFormData.associatedContactId);
                                                            return c ? `${c.firstName} ${c.lastName}`.trim() : '';
                                                        })()}
                                                        onChange={val => {
                                                            const matched = contacts.find(c => c._id === val || `${c.firstName} ${c.lastName}`.trim() === val);
                                                            setTaskFormData(prev => ({
                                                                ...prev,
                                                                associatedContactId: matched ? matched._id : val,
                                                                associatedContactIds: matched ? [matched._id] : (val ? [val] : [])
                                                            }));
                                                        }}
                                                        options={contacts.map(c => ({
                                                            id: c._id,
                                                            label: `${c.firstName} ${c.lastName}${c.company || c.email ? ` (${c.company || c.email})` : ''}`.trim()
                                                        }))}
                                                        placeholder="Select associated contact..."
                                                        isSearchable={true}
                                                        isCreatable={false}
                                                    />
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    {/* Assignee Selection with "Assign someone?" Checkbox directly */}
                                    <div className="pipeline-form-group">
                                        <label className="pipeline-form-checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', fontWeight: 600, fontSize: '13.5px', color: '#1e293b', margin: 0 }}>
                                            <input
                                                type="checkbox"
                                                checked={isAssignCustom}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    setIsAssignCustom(checked);
                                                    if (!checked) {
                                                        const defaultAssignee = (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '') || (detailedDeal ? detailedDeal.salesOwner : '') || 'Admin System';
                                                        setTaskFormData(prev => ({ ...prev, assignee: defaultAssignee }));
                                                    } else {
                                                        if (!taskFormData.assignee || taskFormData.assignee === 'Artiflow' || taskFormData.assignee === 'Admin System') {
                                                            const firstEmp = activeSalesEmployees[0];
                                                            if (firstEmp) {
                                                                const name = `${firstEmp.firstName || ''} ${firstEmp.lastName || ''}`.trim();
                                                                setTaskFormData(prev => ({ ...prev, assignee: name }));
                                                            }
                                                        }
                                                    }
                                                }}
                                                style={{ width: '16px', height: '16px', accentColor: '#0284c7', cursor: 'pointer' }}
                                            />
                                            <span>Assign someone?</span>
                                        </label>

                                        {isAssignCustom && (
                                            <div style={{ marginTop: '10px' }}>
                                                <label className="pipeline-form-label" style={{ fontSize: '12px', color: '#475569', marginBottom: '4px', fontWeight: 600 }}>
                                                    Assign to <span className="required-star">*</span>
                                                </label>
                                                <CreatableSelect
                                                    value={taskFormData.assignee}
                                                    onChange={val => setTaskFormData(prev => ({ ...prev, assignee: val }))}
                                                    options={
                                                        activeSalesEmployees.length > 0
                                                            ? activeSalesEmployees.map(emp => {
                                                                const fullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
                                                                const empId = emp.employeeId || emp.empId || emp.employee_id || emp.emp_id || '';
                                                                const metaParts = [emp.designation, empId].filter(Boolean);
                                                                const metaStr = metaParts.length > 0 ? ` (${metaParts.join(' - ')})` : '';
                                                                return {
                                                                    id: fullName,
                                                                    label: `${fullName}${metaStr}`.trim()
                                                                };
                                                            })
                                                            : (ownerOptions && ownerOptions.length > 0
                                                                ? ownerOptions.map(name => ({ id: name, label: name }))
                                                                : (employees || []).filter(emp => emp && emp.isActive !== false && emp.status !== 'Inactive' && emp.status !== 'inactive').map(emp => {
                                                                    const fullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
                                                                    const empId = emp.employeeId || emp.empId || emp.employee_id || emp.emp_id || '';
                                                                    const metaParts = [emp.designation, empId].filter(Boolean);
                                                                    const metaStr = metaParts.length > 0 ? ` (${metaParts.join(' - ')})` : '';
                                                                    return { id: fullName, label: `${fullName}${metaStr}`.trim() };
                                                                })
                                                            )
                                                    }
                                                    placeholder="Select sales employee..."
                                                    isSearchable={true}
                                                    isCreatable={false}
                                                    required={isAssignCustom}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="pipeline-form-group">
                                        <label className="pipeline-form-label">Description</label>
                                        <textarea
                                            className="pipeline-activity-composer-textarea"
                                            style={{ minHeight: '110px', fontSize: '13.5px', lineHeight: '1.5' }}
                                            placeholder="Enter description"
                                            value={taskFormData.description}
                                            onChange={e => setTaskFormData(prev => ({ ...prev, description: e.target.value }))}
                                        />
                                    </div>
                                </div>

                                <div className="pipeline-modal-footer">
                                    <button
                                        type="button"
                                        className="pipeline-btn-cancel"
                                        onClick={() => { setShowCreateTaskModal(false); setEditingTask(null); }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="pipeline-btn-save"
                                        disabled={!taskFormData.title.trim()}
                                    >
                                        {editingTask ? 'Update Task' : 'Create Task'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* ------------------------------------------------------------------
        Create / Edit Follow-up Reminder Modal
        ------------------------------------------------------------------ */}
            {
                showCreateReminderModal && (
                    <div className="pipeline-modal-backdrop" onClick={() => { setShowCreateReminderModal(false); setEditingReminder(null); }}>
                        <div className="pipeline-modal-card" style={{ maxWidth: '720px', width: '94%', borderRadius: '12px', boxShadow: '0 20px 40px -10px rgba(15, 23, 42, 0.22)', border: '1px solid #e2e8f0' }} onClick={e => e.stopPropagation()}>
                            <div className="pipeline-modal-header" style={{ padding: '16px 22px', borderBottom: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div className="pipeline-modal-icon-badge" style={{ background: '#f0fdf4', color: '#16a34a', width: '34px', height: '34px', borderRadius: '8px' }}>
                                        <Bell size={17} />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                                            {editingReminder ? 'Edit Follow-up Reminder' : 'Set New Follow-up Reminder'}
                                        </h3>
                                        <p className="pipeline-modal-subtitle" style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>
                                            Schedule and route client calls, meetings, WhatsApp follow-ups, or visits
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="pipeline-modal-close-btn"
                                    onClick={() => { setShowCreateReminderModal(false); setEditingReminder(null); }}
                                    title="Close"
                                >
                                    <X size={17} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmitReminder}>
                                <div className="pipeline-modal-body" style={{ padding: '14px 22px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {/* Target Entity Type Selector (Deal vs Contact) */}
                                    {!editingReminder && (
                                        <div className="pipeline-form-group" style={{ margin: 0 }}>
                                            <label className="pipeline-form-label" style={{ marginBottom: '5px', fontSize: '12px', fontWeight: 600 }}>Link Follow-up To</label>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                                <button
                                                    type="button"
                                                    className={`pipeline-channel-btn ${reminderFormData.targetType === 'deal' ? 'active' : ''}`}
                                                    style={{ justifyContent: 'center', padding: '8px 14px', fontSize: '12.5px', borderRadius: '7px' }}
                                                    onClick={() => {
                                                        const firstDeal = deals[0];
                                                        let autoMemberId = '';
                                                        let autoMemberName = '';
                                                        if (firstDeal && (isSalesHead || isSalesManager || isAdmin)) {
                                                            const dOwnerName = (firstDeal.salesOwner || firstDeal.addedBy || '').trim();
                                                            if (dOwnerName) {
                                                                const matchedEmp = (employees || []).find(emp => {
                                                                    const eName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                                                                    const eEmail = (emp.email || '').trim().toLowerCase();
                                                                    const target = dOwnerName.toLowerCase();
                                                                    return (eName && eName === target) || (eEmail && eEmail === target);
                                                                });
                                                                if (matchedEmp) {
                                                                    autoMemberId = String(matchedEmp._id || matchedEmp.id);
                                                                    autoMemberName = `${matchedEmp.firstName || ''} ${matchedEmp.lastName || ''}`.trim() || dOwnerName;
                                                                } else {
                                                                    autoMemberId = `name_${dOwnerName}`;
                                                                    autoMemberName = dOwnerName;
                                                                }
                                                            }
                                                        }
                                                        setReminderFormData(prev => ({
                                                            ...prev,
                                                            targetType: 'deal',
                                                            targetId: firstDeal?._id || '',
                                                            memberId: autoMemberId,
                                                            memberName: autoMemberName
                                                        }));
                                                    }}
                                                >
                                                    <Briefcase size={14} />
                                                    <span>Deal Follow-up</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`pipeline-channel-btn ${reminderFormData.targetType === 'contact' ? 'active' : ''}`}
                                                    style={{ justifyContent: 'center', padding: '8px 14px', fontSize: '12.5px', borderRadius: '7px' }}
                                                    onClick={() => {
                                                        const firstContact = contacts[0];
                                                        let autoMemberId = '';
                                                        let autoMemberName = '';
                                                        if (firstContact && (isSalesHead || isSalesManager || isAdmin)) {
                                                            const cOwnerName = (firstContact.owner || firstContact.addedBy || '').trim();
                                                            if (cOwnerName) {
                                                                const matchedEmp = (employees || []).find(emp => {
                                                                    const eName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                                                                    const eEmail = (emp.email || '').trim().toLowerCase();
                                                                    const target = cOwnerName.toLowerCase();
                                                                    return (eName && eName === target) || (eEmail && eEmail === target);
                                                                });
                                                                if (matchedEmp) {
                                                                    autoMemberId = String(matchedEmp._id || matchedEmp.id);
                                                                    autoMemberName = `${matchedEmp.firstName || ''} ${matchedEmp.lastName || ''}`.trim() || cOwnerName;
                                                                } else {
                                                                    autoMemberId = `name_${cOwnerName}`;
                                                                    autoMemberName = cOwnerName;
                                                                }
                                                            }
                                                        }
                                                        setReminderFormData(prev => ({
                                                            ...prev,
                                                            targetType: 'contact',
                                                            targetId: firstContact?._id || '',
                                                            memberId: autoMemberId,
                                                            memberName: autoMemberName
                                                        }));
                                                    }}
                                                >
                                                    <User size={14} />
                                                    <span>Contact Follow-up</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Target Deal or Contact */}
                                    <div className="pipeline-form-group" style={{ margin: 0 }}>
                                        <label className="pipeline-form-label" style={{ marginBottom: '5px', fontSize: '12px', fontWeight: 600 }}>
                                            Select {reminderFormData.targetType === 'deal' ? 'Deal' : 'Contact'} <span className="required-star">*</span>
                                        </label>
                                        {editingReminder ? (
                                            <div style={{ height: '36px', padding: '0 12px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '7px', display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                                {reminderFormData.targetType === 'deal'
                                                    ? (deals.find(item => String(item._id) === String(reminderFormData.targetId))?.dealName || reminderFormData.entityName || 'Deal')
                                                    : (contacts.find(item => String(item._id) === String(reminderFormData.targetId))?.firstName || reminderFormData.entityName || 'Contact')}
                                            </div>
                                        ) : reminderFormData.targetType === 'deal' ? (
                                            <CreatableSelect
                                                value={(() => {
                                                    const d = deals.find(item => String(item._id) === String(reminderFormData.targetId));
                                                    return d ? `${d.dealName || d.clientName}${d.dealValue ? ` (₹${Number(d.dealValue).toLocaleString('en-IN')})` : ''}` : '';
                                                })()}
                                                onChange={val => {
                                                    const matched = deals.find(item => String(item._id) === String(val) || (item.dealName || item.clientName) === val || `${item.dealName || item.clientName}${item.dealValue ? ` (₹${Number(item.dealValue).toLocaleString('en-IN')})` : ''}` === val);
                                                    const nextTargetId = matched ? matched._id : val;
                                                    let autoMemberId = reminderFormData.memberId;
                                                    let autoMemberName = reminderFormData.memberName;

                                                    if (matched && (isSalesHead || isSalesManager || isAdmin)) {
                                                        const dOwnerName = (matched.salesOwner || matched.addedBy || '').trim();
                                                        if (dOwnerName) {
                                                            const matchedEmp = (employees || []).find(emp => {
                                                                const eName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                                                                const eEmail = (emp.email || '').trim().toLowerCase();
                                                                const eOffEmail = (emp.officialEmail || '').trim().toLowerCase();
                                                                const target = dOwnerName.toLowerCase();
                                                                return (eName && eName === target) || (eEmail && eEmail === target) || (eOffEmail && eOffEmail === target);
                                                            });
                                                            if (matchedEmp) {
                                                                autoMemberId = String(matchedEmp._id || matchedEmp.id);
                                                                autoMemberName = `${matchedEmp.firstName || ''} ${matchedEmp.lastName || ''}`.trim() || dOwnerName;
                                                            } else {
                                                                autoMemberId = `name_${dOwnerName}`;
                                                                autoMemberName = dOwnerName;
                                                            }
                                                        }
                                                    }

                                                    setReminderFormData(prev => ({
                                                        ...prev,
                                                        targetId: nextTargetId,
                                                        memberId: autoMemberId,
                                                        memberName: autoMemberName
                                                    }));
                                                }}
                                                options={deals.filter(d => isItemOwnedByUser(d, 'deal')).map(d => ({
                                                    id: d._id,
                                                    label: `${d.dealName || d.clientName}${d.dealValue ? ` (₹${Number(d.dealValue).toLocaleString('en-IN')})` : ''}`
                                                }))}
                                                placeholder="Select or search Deal..."
                                                isSearchable={true}
                                                isCreatable={false}
                                                required={true}
                                            />
                                        ) : (
                                            <CreatableSelect
                                                value={(() => {
                                                    const c = contacts.find(item => String(item._id) === String(reminderFormData.targetId));
                                                    return c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.company || c.email : '';
                                                })()}
                                                onChange={val => {
                                                    const matched = contacts.find(item => String(item._id) === String(val) || `${item.firstName || ''} ${item.lastName || ''}`.trim() === val);
                                                    const nextTargetId = matched ? matched._id : val;
                                                    let autoMemberId = reminderFormData.memberId;
                                                    let autoMemberName = reminderFormData.memberName;

                                                    if (matched && (isSalesHead || isSalesManager || isAdmin)) {
                                                        const cOwnerName = (matched.owner || matched.addedBy || '').trim();
                                                        if (cOwnerName) {
                                                            const matchedEmp = (employees || []).find(emp => {
                                                                const eName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
                                                                const eEmail = (emp.email || '').trim().toLowerCase();
                                                                const eOffEmail = (emp.officialEmail || '').trim().toLowerCase();
                                                                const target = cOwnerName.toLowerCase();
                                                                return (eName && eName === target) || (eEmail && eEmail === target) || (eOffEmail && eOffEmail === target);
                                                            });
                                                            if (matchedEmp) {
                                                                autoMemberId = String(matchedEmp._id || matchedEmp.id);
                                                                autoMemberName = `${matchedEmp.firstName || ''} ${matchedEmp.lastName || ''}`.trim() || cOwnerName;
                                                            } else {
                                                                autoMemberId = `name_${cOwnerName}`;
                                                                autoMemberName = cOwnerName;
                                                            }
                                                        }
                                                    }

                                                    setReminderFormData(prev => ({
                                                        ...prev,
                                                        targetId: nextTargetId,
                                                        memberId: autoMemberId,
                                                        memberName: autoMemberName
                                                    }));
                                                }}
                                                options={contacts.filter(c => isItemOwnedByUser(c, 'contact')).map(c => ({
                                                    id: c._id,
                                                    label: `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.company || c.email || 'Contact'
                                                }))}
                                                placeholder="Select or search Contact..."
                                                isSearchable={true}
                                                isCreatable={false}
                                                required={true}
                                            />
                                        )}
                                    </div>

                                    {/* Assign / Link Member Field */}
                                    {(() => {
                                        const currentMemberOptions = getFollowUpMemberOptions(reminderFormData.targetType, reminderFormData.targetId);
                                        const isSalesPerson = !isAdmin && !isSalesHead && !isSalesManager;

                                        return (
                                            <div className="pipeline-form-group" style={{ margin: 0 }}>
                                                <label className="pipeline-form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px', fontSize: '12px' }}>
                                                    <span style={{ fontWeight: 600 }}>
                                                        {isSalesPerson ? 'Route to Sales Management' : 'Assign / Link Member'}
                                                    </span>
                                                    <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '1px 7px', borderRadius: '4px', background: isSalesPerson ? '#fef3c7' : '#e0f2fe', color: isSalesPerson ? '#b45309' : '#0369a1' }}>
                                                        {isAdmin
                                                            ? 'Admin Mode'
                                                            : (isSalesHead || isSalesManager)
                                                                ? 'Sales Leadership'
                                                                : 'Sales Executive'}
                                                    </span>
                                                </label>
                                                <CreatableSelect
                                                    value={(() => {
                                                        if (!reminderFormData.memberId) return '';
                                                        const opt = currentMemberOptions.find(o => String(o.id) === String(reminderFormData.memberId) || o.name === reminderFormData.memberName);
                                                        return opt ? (opt.label || opt.name) : (reminderFormData.memberName || '');
                                                    })()}
                                                    onChange={val => {
                                                        if (!val) {
                                                            setReminderFormData(prev => ({ ...prev, memberId: '', memberName: '' }));
                                                            return;
                                                        }
                                                        const matched = currentMemberOptions.find(o => String(o.id) === String(val) || (o.label || o.name) === val || o.name === val);
                                                        setReminderFormData(prev => ({
                                                            ...prev,
                                                            memberId: matched ? matched.id : val,
                                                            memberName: matched ? matched.name : val
                                                        }));
                                                    }}
                                                    options={currentMemberOptions.map(opt => ({
                                                        id: opt.id,
                                                        label: opt.label || opt.name
                                                    }))}
                                                    placeholder={isSalesPerson ? "Select Sales Head / Manager..." : "Select Deal Owner or Team Member..."}
                                                    isSearchable={true}
                                                    isCreatable={false}
                                                    isDeleteable={false}
                                                />
                                                <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#64748b', lineHeight: '1.35' }}>
                                                    {isAdmin
                                                        ? 'Both creator & selected member receive reminders. Admin receives all.'
                                                        : (isSalesHead || isSalesManager)
                                                            ? 'Selecting deal owner sends reminder & notifications to both you and the deal owner.'
                                                            : 'Select your Sales Head or Manager to route reminders to leadership.'}
                                                </p>
                                            </div>
                                        );
                                    })()}

                                    {/* Channel Selector Pills */}
                                    <div className="pipeline-form-group" style={{ margin: 0 }}>
                                        <label className="pipeline-form-label" style={{ marginBottom: '5px', fontSize: '12px', fontWeight: 600 }}>Touchpoint / Channel</label>
                                        <div className="pipeline-followup-channels-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {[
                                                { key: 'Call', label: 'Call', icon: <Phone size={12} />, className: 'channel-call' },
                                                { key: 'Meeting', label: 'Meeting', icon: <Users size={12} />, className: 'channel-meeting' },
                                                { key: 'WhatsApp', label: 'WhatsApp', icon: <MessageSquare size={12} />, className: 'channel-whatsapp' },
                                                { key: 'Email', label: 'Email', icon: <Mail size={12} />, className: 'channel-email' },
                                                { key: 'Demo', label: 'Demo', icon: <Briefcase size={12} />, className: 'channel-demo' },
                                                { key: 'Visit', label: 'Visit', icon: <Building2 size={12} />, className: 'channel-visit' }
                                            ].map(ch => (
                                                <button
                                                    key={ch.key}
                                                    type="button"
                                                    className={`pipeline-channel-btn ${ch.className} ${reminderFormData.channel === ch.key ? 'active' : ''}`}
                                                    style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px', gap: '5px' }}
                                                    onClick={() => setReminderFormData(prev => ({ ...prev, channel: ch.key }))}
                                                >
                                                    {ch.icon}
                                                    <span>{ch.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Title / Purpose */}
                                    <div className="pipeline-form-group" style={{ margin: 0 }}>
                                        <label className="pipeline-form-label" style={{ marginBottom: '5px', fontSize: '12px', fontWeight: 600 }}>Title / Purpose</label>
                                        <input
                                            type="text"
                                            className="pipeline-form-input"
                                            style={{ height: '36px', borderRadius: '7px', fontSize: '13px' }}
                                            placeholder={`e.g. ${reminderFormData.channel} regarding proposal & next steps`}
                                            value={reminderFormData.title}
                                            onChange={e => setReminderFormData(prev => ({ ...prev, title: e.target.value }))}
                                        />
                                    </div>

                                    {/* 4-Column: Scheduled Date, Scheduled Time, Status, Next Follow-up Date */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px' }}>
                                        <div className="pipeline-form-group" style={{ margin: 0 }}>
                                            <label className="pipeline-form-label" style={{ marginBottom: '4px', fontSize: '12px', fontWeight: 600 }}>
                                                Scheduled Date <span className="required-star">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                className="pipeline-form-input"
                                                style={{ height: '34px', borderRadius: '7px', fontSize: '13px' }}
                                                value={reminderFormData.followUpDate}
                                                onChange={e => setReminderFormData(prev => ({ ...prev, followUpDate: e.target.value }))}
                                                required
                                            />
                                        </div>

                                        <div className="pipeline-form-group" style={{ margin: 0 }}>
                                            <label className="pipeline-form-label" style={{ marginBottom: '4px', fontSize: '12px', fontWeight: 600 }}>Scheduled Time</label>
                                            <input
                                                type="time"
                                                className="pipeline-form-input"
                                                style={{ height: '34px', borderRadius: '7px', fontSize: '13px' }}
                                                value={convertTimeTo24H(reminderFormData.followUpTime)}
                                                onChange={e => setReminderFormData(prev => ({ ...prev, followUpTime: e.target.value }))}
                                            />
                                        </div>

                                        <div className="pipeline-form-group" style={{ margin: 0 }}>
                                            <label className="pipeline-form-label" style={{ marginBottom: '4px', fontSize: '12px', fontWeight: 600 }}>Status</label>
                                            <select
                                                className="pipeline-form-input"
                                                style={{ height: '34px', borderRadius: '7px', fontSize: '13px', background: '#fff' }}
                                                value={reminderFormData.status}
                                                onChange={e => setReminderFormData(prev => ({ ...prev, status: e.target.value }))}
                                            >
                                                <option value="Scheduled">Scheduled</option>
                                                <option value="Completed">Completed</option>
                                                <option value="Follow-up Needed">Follow-up Needed</option>
                                                <option value="Rescheduled">Rescheduled</option>
                                                <option value="Missed">Missed</option>
                                            </select>
                                        </div>

                                        <div className="pipeline-form-group" style={{ margin: 0 }}>
                                            <label className="pipeline-form-label" style={{ marginBottom: '4px', fontSize: '12px', fontWeight: 600 }}>Next Follow-up Date</label>
                                            <input
                                                type="date"
                                                className="pipeline-form-input"
                                                style={{ height: '34px', borderRadius: '7px', fontSize: '13px' }}
                                                value={reminderFormData.nextFollowUpDate}
                                                onChange={e => setReminderFormData(prev => ({ ...prev, nextFollowUpDate: e.target.value }))}
                                            />
                                        </div>
                                    </div>

                                    {/* Remarks Field */}
                                    <div className="pipeline-form-group" style={{ margin: 0 }}>
                                        <label className="pipeline-form-label" style={{ marginBottom: '4px', fontSize: '12px', fontWeight: 600 }}>
                                            Remarks & Discussion Details <span className="required-star">*</span>
                                        </label>
                                        <textarea
                                            className="pipeline-activity-composer-textarea"
                                            style={{ minHeight: '80px', fontSize: '13px', lineHeight: '1.45', width: '100%', borderRadius: '7px', boxSizing: 'border-box' }}
                                            placeholder="Enter discussion notes, agreed next steps, objections raised, or client feedback..."
                                            value={reminderFormData.remarks}
                                            onChange={e => setReminderFormData(prev => ({ ...prev, remarks: e.target.value }))}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="pipeline-modal-footer" style={{ borderTop: '1px solid #f1f5f9', padding: '14px 22px', background: '#fafafa', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                    <button
                                        type="button"
                                        className="pipeline-btn-cancel"
                                        disabled={isSubmittingReminder}
                                        onClick={() => { setShowCreateReminderModal(false); setEditingReminder(null); }}
                                        style={{ height: '36px', padding: '0 16px', borderRadius: '7px', fontSize: '12.5px' }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="pipeline-btn-save"
                                        disabled={!reminderFormData.remarks.trim() || isSubmittingReminder}
                                        style={{ height: '36px', padding: '0 20px', borderRadius: '7px', fontSize: '12.5px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                                    >
                                        {isSubmittingReminder ? (
                                            <>
                                                <Loader2 size={13} className="pipeline-spinner-icon" />
                                                <span>{editingReminder ? 'Updating...' : 'Saving...'}</span>
                                            </>
                                        ) : (
                                            <span>{editingReminder ? 'Update Reminder' : 'Save Reminder'}</span>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* ====================================================================
          MILESTONE PAYMENT RECORDS MODAL
          ==================================================================== */}
            {
                showMilestoneModal && createPortal(
                    <div
                        className="pipeline-milestone-modal-overlay"
                        onClick={(e) => { if (e.target === e.currentTarget) setShowMilestoneModal(false); }}
                    >
                        <div className="pipeline-milestone-modal-card">
                            {/* Modal Header */}
                            <div className="pipeline-milestone-modal-header">
                                <div className="pipeline-milestone-modal-header-left">
                                    <div className="pipeline-milestone-modal-icon">
                                        <CreditCard size={20} />
                                    </div>
                                    <div>
                                        <h2 className="pipeline-milestone-modal-title">Payment Milestones & Records</h2>
                                        <p className="pipeline-milestone-modal-subtitle">
                                            {(() => {
                                                if (!milestoneModalDealId) return 'All Payment Records';
                                                const foundDeal = deals.find(d => d._id === milestoneModalDealId);
                                                if (foundDeal) return `Deal: ${foundDeal.dealName || foundDeal.clientName || 'Unknown Deal'}`;
                                                const foundContact = contacts.find(c => `contact_${c._id}` === milestoneModalDealId || c._id === milestoneModalDealId) || detailedContact;
                                                if (foundContact) return `Contact: ${`${foundContact.firstName || ''} ${foundContact.lastName || ''}`.trim() || foundContact.company || 'Client'}`;
                                                return 'All Payment Records';
                                            })()}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="pipeline-milestone-modal-close-btn"
                                    onClick={() => setShowMilestoneModal(false)}
                                    title="Close"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Summary Tiles */}
                            {(() => {
                                const targetDeal = (() => {
                                    if (milestoneModalDealId) {
                                        const found = deals.find(d => d._id === milestoneModalDealId);
                                        if (found) return found;
                                        const foundContact = contacts.find(c => `contact_${c._id}` === milestoneModalDealId || c._id === milestoneModalDealId) || detailedContact;
                                        if (foundContact) {
                                            const matchedDeal = deals.find(d =>
                                                d.contactId === foundContact._id ||
                                                (d.clientName && foundContact.firstName && (
                                                    d.clientName.toLowerCase().includes(foundContact.firstName.toLowerCase()) ||
                                                    foundContact.firstName.toLowerCase().includes(d.clientName.toLowerCase())
                                                )) ||
                                                (d.clientName && foundContact.company && d.clientName.toLowerCase().includes(foundContact.company.toLowerCase()))
                                            );
                                            if (matchedDeal) return matchedDeal;
                                            return {
                                                _id: `contact_${foundContact._id}`,
                                                clientName: `${foundContact.firstName || ''} ${foundContact.lastName || ''}`.trim() || foundContact.company || 'Client',
                                                clientEmail: foundContact.email,
                                                dealName: foundContact.company ? `${foundContact.company} Deployment` : `${foundContact.firstName || 'Client'} Deployment`,
                                                dealValue: 0,
                                                currency: '₹',
                                                expectedCloseDate: new Date(Date.now() + 86400000 * 30).toISOString()
                                            };
                                        }
                                    }
                                    return detailedDeal || {};
                                })();

                                const records = (() => {
                                    if (milestoneModalDealId && !milestoneModalDealId.startsWith('contact_')) {
                                        const d = deals.find(x => x._id === milestoneModalDealId) || detailedDeal;
                                        if (d) return getDealPaymentRecords(d);
                                    }
                                    if (detailedDeal && (!milestoneModalDealId || milestoneModalDealId === detailedDeal._id)) {
                                        return getDealPaymentRecords(detailedDeal);
                                    }
                                    if (detailedContact) {
                                        return getContactPaymentRecords(detailedContact);
                                    }
                                    const foundContact = contacts.find(c => `contact_${c._id}` === milestoneModalDealId || c._id === milestoneModalDealId);
                                    if (foundContact) {
                                        return getContactPaymentRecords(foundContact);
                                    }
                                    return paymentRecords[milestoneModalDealId] || [];
                                })();

                                const dealValue = parsePaymentAmount(targetDeal.dealValue);
                                const paidAmount = records
                                    .filter(r => (r.paymentStatus || '').toLowerCase() === 'paid')
                                    .reduce((sum, r) => sum + parsePaymentAmount(r.paidAmount), 0);

                                const pendingRecordsAmount = records
                                    .filter(r => (r.paymentStatus || '').toLowerCase() !== 'paid')
                                    .reduce((sum, r) => sum + parsePaymentAmount(r.paidAmount), 0);

                                const totalAmount = dealValue > 0 ? dealValue : (paidAmount + pendingRecordsAmount);
                                const pendingAmount = dealValue > 0 ? Math.max(0, dealValue - paidAmount) : pendingRecordsAmount;
                                const currency = targetDeal.currency || '₹';

                                return (
                                    <div className="pipeline-milestone-modal-summary-row">
                                        <div className="pipeline-milestone-summary-tile">
                                            <div className="pipeline-milestone-summary-tile-label">Total Amount</div>
                                            <div className="pipeline-milestone-summary-tile-val blue">{currency} {totalAmount.toLocaleString()}</div>
                                        </div>
                                        <div className="pipeline-milestone-summary-tile">
                                            <div className="pipeline-milestone-summary-tile-label">Paid Amount</div>
                                            <div className="pipeline-milestone-summary-tile-val green">{currency} {paidAmount.toLocaleString()}</div>
                                        </div>
                                        <div className="pipeline-milestone-summary-tile">
                                            <div className="pipeline-milestone-summary-tile-label">Pending Amount</div>
                                            <div className="pipeline-milestone-summary-tile-val orange">{currency} {pendingAmount.toLocaleString()}</div>
                                        </div>
                                        <div className="pipeline-milestone-summary-tile">
                                            <div className="pipeline-milestone-summary-tile-label">Payment Records</div>
                                            <div className="pipeline-milestone-summary-tile-val">{records.length}</div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Modal Body */}
                            <div className="pipeline-milestone-modal-body">
                                {/* Toolbar */}
                                <div className="pipeline-milestone-modal-toolbar">
                                    <span className="pipeline-milestone-modal-toolbar-title">All Payment Records</span>
                                    <button
                                        type="button"
                                        className="pipeline-add-payment-btn"
                                        onClick={() => {
                                            const deal = (() => {
                                                if (milestoneModalDealId) {
                                                    const found = deals.find(d => d._id === milestoneModalDealId);
                                                    if (found) return found;
                                                    const foundContact = contacts.find(c => `contact_${c._id}` === milestoneModalDealId || c._id === milestoneModalDealId) || detailedContact;
                                                    if (foundContact) {
                                                        const matchedDeal = deals.find(d =>
                                                            d.contactId === foundContact._id ||
                                                            (d.clientName && foundContact.firstName && (
                                                                d.clientName.toLowerCase().includes(foundContact.firstName.toLowerCase()) ||
                                                                foundContact.firstName.toLowerCase().includes(d.clientName.toLowerCase())
                                                            )) ||
                                                            (d.clientName && foundContact.company && d.clientName.toLowerCase().includes(foundContact.company.toLowerCase()))
                                                        );
                                                        if (matchedDeal) return matchedDeal;
                                                        return {
                                                            _id: `contact_${foundContact._id}`,
                                                            clientName: `${foundContact.firstName || ''} ${foundContact.lastName || ''}`.trim() || foundContact.company || 'Client',
                                                            clientEmail: foundContact.email,
                                                            dealName: foundContact.company ? `${foundContact.company} Deployment` : `${foundContact.firstName || 'Client'} Deployment`,
                                                            dealValue: 5000,
                                                            currency: '$',
                                                            expectedCloseDate: new Date(Date.now() + 86400000 * 30).toISOString()
                                                        };
                                                    }
                                                }
                                                return detailedDeal || {};
                                            })();
                                            const matchedContact = contacts.find(c =>
                                                (deal.contactId && c._id === deal.contactId) ||
                                                (deal.clientName && c.firstName && (
                                                    `${c.firstName} ${c.lastName || ''}`.trim().toLowerCase() === deal.clientName.toLowerCase() ||
                                                    deal.clientName.toLowerCase().includes(c.firstName.toLowerCase()) ||
                                                    c.firstName.toLowerCase().includes(deal.clientName.toLowerCase())
                                                )) ||
                                                (deal.clientName && c.company && c.company.toLowerCase() === deal.clientName.toLowerCase())
                                            );
                                            const clientEmail = deal.clientEmail || (matchedContact && matchedContact.email) || `${(deal.clientName || 'client').toLowerCase().replace(/[^a-z0-9]/g, '.')}@bhoomi.com`;
                                            const clientDisplayName = deal.clientName || 'Client';
                                            const clientWithEmail = `${clientDisplayName} (${clientEmail})`;
                                            const projectNameVal = deal.dealName ? `${deal.dealName} Deployment` : (deal.clientName ? `${deal.clientName} Deployment` : 'Deployment');

                                            setNewPaymentData({
                                                clientName: clientWithEmail,
                                                clientEmail: clientEmail,
                                                projectName: projectNameVal,
                                                paidAmount: '', paymentMode: 'Bank Transfer',
                                                paymentStatus: 'Paid',
                                                paymentDate: new Date().toISOString().slice(0, 10),
                                                attachmentName: ''
                                            });
                                            setShowAddPaymentForm(prev => !prev);
                                            setAttachmentFile(null);
                                        }}
                                    >
                                        <Plus size={13} />
                                        <span>Add Payment Record</span>
                                    </button>
                                </div>

                                {/* Payment Records Table */}
                                <div className={`pipeline-milestone-table-wrap ${showAddPaymentForm ? 'compact-view' : ''}`}>
                                    <table className="pipeline-milestone-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '48px', textAlign: 'center' }}>Sr. No.</th>
                                                <th style={{ minWidth: '145px' }}>Client Name</th>
                                                <th style={{ minWidth: '135px' }}>Project Name</th>
                                                <th style={{ minWidth: '95px' }}>Paid Amount</th>
                                                <th style={{ minWidth: '95px' }}>Payment Mode</th>
                                                <th style={{ minWidth: '85px' }}>Status</th>
                                                <th style={{ minWidth: '90px' }}>Payment Date</th>
                                                <th style={{ minWidth: '105px' }}>Recorded On</th>
                                                <th style={{ width: '85px', textAlign: 'center' }}>Attachment</th>
                                                <th style={{ width: '36px', textAlign: 'center' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const records = (() => {
                                                    if (milestoneModalDealId && !milestoneModalDealId.startsWith('contact_')) {
                                                        const d = deals.find(x => x._id === milestoneModalDealId) || detailedDeal;
                                                        if (d) return getDealPaymentRecords(d);
                                                    }
                                                    if (detailedDeal && (!milestoneModalDealId || milestoneModalDealId === detailedDeal._id)) {
                                                        return getDealPaymentRecords(detailedDeal);
                                                    }
                                                    if (detailedContact) {
                                                        return getContactPaymentRecords(detailedContact);
                                                    }
                                                    const foundContact = contacts.find(c => `contact_${c._id}` === milestoneModalDealId || c._id === milestoneModalDealId);
                                                    if (foundContact) {
                                                        return getContactPaymentRecords(foundContact);
                                                    }
                                                    return paymentRecords[milestoneModalDealId] || [];
                                                })();

                                                if (records.length === 0) {
                                                    return (
                                                        <tr>
                                                            <td colSpan="10" style={{ textAlign: 'center', padding: '36px 16px', color: '#94a3b8' }}>
                                                                <CreditCard size={28} style={{ margin: '0 auto 8px', opacity: 0.45 }} />
                                                                <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>No Payment Records Found</div>
                                                                <div style={{ fontSize: '11.5px', color: '#94a3b8', marginTop: '3px' }}>
                                                                    No payment records recorded yet. Click <strong>+ Add Payment Record</strong> to add one.
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                }

                                                return records.map((rec, idx) => {
                                                    const rawName = rec.clientName || '';
                                                    const cleanName = rawName.replace(/\s*\([^)]*@[^)]*\)\s*$/, '').trim() || rawName || 'Client';
                                                    const rawEmail = rec.clientEmail || (rawName.match(/\(([^)]*@[^)]*)\)/) ? rawName.match(/\(([^)]*@[^)]*)\)/)[1] : '');

                                                    return (
                                                        <tr key={rec.id || rec._id || idx}>
                                                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#64748b', fontSize: '11.5px' }}>{idx + 1}</td>
                                                            <td>
                                                                <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '175px' }} title={cleanName}>
                                                                    {cleanName}
                                                                </div>
                                                                {rawEmail && (
                                                                    <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '175px' }} title={rawEmail}>
                                                                        {rawEmail}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div style={{ fontWeight: 500, color: '#334155', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }} title={rec.projectName}>
                                                                    {rec.projectName || '—'}
                                                                </div>
                                                            </td>
                                                            <td style={{ fontWeight: 700, fontSize: '12px', color: (rec.paymentStatus || '').toLowerCase() === 'paid' ? '#16a34a' : (rec.paymentStatus || '').toLowerCase() === 'pending' ? '#ea580c' : '#ca8a04', whiteSpace: 'nowrap' }}>
                                                                {rec.paidAmount}
                                                            </td>
                                                            <td style={{ color: '#475569', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                                {rec.paymentMode || '—'}
                                                            </td>
                                                            <td>
                                                                <TableStatusPill
                                                                    status={rec.paymentStatus || 'Pending'}
                                                                    onSelectStatus={async (newStatus) => {
                                                                        try {
                                                                            if (rec._id || (rec.id && !rec.id.toString().startsWith('pay-sample-'))) {
                                                                                await api.put(`/payment-records/${rec._id || rec.id}`, { paymentStatus: newStatus });
                                                                            }
                                                                        } catch (err) {
                                                                            console.warn('Update payment record API sync:', err.message);
                                                                        }
                                                                        setPaymentRecords(prev => {
                                                                            const updated = {};
                                                                            Object.keys(prev).forEach(k => {
                                                                                updated[k] = (prev[k] || []).map(r =>
                                                                                    (r._id === rec._id || r.id === rec.id) ? { ...r, paymentStatus: newStatus } : r
                                                                                );
                                                                            });
                                                                            if (milestoneModalDealId && !updated[milestoneModalDealId]) {
                                                                                updated[milestoneModalDealId] = (prev[milestoneModalDealId] || []).map((r, rIdx) =>
                                                                                    (r._id === rec._id || r.id === rec.id || rIdx === idx) ? { ...r, paymentStatus: newStatus } : r
                                                                                );
                                                                            }
                                                                            return updated;
                                                                        });
                                                                    }}
                                                                />
                                                            </td>
                                                            <td style={{ whiteSpace: 'nowrap', fontSize: '11.5px', color: '#334155' }}>
                                                                {rec.paymentDate || '—'}
                                                            </td>
                                                            <td>
                                                                <div style={{ fontWeight: 600, color: '#334155', fontSize: '11px', whiteSpace: 'nowrap' }}>{rec.recordedOnDate || rec.recordedOn || '—'}</div>
                                                                {rec.recordedOnTime && <div style={{ fontSize: '10px', color: '#64748b', whiteSpace: 'nowrap' }}>{rec.recordedOnTime} (IST)</div>}
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    {rec.attachmentName || rec.attachmentUrl ? (
                                                                        <button
                                                                            type="button"
                                                                            className="pipeline-attachment-view-badge"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setPreviewAttachment(rec);
                                                                            }}
                                                                            title={rec.attachmentName || 'View Attachment'}
                                                                        >
                                                                            <Eye size={12} />
                                                                            <span>View</span>
                                                                        </button>
                                                                    ) : (
                                                                        <span style={{ color: '#cbd5e1', fontSize: '13px' }}>—</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>
                                                                <button
                                                                    type="button"
                                                                    className="pipeline-table-row-delete-btn"
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        if (window.confirm('Are you sure you want to delete this payment record?')) {
                                                                            try {
                                                                                if (rec._id || (rec.id && !rec.id.toString().startsWith('pay-sample-'))) {
                                                                                    await api.delete(`/payment-records/${rec._id || rec.id}`);
                                                                                }
                                                                            } catch (err) {
                                                                                console.warn('Delete payment record API sync:', err.message);
                                                                            }
                                                                            setPaymentRecords(prev => {
                                                                                const updated = {};
                                                                                Object.keys(prev).forEach(k => {
                                                                                    updated[k] = (prev[k] || []).filter(r =>
                                                                                        r._id ? r._id !== rec._id : (r.id ? r.id !== rec.id : true)
                                                                                    );
                                                                                });
                                                                                if (milestoneModalDealId && updated[milestoneModalDealId]) {
                                                                                    updated[milestoneModalDealId] = (updated[milestoneModalDealId] || []).filter((r, rIdx) =>
                                                                                        r._id ? r._id !== rec._id : (r.id ? r.id !== rec.id : rIdx !== idx)
                                                                                    );
                                                                                }
                                                                                return updated;
                                                                            });
                                                                        }
                                                                    }}
                                                                    title="Delete Payment Record"
                                                                >
                                                                    <Trash2 size={12} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                });
                                            })()}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Add Payment Form */}
                                {showAddPaymentForm && (
                                    <div className="pipeline-add-payment-form-section">
                                        <div className="pipeline-add-payment-form-header">
                                            <span className="pipeline-add-payment-form-title">➕ Add New Payment Record</span>
                                            <button
                                                type="button"
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }}
                                                onClick={() => setShowAddPaymentForm(false)}
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                        <div className="pipeline-add-payment-form-body">
                                            {/* Sr No (auto) */}
                                            <div className="pipeline-add-payment-field">
                                                <label className="pipeline-add-payment-label">Sr. No.</label>
                                                <input
                                                    type="text"
                                                    className="pipeline-add-payment-input readonly"
                                                    value={`#${(paymentRecords[milestoneModalDealId] || []).length + 1}`}
                                                    readOnly
                                                    style={{ background: '#f8fafc', color: '#64748b' }}
                                                />
                                            </div>

                                            {/* Client Name (Pre-filled & Non-editable) */}
                                            <div className="pipeline-add-payment-field">
                                                <label className="pipeline-add-payment-label">Client Name</label>
                                                <input
                                                    type="text"
                                                    className="pipeline-add-payment-input readonly"
                                                    placeholder="Enter name"
                                                    value={newPaymentData.clientName || (() => {
                                                        const deal = deals.find(d => d._id === milestoneModalDealId) || detailedDeal || {};
                                                        const matchedContact = contacts.find(c =>
                                                            (deal.contactId && c._id === deal.contactId) ||
                                                            (deal.clientName && c.firstName && (
                                                                `${c.firstName} ${c.lastName || ''}`.trim().toLowerCase() === deal.clientName.toLowerCase() ||
                                                                deal.clientName.toLowerCase().includes(c.firstName.toLowerCase()) ||
                                                                c.firstName.toLowerCase().includes(deal.clientName.toLowerCase())
                                                            )) ||
                                                            (deal.clientName && c.company && c.company.toLowerCase() === deal.clientName.toLowerCase())
                                                        );
                                                        const clientEmail = deal.clientEmail || (matchedContact && matchedContact.email) || `${(deal.clientName || 'client').toLowerCase().replace(/[^a-z0-9]/g, '.')}@bhoomi.com`;
                                                        return `${deal.clientName || 'Client'} (${clientEmail})`;
                                                    })()}
                                                    readOnly
                                                    title="Client name and email are pre-filled and cannot be edited"
                                                />
                                            </div>

                                            {/* Project Name (Pre-filled & Non-editable) */}
                                            <div className="pipeline-add-payment-field">
                                                <label className="pipeline-add-payment-label">Project Name</label>
                                                <input
                                                    type="text"
                                                    className="pipeline-add-payment-input readonly"
                                                    placeholder="Enter name"
                                                    value={newPaymentData.projectName || (() => {
                                                        const deal = deals.find(d => d._id === milestoneModalDealId) || detailedDeal || {};
                                                        return deal.dealName ? `${deal.dealName} Deployment` : (deal.clientName ? `${deal.clientName} Deployment` : 'Deployment');
                                                    })()}
                                                    readOnly
                                                    title="Project name is pre-filled and cannot be edited"
                                                />
                                            </div>

                                            {/* Paid Amount (Mandatory Numeric) */}
                                            <div className="pipeline-add-payment-field">
                                                <label className="pipeline-add-payment-label">
                                                    Paid Amount <span style={{ color: '#ef4444', fontWeight: 'bold' }}>*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    className="pipeline-add-payment-input"
                                                    placeholder="Enter amount"
                                                    value={newPaymentData.paidAmount}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                                            setNewPaymentData(prev => ({ ...prev, paidAmount: val }));
                                                        }
                                                    }}
                                                    required
                                                />
                                            </div>

                                            {/* Payment Mode (Mandatory) */}
                                            <div className="pipeline-add-payment-field">
                                                <label className="pipeline-add-payment-label">
                                                    Payment Mode <span style={{ color: '#ef4444', fontWeight: 'bold' }}>*</span>
                                                </label>
                                                <CreatableSelect
                                                    value={newPaymentData.paymentMode}
                                                    onChange={val => setNewPaymentData(prev => ({ ...prev, paymentMode: val }))}
                                                    options={[...DEFAULT_PAYMENT_MODES, ...customPaymentModes]}
                                                    onCreateOption={newOpt => setCustomPaymentModes(prev => [...prev, newOpt])}
                                                    placeholder="Select or enter payment mode"
                                                    isSearchable={true}
                                                    isCreatable={true}
                                                    required={true}
                                                />
                                            </div>

                                            {/* Payment Status */}
                                            <div className="pipeline-add-payment-field">
                                                <label className="pipeline-add-payment-label">Payment Status</label>
                                                <CreatableSelect
                                                    value={newPaymentData.paymentStatus}
                                                    onChange={val => setNewPaymentData(prev => ({ ...prev, paymentStatus: val }))}
                                                    options={['Paid', 'Pending', 'Partial', 'Overdue', 'Cancelled']}
                                                    placeholder="Select payment status"
                                                    isSearchable={false}
                                                    isCreatable={false}
                                                />
                                            </div>

                                            {/* Payment Date (Mandatory) */}
                                            <div className="pipeline-add-payment-field">
                                                <label className="pipeline-add-payment-label">
                                                    Payment Date <span style={{ color: '#ef4444', fontWeight: 'bold' }}>*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    className="pipeline-add-payment-input"
                                                    value={newPaymentData.paymentDate}
                                                    onChange={e => setNewPaymentData(prev => ({ ...prev, paymentDate: e.target.value }))}
                                                    required
                                                />
                                            </div>

                                            {/* Attachment */}
                                            <div className="pipeline-add-payment-field">
                                                <label className="pipeline-add-payment-label">Attachment</label>
                                                <label className="pipeline-payment-attachment-label">
                                                    <Paperclip size={13} />
                                                    {attachmentFile ? attachmentFile.name : 'Click to upload file'}
                                                    <input
                                                        type="file"
                                                        accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.doc,.docx"
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) {
                                                                setAttachmentFile(file);
                                                                const reader = new FileReader();
                                                                reader.onload = (uploadEvt) => {
                                                                    setNewPaymentData(prev => ({
                                                                        ...prev,
                                                                        attachmentName: file.name,
                                                                        attachmentUrl: uploadEvt.target?.result || '',
                                                                        attachmentType: file.type || ''
                                                                    }));
                                                                };
                                                                reader.readAsDataURL(file);
                                                            }
                                                        }}
                                                    />
                                                </label>
                                            </div>
                                        </div>

                                        <div className="pipeline-add-payment-form-footer">
                                            <button
                                                type="button"
                                                className="pipeline-btn-cancel"
                                                style={{ padding: '7px 16px', fontSize: '12.5px' }}
                                                onClick={() => { setShowAddPaymentForm(false); setAttachmentFile(null); }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="pipeline-btn-save"
                                                style={{ padding: '7px 18px', fontSize: '12.5px' }}
                                                onClick={async () => {
                                                    const amountVal = String(newPaymentData.paidAmount || '').trim();
                                                    if (!amountVal || isNaN(Number(amountVal)) || Number(amountVal) <= 0) {
                                                        alert('Please enter a valid numeric Paid Amount.');
                                                        return;
                                                    }
                                                    if (!newPaymentData.paymentMode || !newPaymentData.paymentMode.trim()) {
                                                        alert('Please select or enter a Payment Mode.');
                                                        return;
                                                    }
                                                    if (!newPaymentData.paymentDate) {
                                                        alert('Please select a Payment Date.');
                                                        return;
                                                    }

                                                    const deal = deals.find(d => d._id === milestoneModalDealId) || detailedDeal || {};
                                                    const clientNameFinal = newPaymentData.clientName || deal.clientName || 'Client';
                                                    const projectNameFinal = newPaymentData.projectName || (deal.dealName ? `${deal.dealName} Deployment` : (deal.clientName ? `${deal.clientName} Deployment` : 'Deployment'));
                                                    const currency = deal.currency || '₹';

                                                    const now = new Date();
                                                    const recordedDate = now.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
                                                    const recordedTime = now.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true });

                                                    const formattedPaidDate = newPaymentData.paymentDate
                                                        ? new Date(newPaymentData.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')
                                                        : recordedDate;

                                                    const newPaymentPayload = {
                                                        dealId: milestoneModalDealId && !milestoneModalDealId.startsWith('contact_') ? milestoneModalDealId : null,
                                                        contactId: milestoneModalDealId && milestoneModalDealId.startsWith('contact_') ? milestoneModalDealId.replace('contact_', '') : null,
                                                        srNo: (paymentRecords[milestoneModalDealId] || []).length + 1,
                                                        clientName: clientNameFinal,
                                                        clientEmail: deal.email || deal.clientEmail || '',
                                                        projectName: projectNameFinal,
                                                        paidAmount: `${currency} ${Number(amountVal).toLocaleString()}`,
                                                        paymentMode: newPaymentData.paymentMode,
                                                        paymentStatus: newPaymentData.paymentStatus || 'Paid',
                                                        paymentDate: formattedPaidDate,
                                                        recordedOnDate: recordedDate,
                                                        recordedOnTime: recordedTime,
                                                        recordedOn: `${recordedDate}, ${recordedTime}`,
                                                        attachmentName: newPaymentData.attachmentName || '',
                                                        attachmentUrl: newPaymentData.attachmentUrl || '',
                                                        attachmentType: newPaymentData.attachmentType || (attachmentFile ? attachmentFile.type : '')
                                                    };

                                                    const targetKey = milestoneModalDealId || (deal._id ? String(deal._id) : 'general');
                                                    try {
                                                        const res = await api.post('/payment-records', newPaymentPayload);
                                                        const savedRec = res.data || { id: `pay-${Date.now()}`, ...newPaymentPayload };
                                                        setPaymentRecords(prev => ({
                                                            ...prev,
                                                            [targetKey]: [...(prev[targetKey] || []), savedRec]
                                                        }));
                                                        logActivity(targetKey, {
                                                            type: 'payment',
                                                            title: 'Payment Recorded',
                                                            content: `Recorded payment of ${newPaymentPayload.paidAmount} via ${newPaymentPayload.paymentMode}. Status: ${newPaymentPayload.paymentStatus}.${newPaymentPayload.attachmentName ? ` (Attachment: ${newPaymentPayload.attachmentName})` : ''}`,
                                                            author: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Admin System'
                                                        });
                                                    } catch (err) {
                                                        console.warn('Save payment record API sync:', err.message);
                                                        const fallbackRec = { id: `pay-${Date.now()}`, ...newPaymentPayload };
                                                        setPaymentRecords(prev => ({
                                                            ...prev,
                                                            [targetKey]: [...(prev[targetKey] || []), fallbackRec]
                                                        }));
                                                        logActivity(targetKey, {
                                                            type: 'payment',
                                                            title: 'Payment Recorded',
                                                            content: `Recorded payment of ${newPaymentPayload.paidAmount} via ${newPaymentPayload.paymentMode}. Status: ${newPaymentPayload.paymentStatus}.${newPaymentPayload.attachmentName ? ` (Attachment: ${newPaymentPayload.attachmentName})` : ''}`,
                                                            author: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Admin System'
                                                        });
                                                    }

                                                    setShowAddPaymentForm(false);
                                                    setAttachmentFile(null);
                                                    setNewPaymentData({
                                                        clientName: '', projectName: '', paidAmount: '',
                                                        paymentMode: 'Bank Transfer', paymentStatus: 'Paid',
                                                        paymentDate: '', attachmentName: '', attachmentUrl: '', attachmentType: ''
                                                    });
                                                }}
                                            >
                                                Save Payment Record
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>,
                    document.body
                )
            }

            {/* Attachment Preview Lightbox / Modal */}
            {previewAttachment &&
                createPortal(
                    <div className="pipeline-preview-modal-backdrop" onClick={() => setPreviewAttachment(null)}>
                        <div className="pipeline-preview-modal-card" onClick={e => e.stopPropagation()}>
                            <div className="pipeline-preview-modal-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                                    <div className="pipeline-preview-modal-icon">
                                        <Paperclip size={16} />
                                    </div>
                                    <div style={{ overflow: 'hidden' }}>
                                        <h4 style={{ margin: 0, fontSize: '14.5px', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: '420px' }}>
                                            {previewAttachment.attachmentName || 'Attachment Document'}
                                        </h4>
                                        <p style={{ margin: '2px 0 0', fontSize: '11.5px', color: '#64748b' }}>
                                            Payment Record • {previewAttachment.clientName ? previewAttachment.clientName.replace(/\s*\([^)]*@[^)]*\)\s*$/, '').trim() : ''}
                                        </p>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {previewAttachment.attachmentUrl && (
                                        <a
                                            href={previewAttachment.attachmentUrl}
                                            download={previewAttachment.attachmentName || 'attachment'}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="pipeline-preview-btn-action"
                                            title="Download / Open file"
                                        >
                                            <Download size={13} />
                                            <span>Download</span>
                                        </a>
                                    )}
                                    <button
                                        type="button"
                                        className="pipeline-modal-close-btn"
                                        onClick={() => setPreviewAttachment(null)}
                                        title="Close preview"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="pipeline-preview-modal-body">
                                {(() => {
                                    const url = previewAttachment.attachmentUrl;
                                    const name = (previewAttachment.attachmentName || '').toLowerCase();
                                    const isImage = url?.startsWith('data:image/') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.webp') || name.endsWith('.gif') || name.endsWith('.svg');
                                    const isPdf = url?.startsWith('data:application/pdf') || name.endsWith('.pdf');

                                    if (url && isImage) {
                                        return (
                                            <div className="pipeline-preview-img-container">
                                                <img
                                                    src={url}
                                                    alt={previewAttachment.attachmentName}
                                                    className="pipeline-preview-img"
                                                />
                                            </div>
                                        );
                                    }

                                    if (url && isPdf) {
                                        return (
                                            <iframe
                                                src={url}
                                                title={previewAttachment.attachmentName}
                                                style={{ width: '100%', height: '62vh', border: 'none', borderRadius: '8px' }}
                                            />
                                        );
                                    }

                                    return (
                                        <div className="pipeline-preview-fallback-card">
                                            <div className="pipeline-preview-file-icon-box">
                                                {isPdf ? <FileText size={38} style={{ color: '#ef4444' }} /> : isImage ? <Paperclip size={38} style={{ color: '#0284c7' }} /> : <FileText size={38} style={{ color: '#6366f1' }} />}
                                            </div>
                                            <div style={{ textAlign: 'center', marginTop: '12px' }}>
                                                <h3 style={{ margin: '0 0 5px', fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                                                    {previewAttachment.attachmentName || 'Payment Receipt Document'}
                                                </h3>
                                                <span className="pipeline-preview-format-tag">
                                                    {name.split('.').pop() || 'DOCUMENT'}
                                                </span>
                                            </div>
                                            <div className="pipeline-preview-meta-grid">
                                                <div>
                                                    <span className="pipeline-preview-meta-label">Client Name</span>
                                                    <span className="pipeline-preview-meta-val">{previewAttachment.clientName ? previewAttachment.clientName.replace(/\s*\([^)]*@[^)]*\)\s*$/, '').trim() : '—'}</span>
                                                </div>
                                                <div>
                                                    <span className="pipeline-preview-meta-label">Project</span>
                                                    <span className="pipeline-preview-meta-val">{previewAttachment.projectName || '—'}</span>
                                                </div>
                                                <div>
                                                    <span className="pipeline-preview-meta-label">Amount Paid</span>
                                                    <span className="pipeline-preview-meta-val" style={{ color: '#16a34a', fontWeight: 700 }}>{previewAttachment.paidAmount}</span>
                                                </div>
                                                <div>
                                                    <span className="pipeline-preview-meta-label">Payment Mode</span>
                                                    <span className="pipeline-preview-meta-val">{previewAttachment.paymentMode}</span>
                                                </div>
                                                <div>
                                                    <span className="pipeline-preview-meta-label">Payment Date</span>
                                                    <span className="pipeline-preview-meta-val">{previewAttachment.paymentDate}</span>
                                                </div>
                                                <div>
                                                    <span className="pipeline-preview-meta-label">Status</span>
                                                    <span className="pipeline-preview-meta-val" style={{ color: '#16a34a', fontWeight: 600 }}>{previewAttachment.paymentStatus}</span>
                                                </div>
                                            </div>
                                            {url ? (
                                                <a
                                                    href={url}
                                                    download={previewAttachment.attachmentName || 'document'}
                                                    className="pipeline-preview-btn-download-main"
                                                >
                                                    <Download size={14} />
                                                    <span>Download Uploaded File</span>
                                                </a>
                                            ) : (
                                                <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: '#64748b' }}>
                                                    <CheckCircle size={14} style={{ color: '#16a34a' }} />
                                                    <span>Verified Payment Receipt Attachment • #{previewAttachment.srNo || '1'}</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>,
                    document.body
                )
            }

            {/* Edit Follow-up Modal */}
            {editingFollowUp &&
                createPortal(
                    <div className="pipeline-edit-modal-backdrop" onClick={() => !isSavingEditFollowUp && setEditingFollowUp(null)}>
                        <div className="pipeline-edit-modal-card" onClick={e => e.stopPropagation()}>
                            <form onSubmit={handleSaveEditFollowUp}>
                                <div className="pipeline-edit-modal-header">
                                    <div className="pipeline-edit-modal-title-wrap">
                                        <div className="pipeline-edit-modal-icon">
                                            <Calendar size={18} />
                                        </div>
                                        <div>
                                            <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                                                Edit Scheduled Follow-up
                                            </h4>
                                            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>
                                                Modify touchpoint details before the scheduled time arrives.
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="pipeline-modal-close-btn"
                                        onClick={() => !isSavingEditFollowUp && setEditingFollowUp(null)}
                                        title="Close"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>

                                <div className="pipeline-edit-modal-body">
                                    {/* Channel selection chips */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                                            Interaction Channel
                                        </label>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            {['Call', 'Meeting', 'WhatsApp', 'Email', 'Demo', 'Visit'].map(chName => (
                                                <button
                                                    key={chName}
                                                    type="button"
                                                    className={`pipeline-followup-channel-chip ${editFollowUpForm.channel === chName ? 'active' : ''}`}
                                                    onClick={() => setEditFollowUpForm(prev => ({ ...prev, channel: chName }))}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '5px',
                                                        padding: '6px 12px',
                                                        borderRadius: '8px',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        cursor: 'pointer',
                                                        border: editFollowUpForm.channel === chName ? '1.5px solid #0284c7' : '1px solid #e2e8f0',
                                                        background: editFollowUpForm.channel === chName ? '#eff6ff' : '#f8fafc',
                                                        color: editFollowUpForm.channel === chName ? '#0284c7' : '#64748b',
                                                        transition: 'all 0.15s ease'
                                                    }}
                                                >
                                                    {getFollowUpChannelIcon(chName, 13)}
                                                    <span>{chName}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Title / Purpose */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '5px' }}>
                                            Title / Agenda <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="pipeline-input-field"
                                            placeholder="e.g. Requirement discussion, Contract review"
                                            value={editFollowUpForm.title}
                                            onChange={e => setEditFollowUpForm(prev => ({ ...prev, title: e.target.value }))}
                                            required
                                            style={{ width: '100%', boxSizing: 'border-box', height: '36px', padding: '0 10px', fontSize: '13px', borderRadius: '7px', border: '1px solid #cbd5e1' }}
                                        />
                                    </div>

                                    {/* Date & Time Picker */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '5px' }}>
                                                Scheduled Date <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <input
                                                type="date"
                                                className="pipeline-input-field"
                                                value={editFollowUpForm.followUpDate}
                                                min={new Date().toISOString().slice(0, 10)}
                                                onChange={e => setEditFollowUpForm(prev => ({ ...prev, followUpDate: e.target.value }))}
                                                required
                                                style={{ width: '100%', boxSizing: 'border-box', height: '36px', padding: '0 10px', fontSize: '13px', borderRadius: '7px', border: '1px solid #cbd5e1' }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '5px' }}>
                                                Scheduled Time <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <input
                                                type="time"
                                                className="pipeline-input-field"
                                                value={editFollowUpForm.followUpTime}
                                                onChange={e => setEditFollowUpForm(prev => ({ ...prev, followUpTime: e.target.value }))}
                                                required
                                                style={{ width: '100%', boxSizing: 'border-box', height: '36px', padding: '0 10px', fontSize: '13px', borderRadius: '7px', border: '1px solid #cbd5e1' }}
                                            />
                                        </div>
                                    </div>

                                    {/* Past-time Notice (Informational only if overdue) */}
                                    {(() => {
                                        const parsed = parseFollowUpDateTime(editFollowUpForm.followUpDate, editFollowUpForm.followUpTime);
                                        const isPast = parsed && parsed.getTime() <= Date.now();
                                        if (!isPast || editFollowUpForm.status === 'Completed') return null;
                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '7px', background: '#fefce8', border: '1px solid #fef08a', color: '#854d0e', fontSize: '12px' }}>
                                                <Clock size={15} style={{ flexShrink: 0 }} />
                                                <span>This touchpoint is scheduled in the past or overdue. You can update the remarks, change status, or reschedule.</span>
                                            </div>
                                        );
                                    })()}

                                    {/* Status & Next Follow-up Date */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '5px' }}>
                                                Status
                                            </label>
                                            <select
                                                className="pipeline-input-field"
                                                value={editFollowUpForm.status}
                                                onChange={e => setEditFollowUpForm(prev => ({ ...prev, status: e.target.value }))}
                                                style={{ width: '100%', boxSizing: 'border-box', height: '36px', padding: '0 10px', fontSize: '13px', borderRadius: '7px', border: '1px solid #cbd5e1', background: '#fff' }}
                                            >
                                                <option value="Scheduled">Scheduled</option>
                                                <option value="Follow-up Needed">Follow-up Needed</option>
                                                <option value="Rescheduled">Rescheduled</option>
                                                <option value="Completed">Completed</option>
                                                <option value="Missed">Missed</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '5px' }}>
                                                Next Follow-up Date (Optional)
                                            </label>
                                            <input
                                                type="date"
                                                className="pipeline-input-field"
                                                value={editFollowUpForm.nextFollowUpDate}
                                                min={editFollowUpForm.followUpDate || new Date().toISOString().slice(0, 10)}
                                                onChange={e => setEditFollowUpForm(prev => ({ ...prev, nextFollowUpDate: e.target.value }))}
                                                style={{ width: '100%', boxSizing: 'border-box', height: '36px', padding: '0 10px', fontSize: '13px', borderRadius: '7px', border: '1px solid #cbd5e1' }}
                                            />
                                        </div>
                                    </div>

                                    {/* Linked Member Field */}
                                    {(() => {
                                        const currentMemberOptions = getFollowUpMemberOptions(editingFollowUp._targetEntityType || 'deal', editingFollowUp._targetEntityId);
                                        const isSalesPerson = !isAdmin && !isSalesHead && !isSalesManager;
                                        const selectedMemberId = editFollowUpForm.memberId || '';
                                        const selectedOpt = currentMemberOptions.find(o => String(o.id) === String(selectedMemberId));

                                        return (
                                            <div style={{ position: 'relative' }}>
                                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '5px' }}>
                                                    {isSalesPerson ? 'Route to Management' : 'Assign / Link Member'}
                                                </label>
                                                {/* Styled wrapper that shows selected option with star/role badge */}
                                                <div style={{ position: 'relative' }}>
                                                    <div style={{
                                                        display: 'flex', alignItems: 'center', gap: '8px',
                                                        height: '38px', padding: '0 32px 0 10px',
                                                        border: '1.5px solid #cbd5e1', borderRadius: '8px',
                                                        background: '#fff', fontSize: '13.5px', color: '#1e293b',
                                                        fontWeight: selectedOpt?.isDealOwner ? 600 : 400,
                                                        cursor: 'pointer', userSelect: 'none',
                                                        boxSizing: 'border-box', width: '100%'
                                                    }}>
                                                        {selectedOpt ? (
                                                            <>
                                                                {selectedOpt.isDealOwner && (
                                                                    <span style={{ color: '#f59e0b', fontSize: '15px', lineHeight: 1 }}>★</span>
                                                                )}
                                                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {selectedOpt.name}
                                                                </span>
                                                                {selectedOpt.isDealOwner ? (
                                                                    <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '1px 7px', borderRadius: '4px', whiteSpace: 'nowrap' }}>Deal Owner</span>
                                                                ) : selectedOpt.role ? (
                                                                    <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '1px 7px', borderRadius: '4px', whiteSpace: 'nowrap' }}>{selectedOpt.role}</span>
                                                                ) : null}
                                                            </>
                                                        ) : (
                                                            <span style={{ color: '#94a3b8' }}>-- No Member Linked --</span>
                                                        )}
                                                        <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none', fontSize: '10px' }}>▼</span>
                                                    </div>
                                                    {/* Actual select overlaid invisibly for browser-native dropdown */}
                                                    <select
                                                        value={selectedMemberId}
                                                        onChange={e => {
                                                            const selId = e.target.value;
                                                            const matchedOpt = currentMemberOptions.find(o => String(o.id) === String(selId));
                                                            setEditFollowUpForm(prev => ({
                                                                ...prev,
                                                                memberId: selId,
                                                                memberName: matchedOpt ? matchedOpt.name : ''
                                                            }));
                                                        }}
                                                        style={{
                                                            position: 'absolute', inset: 0, opacity: 0,
                                                            width: '100%', height: '100%', cursor: 'pointer'
                                                        }}
                                                    >
                                                        <option value="">-- No Member Linked (Self Only) --</option>
                                                        {currentMemberOptions.map(opt => (
                                                            <option key={opt.id} value={opt.id}>
                                                                {opt.isDealOwner ? '★ ' : ''}{opt.name}{opt.isDealOwner ? ' (Deal Owner)' : opt.role ? ` (${opt.role})` : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Remarks / Discussion notes */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '5px' }}>
                                            Remarks & Outcomes <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <textarea
                                            rows={3}
                                            className="pipeline-input-field"
                                            placeholder="Enter follow-up remarks, client responses, or key discussion points..."
                                            value={editFollowUpForm.remarks}
                                            onChange={e => setEditFollowUpForm(prev => ({ ...prev, remarks: e.target.value }))}
                                            required
                                            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '13px', borderRadius: '7px', border: '1px solid #cbd5e1', resize: 'vertical' }}
                                        />
                                    </div>
                                </div>

                                <div className="pipeline-edit-modal-footer">
                                    <button
                                        type="button"
                                        className="pipeline-modal-cancel-btn"
                                        onClick={() => !isSavingEditFollowUp && setEditingFollowUp(null)}
                                        disabled={isSavingEditFollowUp}
                                        style={{ padding: '7px 14px', borderRadius: '7px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="pipeline-btn-primary"
                                        disabled={isSavingEditFollowUp}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '7px 16px',
                                            borderRadius: '7px',
                                            background: '#0284c7',
                                            color: '#ffffff',
                                            border: 'none',
                                            fontSize: '12.5px',
                                            fontWeight: 600,
                                            cursor: isSavingEditFollowUp ? 'not-allowed' : 'pointer',
                                            opacity: (() => {
                                                const p = parseFollowUpDateTime(editFollowUpForm.followUpDate, editFollowUpForm.followUpTime);
                                                return (p && p.getTime() <= Date.now()) || isSavingEditFollowUp ? 0.6 : 1;
                                            })()
                                        }}
                                    >
                                        {isSavingEditFollowUp ? (
                                            <Loader2 size={14} className="pipeline-spinner-icon" />
                                        ) : (
                                            <Check size={14} />
                                        )}
                                        <span>{isSavingEditFollowUp ? 'Saving Changes...' : 'Save Changes'}</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>,
                    document.body
                )
            }

            {/* Lead Requirement Details & Edit Modal */}
            {showDealRequirementModal && detailedDeal && createPortal(
                <div
                    className="pipeline-modal-backdrop"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(15, 23, 42, 0.65)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999,
                        padding: '20px'
                    }}
                    onClick={() => {
                        if (!isSavingRequirement) setShowDealRequirementModal(false);
                    }}
                >
                    <div
                        className="pipeline-requirement-modal-card"
                        style={{
                            width: '100%',
                            maxWidth: '620px',
                            background: '#ffffff',
                            borderRadius: '16px',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            overflow: 'hidden',
                            border: '1px solid #e2e8f0',
                            animation: 'fadeInScale 0.2s ease-out'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{
                            padding: '16px 20px',
                            borderBottom: '1px solid #f1f5f9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: '#f8fafc'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    width: '34px',
                                    height: '34px',
                                    borderRadius: '8px',
                                    background: isEditingRequirement ? '#fef3c7' : '#e0f2fe',
                                    color: isEditingRequirement ? '#b45309' : '#0284c7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {isEditingRequirement ? <Edit2 size={17} /> : <FileText size={17} />}
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                                        {isEditingRequirement ? 'Edit Requirement' : 'Requirement Details'}
                                    </h3>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                        {detailedDeal.dealName || detailedDeal.clientName || 'Deal Details'}
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!isSavingRequirement) setShowDealRequirementModal(false);
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#94a3b8',
                                        cursor: 'pointer',
                                        padding: '6px',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.15s ease'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.color = '#0f172a'; e.currentTarget.style.background = '#e2e8f0'; }}
                                    onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'none'; }}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '20px', maxHeight: '62vh', overflowY: 'auto' }}>
                            {isEditingRequirement ? (
                                <form id="deal-requirement-edit-form" onSubmit={handleSaveDealRequirement}>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                                        Requirement / Product Scope
                                    </label>
                                    <textarea
                                        rows={10}
                                        value={editingRequirementText}
                                        onChange={e => setEditingRequirementText(e.target.value)}
                                        placeholder="Enter or update the lead requirement details..."
                                        style={{
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            padding: '12px 14px',
                                            fontSize: '13px',
                                            lineHeight: '1.6',
                                            borderRadius: '10px',
                                            border: '1.5px solid #0284c7',
                                            outline: 'none',
                                            fontFamily: 'inherit',
                                            resize: 'vertical',
                                            background: '#ffffff',
                                            color: '#0f172a'
                                        }}
                                        autoFocus
                                    />
                                </form>
                            ) : (
                                <div style={{
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '10px',
                                    padding: '16px',
                                    fontSize: '13px',
                                    lineHeight: '1.6',
                                    color: '#334155',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontFamily: 'inherit'
                                }}>
                                    {detailedDeal.requirements || detailedDeal.requirement || detailedDeal.description || detailedDeal.relatedLead?.requirements || detailedDeal.relatedLead?.requirement || 'No requirement details recorded.'}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '12px 20px',
                            borderTop: '1px solid #f1f5f9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: '#fafafa'
                        }}>
                            <div>
                                {isEditingRequirement && (
                                    <span style={{ fontSize: '11.5px', color: '#64748b' }}>
                                        Changes will be saved to this deal and synced to the pipeline.
                                    </span>
                                )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {isEditingRequirement ? (
                                    <>
                                        <button
                                            type="button"
                                            disabled={isSavingRequirement}
                                            onClick={() => setIsEditingRequirement(false)}
                                            style={{
                                                padding: '7px 16px',
                                                borderRadius: '8px',
                                                background: '#ffffff',
                                                border: '1px solid #cbd5e1',
                                                color: '#475569',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                cursor: isSavingRequirement ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            form="deal-requirement-edit-form"
                                            disabled={isSavingRequirement}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '7px 18px',
                                                borderRadius: '8px',
                                                background: '#0284c7',
                                                color: '#ffffff',
                                                border: 'none',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                cursor: isSavingRequirement ? 'not-allowed' : 'pointer',
                                                transition: 'background 0.15s ease'
                                            }}
                                            onMouseEnter={e => { if (!isSavingRequirement) e.currentTarget.style.background = '#0369a1'; }}
                                            onMouseLeave={e => { if (!isSavingRequirement) e.currentTarget.style.background = '#0284c7'; }}
                                        >
                                            <Check size={14} />
                                            <span>{isSavingRequirement ? 'Saving...' : 'Save Changes'}</span>
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsEditingRequirement(true);
                                                const currentText = detailedDeal.requirements || detailedDeal.requirement || detailedDeal.description || detailedDeal.relatedLead?.requirements || detailedDeal.relatedLead?.requirement || '';
                                                setEditingRequirementText(currentText);
                                            }}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '7px 16px',
                                                borderRadius: '8px',
                                                background: '#ffffff',
                                                border: '1px solid #cbd5e1',
                                                color: '#334155',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#94a3b8'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                                        >
                                            <Edit2 size={13} style={{ color: '#64748b' }} />
                                            <span>Edit</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setShowDealRequirementModal(false)}
                                            style={{
                                                padding: '7px 20px',
                                                borderRadius: '8px',
                                                background: '#0284c7',
                                                color: '#ffffff',
                                                border: 'none',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                transition: 'background 0.15s ease'
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#0369a1'}
                                            onMouseLeave={e => e.currentTarget.style.background = '#0284c7'}
                                        >
                                            Close
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div >
    );
}

export default Pipeline;